import {
  BarChart3,
  Building2,
  CalendarDays,
  Check,
  ChevronRight,
  CircleDollarSign,
  Coins,
  ClipboardCheck,
  Clock3,
  ExternalLink,
  FileText,
  HelpCircle,
  Languages,
  LayoutDashboard,
  LoaderCircle,
  LockKeyhole,
  LogOut,
  Menu,
  Settings,
  ShieldCheck,
  TriangleAlert,
  UserRound,
  Users,
} from "lucide-react";
import {
  type ChangeEvent,
  type FormEvent,
  type ReactNode,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import "./payroll.css";
import "./leave.css";
import {
  contextualHelpArticle,
  helpDetailsFor,
  visibleHelpArticles,
  type HelpRole,
  type HelpSection,
} from "./help-catalog";
import { optionLabel } from "./ui-labels";
import {
  suggestNextPayrollPeriod,
  type PayrollFrequency,
} from "./payroll-period";
import {
  approvedLeaveHours,
  splitApprovalPeriod,
  summarizeApprovalLabor,
} from "./time-approval";
import { isTimesheetApprovalDisabled } from "./timesheet-approval";

const API = "https://pr-payroll-api-development.rafael787mailbox.workers.dev";

type Locale = "es" | "en";
const localeTag = (locale: Locale) => (locale === "es" ? "es-PR" : "en-US");
const parseDisplayDate = (value: string) => {
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
  return new Date(dateOnly ? `${value}T12:00:00-04:00` : value);
};
const formatDate = (value: string | null | undefined, locale: Locale) => {
  if (!value) return "—";
  const parsed = parseDisplayDate(value);
  return Number.isNaN(parsed.valueOf())
    ? value
    : new Intl.DateTimeFormat(localeTag(locale), {
        year: "numeric",
        month: "short",
        day: "numeric",
        timeZone: "America/Puerto_Rico",
      }).format(parsed);
};
const formatDateTime = (value: string | null | undefined, locale: Locale) => {
  if (!value) return "—";
  const parsed = parseDisplayDate(value);
  return Number.isNaN(parsed.valueOf())
    ? value
    : new Intl.DateTimeFormat(localeTag(locale), {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZone: "America/Puerto_Rico",
      }).format(parsed);
};
const formatDateRange = (
  from: string | null | undefined,
  to: string | null | undefined,
  locale: Locale,
  openLabel: string,
) => `${formatDate(from, locale)} — ${to ? formatDate(to, locale) : openLabel}`;
type ResponsiveDataRow = { key: string; cells: ReactNode[]; card: ReactNode };
function ResponsiveDataTable({
  label,
  caption,
  columns,
  rows,
  empty,
}: {
  label: string;
  caption: string;
  columns: string[];
  rows: ResponsiveDataRow[];
  empty: string;
}) {
  if (!rows.length) return <p className="empty-state">{empty}</p>;
  return (
    <>
      <div
        className={`responsive-table-shell shared-data-table data-columns-${columns.length}`}
        role="region"
        tabIndex={0}
        aria-label={label}
      >
        <table>
          <caption>{caption}</caption>
          <thead>
            <tr>
              {columns.map((column) => (
                <th scope="col" key={column}>
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                {row.cells.map((cell, index) =>
                  index === 0 ? (
                    <th scope="row" key={index}>
                      {cell}
                    </th>
                  ) : (
                    <td key={index}>{cell}</td>
                  ),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="run-list responsive-card-list">
        {rows.map((row) => row.card)}
      </div>
    </>
  );
}
function OperationalError({
  value,
  locale,
}: {
  value: unknown;
  locale: Locale;
}) {
  if (!value) return null;
  const raw = String(value).trim(),
    internal =
      raw === "true" ||
      /^[a-z0-9_/-]+(?::(?:\d{3}|[a-z0-9_,/-]+))+$/i.test(raw),
    reference =
      internal && raw !== "true"
        ? raw.replace(/[^a-z0-9:_/-]/gi, "").slice(0, 80)
        : "";
  return (
    <p className="form-error operational-error" role="alert">
      <strong>
        {locale === "es"
          ? "No se pudo completar la operación."
          : "The operation could not be completed."}
      </strong>
      <span>
        {internal
          ? locale === "es"
            ? "Revise la información e intente nuevamente."
            : "Review the information and try again."
          : raw}
      </span>
      {reference && (
        <small>
          {locale === "es" ? "Referencia para soporte" : "Support reference"}:{" "}
          {reference}
        </small>
      )}
    </p>
  );
}
function OperationalFeedback({
  message,
  isError,
  locale,
}: {
  message: string;
  isError: boolean;
  locale: Locale;
}) {
  return isError ? (
    <OperationalError value={message} locale={locale} />
  ) : message ? (
    <p className="success-note" role="status">
      {message}
    </p>
  ) : null;
}
function AuditControl({locale,mode="optional_comment",value,onChange,acknowledged=false,onAcknowledge}:{locale:Locale;mode?:"optional_comment"|"required_reason"|"acknowledgment";value?:string;onChange?:(value:string)=>void;acknowledged?:boolean;onAcknowledge?:(value:boolean)=>void}){
  if(mode==="acknowledgment")return <label className="check-label audit-control"><input type="checkbox" required checked={acknowledged} onChange={event=>onAcknowledge?.(event.currentTarget.checked)}/>{locale==="es"?"Confirmo que revisé la información y deseo continuar.":"I confirm that I reviewed the information and want to continue."}</label>;
  const required=mode==="required_reason";
  return <label className="audit-control">{required?(locale==="es"?"Razón requerida para esta acción excepcional":"Reason required for this exceptional action"):(locale==="es"?"Comentario (opcional)":"Comment (optional)")}<textarea required={required} minLength={required?3:undefined} maxLength={1000} value={value??""} onChange={event=>onChange?.(event.currentTarget.value)}/><small>{required?(locale==="es"?"Explique por qué se modifica, revierte o reemplaza información previamente aprobada o finalizada.":"Explain why previously approved or finalized information is being changed, reversed, or replaced."):(locale==="es"?"No necesita escribir su nombre; el sistema registra automáticamente quién realizó la acción.":"You do not need to type your name; the system records who performed the action automatically.")}</small></label>;
}
function LiveUiQuality({ locale }: { locale: Locale }) {
  const anchor = useRef<HTMLSpanElement>(null),
    last = useRef(""),
    [result, setResult] = useState({ overflow: false, clipped: 0 });
  useEffect(() => {
    let frame = 0;
    const inspect = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const workspace = anchor.current?.closest<HTMLElement>(".workspace");
        if (!workspace) return;
        const bounds = workspace.getBoundingClientRect(),
          intentionallyScrollable = (element: Element) => {
            let parent = element.parentElement;
            while (parent && parent !== workspace) {
              const style = getComputedStyle(parent);
              if (
                parent.scrollWidth > parent.clientWidth + 1 &&
                ["auto", "scroll"].includes(style.overflowX)
              )
                return true;
              parent = parent.parentElement;
            }
            return false;
          };
        const clipped = [
          ...workspace.querySelectorAll<HTMLElement>(
            "button,input,select,textarea,a[href]",
          ),
        ].filter((element) => {
          if (
            !element.getClientRects().length ||
            getComputedStyle(element).visibility === "hidden" ||
            element.closest('[aria-hidden="true"]') ||
            intentionallyScrollable(element)
          )
            return false;
          const rect = element.getBoundingClientRect();
          return (
            rect.width > 0 &&
            (rect.left < bounds.left - 1 || rect.right > bounds.right + 1)
          );
        }).length;
        const next = {
            overflow: workspace.scrollWidth > workspace.clientWidth + 1,
            clipped,
          },
          signature = `${Number(next.overflow)}:${next.clipped}`;
        if (signature !== last.current) {
          last.current = signature;
          setResult(next);
        }
      });
    };
    const observer = new MutationObserver(inspect),
      resize = new ResizeObserver(inspect),
      workspace = anchor.current?.closest<HTMLElement>(".workspace");
    if (workspace) {
      observer.observe(workspace, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "style", "hidden", "open"],
      });
      resize.observe(workspace);
    }
    window.addEventListener("resize", inspect);
    inspect();
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      resize.disconnect();
      window.removeEventListener("resize", inspect);
    };
  }, []);
  const ok = !result.overflow && result.clipped === 0;
  return (
    <span
      ref={anchor}
      className={`ui-quality ${ok ? "pass" : "fail"}`}
      role="status"
      title={
        locale === "es"
          ? "Control automático de desbordamiento horizontal y controles visibles recortados"
          : "Automatic horizontal-overflow and visible-clipped-control check"
      }
    >
      {ok
        ? locale === "es"
          ? "UI · sin desborde"
          : "UI · no overflow"
        : locale === "es"
          ? `UI · ${result.overflow ? "página desbordada" : "página contenida"} · ${result.clipped} controles recortados`
          : `UI · ${result.overflow ? "page overflow" : "page contained"} · ${result.clipped} clipped controls`}
    </span>
  );
}

function ActionFeedbackCoordinator() {
  const marker = useRef<HTMLSpanElement>(null),
    host = useRef<HTMLDivElement | null>(null),
    relayed = useRef<HTMLElement | null>(null),
    [feedback, setFeedback] = useState<{ text: string; error: boolean } | null>(
      null,
    );
  useEffect(() => {
    const workspace = marker.current?.closest<HTMLElement>(
      ".workspace, .auth-layout, .setup-screen",
    );
    if (!workspace) return;
    let clickedAt = 0;
    const clearRelay = () => {
      relayed.current?.removeAttribute("data-action-feedback-relayed");
      relayed.current = null;
      host.current?.remove();
      host.current = null;
      setFeedback(null);
    };
    const click = (event: MouseEvent) => {
      const button = (event.target as Element | null)?.closest<HTMLElement>(
        "button, [role='button']",
      );
      if (!button || !workspace.contains(button) || button.hasAttribute("disabled"))
        return;
      clearRelay();
      clickedAt = Date.now();
      const nextHost = document.createElement("div");
      nextHost.className = "action-feedback-host";
      nextHost.setAttribute("aria-live", "polite");
      button.insertAdjacentElement("afterend", nextHost);
      host.current = nextHost;
    };
    const inspect = (mutations: MutationRecord[]) => {
      if (!host.current || Date.now() - clickedAt > 30_000) return;
      const candidates = new Set<HTMLElement>();
      for (const mutation of mutations) {
        const element =
          mutation.target instanceof HTMLElement
            ? mutation.target
            : mutation.target.parentElement;
        const feedbackElement = element?.closest<HTMLElement>(
          "[role='alert'], [role='status'], .action-result, .compensation-action-result, .form-error, .success-note, .notice.error, .notice.success",
        );
        if (feedbackElement) candidates.add(feedbackElement);
        for (const node of mutation.addedNodes)
          if (node instanceof HTMLElement) {
            if (
              node.matches(
                "[role='alert'], [role='status'], .action-result, .compensation-action-result, .form-error, .success-note, .notice.error, .notice.success",
              )
            )
              candidates.add(node);
            node
              .querySelectorAll<HTMLElement>(
                "[role='alert'], [role='status'], .action-result, .compensation-action-result, .form-error, .success-note, .notice.error, .notice.success",
              )
              .forEach((item) => candidates.add(item));
          }
      }
      const candidate = [...candidates].find(
        (item) =>
          workspace.contains(item) &&
          !host.current?.contains(item) &&
          !item.classList.contains("ui-quality") &&
          Boolean(item.textContent?.trim()),
      );
      if (!candidate) return;
      relayed.current?.removeAttribute("data-action-feedback-relayed");
      relayed.current = candidate;
      candidate.setAttribute("data-action-feedback-relayed", "true");
      setFeedback({
        text: candidate.textContent?.replace(/\s+/g, " ").trim() ?? "",
        error:
          candidate.matches("[role='alert'], .form-error, .notice.error") ||
          candidate.classList.contains("error"),
      });
    };
    const observer = new MutationObserver(inspect);
    workspace.addEventListener("click", click, true);
    observer.observe(workspace, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["class", "hidden"],
    });
    return () => {
      observer.disconnect();
      workspace.removeEventListener("click", click, true);
      clearRelay();
    };
  }, []);
  return (
    <>
      <span ref={marker} hidden />
      {host.current && feedback
        ? createPortal(
            <p
              className={feedback.error ? "form-error" : "success-note"}
              role={feedback.error ? "alert" : "status"}
            >
              {feedback.text}
            </p>,
            host.current,
          )
        : null}
    </>
  );
}
type Section =
  | "home"
  | "self"
  | "personnel"
  | "personnel_setup"
  | "time"
  | "tips"
  | "time_log"
  | "payroll"
  | "expenses"
  | "documents"
  | "leave"
  | "reports"
  | "settings"
  | "help";
type PrimaryArea = "home" | "people" | "time" | "payroll" | "company";
type WorkerSection = "home" | "profile" | "time" | "leave" | "pay" | "help";
type Tenant = {
  id: string;
  legalName: string;
  displayName: string;
  defaultLocale: "en-PR" | "es-PR";
  status: string;
  statusReason?: string | null;
  statusChangedAt?: string | null;
};
type WorkerAccount = {
  tenantId: string;
  tenantName: string;
  legalName: string;
  defaultLocale: "en-PR" | "es-PR";
  workerId: string;
  workerName: string;
  roleKey: string;
};
type ReviewContext = {
  tenantId: string | null;
  roleKey: string;
  workerId: string | null;
  workerType:
    | "employee"
    | "professional_services"
    | "pending_classification"
    | null;
  workerName: string | null;
};
type SetupStep = {
  key: "company" | "personnel" | "policies" | "payroll";
  complete: boolean;
  missing: string[];
  counts: null | {
    workers?: number;
    employerRates?: number;
    activeWorkers?: number;
    readyWorkers?: number;
    classificationMissing?: number;
    workerNumberMissing?: number;
    payRateMissing?: number;
    taxElectionMissing?: number;
  };
};
type SetupReadiness = {
  completedSteps: number;
  totalSteps: number;
  percent: number;
  steps: SetupStep[];
};

function PageBreadcrumb({
  locale,
  items,
}: {
  locale: Locale;
  items: string[];
}) {
  return (
    <nav
      className="page-breadcrumb"
      aria-label={locale === "es" ? "Ruta de navegación" : "Breadcrumb"}
    >
      <ol>
        {items.map((item, index) => (
          <li
            key={`${item}-${index}`}
            aria-current={index === items.length - 1 ? "page" : undefined}
          >
            {index > 0 && <ChevronRight aria-hidden="true" />}
            <span>{item}</span>
          </li>
        ))}
      </ol>
    </nav>
  );
}

const setupRequirementText: Record<string, { es: string; en: string }> = {
  legal_name: { es: "nombre legal", en: "legal name" },
  ein: { es: "EIN", en: "EIN" },
  pr_employer_account: {
    es: "cuenta patronal de PR",
    en: "PR employer account",
  },
  physical_address: { es: "dirección física", en: "physical address" },
  phone: { es: "teléfono", en: "phone" },
  email: { es: "correo electrónico", en: "email" },
  pr_deposit_schedule: {
    es: "calendario de depósitos de PR",
    en: "PR deposit schedule",
  },
  federal_deposit_schedule: {
    es: "calendario de depósitos federales",
    en: "federal deposit schedule",
  },
  workweek: { es: "semana laboral efectiva", en: "effective workweek" },
  at_least_one_worker: {
    es: "al menos una persona",
    en: "at least one worker",
  },
  operating_model: { es: "modelo operacional", en: "operating model" },
  employer_rates: {
    es: "tasas vigentes de desempleo de PR y FUTA efectiva",
    en: "current PR unemployment and effective FUTA rates",
  },
  at_least_one_active_worker: {
    es: "al menos una persona activa",
    en: "at least one active worker",
  },
  worker_classification: {
    es: "clasificación de trabajador",
    en: "worker classification",
  },
  worker_number: { es: "número de empleado", en: "worker number" },
  effective_pay_rate: { es: "tasa de pago vigente", en: "current pay rate" },
  certified_499_r4: {
    es: "elección 499 R-4 certificada",
    en: "certified 499 R-4 election",
  },
};
const setupMissingText = (item: SetupStep, locale: Locale) =>
  item.missing
    .map(
      (code) =>
        setupRequirementText[code]?.[locale] ?? code.replaceAll("_", " "),
    )
    .join(" · ");

type WorkflowDialogField = {
  key: string;
  label: string;
  type?: "text" | "date" | "number" | "textarea" | "select" | "file";
  value?: string;
  required?: boolean;
  minLength?: number;
  pattern?: string;
  min?: string;
  max?: string;
  step?: string;
  accept?: string;
  fullWidth?: boolean;
  options?: { value: string; label: string }[];
};
type WorkflowDialogOptions = {
  title: string;
  detail?: string;
  confirmLabel: string;
  cancelLabel: string;
  danger?: boolean;
  fields?: WorkflowDialogField[];
};
type WorkflowDialogResult = Record<string, string | File>;

function requestWorkflowDialog(
  options: WorkflowDialogOptions,
): Promise<WorkflowDialogResult | null> {
  return new Promise((resolve) => {
    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const dialog = document.createElement("dialog"),
      form = document.createElement("form"),
      header = document.createElement("header"),
      heading = document.createElement("div"),
      title = document.createElement("h2"),
      close = document.createElement("button"),
      grid = document.createElement("div"),
      footer = document.createElement("footer"),
      cancel = document.createElement("button"),
      confirm = document.createElement("button");
    const titleId = `workflow-${crypto.randomUUID()}`;
    dialog.className = "workflow-dialog";
    dialog.setAttribute("aria-labelledby", titleId);
    form.noValidate = false;
    title.id = titleId;
    title.textContent = options.title;
    heading.append(title);
    close.type = "button";
    close.textContent = "×";
    close.setAttribute("aria-label", options.cancelLabel);
    header.append(heading, close);
    form.append(header);
    if (options.detail) {
      const detail = document.createElement("p");
      const detailId = `${titleId}-detail`;
      detail.className = "form-detail";
      detail.id = detailId;
      detail.textContent = options.detail;
      dialog.setAttribute("aria-describedby", detailId);
      form.append(detail);
    }
    grid.className = "form-grid";
    for (const field of options.fields ?? []) {
      const label = document.createElement("label");
      label.textContent = field.label;
      if (field.fullWidth) label.className = "full-width";
      let control: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      if (field.type === "textarea")
        control = document.createElement("textarea");
      else if (field.type === "select") {
        const select = document.createElement("select");
        for (const option of field.options ?? []) {
          const item = document.createElement("option");
          item.value = option.value;
          item.textContent = option.label;
          select.append(item);
        }
        control = select;
      } else {
        const input = document.createElement("input");
        input.type = field.type ?? "text";
        if (field.accept) input.accept = field.accept;
        if (field.pattern) input.pattern = field.pattern;
        if (field.min) input.min = field.min;
        if (field.max) input.max = field.max;
        if (field.step) input.step = field.step;
        control = input;
      }
      control.name = field.key;
      control.required = Boolean(field.required);
      if (field.minLength && !(control instanceof HTMLSelectElement))
        control.minLength = field.minLength;
      if (field.value !== undefined) control.value = field.value;
      label.append(control);
      grid.append(label);
    }
    if (options.fields?.length) form.append(grid);
    cancel.type = "button";
    cancel.textContent = options.cancelLabel;
    confirm.type = "submit";
    confirm.className = options.danger ? "danger-action" : "primary";
    confirm.textContent = options.confirmLabel;
    footer.append(cancel, confirm);
    form.append(footer);
    dialog.append(form);
    document.body.append(dialog);
    let settled = false;
    const finish = (result: WorkflowDialogResult | null) => {
      if (settled) return;
      settled = true;
      dialog.close();
      dialog.remove();
      previouslyFocused?.focus();
      resolve(result);
    };
    close.addEventListener("click", () => finish(null));
    cancel.addEventListener("click", () => finish(null));
    dialog.addEventListener("cancel", (event) => {
      event.preventDefault();
      finish(null);
    });
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      if (!form.reportValidity()) return;
      const result: WorkflowDialogResult = {};
      for (const field of options.fields ?? []) {
        const control = form.elements.namedItem(field.key);
        if (control instanceof HTMLInputElement && control.type === "file") {
          const file = control.files?.[0];
          if (file) result[field.key] = file;
        } else if (
          control instanceof HTMLInputElement ||
          control instanceof HTMLTextAreaElement ||
          control instanceof HTMLSelectElement
        )
          result[field.key] = control.value.trim();
      }
      finish(result);
    });
    dialog.showModal();
    const initialFocus = form.querySelector(
      "input,textarea,select",
    ) as HTMLElement | null;
    (initialFocus ?? close).focus();
  });
}

const text = {
  es: {
    product: "Jornada PR",
    tagline: "Nómina, personal y tiempo para Puerto Rico",
    signIn: "Acceso de desarrollo",
    signInDetail: "Ingrese para configurar la plataforma por primera vez.",
    email: "Correo electrónico",
    password: "Contraseña",
    enter: "Entrar a Jornada PR",
    checking: "Verificando sesión",
    signingIn: "Ingresando",
    invalid: "No se pudo iniciar la sesión. Verifique la contraseña.",
    secure: "Acceso cifrado y restringido al desarrollador autorizado",
    firstCompany: "Configure su primera compañía",
    firstCompanyDetail:
      "No existe información empresarial todavía. Estos datos se guardarán en Cloudflare.",
    legalName: "Nombre legal de la compañía",
    displayName: "Nombre para mostrar",
    language: "Idioma predeterminado",
    spanish: "Español — Puerto Rico",
    english: "English — Puerto Rico",
    createCompany: "Crear compañía",
    creating: "Creando compañía",
    required: "Complete todos los campos requeridos.",
    home: "Inicio",
    self: "Mi cuenta",
    personnel: "Personal",
    personnel_setup: "Configuración de la persona",
    time: "Tiempo",
    tips: "Propinas",
    time_log: "LOG",
    payroll: "Compensación",
    expenses: "Gastos",
    documents: "Documentos",
    leave: "Licencias",
    reports: "Informes",
    settings: "Configuración",
    help: "Ayuda",
    welcome: "Configuración inicial",
    welcomeDetail:
      "La compañía fue creada. Continúe configurando los módulos necesarios antes de procesar nómina.",
    addFirstWorker: "Añadir primera persona",
    companyComplete: "Información de la compañía",
    nextPersonnel: "Añadir personal",
    nextPolicies: "Configurar políticas",
    nextPayroll: "Configurar nómina",
    setupProgress: "Progreso de configuración",
    stepsComplete: (completed: number, total: number) =>
      `${completed} de ${total} pasos completados`,
    setupLoading: "Calculando desde Cloudflare",
    setupUnavailable: "No se pudo calcular el progreso. Intente actualizar.",
    requirementsRemaining: (count: number) =>
      `${count} requisito${count === 1 ? "" : "s"} pendiente${count === 1 ? "" : "s"}`,
    workersAdded: (count: number) =>
      `${count} persona${count === 1 ? "" : "s"} añadida${count === 1 ? "" : "s"}`,
    payrollReady: (ready: number, total: number) =>
      `${ready} de ${total} personas listas para nómina`,
    stepComplete: "Requisitos completados",
    companyProfile: "Perfil de la compañía",
    companyProfileDetail: "Información básica guardada",
    logout: "Cerrar sesión",
    developer: "Desarrollador",
    menu: "Menú",
    save: "Guardar configuración",
    saved: "Configuración guardada",
    officialGuidance: "Guía y fuentes oficiales",
  },
  en: {
    product: "Jornada PR",
    tagline: "Payroll, personnel, and time for Puerto Rico",
    signIn: "Developer access",
    signInDetail: "Sign in to configure the platform for the first time.",
    email: "Email address",
    password: "Password",
    enter: "Enter Jornada PR",
    checking: "Checking session",
    signingIn: "Signing in",
    invalid: "Unable to sign in. Check the password.",
    secure: "Encrypted access restricted to the authorized developer",
    firstCompany: "Configure your first company",
    firstCompanyDetail:
      "No company information exists yet. These details will be stored in Cloudflare.",
    legalName: "Legal company name",
    displayName: "Display name",
    language: "Default language",
    spanish: "Español — Puerto Rico",
    english: "English — Puerto Rico",
    createCompany: "Create company",
    creating: "Creating company",
    required: "Complete all required fields.",
    home: "Home",
    self: "My account",
    personnel: "Personnel",
    personnel_setup: "Worker setup",
    time: "Time",
    tips: "Tips",
    time_log: "LOG",
    payroll: "Compensation",
    expenses: "Expenses",
    documents: "Documents",
    leave: "Leave",
    reports: "Reports",
    settings: "Settings",
    help: "Help",
    welcome: "Initial setup",
    welcomeDetail:
      "The company was created. Continue configuring required modules before processing payroll.",
    addFirstWorker: "Add first person",
    companyComplete: "Company information",
    nextPersonnel: "Add personnel",
    nextPolicies: "Configure policies",
    nextPayroll: "Configure payroll",
    setupProgress: "Setup progress",
    stepsComplete: (completed: number, total: number) =>
      `${completed} of ${total} steps complete`,
    setupLoading: "Calculating from Cloudflare",
    setupUnavailable:
      "Setup progress could not be calculated. Refresh and try again.",
    requirementsRemaining: (count: number) =>
      `${count} requirement${count === 1 ? "" : "s"} remaining`,
    workersAdded: (count: number) =>
      `${count} worker${count === 1 ? "" : "s"} added`,
    payrollReady: (ready: number, total: number) =>
      `${ready} of ${total} workers ready for payroll`,
    stepComplete: "Requirements complete",
    companyProfile: "Company profile",
    companyProfileDetail: "Basic information saved",
    logout: "Sign out",
    developer: "Developer",
    menu: "Menu",
    save: "Save configuration",
    saved: "Configuration saved",
    officialGuidance: "Guidance and official sources",
  },
} as const;

const navIcons = {
  home: LayoutDashboard,
  self: UserRound,
  personnel: Users,
  personnel_setup: Users,
  time: Clock3,
  tips: Coins,
  time_log: ClipboardCheck,
  payroll: CircleDollarSign,
  expenses: FileText,
  documents: FileText,
  leave: CalendarDays,
  reports: BarChart3,
  settings: Settings,
  help: HelpCircle,
};

const primaryAreaForSection: Record<Section, PrimaryArea> = {
  home: "home",
  self: "home",
  personnel: "people",
  personnel_setup: "people",
  documents: "people",
  time: "time",
  tips: "time",
  time_log: "time",
  leave: "time",
  expenses: "time",
  payroll: "payroll",
  reports: "payroll",
  settings: "company",
  help: "company",
};
const primaryDefault: Record<PrimaryArea, Section> = {
  home: "home",
  people: "personnel",
  time: "time",
  payroll: "payroll",
  company: "settings",
};
export const activePrimaryAreaForSection = (
  section: Section,
  helpOrigin: Exclude<Section, "help">,
): PrimaryArea =>
  primaryAreaForSection[section === "help" ? helpOrigin : section];

export const primaryAreasForNav = (
  nav: readonly Section[],
  order: readonly PrimaryArea[],
) =>
  order.filter((area) =>
    nav.some((item) => item !== "help" && primaryAreaForSection[item] === area),
  );
const primaryIcons: Record<PrimaryArea, typeof LayoutDashboard> = {
  home: LayoutDashboard,
  people: Users,
  time: Clock3,
  payroll: CircleDollarSign,
  company: Building2,
};

export const sectionPaths: Record<Section, string> = {
  home: "/home",
  self: "/account",
  personnel: "/people",
  personnel_setup: "/people/setup",
  documents: "/people/documents",
  time: "/time",
  tips: "/time/tips",
  time_log: "/time/log",
  leave: "/time/leave",
  expenses: "/time/expenses",
  payroll: "/payroll",
  reports: "/payroll/reports",
  settings: "/company",
  help: "/help",
};
export const sectionFromPath = (path: string): Section =>
  path === "/account"
    ? "self"
    : path.startsWith("/people/setup")
      ? "personnel_setup"
      : path.startsWith("/people/documents")
        ? "documents"
        : path.startsWith("/people")
          ? "personnel"
          : path.startsWith("/time/log")
            ? "time_log"
            : path.startsWith("/time/tips")
              ? "tips"
              : path.startsWith("/time/leave")
                ? "leave"
                : path.startsWith("/time/expenses")
                  ? "expenses"
                  : path.startsWith("/time")
                    ? "time"
                    : path.startsWith("/payroll/reports")
                      ? "reports"
                      : path.startsWith("/payroll")
                        ? "payroll"
                        : path.startsWith("/company")
                          ? "settings"
                          : path.startsWith("/help")
                            ? "help"
                            : "home";
const sectionToPath = (section: Section) => sectionPaths[section];
export const workerSectionPaths: Record<WorkerSection, string> = {
  home: "/home",
  profile: "/profile",
  time: "/time",
  leave: "/leave",
  pay: "/pay",
  help: "/help",
};
export const workerSectionFromPath = (path: string): WorkerSection =>
  path.startsWith("/profile")
    ? "profile"
    : path.startsWith("/time")
      ? "time"
      : path.startsWith("/leave")
        ? "leave"
        : path.startsWith("/pay")
          ? "pay"
          : path.startsWith("/help")
            ? "help"
            : "home";
const workerSectionToPath = (section: WorkerSection) =>
  workerSectionPaths[section];

function useRoutedSection<T extends string>(
  initial: T,
  fromPath: (path: string) => T,
  toPath: (section: T) => string,
) {
  const [section, setCurrent] = useState<T>(() =>
    fromPath(window.location.pathname),
  );
  useEffect(() => {
    const onPop = () => setCurrent(fromPath(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [fromPath]);
  useEffect(() => {
    if (window.location.pathname === "/")
      window.history.replaceState({}, "", toPath(initial));
  }, [initial, toPath]);
  const setSection = useCallback(
    (next: T) => {
      setCurrent(next);
      const path = toPath(next);
      if (window.location.pathname !== path)
        window.history.pushState({}, "", path);
    },
    [toPath],
  );
  return [section, setSection] as const;
}

async function api(path: string, init?: RequestInit): Promise<Response> {
  const headers = new Headers(init?.headers);
  if (!(init?.body instanceof FormData))
    headers.set("content-type", "application/json");
  return fetch(`${API}${path}`, {
    ...init,
    credentials: "include",
    headers,
  });
}

async function apiErrorCode(
  response: Response,
  fallback: string,
): Promise<string> {
  try {
    const body = (await response.json()) as {
      error?: string | { code?: string };
    };
    if (typeof body.error === "string" && body.error) return body.error;
    if (
      body.error &&
      typeof body.error === "object" &&
      typeof body.error.code === "string" &&
      body.error.code
    )
      return body.error.code;
  } catch {}
  return fallback;
}

export function App() {
  const [locale, setLocale] = useState<Locale>("es");
  const [phase, setPhase] = useState<
    "loading" | "login" | "company" | "app" | "worker"
  >("loading");
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [workerAccount, setWorkerAccount] = useState<WorkerAccount | null>(
    null,
  );
  const [developerEmail, setDeveloperEmail] = useState("");

  useEffect(() => {
    document.documentElement.lang = localeTag(locale);
  }, [locale]);

  useEffect(() => {
    const messageFor = (control: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement) => {
      if (control.validity.valueMissing)
        return locale === "es" ? "Este campo es obligatorio." : "This field is required.";
      if (control.validity.typeMismatch || control.validity.patternMismatch)
        return locale === "es" ? "Use el formato indicado para este campo." : "Use the format shown for this field.";
      if (control.validity.rangeUnderflow)
        return locale === "es" ? `El valor mínimo permitido es ${control.getAttribute("min")}.` : `The minimum allowed value is ${control.getAttribute("min")}.`;
      if (control.validity.rangeOverflow)
        return locale === "es" ? `El valor máximo permitido es ${control.getAttribute("max")}.` : `The maximum allowed value is ${control.getAttribute("max")}.`;
      if (control.validity.tooShort)
        return locale === "es" ? `Escriba por lo menos ${control.getAttribute("minlength")} caracteres.` : `Enter at least ${control.getAttribute("minlength")} characters.`;
      return locale === "es" ? "Revise este valor antes de continuar." : "Review this value before continuing.";
    };
    const clear = (control: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement) => {
      const messageId = control.getAttribute("aria-errormessage");
      if (messageId) document.getElementById(messageId)?.remove();
      control.removeAttribute("aria-errormessage");
      control.removeAttribute("aria-invalid");
    };
    const invalid = (event: Event) => {
      const control = event.target;
      if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement || control instanceof HTMLTextAreaElement)) return;
      event.preventDefault();
      const firstInvalid = control.form?.querySelector<HTMLElement>('[aria-invalid="true"]') ?? null;
      clear(control);
      const message = document.createElement("small");
      message.id = `field-error-${crypto.randomUUID()}`;
      message.className = "form-error field-validation-message";
      message.setAttribute("role", "alert");
      message.textContent = messageFor(control);
      control.setAttribute("aria-invalid", "true");
      control.setAttribute("aria-errormessage", message.id);
      control.insertAdjacentElement("afterend", message);
      if (!firstInvalid) control.focus();
    };
    const input = (event: Event) => {
      const control = event.target;
      if ((control instanceof HTMLInputElement || control instanceof HTMLSelectElement || control instanceof HTMLTextAreaElement) && control.validity.valid) clear(control);
    };
    document.addEventListener("invalid", invalid, true);
    document.addEventListener("input", input, true);
    document.addEventListener("change", input, true);
    return () => {
      document.removeEventListener("invalid", invalid, true);
      document.removeEventListener("input", input, true);
      document.removeEventListener("change", input, true);
    };
  }, [locale]);

  useEffect(() => {
    const dialogFocusOrigins = new Map<
      HTMLElement,
      { origin: HTMLElement | null; dispose: () => void }
    >();
    const announceInlineMessages = (root: ParentNode) => {
      root.querySelectorAll<HTMLElement>(".form-error").forEach((message) => {
        message.setAttribute("role", "alert");
        message.setAttribute("aria-live", "assertive");
      });
      const dialogs = [
        ...(root instanceof HTMLElement &&
        root.matches("dialog.workflow-dialog[open]")
          ? [root]
          : []),
        ...root.querySelectorAll<HTMLElement>("dialog.workflow-dialog[open]"),
      ];
      dialogs.forEach((dialog) => {
        if (dialogFocusOrigins.has(dialog)) return;
        const origin =
          document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
        const handleCancel = (event: Event) => {
          event.preventDefault();
          dialog
            .querySelector<HTMLButtonElement>('header button[type="button"]')
            ?.click();
        };
        dialog.addEventListener("cancel", handleCancel);
        dialogFocusOrigins.set(dialog, {
          origin,
          dispose: () => dialog.removeEventListener("cancel", handleCancel),
        });
        queueMicrotask(() => {
          if (
            dialog instanceof HTMLDialogElement &&
            dialog.open &&
            !dialog.matches(":modal")
          ) {
            dialog.close();
            dialog.showModal();
          }
          dialog
            .querySelector<HTMLElement>("input, textarea, select, button")
            ?.focus();
        });
      });
    };
    announceInlineMessages(document);
    const observer = new MutationObserver((records) => {
      records.forEach((record) =>
        record.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          if (node.matches(".form-error")) {
            node.setAttribute("role", "alert");
            node.setAttribute("aria-live", "assertive");
          }
          announceInlineMessages(node);
        }),
      );
      records.forEach((record) =>
        record.removedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          const removedDialogs = [
            node,
            ...node.querySelectorAll<HTMLElement>("dialog.workflow-dialog"),
          ].filter((item) => item.matches("dialog.workflow-dialog"));
          removedDialogs.forEach((dialog) => {
            const focusState = dialogFocusOrigins.get(dialog);
            focusState?.dispose();
            focusState?.origin?.focus();
            dialogFocusOrigins.delete(dialog);
          });
        }),
      );
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      dialogFocusOrigins.forEach(({ dispose }) => dispose());
      dialogFocusOrigins.clear();
    };
  }, []);

  const loadBootstrap = async () => {
    let response: Response;
    try {
      response = await api("/v1/bootstrap/status");
    } catch {
      setPhase("login");
      return;
    }
    if (!response.ok) {
      const worker = await api("/auth/worker/session");
      if (worker.ok) {
        const body = (await worker.json()) as { account: WorkerAccount };
        setWorkerAccount(body.account);
        setLocale(body.account.defaultLocale === "es-PR" ? "es" : "en");
        setPhase("worker");
      } else setPhase("login");
      return;
    }
    const body = (await response.json()) as {
      developer: { email: string };
      tenants: Tenant[];
    };
    setDeveloperEmail(body.developer.email);
    setTenants(body.tenants);
    setSelectedTenantId((current) =>
      body.tenants.some(
        (item) => item.id === current && item.status === "active",
      )
        ? current
        : null,
    );
    setPhase(body.tenants.length === 0 ? "company" : "app");
  };

  useEffect(() => {
    void loadBootstrap();
  }, []);

  if (phase === "loading") return <Loading locale={locale} />;
  if (phase === "login")
    return (
      <Login
        locale={locale}
        setLocale={setLocale}
        onSuccess={loadBootstrap}
        onWorkerSuccess={(account) => {
          setWorkerAccount(account);
          setPhase("worker");
        }}
      />
    );
  if (phase === "company")
    return (
      <CompanySetup
        locale={locale}
        setLocale={setLocale}
        onCreated={(tenant) => {
          setTenants([tenant]);
          setPhase("app");
        }}
      />
    );
  if (phase === "worker" && workerAccount) {
    const workerLogout = () => {
      setWorkerAccount(null);
      setPhase("login");
    };
    if (workerAccount.roleKey !== "user")
      return (
        <Workspace
          locale={locale}
          setLocale={setLocale}
          tenant={{
            id: workerAccount.tenantId,
            displayName: workerAccount.tenantName,
            legalName: workerAccount.legalName,
            defaultLocale: workerAccount.defaultLocale,
            status: "active",
          }}
          actor={workerAccount}
          developerEmail={developerEmail}
          onLogout={workerLogout}
        />
      );
    return (
      <WorkerWorkspace
        locale={locale}
        setLocale={setLocale}
        account={workerAccount}
        onLogout={workerLogout}
      />
    );
  }
  const selectedTenant = tenants.find(
    (item) => item.id === selectedTenantId && item.status === "active",
  );
  if (!selectedTenant)
    return (
      <CompanyDirectory
        locale={locale}
        setLocale={setLocale}
        tenants={tenants}
        reload={loadBootstrap}
        onSelect={(tenant) => {
          setLocale(tenant.defaultLocale === "es-PR" ? "es" : "en");
          setSelectedTenantId(tenant.id);
        }}
        onLogout={() => {
          setDeveloperEmail("");
          setPhase("login");
        }}
      />
    );
  return (
    <Workspace
      locale={locale}
      setLocale={setLocale}
      tenant={selectedTenant}
      developerEmail={developerEmail}
      onBack={() => setSelectedTenantId(null)}
      onLogout={() => {
        setDeveloperEmail("");
        setPhase("login");
      }}
    />
  );
}

function LanguageButton({
  locale,
  setLocale,
}: {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}) {
  return (
    <button
      className="language"
      onClick={() => setLocale(locale === "es" ? "en" : "es")}
    >
      <Languages size={17} />
      {locale === "es" ? "EN" : "ES"}
    </button>
  );
}

function Loading({ locale }: { locale: Locale }) {
  const t = text[locale];
  return (
    <main className="center-screen">
      <div className="loading-card">
        <div className="logo">J</div>
        <LoaderCircle className="spin" />
        <p>{t.checking}</p>
      </div>
    </main>
  );
}

function Login({
  locale,
  setLocale,
  onSuccess,
  onWorkerSuccess,
}: {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  onSuccess: () => Promise<void>;
  onWorkerSuccess: (account: WorkerAccount) => void;
}) {
  const t = text[locale];
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [mode, setMode] = useState<"developer" | "worker">("developer");
  if (mode === "worker")
    return (
      <WorkerLogin
        locale={locale}
        setLocale={setLocale}
        onBack={() => setMode("developer")}
        onSuccess={onWorkerSuccess}
      />
    );

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(false);
    const response = await api("/auth/developer/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    if (response.ok) await onSuccess();
    else setError(true);
    setBusy(false);
  };

  return (
    <main className="auth-layout">
      <ActionFeedbackCoordinator />
      <section className="auth-brand">
        <div className="brand-lockup">
          <div className="logo">J</div>
          <div>
            <strong>{t.product}</strong>
            <span>{t.tagline}</span>
          </div>
        </div>
        <div className="brand-message">
          <ShieldCheck />
          <h1>
            {locale === "es"
              ? "Una plataforma creada para la manera en que trabaja Puerto Rico."
              : "A platform built for the way Puerto Rico works."}
          </h1>
          <p>
            {locale === "es"
              ? "Configure cada compañía desde cero con información aislada, permisos claros y un historial auditable."
              : "Configure every company from the ground up with isolated information, clear permissions, and an auditable history."}
          </p>
        </div>
        <small>© 2026 Jornada PR</small>
      </section>
      <section className="auth-panel">
        <div className="auth-top">
          <LanguageButton locale={locale} setLocale={setLocale} />
        </div>
        <form className="auth-form" onSubmit={submit}>
          <span className="form-icon">
            <LockKeyhole />
          </span>
          <p className="kicker">JORNADA PR</p>
          <h2>{t.signIn}</h2>
          <p className="form-detail">{t.signInDetail}</p>
          <label>
            {t.email}
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="username"
              required
              autoFocus
            />
          </label>
          <label>
            {t.password}
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
            />
          </label>
          {error && <p className="form-error">{t.invalid}</p>}
          <button className="primary" disabled={busy || email.length === 0 || password.length === 0}>
            {busy ? (
              <>
                <LoaderCircle className="spin" />
                {t.signingIn}
              </>
            ) : (
              <>
                {t.enter}
                <ChevronRight />
              </>
            )}
          </button>
          <p className="secure-note">
            <ShieldCheck />
            {t.secure}
          </p>
          <button
            type="button"
            className="secondary auth-secondary"
            onClick={() => setMode("worker")}
          >
            {locale === "es"
              ? "Acceso de empleado o contratista"
              : "Employee or contractor access"}
          </button>
        </form>
      </section>
    </main>
  );
}

function workerDeviceId() {
  const key = "jornada-pr-device-id";
  let value = localStorage.getItem(key);
  if (!value) {
    value = `web-${crypto.randomUUID()}`;
    localStorage.setItem(key, value);
  }
  return value;
}
function WorkerLogin({
  locale,
  setLocale,
  onBack,
  onSuccess,
}: {
  locale: Locale;
  setLocale: (value: Locale) => void;
  onBack: () => void;
  onSuccess: (account: WorkerAccount) => void;
}) {
  const [phone, setPhone] = useState(""),
    [passwordValue, setPasswordValue] = useState(""),
    [tenantId, setTenantId] = useState(""),
    [companies, setCompanies] = useState<
      { tenantId: string; tenantName: string }[]
    >([]),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [recovering, setRecovering] = useState(false),
    [activating, setActivating] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const response = await api("/auth/worker/login", {
      method: "POST",
      body: JSON.stringify({
        phoneE164: phone,
        password: passwordValue,
        devicePublicId: workerDeviceId(),
        tenantId: tenantId || undefined,
      }),
    });
    if (response.status === 409) {
      const body = (await response.json()) as {
        companies: { tenantId: string; tenantName: string }[];
      };
      setCompanies(body.companies ?? []);
      setError(
        locale === "es" ? "Seleccione la compañía." : "Select the company.",
      );
    } else if (response.ok) {
      const session = await api("/auth/worker/session");
      if (session.ok)
        onSuccess(
          ((await session.json()) as { account: WorkerAccount }).account,
        );
      else setError("session");
    } else
      setError(
        locale === "es"
          ? "No se pudo iniciar sesión. Verifique el teléfono, contraseña y dispositivo registrado."
          : "Unable to sign in. Check the phone, password, and registered device.",
      );
    setBusy(false);
  };
  if (recovering)
    return (
      <PasswordRecovery
        locale={locale}
        setLocale={setLocale}
        onBack={() => setRecovering(false)}
      />
    );
  if (activating)
    return (
      <WorkerRegistration
        locale={locale}
        setLocale={setLocale}
        onBack={() => setActivating(false)}
      />
    );
  return (
    <main className="center-screen">
      <form className="auth-form" onSubmit={submit}>
        <div className="auth-top">
          <LanguageButton locale={locale} setLocale={setLocale} />
        </div>
        <span className="form-icon">
          <UserRound />
        </span>
        <p className="kicker">JORNADA PR</p>
        <h2>{locale === "es" ? "Acceso de personal" : "Worker access"}</h2>
        <p className="form-detail">
          {locale === "es"
            ? "Use el número móvil y la contraseña de su cuenta aprobada."
            : "Use the mobile number and password for your approved account."}
        </p>
        <label>
          {locale === "es" ? "Teléfono móvil" : "Mobile phone"}
          <input
            type="tel"
            placeholder="+17875551234"
            value={phone}
            onChange={(e) => setPhone(e.currentTarget.value)}
            required
          />
        </label>
        <label>
          {locale === "es" ? "Contraseña" : "Password"}
          <input
            type="password"
            autoComplete="current-password"
            value={passwordValue}
            onChange={(e) => setPasswordValue(e.currentTarget.value)}
            required
          />
        </label>
        {companies.length > 0 && (
          <label>
            {locale === "es" ? "Compañía" : "Company"}
            <select
              required
              value={tenantId}
              onChange={(e) => setTenantId(e.currentTarget.value)}
            >
              <option value="">—</option>
              {companies.map((company) => (
                <option key={company.tenantId} value={company.tenantId}>
                  {company.tenantName}
                </option>
              ))}
            </select>
          </label>
        )}
        <OperationalError value={error} locale={locale} />
        <button className="primary" disabled={busy}>
          {busy ? (
            <LoaderCircle className="spin" />
          ) : locale === "es" ? (
            "Entrar"
          ) : (
            "Sign in"
          )}
        </button>
        <div className="auth-secondary-actions">
          <button type="button" onClick={() => setActivating(true)}>
            {locale === "es"
              ? "Activar cuenta invitada"
              : "Activate invited account"}
          </button>
          <button type="button" onClick={() => setRecovering(true)}>
            {locale === "es" ? "Olvidé mi contraseña" : "Forgot my password"}
          </button>
        </div>
        <button className="auth-back-action" type="button" onClick={onBack}>
          {locale === "es"
            ? "Volver al acceso de desarrollo"
            : "Back to developer access"}
        </button>
      </form>
    </main>
  );
}

function WorkerRegistration({
  locale,
  setLocale,
  onBack,
}: {
  locale: Locale;
  setLocale: (value: Locale) => void;
  onBack: () => void;
}) {
  const [challengeId, setChallengeId] = useState(""),
    [code, setCode] = useState(""),
    [password, setPassword] = useState(""),
    [consent, setConsent] = useState(false),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMessage("");
    const response = await api("/auth/worker/register", {
      method: "POST",
      body: JSON.stringify({
        challengeId,
        code,
        password,
        preferredLocale: locale === "es" ? "es-PR" : "en-PR",
        devicePublicId: workerDeviceId(),
        deviceLabel: navigator.userAgent.slice(0, 100),
        platform: "web",
        consents: [
          {
            key: "electronic_records_and_platform_terms",
            version: "2026-08-15",
          },
        ],
      }),
    });
    if (response.ok)
      setMessage(
        locale === "es"
          ? "Cuenta registrada. Un administrador debe aprobarla antes del primer acceso."
          : "Account registered. An administrator must approve it before first sign-in.",
      );
    else
      setMessage(
        locale === "es"
          ? "La invitación o el código no son válidos, expiraron o ya se usaron."
          : "The invitation or code is invalid, expired, or already used.",
      );
    setBusy(false);
  };
  return (
    <main className="center-screen">
      <form className="auth-form" onSubmit={submit}>
        <div className="auth-top">
          <LanguageButton locale={locale} setLocale={setLocale} />
        </div>
        <span className="form-icon">
          <UserRound />
        </span>
        <h2>{locale === "es" ? "Activar cuenta" : "Activate account"}</h2>
        <p className="form-detail">
          {locale === "es"
            ? "Use el identificador de invitación y código de seis dígitos enviados por SMS."
            : "Use the invitation identifier and six-digit code sent by SMS."}
        </p>
        <label>
          {locale === "es"
            ? "Identificador de invitación"
            : "Invitation identifier"}
          <input
            value={challengeId}
            onChange={(e) => setChallengeId(e.currentTarget.value)}
            required
          />
        </label>
        <label>
          {locale === "es" ? "Código SMS" : "SMS code"}
          <input
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.currentTarget.value)}
            required
          />
        </label>
        <label>
          {locale === "es" ? "Crear contraseña" : "Create password"}
          <input
            type="password"
            autoComplete="new-password"
            minLength={12}
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            required
          />
          <small>
            {locale === "es"
              ? "Mínimo 12 caracteres."
              : "At least 12 characters."}
          </small>
        </label>
        <label className="check-label">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.currentTarget.checked)}
            required
          />
          {locale === "es"
            ? "Acepto los términos de la plataforma y el uso de registros electrónicos."
            : "I accept the platform terms and use of electronic records."}
        </label>
        {message && <p className="form-detail action-result">{message}</p>}
        <button className="primary" disabled={busy || !consent}>
          {busy ? (
            <LoaderCircle className="spin" />
          ) : locale === "es" ? (
            "Registrar cuenta"
          ) : (
            "Register account"
          )}
        </button>
        <button type="button" onClick={onBack}>
          {locale === "es" ? "Volver" : "Back"}
        </button>
      </form>
    </main>
  );
}

function PasswordRecovery({
  locale,
  setLocale,
  onBack,
}: {
  locale: Locale;
  setLocale: (value: Locale) => void;
  onBack: () => void;
}) {
  const [phone, setPhone] = useState(""),
    [challengeId, setChallengeId] = useState(""),
    [code, setCode] = useState(""),
    [password, setPassword] = useState(""),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  const request = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMessage("");
    const response = await api("/auth/worker/password-recovery", {
      method: "POST",
      body: JSON.stringify({ phoneE164: phone }),
    });
    if (response.ok) {
      const body = (await response.json()) as { challengeId: string };
      setChallengeId(body.challengeId);
      setMessage(
        locale === "es"
          ? "Si la cuenta existe, enviamos un código por SMS."
          : "If the account exists, we sent an SMS code.",
      );
    } else
      setMessage(
        locale === "es"
          ? "Revise el formato del teléfono."
          : "Check the phone format.",
      );
    setBusy(false);
  };
  const complete = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMessage("");
    const response = await api("/auth/worker/password-recovery/complete", {
      method: "POST",
      body: JSON.stringify({
        challengeId,
        code,
        newPassword: password,
        devicePublicId: workerDeviceId(),
        deviceLabel: navigator.userAgent.slice(0, 100),
        platform: "web",
      }),
    });
    if (response.ok) {
      const body = (await response.json()) as { pendingReview?: boolean };
      if (body.pendingReview)
        setMessage(
          locale === "es"
            ? "La cuenta privilegiada requiere revisión manual y un período de espera de 24 horas. Recibirá una notificación con la decisión."
            : "The privileged account requires manual review and a 24-hour waiting period. You will receive a decision notification.",
        );
      else {
        setMessage(
          locale === "es"
            ? "Contraseña cambiada. Ya puede iniciar sesión."
            : "Password changed. You can now sign in.",
        );
        setTimeout(onBack, 1500);
      }
    } else
      setMessage(
        locale === "es"
          ? "El código no es válido, expiró o ya existe una revisión pendiente."
          : "The code is invalid, expired, or a review is already pending.",
      );
    setBusy(false);
  };
  return (
    <main className="center-screen">
      <form className="auth-form" onSubmit={challengeId ? complete : request}>
        <div className="auth-top">
          <LanguageButton locale={locale} setLocale={setLocale} />
        </div>
        <span className="form-icon">
          <LockKeyhole />
        </span>
        <h2>{locale === "es" ? "Recuperar contraseña" : "Recover password"}</h2>
        {!challengeId ? (
          <label>
            {locale === "es" ? "Teléfono móvil" : "Mobile phone"}
            <input
              type="tel"
              placeholder="+17875551234"
              value={phone}
              onChange={(e) => setPhone(e.currentTarget.value)}
              required
            />
          </label>
        ) : (
          <>
            <label>
              {locale === "es" ? "Código SMS" : "SMS code"}
              <input
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.currentTarget.value)}
                required
                autoFocus
              />
            </label>
            <label>
              {locale === "es" ? "Nueva contraseña" : "New password"}
              <input
                type="password"
                autoComplete="new-password"
                minLength={12}
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                required
              />
            </label>
          </>
        )}
        {message && <p className="form-detail action-result">{message}</p>}
        <button className="primary" disabled={busy}>
          {busy ? (
            <LoaderCircle className="spin" />
          ) : challengeId ? (
            locale === "es" ? (
              "Cambiar contraseña"
            ) : (
              "Change password"
            )
          ) : locale === "es" ? (
            "Enviar código"
          ) : (
            "Send code"
          )}
        </button>
        <button type="button" onClick={onBack}>
          {locale === "es" ? "Volver" : "Back"}
        </button>
      </form>
    </main>
  );
}

type WorkerSimulator = {
  viewport: "desktop" | "tablet" | "iphone" | "android";
  mode: "read_only" | "interactive";
  workerType: ReviewContext["workerType"];
  onViewport: (value: "desktop" | "tablet" | "iphone" | "android") => void;
  onMode: (value: "read_only" | "interactive") => void;
  onReviewChange: (context: ReviewContext) => void;
};

export const workerNavigationKeys = (
  workerType: ReviewContext["workerType"],
): Exclude<WorkerSection, "help">[] =>
  workerType === "professional_services"
    ? ["home", "time", "pay", "profile"]
    : ["home", "time", "leave", "pay", "profile"];

function WorkerWorkspace({
  locale,
  setLocale,
  account,
  onLogout,
  simulator,
  developerEmail,
}: {
  locale: Locale;
  setLocale: (value: Locale) => void;
  account: WorkerAccount;
  onLogout: () => void;
  simulator?: WorkerSimulator;
  developerEmail?: string | undefined;
}) {
  const [section, setSection] = useRoutedSection<WorkerSection>(
    "home",
    workerSectionFromPath,
    workerSectionToPath,
  );
  const [contextHelpArticleId, setContextHelpArticleId] = useState<
      string | null
    >(null),
    [workerType, setWorkerType] = useState(simulator?.workerType ?? "employee"),
    [menuOpen, setMenuOpen] = useState(false);
  const tenant: Tenant = {
    id: account.tenantId,
    displayName: account.tenantName,
    legalName: account.legalName,
    defaultLocale: account.defaultLocale,
    status: "active",
  };
  const logout = async () => {
    await api(simulator ? "/auth/logout" : "/auth/worker/logout", {
      method: "POST",
    });
    onLogout();
  };
  const routeIncompleteTaxSetup = async () => {
    const profileResponse = await api(`/v1/tenants/${tenant.id}/self/profile`);
    if (!profileResponse.ok) return;
    const body = (await profileResponse.json()) as {
      profile: {
        workerType: string;
        address: unknown;
        taxpayerIdentifierPresent: boolean;
      };
    };
    const resolvedWorkerType =
      body.profile.workerType === "professional_services"
        ? "professional_services"
        : body.profile.workerType === "pending_classification"
          ? "pending_classification"
          : "employee";
    setWorkerType(resolvedWorkerType);
    const profileReady = Boolean(
      body.profile.address && body.profile.taxpayerIdentifierPresent,
    );
    if (!profileReady) setSection("profile");
    else if (resolvedWorkerType === "employee") {
      const taxResponse = await api(
        `/v1/tenants/${tenant.id}/self/tax-questionnaire`,
      );
      if (
        taxResponse.ok &&
        !Boolean(((await taxResponse.json()) as { current: unknown }).current)
      )
        setSection("pay");
    }
  };
  useEffect(() => {
    void routeIncompleteTaxSetup();
  }, [tenant.id]);
  const helpSection: Record<Exclude<WorkerSection, "help">, HelpSection> = {
    home: "home",
    profile: "personnel",
    time: "time",
    leave: "leave",
    pay: "payroll",
  };
  const openContextHelp = () => {
    if (section === "help") return;
    setContextHelpArticleId(
      contextualHelpArticle(
        "user",
        helpSection[section],
        contractor ? "professional_services" : "employee",
      )?.id ?? null,
    );
    setSection("help");
  };
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [section]);
  const navigateFromHelp = (target: Section) =>
    setSection(
      (
        {
          personnel: "profile",
          time: "time",
          leave: "leave",
          payroll: "pay",
          expenses: "pay",
          documents: "pay",
          settings: "profile",
        } as Partial<Record<Section, WorkerSection>>
      )[target] ?? "home",
    );
  const contractor = workerType === "professional_services",
    itemCatalog: Record<
      Exclude<WorkerSection, "help">,
      {
        key: Exclude<WorkerSection, "help">;
        icon: typeof UserRound;
        es: string;
        en: string;
      }
    > = {
      home: { key: "home", icon: LayoutDashboard, es: "Inicio", en: "Home" },
      time: {
        key: "time",
        icon: Clock3,
        es: contractor ? "Servicios" : "Tiempo",
        en: contractor ? "Services" : "Time",
      },
      leave: { key: "leave", icon: CalendarDays, es: "Licencias", en: "Leave" },
      pay: {
        key: "pay",
        icon: CircleDollarSign,
        es: contractor ? "Pagos" : "Pago",
        en: contractor ? "Payments" : "Pay",
      },
      profile: { key: "profile", icon: UserRound, es: "Perfil", en: "Profile" },
    },
    items = workerNavigationKeys(workerType).map((key) => itemCatalog[key]);
  return (
    <div
      className={`workspace worker-shell${simulator ? ` preview-${simulator.viewport}` : ""}`}
    >
      <ActionFeedbackCoordinator />
      <header className="app-shell-header">
        <div className="app-shell-bar">
          <div className="brand-lockup dark">
            <div className="logo">J</div>
            <div>
              <strong>Jornada PR</strong>
              <span>{account.tenantName}</span>
            </div>
          </div>
          <button
            className="open-menu"
            onClick={() => setMenuOpen((value) => !value)}
            aria-expanded={menuOpen}
          >
            <Menu />
            <span>{locale === "es" ? "Menú" : "Menu"}</span>
          </button>
          <nav
            className={
              menuOpen ? "primary-navigation open" : "primary-navigation"
            }
          >
            {items.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  type="button"
                  key={item.key}
                  className={section === item.key ? "active" : ""}
                  onClick={() => {
                    setSection(item.key);
                    setMenuOpen(false);
                  }}
                >
                  <Icon />
                  {locale === "es" ? item.es : item.en}
                </button>
              );
            })}
            <div className="mobile-menu-utilities">
              <button
                type="button"
                onClick={() => {
                  openContextHelp();
                  setMenuOpen(false);
                }}
              >
                <HelpCircle />
                {locale === "es" ? "Ayuda" : "Help"}
              </button>
              <button type="button" onClick={logout}>
                <LogOut />
                {locale === "es" ? "Salir" : "Sign out"}
              </button>
            </div>
          </nav>
          <div className="shell-utilities">
            <button type="button" onClick={openContextHelp}>
              <HelpCircle />
              <span>{locale === "es" ? "Ayuda" : "Help"}</span>
            </button>
            <LanguageButton locale={locale} setLocale={setLocale} />
            <button type="button" onClick={logout}>
              <LogOut />
              <span>{locale === "es" ? "Salir" : "Sign out"}</span>
            </button>
          </div>
        </div>
      </header>
      <main className="workspace-main">
        <header className="workspace-header">
          <div>
            <p>
              {account.tenantName} · {account.workerName}
            </p>
            <PageBreadcrumb
              locale={locale}
              items={[
                account.workerName,
                section === "help"
                  ? locale === "es"
                    ? "Ayuda"
                    : "Help"
                  : ((locale === "es"
                      ? items.find((item) => item.key === section)?.es
                      : items.find((item) => item.key === section)?.en) ??
                    section),
              ]}
            />
          </div>
          {section !== "help" && (
            <button
              type="button"
              className="context-help"
              onClick={openContextHelp}
            >
              <HelpCircle />
              <span>
                {locale === "es"
                  ? "Ayuda de esta pantalla"
                  : "Help for this screen"}
              </span>
            </button>
          )}
        </header>
        <div
          className="review-content"
          inert={simulator?.mode === "read_only" ? true : undefined}
        >
          {simulator?.mode === "read_only" && (
            <div className="review-mode-banner">
              <LockKeyhole />
              {locale === "es"
                ? "Simulación de solo lectura: puede navegar, pero los controles de la pantalla están bloqueados."
                : "Read-only simulation: navigation remains available while screen controls are blocked."}
            </div>
          )}
          {section === "home" ? (
            <WorkerHome
              locale={locale}
              tenant={tenant}
              contractor={contractor}
              setSection={setSection}
            />
          ) : section === "profile" ? (
            <WorkerProfileHub
              locale={locale}
              tenant={tenant}
              simulated={Boolean(simulator)}
            />
          ) : section === "time" ? (
            <Timekeeping
              locale={locale}
              tenant={tenant}
              canConfigure={false}
              roleKey="user"
              actorWorkerId={account.workerId}
              workerType={contractor ? "professional_services" : "employee"}
            />
          ) : section === "leave" && !contractor ? (
            <Leave
              locale={locale}
              tenant={tenant}
              canManage={false}
              roleKey="user"
              actorWorkerId={account.workerId}
            />
          ) : section === "pay" ? (
            <WorkerPayHub
              locale={locale}
              tenant={tenant}
              contractor={contractor}
            />
          ) : (
            <Help
              locale={locale}
              tenant={tenant}
              role="user"
              navigate={navigateFromHelp}
              initialArticleId={contextHelpArticleId}
              workerType={contractor ? "professional_services" : "employee"}
            />
          )}
        </div>
      </main>
      {simulator && (
        <aside
          className="developer-dock"
          aria-label={
            locale === "es"
              ? "Simulador de vista del desarrollador"
              : "Developer view simulator"
          }
        >
          <ReviewSwitcher
            locale={locale}
            tenant={tenant}
            onChange={simulator.onReviewChange}
            viewport={simulator.viewport}
            onViewport={simulator.onViewport}
            mode={simulator.mode}
            onMode={simulator.onMode}
          />
          <div className="developer-identity">
            <UserRound />
            <span>{developerEmail}</span>
            <LiveUiQuality locale={locale} />
          </div>
        </aside>
      )}
    </div>
  );
}

function WorkerHome({
  locale,
  tenant,
  contractor,
  setSection,
}: {
  locale: Locale;
  tenant: Tenant;
  contractor: boolean;
  setSection: (
    section: "home" | "profile" | "time" | "leave" | "pay" | "help",
  ) => void;
}) {
  return (
    <div className="content">
      <p className="kicker">{tenant.displayName}</p>
      <h1>{locale === "es" ? "Lo que necesita hoy" : "What you need today"}</h1>
      <p className="lead">
        {contractor
          ? locale === "es"
            ? "Revise servicios pendientes, proyectos y pagos profesionales."
            : "Review pending services, projects, and professional payments."
          : locale === "es"
            ? "Revise horario, tiempo pendiente, licencias y su pago más reciente."
            : "Review your schedule, pending time, leave, and latest pay."}
      </p>
      <div className="action-dashboard">
        <button type="button" onClick={() => setSection("time")}>
          <Clock3 />
          <strong>
            {contractor
              ? locale === "es"
                ? "Registrar servicios"
                : "Record services"
              : locale === "es"
                ? "Revisar mi tiempo"
                : "Review my time"}
          </strong>
          <span>
            {locale === "es"
              ? "Ponches, timesheet, comentarios y correcciones"
              : "Punches, timesheet, comments, and corrections"}
          </span>
        </button>
        {!contractor && (
          <button type="button" onClick={() => setSection("leave")}>
            <CalendarDays />
            <strong>{locale === "es" ? "Licencias" : "Leave"}</strong>
            <span>
              {locale === "es"
                ? "Balances y solicitudes"
                : "Balances and requests"}
            </span>
          </button>
        )}
        <button type="button" onClick={() => setSection("pay")}>
          <CircleDollarSign />
          <strong>
            {contractor
              ? locale === "es"
                ? "Pagos"
                : "Payments"
              : locale === "es"
                ? "Pago"
                : "Pay"}
          </strong>
          <span>
            {contractor
              ? locale === "es"
                ? "Estados, retención y relevo"
                : "Statements, withholding, and waiver"
              : locale === "es"
                ? "Talonarios, retención y gastos"
                : "Pay stubs, withholding, and expenses"}
          </span>
        </button>
        <button type="button" onClick={() => setSection("profile")}>
          <UserRound />
          <strong>
            {locale === "es" ? "Perfil y acceso" : "Profile and access"}
          </strong>
          <span>
            {locale === "es"
              ? "Información, documentos y seguridad"
              : "Information, documents, and security"}
          </span>
        </button>
      </div>
    </div>
  );
}
function WorkerPayHub({
  locale,
  tenant,
  contractor,
}: {
  locale: Locale;
  tenant: Tenant;
  contractor: boolean;
}) {
  const [tab, setTab] = useState<"tax" | "documents" | "expenses">(
    contractor ? "tax" : "documents",
  );
  return (
    <>
      <div className="content contextual-heading">
        <p className="kicker">{tenant.displayName}</p>
        <h1>
          {contractor
            ? locale === "es"
              ? "Pagos profesionales"
              : "Professional payments"
            : locale === "es"
              ? "Mi pago"
              : "My pay"}
        </h1>
        <div className="segmented">
          <button
            type="button"
            className={tab === "documents" ? "active" : ""}
            onClick={() => setTab("documents")}
          >
            {contractor
              ? locale === "es"
                ? "Estados de pago"
                : "Payment statements"
              : locale === "es"
                ? "Talonarios y documentos"
                : "Pay stubs & documents"}
          </button>
          <button
            type="button"
            className={tab === "tax" ? "active" : ""}
            onClick={() => setTab("tax")}
          >
            {contractor
              ? locale === "es"
                ? "Retención y relevo"
                : "Withholding & waiver"
              : locale === "es"
                ? "Retención 499 R-4"
                : "499 R-4 withholding"}
          </button>
          <button
            type="button"
            className={tab === "expenses" ? "active" : ""}
            onClick={() => setTab("expenses")}
          >
            {locale === "es" ? "Gastos" : "Expenses"}
          </button>
        </div>
      </div>
      {tab === "documents" ? (
        <EmployeeDocumentLibrary
          locale={locale}
          tenant={tenant}
          contractor={contractor}
        />
      ) : tab === "tax" ? (
        contractor ? (
          <ContractorPaymentSelfSummary locale={locale} tenant={tenant} />
        ) : (
          <EmployeeTaxQuestionnaire locale={locale} tenant={tenant} />
        )
      ) : (
        <EmployeeExpenseClaims locale={locale} tenant={tenant} />
      )}
    </>
  );
}
function ContractorPaymentSelfSummary({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  type Summary = {
    profile: {
      relevoType: string;
      relevoExpiresOn: string | null;
      statutoryExempt: number;
      payeeType: string;
      relevoEvidencePresent: number;
    } | null;
    payments: ContractorPayment[];
    ytd: {
      year: number;
      grossCents: number;
      withholdingCents: number;
      netCents: number;
    };
  };
  const [data, setData] = useState<Summary | null>(null),
    [error, setError] = useState("");
  useEffect(() => {
    void api(`/v1/tenants/${tenant.id}/self/contractor-payment-summary`).then(
      async (response) => {
        if (response.ok) setData((await response.json()) as Summary);
        else
          setError(
            locale === "es"
              ? "No se pudo cargar su resumen de pagos."
              : "Your payment summary could not be loaded.",
          );
      },
    );
  }, [tenant.id, locale]);
  const amount = (cents: number) =>
    new Intl.NumberFormat(locale === "es" ? "es-PR" : "en-PR", {
      style: "currency",
      currency: "USD",
    }).format(cents / 100);
  if (error)
    return (
      <div className="content">
        <OperationalError value={error} locale={locale} />
      </div>
    );
  if (!data)
    return (
      <div className="content">
        <LoaderCircle className="spin" />
      </div>
    );
  return (
    <div className="content contractor-summary">
      <section className="summary-grid">
        <article>
          <span>
            {locale === "es" ? "Pagos brutos YTD" : "YTD gross payments"}
          </span>
          <strong>{amount(data.ytd.grossCents)}</strong>
        </article>
        <article>
          <span>
            {locale === "es" ? "Retención PR YTD" : "YTD PR withholding"}
          </span>
          <strong>{amount(data.ytd.withholdingCents)}</strong>
        </article>
        <article>
          <span>
            {locale === "es" ? "Pagos netos YTD" : "YTD net payments"}
          </span>
          <strong>{amount(data.ytd.netCents)}</strong>
        </article>
      </section>
      <section className="settings-form">
        <h2>
          {locale === "es"
            ? "Configuración de retención de servicios"
            : "Services withholding setup"}
        </h2>
        <dl className="profile-facts">
          <div>
            <dt>{locale === "es" ? "Tipo de proveedor" : "Payee type"}</dt>
            <dd>{data.profile?.payeeType ?? "—"}</dd>
          </div>
          <div>
            <dt>{locale === "es" ? "Relevo" : "Waiver"}</dt>
            <dd>
              {data.profile?.relevoType ??
                (locale === "es" ? "No configurado" : "Not configured")}
            </dd>
          </div>
          <div>
            <dt>{locale === "es" ? "Vencimiento" : "Expiration"}</dt>
            <dd>{formatDate(data.profile?.relevoExpiresOn, locale)}</dd>
          </div>
          <div>
            <dt>
              {locale === "es" ? "Evidencia retenida" : "Evidence retained"}
            </dt>
            <dd>
              {data.profile?.relevoEvidencePresent
                ? locale === "es"
                  ? "Sí"
                  : "Yes"
                : locale === "es"
                  ? "No"
                  : "No"}
            </dd>
          </div>
          <div>
            <dt>
              {locale === "es" ? "Exención legal" : "Statutory exemption"}
            </dt>
            <dd>
              {data.profile?.statutoryExempt
                ? locale === "es"
                  ? "Sí"
                  : "Yes"
                : locale === "es"
                  ? "No"
                  : "No"}
            </dd>
          </div>
        </dl>
      </section>
      <section className="settings-form">
        <h2>{locale === "es" ? "Historial de pagos" : "Payment history"}</h2>
        <ResponsiveDataTable
          label={
            locale === "es"
              ? "Tabla de historial de pagos profesionales"
              : "Professional payment history table"
          }
          caption={
            locale === "es"
              ? "Servicio, fecha, tipo, bruto, retención, neto y estado de cada pago"
              : "Service, date, type, gross, withholding, net, and status for each payment"
          }
          columns={
            locale === "es"
              ? [
                  "Servicio",
                  "Fecha y tipo",
                  "Bruto",
                  "Retención",
                  "Neto",
                  "Estado",
                ]
              : [
                  "Service",
                  "Date and type",
                  "Gross",
                  "Withholding",
                  "Net",
                  "Status",
                ]
          }
          rows={data.payments.map((item) => ({
            key: item.id,
            cells: [
              <strong>{item.description}</strong>,
              <>
                <span>{formatDate(item.paymentDate, locale)}</span>
                <small>
                  {item.compensationType
                    ? optionLabel(item.compensationType, locale)
                    : "—"}
                </small>
              </>,
              amount(item.grossCents),
              amount(item.withholdingCents),
              amount(item.netCents),
              <span className={`run-status ${item.status}`}>
                {optionLabel(item.status, locale)}
              </span>,
            ],
            card: (
              <article>
                <div>
                  <strong>{item.description}</strong>
                  <small>
                    {formatDate(item.paymentDate, locale)} ·{" "}
                    {item.compensationType
                      ? optionLabel(item.compensationType, locale)
                      : "—"}
                  </small>
                </div>
                <p>
                  {locale === "es" ? "Bruto" : "Gross"}{" "}
                  {amount(item.grossCents)} ·{" "}
                  {locale === "es" ? "Retención" : "Withholding"}{" "}
                  {amount(item.withholdingCents)}
                </p>
                <p>
                  {locale === "es" ? "Neto" : "Net"} {amount(item.netCents)}
                </p>
                <span className={`run-status ${item.status}`}>
                  {optionLabel(item.status, locale)}
                </span>
              </article>
            ),
          }))}
          empty={
            locale === "es"
              ? "Todavía no hay pagos profesionales."
              : "There are no professional payments yet."
          }
        />
      </section>
    </div>
  );
}
function WorkerProfileHub({
  locale,
  tenant,
  simulated = false,
}: {
  locale: Locale;
  tenant: Tenant;
  simulated?: boolean;
}) {
  const [tab, setTab] = useState<"profile" | "security">("profile");
  return (
    <>
      <div className="content contextual-heading">
        <p className="kicker">{tenant.displayName}</p>
        <h1>{locale === "es" ? "Mi perfil" : "My profile"}</h1>
        <div className="segmented">
          <button
            type="button"
            className={tab === "profile" ? "active" : ""}
            onClick={() => setTab("profile")}
          >
            {locale === "es" ? "Información personal" : "Personal information"}
          </button>
          <button
            type="button"
            className={tab === "security" ? "active" : ""}
            onClick={() => setTab("security")}
          >
            {locale === "es" ? "Acceso y seguridad" : "Access & security"}
          </button>
        </div>
      </div>
      {tab === "profile" ? (
        <EmployeeSelfProfile locale={locale} tenant={tenant} />
      ) : (
        <EmployeeSecurity locale={locale} simulated={simulated} />
      )}
    </>
  );
}

type AccountDevice = {
  id: string;
  label: string | null;
  platform: string;
  status: string;
  firstSeenAt: string;
  lastSeenAt: string;
  verifiedAt: string | null;
  revokedAt: string | null;
  currentDevice: number;
};
type SecurityEvent = { id: string; action: string; occurredAt: string };
type AccountNotification = {
  id: string;
  eventKey: string;
  titleEs: string;
  titleEn: string;
  bodyEs: string;
  bodyEn: string;
  actionPath: string | null;
  status: string;
  createdAt: string;
  readAt: string | null;
};
type NotificationPreference = {
  eventKey: string;
  inAppEnabled: number | boolean;
  pushEnabled: number | boolean;
  updatedAt: string;
};
function EmployeeSecurity({
  locale,
  simulated = false,
}: {
  locale: Locale;
  simulated?: boolean;
}) {
  const [devices, setDevices] = useState<AccountDevice[]>([]),
    [events, setEvents] = useState<SecurityEvent[]>([]),
    [notifications, setNotifications] = useState<AccountNotification[]>([]),
    [preferences, setPreferences] = useState<NotificationPreference[]>([]),
    [pushConfig, setPushConfig] = useState<{
      supported: boolean;
      publicKey: string | null;
    } | null>(null),
    [pushActive, setPushActive] = useState(false),
    [busy, setBusy] = useState(true),
    [message, setMessage] = useState(""),
    [newPhone, setNewPhone] = useState(""),
    [reason, setReason] = useState(""),
    [challengeId, setChallengeId] = useState(""),
    [code, setCode] = useState("");
  const load = async () => {
    if (simulated) {
      setBusy(false);
      return;
    }
    setBusy(true);
    const [deviceResponse, eventResponse, notificationResponse, pushResponse] =
      await Promise.all([
        api("/auth/worker/devices"),
        api("/auth/worker/security-events"),
        api("/auth/worker/notifications"),
        api("/auth/worker/push-configuration"),
      ]);
    if (deviceResponse.ok)
      setDevices(
        ((await deviceResponse.json()) as { devices: AccountDevice[] }).devices,
      );
    if (eventResponse.ok)
      setEvents(
        ((await eventResponse.json()) as { events: SecurityEvent[] }).events,
      );
    if (notificationResponse.ok) {
      const body = (await notificationResponse.json()) as {
        notifications: AccountNotification[];
        preferences: NotificationPreference[];
      };
      setNotifications(body.notifications);
      setPreferences(body.preferences);
    }
    if (pushResponse.ok) {
      const config = (await pushResponse.json()) as {
        supported: boolean;
        publicKey: string | null;
      };
      setPushConfig(config);
      if (config.supported && "serviceWorker" in navigator) {
        const registration = await navigator.serviceWorker.register("/sw.js"),
          subscription = await registration.pushManager.getSubscription();
        setPushActive(Boolean(subscription));
      }
    }
    setBusy(false);
  };
  useEffect(() => {
    void load();
  }, [simulated]);
  const requestPhone = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMessage("");
    const response = await api("/auth/worker/phone-change", {
      method: "POST",
      body: JSON.stringify({ newPhoneE164: newPhone, reason }),
    });
    if (response.ok) {
      setChallengeId(
        ((await response.json()) as { challengeId: string }).challengeId,
      );
      setMessage(
        locale === "es"
          ? "Enviamos un código al número nuevo."
          : "We sent a code to the new number.",
      );
    } else
      setMessage(
        locale === "es"
          ? "No se pudo solicitar el cambio. Revise el número o si ya está en uso."
          : "Unable to request the change. Check the number or whether it is already used.",
      );
    setBusy(false);
  };
  const completePhone = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const response = await api("/auth/worker/phone-change/complete", {
      method: "POST",
      body: JSON.stringify({ challengeId, code }),
    });
    if (response.ok) {
      setMessage(
        locale === "es"
          ? "Teléfono verificado y actualizado."
          : "Phone verified and updated.",
      );
      setChallengeId("");
      setCode("");
      setNewPhone("");
      setReason("");
    } else
      setMessage(
        locale === "es"
          ? "El código no es válido o expiró."
          : "The code is invalid or expired.",
      );
    setBusy(false);
  };
  const revoke = async (device: AccountDevice) => {
    const values = await requestWorkflowDialog({
      title: locale === "es" ? "Revocar dispositivo" : "Revoke device",
      detail: device.currentDevice
        ? locale === "es"
          ? "Este es el dispositivo actual. Al revocarlo, la sesión se cerrará inmediatamente."
          : "This is the current device. Revoking it will sign out this session immediately."
        : locale === "es"
          ? "El dispositivo dejará de poder usar esta cuenta."
          : "The device will no longer be able to use this account.",
      confirmLabel: locale === "es" ? "Revocar dispositivo" : "Revoke device",
      cancelLabel: locale === "es" ? "Cancelar" : "Cancel",
      danger: true,
      fields: [
        {
          key: "reason",
          label: locale === "es" ? "Razón documentada" : "Documented reason",
          type: "textarea",
          required: true,
          minLength: 3,
          fullWidth: true,
        },
      ],
    });
    if (!values) return;
    setBusy(true);
    const response = await api(`/auth/worker/devices/${device.id}/revoke`, {
      method: "POST",
      body: JSON.stringify({ reason: String(values.reason) }),
    });
    if (response.ok && device.currentDevice) {
      window.location.reload();
      return;
    }
    await load();
  };
  const eventLabel = (action: string) =>
    ({
      password_recovered:
        locale === "es" ? "Contraseña recuperada" : "Password recovered",
      phone_changed: locale === "es" ? "Teléfono cambiado" : "Phone changed",
      device_registered:
        locale === "es" ? "Dispositivo registrado" : "Device registered",
      device_revoked:
        locale === "es" ? "Dispositivo revocado" : "Device revoked",
    })[action] ?? action.replaceAll("_", " ");
  const markRead = async (item: AccountNotification) => {
    await api(`/auth/worker/notifications/${item.id}/read`, { method: "POST" });
    await load();
  };
  const savePreference = async (
    eventKey: string,
    inAppEnabled: boolean,
    pushEnabled: boolean,
  ) => {
    await api("/auth/worker/notification-preference", {
      method: "PUT",
      body: JSON.stringify({ eventKey, inAppEnabled, pushEnabled }),
    });
    await load();
  };
  const preference = (key: string) =>
    preferences.find((item) => item.eventKey === key) ?? {
      eventKey: key,
      inAppEnabled: true,
      pushEnabled: false,
      updatedAt: "",
    };
  const enablePush = async () => {
    if (
      !pushConfig?.supported ||
      !pushConfig.publicKey ||
      !("serviceWorker" in navigator) ||
      !("PushManager" in window)
    )
      return;
    setBusy(true);
    setMessage("");
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setMessage(
          locale === "es"
            ? "El navegador no autorizó las notificaciones."
            : "The browser did not grant notification permission.",
        );
        setBusy(false);
        return;
      }
      const registration = await navigator.serviceWorker.register("/sw.js"),
        padded =
          pushConfig.publicKey +
          "=".repeat((4 - (pushConfig.publicKey.length % 4)) % 4),
        binary = atob(padded.replaceAll("-", "+").replaceAll("_", "/")),
        key = Uint8Array.from(binary, (char) => char.charCodeAt(0)),
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: key,
        }),
        response = await api("/auth/worker/push-subscriptions", {
          method: "POST",
          body: JSON.stringify(subscription.toJSON()),
        });
      if (!response.ok) throw new Error("subscription_failed");
      setPushActive(true);
      setMessage(
        locale === "es"
          ? "Notificaciones push activadas en este navegador."
          : "Push notifications enabled in this browser.",
      );
    } catch {
      setMessage(
        locale === "es"
          ? "No se pudieron activar las notificaciones push."
          : "Push notifications could not be enabled.",
      );
    }
    setBusy(false);
  };
  if (simulated)
    return (
      <div className="content">
        <p className="kicker">JORNADA PR</p>
        <h1>
          {locale === "es" ? "Seguridad y dispositivos" : "Security & devices"}
        </h1>
        <section className="operation-panel">
          <h2>
            {locale === "es"
              ? "Protegido por la sesión personal"
              : "Protected by the personal session"}
          </h2>
          <p>
            {locale === "es"
              ? "La revisión del desarrollador no suplanta la autenticación telefónica de esta persona. Por seguridad, no muestra ni inventa dispositivos, preferencias, notificaciones o historial de acceso, y no permite cambiar el teléfono."
              : "Developer review does not impersonate this person's phone authentication. For security, it neither displays nor invents devices, preferences, notifications, or access history, and it cannot change the login phone."}
          </p>
          <p>
            {locale === "es"
              ? "Estas funciones se prueban desde Acceso de personal con la cuenta autenticada correspondiente."
              : "These functions are tested from Personnel access using the corresponding authenticated account."}
          </p>
        </section>
      </div>
    );
  return (
    <div className="content">
      <p className="kicker">JORNADA PR</p>
      <h1>
        {locale === "es" ? "Seguridad y dispositivos" : "Security & devices"}
      </h1>
      <section className="settings-form">
        <h2>{locale === "es" ? "Notificaciones" : "Notifications"}</h2>
        <p>
          {locale === "es"
            ? "Las alertas internas y push se procesan mediante Cloudflare Queue. Active push una vez por navegador y elija qué eventos desea recibir."
            : "In-app and push alerts are processed through Cloudflare Queue. Enable push once per browser, then choose which events to receive."}
        </p>
        {pushConfig?.supported ? (
          <button
            type="button"
            className="primary"
            disabled={busy || pushActive}
            onClick={() => void enablePush()}
          >
            {pushActive
              ? locale === "es"
                ? "Push activo en este navegador"
                : "Push active in this browser"
              : locale === "es"
                ? "Activar push en este navegador"
                : "Enable push in this browser"}
          </button>
        ) : (
          <p className="form-detail">
            {locale === "es"
              ? "Las notificaciones push todavía no están habilitadas para este entorno."
              : "Push notifications are not yet enabled for this environment."}
          </p>
        )}
        {[
          "payroll_review",
          "leave_request",
          "time_exception",
          "account_security",
        ].map((key) => {
          const item = preference(key);
          return (
            <div className="notification-preference" key={key}>
              <strong>{key.replaceAll("_", " ")}</strong>
              <label className="check-label">
                <input
                  type="checkbox"
                  checked={Boolean(item.inAppEnabled)}
                  onChange={(e) =>
                    void savePreference(
                      key,
                      e.currentTarget.checked,
                      Boolean(item.pushEnabled),
                    )
                  }
                />
                {locale === "es" ? "En la aplicación" : "In app"}
              </label>
              <label className="check-label">
                <input
                  type="checkbox"
                  disabled={!pushActive}
                  checked={Boolean(item.pushEnabled)}
                  onChange={(e) =>
                    void savePreference(
                      key,
                      Boolean(item.inAppEnabled),
                      e.currentTarget.checked,
                    )
                  }
                />
                {locale === "es" ? "Push" : "Push"}
              </label>
            </div>
          );
        })}
        {message && <p className="form-detail action-result">{message}</p>}
        <div className="run-list">
          {notifications.map((item) => (
            <article key={item.id}>
              <div>
                <strong>{locale === "es" ? item.titleEs : item.titleEn}</strong>
                <p>{locale === "es" ? item.bodyEs : item.bodyEn}</p>
                <small>{formatDateTime(item.createdAt, locale)}</small>
              </div>
              <span className={`run-status ${item.status}`}>
                {optionLabel(item.status, locale)}
              </span>
              {item.status === "delivered" && (
                <button type="button" onClick={() => void markRead(item)}>
                  {locale === "es" ? "Marcar leída" : "Mark read"}
                </button>
              )}
            </article>
          ))}
        </div>
      </section>
      <section className="settings-form">
        <h2>
          {locale === "es"
            ? "Cambiar teléfono de acceso"
            : "Change login phone"}
        </h2>
        <p>
          {locale === "es"
            ? "El número nuevo debe verificarse por SMS. Las demás sesiones se cerrarán al completar el cambio."
            : "The new number must be verified by SMS. Other sessions will be closed when the change is completed."}
        </p>
        <form onSubmit={challengeId ? completePhone : requestPhone}>
          <div className="form-grid">
            {!challengeId ? (
              <>
                <label>
                  {locale === "es" ? "Número nuevo" : "New number"}
                  <input
                    type="tel"
                    placeholder="+17875551234"
                    value={newPhone}
                    onChange={(e) => setNewPhone(e.currentTarget.value)}
                    required
                  />
                </label>
                <label>
                  {locale === "es" ? "Razón" : "Reason"}
                  <input
                    minLength={3}
                    value={reason}
                    onChange={(e) => setReason(e.currentTarget.value)}
                    required
                  />
                </label>
              </>
            ) : (
              <label>
                {locale === "es" ? "Código SMS" : "SMS code"}
                <input
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.currentTarget.value)}
                  required
                />
              </label>
            )}
          </div>
          <button className="primary" disabled={busy}>
            {challengeId
              ? locale === "es"
                ? "Verificar y cambiar"
                : "Verify and change"
              : locale === "es"
                ? "Enviar código"
                : "Send code"}
          </button>
        </form>
        {message && <p className="form-detail action-result">{message}</p>}
      </section>
      <section className="settings-form">
        <h2>
          {locale === "es" ? "Dispositivos registrados" : "Registered devices"}
        </h2>
        {busy ? (
          <LoaderCircle className="spin" />
        ) : (
          <div className="run-list">
            {devices.map((device) => (
              <article key={device.id}>
                <div>
                  <strong>
                    {device.label || device.platform}{" "}
                    {device.currentDevice
                      ? `· ${locale === "es" ? "este dispositivo" : "this device"}`
                      : ""}
                  </strong>
                  <p>
                    {optionLabel(device.platform, locale)} ·{" "}
                    {optionLabel(device.status, locale)}
                  </p>
                  <small>
                    {locale === "es" ? "Último uso" : "Last used"}:{" "}
                    {device.lastSeenAt}
                  </small>
                </div>
                {device.status === "active" && (
                  <button type="button" onClick={() => void revoke(device)}>
                    {locale === "es" ? "Revocar" : "Revoke"}
                  </button>
                )}
              </article>
            ))}
          </div>
        )}
      </section>
      <section className="settings-form">
        <h2>
          {locale === "es" ? "Historial de seguridad" : "Security history"}
        </h2>
        {events.length ? (
          <div className="run-list">
            {events.map((event) => (
              <article key={event.id}>
                <strong>{eventLabel(event.action)}</strong>
                <span>{formatDateTime(event.occurredAt, locale)}</span>
              </article>
            ))}
          </div>
        ) : (
          <p>
            {locale === "es"
              ? "No hay eventos de seguridad registrados."
              : "No security events recorded."}
          </p>
        )}
      </section>
    </div>
  );
}

function CompanyDirectory({
  locale,
  setLocale,
  tenants,
  reload,
  onSelect,
  onLogout,
}: {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  tenants: Tenant[];
  reload: () => Promise<void>;
  onSelect: (tenant: Tenant) => void;
  onLogout: () => void;
}) {
  const [creating, setCreating] = useState(false),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState(""),
    [lifecycleDraft, setLifecycleDraft] = useState<{
      tenant: Tenant;
      status: "active" | "suspended";
      reason: string;
    } | null>(null);
  if (creating)
    return (
      <CompanySetup
        locale={locale}
        setLocale={setLocale}
        onCancel={() => setCreating(false)}
        onCreated={async () => {
          setCreating(false);
          await reload();
        }}
      />
    );
  const lifecycle = (tenant: Tenant, status: "active" | "suspended") =>
    setLifecycleDraft({ tenant, status, reason: "" });
  const submitLifecycle = async (e: FormEvent) => {
    e.preventDefault();
    const draft = lifecycleDraft;
    if (!draft) return;
    setBusy(true);
    setMessage("");
    const response = await api(`/v1/tenants/${draft.tenant.id}/lifecycle`, {
      method: "POST",
      body: JSON.stringify({
        status: draft.status,
        reason: draft.reason.trim(),
      }),
    });
    setMessage(
      response.ok
        ? draft.status === "active"
          ? locale === "es"
            ? "Compañía reactivada."
            : "Company reactivated."
          : locale === "es"
            ? "Compañía desactivada; las sesiones fueron revocadas."
            : "Company deactivated; sessions were revoked."
        : `lifecycle:${response.status}`,
    );
    if (response.ok) setLifecycleDraft(null);
    await reload();
    setBusy(false);
  };
  const logout = async () => {
    await api("/auth/logout", { method: "POST" });
    onLogout();
  };
  return (
    <main className="setup-screen">
      <ActionFeedbackCoordinator />
      <header className="simple-header">
        <div className="brand-lockup dark">
          <div className="logo">J</div>
          <div>
            <strong>Jornada PR</strong>
            <span>
              {locale === "es"
                ? "Administración de compañías"
                : "Company administration"}
            </span>
          </div>
        </div>
        <div className="run-actions">
          <LanguageButton locale={locale} setLocale={setLocale} />
          <button type="button" onClick={() => void logout()}>
            <LogOut />
            {locale === "es" ? "Salir" : "Sign out"}
          </button>
        </div>
      </header>
      <section className="setup-card company-directory">
        <div className="section-heading">
          <div>
            <p className="kicker">
              {locale === "es"
                ? "PLATAFORMA MULTICLIENTE"
                : "MULTI-CLIENT PLATFORM"}
            </p>
            <h1>
              {locale === "es" ? "Compañías aisladas" : "Isolated companies"}
            </h1>
            <p>
              {locale === "es"
                ? "Seleccione una compañía activa. Cada compañía mantiene datos, documentos, usuarios y configuración separados."
                : "Select an active company. Each company keeps separate data, documents, users, and configuration."}
            </p>
          </div>
          <button
            type="button"
            className="primary"
            onClick={() => setCreating(true)}
          >
            {locale === "es" ? "Crear otra compañía" : "Create another company"}
          </button>
        </div>
        <div className="run-list">
          {tenants.map((tenant) => (
            <article key={tenant.id}>
              <div>
                <strong>{tenant.displayName}</strong>
                <p>{tenant.legalName}</p>
                <small>
                  {tenant.defaultLocale} ·{" "}
                  {formatDateTime(tenant.statusChangedAt, locale)}
                  {tenant.statusReason ? ` · ${tenant.statusReason}` : ""}
                </small>
              </div>
              <span
                className={`run-status ${tenant.status === "active" ? "active" : "inactive"}`}
              >
                {optionLabel(
                  tenant.status === "suspended" ? "inactive" : tenant.status,
                  locale,
                )}
              </span>
              <div className="run-actions">
                {tenant.status === "active" && (
                  <>
                    <button
                      type="button"
                      className="primary"
                      onClick={() => onSelect(tenant)}
                    >
                      {locale === "es" ? "Abrir" : "Open"}
                    </button>
                    <button
                      type="button"
                      className="danger-action"
                      disabled={busy}
                      onClick={() => lifecycle(tenant, "suspended")}
                    >
                      {locale === "es" ? "Desactivar" : "Deactivate"}
                    </button>
                  </>
                )}
                {tenant.status === "suspended" && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => lifecycle(tenant, "active")}
                  >
                    {locale === "es" ? "Reactivar" : "Reactivate"}
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
        <OperationalFeedback
          message={message}
          isError={message.includes(":")}
          locale={locale}
        />
      </section>
      {lifecycleDraft && (
        <dialog
          className="workflow-dialog"
          open
          aria-labelledby="company-lifecycle-title"
        >
          <form onSubmit={submitLifecycle}>
            <header>
              <div>
                <p className="kicker">
                  {locale === "es"
                    ? "CAMBIO DE ACCESO AUDITADO"
                    : "AUDITED ACCESS CHANGE"}
                </p>
                <h2 id="company-lifecycle-title">
                  {lifecycleDraft.status === "suspended"
                    ? locale === "es"
                      ? "Desactivar compañía"
                      : "Deactivate company"
                    : locale === "es"
                      ? "Reactivar compañía"
                      : "Reactivate company"}
                </h2>
                <p>
                  {lifecycleDraft.tenant.displayName} ·{" "}
                  {lifecycleDraft.tenant.legalName}
                </p>
              </div>
              <button
                type="button"
                aria-label={locale === "es" ? "Cerrar" : "Close"}
                onClick={() => setLifecycleDraft(null)}
              >
                ×
              </button>
            </header>
            <p className="form-detail">
              {lifecycleDraft.status === "suspended"
                ? locale === "es"
                  ? "Se bloqueará el acceso de todo el personal y se revocarán sus sesiones activas. Los datos de la compañía se conservan."
                  : "All worker access will be blocked and active sessions revoked. Company data is retained."
                : locale === "es"
                  ? "El personal aprobado podrá volver a iniciar sesión según sus permisos."
                  : "Approved workers will be able to sign in again according to their permissions."}
            </p>
            <div className="form-grid">
              <label className="full-width">
                {locale === "es" ? "Razón documentada" : "Documented reason"}
                <textarea
                  required
                  minLength={3}
                  maxLength={1000}
                  value={lifecycleDraft.reason}
                  onChange={(e) =>
                    setLifecycleDraft({
                      ...lifecycleDraft,
                      reason: e.currentTarget.value,
                    })
                  }
                />
              </label>
            </div>
            {message.includes(":") && (
              <OperationalError value={message} locale={locale} />
            )}
            <footer>
              <button type="button" onClick={() => setLifecycleDraft(null)}>
                {locale === "es" ? "Cancelar" : "Cancel"}
              </button>
              <button
                className={
                  lifecycleDraft.status === "suspended"
                    ? "danger-action"
                    : "primary"
                }
                disabled={busy || lifecycleDraft.reason.trim().length < 3}
              >
                {busy ? (
                  <LoaderCircle className="spin" />
                ) : lifecycleDraft.status === "suspended" ? (
                  locale === "es" ? (
                    "Desactivar y cerrar sesiones"
                  ) : (
                    "Deactivate and revoke sessions"
                  )
                ) : locale === "es" ? (
                  "Reactivar compañía"
                ) : (
                  "Reactivate company"
                )}
              </button>
            </footer>
          </form>
        </dialog>
      )}
    </main>
  );
}

function CompanySetup({
  locale,
  setLocale,
  onCreated,
  onCancel,
}: {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  onCreated: (tenant: Tenant) => void;
  onCancel?: () => void;
}) {
  const t = text[locale];
  const [legalName, setLegalName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [defaultLocale, setDefaultLocale] = useState<"es-PR" | "en-PR">(
    "es-PR",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(false);
    const response = await api("/v1/tenants", {
      method: "POST",
      body: JSON.stringify({ legalName, displayName, defaultLocale }),
    });
    if (response.ok) {
      const body = (await response.json()) as { tenant: Tenant };
      onCreated(body.tenant);
    } else setError(true);
    setBusy(false);
  };

  return (
    <main className="setup-screen">
      <ActionFeedbackCoordinator />
      <header className="simple-header">
        <div className="brand-lockup dark">
          <div className="logo">J</div>
          <div>
            <strong>{t.product}</strong>
            <span>{t.tagline}</span>
          </div>
        </div>
        <LanguageButton locale={locale} setLocale={setLocale} />
      </header>
      <section className="setup-card">
        <span className="step">01</span>
        <div className="setup-intro">
          <span className="form-icon">
            <Building2 />
          </span>
          <div>
            <p className="kicker">CONFIGURACIÓN INICIAL</p>
            <h1>{t.firstCompany}</h1>
            <p>{t.firstCompanyDetail}</p>
          </div>
        </div>
        <form onSubmit={submit}>
          <label>
            {t.legalName}
            <input
              value={legalName}
              onChange={(event) => {
                setLegalName(event.target.value);
                if (!displayName) setDisplayName(event.target.value);
              }}
              required
            />
          </label>
          <label>
            {t.displayName}
            <input
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              required
            />
          </label>
          <label>
            {t.language}
            <select
              value={defaultLocale}
              onChange={(event) =>
                setDefaultLocale(event.target.value as "es-PR" | "en-PR")
              }
            >
              <option value="es-PR">{t.spanish}</option>
              <option value="en-PR">{t.english}</option>
            </select>
          </label>
          {error && <p className="form-error">{t.required}</p>}
          <button
            className="primary"
            disabled={busy || !legalName || !displayName}
          >
            {busy ? (
              <>
                <LoaderCircle className="spin" />
                {t.creating}
              </>
            ) : (
              <>
                {t.createCompany}
                <ChevronRight />
              </>
            )}
          </button>
          {onCancel && (
            <button type="button" onClick={onCancel}>
              {locale === "es" ? "Cancelar" : "Cancel"}
            </button>
          )}
        </form>
      </section>
    </main>
  );
}

function Workspace({
  locale,
  setLocale,
  tenant,
  actor,
  onBack,
  onLogout,
  developerEmail,
}: {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  tenant: Tenant;
  actor?: WorkerAccount;
  onBack?: () => void;
  onLogout: () => void;
  developerEmail?: string | undefined;
}) {
  const t = text[locale];
  const [section, setSection] = useRoutedSection<Section>(
    "home",
    sectionFromPath,
    sectionToPath,
  );
  const [personnelNavigationKey, setPersonnelNavigationKey] = useState(0);
  const [contextHelpArticleId, setContextHelpArticleId] = useState<
    string | null
  >(null);
  const [helpOrigin, setHelpOrigin] =
    useState<Exclude<Section, "help">>("home");
  const [menuOpen, setMenuOpen] = useState(false);
  const [contractorFirst, setContractorFirst] = useState(false);
  const [reviewViewport, setReviewViewport] = useState<
      "desktop" | "tablet" | "iphone" | "android"
    >("desktop"),
    [reviewMode, setReviewMode] = useState<"read_only" | "interactive">(
      "interactive",
    );
  const [reviewContext, setReviewContext] = useState<ReviewContext>(
    actor
      ? {
          tenantId: actor.tenantId,
          roleKey: actor.roleKey,
          workerId: actor.workerId,
          workerType: null,
          workerName: actor.workerName,
        }
      : {
          tenantId: null,
          roleKey: "platform_administrator",
          workerId: null,
          workerType: null,
          workerName: null,
        },
  );
  useEffect(() => {
    const controller = new AbortController();
    void api(`/v1/tenants/${tenant.id}/operating-model`, {
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) return;
        const body = (await response.json()) as {
          settings: OperatingModel | null;
        };
        setContractorFirst(Boolean(body.settings?.contractorFirst));
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, [tenant.id]);
  const nav = useMemo(() => {
    const all = [
      "home",
      ...(actor ? ["self" as const] : []),
      "personnel",
      "personnel_setup",
      "time",
      "tips",
      "expenses",
      "leave",
      "time_log",
      "payroll",
      "documents",
      "reports",
      "settings",
      "help",
    ] as Section[];
    if (reviewContext.roleKey === "user")
      return all.filter((item) =>
        [
          "home",
          "personnel",
          "time",
          "tips",
          "payroll",
          "expenses",
          "documents",
          "help",
          ...(reviewContext.workerType === "professional_services"
            ? []
            : ["leave"]),
        ].includes(item),
      );
    if (reviewContext.roleKey === "supervisor")
      return all.filter((item) =>
        [
          "home",
          "self",
          "personnel",
          "time",
          "tips",
          "expenses",
          "leave",
          "help",
        ].includes(item),
      );
    return ["owner", "platform_administrator"].includes(reviewContext.roleKey)
      ? all
      : all.filter((item) => item !== "time_log");
  }, [reviewContext.roleKey, reviewContext.workerType]);
  const activeArea = activePrimaryAreaForSection(section, helpOrigin);
  const navigateSection = (next: Section) => {
    if (next === "personnel") setPersonnelNavigationKey((value) => value + 1);
    setSection(next);
  };
  const managementView = !["user", "supervisor"].includes(
    reviewContext.roleKey,
  );
  const primaryAreaOrder =
    contractorFirst && managementView
      ? (["home", "payroll", "people", "time", "company"] as PrimaryArea[])
      : (["home", "people", "time", "payroll", "company"] as PrimaryArea[]);
  const primaryAreas = primaryAreasForNav(nav, primaryAreaOrder);
  // Employee setup and documents are contextual actions inside an employee
  // record. Keeping them out of the global People navigation avoids three
  // competing entry points for the same workflow.
  const secondaryNav =
    activeArea === "people" || activeArea === "company"
      ? []
      : nav.filter(
          (item) =>
            primaryAreaForSection[item] === activeArea &&
            item !== "home" &&
            item !== "self" &&
            item !== "help",
        );
  const areaLabel = (area: PrimaryArea) =>
    ({
      home: { es: "Inicio", en: "Home" },
      people: { es: "Personas", en: "People" },
      time: { es: "Tiempo", en: "Time" },
      payroll: { es: "Compensación", en: "Compensation" },
      company: { es: "Compañía", en: "Company" },
    })[area][locale];
  useEffect(() => {
    if (!nav.includes(section)) setSection("home");
  }, [nav, section]);
  const openContextHelp = () => {
    if (section === "help") return;
    const helpSectionForManagement: Record<Section, HelpSection> = {
      home: "home",
      self: "personnel",
      personnel: "personnel",
      personnel_setup: "personnel",
      time: "time",
      tips: "time",
      time_log: "time",
      payroll: "payroll",
      expenses: "expenses",
      documents: "documents",
      leave: "leave",
      reports: "reports",
      settings: "settings",
      help: "home",
    };
    const article = contextualHelpArticle(
      reviewContext.roleKey as HelpRole,
      helpSectionForManagement[section],
      reviewContext.workerType === "professional_services"
        ? "professional_services"
        : reviewContext.workerType === "employee"
          ? "employee"
          : undefined,
    );
    setContextHelpArticleId(article?.id ?? null);
    setHelpOrigin(section);
    setSection("help");
  };
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [section]);

  const logout = async () => {
    await api(actor ? "/auth/worker/logout" : "/auth/logout", {
      method: "POST",
    });
    onLogout();
  };

  if (
    !actor &&
    reviewContext.roleKey === "user" &&
    reviewContext.workerId &&
    reviewContext.workerName
  ) {
    return (
      <WorkerWorkspace
        key={reviewContext.workerId}
        locale={locale}
        setLocale={setLocale}
        account={{
          tenantId: tenant.id,
          tenantName: tenant.displayName,
          legalName: tenant.legalName,
          defaultLocale: tenant.defaultLocale,
          workerId: reviewContext.workerId,
          workerName: reviewContext.workerName,
          roleKey: "user",
        }}
        onLogout={onLogout}
        simulator={{
          viewport: reviewViewport,
          mode: reviewMode,
          workerType: reviewContext.workerType,
          onViewport: setReviewViewport,
          onMode: setReviewMode,
          onReviewChange: setReviewContext,
        }}
        developerEmail={developerEmail}
      />
    );
  }

  return (
    <div className={`workspace preview-${reviewViewport}`}>
      <ActionFeedbackCoordinator />
      <a className="skip-link" href="#main-content">
        {locale === "es"
          ? "Saltar al contenido principal"
          : "Skip to main content"}
      </a>
      <header className="app-shell-header">
        <div className="app-shell-bar">
          <div className="brand-lockup dark">
            <div className="logo">J</div>
            <div>
              <strong>{t.product}</strong>
              <span>{tenant.displayName}</span>
            </div>
          </div>
          <button
            className="open-menu"
            onClick={() => setMenuOpen((value) => !value)}
            aria-expanded={menuOpen}
            aria-controls="primary-navigation"
          >
            <Menu />
            <span>{t.menu}</span>
          </button>
          <nav
            id="primary-navigation"
            className={
              menuOpen ? "primary-navigation open" : "primary-navigation"
            }
            aria-label={
              locale === "es" ? "Navegación principal" : "Primary navigation"
            }
          >
            {primaryAreas.map((area) => {
              const Icon = primaryIcons[area];
              return (
                <button
                  key={area}
                  type="button"
                  className={activeArea === area ? "active" : ""}
                  aria-current={activeArea === area ? "page" : undefined}
                  onClick={() => {
                    navigateSection(primaryDefault[area]);
                    setMenuOpen(false);
                  }}
                >
                  <Icon />
                  {areaLabel(area)}
                </button>
              );
            })}
            <div className="mobile-menu-utilities">
              {!actor && onBack && (
                <button type="button" onClick={onBack}>
                  <Building2 />
                  {locale === "es" ? "Cambiar compañía" : "Switch company"}
                </button>
              )}
              {actor && (
                <button
                  type="button"
                  onClick={() => {
                    setSection("self");
                    setMenuOpen(false);
                  }}
                >
                  <UserRound />
                  {locale === "es" ? "Mi cuenta" : "My account"}
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  openContextHelp();
                  setMenuOpen(false);
                }}
              >
                <HelpCircle />
                {locale === "es" ? "Ayuda" : "Help"}
              </button>
              <button type="button" onClick={logout}>
                <LogOut />
                {locale === "es" ? "Salir" : "Sign out"}
              </button>
            </div>
          </nav>
          <div className="shell-utilities">
            {!actor && onBack && (
              <button
                type="button"
                onClick={onBack}
                title={locale === "es" ? "Cambiar compañía" : "Switch company"}
              >
                <Building2 />
                <span>{locale === "es" ? "Cambiar" : "Switch"}</span>
              </button>
            )}
            {actor && (
              <button type="button" onClick={() => setSection("self")}>
                <UserRound />
                <span>{locale === "es" ? "Mi cuenta" : "My account"}</span>
              </button>
            )}
            <button type="button" onClick={openContextHelp}>
              <HelpCircle />
              <span>{locale === "es" ? "Ayuda" : "Help"}</span>
            </button>
            <LanguageButton locale={locale} setLocale={setLocale} />
            <button type="button" onClick={logout} title={t.logout}>
              <LogOut />
              <span>{locale === "es" ? "Salir" : "Sign out"}</span>
            </button>
          </div>
        </div>
        {secondaryNav.length > 0 && (
          <nav
            className="secondary-navigation"
            aria-label={
              locale === "es" ? "Navegación de sección" : "Section navigation"
            }
          >
            {secondaryNav.map((item) => {
              const Icon = navIcons[item];
              return (
                <button
                  type="button"
                  key={item}
                  className={section === item ? "active" : ""}
                  aria-current={section === item ? "page" : undefined}
                  onClick={() => navigateSection(item)}
                >
                  <Icon />
                  {item === "payroll" && reviewContext.roleKey === "user"
                    ? locale === "es"
                      ? "Retención"
                      : "Withholding"
                    : t[item]}
                </button>
              );
            })}
          </nav>
        )}
      </header>
      <main className="workspace-main" id="main-content" tabIndex={-1}>
        <header className="workspace-header">
          <div>
            <p>{tenant.displayName}</p>
            <PageBreadcrumb
              locale={locale}
              items={[
                areaLabel(activeArea),
                section === "help"
                  ? locale === "es"
                    ? `Ayuda de ${areaLabel(activeArea)}`
                    : `${areaLabel(activeArea)} help`
                  : section === "payroll" && reviewContext.roleKey === "user"
                    ? locale === "es"
                      ? "Retención"
                      : "Withholding"
                    : t[section],
              ]}
            />
          </div>
          {section !== "help" && (
            <button
              type="button"
              className="context-help"
              onClick={openContextHelp}
              title={
                locale === "es"
                  ? "Ayuda para esta pantalla"
                  : "Help for this screen"
              }
            >
              <HelpCircle />
              <span>
                {locale === "es"
                  ? "Ayuda de esta pantalla"
                  : "Help for this screen"}
              </span>
            </button>
          )}
        </header>
        {reviewMode === "read_only" && (
          <div className="review-mode-banner">
            <LockKeyhole />
            {locale === "es"
              ? "Simulación de solo lectura: puede navegar, pero los controles de la pantalla están bloqueados."
              : "Read-only simulation: you may navigate, but screen controls are locked."}
          </div>
        )}
        <div className="review-content" inert={reviewMode === "read_only"}>
          {section === "home" ? (
            <Home
              t={t}
              locale={locale}
              tenant={tenant}
              setSection={setSection}
              roleKey={reviewContext.roleKey}
            />
          ) : section === "self" && actor ? (
            <PrivilegedWorkerSelfService locale={locale} tenant={tenant} />
          ) : section === "personnel" ? (
            <Personnel
              key={`${reviewContext.roleKey}-${reviewContext.workerId ?? "general"}-${personnelNavigationKey}`}
              locale={locale}
              tenant={tenant}
              canManage={[
                "platform_administrator",
                "administrator",
                "owner",
              ].includes(reviewContext.roleKey)}
              managerRole={reviewContext.roleKey}
              navigate={navigateSection}
            />
          ) : section === "personnel_setup" ? (
            <Payroll
              locale={locale}
              tenant={tenant}
              navigate={setSection}
              setupMode={
                (sessionStorage.getItem("personnel-setup-kind") as
                  | "overtime"
                  | "pay"
                  | "tax") ?? "pay"
              }
              setupWorkerId={
                sessionStorage.getItem("personnel-setup-worker") ?? ""
              }
            />
          ) : section === "time" ? (
            <Timekeeping
              key={`${reviewContext.roleKey}-${reviewContext.workerId ?? "general"}`}
              locale={locale}
              tenant={tenant}
              canConfigure={[
                "platform_administrator",
                "administrator",
                "owner",
              ].includes(reviewContext.roleKey)}
              roleKey={reviewContext.roleKey}
              actorWorkerId={reviewContext.workerId}
              workerType={reviewContext.workerType}
            />
          ) : section === "time_log" ? (
            <TimeEventLog locale={locale} tenant={tenant} />
          ) : section === "tips" ? (
            <TipsWorkflow locale={locale} tenant={tenant} />
          ) : section === "payroll" ? (
            reviewContext.roleKey === "user" && reviewContext.workerId ? (
              <EmployeeTaxQuestionnaire
                key={reviewContext.workerId}
                locale={locale}
                tenant={tenant}
              />
            ) : (
              <Payroll locale={locale} tenant={tenant} navigate={setSection} />
            )
          ) : section === "expenses" ? (
            reviewContext.roleKey === "user" ? (
              <EmployeeExpenseClaims
                key={reviewContext.workerId ?? "expenses"}
                locale={locale}
                tenant={tenant}
              />
            ) : (
              <ExpenseManagement
                locale={locale}
                tenant={tenant}
                actorWorkerId={reviewContext.workerId}
              />
            )
          ) : section === "documents" ? (
            reviewContext.roleKey === "user" && reviewContext.workerId ? (
              <EmployeeDocumentLibrary
                key={reviewContext.workerId}
                locale={locale}
                tenant={tenant}
              />
            ) : (
              <CompanyDocumentLibrary locale={locale} tenant={tenant} />
            )
          ) : section === "leave" ? (
            <Leave
              key={`${reviewContext.roleKey}-${reviewContext.workerId ?? "general"}`}
              locale={locale}
              tenant={tenant}
              canManage={[
                "platform_administrator",
                "administrator",
                "owner",
              ].includes(reviewContext.roleKey)}
              roleKey={reviewContext.roleKey}
              actorWorkerId={reviewContext.workerId}
            />
          ) : section === "reports" ? (
            <Reports locale={locale} tenant={tenant} />
          ) : section === "settings" ? (
            <Configuration
              locale={locale}
              tenant={tenant}
              roleKey={reviewContext.roleKey}
            />
          ) : section === "help" ? (
            <Help
              locale={locale}
              tenant={tenant}
              role={reviewContext.roleKey as HelpRole}
              navigate={setSection}
              initialArticleId={contextHelpArticleId}
            />
          ) : (
            <Home
              t={t}
              locale={locale}
              tenant={tenant}
              setSection={setSection}
              roleKey={reviewContext.roleKey}
            />
          )}
        </div>
      </main>
      {!actor && (
        <aside
          className="developer-dock"
          aria-label={
            locale === "es"
              ? "Simulador de vista del desarrollador"
              : "Developer view simulator"
          }
        >
          <ReviewSwitcher
            locale={locale}
            tenant={tenant}
            onChange={setReviewContext}
            viewport={reviewViewport}
            onViewport={setReviewViewport}
            mode={reviewMode}
            onMode={setReviewMode}
          />
          <div className="developer-identity">
            <UserRound />
            <span>{developerEmail}</span>
            <LiveUiQuality locale={locale} />
          </div>
        </aside>
      )}
    </div>
  );
}

function PrivilegedWorkerSelfService({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  type PersonalSection =
    | "profile"
    | "withholding"
    | "expenses"
    | "documents"
    | "security";
  const [section, setSection] = useState<PersonalSection>("profile"),
    items: { key: PersonalSection; es: string; en: string }[] = [
      { key: "profile", es: "Perfil", en: "Profile" },
      { key: "withholding", es: "Retención", en: "Withholding" },
      { key: "expenses", es: "Gastos", en: "Expenses" },
      { key: "documents", es: "Documentos", en: "Documents" },
      { key: "security", es: "Seguridad", en: "Security" },
    ];
  return (
    <>
      <div className="content">
        <p className="kicker">{tenant.displayName}</p>
        <h1>
          {locale === "es"
            ? "Mi cuenta y funciones personales"
            : "My account and personal functions"}
        </h1>
        <p className="lead">
          {locale === "es"
            ? "Estas funciones pertenecen a su propia cuenta autenticada y permanecen separadas de las decisiones que toma para la compañía o su equipo."
            : "These functions belong to your own authenticated account and remain separate from decisions you make for the company or your team."}
        </p>
        <div className="segmented">
          {items.map((item) => (
            <button
              type="button"
              key={item.key}
              className={section === item.key ? "active" : ""}
              onClick={() => setSection(item.key)}
            >
              {locale === "es" ? item.es : item.en}
            </button>
          ))}
        </div>
      </div>
      {section === "profile" ? (
        <EmployeeSelfProfile locale={locale} tenant={tenant} />
      ) : section === "withholding" ? (
        <EmployeeTaxQuestionnaire locale={locale} tenant={tenant} />
      ) : section === "expenses" ? (
        <EmployeeExpenseClaims locale={locale} tenant={tenant} />
      ) : section === "documents" ? (
        <EmployeeDocumentLibrary locale={locale} tenant={tenant} />
      ) : (
        <EmployeeSecurity locale={locale} />
      )}
    </>
  );
}

function Home({
  t,
  locale,
  tenant,
  setSection,
  roleKey,
}: {
  t: (typeof text)[Locale];
  locale: Locale;
  tenant: Tenant;
  setSection: (section: Section) => void;
  roleKey: ReviewContext["roleKey"];
}) {
  return roleKey === "supervisor" ? (
    <SupervisorHome locale={locale} tenant={tenant} setSection={setSection} />
  ) : (
    <CompanySetupHome
      t={t}
      locale={locale}
      tenant={tenant}
      setSection={setSection}
      roleKey={roleKey}
    />
  );
}

function SupervisorHome({
  locale,
  tenant,
  setSection,
}: {
  locale: Locale;
  tenant: Tenant;
  setSection: (section: Section) => void;
}) {
  const [attention, setAttention] = useState<{
      workers: number | null;
      timesheets: number | null;
      exceptions: number | null;
      leave: number | null;
    }>({ workers: null, timesheets: null, exceptions: null, leave: null }),
    [busy, setBusy] = useState(true);
  useEffect(() => {
    let active = true;
    const load = async () => {
      const [
        workerResponse,
        timesheetResponse,
        exceptionResponse,
        leaveResponse,
      ] = await Promise.all([
        api(`/v1/tenants/${tenant.id}/workers`),
        api(`/v1/tenants/${tenant.id}/timesheets`),
        api(`/v1/tenants/${tenant.id}/punch-exceptions`),
        api(`/v1/tenants/${tenant.id}/leave-requests`),
      ]);
      const workers = workerResponse.ok
          ? ((await workerResponse.json()) as { workers: unknown[] }).workers
              .length
          : null,
        timesheets = timesheetResponse.ok
          ? (
              (await timesheetResponse.json()) as {
                timesheets: { status: string }[];
              }
            ).timesheets.filter((item) =>
              ["submitted", "pending_review"].includes(item.status),
            ).length
          : null,
        exceptions = exceptionResponse.ok
          ? (
              (await exceptionResponse.json()) as {
                exceptions: { status: string }[];
              }
            ).exceptions.filter((item) =>
              ["pending", "submitted"].includes(item.status),
            ).length
          : null,
        leave = leaveResponse.ok
          ? (
              (await leaveResponse.json()) as { requests: { status: string }[] }
            ).requests.filter((item) =>
              ["submitted", "pending"].includes(item.status),
            ).length
          : null;
      if (active) {
        setAttention({ workers, timesheets, exceptions, leave });
        setBusy(false);
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [tenant.id]);
  const value = (count: number | null) =>
    busy
      ? "—"
      : count === null
        ? locale === "es"
          ? "No disponible"
          : "Unavailable"
        : String(count);
  const cards = [
    {
      key: "workers",
      count: attention.workers,
      icon: Users,
      section: "personnel" as Section,
      es: "Personas asignadas",
      en: "Assigned people",
      detailEs: "Directorio limitado al equipo bajo su alcance efectivo.",
      detailEn: "Directory limited to the team in your effective scope.",
    },
    {
      key: "timesheets",
      count: attention.timesheets,
      icon: ClipboardCheck,
      section: "time" as Section,
      es: "Hojas pendientes",
      en: "Pending timesheets",
      detailEs: "Hojas sometidas que requieren revisión autorizada.",
      detailEn: "Submitted timesheets requiring authorized review.",
    },
    {
      key: "exceptions",
      count: attention.exceptions,
      icon: TriangleAlert,
      section: "time" as Section,
      es: "Excepciones de ponche",
      en: "Punch exceptions",
      detailEs: "Excepciones pendientes dentro del equipo asignado.",
      detailEn: "Pending exceptions within the assigned team.",
    },
    {
      key: "leave",
      count: attention.leave,
      icon: CalendarDays,
      section: "leave" as Section,
      es: "Solicitudes de licencia",
      en: "Leave requests",
      detailEs: "Solicitudes pendientes de una decisión dentro de su alcance.",
      detailEn: "Requests awaiting a decision within your scope.",
    },
  ];
  return (
    <div className="content supervisor-home">
      <p className="kicker">{tenant.displayName}</p>
      <h1>{locale === "es" ? "Atención del equipo" : "Team attention"}</h1>
      <p className="lead">
        {locale === "es"
          ? "Revise solamente personas y trabajo dentro de sus asignaciones efectivas. La configuración patronal y la nómina permanecen fuera de esta vista."
          : "Review only people and work within your effective assignments. Employer configuration and payroll remain outside this view."}
      </p>
      <div className="action-dashboard">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <button
              type="button"
              key={card.key}
              onClick={() => setSection(card.section)}
            >
              <Icon />
              <strong>
                {locale === "es" ? card.es : card.en} · {value(card.count)}
              </strong>
              <span>{locale === "es" ? card.detailEs : card.detailEn}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CompanySetupHome({
  t,
  locale,
  tenant,
  setSection,
  roleKey,
}: {
  t: (typeof text)[Locale];
  locale: Locale;
  tenant: Tenant;
  setSection: (section: Section) => void;
  roleKey: ReviewContext["roleKey"];
}) {
  const [readiness, setReadiness] = useState<SetupReadiness | null>(null),
    [readinessError, setReadinessError] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    setReadiness(null);
    setReadinessError(false);
    void api(`/v1/tenants/${tenant.id}/setup-readiness`, {
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(String(response.status));
        setReadiness((await response.json()) as SetupReadiness);
      })
      .catch((error) => {
        if ((error as Error).name !== "AbortError") setReadinessError(true);
      });
    return () => controller.abort();
  }, [tenant.id]);
  const step = (key: SetupStep["key"]) =>
      readiness?.steps.find((item) => item.key === key),
    detail = (key: SetupStep["key"]) => {
      const item = step(key);
      if (!item) return t.setupLoading;
      if (item.complete) return t.stepComplete;
      if (key === "personnel") return t.workersAdded(item.counts?.workers ?? 0);
      const issues = setupMissingText(item, locale);
      if (key === "payroll")
        return `${t.payrollReady(item.counts?.readyWorkers ?? 0, item.counts?.activeWorkers ?? 0)} · ${issues}`;
      return issues || t.requirementsRemaining(item.missing.length);
    },
    setupComplete = Boolean(
      readiness && readiness.completedSteps === readiness.totalSteps,
    );
  return (
    <div className="content">
      <p className="kicker">{tenant.displayName}</p>
      <h1>
        {setupComplete
          ? locale === "es"
            ? "Centro de operaciones"
            : "Operations center"
          : t.welcome}
      </h1>
      <p className="lead">
        {setupComplete
          ? locale === "es"
            ? "Revise pendientes reales de personal, tiempo, compensación, gastos y licencias. Los contadores cambian con la información guardada."
            : "Review real pending work across people, time, compensation, expenses, and leave. Counts change with saved information."
          : t.welcomeDetail}
      </p>
      {["administrator", "owner", "platform_administrator"].includes(
        roleKey,
      ) && (
        <ManagementAttention
          locale={locale}
          tenant={tenant}
          setSection={setSection}
          roleKey={roleKey}
        />
      )}
      <div className={`setup-grid${setupComplete ? " setup-complete" : ""}`}>
        {!setupComplete && (
          <article className="progress-card">
            <div className="progress-head">
              <div>
                <p>{t.setupProgress}</p>
                <strong>
                  {readiness
                    ? t.stepsComplete(
                        readiness.completedSteps,
                        readiness.totalSteps,
                      )
                    : t.setupLoading}
                </strong>
              </div>
              <span>{readiness?.percent ?? 0}%</span>
            </div>
            <div className="progress-bar">
              <span style={{ width: `${readiness?.percent ?? 0}%` }} />
            </div>
            {readinessError && (
              <p className="form-error">{t.setupUnavailable}</p>
            )}
            <SetupItem
              complete={Boolean(step("company")?.complete)}
              icon={Building2}
              title={t.companyComplete}
              detail={detail("company")}
              onClick={() => setSection("settings")}
            />
            <SetupItem
              complete={Boolean(step("personnel")?.complete)}
              icon={Users}
              title={t.nextPersonnel}
              detail={detail("personnel")}
              onClick={() => setSection("personnel")}
            />
            <SetupItem
              complete={Boolean(step("policies")?.complete)}
              icon={Settings}
              title={t.nextPolicies}
              detail={detail("policies")}
              onClick={() => setSection("settings")}
            />
            <SetupItem
              complete={Boolean(step("payroll")?.complete)}
              icon={CircleDollarSign}
              title={t.nextPayroll}
              detail={detail("payroll")}
              onClick={() => setSection("payroll")}
            />
          </article>
        )}
        <article className="company-card">
          <span className="form-icon">
            <Building2 />
          </span>
          <p className="kicker">{t.companyProfile}</p>
          <h2>{tenant.displayName}</h2>
          <p>{tenant.legalName}</p>
          <dl>
            <div>
              <dt>{t.language}</dt>
              <dd>{tenant.defaultLocale}</dd>
            </div>
            <div>
              <dt>{locale === "es" ? "Estado" : "Status"}</dt>
              <dd className="status">
                <Check />
                {optionLabel(tenant.status, locale)}
              </dd>
            </div>
          </dl>
        </article>
      </div>
    </div>
  );
}

function ManagementAttention({
  locale,
  tenant,
  setSection,
  roleKey,
}: {
  locale: Locale;
  tenant: Tenant;
  setSection: (section: Section) => void;
  roleKey: ReviewContext["roleKey"];
}) {
  type Attention = {
    personnel: number | null;
    payroll: number | null;
    expenses: number | null;
    leave: number | null;
  };
  const [attention, setAttention] = useState<Attention>({
      personnel: null,
      payroll: null,
      expenses: null,
      leave: null,
    }),
    [busy, setBusy] = useState(true);
  useEffect(() => {
    let active = true;
    const load = async () => {
      const [workerResponse, payrollResponse, expenseResponse, leaveResponse] =
        await Promise.all([
          api(`/v1/tenants/${tenant.id}/workers`),
          api(`/v1/tenants/${tenant.id}/payroll-runs`),
          api(`/v1/tenants/${tenant.id}/expense-claims`),
          api(`/v1/tenants/${tenant.id}/leave-requests`),
        ]);
      const personnel = workerResponse.ok
          ? (
              (await workerResponse.json()) as { workers: Worker[] }
            ).workers.filter(
              (worker) =>
                !["inactive", "terminated"].includes(worker.status) &&
                !worker.readiness.complete,
            ).length
          : null,
        payroll = payrollResponse.ok
          ? (
              (await payrollResponse.json()) as { runs: PayrollRun[] }
            ).runs.filter((run) =>
              ["draft", "diagnostic", "calculated", "approved"].includes(
                run.status,
              ),
            ).length
          : null,
        expenses = expenseResponse.ok
          ? (
              (await expenseResponse.json()) as { claims: ExpenseClaim[] }
            ).claims.filter((claim) => claim.status === "submitted").length
          : null,
        leave = leaveResponse.ok
          ? (
              (await leaveResponse.json()) as { requests: LeaveRequest[] }
            ).requests.filter((request) =>
              ["submitted", "pending"].includes(request.status),
            ).length
          : null;
      if (active) {
        setAttention({ personnel, payroll, expenses, leave });
        setBusy(false);
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [tenant.id]);
  const value = (count: number | null) =>
      busy
        ? "—"
        : count === null
          ? locale === "es"
            ? "No disponible"
            : "Unavailable"
          : String(count),
    cards = [
      {
        key: "personnel",
        count: attention.personnel,
        icon: Users,
        section: "personnel" as Section,
        es: "Expedientes incompletos",
        en: "Incomplete records",
        detailEs: "Personas activas que aún no están listas para nómina.",
        detailEn: "Active people who are not yet payroll-ready.",
      },
      {
        key: "payroll",
        count: attention.payroll,
        icon: CircleDollarSign,
        section: "payroll" as Section,
        es: "Nóminas en proceso",
        en: "Payroll runs in progress",
        detailEs: "Nóminas no finalizadas que requieren continuar su flujo.",
        detailEn: "Unfinalized payroll runs that need workflow action.",
      },
      {
        key: "expenses",
        count: attention.expenses,
        icon: ClipboardCheck,
        section: "expenses" as Section,
        es: "Gastos por revisar",
        en: "Expenses to review",
        detailEs: "Reclamaciones sometidas pendientes de una decisión.",
        detailEn: "Submitted claims awaiting a decision.",
      },
      {
        key: "leave",
        count: attention.leave,
        icon: CalendarDays,
        section: "leave" as Section,
        es: "Licencias por revisar",
        en: "Leave to review",
        detailEs: "Solicitudes sometidas pendientes de una decisión.",
        detailEn: "Submitted requests awaiting a decision.",
      },
    ];
  return (
    <section
      className="management-attention"
      aria-labelledby="management-attention-heading"
    >
      <div className="section-heading">
        <div>
          <p className="kicker">
            {locale === "es" ? "Trabajo pendiente" : "Pending work"}
          </p>
          <h2 id="management-attention-heading">
            {locale === "es" ? "Atención operacional" : "Operational attention"}
          </h2>
        </div>
        <p>
          {roleKey === "owner"
            ? locale === "es"
              ? "Gobernanza y operaciones de la compañía basadas en datos actuales."
              : "Company governance and operations based on current data."
            : locale === "es"
              ? "Operaciones que requieren seguimiento autorizado."
              : "Operations requiring authorized follow-up."}
        </p>
      </div>
      <div className="action-dashboard">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <button
              type="button"
              key={card.key}
              onClick={() => setSection(card.section)}
            >
              <Icon />
              <strong>
                {locale === "es" ? card.es : card.en} · {value(card.count)}
              </strong>
              <span>{locale === "es" ? card.detailEs : card.detailEn}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function SetupItem({
  complete,
  icon: Icon,
  title,
  detail,
  onClick,
}: {
  complete?: boolean;
  icon: typeof Building2;
  title: string;
  detail: string;
  onClick?: () => void;
}) {
  return (
    <button className="setup-item" onClick={onClick} disabled={complete}>
      <span className={complete ? "item-icon complete" : "item-icon"}>
        {complete ? <Check /> : <Icon />}
      </span>
      <div>
        <strong>{title}</strong>
        <small title={detail}>{detail}</small>
      </div>
      {!complete && <ChevronRight />}
    </button>
  );
}

type Worker = {
  id: string;
  firstName: string;
  middleName: string | null;
  lastName: string | null;
  preferredName: string | null;
  dateOfBirth: string | null;
  mailingAddress: {
    line1: string;
    line2: string | null;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  } | null;
  workerNumber: string | null;
  workerType: string;
  status: string;
  profileCompletionPercent: number;
  positionTitle: string | null;
  phoneE164: string | null;
  secondaryPhoneE164: string | null;
  email: string | null;
  startDate: string | null;
  endDate: string | null;
  notes: string | null;
  roleKey: string;
  timeEntryMethod: string;
  hasNoTrackingPolicy?: boolean;
  scheduleMode: string;
  tipEligible: boolean;
  address: unknown | null;
  taxpayerIdentifierPresent: boolean;
  taxOnboarding: {
    applicable: boolean;
    profilePrerequisitesComplete: boolean;
    status: "not_applicable" | "not_started" | "draft" | "certified";
    effectiveFrom: string | null;
    certifiedAt: string | null;
    payrollBlocked: boolean;
  };
  readiness: {
    percent: number;
    complete: boolean;
    items: ReadinessItem[];
    missing: ReadinessItem[];
  };
};
const employeePayrollBlockers = new Set([
  "worker_number",
  "effective_pay_rate",
  "certified_499_r4",
]);
const professionalServicePaymentBlockers = new Set([
  "worker_number",
  "effective_pay_rate",
]);
const paymentBlockingItems = (worker: Worker) => {
  const blockers =
    worker.workerType === "professional_services"
      ? professionalServicePaymentBlockers
      : employeePayrollBlockers;
  return worker.readiness.missing.filter((item) => blockers.has(item.code));
};
type AccountMembership = {
  id: string;
  workerId: string;
  workerName: string;
  roleKey: string;
  status: string;
  accountStatus: string;
  maskedPhone: string;
  phoneVerifiedAt: string | null;
  approvedAt: string | null;
  lastSessionAt: string | null;
  invitationDeliveryStatus: string | null;
  invitationExpiresAt: string | null;
};
type OwnershipTransfer = {
  id: string;
  currentOwnerMembershipId: string;
  targetMembershipId: string;
  status: string;
  reason: string;
  initiatedAt: string;
  eligibleAt: string;
  expiresAt: string;
  acceptedAt: string | null;
  cancelledAt: string | null;
};
type EmploymentPeriod = {
  id: string;
  startDate: string;
  endDate: string | null;
  lastDayWorked: string | null;
  terminationReason: string | null;
  rehireOfPeriodId: string | null;
  finalPayDueOn: string | null;
  finalPayStatus: string;
  finalPayRunId: string | null;
  leaveDisposition: string | null;
  finalDeductionDisposition: string | null;
};
type PersonnelDocument = {
  id: string;
  category: string;
  title: string;
  visibility: string;
  effectiveOn: string | null;
  expiresOn: string | null;
  fileName: string;
  contentType: string;
  byteSize: number;
  contentSha256: string;
  status: string;
  archivedAt: string | null;
  archiveReason: string | null;
  createdAt: string;
};
type WorkerProfileWorkspace = {
  workerId: string;
  workerType: string;
  visibility: Record<
    | "overview"
    | "employment"
    | "time"
    | "leave"
    | "tax"
    | "pay"
    | "documents"
    | "access",
    boolean
  >;
  time: {
    timesheetCount: number;
    latestTimesheetStatus: string | null;
    latestPeriodStart: string | null;
    latestPeriodEnd: string | null;
    eventCount30Days: number;
    attentionCount: number;
  };
  compensation: {
    rateType: string;
    rateCents: number;
    salaryFrequency: string | null;
    salaryRegularHoursHundredths: number | null;
    salaryOvertimeMethod: string | null;
    effectiveFrom: string;
  } | null;
  overtimeClassification: {
    classification: string;
    effectiveFrom: string;
    effectiveTo: string | null;
    dutiesSummary: string;
  } | null;
  contractorProfile: {
    relevoType: "none" | "partial" | "full";
    relevoNumber: string | null;
    relevoExpiresOn: string | null;
    statutoryExempt: number;
    payeeType: "individual" | "entity";
  } | null;
  leave: {
    balances: {
      policyId: string;
      name: string;
      leaveType: string;
      accrualMethod: string;
      balanceHours: number;
    }[];
    requests: { totalCount: number; pendingCount: number };
  };
  deductions: {
    id: string;
    deductionType: string;
    description: string;
    calculationType: string;
    amountCents: number | null;
    rateBps: number | null;
    currentBalanceCents: number | null;
    maximumPerPayrollCents: number | null;
    priority: number;
    effectiveFrom: string;
    effectiveTo: string | null;
    status: string;
  }[];
  pay: {
    kind: "employee" | "professional_services";
    paymentCount: number;
    ytdGrossCents: number;
    ytdTaxCents?: number;
    ytdDeductionCents?: number;
    ytdWithholdingCents?: number;
    ytdNetCents: number;
    latestPayDate: string | null;
    openingBalance?: {
      taxYear: number;
      asOfDate: string;
      grossWagesCents?: number;
      grossPaymentsCents?: number;
      nontaxablePayCents?: number;
      prTaxableWagesCents?: number;
      ficaWagesCents?: number;
      sinotWagesCents?: number;
      futaWagesCents?: number;
      unemploymentWagesCents?: number;
      prWithholdingCents?: number;
      socialSecurityEmployeeCents?: number;
      medicareEmployeeCents?: number;
      sinotEmployeeCents?: number;
      otherDeductionsCents?: number;
      netPayCents?: number;
      sourceNote: string;
    } | null;
  } | null;
  documents: { totalCount: number; activeCount: number } | null;
};
type TerminationDraft = {
  worker: Worker;
  terminationDate: string;
  lastDayWorked: string;
  reason: string;
  finalPayDueOn: string;
  leaveDisposition: "paid_out" | "forfeited" | "transferred" | "not_applicable";
  finalDeductionDisposition:
    | "apply_active"
    | "suspend_voluntary"
    | "suspend_all";
};
type PersonnelActionDraft =
  | { kind: "revoke_access"; membership: AccountMembership; reason: string }
  | { kind: "propose_owner"; membership: AccountMembership; reason: string }
  | { kind: "cancel_transfer"; transfer: OwnershipTransfer; reason: string }
  | {
      kind: "archive_document";
      worker: Worker;
      document: PersonnelDocument;
      reason: string;
    }
  | { kind: "rehire"; worker: Worker; rehireDate: string; reason: string };

const initialWorker = {
  firstName: "",
  middleName: null as string | null,
  lastName: null as string | null,
  preferredName: null as string | null,
  dateOfBirth: null as string | null,
  mailingAddress: null as {
    line1: string;
    line2: string | null;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  } | null,
  workerNumber: null as string | null,
  workerType: "employee",
  status: "draft",
  positionTitle: null as string | null,
  phoneE164: null as string | null,
  secondaryPhoneE164: null as string | null,
  email: null as string | null,
  startDate: null as string | null,
  endDate: null as string | null,
  notes: null as string | null,
  timeEntryMethod: "clock",
  scheduleMode: "fixed",
  tipEligible: false,
  roleKey: "user",
};

function Personnel({
  locale,
  tenant,
  canManage,
  managerRole,
  navigate,
}: {
  locale: Locale;
  tenant: Tenant;
  canManage: boolean;
  managerRole: string;
  navigate: (section: Section) => void;
}) {
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [memberships, setMemberships] = useState<AccountMembership[]>([]);
  const [transfers, setTransfers] = useState<OwnershipTransfer[]>([]);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedWorkerId, setSelectedWorkerId] = useState<string | null>(null),
    [profileTab, setProfileTab] = useState<
      | "overview"
      | "employment"
      | "time"
      | "leave"
      | "tax"
      | "pay"
      | "documents"
      | "access"
    >("overview"),
    [workerQuery, setWorkerQuery] = useState("");
  const [form, setForm] = useState(initialWorker);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState(false);
  const [personnelError, setPersonnelError] = useState("");
  const [accountError, setAccountError] = useState("");
  const [employmentHistory, setEmploymentHistory] = useState<
    Record<string, EmploymentPeriod[]>
  >({});
  const [profileWorkspaces, setProfileWorkspaces] = useState<
    Record<string, WorkerProfileWorkspace>
  >({});
  const [documentWorkerId, setDocumentWorkerId] = useState<string | null>(null),
    [personnelDocuments, setPersonnelDocuments] = useState<PersonnelDocument[]>(
      [],
    ),
    [personnelDocumentFile, setPersonnelDocumentFile] = useState<File | null>(
      null,
    );
  const [personnelDocumentForm, setPersonnelDocumentForm] = useState({
    category: "employment",
    title: "",
    visibility: "employee_visible",
    effectiveOn: "",
    expiresOn: "",
  });
  const [terminationDraft, setTerminationDraft] =
    useState<TerminationDraft | null>(null);
  const [personnelAction, setPersonnelAction] =
    useState<PersonnelActionDraft | null>(null);
  const [taxProfileForm, setTaxProfileForm] = useState<null | {
    workerId: string;
    line1: string;
    line2: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    taxpayerId: string;
  }>(null);
  const [contractorTaxForm, setContractorTaxForm] = useState<null | {
    workerId: string;
    relevoType: "none" | "partial" | "full";
    relevoNumber: string;
    relevoExpiresOn: string;
    statutoryExempt: boolean;
    payeeType: "individual" | "entity";
    openingTaxYear: string;
    openingAsOfDate: string;
    openingGrossPayments: string;
    openingSourceNote: string;
  }>(null);
  const [ytdOpeningForm, setYtdOpeningForm] = useState<null | {
    workerId: string;
    taxYear: string;
    asOfDate: string;
    grossWages: string;
    nontaxablePay: string;
    prTaxableWages: string;
    ficaWages: string;
    sinotWages: string;
    futaWages: string;
    unemploymentWages: string;
    prWithholding: string;
    socialSecurityEmployee: string;
    medicareEmployee: string;
    sinotEmployee: string;
    otherDeductions: string;
    netPay: string;
    sourceNote: string;
  }>(null);
  const load = async () => {
    const [response, accounts, transferResponse] = await Promise.all([
      api(`/v1/tenants/${tenant.id}/workers`),
      api(`/v1/tenants/${tenant.id}/account-memberships`),
      canManage
        ? api(`/v1/tenants/${tenant.id}/ownership-transfers`)
        : Promise.resolve(null),
    ]);
    if (response.ok)
      setWorkers(((await response.json()) as { workers: Worker[] }).workers);
    if (accounts.ok)
      setMemberships(
        ((await accounts.json()) as { memberships: AccountMembership[] })
          .memberships,
      );
    if (transferResponse?.ok)
      setTransfers(
        ((await transferResponse.json()) as { transfers: OwnershipTransfer[] })
          .transfers,
      );
    setBusy(false);
  };
  useEffect(() => {
    void load();
  }, [tenant.id]);
  const field = (
    key: keyof typeof initialWorker,
    value: string | boolean | null,
  ) =>
    setForm((current) => ({ ...current, [key]: value === "" ? null : value }));
  const personnelAddressField = (
    key: "line1" | "line2" | "city" | "state" | "postalCode",
    value: string,
  ) =>
    setForm((current) => ({
      ...current,
      mailingAddress: {
        line1: current.mailingAddress?.line1 ?? "",
        line2: current.mailingAddress?.line2 ?? null,
        city: current.mailingAddress?.city ?? "",
        state: current.mailingAddress?.state ?? "PR",
        postalCode: current.mailingAddress?.postalCode ?? "",
        country: current.mailingAddress?.country ?? "PR",
        [key]: key === "line2" && value === "" ? null : value,
      },
    }));
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(false);
    if (
      editingId &&
      form.startDate &&
      workers.find((worker) => worker.id === editingId)?.startDate === null
    ) {
      const initialDateResponse = await api(
        `/v1/tenants/${tenant.id}/workers/${editingId}/initial-employment-date`,
        {
          method: "POST",
          body: JSON.stringify({
            startDate: form.startDate,
            reason:
              locale === "es"
                ? "Fecha inicial registrada durante la configuración de personal"
                : "Initial date recorded during personnel setup",
          }),
        },
      );
      if (!initialDateResponse.ok) {
        setError(true);
        setBusy(false);
        return;
      }
    }
    const response = await api(
      editingId
        ? `/v1/tenants/${tenant.id}/workers/${editingId}`
        : `/v1/tenants/${tenant.id}/workers`,
      { method: editingId ? "PUT" : "POST", body: JSON.stringify(form) },
    );
    if (response.ok) {
      setForm(initialWorker);
      setAdding(false);
      setEditingId(null);
      await load();
      window.dispatchEvent(new Event("workers-updated"));
    } else {
      const body = (await response.json().catch(() => null)) as
        | {
          error?: string | { code?: string; requestId?: string };
          requestId?: string;
          fields?: string[];
          diagnostic?: { constraint?: string; subsystem?: string };
          conflict?: { field?: string; displayName?: string };
          }
        | null;
      const conflictingName = body?.conflict?.displayName;
      const responseError = typeof body?.error === "string" ? body.error : body?.error?.code;
      const responseRequestId = body?.requestId || (typeof body?.error === "object" ? body.error.requestId : undefined);
      const diagnosticReference = responseRequestId
        ? ` (${locale === "es" ? "referencia" : "reference"}: ${responseRequestId})`
        : "";
      const databaseDiagnostic = body?.diagnostic
        ? ` [${body.diagnostic.subsystem || "personnel_transaction"}/${body.diagnostic.constraint || "unknown_constraint"}]`
        : "";
      const diagnosticMessage =
        responseError === "personnel_validation_failed"
          ? locale === "es"
            ? `Revise estos campos: ${(body?.fields || ["expediente"]).join(", ")}.`
            : `Review these fields: ${(body?.fields || ["record"]).join(", ")}.`
          : responseError === "personnel_conflict" && body?.conflict?.field === "phoneE164"
          ? locale === "es"
            ? `Este teléfono ya pertenece a ${conflictingName || "otro expediente"}.`
            : `This phone already belongs to ${conflictingName || "another record"}.`
          : responseError === "personnel_conflict" && body?.conflict?.field === "workerNumber"
            ? locale === "es"
              ? `Este número de trabajador ya pertenece a ${conflictingName || "otro expediente"}.`
              : `This worker number already belongs to ${conflictingName || "another record"}.`
            : responseError === "personnel_lifecycle_mismatch"
              ? locale === "es"
                ? "El expediente cambió mientras estaba abierto. Cancele la edición, vuelva a abrirlo e inténtelo nuevamente."
                : "The record changed while it was open. Cancel editing, reopen it, and try again."
              : locale === "es"
                ? "El servidor rechazó el expediente por una regla de integridad no relacionada con teléfono o número de trabajador."
                : "The server rejected the record because of an integrity rule unrelated to phone or worker number.";
      setPersonnelError(`${diagnosticMessage} [HTTP ${response.status}${responseError ? `/${responseError}` : ""}]${databaseDiagnostic}${diagnosticReference}`);
      setError(true);
      setBusy(false);
    }
  };
  const edit = (worker: Worker) => {
    setSelectedWorkerId(worker.id);
    setEditingId(worker.id);
    setAdding(true);
    setForm({
      firstName: worker.firstName,
      middleName: worker.middleName,
      lastName: worker.lastName,
      preferredName: worker.preferredName,
      dateOfBirth: worker.dateOfBirth,
      mailingAddress: worker.mailingAddress,
      workerNumber: worker.workerNumber,
      workerType: worker.workerType,
      status: worker.status,
      positionTitle: worker.positionTitle,
      phoneE164: worker.phoneE164,
      secondaryPhoneE164: worker.secondaryPhoneE164,
      email: worker.email,
      startDate: worker.startDate,
      endDate: worker.endDate,
      notes: worker.notes,
      timeEntryMethod: worker.timeEntryMethod,
      scheduleMode: worker.scheduleMode,
      tipEligible: worker.tipEligible,
      roleKey: worker.roleKey,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancel = () => {
    setAdding(false);
    setEditingId(null);
    setForm(initialWorker);
    setError(false);
    setPersonnelError("");
  };
  const invite = async (worker: Worker) => {
    setBusy(true);
    setAccountError("");
    const response = await api(`/v1/tenants/${tenant.id}/account-invitations`, {
      method: "POST",
      body: JSON.stringify({ workerId: worker.id, roleKey: worker.roleKey }),
    });
    if (!response.ok)
      setAccountError(
        response.status === 409
          ? locale === "es"
            ? "La persona ya tiene una membresía o la cuenta requiere revisión."
            : "This person already has a membership or the account requires review."
          : locale === "es"
            ? "No se pudo enviar la invitación. Verifique el teléfono móvil."
            : "Unable to send the invitation. Verify the mobile phone.",
      );
    await load();
  };
  const resendInvitation = async (membership: AccountMembership) => {
    setBusy(true);
    setAccountError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/memberships/${membership.id}/resend-invitation`,
      { method: "POST" },
    );
    if (!response.ok)
      setAccountError(
        locale === "es"
          ? "No se pudo reenviar la invitación. Verifique el estado y la configuración de SMS."
          : "Unable to resend the invitation. Check its status and SMS configuration.",
      );
    await load();
  };
  const approve = async (membership: AccountMembership) => {
    setBusy(true);
    setAccountError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/memberships/${membership.id}/approve`,
      { method: "POST" },
    );
    if (!response.ok)
      setAccountError(
        locale === "es"
          ? "No se pudo aprobar la cuenta en su estado actual."
          : "The account could not be approved in its current state.",
      );
    await load();
  };
  const revokeMembership = (membership: AccountMembership) =>
    setPersonnelAction({ kind: "revoke_access", membership, reason: "" });
  const initiateTransfer = (membership: AccountMembership) =>
    setPersonnelAction({ kind: "propose_owner", membership, reason: "" });
  const acceptTransfer = async (transfer: OwnershipTransfer) => {
    setBusy(true);
    setAccountError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/ownership-transfers/${transfer.id}/accept`,
      { method: "POST" },
    );
    if (response.ok) {
      window.location.reload();
      return;
    }
    setAccountError(
      locale === "es"
        ? "La transferencia aún está en enfriamiento, expiró o no pertenece a esta cuenta."
        : "The transfer is still cooling off, expired, or does not belong to this account.",
    );
    await load();
  };
  const cancelTransfer = (transfer: OwnershipTransfer) =>
    setPersonnelAction({ kind: "cancel_transfer", transfer, reason: "" });
  const loadEmploymentHistory = async (worker: Worker) => {
    const response = await api(
      `/v1/tenants/${tenant.id}/workers/${worker.id}/employment-history`,
    );
    if (response.ok) {
      const body = (await response.json()) as { periods: EmploymentPeriod[] };
      setEmploymentHistory((current) => ({
        ...current,
        [worker.id]: body.periods,
      }));
    } else
      setAccountError(
        locale === "es"
          ? "No se pudo cargar el historial de empleo."
          : "Employment history could not be loaded.",
      );
  };
  const loadProfileWorkspace = async (worker: Worker) => {
    const response = await api(
      `/v1/tenants/${tenant.id}/workers/${worker.id}/profile-workspace`,
    );
    if (response.ok) {
      const body = (await response.json()) as {
        workspace: WorkerProfileWorkspace;
      };
      setProfileWorkspaces((current) => ({
        ...current,
        [worker.id]: body.workspace,
      }));
    } else
      setAccountError(
        locale === "es"
          ? "No se pudo cargar el resumen operacional de esta persona."
          : "This worker's operational summary could not be loaded.",
      );
  };
  const openYtdOpening = (worker: Worker) => {
    const value = profileWorkspaces[worker.id]?.pay?.openingBalance,
      toDollars = (c: number | undefined) =>
        c === undefined ? "" : (c / 100).toFixed(2);
    setYtdOpeningForm({
      workerId: worker.id,
      taxYear: String(value?.taxYear ?? new Date().getFullYear()),
      asOfDate: value?.asOfDate ?? "",
      grossWages: toDollars(value?.grossWagesCents),
      nontaxablePay: toDollars(value?.nontaxablePayCents),
      prTaxableWages: toDollars(value?.prTaxableWagesCents),
      ficaWages: toDollars(value?.ficaWagesCents),
      sinotWages: toDollars(value?.sinotWagesCents),
      futaWages: toDollars(value?.futaWagesCents),
      unemploymentWages: toDollars(value?.unemploymentWagesCents),
      prWithholding: toDollars(value?.prWithholdingCents),
      socialSecurityEmployee: toDollars(value?.socialSecurityEmployeeCents),
      medicareEmployee: toDollars(value?.medicareEmployeeCents),
      sinotEmployee: toDollars(value?.sinotEmployeeCents),
      otherDeductions: toDollars(value?.otherDeductionsCents),
      netPay: toDollars(value?.netPayCents),
      sourceNote: value?.sourceNote ?? "",
    });
  };
  const saveYtdOpening = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedWorker || !ytdOpeningForm) return;
    setBusy(true);
    setAccountError("");
    const cents = (value: string) => Math.round(Number(value || 0) * 100),
      response = await api(
        `/v1/tenants/${tenant.id}/workers/${selectedWorker.id}/payroll-ytd-opening`,
        {
          method: "PUT",
          body: JSON.stringify({
            taxYear: Number(ytdOpeningForm.taxYear),
            asOfDate: ytdOpeningForm.asOfDate,
            grossWagesCents: cents(ytdOpeningForm.grossWages),
            nontaxablePayCents: cents(ytdOpeningForm.nontaxablePay),
            prTaxableWagesCents: cents(ytdOpeningForm.prTaxableWages),
            ficaWagesCents: cents(ytdOpeningForm.ficaWages),
            sinotWagesCents: cents(ytdOpeningForm.sinotWages),
            futaWagesCents: cents(ytdOpeningForm.futaWages),
            unemploymentWagesCents: cents(ytdOpeningForm.unemploymentWages),
            prWithholdingCents: cents(ytdOpeningForm.prWithholding),
            socialSecurityEmployeeCents: cents(
              ytdOpeningForm.socialSecurityEmployee,
            ),
            medicareEmployeeCents: cents(ytdOpeningForm.medicareEmployee),
            sinotEmployeeCents: cents(ytdOpeningForm.sinotEmployee),
            otherDeductionsCents: cents(ytdOpeningForm.otherDeductions),
            netPayCents: cents(ytdOpeningForm.netPay),
            sourceNote: ytdOpeningForm.sourceNote,
          }),
        },
      );
    if (response.ok) {
      setYtdOpeningForm(null);
      await loadProfileWorkspace(selectedWorker);
    } else
      setAccountError(
        locale === "es"
          ? "No se pudieron guardar los acumulados anteriores."
          : "Prior year-to-date totals could not be saved.",
      );
    setBusy(false);
  };
  const loadPersonnelDocuments = async (worker: Worker) => {
    setDocumentWorkerId(worker.id);
    setPersonnelDocumentFile(null);
    const response = await api(
      `/v1/tenants/${tenant.id}/workers/${worker.id}/personnel-documents`,
    );
    if (response.ok)
      setPersonnelDocuments(
        ((await response.json()) as { documents: PersonnelDocument[] })
          .documents,
      );
    else
      setAccountError(
        locale === "es"
          ? "No se pudieron cargar los documentos."
          : "Personnel documents could not be loaded.",
      );
  };
  const uploadPersonnelDocument = async (worker: Worker) => {
    if (!personnelDocumentFile) return;
    setBusy(true);
    setAccountError("");
    const body = new FormData();
    body.set("file", personnelDocumentFile);
    Object.entries(personnelDocumentForm).forEach(([key, value]) =>
      body.set(key, value),
    );
    const response = await api(
      `/v1/tenants/${tenant.id}/workers/${worker.id}/personnel-documents`,
      { method: "POST", body },
    );
    if (response.ok) {
      setPersonnelDocumentFile(null);
      setPersonnelDocumentForm({
        ...personnelDocumentForm,
        title: "",
        effectiveOn: "",
        expiresOn: "",
      });
      await loadPersonnelDocuments(worker);
    } else
      setAccountError(
        locale === "es"
          ? "No se pudo guardar. Verifique fechas, título y un archivo permitido de hasta 10 MB."
          : "Unable to save. Check dates, title, and an allowed file up to 10 MB.",
      );
    setBusy(false);
  };
  const downloadPersonnelDocument = async (
    worker: Worker,
    item: PersonnelDocument,
  ) => {
    const response = await api(
      `/v1/tenants/${tenant.id}/workers/${worker.id}/personnel-documents/${item.id}`,
    );
    if (!response.ok) return;
    const url = URL.createObjectURL(await response.blob()),
      anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = item.fileName;
    anchor.click();
    URL.revokeObjectURL(url);
  };
  const archivePersonnelDocument = (worker: Worker, item: PersonnelDocument) =>
    setPersonnelAction({
      kind: "archive_document",
      worker,
      document: item,
      reason: "",
    });
  const openTermination = (worker: Worker) => {
    const today = new Date().toISOString().slice(0, 10);
    setAccountError("");
    setTerminationDraft({
      worker,
      terminationDate: today,
      lastDayWorked: today,
      reason: "",
      finalPayDueOn: today,
      leaveDisposition: "not_applicable",
      finalDeductionDisposition: "apply_active",
    });
  };
  const terminate = async (event: FormEvent) => {
    event.preventDefault();
    if (!terminationDraft) return;
    setBusy(true);
    setAccountError("");
    const { worker, ...payload } = terminationDraft,
      response = await api(
        `/v1/tenants/${tenant.id}/workers/${worker.id}/terminate`,
        { method: "POST", body: JSON.stringify(payload) },
      );
    if (!response.ok) {
      setAccountError(
        locale === "es"
          ? "No se pudo terminar: revise fechas, período abierto, propietario, licencia y pago final."
          : "Termination failed: check dates, open period, owner status, leave, and final pay.",
      );
      setBusy(false);
      return;
    }
    setTerminationDraft(null);
    await load();
  };
  const rehire = (worker: Worker) =>
    setPersonnelAction({
      kind: "rehire",
      worker,
      rehireDate: new Date().toISOString().slice(0, 10),
      reason: "",
    });
  const submitPersonnelAction = async (event: FormEvent) => {
    event.preventDefault();
    const action = personnelAction;
    if (!action) return;
    setBusy(true);
    setAccountError("");
    let response: Response;
    if (action.kind === "revoke_access")
      response = await api(
        `/v1/tenants/${tenant.id}/memberships/${action.membership.id}/revoke`,
        { method: "POST", body: JSON.stringify({ reason: action.reason }) },
      );
    else if (action.kind === "propose_owner")
      response = await api(`/v1/tenants/${tenant.id}/ownership-transfers`, {
        method: "POST",
        body: JSON.stringify({
          targetMembershipId: action.membership.id,
          reason: action.reason,
        }),
      });
    else if (action.kind === "cancel_transfer")
      response = await api(
        `/v1/tenants/${tenant.id}/ownership-transfers/${action.transfer.id}/cancel`,
        { method: "POST", body: JSON.stringify({ reason: action.reason }) },
      );
    else if (action.kind === "archive_document")
      response = await api(
        `/v1/tenants/${tenant.id}/workers/${action.worker.id}/personnel-documents/${action.document.id}/archive`,
        { method: "POST", body: JSON.stringify({ reason: action.reason }) },
      );
    else
      response = await api(
        `/v1/tenants/${tenant.id}/workers/${action.worker.id}/rehire`,
        {
          method: "POST",
          body: JSON.stringify({
            rehireDate: action.rehireDate,
            roleKey: "user",
            positionTitle: action.worker.positionTitle,
            timeEntryMethod: action.worker.timeEntryMethod,
            scheduleMode: action.worker.scheduleMode,
            reason: action.reason,
          }),
        },
      );
    if (!response.ok) {
      const fallback = {
        revoke_access: [
          "No se puede revocar este acceso.",
          "This access cannot be revoked.",
        ],
        propose_owner: [
          "No se pudo iniciar la transferencia.",
          "Unable to initiate the transfer.",
        ],
        cancel_transfer: [
          "No se pudo cancelar la transferencia.",
          "Unable to cancel the transfer.",
        ],
        archive_document: [
          "No se pudo archivar el documento.",
          "Unable to archive the document.",
        ],
        rehire: [
          "No se pudo recontratar. Revise la fecha.",
          "Rehire failed. Check the date.",
        ],
      }[action.kind];
      setAccountError(fallback[locale === "es" ? 0 : 1]!);
      setBusy(false);
      return;
    }
    setPersonnelAction(null);
    if (action.kind === "archive_document")
      await loadPersonnelDocuments(action.worker);
    await load();
  };
  const actionCopy = personnelAction
    ? (
        {
          revoke_access: {
            es: "Revocar acceso",
            en: "Revoke access",
            detailEs:
              "La sesión y el acceso de esta membresía se cerrarán. El historial se conserva.",
            detailEn:
              "This membership's session and access will be closed. History is retained.",
          },
          propose_owner: {
            es: "Proponer transferencia de propiedad",
            en: "Propose ownership transfer",
            detailEs:
              "La transferencia conserva un período de espera y debe ser aceptada por la cuenta propuesta.",
            detailEn:
              "The transfer retains a waiting period and must be accepted by the proposed account.",
          },
          cancel_transfer: {
            es: "Cancelar transferencia",
            en: "Cancel ownership transfer",
            detailEs:
              "La propuesta pendiente se cancelará con la razón documentada.",
            detailEn:
              "The pending proposal will be cancelled with the documented reason.",
          },
          archive_document: {
            es: "Archivar documento",
            en: "Archive document",
            detailEs:
              "El archivo dejará de estar activo, pero su registro y huella se conservarán.",
            detailEn:
              "The file will no longer be active, but its record and fingerprint will be retained.",
          },
          rehire: {
            es: "Recontratar persona",
            en: "Rehire worker",
            detailEs:
              "Se creará un nuevo período de empleo. El período anterior no se modifica y se requerirá una invitación nueva.",
            detailEn:
              "A new employment period will be created. The prior period is unchanged and a fresh invitation will be required.",
          },
        } as const
      )[personnelAction.kind]
    : null;
  const actionSubject = personnelAction
    ? personnelAction.kind === "revoke_access" ||
      personnelAction.kind === "propose_owner"
      ? personnelAction.membership.workerName
      : personnelAction.kind === "cancel_transfer"
        ? personnelAction.transfer.reason
        : personnelAction.kind === "archive_document"
          ? `${personnelAction.worker.firstName} · ${personnelAction.document.title}`
          : `${personnelAction.worker.firstName} ${personnelAction.worker.lastName ?? ""}`
    : "";
  const selectedWorker =
      workers.find((worker) => worker.id === selectedWorkerId) ?? null,
    selectedMembership =
      memberships.find((item) => item.workerId === selectedWorkerId) ?? null,
    normalizedQuery = workerQuery.trim().toLocaleLowerCase(),
    visibleWorkers = selectedWorker
      ? [selectedWorker]
      : workers.filter(
          (worker) =>
            !normalizedQuery ||
            `${worker.firstName} ${worker.lastName ?? ""} ${worker.preferredName ?? ""} ${worker.positionTitle ?? ""} ${worker.workerNumber ?? ""}`
              .toLocaleLowerCase()
              .includes(normalizedQuery),
        );
  const openWorker = (worker: Worker) => {
    setSelectedWorkerId(worker.id);
    setProfileTab("overview");
    void loadProfileWorkspace(worker);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const openTaxProfile = (worker: Worker) => {
    const address = (worker.address ?? {}) as Record<string, string>;
    setSelectedWorkerId(worker.id);
    setProfileTab("tax");
    setTaxProfileForm({
      workerId: worker.id,
      line1: address.line1 ?? "",
      line2: address.line2 ?? "",
      city: address.city ?? "",
      state: address.state ?? "PR",
      postalCode: address.postalCode ?? "",
      country: address.country ?? "PR",
      taxpayerId: "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const openPersonnelSetup = (
    kind: "overtime" | "pay" | "tax",
    worker: Worker,
  ) => {
    sessionStorage.setItem("personnel-setup-kind", kind);
    sessionStorage.setItem("personnel-setup-worker", worker.id);
    if (kind === "tax") sessionStorage.setItem("payroll-tax-worker", worker.id);
    navigate("personnel_setup");
  };
  const openReadinessItem = (item: ReadinessItem, worker: Worker) => {
    if (
      item.code === "residential_address" ||
      item.code === "taxpayer_identifier"
    )
      openTaxProfile(worker);
    else if (item.code === "effective_pay_rate")
      openPersonnelSetup("pay", worker);
    else if (item.code === "certified_499_r4")
      openPersonnelSetup("tax", worker);
    else edit(worker);
  };
  const saveTaxProfile = async (event: FormEvent) => {
    event.preventDefault();
    if (!taxProfileForm) return;
    setBusy(true);
    setAccountError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/workers/${taxProfileForm.workerId}/tax-profile`,
      {
        method: "PUT",
        body: JSON.stringify({
          address: {
            line1: taxProfileForm.line1,
            line2: taxProfileForm.line2 || null,
            city: taxProfileForm.city,
            state: taxProfileForm.state,
            postalCode: taxProfileForm.postalCode,
            country: taxProfileForm.country,
          },
          taxpayerId: taxProfileForm.taxpayerId || undefined,
        }),
      },
    );
    if (response.ok) {
      setTaxProfileForm(null);
      await load();
    } else
      setAccountError(
        locale === "es"
          ? "No se pudo guardar la dirección o identificación. Revise todos los campos."
          : "Address or taxpayer ID could not be saved. Review every field.",
      );
    setBusy(false);
  };
  const openContractorTaxProfile = (worker: Worker) => {
    const value = profileWorkspaces[worker.id]?.contractorProfile,
      opening = profileWorkspaces[worker.id]?.pay?.openingBalance;
    setContractorTaxForm({
      workerId: worker.id,
      relevoType: value?.relevoType ?? "none",
      relevoNumber: value?.relevoNumber ?? "",
      relevoExpiresOn: value?.relevoExpiresOn ?? "",
      statutoryExempt: Boolean(value?.statutoryExempt),
      payeeType: value?.payeeType ?? "individual",
      openingTaxYear: String(opening?.taxYear ?? new Date().getFullYear()),
      openingAsOfDate: opening?.asOfDate ?? "",
      openingGrossPayments:
        opening?.grossPaymentsCents === undefined
          ? ""
          : (opening.grossPaymentsCents / 100).toFixed(2),
      openingSourceNote:
        opening?.sourceNote ??
        "Pagos recibidos antes del primer período procesado en Jornada PR",
    });
  };
  const saveContractorTaxProfile = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedWorker || !contractorTaxForm) return;
    setBusy(true);
    setAccountError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/workers/${contractorTaxForm.workerId}/contractor-tax-profile`,
      {
        method: "PUT",
        body: JSON.stringify({
          relevoType: contractorTaxForm.relevoType,
          relevoNumber: contractorTaxForm.relevoNumber.trim() || null,
          relevoExpiresOn: contractorTaxForm.relevoExpiresOn || null,
          statutoryExempt: contractorTaxForm.statutoryExempt,
          payeeType: contractorTaxForm.payeeType,
        }),
      },
    );
    const openingResponse = response.ok
      ? await api(
          `/v1/tenants/${tenant.id}/workers/${contractorTaxForm.workerId}/contractor-ytd-opening`,
          {
            method: "PUT",
            body: JSON.stringify({
              taxYear: Number(contractorTaxForm.openingTaxYear),
              asOfDate: contractorTaxForm.openingAsOfDate,
              grossPaymentsCents: Math.round(
                Number(contractorTaxForm.openingGrossPayments || 0) * 100,
              ),
              sourceNote: contractorTaxForm.openingSourceNote,
            }),
          },
        )
      : null;
    if (response.ok && openingResponse?.ok) {
      setContractorTaxForm(null);
      await loadProfileWorkspace(selectedWorker);
    } else {
      const body = (await response.json().catch(() => ({}))) as {
        error?: string;
      };
      setAccountError(
        body.error === "relevo_number_required"
          ? locale === "es"
            ? "Ingrese el número del relevo seleccionado."
            : "Enter the selected waiver number."
          : locale === "es"
            ? "No se pudo guardar la retención, el relevo o el acumulado inicial."
            : "Withholding, waiver, or opening year-to-date amount could not be saved.",
      );
    }
    setBusy(false);
  };
  const profileWorkspace = selectedWorker
    ? profileWorkspaces[selectedWorker.id]
    : undefined;
  const money = (cents: number | undefined) =>
    new Intl.NumberFormat(locale === "es" ? "es-PR" : "en-US", {
      style: "currency",
      currency: "USD",
    }).format((cents ?? 0) / 100);
  return (
    <div className="content">
      <div className="section-heading">
        <div>
          <p className="kicker">{tenant.displayName}</p>
          <h1>
            {selectedWorker
              ? `${selectedWorker.preferredName || selectedWorker.firstName} ${selectedWorker.lastName ?? ""}`
              : locale === "es"
                ? "Personas"
                : "People"}
          </h1>
          <p className="lead">
            {selectedWorker
              ? managerRole === "supervisor"
                ? locale === "es"
                  ? "Expediente operativo de empleo, tiempo, horario y licencias para su equipo asignado."
                  : "Operational employment, time, schedule, and leave record for your assigned team."
                : selectedWorker.workerType === "professional_services"
                  ? locale === "es"
                    ? "Expediente unificado de servicios, tiempo, retención, pagos, documentos y acceso."
                    : "Unified service, time, withholding, payment, document, and access record."
                  : locale === "es"
                    ? "Expediente unificado de empleo, tiempo, licencia, contribuciones, pago, documentos y acceso."
                    : "Unified employment, time, leave, tax, pay, document, and access record."
              : locale === "es"
                ? "Cree personas progresivamente. Los datos faltantes permanecen visibles antes de tiempo, nómina, pago y radicación."
                : "Create people progressively. Missing data remains visible before time, payroll, payment, and filing."}
          </p>
        </div>
        {selectedWorker && (
          <button
            type="button"
            onClick={() => {
              setSelectedWorkerId(null);
              setDocumentWorkerId(null);
            }}
          >
            {locale === "es" ? "Volver al directorio" : "Back to directory"}
          </button>
        )}
        {canManage && (
          <button
            className="primary"
            onClick={() =>
              adding
                ? cancel()
                : (setAdding(true), setEditingId(null), setForm(initialWorker))
            }
          >
            <Users />
            {adding
              ? locale === "es"
                ? "Cancelar"
                : "Cancel"
              : locale === "es"
                ? "Añadir persona"
                : "Add person"}
          </button>
        )}
      </div>
      {!selectedWorker && (
        <>
          <div className="people-toolbar">
            <label>
              {locale === "es" ? "Buscar personas" : "Search people"}
              <input
                type="search"
                value={workerQuery}
                onChange={(event) => setWorkerQuery(event.target.value)}
                placeholder={
                  locale === "es"
                    ? "Nombre, puesto o número"
                    : "Name, position, or number"
                }
              />
            </label>
            <span>
              {visibleWorkers.length}{" "}
              {locale === "es" ? "resultado(s)" : "result(s)"}
            </span>
          </div>
          <p className="field-note">
            {locale === "es"
              ? "Para dar acceso a una persona: complete su teléfono móvil y seleccione “Invitar a la aplicación”."
              : "To give a person access: complete their mobile phone and select “Invite to app”."}
          </p>
        </>
      )}
      {selectedWorker && (
        <section className="worker-profile-shell">
          <header>
            <div className="worker-avatar">
              {(selectedWorker.preferredName || selectedWorker.firstName)[0]}
            </div>
            <div>
              <strong>
                {selectedWorker.preferredName || selectedWorker.firstName}{" "}
                {selectedWorker.lastName ?? ""}
              </strong>
              <span>
                {selectedWorker.positionTitle ||
                  (locale === "es"
                    ? "Puesto sin especificar"
                    : "Position not specified")}{" "}
                · {optionLabel(selectedWorker.status, locale)} ·{" "}
                {selectedWorker.profileCompletionPercent}%{" "}
                {locale === "es" ? "completo" : "complete"}
              </span>
            </div>
          </header>
          <nav
            className="worker-profile-tabs"
            role="tablist"
            aria-label={
              locale === "es"
                ? "Secciones del expediente"
                : "Worker record sections"
            }
          >
            {(
              [
                { key: "overview", es: "Resumen", en: "Overview" },
                {
                  key: "employment",
                  es:
                    selectedWorker.workerType === "professional_services"
                      ? "Servicio y compensación"
                      : "Empleo y compensación",
                  en:
                    selectedWorker.workerType === "professional_services"
                      ? "Service & compensation"
                      : "Employment & compensation",
                },
                {
                  key: "time",
                  es:
                    selectedWorker.workerType === "professional_services"
                      ? "Tiempo y servicios"
                      : "Tiempo y horario",
                  en:
                    selectedWorker.workerType === "professional_services"
                      ? "Time & services"
                      : "Time & schedule",
                },
                { key: "leave", es: "Licencias", en: "Leave" },
                {
                  key: "tax",
                  es:
                    selectedWorker.workerType === "professional_services"
                      ? "Retención y relevo"
                      : "Contribuciones y deducciones",
                  en:
                    selectedWorker.workerType === "professional_services"
                      ? "Withholding & waiver"
                      : "Tax & deductions",
                },
                {
                  key: "pay",
                  es:
                    selectedWorker.workerType === "professional_services"
                      ? "Pagos"
                      : "Pago",
                  en:
                    selectedWorker.workerType === "professional_services"
                      ? "Payments"
                      : "Pay",
                },
                { key: "documents", es: "Documentos", en: "Documents" },
                { key: "access", es: "Acceso", en: "Access" },
              ] as const
            )
              .filter(
                (tab) =>
                  profileWorkspace?.visibility[tab.key] ??
                  (!(
                    tab.key === "leave" &&
                    selectedWorker.workerType === "professional_services"
                  ) &&
                    (!(["tax", "pay", "documents", "access"] as const).includes(
                      tab.key as "tax" | "pay" | "documents" | "access",
                    ) ||
                      managerRole !== "supervisor")),
              )
              .map((tab) => (
                <button
                  type="button"
                  role="tab"
                  key={tab.key}
                  id={`worker-profile-tab-${tab.key}`}
                  aria-controls="worker-profile-panel"
                  aria-selected={profileTab === tab.key}
                  tabIndex={profileTab === tab.key ? 0 : -1}
                  className={profileTab === tab.key ? "active" : ""}
                  onKeyDown={(event) => {
                    if (
                      !["ArrowLeft", "ArrowRight", "Home", "End"].includes(
                        event.key,
                      )
                    )
                      return;
                    event.preventDefault();
                    const tabs = Array.from(
                      event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>(
                        '[role="tab"]',
                      ) ?? [],
                    );
                    const index = tabs.indexOf(event.currentTarget),
                      next =
                        event.key === "Home"
                          ? 0
                          : event.key === "End"
                            ? tabs.length - 1
                            : (index +
                                (event.key === "ArrowRight" ? 1 : -1) +
                                tabs.length) %
                              tabs.length;
                    tabs[next]?.focus();
                    tabs[next]?.click();
                  }}
                  onClick={() => {
                    if (adding) {
                      setAdding(false);
                      setEditingId(null);
                      setForm(initialWorker);
                      setError(false);
                    }
                    setTaxProfileForm(null);
                    setProfileTab(tab.key);
                    if (
                      tab.key === "employment" &&
                      !employmentHistory[selectedWorker.id]
                    )
                      void loadEmploymentHistory(selectedWorker);
                    if (tab.key === "documents" && canManage)
                      void loadPersonnelDocuments(selectedWorker);
                  }}
                >
                  {locale === "es" ? tab.es : tab.en}
                </button>
              ))}
          </nav>
        </section>
      )}
      {selectedWorker && !adding && (
        <section
          className="worker-profile-content"
          id="worker-profile-panel"
          role="tabpanel"
          aria-labelledby={`worker-profile-tab-${profileTab}`}
          aria-live="polite"
        >
          {profileTab === "overview" && (
            <>
              <div className="contextual-heading">
                <div>
                  <p className="kicker">
                    {locale === "es"
                      ? "RESUMEN DEL EXPEDIENTE"
                      : "RECORD OVERVIEW"}
                  </p>
                  <h2>
                    {locale === "es"
                      ? "Información actual"
                      : "Current information"}
                  </h2>
                </div>
                {canManage && (
                  <button type="button" onClick={() => edit(selectedWorker)}>
                    {locale === "es"
                      ? "Editar información"
                      : "Edit information"}
                  </button>
                )}
              </div>
              <dl className="profile-facts">
                <div>
                  <dt>{locale === "es" ? "Tipo" : "Type"}</dt>
                  <dd>{optionLabel(selectedWorker.workerType, locale)}</dd>
                </div>
                <div>
                  <dt>
                    {locale === "es"
                      ? "Puesto o servicio"
                      : "Position or service"}
                  </dt>
                  <dd>
                    {selectedWorker.positionTitle ||
                      (locale === "es" ? "Sin especificar" : "Not specified")}
                  </dd>
                </div>
                <div>
                  <dt>{locale === "es" ? "Estado" : "Status"}</dt>
                  <dd>{optionLabel(selectedWorker.status, locale)}</dd>
                </div>
                <div>
                  <dt>{locale === "es" ? "Fecha de inicio" : "Start date"}</dt>
                  <dd>
                    {selectedWorker.startDate
                      ? formatDate(selectedWorker.startDate, locale)
                      : "—"}
                  </dd>
                </div>
                <div>
                  <dt>
                    {locale === "es" ? "Fecha de nacimiento" : "Date of birth"}
                  </dt>
                  <dd>
                    {selectedWorker.dateOfBirth
                      ? formatDate(selectedWorker.dateOfBirth, locale)
                      : "—"}
                  </dd>
                </div>
                <div>
                  <dt>
                    {locale === "es"
                      ? "Dirección residencial (obligatoria)"
                      : "Residential address (required)"}
                  </dt>
                  <dd>
                    {selectedWorker.mailingAddress
                      ? [
                          selectedWorker.mailingAddress.line1,
                          selectedWorker.mailingAddress.line2,
                          `${selectedWorker.mailingAddress.city}, ${selectedWorker.mailingAddress.state} ${selectedWorker.mailingAddress.postalCode}`,
                        ]
                          .filter(Boolean)
                          .join(" · ")
                      : "—"}
                  </dd>
                </div>
                {managerRole !== "supervisor" && (
                  <div>
                    <dt>{locale === "es" ? "Teléfono" : "Phone"}</dt>
                    <dd>{selectedWorker.phoneE164 || "—"}</dd>
                  </div>
                )}
                {managerRole !== "supervisor" && (
                  <div>
                    <dt>Email</dt>
                    <dd>{selectedWorker.email || "—"}</dd>
                  </div>
                )}
                <div>
                  <dt>
                    {locale === "es" ? "Modo de horario" : "Schedule mode"}
                  </dt>
                  <dd>{optionLabel(selectedWorker.scheduleMode, locale)}</dd>
                </div>
                <div>
                  <dt>
                    {locale === "es" ? "Método de tiempo" : "Time-entry method"}
                  </dt>
                  <dd>{optionLabel(selectedWorker.timeEntryMethod, locale)}</dd>
                </div>
                <div>
                  <dt>
                    {locale === "es" ? "Perfil completo" : "Profile complete"}
                  </dt>
                  <dd>{selectedWorker.profileCompletionPercent}%</dd>
                </div>
              </dl>
              {paymentBlockingItems(selectedWorker).length > 0 && (
                <div className="worker-readiness">
                  <strong>
                    {locale === "es" ? "Falta completar" : "Still required"}
                  </strong>
                  <ul>
                    {paymentBlockingItems(selectedWorker).map((item) => (
                      <li key={item.code}>
                        <button
                          type="button"
                          onClick={() =>
                            openReadinessItem(item, selectedWorker)
                          }
                        >
                          {readinessLabels[item.code]?.[
                            locale === "es" ? "es" : "en"
                          ] ?? item.code}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
          {profileTab === "employment" && (
            <>
              <div className="contextual-heading">
                <div>
                  <p className="kicker">
                    {locale === "es"
                      ? "RELACIÓN VIGENTE E HISTORIAL"
                      : "CURRENT ARRANGEMENT AND HISTORY"}
                  </p>
                  <h2>
                    {selectedWorker.workerType === "professional_services"
                      ? locale === "es"
                        ? "Servicio y compensación"
                        : "Service and compensation"
                      : locale === "es"
                        ? "Empleo y compensación"
                        : "Employment and compensation"}
                  </h2>
                </div>
                {canManage && (
                  <button type="button" onClick={() => edit(selectedWorker)}>
                    {locale === "es"
                      ? "Editar relación y registro de tiempo"
                      : "Edit arrangement and time tracking"}
                  </button>
                )}
                {canManage &&
                  !["terminated", "inactive", "draft"].includes(
                    selectedWorker.status,
                  ) && (
                    <button
                      type="button"
                      className="danger-action"
                      onClick={() => openTermination(selectedWorker)}
                    >
                      {locale === "es"
                        ? "Terminar empleo"
                        : "Terminate employment"}
                    </button>
                  )}
                {canManage && selectedWorker.status === "terminated" && (
                  <button type="button" onClick={() => rehire(selectedWorker)}>
                    {locale === "es" ? "Recontratar" : "Rehire"}
                  </button>
                )}
              </div>
              <dl className="profile-facts">
                <div>
                  <dt>
                    {locale === "es" ? "Clasificación" : "Classification"}
                  </dt>
                  <dd>{optionLabel(selectedWorker.workerType, locale)}</dd>
                </div>
                <div>
                  <dt>
                    {locale === "es" ? "Puesto o título" : "Position or title"}
                  </dt>
                  <dd>{selectedWorker.positionTitle || "—"}</dd>
                </div>
                <div>
                  <dt>{locale === "es" ? "Número" : "Number"}</dt>
                  <dd>{selectedWorker.workerNumber || "—"}</dd>
                </div>
                <div>
                  <dt>{locale === "es" ? "Inicio" : "Start"}</dt>
                  <dd>{formatDate(selectedWorker.startDate, locale)}</dd>
                </div>
                <div>
                  <dt>{locale === "es" ? "Terminación" : "Termination"}</dt>
                  <dd>{formatDate(selectedWorker.endDate, locale)}</dd>
                </div>
                <div>
                  <dt>{locale === "es" ? "Estado" : "Status"}</dt>
                  <dd>{optionLabel(selectedWorker.status, locale)}</dd>
                </div>
              </dl>
              <section className="settings-form">
                <div className="section-heading">
                  <div>
                    <h3>
                      {locale === "es"
                        ? "Compensación y control de tiempo"
                        : "Compensation and time control"}
                    </h3>
                    <p>
                      {locale === "es"
                        ? "Información vigente visible sin entrar al formulario de edición."
                        : "Current information visible without opening an edit form."}
                    </p>
                  </div>
                </div>
                <dl className="profile-facts">
                  <div>
                    <dt>
                      {locale === "es"
                        ? "Tipo de compensación"
                        : "Compensation type"}
                    </dt>
                    <dd>
                      {profileWorkspace?.compensation
                        ? optionLabel(
                            profileWorkspace.compensation.rateType,
                            locale,
                          )
                        : locale === "es"
                          ? "No configurada"
                          : "Not configured"}
                    </dd>
                  </div>
                  <div>
                    <dt>
                      {profileWorkspace?.compensation?.rateType === "salary"
                        ? locale === "es"
                          ? "Salario por nómina"
                          : "Salary per payroll"
                        : locale === "es"
                          ? "Tarifa"
                          : "Rate"}
                    </dt>
                    <dd>
                      {profileWorkspace?.compensation
                        ? money(profileWorkspace.compensation.rateCents)
                        : "—"}
                    </dd>
                  </div>
                  <div>
                    <dt>
                      {locale === "es" ? "Vigente desde" : "Effective from"}
                    </dt>
                    <dd>
                      {formatDate(
                        profileWorkspace?.compensation?.effectiveFrom,
                        locale,
                      )}
                    </dd>
                  </div>
                  {profileWorkspace?.compensation?.salaryFrequency && (
                    <div>
                      <dt>
                        {locale === "es"
                          ? "Frecuencia salarial"
                          : "Salary frequency"}
                      </dt>
                      <dd>
                        {optionLabel(
                          profileWorkspace.compensation.salaryFrequency,
                          locale,
                        )}
                      </dd>
                    </div>
                  )}
                  {profileWorkspace?.compensation
                    ?.salaryRegularHoursHundredths !== null &&
                    profileWorkspace?.compensation
                      ?.salaryRegularHoursHundredths !== undefined && (
                      <div>
                        <dt>
                          {locale === "es"
                            ? "Horas regulares cubiertas"
                            : "Regular hours covered"}
                        </dt>
                        <dd>
                          {(
                            profileWorkspace.compensation
                              .salaryRegularHoursHundredths / 100
                          ).toFixed(2)}{" "}
                          h
                        </dd>
                      </div>
                    )}
                  <div>
                    <dt>
                      {locale === "es"
                        ? "Método de tiempo"
                        : "Time-entry method"}
                    </dt>
                    <dd>
                      {optionLabel(selectedWorker.timeEntryMethod, locale)}
                    </dd>
                  </div>
                  <div>
                    <dt>
                      {locale === "es" ? "Modo de horario" : "Schedule mode"}
                    </dt>
                    <dd>{optionLabel(selectedWorker.scheduleMode, locale)}</dd>
                  </div>
                  {selectedWorker.workerType === "employee" && (
                    <div>
                      <dt>
                        {locale === "es"
                          ? "Horas extra"
                          : "Overtime classification"}
                      </dt>
                      <dd>
                        {profileWorkspace?.overtimeClassification
                          ? optionLabel(
                              profileWorkspace.overtimeClassification
                                .classification,
                              locale,
                            )
                          : locale === "es"
                            ? "No configurada"
                            : "Not configured"}
                      </dd>
                    </div>
                  )}
                  <div>
                    <dt>
                      {locale === "es"
                        ? "Política sin ponches"
                        : "No-punch policy"}
                    </dt>
                    <dd>
                      {selectedWorker.hasNoTrackingPolicy
                        ? locale === "es"
                          ? "Documentada"
                          : "Documented"
                        : locale === "es"
                          ? "No aplica"
                          : "Not applicable"}
                    </dd>
                  </div>
                </dl>
                {canManage && (
                  <div className="form-actions">
                    <button
                      type="button"
                      onClick={() => openPersonnelSetup("pay", selectedWorker)}
                    >
                      {selectedWorker.workerType === "professional_services"
                        ? locale === "es"
                          ? "Configurar compensación"
                          : "Configure compensation"
                        : locale === "es"
                          ? "Configurar salario o tarifa"
                          : "Configure salary or rate"}
                    </button>
                    {selectedWorker.workerType === "employee" && (
                      <button
                        type="button"
                        onClick={() =>
                          openPersonnelSetup("overtime", selectedWorker)
                        }
                      >
                        {locale === "es"
                          ? "Configurar exento / no exento"
                          : "Configure exempt / nonexempt"}
                      </button>
                    )}
                    <button type="button" onClick={() => edit(selectedWorker)}>
                      {locale === "es"
                        ? "Configurar ponches y horario"
                        : "Configure punches and schedule"}
                    </button>
                  </div>
                )}
              </section>
              {canManage && (
                <Timekeeping
                  locale={locale}
                  tenant={tenant}
                  canConfigure={canManage}
                  roleKey={managerRole}
                  actorWorkerId={selectedWorker.id}
                  workerType={
                    selectedWorker.workerType as ReviewContext["workerType"]
                  }
                  workspace="person_setup"
                />
              )}
              <div className="profile-history">
                <h3>
                  {locale === "es"
                    ? "Historial inmutable"
                    : "Immutable history"}
                </h3>
                {(employmentHistory[selectedWorker.id] ?? []).length ? (
                  <div className="run-list">
                    {(employmentHistory[selectedWorker.id] ?? []).map(
                      (period) => (
                        <article key={period.id}>
                          <div>
                            <strong>
                              {formatDateRange(
                                period.startDate,
                                period.endDate,
                                locale,
                                locale === "es" ? "Vigente" : "Current",
                              )}
                            </strong>
                            <p>
                              {period.terminationReason ??
                                (locale === "es"
                                  ? "Período activo"
                                  : "Active period")}
                            </p>
                            <small>
                              {locale === "es" ? "Pago final" : "Final pay"}:{" "}
                              {optionLabel(period.finalPayStatus, locale)}
                            </small>
                          </div>
                        </article>
                      ),
                    )}
                  </div>
                ) : (
                  <p>
                    {locale === "es"
                      ? "No hay períodos históricos adicionales."
                      : "There are no additional historical periods."}
                  </p>
                )}
              </div>
            </>
          )}
          {profileTab === "time" && (
            <>
              <div className="contextual-heading">
                <div>
                  <p className="kicker">
                    {locale === "es" ? "TIEMPO Y HORARIO" : "TIME AND SCHEDULE"}
                  </p>
                  <h2>
                    {locale === "es"
                      ? "Actividad y hojas de tiempo"
                      : "Time activity and timesheets"}
                  </h2>
                </div>
                <button type="button" onClick={() => navigate("time")}>
                  {locale === "es"
                    ? "Abrir tiempo y horario"
                    : "Open time and schedule"}
                </button>
              </div>
              <dl className="profile-facts">
                <div>
                  <dt>
                    {locale === "es"
                      ? "Tarjeta más reciente"
                      : "Latest timecard"}
                  </dt>
                  <dd>
                    {profileWorkspace?.time.latestTimesheetStatus
                      ? optionLabel(
                          profileWorkspace.time.latestTimesheetStatus,
                          locale,
                        )
                      : "—"}
                  </dd>
                </div>
                <div>
                  <dt>
                    {locale === "es"
                      ? "Eventos · últimos 30 días"
                      : "Events · last 30 days"}
                  </dt>
                  <dd>{profileWorkspace?.time.eventCount30Days ?? 0}</dd>
                </div>
                <div>
                  <dt>
                    {locale === "es" ? "Requieren atención" : "Needs attention"}
                  </dt>
                  <dd>{profileWorkspace?.time.attentionCount ?? 0}</dd>
                </div>
              </dl>
              <p className="form-detail">
                {profileWorkspace?.time.latestPeriodStart
                  ? `${locale === "es" ? "Período más reciente" : "Latest period"}: ${formatDateRange(profileWorkspace.time.latestPeriodStart, profileWorkspace.time.latestPeriodEnd, locale, "—")}`
                  : locale === "es"
                    ? "Todavía no hay tarjetas de tiempo para esta persona."
                    : "This worker has no timecards yet."}
              </p>
            </>
          )}
          {profileTab === "leave" && (
            <>
              <div className="contextual-heading">
                <div>
                  <p className="kicker">
                    {locale === "es" ? "LICENCIAS" : "LEAVE"}
                  </p>
                  <h2>
                    {locale === "es"
                      ? "Balances, solicitudes y transacciones"
                      : "Balances, requests, and transactions"}
                  </h2>
                </div>
                <button type="button" onClick={() => navigate("leave")}>
                  {locale === "es" ? "Abrir licencias" : "Open leave"}
                </button>
              </div>
              <dl className="profile-facts">
                <div>
                  <dt>
                    {locale === "es" ? "Solicitudes totales" : "Total requests"}
                  </dt>
                  <dd>{profileWorkspace?.leave.requests.totalCount ?? 0}</dd>
                </div>
                <div>
                  <dt>
                    {locale === "es"
                      ? "Solicitudes pendientes"
                      : "Pending requests"}
                  </dt>
                  <dd>{profileWorkspace?.leave.requests.pendingCount ?? 0}</dd>
                </div>
                {(profileWorkspace?.leave.balances ?? []).map((balance) => (
                  <div key={balance.policyId}>
                    <dt>
                      {balance.name} · {optionLabel(balance.leaveType, locale)}
                    </dt>
                    <dd>
                      {balance.balanceHours}{" "}
                      {locale === "es" ? "horas" : "hours"}
                    </dd>
                    <small>{optionLabel(balance.accrualMethod, locale)}</small>
                  </div>
                ))}
              </dl>
              {!profileWorkspace?.leave.balances.length && (
                <p className="form-detail">
                  {locale === "es"
                    ? "No hay políticas de licencia vigentes asignadas."
                    : "No current leave policies are assigned."}
                </p>
              )}
            </>
          )}
          {profileTab === "tax" &&
            selectedWorker.workerType === "professional_services" && (
              <>
                <div className="contextual-heading">
                  <div>
                    <p className="kicker">
                      {locale === "es"
                        ? "RETENCIÓN DE SERVICIOS PROFESIONALES"
                        : "PROFESSIONAL SERVICES WITHHOLDING"}
                    </p>
                    <h2>
                      {locale === "es"
                        ? "Retención y relevo"
                        : "Withholding and waiver"}
                    </h2>
                    <p className="form-detail">
                      {locale === "es"
                        ? "La retención predeterminada es 10%. Registre un relevo solamente cuando exista documentación válida."
                        : "Default withholding is 10%. Record a waiver only when valid documentation exists."}
                    </p>
                  </div>
                </div>
                <dl className="profile-facts">
                  <div>
                    <dt>
                      {locale === "es"
                        ? "Modelo aplicable"
                        : "Applicable model"}
                    </dt>
                    <dd>
                      {(profileWorkspace?.contractorProfile?.statutoryExempt ??
                      0)
                        ? locale === "es"
                          ? "Exención estatutaria · 0%"
                          : "Statutory exemption · 0%"
                        : profileWorkspace?.contractorProfile?.relevoType ===
                            "partial"
                          ? locale === "es"
                            ? "Relevo parcial · 6%"
                            : "Partial waiver · 6%"
                          : profileWorkspace?.contractorProfile?.relevoType ===
                              "full"
                            ? locale === "es"
                              ? "Relevo total · 0%"
                              : "Full waiver · 0%"
                            : locale === "es"
                              ? "Retención normal · 10%"
                              : "Standard withholding · 10%"}
                    </dd>
                  </div>
                  <div>
                    <dt>
                      {locale === "es" ? "Número de relevo" : "Waiver number"}
                    </dt>
                    <dd>
                      {profileWorkspace?.contractorProfile?.relevoNumber || "—"}
                    </dd>
                  </div>
                  <div>
                    <dt>{locale === "es" ? "Expiración" : "Expiration"}</dt>
                    <dd>
                      {profileWorkspace?.contractorProfile?.relevoExpiresOn
                        ? formatDate(
                            profileWorkspace.contractorProfile.relevoExpiresOn,
                            locale,
                          )
                        : "—"}
                    </dd>
                  </div>
                  <div>
                    <dt>
                      {locale === "es"
                        ? "Cobrado antes del primer período"
                        : "Paid before the first period"}
                    </dt>
                    <dd>
                      {money(
                        profileWorkspace?.pay?.openingBalance
                          ?.grossPaymentsCents,
                      )}
                    </dd>
                    <small>
                      {profileWorkspace?.pay?.openingBalance?.asOfDate
                        ? `${locale === "es" ? "Hasta" : "Through"} ${formatDate(profileWorkspace.pay.openingBalance.asOfDate, locale)}`
                        : locale === "es"
                          ? "No registrado"
                          : "Not recorded"}
                    </small>
                  </div>
                </dl>
                {canManage &&
                  contractorTaxForm?.workerId !== selectedWorker.id && (
                    <button
                      type="button"
                      onClick={() => openContractorTaxProfile(selectedWorker)}
                    >
                      {locale === "es"
                        ? "Actualizar retención o relevo"
                        : "Update withholding or waiver"}
                    </button>
                  )}
                {canManage &&
                  contractorTaxForm?.workerId === selectedWorker.id && (
                    <form
                      className="settings-form"
                      onSubmit={saveContractorTaxProfile}
                    >
                      <div className="form-grid">
                        <label>
                          {locale === "es" ? "Tipo de persona" : "Payee type"}
                          <select
                            value={contractorTaxForm.payeeType}
                            onChange={(event) =>
                              setContractorTaxForm({
                                ...contractorTaxForm,
                                payeeType: event.currentTarget.value as
                                  | "individual"
                                  | "entity",
                              })
                            }
                          >
                            <option value="individual">
                              {locale === "es" ? "Individuo" : "Individual"}
                            </option>
                            <option value="entity">
                              {locale === "es" ? "Entidad" : "Entity"}
                            </option>
                          </select>
                        </label>
                        <label>
                          {locale === "es"
                            ? "Año del acumulado inicial"
                            : "Opening balance year"}
                          <input
                            type="number"
                            min="2000"
                            max="2100"
                            required
                            value={contractorTaxForm.openingTaxYear}
                            onChange={(event) =>
                              setContractorTaxForm({
                                ...contractorTaxForm,
                                openingTaxYear: event.currentTarget.value,
                              })
                            }
                          />
                        </label>
                        <label>
                          {locale === "es"
                            ? "Cobrado antes de la primera compensación"
                            : "Paid before the first compensation"}
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            required
                            value={contractorTaxForm.openingGrossPayments}
                            onChange={(event) =>
                              setContractorTaxForm({
                                ...contractorTaxForm,
                                openingGrossPayments: event.currentTarget.value,
                              })
                            }
                          />
                        </label>
                        <label>
                          {locale === "es" ? "Fecha de corte" : "As-of date"}
                          <input
                            type="date"
                            required
                            value={contractorTaxForm.openingAsOfDate}
                            onChange={(event) =>
                              setContractorTaxForm({
                                ...contractorTaxForm,
                                openingAsOfDate: event.currentTarget.value,
                              })
                            }
                          />
                        </label>
                        <label>
                          {locale === "es"
                            ? "Nota de procedencia"
                            : "Source note"}
                          <input
                            required
                            minLength={3}
                            value={contractorTaxForm.openingSourceNote}
                            onChange={(event) =>
                              setContractorTaxForm({
                                ...contractorTaxForm,
                                openingSourceNote: event.currentTarget.value,
                              })
                            }
                          />
                        </label>
                        <label>
                          {locale === "es"
                            ? "Modelo de retención"
                            : "Withholding model"}
                          <select
                            value={contractorTaxForm.relevoType}
                            onChange={(event) =>
                              setContractorTaxForm({
                                ...contractorTaxForm,
                                relevoType: event.currentTarget.value as
                                  | "none"
                                  | "partial"
                                  | "full",
                              })
                            }
                          >
                            <option value="none">
                              {locale === "es"
                                ? "Retención normal · 10%"
                                : "Standard withholding · 10%"}
                            </option>
                            <option value="partial">
                              {locale === "es"
                                ? "Relevo parcial · 6%"
                                : "Partial waiver · 6%"}
                            </option>
                            <option value="full">
                              {locale === "es"
                                ? "Relevo total · 0%"
                                : "Full waiver · 0%"}
                            </option>
                          </select>
                        </label>
                        <label>
                          {locale === "es"
                            ? "Número de relevo"
                            : "Waiver number"}
                          <input
                            required={contractorTaxForm.relevoType !== "none"}
                            disabled={contractorTaxForm.relevoType === "none"}
                            value={contractorTaxForm.relevoNumber}
                            onChange={(event) =>
                              setContractorTaxForm({
                                ...contractorTaxForm,
                                relevoNumber: event.currentTarget.value,
                              })
                            }
                          />
                        </label>
                        <label>
                          {locale === "es"
                            ? "Fecha de expiración (opcional si el relevo no expira)"
                            : "Expiration date (optional when the waiver does not expire)"}
                          <input
                            type="date"
                            disabled={contractorTaxForm.relevoType === "none"}
                            value={contractorTaxForm.relevoExpiresOn}
                            onChange={(event) =>
                              setContractorTaxForm({
                                ...contractorTaxForm,
                                relevoExpiresOn: event.currentTarget.value,
                              })
                            }
                          />
                        </label>
                      </div>
                      <label className="checkbox-row">
                        <input
                          type="checkbox"
                          checked={contractorTaxForm.statutoryExempt}
                          onChange={(event) =>
                            setContractorTaxForm({
                              ...contractorTaxForm,
                              statutoryExempt: event.currentTarget.checked,
                            })
                          }
                        />
                        {locale === "es"
                          ? "Exención estatutaria documentada · no retener (0%)"
                          : "Documented statutory exemption · do not withhold (0%)"}
                      </label>
                      <div className="form-actions">
                        <button className="primary" disabled={busy}>
                          {locale === "es"
                            ? "Guardar retención"
                            : "Save withholding"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setContractorTaxForm(null)}
                        >
                          {locale === "es" ? "Cancelar" : "Cancel"}
                        </button>
                      </div>
                    </form>
                  )}
              </>
            )}
          {profileTab === "tax" &&
            selectedWorker.workerType !== "professional_services" && (
              <>
                <div className="contextual-heading">
                  <div>
                    <p className="kicker">
                      {locale === "es"
                        ? "CONTRIBUCIONES Y DEDUCCIONES"
                        : "TAX AND DEDUCTIONS"}
                    </p>
                    <h2>
                      {selectedWorker.workerType === "professional_services"
                        ? locale === "es"
                          ? "Retención de servicios y relevo"
                          : "Service withholding and waiver"
                        : locale === "es"
                          ? "Elecciones y deducciones"
                          : "Elections and deductions"}
                    </h2>
                  </div>
                  <button type="button" onClick={() => navigate("payroll")}>
                    {locale === "es"
                      ? "Abrir nómina y deducciones"
                      : "Open payroll and deductions"}
                  </button>
                </div>
                <dl className="profile-facts">
                  <div>
                    <dt>
                      {selectedWorker.workerType === "professional_services"
                        ? locale === "es"
                          ? "Estado contributivo"
                          : "Withholding status"
                        : "499 R-4"}
                    </dt>
                    <dd>
                      {optionLabel(selectedWorker.taxOnboarding.status, locale)}
                    </dd>
                  </div>
                  <div>
                    <dt>
                      {locale === "es"
                        ? "Identificación contributiva"
                        : "Taxpayer ID"}
                    </dt>
                    <dd>
                      {selectedWorker.taxpayerIdentifierPresent
                        ? locale === "es"
                          ? "Registrada"
                          : "On file"
                        : locale === "es"
                          ? "Pendiente"
                          : "Pending"}
                    </dd>
                  </div>
                  <div>
                    <dt>
                      {locale === "es"
                        ? "Nómina habilitada"
                        : "Payroll enabled"}
                    </dt>
                    <dd>
                      {selectedWorker.taxOnboarding.payrollBlocked
                        ? locale === "es"
                          ? "Bloqueada"
                          : "Blocked"
                        : locale === "es"
                          ? "Lista"
                          : "Ready"}
                    </dd>
                  </div>
                </dl>
                {canManage &&
                  taxProfileForm?.workerId !== selectedWorker.id && (
                    <button
                      type="button"
                      onClick={() => openTaxProfile(selectedWorker)}
                    >
                      {locale === "es"
                        ? "Actualizar perfil contributivo"
                        : "Update tax profile"}
                    </button>
                  )}
                {canManage &&
                  taxProfileForm?.workerId === selectedWorker.id && (
                    <form className="settings-form" onSubmit={saveTaxProfile}>
                      <h3>
                        {locale === "es"
                          ? "Dirección residencial e identificación contributiva"
                          : "Residential address and taxpayer identification"}
                      </h3>
                      <div className="form-grid">
                        <label>
                          {locale === "es"
                            ? "Dirección residencial"
                            : "Residential address"}
                          <input
                            required
                            value={taxProfileForm.line1}
                            onChange={(e) =>
                              setTaxProfileForm({
                                ...taxProfileForm,
                                line1: e.currentTarget.value,
                              })
                            }
                          />
                        </label>
                        <label>
                          {locale === "es"
                            ? "Dirección 2 (opcional)"
                            : "Address line 2 (optional)"}
                          <input
                            value={taxProfileForm.line2}
                            onChange={(e) =>
                              setTaxProfileForm({
                                ...taxProfileForm,
                                line2: e.currentTarget.value,
                              })
                            }
                          />
                        </label>
                        <label>
                          {locale === "es" ? "Ciudad" : "City"}
                          <input
                            required
                            value={taxProfileForm.city}
                            onChange={(e) =>
                              setTaxProfileForm({
                                ...taxProfileForm,
                                city: e.currentTarget.value,
                              })
                            }
                          />
                        </label>
                        <label>
                          {locale === "es"
                            ? "Estado/territorio"
                            : "State/territory"}
                          <input
                            required
                            value={taxProfileForm.state}
                            onChange={(e) =>
                              setTaxProfileForm({
                                ...taxProfileForm,
                                state: e.currentTarget.value,
                              })
                            }
                          />
                        </label>
                        <label>
                          {locale === "es" ? "Código postal" : "Postal code"}
                          <input
                            required
                            value={taxProfileForm.postalCode}
                            onChange={(e) =>
                              setTaxProfileForm({
                                ...taxProfileForm,
                                postalCode: e.currentTarget.value,
                              })
                            }
                          />
                        </label>
                        <label>
                          {locale === "es"
                            ? "Seguro Social / identificación contributiva"
                            : "SSN / taxpayer identification"}
                          <input
                            type="password"
                            autoComplete="off"
                            placeholder={
                              selectedWorker.taxpayerIdentifierPresent
                                ? locale === "es"
                                  ? "Deje vacío para conservar el existente"
                                  : "Leave blank to keep existing"
                                : "123-45-6789"
                            }
                            required={!selectedWorker.taxpayerIdentifierPresent}
                            value={taxProfileForm.taxpayerId}
                            onChange={(e) =>
                              setTaxProfileForm({
                                ...taxProfileForm,
                                taxpayerId: e.currentTarget.value,
                              })
                            }
                          />
                        </label>
                      </div>
                      <div className="form-actions">
                        <button className="primary" disabled={busy}>
                          {locale === "es"
                            ? "Guardar información"
                            : "Save information"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setTaxProfileForm(null)}
                        >
                          {locale === "es" ? "Cancelar" : "Cancel"}
                        </button>
                      </div>
                    </form>
                  )}
                {selectedWorker.workerType !== "professional_services" &&
                  canManage && (
                    <button
                      type="button"
                      onClick={() => openPersonnelSetup("tax", selectedWorker)}
                    >
                      {locale === "es"
                        ? "Ingresar 499 R-4 firmado en papel"
                        : "Enter signed paper 499 R-4"}
                    </button>
                  )}
                {selectedWorker.workerType !== "professional_services" && (
                  <div className="profile-history">
                    <h3>
                      {locale === "es"
                        ? "Deducciones vigentes e históricas"
                        : "Current and historical deductions"}
                    </h3>
                    <ResponsiveDataTable
                      label={
                        locale === "es"
                          ? "Tabla de deducciones de la persona"
                          : "Worker deductions table"
                      }
                      caption={
                        locale === "es"
                          ? "Descripción, tipo, cálculo, balance, prioridad, vigencia y estado"
                          : "Description, type, calculation, balance, priority, effective range, and status"
                      }
                      columns={
                        locale === "es"
                          ? [
                              "Deducción",
                              "Cálculo y balance",
                              "Prioridad",
                              "Vigencia",
                              "Estado",
                            ]
                          : [
                              "Deduction",
                              "Calculation and balance",
                              "Priority",
                              "Effective range",
                              "Status",
                            ]
                      }
                      rows={(profileWorkspace?.deductions ?? []).map(
                        (item) => ({
                          key: item.id,
                          cells: [
                            <>
                              <strong>{item.description}</strong>
                              <small>
                                {optionLabel(item.deductionType, locale)}
                              </small>
                            </>,
                            <>
                              <span>
                                {item.calculationType === "fixed"
                                  ? money(item.amountCents ?? 0)
                                  : `${(item.rateBps ?? 0) / 100}%`}
                              </span>
                              <small>
                                {locale === "es" ? "Balance" : "Balance"}:{" "}
                                {item.currentBalanceCents === null
                                  ? "—"
                                  : money(item.currentBalanceCents)}
                              </small>
                            </>,
                            item.priority,
                            formatDateRange(
                              item.effectiveFrom,
                              item.effectiveTo,
                              locale,
                              locale === "es" ? "Vigente" : "Current",
                            ),
                            <span className={`run-status ${item.status}`}>
                              {optionLabel(item.status, locale)}
                            </span>,
                          ],
                          card: (
                            <article>
                              <div>
                                <strong>{item.description}</strong>
                                <small>
                                  {optionLabel(item.deductionType, locale)} ·{" "}
                                  {formatDateRange(
                                    item.effectiveFrom,
                                    item.effectiveTo,
                                    locale,
                                    locale === "es" ? "Vigente" : "Current",
                                  )}
                                </small>
                              </div>
                              <p>
                                {item.calculationType === "fixed"
                                  ? money(item.amountCents ?? 0)
                                  : `${(item.rateBps ?? 0) / 100}%`}{" "}
                                · {locale === "es" ? "Balance" : "Balance"}{" "}
                                {item.currentBalanceCents === null
                                  ? "—"
                                  : money(item.currentBalanceCents)}{" "}
                                · {locale === "es" ? "Prioridad" : "Priority"}{" "}
                                {item.priority}
                              </p>
                              <span className={`run-status ${item.status}`}>
                                {optionLabel(item.status, locale)}
                              </span>
                            </article>
                          ),
                        }),
                      )}
                      empty={
                        locale === "es"
                          ? "No hay deducciones configuradas para esta persona."
                          : "No deductions are configured for this worker."
                      }
                    />
                  </div>
                )}
              </>
            )}
          {profileTab === "pay" && (
            <>
              <div className="contextual-heading">
                <div>
                  <p className="kicker">{locale === "es" ? "PAGO" : "PAY"}</p>
                  <h2>
                    {selectedWorker.workerType === "professional_services"
                      ? locale === "es"
                        ? "Pagos por servicios"
                        : "Service payments"
                      : locale === "es"
                        ? "Nómina y comprobantes"
                        : "Payroll and pay statements"}
                  </h2>
                </div>
                <button type="button" onClick={() => navigate("payroll")}>
                  {locale === "es"
                    ? "Abrir historial de pago"
                    : "Open pay history"}
                </button>
              </div>
              <dl className="profile-facts">
                <div>
                  <dt>
                    {locale === "es"
                      ? "Pagos finalizados este año"
                      : "Finalized payments this year"}
                  </dt>
                  <dd>{profileWorkspace?.pay?.paymentCount ?? 0}</dd>
                </div>
                <div>
                  <dt>
                    {locale === "es" ? "Bruto del año" : "Year-to-date gross"}
                  </dt>
                  <dd>{money(profileWorkspace?.pay?.ytdGrossCents)}</dd>
                </div>
                <div>
                  <dt>
                    {selectedWorker.workerType === "professional_services"
                      ? locale === "es"
                        ? "Retenido en el año"
                        : "Year-to-date withholding"
                      : locale === "es"
                        ? "Contribuciones del año"
                        : "Year-to-date taxes"}
                  </dt>
                  <dd>
                    {money(
                      profileWorkspace?.pay?.kind === "professional_services"
                        ? profileWorkspace.pay.ytdWithholdingCents
                        : profileWorkspace?.pay?.ytdTaxCents,
                    )}
                  </dd>
                </div>
                {profileWorkspace?.pay?.kind === "employee" && (
                  <div>
                    <dt>
                      {locale === "es"
                        ? "Deducciones del año"
                        : "Year-to-date deductions"}
                    </dt>
                    <dd>{money(profileWorkspace.pay.ytdDeductionCents)}</dd>
                  </div>
                )}
                <div>
                  <dt>
                    {locale === "es" ? "Neto del año" : "Year-to-date net"}
                  </dt>
                  <dd>{money(profileWorkspace?.pay?.ytdNetCents)}</dd>
                </div>
                <div>
                  <dt>
                    {locale === "es" ? "Pago más reciente" : "Latest payment"}
                  </dt>
                  <dd>
                    {formatDate(profileWorkspace?.pay?.latestPayDate, locale)}
                  </dd>
                </div>
              </dl>
              {selectedWorker.workerType === "employee" && canManage && (
                <section className="operation-panel">
                  <div className="section-heading">
                    <div>
                      <h3>
                        {locale === "es"
                          ? "Acumulados anteriores al sistema"
                          : "Totals before system use"}
                      </h3>
                      <p>
                        {profileWorkspace?.pay?.openingBalance
                          ? locale === "es"
                            ? `Registrados hasta ${formatDate(profileWorkspace.pay.openingBalance.asOfDate, locale)}. Se incluyen en topes contributivos y totales del año.`
                            : `Recorded through ${formatDate(profileWorkspace.pay.openingBalance.asOfDate, locale)}. Included in wage bases and year-to-date totals.`
                          : locale === "es"
                            ? "Todavía no se han registrado. Añádalos cuando tenga los totales oficiales del año."
                            : "None recorded yet. Add them when official year-to-date totals are available."}
                      </p>
                    </div>
                    {!ytdOpeningForm && (
                      <button
                        type="button"
                        onClick={() => openYtdOpening(selectedWorker)}
                      >
                        {profileWorkspace?.pay?.openingBalance
                          ? locale === "es"
                            ? "Editar acumulados"
                            : "Edit totals"
                          : locale === "es"
                            ? "Registrar acumulados"
                            : "Record totals"}
                      </button>
                    )}
                  </div>
                  {ytdOpeningForm?.workerId === selectedWorker.id && (
                    <form onSubmit={saveYtdOpening}>
                      <p className="field-note">
                        {locale === "es"
                          ? "Use el último talonario anterior al primer período creado aquí. Las bases sujetas permiten controlar correctamente los topes de SINOT, FUTA y desempleo."
                          : "Use the last pay statement before the first period created here. Subject wage bases correctly control SINOT, FUTA, and unemployment limits."}
                      </p>
                      <div className="form-grid">
                        <label>
                          {locale === "es" ? "Año contributivo" : "Tax year"}
                          <input
                            required
                            type="number"
                            min="2000"
                            max="2100"
                            value={ytdOpeningForm.taxYear}
                            onChange={(e) =>
                              setYtdOpeningForm({
                                ...ytdOpeningForm,
                                taxYear: e.currentTarget.value,
                              })
                            }
                          />
                        </label>
                        <label>
                          {locale === "es"
                            ? "Acumulado hasta"
                            : "Totals through"}
                          <input
                            required
                            type="date"
                            value={ytdOpeningForm.asOfDate}
                            onInput={(e) =>
                              setYtdOpeningForm({
                                ...ytdOpeningForm,
                                asOfDate: e.currentTarget.value,
                              })
                            }
                          />
                        </label>
                        {[
                          ["grossWages", "Salario bruto", "Gross wages"],
                          [
                            "nontaxablePay",
                            "Pagos no tributables",
                            "Nontaxable pay",
                          ],
                          [
                            "prTaxableWages",
                            "Salario sujeto a retención PR",
                            "PR taxable wages",
                          ],
                          [
                            "ficaWages",
                            "Salario sujeto a Seguro Social/Medicare",
                            "Social Security/Medicare wages",
                          ],
                          [
                            "sinotWages",
                            "Salario sujeto a SINOT",
                            "SINOT wages",
                          ],
                          ["futaWages", "Salario sujeto a FUTA", "FUTA wages"],
                          [
                            "unemploymentWages",
                            "Salario sujeto a desempleo PR",
                            "PR unemployment wages",
                          ],
                          [
                            "prWithholding",
                            "Retención de PR",
                            "PR withholding",
                          ],
                          [
                            "socialSecurityEmployee",
                            "Seguro Social retenido",
                            "Social Security withheld",
                          ],
                          [
                            "medicareEmployee",
                            "Medicare retenido",
                            "Medicare withheld",
                          ],
                          ["sinotEmployee", "SINOT retenido", "SINOT withheld"],
                          [
                            "otherDeductions",
                            "Otras deducciones",
                            "Other deductions",
                          ],
                          ["netPay", "Pago neto", "Net pay"],
                        ].map(([key, es, en]) => (
                          <label key={key}>
                            {locale === "es" ? es : en}
                            <input
                              required
                              type="number"
                              min="0"
                              step="0.01"
                              value={(ytdOpeningForm as any)[key]}
                              onChange={(e) =>
                                setYtdOpeningForm({
                                  ...ytdOpeningForm,
                                  [key]: e.currentTarget.value,
                                })
                              }
                            />
                          </label>
                        ))}
                        <label>
                          {locale === "es"
                            ? "Fuente o explicación"
                            : "Source or explanation"}
                          <textarea
                            required
                            minLength={3}
                            value={ytdOpeningForm.sourceNote}
                            onChange={(e) =>
                              setYtdOpeningForm({
                                ...ytdOpeningForm,
                                sourceNote: e.currentTarget.value,
                              })
                            }
                          />
                        </label>
                      </div>
                      <div className="form-actions">
                        <button className="primary" disabled={busy}>
                          {locale === "es"
                            ? "Guardar acumulados"
                            : "Save totals"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setYtdOpeningForm(null)}
                        >
                          {locale === "es" ? "Cancelar" : "Cancel"}
                        </button>
                      </div>
                    </form>
                  )}
                </section>
              )}
            </>
          )}
          {profileTab === "documents" && (
            <>
              <div className="contextual-heading">
                <div>
                  <p className="kicker">
                    {locale === "es" ? "DOCUMENTOS" : "DOCUMENTS"}
                  </p>
                  <h2>
                    {locale === "es"
                      ? "Expediente documental unificado"
                      : "Unified document record"}
                  </h2>
                </div>
              </div>
              <dl className="profile-facts">
                <div>
                  <dt>
                    {locale === "es"
                      ? "Documentos de personal activos"
                      : "Active personnel documents"}
                  </dt>
                  <dd>
                    {
                      personnelDocuments.filter(
                        (item) => item.status === "active",
                      ).length
                    }
                  </dd>
                </div>
                <div>
                  <dt>
                    {locale === "es"
                      ? "Comprobantes y documentos generados"
                      : "Generated statements and documents"}
                  </dt>
                  <dd>{profileWorkspace?.documents?.activeCount ?? 0}</dd>
                </div>
                <div>
                  <dt>
                    {locale === "es" ? "Total generado" : "Generated total"}
                  </dt>
                  <dd>{profileWorkspace?.documents?.totalCount ?? 0}</dd>
                </div>
              </dl>
              {canManage && (
                <section className="operation-panel">
                  <h3>
                    {locale === "es"
                      ? "Documentos de personal"
                      : "Personnel documents"}
                  </h3>
                  <div className="form-grid">
                    <label>
                      {locale === "es" ? "Categoría" : "Category"}
                      <select
                        value={personnelDocumentForm.category}
                        onChange={(e) =>
                          setPersonnelDocumentForm({
                            ...personnelDocumentForm,
                            category: e.currentTarget.value,
                          })
                        }
                      >
                        {[
                          "employment",
                          "identity",
                          "tax",
                          "benefit",
                          "policy",
                          "performance",
                          "discipline",
                          "medical",
                          "other",
                        ].map((value) => (
                          <option key={value} value={value}>
                            {optionLabel(value, locale)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      {locale === "es" ? "Título" : "Title"}
                      <input
                        required
                        minLength={2}
                        value={personnelDocumentForm.title}
                        onChange={(e) =>
                          setPersonnelDocumentForm({
                            ...personnelDocumentForm,
                            title: e.currentTarget.value,
                          })
                        }
                      />
                    </label>
                    <label>
                      {locale === "es" ? "Visibilidad" : "Visibility"}
                      <select
                        value={personnelDocumentForm.visibility}
                        onChange={(e) =>
                          setPersonnelDocumentForm({
                            ...personnelDocumentForm,
                            visibility: e.currentTarget.value,
                          })
                        }
                      >
                        <option value="employee_visible">
                          {locale === "es"
                            ? "Visible al empleado"
                            : "Employee visible"}
                        </option>
                        <option value="management_only">
                          {locale === "es"
                            ? "Solo gerencia"
                            : "Management only"}
                        </option>
                      </select>
                    </label>
                    <label>
                      {locale === "es" ? "Vigente desde" : "Effective on"}
                      <input
                        type="date"
                        value={personnelDocumentForm.effectiveOn}
                        onInput={(e) =>
                          setPersonnelDocumentForm({
                            ...personnelDocumentForm,
                            effectiveOn: e.currentTarget.value,
                          })
                        }
                      />
                    </label>
                    <label>
                      {locale === "es" ? "Archivo privado" : "Private file"}
                      <input
                        type="file"
                        accept="application/pdf,image/png,image/jpeg,image/webp,text/plain"
                        onChange={(e) =>
                          setPersonnelDocumentFile(
                            e.currentTarget.files?.[0] ?? null,
                          )
                        }
                      />
                    </label>
                  </div>
                  <button
                    type="button"
                    disabled={
                      busy ||
                      !personnelDocumentFile ||
                      personnelDocumentForm.title.trim().length < 2
                    }
                    onClick={() => void uploadPersonnelDocument(selectedWorker)}
                  >
                    {locale === "es" ? "Guardar documento" : "Save document"}
                  </button>
                  <div className="run-list">
                    {personnelDocuments.map((item) => (
                      <article key={item.id}>
                        <div>
                          <strong>{item.title}</strong>
                          <p>
                            {optionLabel(item.category, locale)} ·{" "}
                            {optionLabel(item.visibility, locale)} ·{" "}
                            {formatDate(item.effectiveOn, locale)}
                          </p>
                          <small>
                            {item.fileName} · {Math.ceil(item.byteSize / 1024)}{" "}
                            KB
                          </small>
                        </div>
                        <span className={`run-status ${item.status}`}>
                          {optionLabel(item.status, locale)}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            void downloadPersonnelDocument(selectedWorker, item)
                          }
                        >
                          {locale === "es" ? "Descargar" : "Download"}
                        </button>
                        {item.status === "active" && (
                          <button
                            type="button"
                            onClick={() =>
                              void archivePersonnelDocument(
                                selectedWorker,
                                item,
                              )
                            }
                          >
                            {locale === "es" ? "Archivar" : "Archive"}
                          </button>
                        )}
                      </article>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
          {profileTab === "access" && (
            <>
              <div className="contextual-heading">
                <div>
                  <p className="kicker">
                    {locale === "es"
                      ? "ACCESO Y SEGURIDAD"
                      : "ACCESS AND SECURITY"}
                  </p>
                  <h2>
                    {locale === "es"
                      ? "Cuenta de la aplicación"
                      : "Application account"}
                  </h2>
                </div>
              </div>
              <dl className="profile-facts">
                <div>
                  <dt>{locale === "es" ? "Registro" : "Registration"}</dt>
                  <dd>
                    {selectedMembership
                      ? optionLabel(selectedMembership.status, locale)
                      : locale === "es"
                        ? "Sin invitar"
                        : "Not invited"}
                  </dd>
                </div>
                <div>
                  <dt>
                    {locale === "es" ? "Número de acceso" : "Login number"}
                  </dt>
                  <dd>
                    {selectedMembership?.maskedPhone ??
                      selectedWorker.phoneE164 ??
                      "—"}
                  </dd>
                </div>
                <div>
                  <dt>{locale === "es" ? "Rol" : "Role"}</dt>
                  <dd>
                    {optionLabel(
                      selectedMembership?.roleKey ?? selectedWorker.roleKey,
                      locale,
                    )}
                  </dd>
                </div>
                <div>
                  <dt>{locale === "es" ? "Última sesión" : "Last session"}</dt>
                  <dd>{selectedMembership?.lastSessionAt ?? "—"}</dd>
                </div>
                <div>
                  <dt>
                    {locale === "es" ? "Teléfono verificado" : "Phone verified"}
                  </dt>
                  <dd>{selectedMembership?.phoneVerifiedAt ?? "—"}</dd>
                </div>
                <div>
                  <dt>{locale === "es" ? "Cuenta" : "Account"}</dt>
                  <dd>
                    {selectedMembership
                      ? optionLabel(selectedMembership.accountStatus, locale)
                      : locale === "es"
                        ? "No creada"
                        : "Not created"}
                  </dd>
                </div>
              </dl>
            </>
          )}
        </section>
      )}
      {adding && (
        <form className="settings-form personnel-form" onSubmit={submit}>
          <h2>
            {editingId
              ? locale === "es"
                ? "Editar persona"
                : "Edit person"
              : locale === "es"
                ? "Nueva persona"
                : "New person"}
          </h2>
          <div className="form-grid">
            <label>
              {locale === "es" ? "Nombre" : "First name"}
              <input
                required
                value={form.firstName}
                onChange={(e) => field("firstName", e.currentTarget.value)}
              />
            </label>
            <label>
              {locale === "es" ? "Segundo nombre (opcional)" : "Middle name (optional)"}
              <input
                value={form.middleName ?? ""}
                onChange={(e) => field("middleName", e.currentTarget.value)}
              />
            </label>
            <label>
              {locale === "es" ? "Apellidos" : "Last name"}
              <input
                value={form.lastName ?? ""}
                onChange={(e) => field("lastName", e.currentTarget.value)}
              />
            </label>
            <label>
              {locale === "es" ? "Nombre preferido (opcional)" : "Preferred name (optional)"}
              <input
                value={form.preferredName ?? ""}
                onChange={(e) => field("preferredName", e.currentTarget.value)}
              />
            </label>
            <label>
              {locale === "es" ? "Fecha de nacimiento" : "Date of birth"}
              <input
                disabled
                type="date"
                value={form.dateOfBirth ?? ""}
                onChange={(e) => field("dateOfBirth", e.currentTarget.value)}
              />
            </label>
            <fieldset className="form-fieldset form-grid-span">
              <legend>
                {locale === "es"
                  ? "Dirección residencial (obligatoria)"
                  : "Residential address (required)"}
              </legend>
              <div className="form-grid">
                <label>
                  {locale === "es" ? "Dirección" : "Address"}
                  <input
                    required
                    value={form.mailingAddress?.line1 ?? ""}
                    onChange={(e) =>
                      personnelAddressField("line1", e.currentTarget.value)
                    }
                  />
                </label>
                <label>
                  {locale === "es"
                    ? "Dirección 2 (opcional)"
                    : "Address line 2 (optional)"}
                  <input
                    value={form.mailingAddress?.line2 ?? ""}
                    onChange={(e) =>
                      personnelAddressField("line2", e.currentTarget.value)
                    }
                  />
                </label>
                <label>
                  {locale === "es" ? "Ciudad" : "City"}
                  <input
                    required
                    value={form.mailingAddress?.city ?? ""}
                    onChange={(e) =>
                      personnelAddressField("city", e.currentTarget.value)
                    }
                  />
                </label>
                <label>
                  {locale === "es" ? "Estado/territorio" : "State/territory"}
                  <input
                    required
                    value={form.mailingAddress?.state ?? "PR"}
                    onChange={(e) =>
                      personnelAddressField("state", e.currentTarget.value)
                    }
                  />
                </label>
                <label>
                  {locale === "es" ? "Código postal" : "Postal code"}
                  <input
                    required
                    value={form.mailingAddress?.postalCode ?? ""}
                    onChange={(e) =>
                      personnelAddressField("postalCode", e.currentTarget.value)
                    }
                  />
                </label>
              </div>
            </fieldset>
            <label>
              {locale === "es" ? "Tipo de trabajador" : "Worker type"}
              <select
                required
                value={form.workerType}
                onChange={(e) => field("workerType", e.currentTarget.value)}
              >
                <option value="employee">
                  {optionLabel("employee", locale)}
                </option>
                <option value="professional_services">
                  {optionLabel("professional_services", locale)}
                </option>
                <option value="pending_classification">
                  {optionLabel("pending_classification", locale)}
                </option>
              </select>
            </label>
            <div className="field-note form-grid-span" role="status">
              <strong>
                {locale === "es"
                  ? "Estado administrado por el sistema"
                  : "System-managed status"}
              </strong>
              <p>
                {editingId
                  ? locale === "es"
                    ? `Estado actual: ${optionLabel(form.status, locale)}. Para terminar o reactivar, use la acción correspondiente del expediente.`
                    : `Current status: ${optionLabel(form.status, locale)}. Use the corresponding record action to terminate or reactivate.`
                  : locale === "es"
                    ? "Se determinará automáticamente según la información completada."
                    : "It will be determined automatically from the completed information."}
              </p>
            </div>
            <label>
              {locale === "es" ? "Rol" : "Role"}
              <select
                required
                value={form.roleKey}
                onChange={(e) => field("roleKey", e.currentTarget.value)}
              >
                <option value="user">{optionLabel("user", locale)}</option>
                <option value="supervisor">
                  {optionLabel("supervisor", locale)}
                </option>
                {(["owner", "platform_administrator"].includes(managerRole) ||
                  form.roleKey === "administrator") && (
                  <option
                    value="administrator"
                    disabled={
                      !["owner", "platform_administrator"].includes(managerRole)
                    }
                  >
                    {optionLabel("administrator", locale)}
                  </option>
                )}
                {form.roleKey === "owner" && (
                  <option value="owner" disabled>
                    {optionLabel("owner", locale)}
                  </option>
                )}
              </select>
            </label>
            <label>
              {locale === "es" ? "Puesto/título (opcional al crear)" : "Position/title (optional at creation)"}
              <input
                value={form.positionTitle ?? ""}
                onChange={(e) => field("positionTitle", e.currentTarget.value)}
              />
              <small>{locale === "es" ? "Se necesitará antes de completar la configuración laboral." : "It will be needed before employment setup is complete."}</small>
            </label>
            <label>
              {locale === "es"
                ? "Teléfono de acceso (opcional al crear)"
                : "Login phone (optional at creation)"}
              <input
                placeholder="+17875551234"
                value={form.phoneE164 ?? ""}
                onChange={(e) => field("phoneE164", e.currentTarget.value)}
              />
              <small>{locale === "es" ? "Añádalo cuando la persona vaya a recibir acceso a la aplicación." : "Add it when the person will receive application access."}</small>
            </label>
            <label>
              {locale === "es" ? "Teléfono secundario (opcional)" : "Secondary phone (optional)"}
              <input
                placeholder="+17875551234"
                value={form.secondaryPhoneE164 ?? ""}
                onChange={(e) =>
                  field("secondaryPhoneE164", e.currentTarget.value)
                }
              />
            </label>
            <label>
              {locale === "es" ? "Email (opcional)" : "Email (optional)"}
              <input
                type="email"
                value={form.email ?? ""}
                onChange={(e) => field("email", e.currentTarget.value)}
              />
            </label>
            <label>
              {locale === "es" ? "Fecha de inicio (opcional al crear)" : "Start date (optional at creation)"}
              <input
                disabled={Boolean(
                  editingId &&
                    workers.find((worker) => worker.id === editingId)
                      ?.startDate,
                )}
                type="date"
                value={form.startDate ?? ""}
                onInput={(e) =>
                  field("startDate", (e.target as HTMLInputElement).value)
                }
                onChange={(e) => field("startDate", e.currentTarget.value)}
              />
              {editingId &&
                !workers.find((worker) => worker.id === editingId)
                  ?.startDate && (
                  <small>
                    {locale === "es"
                      ? "Registre una sola vez la fecha inicial real. Después se conserva como historial laboral."
                      : "Record the actual initial date once. It is then retained as employment history."}
                  </small>
                )}
            </label>
            <label>
              {locale === "es" ? "Fecha de terminación" : "End date"}
              <input
                disabled
                type="date"
                value={form.endDate ?? ""}
                onInput={(e) => field("endDate", e.currentTarget.value)}
              />
            </label>
            <label>
              {locale === "es" ? "Número de trabajador" : "Worker number"}
              <input
                value={form.workerNumber ?? ""}
                onChange={(e) => field("workerNumber", e.currentTarget.value)}
              />
              <small>
                {locale === "es"
                  ? "Opcional. Si queda vacío, el sistema asignará uno automáticamente."
                  : "Optional. If left blank, the system assigns one automatically."}
              </small>
            </label>
            <label>
              {locale === "es" ? "Método de tiempo" : "Time-entry method"}
              <select
                required
                value={form.timeEntryMethod}
                onChange={(e) =>
                  field("timeEntryMethod", e.currentTarget.value)
                }
              >
                <option value="clock">{optionLabel("clock", locale)}</option>
                <option value="weekly_timesheet">
                  {optionLabel("weekly_timesheet", locale)}
                </option>
                <option value="administrator_entry">
                  {optionLabel("administrator_entry", locale)}
                </option>
                <option value="none">
                  {locale === "es"
                    ? "Sin registro — requiere política gobernada"
                    : "No tracking — governed policy required"}
                </option>
              </select>
            </label>
            <label>
              {locale === "es" ? "Modo de horario" : "Schedule mode"}
              <select
                required
                value={form.scheduleMode}
                onChange={(e) => field("scheduleMode", e.currentTarget.value)}
              >
                <option value="fixed">{optionLabel("fixed", locale)}</option>
                <option value="flexible">
                  {optionLabel("flexible", locale)}
                </option>
                <option value="as_needed">
                  {optionLabel("as_needed", locale)}
                </option>
                <option value="field_mobile">
                  {optionLabel("field_mobile", locale)}
                </option>
                <option value="project_service">
                  {optionLabel("project_service", locale)}
                </option>
              </select>
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={form.tipEligible}
                onChange={(e) => field("tipEligible", e.currentTarget.checked)}
              />
              {locale === "es"
                ? "Esta persona cualifica para recibir propinas"
                : "This person is eligible to receive tips"}
            </label>
            <label>
              {locale === "es" ? "Notas (opcional)" : "Notes (optional)"}
              <input
                value={form.notes ?? ""}
                onChange={(e) => field("notes", e.currentTarget.value)}
              />
            </label>
          </div>
          {error && (
            <p className="form-error">
              {personnelError ||
                (locale === "es"
                  ? "No se pudo guardar el expediente. Revise los datos e inténtelo nuevamente."
                  : "The record could not be saved. Review the data and try again.")}
            </p>
          )}
          <button className="primary" disabled={busy}>
            {busy ? <LoaderCircle className="spin" /> : <Check />}
            {locale === "es" ? "Guardar persona" : "Save person"}
          </button>
        </form>
      )}
      {busy && !adding ? (
        <LoaderCircle className="spin" />
      ) : workers.length === 0 && !adding ? (
        <section className="empty-module">
          <span>
            <Users />
          </span>
          <h2>
            {locale === "es" ? "No hay personas todavía" : "No people yet"}
          </h2>
          <p>
            {locale === "es"
              ? "Añada la primera persona con solo el nombre si eso es todo lo disponible."
              : "Add the first person with only a name if that is all that is available."}
          </p>
        </section>
      ) : !selectedWorker && !adding ? (
        <div className="worker-grid">
          {visibleWorkers.map((worker) => (
            <article className="worker-card" key={worker.id}>
              <div className="worker-avatar">
                {(worker.preferredName || worker.firstName)[0]}
              </div>
              <div>
                <h2>
                  {worker.preferredName || worker.firstName} {worker.lastName}
                </h2>
                <p>
                  {worker.positionTitle ||
                    optionLabel(worker.workerType, locale)}
                </p>
                <span>
                  {optionLabel(worker.roleKey, locale)} ·{" "}
                  {optionLabel(worker.status, locale)}
                </span>
                {!selectedWorker && (
                  <button
                    type="button"
                    className="profile-open"
                    onClick={() => openWorker(worker)}
                  >
                    {locale === "es" ? "Abrir expediente" : "Open record"}
                    <ChevronRight />
                  </button>
                )}
                {canManage &&
                  !memberships.some((item) => item.workerId === worker.id) && (
                    <button
                      type="button"
                      disabled={
                        !worker.phoneE164 ||
                        ["inactive", "terminated"].includes(worker.status)
                      }
                      title={
                        !worker.phoneE164
                          ? locale === "es"
                            ? "Complete el teléfono móvil antes de invitar"
                            : "Complete the mobile phone before inviting"
                          : undefined
                      }
                      onClick={() => void invite(worker)}
                    >
                      {locale === "es"
                        ? "Invitar a la aplicación"
                        : "Invite to app"}
                    </button>
                  )}
                {managerRole !== "supervisor" &&
                  worker.taxOnboarding.applicable &&
                  !(
                    worker.taxOnboarding.profilePrerequisitesComplete &&
                    worker.taxOnboarding.status === "certified"
                  ) && (
                    <div className="operation-panel tax-onboarding-summary">
                      <strong>
                        {locale === "es"
                          ? "Información contributiva"
                          : "Tax information"}
                      </strong>
                      <dl>
                        <div>
                          <dt>
                            {locale === "es"
                              ? "Perfil e identificación"
                              : "Profile and taxpayer ID"}
                          </dt>
                          <dd>
                            {worker.taxOnboarding
                              .profilePrerequisitesComplete ? (
                              <span className="run-status approved">
                                {locale === "es" ? "Completo" : "Complete"}
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => openTaxProfile(worker)}
                              >
                                {locale === "es"
                                  ? "Actualizar información incompleta"
                                  : "Update incomplete information"}
                              </button>
                            )}
                          </dd>
                        </div>
                        <div>
                          <dt>499 R-4</dt>
                          <dd>
                            {worker.taxOnboarding.status === "certified"
                              ? locale === "es"
                                ? `Certificado · vigente desde ${formatDate(worker.taxOnboarding.effectiveFrom, locale)}`
                                : `Certified · effective ${formatDate(worker.taxOnboarding.effectiveFrom, locale)}`
                              : worker.taxOnboarding.status === "draft"
                                ? locale === "es"
                                  ? "Borrador guardado · falta certificar"
                                  : "Draft saved · certification required"
                                : locale === "es"
                                  ? "No iniciado"
                                  : "Not started"}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  )}
                {canManage &&
                  (() => {
                    const membership = memberships.find(
                      (item) => item.workerId === worker.id,
                    );
                    const canGovernMembership = membership
                      ? ["owner", "platform_administrator"].includes(
                          managerRole,
                        ) ||
                        !["administrator", "owner"].includes(membership.roleKey)
                      : false;
                    return membership ? (
                      <div>
                        <small>
                          {locale === "es" ? "Cuenta" : "Account"}:{" "}
                          {optionLabel(membership.status, locale)} ·{" "}
                          {membership.maskedPhone}
                        </small>
                        {membership.status === "invited" &&
                          canGovernMembership && (
                            <>
                              <small>
                                {locale === "es" ? "SMS" : "SMS"}:{" "}
                                {membership.invitationDeliveryStatus ??
                                  "queued"}{" "}
                                · {locale === "es" ? "expira" : "expires"}{" "}
                                {membership.invitationExpiresAt ?? "—"}
                              </small>
                              <button
                                type="button"
                                onClick={() =>
                                  void resendInvitation(membership)
                                }
                              >
                                {locale === "es"
                                  ? "Reenviar invitación"
                                  : "Resend invitation"}
                              </button>
                            </>
                          )}
                        {membership.status === "pending_approval" &&
                          canGovernMembership && (
                            <button
                              type="button"
                              onClick={() => void approve(membership)}
                            >
                              {locale === "es"
                                ? "Aprobar acceso"
                                : "Approve access"}
                            </button>
                          )}
                        {membership.status === "active" &&
                          canGovernMembership && (
                            <button
                              type="button"
                              onClick={() => void revokeMembership(membership)}
                            >
                              {locale === "es"
                                ? "Revocar acceso"
                                : "Revoke access"}
                            </button>
                          )}
                        {managerRole === "owner" &&
                          membership.status === "active" &&
                          membership.roleKey === "administrator" && (
                            <button
                              type="button"
                              onClick={() => void initiateTransfer(membership)}
                            >
                              {locale === "es"
                                ? "Proponer como propietario"
                                : "Propose as owner"}
                            </button>
                          )}
                      </div>
                    ) : null;
                  })()}
              </div>
              <strong>
                {worker.profileCompletionPercent}%
                <small>{locale === "es" ? "perfil" : "profile"}</small>
              </strong>
              {paymentBlockingItems(worker).length > 0 && (
                <div className="worker-readiness">
                  <strong>
                    {locale === "es" ? "Falta completar" : "Still required"}
                  </strong>
                  <ul>
                    {paymentBlockingItems(worker).map((item) => (
                      <li key={item.code}>
                        <button
                          type="button"
                          onClick={() => openReadinessItem(item, worker)}
                        >
                          {readinessLabels[item.code]?.[
                            locale === "es" ? "es" : "en"
                          ] ?? item.code}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {(employmentHistory[worker.id]?.length ?? 0) > 0 && (
                <div className="operation-panel">
                  {(employmentHistory[worker.id] ?? []).map((period) => (
                    <div key={period.id}>
                      <strong>
                        {formatDateRange(
                          period.startDate,
                          period.endDate,
                          locale,
                          locale === "es" ? "Vigente" : "Current",
                        )}
                      </strong>
                      <small>
                        {period.terminationReason ??
                          (locale === "es"
                            ? "Período activo"
                            : "Active period")}{" "}
                        · {locale === "es" ? "pago final" : "final pay"}:{" "}
                        {period.finalPayStatus}
                        {period.finalPayDueOn
                          ? ` (${formatDate(period.finalPayDueOn, locale)})`
                          : ""}{" "}
                        · {period.leaveDisposition ?? "—"}
                      </small>
                    </div>
                  ))}
                </div>
              )}
              {canManage && documentWorkerId === worker.id && (
                <section className="operation-panel">
                  <h3>
                    {locale === "es"
                      ? "Expediente documental"
                      : "Document record"}
                  </h3>
                  <div className="form-grid">
                    <label>
                      {locale === "es" ? "Categoría" : "Category"}
                      <select
                        value={personnelDocumentForm.category}
                        onChange={(e) =>
                          setPersonnelDocumentForm({
                            ...personnelDocumentForm,
                            category: e.currentTarget.value,
                          })
                        }
                      >
                        {[
                          "employment",
                          "identity",
                          "tax",
                          "benefit",
                          "policy",
                          "performance",
                          "discipline",
                          "medical",
                          "other",
                        ].map((value) => (
                          <option key={value} value={value}>
                            {optionLabel(value, locale)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      {locale === "es" ? "Título" : "Title"}
                      <input
                        minLength={2}
                        maxLength={200}
                        required
                        value={personnelDocumentForm.title}
                        onChange={(e) =>
                          setPersonnelDocumentForm({
                            ...personnelDocumentForm,
                            title: e.currentTarget.value,
                          })
                        }
                      />
                    </label>
                    <label>
                      {locale === "es" ? "Visibilidad" : "Visibility"}
                      <select
                        value={personnelDocumentForm.visibility}
                        onChange={(e) =>
                          setPersonnelDocumentForm({
                            ...personnelDocumentForm,
                            visibility: e.currentTarget.value,
                          })
                        }
                      >
                        <option value="employee_visible">
                          {locale === "es"
                            ? "Visible al empleado"
                            : "Employee visible"}
                        </option>
                        <option value="management_only">
                          {locale === "es"
                            ? "Solo gerencia"
                            : "Management only"}
                        </option>
                      </select>
                    </label>
                    <label>
                      {locale === "es" ? "Vigente desde" : "Effective on"}
                      <input
                        type="date"
                        value={personnelDocumentForm.effectiveOn}
                        onInput={(e) =>
                          setPersonnelDocumentForm({
                            ...personnelDocumentForm,
                            effectiveOn: e.currentTarget.value,
                          })
                        }
                      />
                    </label>
                    <label>
                      {locale === "es" ? "Expira" : "Expires"}
                      <input
                        type="date"
                        value={personnelDocumentForm.expiresOn}
                        onInput={(e) =>
                          setPersonnelDocumentForm({
                            ...personnelDocumentForm,
                            expiresOn: e.currentTarget.value,
                          })
                        }
                      />
                    </label>
                    <label>
                      {locale === "es" ? "Archivo privado" : "Private file"}
                      <input
                        type="file"
                        accept="application/pdf,image/png,image/jpeg,image/webp,text/plain"
                        onChange={(e) =>
                          setPersonnelDocumentFile(
                            e.currentTarget.files?.[0] ?? null,
                          )
                        }
                      />
                    </label>
                  </div>
                  <button
                    type="button"
                    disabled={
                      busy ||
                      !personnelDocumentFile ||
                      personnelDocumentForm.title.trim().length < 2
                    }
                    onClick={() => void uploadPersonnelDocument(worker)}
                  >
                    {locale === "es" ? "Guardar documento" : "Save document"}
                  </button>
                  <div className="run-list">
                    {personnelDocuments.map((item) => (
                      <article key={item.id}>
                        <div>
                          <strong>{item.title}</strong>
                          <p>
                            {optionLabel(item.category, locale)} ·{" "}
                            {optionLabel(item.visibility, locale)} ·{" "}
                            {formatDate(item.effectiveOn, locale)}
                            {item.expiresOn
                              ? ` — ${formatDate(item.expiresOn, locale)}`
                              : ""}
                          </p>
                          <small>
                            {item.fileName} · {Math.ceil(item.byteSize / 1024)}{" "}
                            KB · SHA-256 {item.contentSha256.slice(0, 12)}…
                            {item.archiveReason
                              ? ` · ${item.archiveReason}`
                              : ""}
                          </small>
                        </div>
                        <span className={`run-status ${item.status}`}>
                          {optionLabel(item.status, locale)}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            void downloadPersonnelDocument(worker, item)
                          }
                        >
                          {locale === "es" ? "Descargar" : "Download"}
                        </button>
                        {item.status === "active" && (
                          <button
                            type="button"
                            onClick={() =>
                              void archivePersonnelDocument(worker, item)
                            }
                          >
                            {locale === "es" ? "Archivar" : "Archive"}
                          </button>
                        )}
                      </article>
                    ))}
                  </div>
                </section>
              )}
            </article>
          ))}
        </div>
      ) : null}
      <OperationalError value={accountError} locale={locale} />
      {terminationDraft && (
        <dialog
          className="workflow-dialog"
          open
          aria-labelledby="termination-title"
        >
          <form onSubmit={terminate}>
            <header>
              <div>
                <p className="kicker">
                  {locale === "es"
                    ? "CAMBIO IRREVERSIBLE DE HISTORIAL"
                    : "IRREVERSIBLE HISTORY CHANGE"}
                </p>
                <h2 id="termination-title">
                  {locale === "es"
                    ? "Terminar relación de trabajo"
                    : "End work relationship"}
                </h2>
                <p>
                  {terminationDraft.worker.firstName}{" "}
                  {terminationDraft.worker.lastName}
                </p>
              </div>
              <button
                type="button"
                aria-label={locale === "es" ? "Cerrar" : "Close"}
                onClick={() => setTerminationDraft(null)}
              >
                ×
              </button>
            </header>
            <p className="form-detail">
              {locale === "es"
                ? "La relación se cierra con historial permanente. La nómina final no se marca pagada hasta que se calcule y finalice por separado."
                : "The relationship closes with permanent history. Final pay is not marked paid until it is separately calculated and finalized."}
            </p>
            <div className="form-grid">
              <label>
                {locale === "es" ? "Fecha de terminación" : "Termination date"}
                <input
                  required
                  type="date"
                  value={terminationDraft.terminationDate}
                  onInput={(e) =>
                    setTerminationDraft({
                      ...terminationDraft,
                      terminationDate: e.currentTarget.value,
                      lastDayWorked:
                        terminationDraft.lastDayWorked || e.currentTarget.value,
                      finalPayDueOn:
                        terminationDraft.finalPayDueOn || e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Último día trabajado" : "Last day worked"}
                <input
                  required
                  type="date"
                  max={terminationDraft.terminationDate}
                  value={terminationDraft.lastDayWorked}
                  onInput={(e) =>
                    setTerminationDraft({
                      ...terminationDraft,
                      lastDayWorked: e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es"
                  ? "Fecha prevista de pago final"
                  : "Final-pay due date"}
                <input
                  required
                  type="date"
                  min={terminationDraft.terminationDate}
                  value={terminationDraft.finalPayDueOn}
                  onInput={(e) =>
                    setTerminationDraft({
                      ...terminationDraft,
                      finalPayDueOn: e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es"
                  ? "Tratamiento de licencia"
                  : "Leave treatment"}
                <select
                  value={terminationDraft.leaveDisposition}
                  onChange={(e) =>
                    setTerminationDraft({
                      ...terminationDraft,
                      leaveDisposition: e.currentTarget
                        .value as TerminationDraft["leaveDisposition"],
                    })
                  }
                >
                  <option value="paid_out">
                    {locale === "es"
                      ? "Pagar balance aplicable"
                      : "Pay applicable balance"}
                  </option>
                  <option value="forfeited">
                    {locale === "es"
                      ? "Caducar donde la ley/política permita"
                      : "Forfeit where law/policy permits"}
                  </option>
                  <option value="transferred">
                    {locale === "es"
                      ? "Conservar para recontratación"
                      : "Retain for rehire"}
                  </option>
                  <option value="not_applicable">
                    {locale === "es"
                      ? "No aplica — sin balance"
                      : "Not applicable — no balance"}
                  </option>
                </select>
              </label>
              <label>
                {locale === "es"
                  ? "Deducciones de nómina final"
                  : "Final-pay deductions"}
                <select
                  value={terminationDraft.finalDeductionDisposition}
                  onChange={(e) =>
                    setTerminationDraft({
                      ...terminationDraft,
                      finalDeductionDisposition: e.currentTarget
                        .value as TerminationDraft["finalDeductionDisposition"],
                    })
                  }
                >
                  <option value="apply_active">
                    {locale === "es"
                      ? "Aplicar instrucciones activas"
                      : "Apply active instructions"}
                  </option>
                  <option value="suspend_voluntary">
                    {locale === "es"
                      ? "Suspender voluntarias"
                      : "Suspend voluntary deductions"}
                  </option>
                  <option value="suspend_all">
                    {locale === "es"
                      ? "Suspender todas"
                      : "Suspend all deductions"}
                  </option>
                </select>
              </label>
              <label className="full-width">
                {locale === "es" ? "Razón documentada" : "Documented reason"}
                <textarea
                  required
                  minLength={3}
                  value={terminationDraft.reason}
                  onChange={(e) =>
                    setTerminationDraft({
                      ...terminationDraft,
                      reason: e.currentTarget.value,
                    })
                  }
                />
              </label>
            </div>
            <footer>
              <button type="button" onClick={() => setTerminationDraft(null)}>
                {locale === "es" ? "Cancelar" : "Cancel"}
              </button>
              <button
                className="danger-action"
                disabled={busy || terminationDraft.reason.trim().length < 3}
              >
                {busy ? (
                  <LoaderCircle className="spin" />
                ) : locale === "es" ? (
                  "Confirmar terminación"
                ) : (
                  "Confirm termination"
                )}
              </button>
            </footer>
          </form>
        </dialog>
      )}
      {personnelAction && actionCopy && (
        <dialog
          className="workflow-dialog"
          open
          aria-labelledby="personnel-action-title"
        >
          <form onSubmit={submitPersonnelAction}>
            <header>
              <div>
                <p className="kicker">
                  {locale === "es" ? "ACCIÓN AUDITADA" : "AUDITED ACTION"}
                </p>
                <h2 id="personnel-action-title">
                  {locale === "es" ? actionCopy.es : actionCopy.en}
                </h2>
                <p>{actionSubject}</p>
              </div>
              <button
                type="button"
                aria-label={locale === "es" ? "Cerrar" : "Close"}
                onClick={() => setPersonnelAction(null)}
              >
                ×
              </button>
            </header>
            <p className="form-detail">
              {locale === "es" ? actionCopy.detailEs : actionCopy.detailEn}
            </p>
            <div className="form-grid">
              {personnelAction.kind === "rehire" && (
                <label>
                  {locale === "es" ? "Nueva fecha de inicio" : "New start date"}
                  <input
                    required
                    type="date"
                    value={personnelAction.rehireDate}
                    onInput={(e) =>
                      setPersonnelAction({
                        ...personnelAction,
                        rehireDate: e.currentTarget.value,
                      })
                    }
                  />
                </label>
              )}
              <label className="full-width">
                {locale === "es" ? "Razón documentada" : "Documented reason"}
                <textarea
                  required
                  minLength={3}
                  value={personnelAction.reason}
                  onChange={(e) =>
                    setPersonnelAction({
                      ...personnelAction,
                      reason: e.currentTarget.value,
                    })
                  }
                />
              </label>
            </div>
            <footer>
              <button type="button" onClick={() => setPersonnelAction(null)}>
                {locale === "es" ? "Cancelar" : "Cancel"}
              </button>
              <button
                className={
                  personnelAction.kind === "revoke_access" ||
                  personnelAction.kind === "archive_document"
                    ? "danger-action"
                    : "primary"
                }
                disabled={busy || personnelAction.reason.trim().length < 3}
              >
                {busy ? (
                  <LoaderCircle className="spin" />
                ) : locale === "es" ? (
                  "Confirmar acción"
                ) : (
                  "Confirm action"
                )}
              </button>
            </footer>
          </form>
        </dialog>
      )}
      {canManage && transfers.length > 0 && (
        <section className="settings-form">
          <h2>
            {locale === "es"
              ? "Transferencias de propiedad"
              : "Ownership transfers"}
          </h2>
          <div className="run-list">
            {transfers.map((transfer) => (
              <article key={transfer.id}>
                <div>
                  <strong>{optionLabel(transfer.status, locale)}</strong>
                  <p>{transfer.reason}</p>
                  <small>
                    {locale === "es"
                      ? "Puede aceptarse desde"
                      : "Eligible from"}
                    : {transfer.eligibleAt} ·{" "}
                    {locale === "es" ? "Expira" : "Expires"}:{" "}
                    {transfer.expiresAt}
                  </small>
                </div>
                {managerRole === "administrator" &&
                  transfer.status === "pending" && (
                    <button
                      type="button"
                      onClick={() => void acceptTransfer(transfer)}
                    >
                      {locale === "es"
                        ? "Aceptar propiedad"
                        : "Accept ownership"}
                    </button>
                  )}
                {managerRole === "owner" && transfer.status === "pending" && (
                  <button
                    type="button"
                    onClick={() => void cancelTransfer(transfer)}
                  >
                    {locale === "es"
                      ? "Cancelar transferencia"
                      : "Cancel transfer"}
                  </button>
                )}
              </article>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

type ReviewData = {
  current: {
    tenantId: string | null;
    roleKey: string;
    workerId: string | null;
  };
  roles: string[];
  people: {
    tenantId: string;
    workerId: string;
    workerType: "employee" | "professional_services" | "pending_classification";
    assignedRoleKey: string | null;
    displayName: string;
  }[];
};

type Project = {
  id: string;
  code: string | null;
  name: string;
  status: string;
};
type Vehicle = {
  id: string;
  unitNumber: string;
  plate?: string | null;
  vin?: string | null;
  make?: string | null;
  model?: string | null;
  modelYear?: number | null;
  status: string;
  notes?: string | null;
};
type FieldAssignment = {
  id: string;
  workerId: string;
  workerName: string;
  projectId: string | null;
  projectName: string | null;
  vehicleId: string | null;
  vehicleUnit: string | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  status: string;
  reason: string;
};
type TimeEvent = {
  id: string;
  workerId: string;
  workerName: string;
  eventType: string;
  source: string;
  eventAt: string;
  receivedAt: string;
  status: string;
  projectName: string | null;
  vehicleUnit: string | null;
  departmentId: string | null;
  departmentName: string | null;
  locationId: string | null;
  locationName: string | null;
  vehicleId?: string | null;
  costCenterId: string | null;
  costCenterCode: string | null;
  comment: string | null;
};
type LaborDay = {
  workDate: string;
  workedMinutes: number;
  mealMinutes: number;
  minimumMealMinutes: number;
  dailyOvertimeAfterMinutes?: number;
  alternateWorkweekAgreementId?: string;
  mealIssue: boolean;
  secondMealReview: boolean;
  mealIssueUnresolved: boolean;
  secondMealReviewUnresolved: boolean;
  autoDeductedMealMinutes?: number;
  mealAutoDeductionPolicyId?: string;
  firstMealResolution: MealResolution | null;
  secondMealResolution: MealResolution | null;
};
type LaborEvaluation = {
  workerId: string;
  workerName: string;
  overtimeEligible: boolean;
  overtimeMultiplier: number;
  ruleVersion: string;
  workedMinutes: number;
  dailyOvertimeMinutes: number;
  weeklyOvertimeMinutes: number;
  totalOvertimeMinutes: number;
  seventhDayReview: boolean;
  seventhDayMinutes: number;
  seventhDayWorkDate: string | null;
  workweekAligned: boolean;
  alternateWorkdayReview: boolean;
  mealIssueCount: number;
  secondMealReviewCount: number;
  eventIssues: string[];
  days: LaborDay[];
};
type MealResolution = {
  id: string;
  workerId: string;
  workerName?: string;
  workDate: string;
  issueKind: "first_meal" | "second_meal";
  resolutionType: "penalty_due" | "statutory_second_omission";
  penaltyMinutes: number;
  penaltyMultiplierBps: number;
  ruleVersion: string;
  reason?: string;
  createdAt?: string;
};
type MealAgreement = {
  id: string;
  workerId: string;
  workerName: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  minimumMealMinutes: number;
  occupationBasis: string;
  employeeSignedOn: string;
  employerSignedOn: string;
  reason: string;
  evidenceFileName: string;
  createdAt: string;
};
type MealAutoDeductionPolicy = {
  id: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  thresholdMinutes: number;
  deductionMinutes: number;
  status: string;
  reason: string;
  createdAt: string;
};
type AlternateWorkweekAgreement = {
  id: string;
  workerId: string;
  workerName: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  schedule: { dayOfWeek: number; minutes: number }[];
  employeeSignedOn: string;
  employerSignedOn: string;
  reason: string;
  legalSourceUrl: string;
  evidenceFileName: string;
  createdAt: string;
  terminationId: string | null;
  terminatedEffectiveOn: string | null;
  terminationMode: string | null;
  terminationReason: string | null;
  terminationEvidenceFileName: string | null;
};
type EmployerAcquisition = {
  id: string;
  effectiveOn: string;
  transactionType: string;
  predecessorLegalName: string;
  successorLegalName: string;
  reason: string;
  legalSourceUrl: string;
  evidenceFileName: string;
  createdAt: string;
  continuations: {
    id: string;
    agreementId: string;
    workerId: string;
    continuationBasis: string;
  }[];
};
type ScheduleChangeRequest = {
  id: string;
  workerId: string;
  workerName: string;
  changeType: string;
  requestedDetails: string;
  requestedEffectiveOn: string;
  requestedThrough: string | null;
  proposedSchedule: {
    name: string;
    scheduleMode: string;
    punchPolicyId: string | null;
    shifts: ScheduleShift[];
  } | null;
  reason: string;
  priorityBasis: string;
  employmentStartDate: string;
  regularWeeklyMinutes: number;
  receivedOn: string;
  receivedAt: string;
  responseDueOn: string;
  caregiverEvidenceFileName: string | null;
  status: string;
  meetingOn: string | null;
  finalResponseDueOn: string | null;
  writtenResponse: string | null;
  conditions: string | null;
  denialReason: string | null;
  alternative: string | null;
  resultingScheduleId: string | null;
  lateResponse: number | null;
  respondedAt: string | null;
};
type LeaveTimeSegmentView = {
  leaveRequestId: string;
  workerId: string;
  workDate: string;
  hoursHundredths: number;
  policyName: string;
  leaveType: string;
};
type CategorizedTimeEntryView = {
  id: string;
  timesheetId: string;
  workerId: string;
  workDate: string;
  category:
    | "regular_work"
    | "training"
    | "break_paid"
    | "break_unpaid"
    | "travel"
    | "other_paid"
    | "other_unpaid";
  minutes: number;
  description: string | null;
  projectId: string | null;
  projectName: string | null;
  vehicleId: string | null;
  vehicleUnit: string | null;
  departmentName: string | null;
  locationName: string | null;
  costCenterCode: string | null;
};
type Timesheet = {
  id: string;
  workerId: string;
  workerName: string;
  periodStart: string;
  periodEnd: string;
  status: string;
  submittedAt: string | null;
  supervisorApprovedAt: string | null;
  lockedAt: string | null;
  reopenCount: number;
  reopenedAt: string | null;
  latestTransitionReason: string | null;
  leaveSegments: LeaveTimeSegmentView[];
  categorizedEntries: CategorizedTimeEntryView[];
};
type TimeCorrection = {
  id: string;
  workerId: string;
  workerName: string;
  originalEventId: string | null;
  originalEventType: string | null;
  originalEventAt: string | null;
  requestedEventType: string;
  requestedEventAt: string;
  reason: string;
  status: string;
  reviewReason: string | null;
  appliedEventId: string | null;
};
type DailyComment = {
  id: string;
  workerId: string;
  workerName: string;
  workDate: string;
  comment: string;
  projectId: string | null;
  projectName: string | null;
  status: string;
  reviewComment: string | null;
  reviewedAt: string | null;
  createdAt: string;
};
type PunchPolicy = {
  id: string;
  name: string;
  beforeStartMinutes: number;
  afterStartMinutes: number;
  beforeEndMinutes: number;
  afterEndMinutes: number;
  outOfWindowAction: string;
  requireMeal: number;
  active: number;
};
type ScheduleShift = {
  dayOfWeek: number;
  sequence: number;
  startTime: string;
  endTime: string;
  mealMinutes: number;
  expectedPaidMinutes: number;
};
type WorkSchedule = {
  id: string;
  workerId: string;
  workerName: string;
  name: string;
  scheduleMode: string;
  punchPolicyId: string | null;
  punchPolicyName: string | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  shifts: ScheduleShift[];
  leaveSegments: LeaveTimeSegmentView[];
};
type PunchException = {
  id: string;
  workerId: string;
  workerName: string;
  timeEventId: string;
  eventType: string;
  scheduledAt: string;
  actualAt: string;
  deviationMinutes: number;
  policyAction: string;
  explanation: string | null;
  status: string;
  reviewComment: string | null;
  policyName: string;
};
type NoTrackingPolicy = {
  id: string;
  workerId: string;
  workerName: string;
  basis: "professional_services" | "exempt_salary";
  effectiveFrom: string;
  effectiveTo: string | null;
  reason: string;
  legalSourceUrl: string;
  terminationId: string | null;
  terminatedEffectiveOn: string | null;
  terminationReason: string | null;
};
type TimeActionDraft =
  | {
      kind: "correction";
      item: TimeCorrection;
      decision: "approved" | "rejected" | "applied";
      reason: string;
    }
  | {
      kind: "daily_comment";
      item: DailyComment;
      decision: "acknowledged" | "action_required";
      reason: string;
    }
  | {
      kind: "end_no_tracking";
      item: NoTrackingPolicy;
      effectiveOn: string;
      reason: string;
    }
  | {
      kind: "end_schedule";
      item: WorkSchedule;
      effectiveTo: string;
      reason: string;
    }
  | {
      kind: "punch_exception";
      item: PunchException;
      decision: "approved" | "rejected";
      reason: string;
    }
  | { kind: "withdraw_schedule"; item: ScheduleChangeRequest; reason: string }
  | { kind: "void_entry"; item: CategorizedTimeEntryView; reason: string }
  | { kind: "timesheet"; item: Timesheet; status: string; reason: string }
  | {
      kind: "close_assignment";
      item: FieldAssignment;
      effectiveTo: string;
      reason: string;
    };
type TimeEditDraft =
  | {
      kind: "meal";
      workerId: string;
      workDate: string;
      issueKind: "first_meal" | "second_meal";
      allowOmission: boolean;
      resolutionType: "penalty_due" | "statutory_second_omission";
      penaltyMinutes: string;
      reason: string;
    }
  | {
      kind: "project";
      item: Project;
      name: string;
      code: string;
      status: "active" | "inactive" | "closed";
      reason: string;
    }
  | {
      kind: "vehicle";
      item: Vehicle;
      unitNumber: string;
      status: "active" | "inactive" | "retired";
      notes: string;
      reason: string;
    };
type CloverTipIdentity = {
  cloverEmployeeId: string;
  cloverEmployeeName: string;
  transactionCount: number;
  tipCents: number;
};
type CloverTipTransaction = {
  paymentId: string;
  orderId: string | null;
  orderAt: string;
  cloverEmployeeId: string;
  cloverEmployeeName: string;
  tipCents: number;
};
const parseCsvLine = (line: string) => {
  const values: string[] = [];
  let value = "",
    quoted = false;
  for (let index = 0; index < line.length; index++) {
    const character = line[index]!;
    if (character === '"') {
      if (quoted && line[index + 1] === '"') {
        value += '"';
        index++;
      } else quoted = !quoted;
    } else if (character === "," && !quoted) {
      values.push(value);
      value = "";
    } else value += character;
  }
  values.push(value);
  return values;
};
function TipsPlaceholder({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const [file, setFile] = useState<File | null>(null),
    [identities, setIdentities] = useState<CloverTipIdentity[]>([]),
    [workers, setWorkers] = useState<
      {
        id: string;
        firstName: string;
        middleName?: string | null;
        lastName?: string | null;
        workerType: string;
        tipEligible: boolean;
      }[]
    >([]),
    [mapping, setMapping] = useState<Record<string, string[]>>({}),
    [ignored, setIgnored] = useState(0),
    [duplicates, setDuplicates] = useState(0),
    [message, setMessage] = useState(""),
    [saveMessage, setSaveMessage] = useState(""),
    [reason, setReason] = useState(""),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    void Promise.all([
      api(`/v1/tenants/${tenant.id}/workers`),
      api(`/v1/tenants/${tenant.id}/restaurant-tip-mappings`),
    ]).then(async ([workerResponse, mappingResponse]) => {
      if (workerResponse.ok)
        setWorkers(
          ((await workerResponse.json()) as { workers: typeof workers })
            .workers,
        );
      if (mappingResponse.ok) {
        const body = (await mappingResponse.json()) as {
          mappings: { cloverEmployeeId: string; workerId: string }[];
        };
        const next: Record<string, string[]> = {};
        for (const item of body.mappings)
          next[item.cloverEmployeeId] = [
            ...(next[item.cloverEmployeeId] ?? []),
            item.workerId,
          ];
        setMapping(next);
      }
    });
  }, [tenant.id]);
  const readFile = async () => {
    if (!file) return;
    setBusy(true);
    setMessage("");
    try {
      const lines = (await file.text())
          .replace(/^\uFEFF/, "")
          .split(/\r?\n/)
          .filter(Boolean),
        headers = parseCsvLine(lines[0] ?? "").map((item) => item.trim()),
        required = [
          "Result",
          "Order Date",
          "Order Employee ID",
          "Order Employee Name",
          "Tip Amount",
          "Payment ID",
          "Order ID",
        ],
        missing = required.filter((item) => !headers.includes(item));
      if (missing.length) throw new Error(`missing:${missing.join(", ")}`);
      const indexes = Object.fromEntries(
          headers.map((header, index) => [header, index]),
        ),
        seen = new Set<string>(),
        groups = new Map<string, CloverTipIdentity>();
      let skipped = 0,
        duplicateCount = 0;
      for (const line of lines.slice(1)) {
        const values = parseCsvLine(line),
          result = (values[indexes.Result] ?? "").trim().toUpperCase();
        if (result !== "SUCCESS") {
          skipped++;
          continue;
        }
        const paymentId = (values[indexes["Payment ID"]] ?? "").trim();
        if (!paymentId || seen.has(paymentId)) {
          duplicateCount++;
          continue;
        }
        seen.add(paymentId);
        const id = (values[indexes["Order Employee ID"]] ?? "").trim(),
          name = (values[indexes["Order Employee Name"]] ?? "").trim(),
          tip = Number(values[indexes["Tip Amount"]] ?? "0");
        if (!id || !name || !Number.isFinite(tip)) {
          skipped++;
          continue;
        }
        const current = groups.get(id) ?? {
          cloverEmployeeId: id,
          cloverEmployeeName: name,
          transactionCount: 0,
          tipCents: 0,
        };
        current.transactionCount++;
        current.tipCents += Math.round(tip * 100);
        groups.set(id, current);
      }
      setIdentities(
        [...groups.values()].sort((a, b) =>
          a.cloverEmployeeName.localeCompare(b.cloverEmployeeName),
        ),
      );
      setIgnored(skipped);
      setDuplicates(duplicateCount);
      setMessage(
        locale === "es"
          ? "Archivo leído. Revise el importe de propinas y complete el mapeo."
          : "File read. Review tip amounts and complete the mapping.",
      );
    } catch (error) {
      setIdentities([]);
      setMessage(
        locale === "es"
          ? `No se pudo leer el CSV${error instanceof Error && error.message.startsWith("missing:") ? `. Faltan columnas: ${error.message.slice(8)}` : "."}`
          : "Unable to read the CSV.",
      );
    }
    setBusy(false);
  };
  const toggle = (cloverId: string, workerId: string) =>
    setMapping((current) => ({
      ...current,
      [cloverId]: (current[cloverId] ?? []).includes(workerId)
        ? (current[cloverId] ?? []).filter((id) => id !== workerId)
        : [...(current[cloverId] ?? []), workerId],
    }));
  const save = async () => {
    setBusy(true);
    setSaveMessage("");
    const response = await api(
      `/v1/tenants/${tenant.id}/restaurant-tip-mappings`,
      {
        method: "PUT",
        body: JSON.stringify({
          mappings: identities.map((item) => ({
            cloverEmployeeId: item.cloverEmployeeId,
            cloverEmployeeName: item.cloverEmployeeName,
            workerIds: mapping[item.cloverEmployeeId] ?? [],
          })),
          changeReason: reason,
        }),
      },
    );
    setSaveMessage(
      response.ok
        ? locale === "es"
          ? "Mapeo de nombres guardado y auditado."
          : "Name mapping saved and audited."
        : locale === "es"
          ? "No se pudo guardar el mapeo."
          : "Unable to save mapping.",
    );
    if (response.ok) setReason("");
    setBusy(false);
  };
  const eligibleWorkers = workers.filter((worker) => worker.tipEligible),
    money = (cents: number) =>
      new Intl.NumberFormat(locale === "es" ? "es-PR" : "en-US", {
        style: "currency",
        currency: "USD",
      }).format(cents / 100);
  return (
    <section className="data-section">
      <h1>{locale === "es" ? "Propinas" : "Tips"}</h1>
      <p className="lead">
        {locale === "es"
          ? "Aquí se prepararán, calcularán y aprobarán las propinas del período usando únicamente horas de trabajo aprobadas."
          : "Tips for the period will be prepared, calculated, and approved here using approved work hours only."}
      </p>
      <div className="settings-form">
        <label>
          {locale === "es" ? "Archivo CSV de Clover" : "Clover CSV file"}
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(event) =>
              setFile(event.currentTarget.files?.[0] ?? null)
            }
          />
        </label>
        <div className="form-actions">
          <button
            type="button"
            className="primary"
            disabled={!file || busy}
            onClick={() => void readFile()}
          >
            {busy ? (
              <LoaderCircle className="spin" />
            ) : locale === "es" ? (
              "Cargar y revisar CSV"
            ) : (
              "Load and review CSV"
            )}
          </button>
        </div>
      </div>
      {message && (
        <p
          className={
            message.includes("No se") || message.includes("Unable")
              ? "form-error"
              : "success-note"
          }
        >
          {message}
        </p>
      )}
      {identities.length > 0 && (
        <>
          <div className="metric-grid">
            <article>
              <small>
                {locale === "es" ? "Propinas SUCCESS" : "SUCCESS tips"}
              </small>
              <strong>
                {money(
                  identities.reduce((sum, item) => sum + item.tipCents, 0),
                )}
              </strong>
            </article>
            <article>
              <small>
                {locale === "es"
                  ? "Transacciones utilizadas"
                  : "Transactions used"}
              </small>
              <strong>
                {identities.reduce(
                  (sum, item) => sum + item.transactionCount,
                  0,
                )}
              </strong>
            </article>
            <article>
              <small>
                {locale === "es" ? "Filas no utilizadas" : "Rows not used"}
              </small>
              <strong>{ignored}</strong>
            </article>
            <article>
              <small>
                {locale === "es" ? "Duplicados omitidos" : "Duplicates skipped"}
              </small>
              <strong>{duplicates}</strong>
            </article>
          </div>
          <h2>
            {locale === "es"
              ? "Mapear personal de Clover"
              : "Map Clover personnel"}
          </h2>
          <p>
            {locale === "es"
              ? "Puede seleccionar más de una persona. Cuando un nombre sea compartido, la asignación posterior comprobará quién estaba trabajando a la hora indicada en Order Date."
              : "You may select more than one person. When a name is shared, the later allocation will check who was working at the Order Date time."}
          </p>
          <div className="responsive-table tip-mapping-table">
            <table>
              <thead>
                <tr>
                  <th>
                    {locale === "es" ? "Nombre en Clover" : "Clover name"}
                  </th>
                  <th>{locale === "es" ? "Transacciones" : "Transactions"}</th>
                  <th>Tip Amount</th>
                  <th>
                    {locale === "es" ? "Personas elegibles" : "Eligible people"}
                  </th>
                </tr>
              </thead>
              <tbody>
                {identities.map((item) => (
                  <tr key={item.cloverEmployeeId}>
                    <td className="tip-clover-identity">
                      <strong>{item.cloverEmployeeName}</strong>
                      <small>{item.cloverEmployeeId}</small>
                    </td>
                    <td>{item.transactionCount}</td>
                    <td>{money(item.tipCents)}</td>
                    <td>
                      <div className="tip-worker-options">
                        {eligibleWorkers.map((worker) => {
                          const name = [
                            worker.firstName,
                            worker.middleName,
                            worker.lastName,
                          ]
                            .filter(Boolean)
                            .join(" ");
                          return (
                            <label
                              key={worker.id}
                              className="tip-worker-option"
                            >
                              <input
                                type="checkbox"
                                checked={(
                                  mapping[item.cloverEmployeeId] ?? []
                                ).includes(worker.id)}
                                onChange={() =>
                                  toggle(item.cloverEmployeeId, worker.id)
                                }
                              />
                              <span>
                                <strong>{name}</strong>
                                <small>
                                  {worker.workerType === "professional_services"
                                    ? locale === "es"
                                      ? "Servicios profesionales"
                                      : "Professional services"
                                    : locale === "es"
                                      ? "Empleado"
                                      : "Employee"}
                                </small>
                              </span>
                            </label>
                          );
                        })}
                        {!eligibleWorkers.length && (
                          <small>
                            {locale === "es"
                              ? "No hay personas marcadas como elegibles para propinas."
                              : "No people are marked as tip-eligible."}
                          </small>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <AuditControl locale={locale} value={reason} onChange={setReason} />
          <div className="form-actions">
            <button
              type="button"
              className="primary"
              disabled={busy}
              onClick={() => void save()}
            >
              {locale === "es" ? "Guardar mapeo" : "Save mapping"}
            </button>
          </div>
          {saveMessage && (
            <p
              className={
                saveMessage.includes("No se") || saveMessage.includes("Unable")
                  ? "form-error"
                  : "success-note"
              }
              role="status"
            >
              {saveMessage}
            </p>
          )}
        </>
      )}
    </section>
  );
}

function cloverOrderDateToIso(value: string) {
  const match = value
    .trim()
    .match(
      /^(\d{2})-([A-Za-z]{3})-(\d{4})\s+(\d{1,2}):(\d{2})\s+(AM|PM)\s+AST$/i,
    );
  if (!match) return null;
  const months: { [key: string]: number } = {
      jan: 0,
      feb: 1,
      mar: 2,
      apr: 3,
      may: 4,
      jun: 5,
      jul: 6,
      aug: 7,
      sep: 8,
      oct: 9,
      nov: 10,
      dec: 11,
    },
    month = months[match[2].toLowerCase()];
  if (month === undefined) return null;
  let hour = Number(match[4]) % 12;
  if (match[6].toUpperCase() === "PM") hour += 12;
  return new Date(
    Date.UTC(
      Number(match[3]),
      month,
      Number(match[1]),
      hour + 4,
      Number(match[5]),
    ),
  ).toISOString();
}
function showTipSaveStatus(locale: Locale, message: string, isError: boolean) {
  const label =
      locale === "es"
        ? "Guardar distribución para nómina"
        : "Save payroll allocation",
    button = [...document.querySelectorAll<HTMLButtonElement>("button")].find(
      (item) => item.textContent?.includes(label),
    );
  if (!button) return;
  button.parentElement?.querySelector(".tip-save-status")?.remove();
  const status = document.createElement("p");
  status.className = `tip-save-status ${isError ? "form-error" : "success-note"}`;
  status.setAttribute("role", isError ? "alert" : "status");
  status.textContent = message;
  button.insertAdjacentElement("afterend", status);
  status.scrollIntoView({ behavior: "smooth", block: "nearest" });
}
function TipsWorkflow({ locale, tenant }: { locale: Locale; tenant: Tenant }) {
  const [file, setFile] = useState<File | null>(null),
    [runs, setRuns] = useState<
      {
        id: string;
        periodStart: string;
        periodEnd: string;
        payDate: string;
        status: string;
      }[]
    >([]),
    [runId, setRunId] = useState(""),
    [workers, setWorkers] = useState<
      {
        id: string;
        firstName: string;
        lastName?: string | null;
        workerType: string;
        tipEligible: boolean;
      }[]
    >([]),
    [mapping, setMapping] = useState<Record<string, string[]>>({}),
    [transactions, setTransactions] = useState<CloverTipTransaction[]>([]),
    [intervals, setIntervals] = useState<
      { workerId: string; clockInAt: string; clockOutAt: string }[]
    >([]),
    [pendingTipTimecards, setPendingTipTimecards] = useState<
      {
        workerId: string;
        workerName: string;
        periodStart: string;
        periodEnd: string;
        status: string;
      }[]
    >([]),
    [adjustments, setAdjustments] = useState<
      Record<string, { amount: string; reason: string }>
    >({}),
    [settings, setSettings] = useState<{
      creditCardFeeBps: number;
      distributionMethod: RestaurantTipMethod;
    }>({ creditCardFeeBps: 350, distributionMethod: "individual" }),
    [reason, setReason] = useState(""),
    [message, setMessage] = useState(""),
    [messageError, setMessageError] = useState(false),
    [busy, setBusy] = useState(false),
    [mappingSaved, setMappingSaved] = useState(false);
  useEffect(() => {
    void Promise.all([
      api(`/v1/tenants/${tenant.id}/restaurant-tip-payrolls`),
      api(`/v1/tenants/${tenant.id}/workers`),
      api(`/v1/tenants/${tenant.id}/restaurant-tip-mappings`),
      api(`/v1/tenants/${tenant.id}/restaurant-tip-settings`),
    ]).then(async ([r, w, m, s]) => {
      if (r.ok) setRuns(((await r.json()) as { runs: typeof runs }).runs);
      if (w.ok)
        setWorkers(((await w.json()) as { workers: typeof workers }).workers);
      if (m.ok) {
        const next: Record<string, string[]> = {};
        for (const row of (
          (await m.json()) as {
            mappings: { cloverEmployeeId: string; workerId: string }[];
          }
        ).mappings)
          next[row.cloverEmployeeId] = [
            ...(next[row.cloverEmployeeId] ?? []),
            row.workerId,
          ];
        setMapping(next);
      }
      if (s.ok)
        setSettings(
          ((await s.json()) as { settings: typeof settings }).settings,
        );
    });
  }, [tenant.id]);
  useEffect(() => {
    if (!runId) {
      setIntervals([]);
      setPendingTipTimecards([]);
      return;
    }
    const controller = new AbortController();
    setBusy(true);
    setMessage("");
    setMessageError(false);
    void api(
      `/v1/tenants/${tenant.id}/payroll-runs/${runId}/restaurant-tip-work-intervals`,
      { signal: controller.signal },
    )
      .then(async (response) => {
        if (!response.ok)
          throw new Error(await apiErrorCode(response, `HTTP ${response.status}`));
        const body = (await response.json()) as {
          intervals: typeof intervals;
          pendingTimecards: typeof pendingTipTimecards;
        };
        setIntervals(body.intervals);
        setPendingTipTimecards(body.pendingTimecards);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setMessageError(true);
        setMessage(
          locale === "es"
            ? `No se pudieron validar las hojas de tiempo: ${error instanceof Error ? error.message : String(error)}`
            : `Unable to validate timecards: ${error instanceof Error ? error.message : String(error)}`,
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setBusy(false);
      });
    return () => controller.abort();
  }, [runId, tenant.id, locale]);
  const identities = [
    ...new Map(transactions.map((t) => [t.cloverEmployeeId, t])).values(),
  ].map((x) => ({
    id: x.cloverEmployeeId,
    name: x.cloverEmployeeName,
    tipCents: transactions
      .filter((t) => t.cloverEmployeeId === x.cloverEmployeeId)
      .reduce((s, t) => s + t.tipCents, 0),
  }));
  const eligible = workers.filter((w) => w.tipEligible),
    gross = transactions.reduce((s, t) => s + t.tipCents, 0),
    fee = Math.round((gross * settings.creditCardFeeBps) / 10000),
    net = gross - fee,
    methodName =
      settings.distributionMethod === "individual"
        ? locale === "es"
          ? "Cada persona recibe sus propias propinas"
          : "Each person receives their own tips"
        : settings.distributionMethod === "equal_by_shared_shift"
          ? locale === "es"
            ? "Distribución igual entre quienes trabajaron juntos"
            : "Equal among people working together"
          : locale === "es"
            ? "Distribución proporcional por horas del período"
            : "Proportional by payroll-period hours";
  const allocationResult = (() => {
      const out = new Map<string, number>(),
        unresolved: string[] = [];
      const workingAt = (iso: string) => {
        const at = Date.parse(iso);
        return eligible
          .filter((w) =>
            intervals.some(
              (i) =>
                i.workerId === w.id &&
                Date.parse(i.clockInAt) <= at &&
                Date.parse(i.clockOutAt) >= at,
            ),
          )
          .map((w) => w.id);
      };
      if (settings.distributionMethod === "payroll_period_hours") {
        const minutes = new Map<string, number>();
        for (const i of intervals)
          minutes.set(
            i.workerId,
            (minutes.get(i.workerId) ?? 0) +
              Math.max(
                0,
                (Date.parse(i.clockOutAt) - Date.parse(i.clockInAt)) / 60000,
              ),
          );
        const total = [...minutes.values()].reduce((s, v) => s + v, 0);
        let assigned = 0;
        const rows = [...minutes].filter(([, v]) => v > 0);
        rows.forEach(([id, value], index) => {
          const cents =
            index === rows.length - 1
              ? net - assigned
              : Math.floor((net * value) / total);
          out.set(id, cents);
          assigned += cents;
        });
        if (!rows.length && net > 0)
          unresolved.push(
            locale === "es"
              ? "No hay horas aprobadas para distribuir las propinas."
              : "There are no approved hours for allocating tips.",
          );
      } else
        for (const tx of transactions) {
          const working = workingAt(tx.orderAt),
            mapped = mapping[tx.cloverEmployeeId] ?? [],
            ids =
              settings.distributionMethod === "equal_by_shared_shift"
                ? working
                : working.filter((id) => mapped.includes(id));
          const recipients = ids.length
            ? ids
            : settings.distributionMethod === "individual" &&
                mapped.length === 1
              ? mapped
              : [];
          if (!recipients.length) {
            unresolved.push(`${tx.cloverEmployeeName} · ${tx.orderAt}`);
            continue;
          }
          const netTx =
              tx.tipCents -
              Math.round((tx.tipCents * settings.creditCardFeeBps) / 10000),
            each = Math.floor(netTx / recipients.length),
            remainder = netTx - each * recipients.length;
          recipients.forEach((id, i) =>
            out.set(id, (out.get(id) ?? 0) + each + (i < remainder ? 1 : 0)),
          );
        }
      return {
        calculated: [...out].map(([workerId, calculatedCents]) => ({
          workerId,
          calculatedCents,
        })),
        unresolved,
      };
    })(),
    calculated = allocationResult.calculated;
  const read = async () => {
    if (!file || !runId) return;
    setBusy(true);
    setMessage("");
    setMessageError(false);
    try {
      const intervalResponse = await api(
        `/v1/tenants/${tenant.id}/payroll-runs/${runId}/restaurant-tip-work-intervals`,
      );
      if (!intervalResponse.ok)
        throw new Error(`work_intervals:${intervalResponse.status}`);
      const readiness = (await intervalResponse.json()) as {
        intervals: typeof intervals;
        pendingTimecards: typeof pendingTipTimecards;
      };
      setIntervals(readiness.intervals);
      setPendingTipTimecards(readiness.pendingTimecards);
      if (readiness.pendingTimecards.length)
        throw new Error(
          locale === "es"
            ? "Hay hojas de tiempo de personal con propinas sin aprobar. Corríjalas antes de cargar el archivo."
            : "Tip-eligible personnel have unapproved timecards. Correct them before loading the file.",
        );
      const lines = (await file.text())
          .replace(/^\uFEFF/, "")
          .split(/\r?\n/)
          .filter(Boolean),
        headers = parseCsvLine(lines[0] ?? "").map((v) => v.trim()),
        ix = Object.fromEntries(headers.map((h, i) => [h, i])),
        seen = new Set<string>(),
        next: CloverTipTransaction[] = [];
      for (const line of lines.slice(1)) {
        const v = parseCsvLine(line);
        if ((v[ix.Result] ?? "").trim().toUpperCase() !== "SUCCESS") continue;
        const paymentId = (v[ix["Payment ID"]] ?? "").trim();
        if (!paymentId || seen.has(paymentId)) continue;
        seen.add(paymentId);
        const tip = Number(v[ix["Tip Amount"]] ?? 0),
          orderAt = cloverOrderDateToIso((v[ix["Order Date"]] ?? "").trim());
        if (Number.isFinite(tip) && tip > 0 && orderAt)
          next.push({
            paymentId,
            orderId: (v[ix["Order ID"]] ?? "").trim() || null,
            orderAt,
            cloverEmployeeId: (v[ix["Order Employee ID"]] ?? "").trim(),
            cloverEmployeeName: (v[ix["Order Employee Name"]] ?? "").trim(),
            tipCents: Math.round(tip * 100),
          });
      }
      setTransactions(next);
      setMappingSaved(false);
      setMessage(
        locale === "es"
          ? "Archivo leído. Complete y guarde el mapeo."
          : "File read. Complete and save the mapping.",
      );
    } catch (error) {
      setMessageError(true);
      setMessage(
        locale === "es"
          ? `No se pudo preparar la distribución: ${String(error)}`
          : `Unable to prepare allocation: ${String(error)}`,
      );
    } finally {
      setBusy(false);
    }
  };
  const saveMap = async () => {
    setBusy(true);
    const response = await api(
      `/v1/tenants/${tenant.id}/restaurant-tip-mappings`,
      {
        method: "PUT",
        body: JSON.stringify({
          mappings: identities.map((i) => ({
            cloverEmployeeId: i.id,
            cloverEmployeeName: i.name,
            workerIds: mapping[i.id] ?? [],
          })),
          changeReason: reason || "Mapeo para distribución de propinas",
        }),
      },
    );
    setMappingSaved(response.ok);
    setMessage(
      response.ok
        ? locale === "es"
          ? "Mapeo guardado. Revise la configuración y calcule la distribución."
          : "Mapping saved. Review the configuration and calculate allocation."
        : locale === "es"
          ? "No se pudo guardar el mapeo."
          : "Unable to save mapping.",
    );
    setBusy(false);
  };
  const saveAllocation = async () => {
    if (!file) return;
    setBusy(true);
    document.querySelector(".tip-save-status")?.remove();
    try {
      if (allocationResult.unresolved.length)
        throw new Error(
          locale === "es"
            ? `${allocationResult.unresolved.length} transacciones no coinciden con personal trabajando.`
            : `${allocationResult.unresolved.length} transactions do not match working personnel.`,
        );
      const hash = Array.from(
        new Uint8Array(
          await crypto.subtle.digest("SHA-256", await file.arrayBuffer()),
        ),
      )
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
      const allocations = calculated.map((a) => {
          const adj = adjustments[a.workerId] ?? { amount: "0", reason: "" },
            adjustmentCents = Math.round(Number(adj.amount || 0) * 100);
          return {
            ...a,
            adjustmentCents,
            adjustmentReason: adjustmentCents ? adj.reason : null,
          };
        }),
        response = await api(`/v1/tenants/${tenant.id}/restaurant-tip-batch`, {
          method: "PUT",
          body: JSON.stringify({
            payrollRunId: runId,
            sourceFileName: file.name,
            sourceSha256: hash,
            grossTipCents: gross,
            transactions,
            allocations,
            changeReason: reason || "Distribución final de propinas",
          }),
        });
      if (!response.ok)
        throw new Error(
          await apiErrorCode(response, `HTTP ${response.status}`),
        );
      showTipSaveStatus(
        locale,
        locale === "es"
          ? "Propinas guardadas para esta nómina y listas para Calcular."
          : "Tips saved for this payroll and ready for Calculate.",
        false,
      );
    } catch (error) {
      const detail =
        error instanceof Error
          ? error.message
          : typeof error === "string"
            ? error
            : JSON.stringify(error);
      showTipSaveStatus(
        locale,
        locale === "es"
          ? `No se pudo guardar la distribución: ${detail}`
          : `Unable to save allocation: ${detail}`,
        true,
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="data-section">
      <h1>{locale === "es" ? "Propinas" : "Tips"}</h1>
      <div className="settings-form">
        <label>
          {locale === "es"
            ? "Nómina a la que se atribuirán las propinas"
            : "Payroll receiving these tips"}
          <select
            required
            value={runId}
            onChange={(e) => {
              setRunId(e.currentTarget.value);
              setTransactions([]);
            }}
          >
            <option value="">—</option>
            {runs.map((r) => (
              <option key={r.id} value={r.id}>
                {formatDateRange(r.periodStart, r.periodEnd, locale, "—")} ·{" "}
                {formatDate(r.payDate, locale)}
              </option>
            ))}
          </select>
        </label>
        <label>
          CSV Clover
          <input
            type="file"
            accept=".csv,text/csv"
            disabled={!runId || busy || pendingTipTimecards.length > 0}
            onChange={(e) => setFile(e.currentTarget.files?.[0] ?? null)}
          />
        </label>
        <button
          type="button"
          className="primary"
          disabled={
            !file || !runId || busy || pendingTipTimecards.length > 0
          }
          onClick={() => void read()}
        >
          {locale === "es" ? "Cargar archivo" : "Load file"}
        </button>
      </div>
      {pendingTipTimecards.length > 0 && (
        <section className="form-error" role="alert">
          <strong>
            {locale === "es"
              ? "No puede cargar ni mapear propinas todavía."
              : "Tips cannot be loaded or mapped yet."}
          </strong>
          <p>
            {locale === "es"
              ? "Apruebe y cierre estas hojas de tiempo del personal marcado para recibir propinas:"
              : "Approve and lock these timecards for personnel marked as tip-eligible:"}
          </p>
          <ul>
            {pendingTipTimecards.map((item) => (
              <li key={`${item.workerId}:${item.periodStart}`}>
                {item.workerName}: {formatDateRange(item.periodStart, item.periodEnd, locale, "—")}
              </li>
            ))}
          </ul>
        </section>
      )}
      {message && (
        <p
          className={messageError ? "form-error" : "success-note"}
          role={messageError ? "alert" : "status"}
        >
          {message}
        </p>
      )}
      {identities.length > 0 && (
        <>
          <h2>{locale === "es" ? "Mapeo de Clover" : "Clover mapping"}</h2>
          <div className="tip-worker-options">
            {identities.map((i) => (
              <article key={i.id} className="tip-worker-option">
                <strong>
                  {i.name} · ${(i.tipCents / 100).toFixed(2)}
                </strong>
                {eligible.map((w) => (
                  <label key={w.id}>
                    <input
                      type="checkbox"
                      checked={(mapping[i.id] ?? []).includes(w.id)}
                      onChange={() =>
                        setMapping((c) => ({
                          ...c,
                          [i.id]: (c[i.id] ?? []).includes(w.id)
                            ? (c[i.id] ?? []).filter((x) => x !== w.id)
                            : [...(c[i.id] ?? []), w.id],
                        }))
                      }
                    />
                    {w.firstName} {w.lastName}
                  </label>
                ))}
              </article>
            ))}
          </div>
          <AuditControl locale={locale} value={reason} onChange={setReason} />
          <button type="button" onClick={() => void saveMap()} disabled={busy}>
            {locale === "es" ? "Guardar mapeo" : "Save mapping"}
          </button>
        </>
      )}
      {mappingSaved && (
        <>
          <div className="info-note">
            <strong>{methodName}</strong>
            <p>
              {locale === "es"
                ? `Se descontará ${settings.creditCardFeeBps / 100}%: $${(fee / 100).toFixed(2)}. Propinas netas: $${(net / 100).toFixed(2)}.`
                : `${settings.creditCardFeeBps / 100}% will be deducted: $${(fee / 100).toFixed(2)}. Net tips: $${(net / 100).toFixed(2)}.`}
            </p>
          </div>
          <h2>{locale === "es" ? "Propinas por persona" : "Tips by person"}</h2>
          <div className="responsive-table">
            <table>
              <thead>
                <tr>
                  <th>{locale === "es" ? "Persona" : "Person"}</th>
                  <th>{locale === "es" ? "Calculado" : "Calculated"}</th>
                  <th>{locale === "es" ? "Añadir/reducir" : "Add/reduce"}</th>
                  <th>{locale === "es" ? "Razón" : "Reason"}</th>
                  <th>{locale === "es" ? "Final" : "Final"}</th>
                </tr>
              </thead>
              <tbody>
                {calculated.map((a) => {
                  const w = workers.find((x) => x.id === a.workerId),
                    adj = adjustments[a.workerId] ?? {
                      amount: "0",
                      reason: "",
                    },
                    adjCents = Math.round(Number(adj.amount || 0) * 100);
                  return (
                    <tr key={a.workerId}>
                      <td>
                        {w?.firstName} {w?.lastName}
                      </td>
                      <td>${(a.calculatedCents / 100).toFixed(2)}</td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          value={adj.amount}
                          onChange={(e) =>
                            setAdjustments((c) => ({
                              ...c,
                              [a.workerId]: {
                                ...adj,
                                amount: e.currentTarget.value,
                              },
                            }))
                          }
                        />
                      </td>
                      <td>
                        <input
                          value={adj.reason}
                          required={adjCents !== 0}
                          onChange={(e) =>
                            setAdjustments((c) => ({
                              ...c,
                              [a.workerId]: {
                                ...adj,
                                reason: e.currentTarget.value,
                              },
                            }))
                          }
                        />
                      </td>
                      <td>
                        ${((a.calculatedCents + adjCents) / 100).toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <button
            type="button"
            className="primary"
            disabled={
              busy ||
              calculated.some((a) => {
                const x = adjustments[a.workerId];
                return x && Number(x.amount) !== 0 && !x.reason.trim();
              })
            }
            onClick={() => void saveAllocation()}
          >
            {locale === "es"
              ? "Guardar distribución para nómina"
              : "Save payroll allocation"}
          </button>
        </>
      )}
    </section>
  );
}

function TimeEventLog({ locale, tenant }: { locale: Locale; tenant: Tenant }) {
  const [events, setEvents] = useState<TimeEvent[]>([]),
    [busy, setBusy] = useState(true),
    [error, setError] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    setBusy(true);
    setError("");
    void api(`/v1/tenants/${tenant.id}/time-events`, {
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          setError(`time_events:${response.status}`);
          return;
        }
        setEvents(((await response.json()) as { events: TimeEvent[] }).events);
      })
      .catch(() => {
        if (!controller.signal.aborted) setError("time_events:network");
      })
      .finally(() => {
        if (!controller.signal.aborted) setBusy(false);
      });
    return () => controller.abort();
  }, [tenant.id]);
  return (
    <div className="content">
      <div className="section-heading">
        <div>
          <p className="kicker">{tenant.displayName}</p>
          <h1>LOG</h1>
          <p className="lead">
            {locale === "es"
              ? "Historial de entradas, salidas, alimentos y demás eventos registrados en Tiempo."
              : "History of clock-in, clock-out, meal, and other events recorded in Time."}
          </p>
        </div>
      </div>
      {busy ? (
        <p>{locale === "es" ? "Cargando eventos…" : "Loading events…"}</p>
      ) : error ? (
        <OperationalError value={error} locale={locale} />
      ) : events.length === 0 ? (
        <section className="empty-module small">
          <span>
            <Clock3 />
          </span>
          <h2>{locale === "es" ? "No hay eventos" : "No events"}</h2>
        </section>
      ) : (
        <div className="event-list">
          {events.map((item) => (
            <article key={item.id}>
              <span className="event-icon">
                <Clock3 />
              </span>
              <div>
                <strong>{item.workerName}</strong>
                <p>
                  {optionLabel(item.eventType, locale)} ·{" "}
                  {formatDateTime(item.eventAt, locale)}
                </p>
                <small>
                  {item.source}
                  {item.projectName ? ` · ${item.projectName}` : ""}
                  {item.vehicleUnit ? ` · ${item.vehicleUnit}` : ""}
                  {item.departmentName ? ` · ${item.departmentName}` : ""}
                  {item.locationName ? ` · ${item.locationName}` : ""}
                  {item.costCenterCode ? ` · ${item.costCenterCode}` : ""}
                </small>
              </div>
              <em>{optionLabel(item.status, locale)}</em>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function Timekeeping({
  locale,
  tenant,
  canConfigure,
  roleKey,
  actorWorkerId,
  workerType,
  workspace = "operations",
}: {
  locale: Locale;
  tenant: Tenant;
  canConfigure: boolean;
  roleKey: string;
  actorWorkerId: string | null;
  workerType: ReviewContext["workerType"];
  workspace?:
    | "operations"
    | "person_setup"
    | "company_time"
    | "company_organization";
}) {
  const timeFocus = useMemo(() => {
    const query = new URLSearchParams(window.location.search),
      workerId = query.get("workerId"),
      periodStart = query.get("periodStart"),
      periodEnd = query.get("periodEnd");
    return workerId && periodStart && periodEnd
      ? { workerId, periodStart, periodEnd }
      : null;
  }, []);
  const [workers, setWorkers] = useState<Worker[]>([]),
    [projects, setProjects] = useState<Project[]>([]),
    [vehicles, setVehicles] = useState<Vehicle[]>([]),
    [events, setEvents] = useState<TimeEvent[]>([]),
    [allocationDepartments, setAllocationDepartments] = useState<OrgItem[]>([]),
    [allocationLocations, setAllocationLocations] = useState<OrgItem[]>([]),
    [allocationCostCenters, setAllocationCostCenters] = useState<OrgItem[]>([]),
    [fieldAssignments, setFieldAssignments] = useState<FieldAssignment[]>([]),
    [labor, setLabor] = useState<LaborEvaluation[]>([]),
    [laborError, setLaborError] = useState(""),
    [mealResolutions, setMealResolutions] = useState<MealResolution[]>([]),
    [mealAgreements, setMealAgreements] = useState<MealAgreement[]>([]),
    [mealAutoPolicies, setMealAutoPolicies] = useState<
      MealAutoDeductionPolicy[]
    >([]),
    [alternateAgreements, setAlternateAgreements] = useState<
      AlternateWorkweekAgreement[]
    >([]),
    [employerAcquisitions, setEmployerAcquisitions] = useState<
      EmployerAcquisition[]
    >([]),
    [scheduleChangeRequests, setScheduleChangeRequests] = useState<
      ScheduleChangeRequest[]
    >([]),
    [timesheets, setTimesheets] = useState<Timesheet[]>([]),
    [corrections, setCorrections] = useState<TimeCorrection[]>([]),
    [dailyComments, setDailyComments] = useState<DailyComment[]>([]),
    [punchPolicies, setPunchPolicies] = useState<PunchPolicy[]>([]),
    [schedules, setSchedules] = useState<WorkSchedule[]>([]),
    [punchExceptions, setPunchExceptions] = useState<PunchException[]>([]),
    [noTrackingPolicies, setNoTrackingPolicies] = useState<NoTrackingPolicy[]>(
      [],
    );
  const [busy, setBusy] = useState(true),
    [error, setError] = useState(false),
    [timeActionFailure, setTimeActionFailure] = useState(""),
    [timekeepingGateIssues, setTimekeepingGateIssues] = useState<string[]>([]);
  const [viewingTimesheetId, setViewingTimesheetId] = useState("");
  const [showTimeImport, setShowTimeImport] = useState(false),
    [showDailyComments, setShowDailyComments] = useState(false),
    [showOtherTimeOptions, setShowOtherTimeOptions] = useState(false);
  const [approvalWorkerId, setApprovalWorkerId] = useState(""),
    [approvalRunId, setApprovalRunId] = useState(""),
    [approvalCandidates, setApprovalCandidates] = useState<
      { workerId: string; payrollRunId: string }[]
    >([]),
    [approvalRows, setApprovalRows] = useState<
      {
        workDate: string;
        clockIn: string | null;
        clockOut: string | null;
        mealMinutes: number;
        payableMinutes: number;
        sourceCode: string | null;
        attendanceStatus: string | null;
        departmentId: string | null;
        locationId: string | null;
        costCenterId: string | null;
        leaveName: string | null;
        holidayName: string | null;
        holidayHoursHundredths: number;
      }[]
    >([]),
    [approvalLaborSummary, setApprovalLaborSummary] = useState<{
      workedMinutes: number;
      totalOvertimeMinutes: number;
      eventIssueCount: number;
      mealIssueCount: number;
      secondMealReviewCount: number;
    } | null>(null),
    [approvalEvaluations, setApprovalEvaluations] = useState<LaborEvaluation[]>(
      [],
    ),
    [approvalResolutions, setApprovalResolutions] = useState<
      {
        id: string;
        issueCode: string;
        workDate: string;
        issueDetail: string | null;
        resolution: string;
      }[]
    >([]),
    [approvalLeaveHours, setApprovalLeaveHours] = useState(0),
    [approvalHolidayHours, setApprovalHolidayHours] = useState(0),
    [approvalMessage, setApprovalMessage] = useState("");
  const professionalServicesView =
    workerType === "professional_services" ||
    (roleKey === "user" &&
      workers.find((worker) => worker.id === actorWorkerId)?.workerType ===
        "professional_services");
  const localNow = () => {
    const date = new Date();
    date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
    return date.toISOString().slice(0, 16);
  };
  const today = new Date().toISOString().slice(0, 10),
    weekStart = new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10);
  const [laborFrom, setLaborFrom] = useState(
      timeFocus?.periodStart ?? weekStart,
    ),
    [laborTo, setLaborTo] = useState(
      timeFocus
        ? new Date(
            new Date(`${timeFocus.periodStart}T00:00:00Z`).getTime() +
              6 * 86400000,
          )
            .toISOString()
            .slice(0, 10)
        : today,
    );
  const [categorizedForm, setCategorizedForm] = useState({
    timesheetId: "",
    workDate: today,
    category: "regular_work",
    minutes: "",
    description: "",
    projectId: "",
    vehicleId: "",
  });
  const [eventForm, setEventForm] = useState({
    workerId: timeFocus?.workerId ?? "",
    eventType: "clock_in",
    eventAt: localNow(),
    projectId: "",
    vehicleId: "",
    departmentId: "",
    locationId: "",
    costCenterId: "",
    comment: "",
  });
  type GridRow = {
    workDate: string;
    clockIn: string;
    clockOut: string;
    mealMinutes: 0 | 30 | 60;
    mealStart: string;
    mealEnd: string;
    chargeType: "home" | "department" | "location" | "cost_center";
    chargeId: string;
    ruleId: string;
    leavePolicyId: string;
    attendanceStatus: "scheduled" | "contractor_absent";
    approvedLeaveName?: string;
    existing?: boolean;
    metadataDirty?: boolean;
  };
  const [gridRows, setGridRows] = useState<GridRow[]>([]),
    [gridLoading, setGridLoading] = useState(false),
    [gridMessage, setGridMessage] = useState(""),
    [gridRules, setGridRules] = useState<PayDifferentialRule[]>([]),
    [gridAssignments, setGridAssignments] = useState<
      PayDifferentialAssignment[]
    >([]),
    [gridLeavePolicies, setGridLeavePolicies] = useState<LeavePolicy[]>([]),
    [gridLeaveAssignments, setGridLeaveAssignments] = useState<
      LeavePolicyAssignment[]
    >([]),
    [gridRuns, setGridRuns] = useState<PayrollRun[]>([]),
    [gridRunId, setGridRunId] = useState("");
  const [gridMealMode, setGridMealMode] = useState<"duration" | "times">(
      "duration",
    ),
    [gridComment, setGridComment] = useState("");
  const [timeImportFile, setTimeImportFile] = useState<File | null>(null);
  const [timeImportMappingIssues, setTimeImportMappingIssues] = useState<
      {
        sourceKey: string;
        workerName: string;
        candidates: { workerId: string; workerName: string; score: number }[];
      }[]
    >([]),
    [timeImportMappings, setTimeImportMappings] = useState<
      Record<string, string>
    >({});
  const [timeImportResult, setTimeImportResult] = useState<{
    id: string;
    fileName: string;
    status: string;
    sourceRowCount: number;
    acceptedPairCount: number;
    acceptedEventCount: number;
    skippedRowCount: number;
    rejectedRowCount: number;
    duplicatePairCount?: number;
  } | null>(null);
  const [timeImportAlreadyProcessed, setTimeImportAlreadyProcessed] =
    useState(false);
  const [timeImportError, setTimeImportError] = useState("");
  const [projectForm, setProjectForm] = useState({
    code: "",
    name: "",
    status: "active",
  });
  const [vehicleForm, setVehicleForm] = useState({
    unitNumber: "",
    status: "active",
  });
  const [correctionForm, setCorrectionForm] = useState({
    workerId: "",
    originalEventId: "",
    requestedEventType: "clock_in",
    requestedEventAt: localNow(),
    reason: "",
  });
  const [assignmentForm, setAssignmentForm] = useState({
    workerId: "",
    resourceType: "project",
    resourceId: "",
    effectiveFrom: new Date().toISOString().slice(0, 10),
    effectiveTo: "",
    reason: "",
  });
  const [dailyCommentForm, setDailyCommentForm] = useState({
    workerId: "",
    workDate: new Date().toISOString().slice(0, 10),
    projectId: "",
    comment: "",
  });
  const [policyForm, setPolicyForm] = useState({
    name: "",
    beforeStartMinutes: 7,
    afterStartMinutes: 7,
    beforeEndMinutes: 7,
    afterEndMinutes: 7,
    outOfWindowAction: "approval",
    requireMeal: true,
    active: true,
  });
  const [scheduleForm, setScheduleForm] = useState<{
    workerId: string;
    name: string;
    scheduleMode: string;
    punchPolicyId: string;
    effectiveFrom: string;
    effectiveTo: string;
    shifts: ScheduleShift[];
  }>({
    workerId: "",
    name: "",
    scheduleMode: "fixed",
    punchPolicyId: "",
    effectiveFrom: new Date().toISOString().slice(0, 10),
    effectiveTo: "",
    shifts: [1, 2, 3, 4, 5].map((dayOfWeek) => ({
      dayOfWeek,
      sequence: 1,
      startTime: "07:00",
      endTime: "16:00",
      mealMinutes: 60,
      expectedPaidMinutes: 480,
    })),
  });
  const [mealAgreementForm, setMealAgreementForm] = useState({
    workerId: "",
    effectiveFrom: today,
    effectiveTo: "",
    minimumMealMinutes: "30",
    occupationBasis: "general",
    employeeSignedOn: today,
    employerSignedOn: today,
    reason: "",
    evidence: null as File | null,
  });
  const [mealAutoPolicyForm, setMealAutoPolicyForm] = useState({
    effectiveFrom: "2026-06-08",
    thresholdMinutes: "300",
    deductionMinutes: "60",
    reason: "Regla general para empleados actuales",
  });
  const loadMealAutoPolicies = async () => {
    const response = await api(
      `/v1/tenants/${tenant.id}/meal-auto-deduction-policies`,
    );
    if (response.ok)
      setMealAutoPolicies(
        ((await response.json()) as { policies: MealAutoDeductionPolicy[] })
          .policies,
      );
  };
  useEffect(() => {
    void loadMealAutoPolicies();
  }, [tenant.id]);
  const saveMealAutoPolicy = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    const response = await api(
      `/v1/tenants/${tenant.id}/meal-auto-deduction-policies`,
      {
        method: "POST",
        body: JSON.stringify({
          effectiveFrom: mealAutoPolicyForm.effectiveFrom,
          thresholdMinutes: Number(mealAutoPolicyForm.thresholdMinutes),
          deductionMinutes: Number(mealAutoPolicyForm.deductionMinutes),
          reason: mealAutoPolicyForm.reason,
        }),
      },
    );
    if (!response.ok) setError(true);
    await loadMealAutoPolicies();
    await load();
  };
  const officialAlternateWorkweekSource =
    "https://www.trabajo.pr.gov/docs/Libreria_Laboral/Leyes/Ley%20379-1948%2C%20Ley%20para%20Establecer%20la%20Jornada%20de%20Trabajo%20en%20Puerto%20Rico.pdf";
  const [noTrackingForm, setNoTrackingForm] = useState({
    workerId: "",
    basis: "professional_services",
    effectiveFrom: today,
    effectiveTo: "",
    reason: "",
    legalSourceUrl:
      "https://www.trabajo.pr.gov/docs/Boletines/Guias_Legislacion_Laboral.pdf",
  });
  const [alternateForm, setAlternateForm] = useState({
    workerId: "",
    effectiveFrom: today,
    effectiveTo: "",
    employeeSignedOn: today,
    employerSignedOn: today,
    reason: "",
    legalSourceUrl: officialAlternateWorkweekSource,
    evidence: null as File | null,
    dayHours: ["", "", "", "", "", ""],
  });
  const [alternateTerminationForm, setAlternateTerminationForm] = useState({
    agreementId: "",
    effectiveOn: today,
    terminationMode: "mutual",
    employeeSignedOn: today,
    employerSignedOn: today,
    reason: "",
    evidence: null as File | null,
  });
  const [acquisitionForm, setAcquisitionForm] = useState({
    effectiveOn: today,
    transactionType: "acquisition",
    predecessorLegalName: "",
    successorLegalName: "",
    reason: "",
    legalSourceUrl: officialAlternateWorkweekSource,
    evidence: null as File | null,
  });
  const [scheduleChangeForm, setScheduleChangeForm] = useState<{
    changeType: string;
    requestedDetails: string;
    requestedEffectiveOn: string;
    requestedThrough: string;
    reason: string;
    priorityBasis: string;
    caregiverEvidence: File | null;
    proposalName: string;
    scheduleMode: string;
    punchPolicyId: string;
    shifts: ScheduleShift[];
  }>({
    changeType: "schedule",
    requestedDetails: "",
    requestedEffectiveOn: "",
    requestedThrough: "",
    reason: "",
    priorityBasis: "standard",
    caregiverEvidence: null,
    proposalName: "",
    scheduleMode: "fixed",
    punchPolicyId: "",
    shifts: [],
  });
  const [scheduleResponseForm, setScheduleResponseForm] = useState({
    requestId: "",
    decision: "approved",
    meetingOn: "",
    writtenResponse: "",
    conditions: "",
    denialReason: "",
    alternative: "",
  });
  const [timeAction, setTimeAction] = useState<TimeActionDraft | null>(null);
  const [timeEdit, setTimeEdit] = useState<TimeEditDraft | null>(null);
  useEffect(() => {
    if (workspace !== "person_setup" || !actorWorkerId) return;
    setScheduleForm((current) => ({ ...current, workerId: actorWorkerId }));
    setMealAgreementForm((current) => ({
      ...current,
      workerId: actorWorkerId,
    }));
    setNoTrackingForm((current) => ({ ...current, workerId: actorWorkerId }));
    setAlternateForm((current) => ({ ...current, workerId: actorWorkerId }));
  }, [workspace, actorWorkerId]);
  const load = async () => {
    const [
      w,
      p,
      v,
      e,
      allocationResponse,
      a,
      l,
      mealResponse,
      alternateResponse,
      acquisitionResponse,
      scheduleChangeResponse,
      timesheetResponse,
      correctionResponse,
      dailyCommentResponse,
      policyResponse,
      scheduleResponse,
      exceptionResponse,
      noTrackingResponse,
    ] = await Promise.all([
      api(`/v1/tenants/${tenant.id}/workers`),
      api(`/v1/tenants/${tenant.id}/projects`),
      api(`/v1/tenants/${tenant.id}/vehicles`),
      api(`/v1/tenants/${tenant.id}/time-events`),
      api(`/v1/tenants/${tenant.id}/work-allocation-options`),
      api(`/v1/tenants/${tenant.id}/field-resource-assignments`),
      api(
        `/v1/tenants/${tenant.id}/labor-evaluation?${new URLSearchParams({ from: laborFrom, to: laborTo })}`,
      ),
      api(
        `/v1/tenants/${tenant.id}/meal-periods?${new URLSearchParams({ from: laborFrom, to: laborTo })}`,
      ),
      api(`/v1/tenants/${tenant.id}/alternate-workweek-agreements`),
      api(`/v1/tenants/${tenant.id}/employer-acquisitions`),
      api(`/v1/tenants/${tenant.id}/schedule-change-requests`),
      api(`/v1/tenants/${tenant.id}/timesheets`),
      api(`/v1/tenants/${tenant.id}/time-corrections`),
      api(`/v1/tenants/${tenant.id}/daily-comments`),
      api(`/v1/tenants/${tenant.id}/punch-policies`),
      api(`/v1/tenants/${tenant.id}/schedules`),
      api(`/v1/tenants/${tenant.id}/punch-exceptions`),
      api(`/v1/tenants/${tenant.id}/no-tracking-policies`),
    ]);
    if (w.ok) {
      const people = ((await w.json()) as { workers: Worker[] }).workers;
      setWorkers(people);
      const ownId =
        actorWorkerId && people.some((item) => item.id === actorWorkerId)
          ? actorWorkerId
          : "";
      if (roleKey === "user" && ownId) {
        setEventForm((current) => ({ ...current, workerId: ownId }));
        setCorrectionForm((current) => ({ ...current, workerId: ownId }));
        setDailyCommentForm((current) => ({ ...current, workerId: ownId }));
      }
    }
    if (p.ok)
      setProjects(((await p.json()) as { projects: Project[] }).projects);
    if (v.ok)
      setVehicles(((await v.json()) as { vehicles: Vehicle[] }).vehicles);
    if (e.ok) setEvents(((await e.json()) as { events: TimeEvent[] }).events);
    if (allocationResponse.ok) {
      const body = (await allocationResponse.json()) as {
        departments: OrgItem[];
        locations: OrgItem[];
        costCenters: OrgItem[];
      };
      setAllocationDepartments(body.departments);
      setAllocationLocations(body.locations);
      setAllocationCostCenters(body.costCenters);
    }
    if (a.ok)
      setFieldAssignments(
        ((await a.json()) as { assignments: FieldAssignment[] }).assignments,
      );
    if (l.ok) {
      setLabor(
        ((await l.json()) as { evaluations: LaborEvaluation[] }).evaluations,
      );
      setLaborError("");
    } else {
      const body = (await l.json().catch(() => null)) as {
        error?: string | { code?: string };
      } | null;
      const error = body?.error;
      setLabor([]);
      setLaborError(
        typeof error === "string" ? error : (error?.code ?? `HTTP ${l.status}`),
      );
    }
    if (mealResponse.ok) {
      const body = (await mealResponse.json()) as {
        resolutions: MealResolution[];
        agreements: MealAgreement[];
      };
      setMealResolutions(body.resolutions);
      setMealAgreements(body.agreements);
    }
    if (alternateResponse.ok)
      setAlternateAgreements(
        (
          (await alternateResponse.json()) as {
            agreements: AlternateWorkweekAgreement[];
          }
        ).agreements,
      );
    if (acquisitionResponse.ok)
      setEmployerAcquisitions(
        (
          (await acquisitionResponse.json()) as {
            acquisitions: EmployerAcquisition[];
          }
        ).acquisitions,
      );
    if (scheduleChangeResponse.ok)
      setScheduleChangeRequests(
        (
          (await scheduleChangeResponse.json()) as {
            requests: ScheduleChangeRequest[];
          }
        ).requests,
      );
    if (timesheetResponse.ok)
      setTimesheets(
        ((await timesheetResponse.json()) as { timesheets: Timesheet[] })
          .timesheets,
      );
    if (correctionResponse.ok)
      setCorrections(
        ((await correctionResponse.json()) as { corrections: TimeCorrection[] })
          .corrections,
      );
    if (dailyCommentResponse.ok)
      setDailyComments(
        ((await dailyCommentResponse.json()) as { comments: DailyComment[] })
          .comments,
      );
    if (policyResponse.ok)
      setPunchPolicies(
        ((await policyResponse.json()) as { policies: PunchPolicy[] }).policies,
      );
    if (scheduleResponse.ok)
      setSchedules(
        ((await scheduleResponse.json()) as { schedules: WorkSchedule[] })
          .schedules,
      );
    if (exceptionResponse.ok)
      setPunchExceptions(
        ((await exceptionResponse.json()) as { exceptions: PunchException[] })
          .exceptions,
      );
    if (noTrackingResponse.ok)
      setNoTrackingPolicies(
        ((await noTrackingResponse.json()) as { policies: NoTrackingPolicy[] })
          .policies,
      );
    setBusy(false);
  };
  useEffect(() => {
    void load();
  }, [tenant.id, laborFrom, laborTo]);
  useEffect(() => {
    if (roleKey === "user") return;
    void Promise.all([
      api(`/v1/tenants/${tenant.id}/pay-differentials`),
      api(`/v1/tenants/${tenant.id}/payroll-runs`),
      api(`/v1/tenants/${tenant.id}/leave-policies`),
      api(`/v1/tenants/${tenant.id}/time-approval-candidates`),
    ]).then(
      async ([
        differentialResponse,
        runResponse,
        leaveResponse,
        approvalResponse,
      ]) => {
        if (differentialResponse.ok) {
          const body = (await differentialResponse.json()) as {
            rules: PayDifferentialRule[];
            assignments: PayDifferentialAssignment[];
          };
          setGridRules(body.rules);
          setGridAssignments(body.assignments);
        }
        if (leaveResponse.ok) {
          const body = (await leaveResponse.json()) as {
            policies: LeavePolicy[];
            assignments: LeavePolicyAssignment[];
          };
          setGridLeavePolicies(body.policies);
          setGridLeaveAssignments(body.assignments);
        }
        if (runResponse.ok) {
          const runs = (
            (await runResponse.json()) as { runs: PayrollRun[] }
          ).runs.filter(
            (run) =>
              run.runType === "regular" &&
              ["draft", "diagnostic", "calculated"].includes(run.status),
          );
          setGridRuns(runs);
          const focused = runs.find(
            (run) =>
              run.periodStart === timeFocus?.periodStart &&
              run.periodEnd === timeFocus?.periodEnd,
          );
          if (focused && timeFocus) {
            setGridRunId(focused.id);
            setApprovalWorkerId(timeFocus.workerId);
            setApprovalRunId(focused.id);
          }
        }
        if (approvalResponse.ok)
          setApprovalCandidates(
            (
              (await approvalResponse.json()) as {
                candidates: { workerId: string; payrollRunId: string }[];
              }
            ).candidates,
          );
      },
    );
  }, [tenant.id, roleKey]);
  useEffect(() => {
    const run = gridRuns.find((item) => item.id === gridRunId);
    if (!run) setGridRows([]);
  }, [gridRunId, gridRuns]);
  useEffect(() => {
    if (roleKey === "user" || !eventForm.workerId || !gridRunId) {
      setGridLoading(false);
      return;
    }
    const run = gridRuns.find((item) => item.id === gridRunId);
    if (!run) return;
    let cancelled = false;
    const selectedWorker = workers.find(
        (item) => item.id === eventForm.workerId,
      ),
      nonHourlyAttendance = selectedWorker?.timeEntryMethod === "none",
      activeSchedule = schedules.find(
        (item) =>
          item.workerId === eventForm.workerId &&
          item.effectiveFrom <= run.periodEnd &&
          (!item.effectiveTo || item.effectiveTo >= run.periodStart),
      ),
      days: GridRow[] = [];
    for (
      let date = new Date(`${run.periodStart}T00:00:00Z`),
        end = new Date(`${run.periodEnd}T00:00:00Z`);
      date <= end;
      date = new Date(date.getTime() + 86400000)
    ) {
      const workDate = date.toISOString().slice(0, 10),
        shift = activeSchedule?.shifts.find(
          (item) => item.dayOfWeek === date.getUTCDay(),
        );
      days.push({
        workDate,
        clockIn: nonHourlyAttendance && shift ? shift.startTime : "",
        clockOut: nonHourlyAttendance && shift ? shift.endTime : "",
        mealMinutes:
          nonHourlyAttendance && shift
            ? shift.mealMinutes === 30
              ? 30
              : shift.mealMinutes === 60
                ? 60
                : 0
            : 60,
        mealStart: "",
        mealEnd: "",
        chargeType: "home",
        chargeId: "",
        ruleId: "",
        leavePolicyId: "",
        attendanceStatus: "scheduled",
      });
    }
    setGridRows([]);
    setGridLoading(true);
    setGridComment("");
    setGridMessage("");
    void api(
      `/v1/tenants/${tenant.id}/time-entry-grid?${new URLSearchParams({ workerId: eventForm.workerId, from: run.periodStart, to: run.periodEnd })}`,
    ).then(async (response) => {
      if (cancelled) return;
      if (!response.ok) {
        setGridLoading(false);
        return;
      }
      const body = (await response.json()) as {
        rows: {
          workDate: string;
          clockIn: string;
          clockOut: string;
          mealStart: string | null;
          mealEnd: string | null;
          mealMinutes: number;
          sourceCode: string | null;
          departmentId: string | null;
          locationId: string | null;
          costCenterId: string | null;
        }[];
        leaveRows: { workDate: string; policyId: string; policyName: string }[];
        attendanceRows: {
          workDate: string;
          clockIn: string;
          clockOut: string;
          mealMinutes: number;
          payableMinutes: number;
          attendanceStatus: "scheduled" | "contractor_absent";
          departmentId: string | null;
          locationId: string | null;
          costCenterId: string | null;
        }[];
      };
      if (cancelled) return;
      setGridRows(
        days.map((row) => {
          const leave = body.leaveRows.find(
            (item) => item.workDate === row.workDate,
          );
          if (leave)
            return {
              ...row,
              leavePolicyId: leave.policyId,
              approvedLeaveName: leave.policyName,
              existing: true,
            };
          const attendance = body.attendanceRows?.find(
            (item) => item.workDate === row.workDate,
          );
          if (attendance)
            return {
              ...row,
              clockIn: attendance.clockIn,
              clockOut: attendance.clockOut,
              mealMinutes:
                attendance.mealMinutes === 30
                  ? 30
                  : attendance.mealMinutes === 60
                    ? 60
                    : 0,
              attendanceStatus: attendance.attendanceStatus,
              chargeType: attendance.departmentId
                ? "department"
                : attendance.locationId
                  ? "location"
                  : attendance.costCenterId
                    ? "cost_center"
                    : "home",
              chargeId:
                attendance.departmentId ??
                attendance.locationId ??
                attendance.costCenterId ??
                "",
              existing: true,
            };
          const saved = body.rows.find(
            (item) => item.workDate === row.workDate,
          );
          if (!saved) return row;
          const rule = gridRules.find(
            (item) => item.sourceCode === saved.sourceCode,
          );
          return {
            ...row,
            clockIn: saved.clockIn,
            clockOut: saved.clockOut,
            mealStart: saved.mealStart ?? "",
            mealEnd: saved.mealEnd ?? "",
            mealMinutes:
              saved.mealMinutes === 30 ? 30 : saved.mealMinutes === 60 ? 60 : 0,
            chargeType: saved.departmentId
              ? "department"
              : saved.locationId
                ? "location"
                : saved.costCenterId
                  ? "cost_center"
                  : "home",
            chargeId:
              saved.departmentId ??
              saved.locationId ??
              saved.costCenterId ??
              "",
            ruleId: rule?.id ?? "",
            existing: true,
          };
        }),
      );
      setGridLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [
    tenant.id,
    roleKey,
    eventForm.workerId,
    gridRunId,
    gridRuns,
    gridRules,
    workers,
    schedules,
  ]);
  useEffect(() => {
    if (!approvalWorkerId || !approvalRunId) {
      setApprovalRows([]);
      setApprovalLaborSummary(null);
      setApprovalEvaluations([]);
      setApprovalResolutions([]);
      setApprovalLeaveHours(0);
      setApprovalHolidayHours(0);
      return;
    }
    const run = gridRuns.find((item) => item.id === approvalRunId);
    if (!run) return;
    const contractorApproval =
      workers.find((item) => item.id === approvalWorkerId)?.workerType ===
      "professional_services";
    setApprovalMessage("");
    setApprovalRows([]);
    setApprovalLaborSummary(null);
    setApprovalEvaluations([]);
    setApprovalResolutions([]);
    setApprovalLeaveHours(0);
    setApprovalHolidayHours(0);
    let cancelled = false;
    void (async () => {
      const laborRanges = splitApprovalPeriod(run.periodStart, run.periodEnd);
      const [gridResponse, resolutionResponse, ...laborResponses] =
        await Promise.all([
          api(
            `/v1/tenants/${tenant.id}/time-entry-grid?${new URLSearchParams({ workerId: approvalWorkerId, from: run.periodStart, to: run.periodEnd })}`,
          ),
          api(
            `/v1/tenants/${tenant.id}/time-review-resolutions?${new URLSearchParams({ workerId: approvalWorkerId, payrollRunId: run.id })}`,
          ),
          ...laborRanges.map((range) =>
            api(
              `/v1/tenants/${tenant.id}/labor-evaluation?${new URLSearchParams({ ...range, workerId: approvalWorkerId })}`,
            ),
          ),
        ]);
      if (cancelled) return;
      if (!gridResponse.ok) {
        setApprovalRows([]);
        setApprovalMessage(
          locale === "es"
            ? "No se pudo cargar el detalle de este período."
            : "The period detail could not be loaded.",
        );
        return;
      }
      if (resolutionResponse.ok)
        setApprovalResolutions(
          (
            (await resolutionResponse.json()) as {
              resolutions: {
                id: string;
                issueCode: string;
                workDate: string;
                issueDetail: string | null;
                resolution: string;
              }[];
            }
          ).resolutions,
        );
      const body = (await gridResponse.json()) as {
        rows: {
          workDate: string;
          clockIn: string;
          clockOut: string;
          mealMinutes: number;
          sourceCode: string | null;
          departmentId: string | null;
          locationId: string | null;
          costCenterId: string | null;
        }[];
        attendanceRows: {
          workDate: string;
          clockIn: string;
          clockOut: string;
          mealMinutes: number;
          payableMinutes: number;
          attendanceStatus: string;
          departmentId: string | null;
          locationId: string | null;
          costCenterId: string | null;
        }[];
        leaveRows: {
          workDate: string;
          policyName: string;
          hoursHundredths: number;
        }[];
        holidayRows: {
          id: string;
          workDate: string;
          name: string;
          hoursHundredths: number;
        }[];
      };
      setApprovalLeaveHours(
        contractorApproval ? 0 : approvedLeaveHours(body.leaveRows),
      );
      setApprovalHolidayHours(
        contractorApproval
          ? 0
          : (body.holidayRows ?? []).reduce(
              (total, item) => total + item.hoursHundredths / 100,
              0,
            ),
      );
      const laborBodies = await Promise.all(
        laborResponses.map(async (response) =>
          response.ok
            ? ((await response.json()) as {
                evaluations: LaborEvaluation[];
              })
            : null,
        ),
      );
      if (cancelled) return;
      if (laborBodies.some((item) => item === null)) {
        setApprovalLaborSummary(null);
        setApprovalMessage(
          locale === "es"
            ? "No se pudo calcular el tiempo trabajado para todo el período."
            : "Worked time could not be calculated for the full period.",
        );
      } else {
        const evaluations = laborBodies.flatMap(
          (item) => item?.evaluations ?? [],
        );
        setApprovalEvaluations(evaluations);
        const summary = summarizeApprovalLabor(evaluations);
        setApprovalLaborSummary(
          contractorApproval
            ? { ...summary, totalOvertimeMinutes: 0 }
            : summary,
        );
      }
      const dates = new Set([
        ...body.rows.map((item) => item.workDate),
        ...(body.attendanceRows ?? []).map((item) => item.workDate),
        ...body.leaveRows.map((item) => item.workDate),
        ...(body.holidayRows ?? []).map((item) => item.workDate),
      ]);
      setApprovalRows(
        [...dates].sort().map((workDate) => {
          const recorded = body.rows.find((item) => item.workDate === workDate),
            attendance = body.attendanceRows?.find(
              (item) => item.workDate === workDate,
            ),
            leave = body.leaveRows.find((item) => item.workDate === workDate),
            holiday = body.holidayRows?.find(
              (item) => item.workDate === workDate,
            ),
            source = attendance ?? recorded;
          return {
            workDate,
            clockIn: source?.clockIn ?? null,
            clockOut: source?.clockOut ?? null,
            mealMinutes: source?.mealMinutes ?? 0,
            payableMinutes: attendance?.payableMinutes ?? 0,
            sourceCode: recorded?.sourceCode ?? null,
            attendanceStatus: attendance?.attendanceStatus ?? null,
            departmentId: source?.departmentId ?? null,
            locationId: source?.locationId ?? null,
            costCenterId: source?.costCenterId ?? null,
            leaveName: leave?.policyName ?? null,
            holidayName: holiday?.name ?? null,
            holidayHoursHundredths: holiday?.hoursHundredths ?? 0,
          };
        }),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [tenant.id, approvalWorkerId, approvalRunId, gridRuns, locale, workers]);
  useEffect(() => {
    if (!approvalWorkerId || !approvalRunId) return;
    const timer = window.setTimeout(
      () =>
        document
          .querySelector(".timesheet-approval-guide")
          ?.scrollIntoView({ behavior: "smooth", block: "start" }),
      100,
    );
    return () => window.clearTimeout(timer);
  }, [approvalWorkerId, approvalRunId]);
  const updateGridRow = (index: number, change: Partial<GridRow>) =>
    setGridRows((rows) =>
      rows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...change } : row,
      ),
    );
  const fillGridSchedule = () => {
    const selectedRun = gridRuns.find((item) => item.id === gridRunId);
    if (!selectedRun) return;
    const schedule = schedules.find(
      (item) =>
        item.workerId === eventForm.workerId &&
        item.effectiveFrom <= selectedRun.periodEnd &&
        (!item.effectiveTo || item.effectiveTo >= selectedRun.periodStart),
    );
    setGridRows((rows) =>
      rows.map((row) => {
        if (row.existing || row.leavePolicyId) return row;
        const shift = schedule?.shifts.find(
          (item) =>
            item.dayOfWeek ===
            new Date(`${row.workDate}T00:00:00Z`).getUTCDay(),
        );
        return shift
          ? {
              ...row,
              clockIn: shift.startTime,
              clockOut: shift.endTime,
              mealMinutes:
                shift.mealMinutes === 30
                  ? 30
                  : shift.mealMinutes === 60
                    ? 60
                    : 0,
              mealStart: "",
              mealEnd: "",
            }
          : { ...row, clockIn: "", clockOut: "", mealStart: "", mealEnd: "" };
      }),
    );
  };
  const saveGrid = async () => {
    const nonHourlyAttendance =
        workers.find((item) => item.id === eventForm.workerId)
          ?.timeEntryMethod === "none",
      run = gridRuns.find((item) => item.id === gridRunId),
      timeRows = gridRows.filter(
        (row) =>
          !row.existing && row.clockIn && row.clockOut && !row.leavePolicyId,
      ),
      leaveRows = gridRows.filter((row) => !row.existing && row.leavePolicyId),
      importedRows = gridRows.filter(
        (row) => row.existing && row.metadataDirty && !row.approvedLeaveName,
      );
    if (
      !eventForm.workerId ||
      !run ||
      (!timeRows.length &&
        !leaveRows.length &&
        !gridRows.some((row) => row.existing))
    )
      return;
    setBusy(true);
    setError(false);
    setGridMessage("");
    for (const row of leaveRows) {
      const schedule = schedules.find(
          (item) =>
            item.workerId === eventForm.workerId &&
            item.effectiveFrom <= row.workDate &&
            (!item.effectiveTo || item.effectiveTo >= row.workDate),
        ),
        shift = schedule?.shifts.find(
          (item) =>
            item.dayOfWeek ===
            new Date(`${row.workDate}T00:00:00Z`).getUTCDay(),
        ),
        hours = String((shift?.expectedPaidMinutes ?? 480) / 60),
        requestResponse = await api(`/v1/tenants/${tenant.id}/leave-requests`, {
          method: "POST",
          body: JSON.stringify({
            workerId: eventForm.workerId,
            policyId: row.leavePolicyId,
            startsOn: row.workDate,
            endsOn: row.workDate,
            hours,
            reason: "Ausencia registrada durante preparación de nómina",
          }),
        });
      if (!requestResponse.ok) {
        setGridMessage(
          `leave_request:${requestResponse.status} · ${row.workDate}`,
        );
        setError(true);
        setBusy(false);
        return;
      }
      const requestId = (
          (await requestResponse.json()) as { request: { id: string } }
        ).request.id,
        reviewResponse = await api(
          `/v1/tenants/${tenant.id}/leave-requests/${requestId}/review`,
          {
            method: "POST",
            body: JSON.stringify({
              decision: "approved",
              comment: "Aprobada al registrar el período",
            }),
          },
        );
      if (!reviewResponse.ok) {
        const failure = (await reviewResponse.json().catch(() => null)) as {
            error?: string;
            availableHours?: number;
            requestedHours?: number;
          } | null,
          code = failure?.error ?? `HTTP ${reviewResponse.status}`,
          message =
            code === "insufficient_leave_balance"
              ? locale === "es"
                ? `Saldo insuficiente de licencia: disponible ${(failure?.availableHours ?? 0).toFixed(2)} h; solicitado ${(failure?.requestedHours ?? 0).toFixed(2)} h.`
                : `Insufficient leave balance: ${(failure?.availableHours ?? 0).toFixed(2)} h available; ${(failure?.requestedHours ?? 0).toFixed(2)} h requested.`
              : code === "leave_supervisor_assignment_required"
                ? locale === "es"
                  ? "Esta persona no tiene un supervisor formalmente asignado."
                  : "This person has no formally assigned supervisor."
                : code === "leave_schedule_shift_required"
                  ? locale === "es"
                    ? "No existe un horario aplicable para esa fecha."
                    : "No applicable schedule exists for that date."
                  : code === "leave_timesheet_locked"
                    ? locale === "es"
                      ? "La hoja de tiempo de esa fecha ya está cerrada."
                      : "The timesheet for that date is already locked."
                    : code;
        setGridMessage(`${message} · ${row.workDate}`);
        setError(true);
        setBusy(false);
        return;
      }
    }
    if (timeRows.length) {
      const response = await api(`/v1/tenants/${tenant.id}/time-entry-grid`, {
        method: "POST",
        body: JSON.stringify({
          workerId: eventForm.workerId,
          comment: gridComment || null,
          rows: timeRows.map((row) => ({
            workDate: row.workDate,
            clockIn: row.clockIn,
            clockOut: row.clockOut,
            mealMinutes: gridMealMode === "duration" ? row.mealMinutes : 0,
            mealStart: gridMealMode === "times" ? row.mealStart || null : null,
            mealEnd: gridMealMode === "times" ? row.mealEnd || null : null,
            chargeType: row.chargeType,
            chargeId: row.chargeId || null,
            ruleId: row.ruleId || null,
            attendanceStatus: nonHourlyAttendance
              ? row.attendanceStatus
              : "scheduled",
          })),
        }),
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as {
          error?: string;
          workDate?: string;
        } | null;
        setGridMessage(
          `${body?.error ?? `HTTP ${response.status}`}${body?.workDate ? ` · ${body.workDate}` : ""}`,
        );
        setError(true);
        setBusy(false);
        return;
      }
    }
    if (importedRows.length) {
      const response = await api(`/v1/tenants/${tenant.id}/time-entry-grid`, {
        method: "PATCH",
        body: JSON.stringify({
          workerId: eventForm.workerId,
          comment: gridComment || null,
          rows: importedRows.map((row) => ({
            workDate: row.workDate,
            clockIn: row.clockIn,
            clockOut: row.clockOut,
            mealMinutes: row.mealMinutes,
            mealStart: null,
            mealEnd: null,
            chargeType: row.chargeType,
            chargeId: row.chargeId || null,
            ruleId: row.ruleId || null,
            attendanceStatus: "scheduled",
          })),
        }),
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as {
            error?: string | { code?: string; requestId?: string };
            workDate?: string;
          } | null,
          failure =
            typeof body?.error === "string"
              ? body.error
              : (body?.error?.code ?? `HTTP ${response.status}`),
          reference =
            typeof body?.error === "object" && body.error?.requestId
              ? ` · ${body.error.requestId}`
              : "";
        setGridMessage(
          `${failure}${body?.workDate ? ` · ${body.workDate}` : ""}${reference}`,
        );
        setError(true);
        setBusy(false);
        return;
      }
    }
    let createdTimesheets = 0;
    const periodEnd = new Date(`${run.periodEnd}T00:00:00Z`);
    for (
      let start = new Date(`${run.periodStart}T00:00:00Z`);
      start <= periodEnd;
      start = new Date(start.getTime() + 7 * 86400000)
    ) {
      const periodStart = start.toISOString().slice(0, 10),
        periodEndForSheet = new Date(
          Math.min(start.getTime() + 6 * 86400000, periodEnd.getTime()),
        )
          .toISOString()
          .slice(0, 10),
        alreadyCovered = timesheets.some(
          (sheet) =>
            sheet.workerId === eventForm.workerId &&
            sheet.periodStart <= periodStart &&
            sheet.periodEnd >= periodEndForSheet,
        );
      if (alreadyCovered) continue;
      const response = await api(`/v1/tenants/${tenant.id}/timesheets`, {
        method: "POST",
        body: JSON.stringify({
          workerId: eventForm.workerId,
          periodStart,
          periodEnd: periodEndForSheet,
        }),
      });
      if (!response.ok) {
        setGridMessage(
          locale === "es"
            ? `No se pudo crear la hoja de tiempo ${periodStart} — ${periodEndForSheet}.`
            : `The ${periodStart} — ${periodEndForSheet} timesheet could not be created.`,
        );
        setError(true);
        setBusy(false);
        return;
      }
      createdTimesheets++;
    }
    setGridMessage(
      locale === "es"
        ? `${timeRows.length} días nuevos, ${importedRows.length} días importados ajustados y ${leaveRows.length} licencias guardados; ${createdTimesheets} hojas de tiempo preparadas para aprobación.`
        : `${timeRows.length} new days, ${importedRows.length} imported days adjusted, and ${leaveRows.length} leave days saved; ${createdTimesheets} timesheets prepared for approval.`,
    );
    await load();
    // The approval candidate list is loaded separately from the time grid. A
    // newly created sheet was therefore invisible to the guided approval panel
    // until a full page refresh, making "Guardar período" appear to do nothing.
    setApprovalCandidates((current) =>
      current.some(
        (candidate) =>
          candidate.workerId === eventForm.workerId &&
          candidate.payrollRunId === run.id,
      )
        ? current
        : [
            ...current,
            { workerId: eventForm.workerId, payrollRunId: run.id },
          ],
    );
    setApprovalWorkerId(eventForm.workerId);
    setApprovalRunId(run.id);
    setEventForm((current) => ({ ...current, workerId: "" }));
    setGridRunId("");
    setGridRows([]);
    setGridComment("");
    window.setTimeout(
      () =>
        document
          .querySelector(".timesheet-approval-guide")
          ?.scrollIntoView({ behavior: "smooth", block: "start" }),
      250,
    );
  };
  const importTime = async () => {
    if (!timeImportFile) return;
    setBusy(true);
    setTimeImportError("");
    setTimeImportResult(null);
    setTimeImportAlreadyProcessed(false);
    const form = new FormData();
    form.set("file", timeImportFile);
    if (Object.keys(timeImportMappings).length)
      form.set("workerMappings", JSON.stringify(timeImportMappings));
    const response = await api(`/v1/tenants/${tenant.id}/time-events/import`, {
      method: "POST",
      body: form,
    });
    const body = (await response.json()) as {
      batch?: typeof timeImportResult;
      error?: string;
      rejected?: {
        rowNumber: number;
        reason: string;
        workerName?: string;
        sourceKey?: string;
        candidates?: { workerId: string; workerName: string; score: number }[];
      }[];
    };
    if (response.ok && body.batch) {
      setTimeImportResult(body.batch);
      setTimeImportFile(null);
      setTimeImportMappingIssues([]);
      setTimeImportMappings({});
      await load();
      window.setTimeout(
        () =>
          document
            .getElementById("time-import-result")
            ?.scrollIntoView({ behavior: "smooth", block: "center" }),
        100,
      );
    } else if (
      response.status === 409 &&
      body.error === "duplicate_import" &&
      body.batch
    ) {
      setTimeImportResult(body.batch);
      setTimeImportAlreadyProcessed(true);
      setTimeImportFile(null);
      setTimeImportMappingIssues([]);
      setTimeImportMappings({});
      setBusy(false);
      window.setTimeout(
        () =>
          document
            .getElementById("time-import-result")
            ?.scrollIntoView({ behavior: "smooth", block: "center" }),
        100,
      );
    } else {
      const mappingIssues = [
        ...new Map(
          (body.rejected ?? [])
            .filter(
              (item) =>
                item.reason === "worker_not_found" &&
                item.sourceKey &&
                item.workerName,
            )
            .map((item) => [
              item.sourceKey!,
              {
                sourceKey: item.sourceKey!,
                workerName: item.workerName!,
                candidates: item.candidates ?? [],
              },
            ]),
        ).values(),
      ];
      setTimeImportMappingIssues(mappingIssues);
      setTimeImportError(
        body.rejected?.length
          ? `${body.error}: ${body.rejected.map((item) => `${item.rowNumber} ${item.workerName ? `(${item.workerName}) ` : ""}${item.reason}`).join(", ")}`
          : (body.error ?? `HTTP ${response.status}`),
      );
      setBusy(false);
      window.setTimeout(
        () =>
          document
            .getElementById("time-import-result")
            ?.scrollIntoView({ behavior: "smooth", block: "center" }),
        100,
      );
    }
  };
  const downloadTimeImportTemplate = () => {
    const headers = [
      "FirstName",
      "MiddleName",
      "LastName",
      "InDate",
      "InTime",
      "OutDate",
      "OutTime",
      "Code",
    ];
    const blob = new Blob([`\uFEFF${headers.join(",")}\r\n`], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob),
      anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "plantilla-importacion-tiempo.csv";
    anchor.click();
    URL.revokeObjectURL(url);
  };
  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    setBusy(true);
    setError(false);
    setTimekeepingGateIssues([]);
    let response: Response;
    response = await api(`/v1/tenants/${tenant.id}/time-events`, {
      method: "POST",
      body: JSON.stringify({
        ...eventForm,
        eventAt: new Date(eventForm.eventAt).toISOString(),
        projectId: null,
        vehicleId: null,
        comment: eventForm.comment || null,
      }),
    });
    if (
      response.ok &&
      (eventForm.departmentId || eventForm.locationId || eventForm.costCenterId)
    ) {
      const created = (await response.clone().json()) as {
        event: { id: string };
      };
      const allocationResponse = await api(
        `/v1/tenants/${tenant.id}/time-events/${created.event.id}/allocation`,
        {
          method: "PUT",
          body: JSON.stringify({
            departmentId: eventForm.departmentId || null,
            locationId: eventForm.locationId || null,
            costCenterId: eventForm.costCenterId || null,
            reason:
              locale === "es"
                ? "Distribución seleccionada al registrar tiempo"
                : "Allocation selected during time entry",
          }),
        },
      );
      if (!allocationResponse.ok) response = allocationResponse;
    }
    if (response.ok) {
      setEventForm({ ...eventForm, eventAt: localNow(), comment: "" });
      setProjectForm({ code: "", name: "", status: "active" });
      setVehicleForm({ unitNumber: "", status: "active" });
      await load();
    } else {
      try {
        const body = (await response.clone().json()) as {
          error?: string;
          issues?: string[];
        };
        if (body.error === "timekeeping_stage_requirement")
          setTimekeepingGateIssues(body.issues ?? []);
      } catch {}
      setError(true);
      setBusy(false);
    }
  };
  const createSheet = async () => {
    if (!eventForm.workerId) return;
    setBusy(true);
    setError(false);
    setTimekeepingGateIssues([]);
    const response = await api(`/v1/tenants/${tenant.id}/timesheets`, {
      method: "POST",
      body: JSON.stringify({
        workerId: eventForm.workerId,
        periodStart: laborFrom,
        periodEnd: laborTo,
      }),
    });
    if (!response.ok) {
      setError(true);
      try {
        const body = (await response.json()) as {
          error?: string;
          issues?: string[];
        };
        if (body.error === "timekeeping_stage_requirement")
          setTimekeepingGateIssues(body.issues ?? []);
      } catch {}
    }
    await load();
  };
  const createFocusedSheets = async () => {
    if (!timeFocus) return;
    setBusy(true);
    setError(false);
    const periodEnd = new Date(`${timeFocus.periodEnd}T00:00:00Z`);
    for (
      let start = new Date(`${timeFocus.periodStart}T00:00:00Z`);
      start <= periodEnd;
      start = new Date(start.getTime() + 7 * 86400000)
    ) {
      const periodStart = start.toISOString().slice(0, 10),
        periodEndForSheet = new Date(
          Math.min(start.getTime() + 6 * 86400000, periodEnd.getTime()),
        )
          .toISOString()
          .slice(0, 10);
      if (
        timesheets.some(
          (sheet) =>
            sheet.workerId === timeFocus.workerId &&
            sheet.periodStart === periodStart &&
            sheet.periodEnd === periodEndForSheet,
        )
      )
        continue;
      const response = await api(`/v1/tenants/${tenant.id}/timesheets`, {
        method: "POST",
        body: JSON.stringify({
          workerId: timeFocus.workerId,
          periodStart,
          periodEnd: periodEndForSheet,
        }),
      });
      if (!response.ok) {
        setError(true);
        break;
      }
    }
    await load();
  };
  useEffect(() => {
    if (!busy && timeFocus)
      document
        .getElementById("timesheet-approval")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [busy, timeFocus]);
  const requestCorrection = async (ev: FormEvent) => {
    ev.preventDefault();
    setBusy(true);
    setError(false);
    const response = await api(`/v1/tenants/${tenant.id}/time-corrections`, {
      method: "POST",
      body: JSON.stringify({
        ...correctionForm,
        originalEventId: correctionForm.originalEventId || null,
        requestedEventAt: new Date(
          correctionForm.requestedEventAt,
        ).toISOString(),
      }),
    });
    if (response.ok)
      setCorrectionForm({
        ...correctionForm,
        originalEventId: "",
        requestedEventAt: localNow(),
        reason: "",
      });
    else setError(true);
    await load();
  };
  const moveCorrection = (
    item: TimeCorrection,
    decision: "approved" | "rejected" | "applied",
  ) => setTimeAction({ kind: "correction", item, decision, reason: "" });
  const submitDailyComment = async (ev: FormEvent) => {
    ev.preventDefault();
    setBusy(true);
    setError(false);
    const response = await api(`/v1/tenants/${tenant.id}/daily-comments`, {
      method: "POST",
      body: JSON.stringify({
        ...dailyCommentForm,
        projectId: dailyCommentForm.projectId || null,
      }),
    });
    if (response.ok) setDailyCommentForm({ ...dailyCommentForm, comment: "" });
    else setError(true);
    await load();
  };
  const reviewDailyComment = (
    item: DailyComment,
    decision: "acknowledged" | "action_required",
  ) => setTimeAction({ kind: "daily_comment", item, decision, reason: "" });
  const createPolicy = async (ev: FormEvent) => {
    ev.preventDefault();
    setBusy(true);
    setError(false);
    const response = await api(`/v1/tenants/${tenant.id}/punch-policies`, {
      method: "POST",
      body: JSON.stringify(policyForm),
    });
    if (response.ok) setPolicyForm({ ...policyForm, name: "" });
    else setError(true);
    await load();
  };
  const createNoTracking = async (ev: FormEvent) => {
    ev.preventDefault();
    setBusy(true);
    setError(false);
    const response = await api(
      `/v1/tenants/${tenant.id}/no-tracking-policies`,
      {
        method: "POST",
        body: JSON.stringify({
          ...noTrackingForm,
          effectiveTo: noTrackingForm.effectiveTo || null,
        }),
      },
    );
    if (response.ok)
      setNoTrackingForm({ ...noTrackingForm, workerId: "", reason: "" });
    else setError(true);
    await load();
  };
  const endNoTracking = (item: NoTrackingPolicy) =>
    setTimeAction({
      kind: "end_no_tracking",
      item,
      effectiveOn: today,
      reason: "",
    });
  const createWorkSchedule = async (ev: FormEvent) => {
    ev.preventDefault();
    setBusy(true);
    setError(false);
    const response = await api(`/v1/tenants/${tenant.id}/schedules`, {
      method: "POST",
      body: JSON.stringify({
        ...scheduleForm,
        punchPolicyId: scheduleForm.punchPolicyId || null,
        effectiveTo: scheduleForm.effectiveTo || null,
        shifts: ["fixed", "flexible"].includes(scheduleForm.scheduleMode)
          ? scheduleForm.shifts.map((shift, index, shifts) => {
              const [startHour, startMinute] = shift.startTime.split(":").map(Number);
              const [endHour, endMinute] = shift.endTime.split(":").map(Number);
              const start = startHour * 60 + startMinute;
              let end = endHour * 60 + endMinute;
              if (end <= start) end += 24 * 60;
              return {
                ...shift,
                sequence:
                  shifts.slice(0, index).filter(
                    (item) => item.dayOfWeek === shift.dayOfWeek,
                  ).length + 1,
                expectedPaidMinutes: Math.max(
                  1,
                  end - start - shift.mealMinutes,
                ),
              };
            })
          : [],
      }),
    });
    if (response.ok)
      setScheduleForm({
        ...scheduleForm,
        name: "",
        effectiveFrom: new Date().toISOString().slice(0, 10),
      });
    else setError(true);
    await load();
  };
  const reviewPunch = (
    item: PunchException,
    decision: "approved" | "rejected",
  ) => setTimeAction({ kind: "punch_exception", item, decision, reason: "" });
  const endWorkSchedule = (item: WorkSchedule) =>
    setTimeAction({
      kind: "end_schedule",
      item,
      effectiveTo: today < item.effectiveFrom ? item.effectiveFrom : today,
      reason: "",
    });
  const createMealAgreement = async (ev: FormEvent) => {
    ev.preventDefault();
    if (!mealAgreementForm.evidence) return;
    setBusy(true);
    setError(false);
    const form = new FormData();
    for (const [key, value] of Object.entries(mealAgreementForm))
      if (key !== "evidence") form.set(key, String(value));
    form.set("evidence", mealAgreementForm.evidence);
    const response = await api(
      `/v1/tenants/${tenant.id}/meal-period-agreements`,
      { method: "POST", body: form },
    );
    if (response.ok)
      setMealAgreementForm({
        ...mealAgreementForm,
        reason: "",
        evidence: null,
      });
    else setError(true);
    await load();
  };
  const createAlternateAgreement = async (ev: FormEvent) => {
    ev.preventDefault();
    if (!alternateForm.evidence) return;
    const schedule = alternateForm.dayHours.flatMap((hours, dayOfWeek) =>
      hours ? [{ dayOfWeek, minutes: Math.round(Number(hours) * 60) }] : [],
    );
    if (
      !schedule.length ||
      schedule.some(
        (day) =>
          !Number.isInteger(day.minutes) ||
          day.minutes < 1 ||
          day.minutes > 600,
      ) ||
      schedule.reduce((sum, day) => sum + day.minutes, 0) > 2400
    ) {
      setError(true);
      return;
    }
    setBusy(true);
    setError(false);
    const form = new FormData();
    for (const key of [
      "workerId",
      "effectiveFrom",
      "effectiveTo",
      "employeeSignedOn",
      "employerSignedOn",
      "reason",
      "legalSourceUrl",
    ] as const)
      form.set(key, alternateForm[key]);
    form.set("scheduleJson", JSON.stringify(schedule));
    form.set("evidence", alternateForm.evidence);
    const response = await api(
      `/v1/tenants/${tenant.id}/alternate-workweek-agreements`,
      { method: "POST", body: form },
    );
    if (response.ok)
      setAlternateForm({
        ...alternateForm,
        workerId: "",
        reason: "",
        evidence: null,
        dayHours: ["", "", "", "", "", ""],
      });
    else setError(true);
    await load();
  };
  const terminateAlternateAgreement = async (ev: FormEvent) => {
    ev.preventDefault();
    if (
      !alternateTerminationForm.agreementId ||
      !alternateTerminationForm.evidence
    )
      return;
    setBusy(true);
    setError(false);
    const form = new FormData();
    for (const key of ["effectiveOn", "terminationMode", "reason"] as const)
      form.set(key, alternateTerminationForm[key]);
    if (alternateTerminationForm.terminationMode === "mutual") {
      form.set("employeeSignedOn", alternateTerminationForm.employeeSignedOn);
      form.set("employerSignedOn", alternateTerminationForm.employerSignedOn);
    }
    form.set("evidence", alternateTerminationForm.evidence);
    const response = await api(
      `/v1/tenants/${tenant.id}/alternate-workweek-agreements/${alternateTerminationForm.agreementId}/terminate`,
      { method: "POST", body: form },
    );
    if (response.ok)
      setAlternateTerminationForm({
        ...alternateTerminationForm,
        agreementId: "",
        reason: "",
        evidence: null,
      });
    else setError(true);
    await load();
  };
  const createEmployerAcquisition = async (ev: FormEvent) => {
    ev.preventDefault();
    if (!acquisitionForm.evidence) return;
    setBusy(true);
    setError(false);
    const form = new FormData();
    for (const key of [
      "effectiveOn",
      "transactionType",
      "predecessorLegalName",
      "successorLegalName",
      "reason",
      "legalSourceUrl",
    ] as const)
      form.set(key, acquisitionForm[key]);
    form.set("evidence", acquisitionForm.evidence);
    const response = await api(
      `/v1/tenants/${tenant.id}/employer-acquisitions`,
      { method: "POST", body: form },
    );
    if (response.ok)
      setAcquisitionForm({
        ...acquisitionForm,
        predecessorLegalName: "",
        successorLegalName: "",
        reason: "",
        evidence: null,
      });
    else setError(true);
    await load();
  };
  const submitScheduleChange = async (ev: FormEvent) => {
    ev.preventDefault();
    const needsSchedule = ["schedule", "hours", "multiple"].includes(
      scheduleChangeForm.changeType,
    );
    const requiresShifts =
      needsSchedule &&
      ["fixed", "flexible"].includes(scheduleChangeForm.scheduleMode);
    if (
      (needsSchedule && !scheduleChangeForm.proposalName) ||
      (requiresShifts && !scheduleChangeForm.shifts.length) ||
      (scheduleChangeForm.priorityBasis === "certified_informal_caregiver" &&
        !scheduleChangeForm.caregiverEvidence)
    ) {
      setError(true);
      return;
    }
    setBusy(true);
    setError(false);
    const form = new FormData();
    for (const key of [
      "changeType",
      "requestedDetails",
      "requestedEffectiveOn",
      "requestedThrough",
      "reason",
      "priorityBasis",
    ] as const)
      form.set(key, scheduleChangeForm[key]);
    if (needsSchedule)
      form.set(
        "proposedScheduleJson",
        JSON.stringify({
          name: scheduleChangeForm.proposalName,
          scheduleMode: scheduleChangeForm.scheduleMode,
          punchPolicyId: scheduleChangeForm.punchPolicyId || null,
          shifts: requiresShifts ? scheduleChangeForm.shifts : [],
        }),
      );
    if (scheduleChangeForm.caregiverEvidence)
      form.set("caregiverEvidence", scheduleChangeForm.caregiverEvidence);
    const response = await api(
      `/v1/tenants/${tenant.id}/schedule-change-requests`,
      { method: "POST", body: form },
    );
    if (response.ok)
      setScheduleChangeForm({
        ...scheduleChangeForm,
        requestedDetails: "",
        requestedEffectiveOn: "",
        requestedThrough: "",
        reason: "",
        caregiverEvidence: null,
        proposalName: "",
        shifts: [],
      });
    else setError(true);
    await load();
  };
  const withdrawScheduleChange = (item: ScheduleChangeRequest) =>
    setTimeAction({ kind: "withdraw_schedule", item, reason: "" });
  const respondScheduleChange = async (ev: FormEvent) => {
    ev.preventDefault();
    if (!scheduleResponseForm.requestId) return;
    setBusy(true);
    setError(false);
    const response = await api(
      `/v1/tenants/${tenant.id}/schedule-change-requests/${scheduleResponseForm.requestId}/respond`,
      {
        method: "POST",
        body: JSON.stringify({
          ...scheduleResponseForm,
          requestId: undefined,
          meetingOn: scheduleResponseForm.meetingOn || "",
          conditions: scheduleResponseForm.conditions || undefined,
          denialReason:
            scheduleResponseForm.decision === "denied"
              ? scheduleResponseForm.denialReason
              : undefined,
          alternative:
            scheduleResponseForm.decision === "denied"
              ? scheduleResponseForm.alternative
              : undefined,
        }),
      },
    );
    if (response.ok)
      setScheduleResponseForm({
        requestId: "",
        decision: "approved",
        meetingOn: "",
        writtenResponse: "",
        conditions: "",
        denialReason: "",
        alternative: "",
      });
    else setError(true);
    await load();
  };
  const resolveMeal = (
    workerId: string,
    workDate: string,
    issueKind: "first_meal" | "second_meal",
    allowOmission: boolean,
  ) =>
    setTimeEdit({
      kind: "meal",
      workerId,
      workDate,
      issueKind,
      allowOmission,
      resolutionType: "penalty_due",
      penaltyMinutes: "60",
      reason: "",
    });
  const createCategorizedEntry = async (ev: FormEvent) => {
    ev.preventDefault();
    const sheet = timesheets.find(
        (item) => item.id === categorizedForm.timesheetId,
      ),
      minutes = Number(categorizedForm.minutes);
    if (!sheet || !Number.isInteger(minutes) || minutes < 1 || minutes > 1440)
      return setError(true);
    setBusy(true);
    setError(false);
    const response = await api(
      `/v1/tenants/${tenant.id}/timesheets/${sheet.id}/time-entries`,
      {
        method: "POST",
        body: JSON.stringify({
          workDate: categorizedForm.workDate,
          category: categorizedForm.category,
          minutes,
          description: categorizedForm.description || null,
          projectId: null,
          vehicleId: null,
          departmentId: null,
          locationId: null,
          costCenterId: null,
        }),
      },
    );
    if (response.ok)
      setCategorizedForm({ ...categorizedForm, minutes: "", description: "" });
    else setError(true);
    await load();
  };
  const voidCategorizedEntry = (item: CategorizedTimeEntryView) =>
    setTimeAction({ kind: "void_entry", item, reason: "" });
  const moveSheet = (item: Timesheet, status: string) =>
    setTimeAction({ kind: "timesheet", item, status, reason: "" });
  const viewTimesheet = (sheet: Timesheet) => {
    setViewingTimesheetId(sheet.id);
    setLaborFrom(sheet.periodStart);
    setLaborTo(sheet.periodEnd);
    requestAnimationFrame(() =>
      document
        .getElementById("timesheet-detail")
        ?.scrollIntoView({ behavior: "smooth", block: "start" }),
    );
  };
  const correctTimeEvent = (event: TimeEvent) => {
    setCorrectionForm((current) => ({
      ...current,
      workerId: event.workerId,
      originalEventId: event.id,
      requestedEventType: event.eventType,
      requestedEventAt: new Date(event.eventAt).toISOString().slice(0, 16),
      reason: "",
    }));
    requestAnimationFrame(() =>
      document
        .getElementById("time-corrections")
        ?.scrollIntoView({ behavior: "smooth", block: "start" }),
    );
  };
  const timesheetActions = (sheet: Timesheet) => (
    <div className="run-actions">
      <button type="button" onClick={() => viewTimesheet(sheet)}>
        {locale === "es" ? "Ver hoja" : "View timesheet"}
      </button>
      {["open", "rejected"].includes(sheet.status) &&
        (roleKey === "user" || canConfigure) && (
          <button
            type="button"
            disabled={viewingTimesheetId !== sheet.id}
            title={
              viewingTimesheetId !== sheet.id
                ? locale === "es"
                  ? "Revise la hoja antes de someterla"
                  : "Review the timesheet before submitting it"
                : undefined
            }
            onClick={() => void moveSheet(sheet, "submitted")}
          >
            {locale === "es" ? "Someter" : "Submit"}
          </button>
        )}
      {sheet.status === "submitted" &&
        roleKey !== "user" &&
        sheet.workerId !== actorWorkerId && (
          <>
            <button
              type="button"
              onClick={() => void moveSheet(sheet, "supervisor_approved")}
            >
              {locale === "es" ? "Aprobar supervisor" : "Supervisor approve"}
            </button>
            <button
              type="button"
              className="danger-action"
              onClick={() => void moveSheet(sheet, "rejected")}
            >
              {locale === "es" ? "Rechazar" : "Reject"}
            </button>
          </>
        )}
      {sheet.status === "supervisor_approved" && canConfigure && (
        <button
          type="button"
          onClick={() => void moveSheet(sheet, "payroll_approved")}
        >
          {locale === "es" ? "Aprobar nómina" : "Payroll approve"}
        </button>
      )}
      {sheet.status === "payroll_approved" && canConfigure && (
        <button type="button" onClick={() => void moveSheet(sheet, "locked")}>
          {locale === "es" ? "Bloquear período" : "Lock period"}
        </button>
      )}
      {sheet.status === "locked" &&
        canConfigure &&
        sheet.workerId !== actorWorkerId && (
          <button type="button" onClick={() => void moveSheet(sheet, "open")}>
            {locale === "es"
              ? "Reabrir y recalcular"
              : "Reopen and recalculate"}
          </button>
        )}
    </div>
  );
  const punchExceptionActions = (item: PunchException) =>
    item.status === "pending_review" &&
    roleKey !== "user" &&
    item.workerId !== actorWorkerId ? (
      <div className="run-actions">
        <button
          type="button"
          onClick={() => void reviewPunch(item, "approved")}
        >
          {locale === "es" ? "Aprobar" : "Approve"}
        </button>
        <button
          type="button"
          className="danger-action"
          onClick={() => void reviewPunch(item, "rejected")}
        >
          {locale === "es" ? "Rechazar" : "Reject"}
        </button>
      </div>
    ) : null;
  const correctionActions = (item: TimeCorrection) => (
    <div className="run-actions">
      {item.status === "requested" &&
        roleKey !== "user" &&
        item.workerId !== actorWorkerId && (
          <>
            <button
              type="button"
              onClick={() => void moveCorrection(item, "approved")}
            >
              {locale === "es" ? "Aprobar" : "Approve"}
            </button>
            <button
              type="button"
              className="danger-action"
              onClick={() => void moveCorrection(item, "rejected")}
            >
              {locale === "es" ? "Rechazar" : "Reject"}
            </button>
          </>
        )}
      {item.status === "approved" &&
        roleKey !== "user" &&
        item.workerId !== actorWorkerId && (
          <button
            type="button"
            onClick={() => void moveCorrection(item, "applied")}
          >
            {locale === "es" ? "Aplicar" : "Apply"}
          </button>
        )}
    </div>
  );
  const dailyCommentActions = (item: DailyComment) =>
    item.status === "submitted" &&
    roleKey !== "user" &&
    item.workerId !== actorWorkerId ? (
      <div className="run-actions">
        <button
          type="button"
          onClick={() => void reviewDailyComment(item, "acknowledged")}
        >
          {locale === "es" ? "Reconocer" : "Acknowledge"}
        </button>
        <button
          type="button"
          onClick={() => void reviewDailyComment(item, "action_required")}
        >
          {locale === "es" ? "Requiere acción" : "Action required"}
        </button>
      </div>
    ) : null;
  const createAssignment = async (ev: FormEvent) => {
    ev.preventDefault();
    setBusy(true);
    setError(false);
    const response = await api(
      `/v1/tenants/${tenant.id}/field-resource-assignments`,
      {
        method: "POST",
        body: JSON.stringify({
          ...assignmentForm,
          effectiveTo: assignmentForm.effectiveTo || null,
        }),
      },
    );
    if (response.ok)
      setAssignmentForm({
        ...assignmentForm,
        resourceId: "",
        effectiveTo: "",
        reason: "",
      });
    else setError(true);
    await load();
  };
  const closeAssignment = (item: FieldAssignment) =>
    setTimeAction({
      kind: "close_assignment",
      item,
      effectiveTo: today,
      reason: "",
    });
  const editProject = (item: Project) =>
    setTimeEdit({
      kind: "project",
      item,
      name: item.name,
      code: item.code ?? "",
      status: item.status as "active" | "inactive" | "closed",
      reason: "",
    });
  const editVehicle = (item: Vehicle) =>
    setTimeEdit({
      kind: "vehicle",
      item,
      unitNumber: item.unitNumber,
      status: item.status as "active" | "inactive" | "retired",
      notes: item.notes ?? "",
      reason: "",
    });
  const submitTimeEdit = async (ev: FormEvent) => {
    ev.preventDefault();
    const draft = timeEdit;
    if (!draft) return;
    setBusy(true);
    setError(false);
    let path: string,
      body: unknown,
      method = "POST";
    if (draft.kind === "meal") {
      const penaltyMinutes =
        draft.resolutionType === "penalty_due"
          ? Number(draft.penaltyMinutes)
          : 0;
      if (
        draft.resolutionType === "penalty_due" &&
        (!Number.isInteger(penaltyMinutes) ||
          penaltyMinutes < 1 ||
          penaltyMinutes > 60)
      ) {
        setError(true);
        setBusy(false);
        return;
      }
      path = "meal-period-resolutions";
      body = {
        workerId: draft.workerId,
        workDate: draft.workDate,
        issueKind: draft.issueKind,
        resolutionType: draft.resolutionType,
        penaltyMinutes,
        reason: draft.reason,
      };
    } else if (draft.kind === "project") {
      path = `projects/${draft.item.id}`;
      method = "PUT";
      body = {
        name: draft.name,
        code: draft.code || null,
        status: draft.status,
        reason: draft.reason,
      };
    } else {
      path = `vehicles/${draft.item.id}`;
      method = "PUT";
      body = {
        unitNumber: draft.unitNumber,
        status: draft.status,
        notes: draft.notes || null,
        reason: draft.reason,
      };
    }
    const response = await api(`/v1/tenants/${tenant.id}/${path}`, {
      method,
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      setError(true);
      setBusy(false);
      return;
    }
    setTimeEdit(null);
    await load();
  };
  const submitTimeAction = async (ev: FormEvent) => {
    ev.preventDefault();
    const action = timeAction;
    if (!action) return;
    setBusy(true);
    setError(false);
    setTimeActionFailure("");
    let path: string,
      method = "POST",
      body: unknown;
    if (action.kind === "correction") {
      path = `time-corrections/${action.item.id}/${action.decision === "applied" ? "apply" : "review"}`;
      body =
        action.decision === "applied"
          ? { reason: action.reason }
          : { decision: action.decision, reason: action.reason };
    } else if (action.kind === "daily_comment") {
      path = `daily-comments/${action.item.id}/review`;
      body = { status: action.decision, comment: action.reason };
    } else if (action.kind === "end_no_tracking") {
      path = `no-tracking-policies/${action.item.id}/end`;
      body = { effectiveOn: action.effectiveOn, reason: action.reason };
    } else if (action.kind === "end_schedule") {
      path = `schedules/${action.item.id}/end`;
      body = { effectiveTo: action.effectiveTo, reason: action.reason };
    } else if (action.kind === "punch_exception") {
      path = `punch-exceptions/${action.item.id}/review`;
      body = { decision: action.decision, comment: action.reason };
    } else if (action.kind === "withdraw_schedule") {
      path = `schedule-change-requests/${action.item.id}/withdraw`;
      body = { reason: action.reason };
    } else if (action.kind === "void_entry") {
      path = `time-entries/${action.item.id}/void`;
      body = { reason: action.reason };
    } else if (action.kind === "timesheet") {
      path = `timesheets/${action.item.id}/status`;
      body = { status: action.status, reason: action.reason };
    } else {
      path = `field-resource-assignments/${action.item.id}/close`;
      method = "PUT";
      body = { effectiveTo: action.effectiveTo, reason: action.reason };
    }
    const response = await api(`/v1/tenants/${tenant.id}/${path}`, {
      method,
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const failure = (await response
        .clone()
        .json()
        .catch(() => null)) as {
        error?: string | { code?: string; requestId?: string };
        code?: string;
        requestId?: string;
      } | null;
      const nested =
        failure?.error && typeof failure.error === "object"
          ? failure.error
          : null;
      setTimeActionFailure(
        `${typeof failure?.error === "string" ? failure.error : (nested?.code ?? failure?.code ?? "request_failed")} · HTTP ${response.status}${(nested?.requestId ?? failure?.requestId) ? ` · ${nested?.requestId ?? failure?.requestId}` : ""}`,
      );
      setError(true);
      setBusy(false);
      return;
    }
    setTimeAction(null);
    await load();
  };
  const timeActionCopy = timeAction
    ? (
        {
          correction: [
            "Decidir corrección de tiempo",
            "Decide time correction",
          ],
          daily_comment: ["Revisar comentario diario", "Review daily comment"],
          end_no_tracking: [
            "Terminar política sin registro",
            "End no-tracking policy",
          ],
          end_schedule: ["Terminar horario vigente", "End current schedule"],
          punch_exception: [
            "Decidir excepción de ponche",
            "Decide punch exception",
          ],
          withdraw_schedule: [
            "Retirar solicitud de horario",
            "Withdraw schedule request",
          ],
          void_entry: ["Anular entrada de tiempo", "Void time entry"],
          timesheet: [
            "Cambiar estado de hoja de tiempo",
            "Change timesheet status",
          ],
          close_assignment: [
            "Cerrar asignación de campo",
            "Close field assignment",
          ],
        } as const
      )[timeAction.kind]
    : null;
  const timeActionSubject = timeAction
    ? timeAction.kind === "correction" ||
      timeAction.kind === "daily_comment" ||
      timeAction.kind === "punch_exception"
      ? timeAction.item.workerName
      : timeAction.kind === "end_no_tracking"
        ? timeAction.item.workerName
        : timeAction.kind === "end_schedule"
          ? `${timeAction.item.workerName} · ${timeAction.item.name}`
          : timeAction.kind === "withdraw_schedule"
            ? timeAction.item.requestedDetails
            : timeAction.kind === "void_entry"
              ? `${formatDate(timeAction.item.workDate, locale)} · ${timeAction.item.minutes} min`
              : timeAction.kind === "timesheet"
                ? `${timeAction.item.workerName} · ${formatDateRange(timeAction.item.periodStart, timeAction.item.periodEnd, locale, "—")}`
                : `${timeAction.item.workerName} · ${timeAction.item.projectName ?? timeAction.item.vehicleUnit ?? "—"}`
    : "";
  const eventDate = eventForm.eventAt.slice(0, 10),
    effectiveAssignments = fieldAssignments.filter(
      (item) =>
        item.workerId === eventForm.workerId &&
        item.status === "active" &&
        item.effectiveFrom <= eventDate &&
        (!item.effectiveTo || item.effectiveTo >= eventDate),
    ),
    projectAssignmentIds = new Set(
      effectiveAssignments.flatMap((item) =>
        item.projectId ? [item.projectId] : [],
      ),
    ),
    vehicleAssignmentIds = new Set(
      effectiveAssignments.flatMap((item) =>
        item.vehicleId ? [item.vehicleId] : [],
      ),
    ),
    visibleProjects = projects.filter(
      (item) =>
        item.status === "active" &&
        (!projectAssignmentIds.size || projectAssignmentIds.has(item.id)),
    ),
    visibleVehicles = vehicles.filter(
      (item) =>
        item.status === "active" &&
        (!vehicleAssignmentIds.size || vehicleAssignmentIds.has(item.id)),
    );
  const categorizedLabels: Record<
    CategorizedTimeEntryView["category"],
    [string, string]
  > = {
    regular_work: ["Trabajo regular", "Regular work"],
    training: ["Adiestramiento", "Training"],
    break_paid: ["Descanso pagado", "Paid break"],
    break_unpaid: ["Descanso no pagado", "Unpaid break"],
    travel: ["Viaje", "Travel"],
    other_paid: ["Otro tiempo pagado", "Other paid time"],
    other_unpaid: ["Otro tiempo no pagado", "Other unpaid time"],
  };
  const activeOwnNoTracking = noTrackingPolicies.find(
    (item) =>
      item.workerId === actorWorkerId &&
      item.effectiveFrom <= today &&
      (!item.effectiveTo || item.effectiveTo >= today) &&
      (!item.terminatedEffectiveOn || item.terminatedEffectiveOn > today),
  );
  const workspaceWorkers =
    workspace === "person_setup" && actorWorkerId
      ? workers.filter((item) => item.id === actorWorkerId)
      : workers;
  const entryPeriodRuns = eventForm.workerId
    ? gridRuns.filter(
        (run) =>
          !timesheets.some(
            (sheet) =>
              sheet.workerId === eventForm.workerId &&
              sheet.periodEnd >= run.periodStart &&
              sheet.periodStart <= run.periodEnd &&
              [
                "submitted",
                "supervisor_approved",
                "payroll_approved",
                "locked",
              ].includes(sheet.status),
          ),
      )
    : gridRuns;
  const gridWorker = workers.find((item) => item.id === eventForm.workerId),
    gridUsesScheduledAttendance = gridWorker?.timeEntryMethod === "none";
  const pendingTimesheets = timesheets.filter(
      (sheet) => sheet.status !== "locked",
    ),
    focusedApprovalRunId = timeFocus
      ? gridRuns.find(
          (run) =>
            run.periodStart === timeFocus.periodStart &&
            run.periodEnd === timeFocus.periodEnd,
        )?.id
      : null,
    approvalWorkers = workers.filter((worker) =>
      approvalCandidates.some(
        (candidate) =>
          candidate.workerId === worker.id &&
          (!focusedApprovalRunId ||
            candidate.payrollRunId === focusedApprovalRunId),
      ),
    ),
    approvalPeriodOptions = gridRuns.filter((run) =>
      approvalCandidates.some(
        (candidate) =>
          candidate.workerId === approvalWorkerId &&
          candidate.payrollRunId === run.id,
      ),
    ),
    approvalRun = approvalPeriodOptions.find((run) => run.id === approvalRunId),
    approvalWorker = workers.find((worker) => worker.id === approvalWorkerId),
    approvalIsContractor =
      approvalWorker?.workerType === "professional_services",
    selectedApprovalSheets = approvalRun
      ? pendingTimesheets.filter(
          (sheet) =>
            sheet.workerId === approvalWorkerId &&
            sheet.periodEnd >= approvalRun.periodStart &&
            sheet.periodStart <= approvalRun.periodEnd,
        )
      : [],
    approvalComments = approvalRun
      ? dailyComments.filter(
          (item) =>
            item.workerId === approvalWorkerId &&
            item.workDate >= approvalRun.periodStart &&
            item.workDate <= approvalRun.periodEnd,
        )
      : [],
    approvalAlerts = approvalEvaluations
      .flatMap((evaluation, evaluationIndex) => [
        ...evaluation.eventIssues.map((issue, index) => {
          const parts = issue.split(":"),
            code = parts[0] ?? issue,
            date = parts[1],
            detail = parts.slice(2).join(":"),
            scheduleIssue = code === "flexible_core_hours_missing",
            classificationIssue = code.includes("overtime_classification");
          return {
            key: `event-${evaluationIndex}-${index}-${issue}`,
            issueCode: code,
            issueDetail: detail || null,
            kind: scheduleIssue
              ? "schedule"
              : classificationIssue
                ? "classification"
                : "time",
            title:
              code === "open_clock_in"
                ? locale === "es"
                  ? "Falta la hora de salida"
                  : "Missing clock-out"
                : code === "unpaired_clock_out"
                  ? locale === "es"
                    ? "Falta la hora de entrada"
                    : "Missing clock-in"
                  : code === "duplicate_clock_in"
                    ? locale === "es"
                      ? "Hay una entrada duplicada"
                      : "Duplicate clock-in"
                    : scheduleIssue
                      ? locale === "es"
                        ? "El horario trabajado no cubre el horario configurado"
                        : "Worked time does not cover the configured schedule"
                      : classificationIssue
                        ? locale === "es"
                          ? "Falta revisar la clasificación de horas extra"
                          : "Overtime classification needs review"
                        : locale === "es"
                          ? "Evento de tiempo requiere revisión"
                          : "Time event needs review",
            detail: date
              ? `${formatDate(date.slice(0, 10), locale)}${detail ? ` · ${detail}` : ""}`
              : locale === "es"
                ? "Revise la hoja antes de aprobar."
                : "Review the timesheet before approving.",
            workDate: date?.slice(0, 10) ?? null,
            blocking: !scheduleIssue,
          };
        }),
        ...evaluation.days.flatMap((day) => {
          const items: {
            key: string;
            issueCode: string;
            issueDetail: string | null;
            kind: string;
            title: string;
            detail: string;
            workDate: string;
            blocking: boolean;
          }[] = [];
          if (day.mealIssueUnresolved)
            items.push({
              key: `meal-${day.workDate}`,
              issueCode: "first_meal",
              issueDetail: null,
              kind: "first_meal",
              title:
                locale === "es"
                  ? "Período de comida sin resolver"
                  : "Unresolved meal period",
              detail: `${formatDate(day.workDate, locale)} · ${day.mealMinutes} min`,
              workDate: day.workDate,
              blocking: true,
            });
          if (day.secondMealReviewUnresolved)
            items.push({
              key: `second-meal-${day.workDate}`,
              issueCode: "second_meal",
              issueDetail: null,
              kind: "second_meal",
              title:
                locale === "es"
                  ? "Revisión de segunda comida"
                  : "Second-meal review",
              detail: `${formatDate(day.workDate, locale)} · ${(day.workedMinutes / 60).toFixed(2)} h`,
              workDate: day.workDate,
              blocking: true,
            });
          return items;
        }),
      ])
      .filter(
        (alert) =>
          (!approvalIsContractor ||
            (!alert.issueCode.includes("overtime_classification") &&
              !["first_meal", "second_meal"].includes(alert.issueCode))) &&
          !approvalResolutions.some(
            (resolution) =>
              resolution.issueCode === alert.issueCode &&
              resolution.workDate === alert.workDate,
          ),
      ),
    approvalAlertCount = approvalAlerts.length,
    approvalAttendanceMinutes = approvalRows.reduce(
      (total, row) => total + row.payableMinutes,
      0,
    ),
    approvalAttendanceDays = approvalRows.filter(
      (row) => row.attendanceStatus === "scheduled",
    ).length,
    approvalBlockingAlertCount = approvalAlerts.filter(
      (alert) => alert.blocking,
    ).length,
    approvalTarget = !selectedApprovalSheets.length
      ? "submitted"
      : selectedApprovalSheets.some((sheet) =>
            ["open", "rejected"].includes(sheet.status),
          )
        ? "submitted"
        : selectedApprovalSheets.some((sheet) => sheet.status === "submitted")
          ? "supervisor_approved"
          : selectedApprovalSheets.some(
                (sheet) => sheet.status === "supervisor_approved",
              )
            ? "payroll_approved"
            : selectedApprovalSheets.some(
                  (sheet) => sheet.status === "payroll_approved",
                )
              ? "locked"
              : null;
  const approvalActionSheet = approvalTarget
      ? selectedApprovalSheets
          .filter((sheet) =>
            approvalTarget === "submitted"
              ? ["open", "rejected"].includes(sheet.status)
              : approvalTarget === "supervisor_approved"
                ? sheet.status === "submitted"
                : approvalTarget === "payroll_approved"
                  ? sheet.status === "supervisor_approved"
                  : sheet.status === "payroll_approved",
          )
          .sort((a, b) => a.periodStart.localeCompare(b.periodStart))[0]
      : null,
    approvalActionStart = approvalActionSheet?.periodStart ?? approvalRun?.periodStart,
    approvalActionEnd = approvalActionSheet?.periodEnd ??
      (approvalActionStart && approvalRun
        ? new Date(
            Math.min(
              new Date(`${approvalActionStart}T00:00:00Z`).getTime() +
                6 * 86400000,
              new Date(`${approvalRun.periodEnd}T00:00:00Z`).getTime(),
            ),
          )
            .toISOString()
            .slice(0, 10)
        : undefined),
    approvalActionRange =
      approvalActionStart && approvalActionEnd
        ? formatDateRange(
            approvalActionStart,
            approvalActionEnd,
            locale,
            "—",
          )
        : "—";
  const editApprovalTimesheet = () => {
    if (!approvalRun || !approvalWorkerId) return;
    setEventForm((current) => ({
      ...current,
      workerId: approvalWorkerId,
    }));
    setGridRunId(approvalRun.id);
    requestAnimationFrame(() =>
      document
        .querySelector(".time-entry-grid")
        ?.scrollIntoView({ behavior: "smooth", block: "start" }),
    );
  };
  const openApprovalPersonSetup = (kind: "overtime" | "pay") => {
    if (!approvalWorkerId) return;
    sessionStorage.setItem("personnel-setup-worker", approvalWorkerId);
    sessionStorage.setItem("personnel-setup-kind", kind);
    window.history.pushState({}, "", sectionPaths.personnel_setup);
    window.dispatchEvent(new PopStateEvent("popstate"));
  };
  const acceptApprovalScheduleAlert = async (alert: {
    issueCode: string;
    issueDetail: string | null;
    workDate: string | null;
  }) => {
    if (!approvalRun || !approvalWorkerId || !alert.workDate) return;
    setBusy(true);
    setApprovalMessage("");
    const response = await api(
      `/v1/tenants/${tenant.id}/time-review-resolutions`,
      {
        method: "POST",
        body: JSON.stringify({
          payrollRunId: approvalRun.id,
          workerId: approvalWorkerId,
          issueCode: alert.issueCode,
          workDate: alert.workDate,
          issueDetail: alert.issueDetail,
        }),
      },
    );
    if (response.ok) {
      const body = (await response.json()) as {
        resolution: {
          id: string;
          issueCode: string;
          workDate: string;
          issueDetail: string | null;
          resolution: string;
        };
      };
      setApprovalResolutions((current) => [...current, body.resolution]);
      setApprovalMessage(
        locale === "es"
          ? "El horario registrado fue aceptado y la decisión quedó auditada."
          : "The recorded schedule was accepted and the decision was audited.",
      );
    } else {
      setApprovalMessage(
        locale === "es"
          ? "No se pudo guardar la aceptación. Intente nuevamente."
          : "The acceptance could not be saved. Try again.",
      );
    }
    setBusy(false);
  };
  const saveApprovalSelection = async () => {
    if (!approvalTarget || !approvalRun || !approvalWorkerId) return;
    let sheets = selectedApprovalSheets;
    setBusy(true);
    setApprovalMessage("");
    if (!sheets.length) {
      const latestResponse = await api(`/v1/tenants/${tenant.id}/timesheets`);
      if (latestResponse.ok) {
        const latestTimesheets = (
          (await latestResponse.json()) as { timesheets: Timesheet[] }
        ).timesheets;
        setTimesheets(latestTimesheets);
        const latestPeriodSheets = latestTimesheets.filter(
          (sheet) =>
            sheet.workerId === approvalWorkerId &&
            sheet.periodEnd >= approvalRun.periodStart &&
            sheet.periodStart <= approvalRun.periodEnd,
        );
        const actionableSheets = latestPeriodSheets.filter(
          (sheet) => sheet.status !== "locked",
        );
        if (latestPeriodSheets.length > 0 && actionableSheets.length === 0) {
          const candidatesResponse = await api(
            `/v1/tenants/${tenant.id}/time-approval-candidates`,
          );
          if (candidatesResponse.ok)
            setApprovalCandidates(
              (
                (await candidatesResponse.json()) as {
                  candidates: { workerId: string; payrollRunId: string }[];
                }
              ).candidates,
            );
          setApprovalWorkerId("");
          setApprovalRunId("");
          setApprovalMessage(
            locale === "es"
              ? "Este período ya fue aprobado y cerrado. Se retiró de las hojas pendientes."
              : "This period was already approved and closed. It was removed from pending timesheets.",
          );
          setBusy(false);
          return;
        }
        if (actionableSheets.length > 0) sheets = actionableSheets;
      }
    }
    if (!sheets.length) {
      const created: Timesheet[] = [];
      const periodEnd = new Date(`${approvalRun.periodEnd}T00:00:00Z`);
      for (
        let start = new Date(`${approvalRun.periodStart}T00:00:00Z`);
        start <= periodEnd;
        start = new Date(start.getTime() + 7 * 86400000)
      ) {
        const periodStart = start.toISOString().slice(0, 10),
          periodEndForSheet = new Date(
            Math.min(start.getTime() + 6 * 86400000, periodEnd.getTime()),
          )
            .toISOString()
            .slice(0, 10),
          response = await api(`/v1/tenants/${tenant.id}/timesheets`, {
            method: "POST",
            body: JSON.stringify({
              workerId: approvalWorkerId,
              periodStart,
              periodEnd: periodEndForSheet,
            }),
          });
        if (!response.ok) {
          setApprovalMessage(
            locale === "es"
              ? "No se pudo preparar la aprobación del período. Revise la información de la persona."
              : "The period approval could not be prepared. Review the person's information.",
          );
          setBusy(false);
          return;
        }
        const body = (await response.json()) as { timesheet: Timesheet };
        created.push({
          ...body.timesheet,
          leaveSegments: [],
          categorizedEntries: [],
        });
      }
      sheets = created;
    }
    const eligible = sheets
      .filter((sheet) =>
        approvalTarget === "submitted"
          ? ["open", "rejected"].includes(sheet.status)
          : approvalTarget === "supervisor_approved"
            ? sheet.status === "submitted"
            : approvalTarget === "payroll_approved"
              ? sheet.status === "supervisor_approved"
              : sheet.status === "payroll_approved",
      )
      .sort((a, b) => a.periodStart.localeCompare(b.periodStart))
      .slice(0, 1);
    for (const sheet of eligible) {
      const response = await api(
        `/v1/tenants/${tenant.id}/timesheets/${sheet.id}/status`,
        {
          method: "POST",
          body: JSON.stringify({
            status: approvalTarget,
            reason:
              locale === "es"
                ? "Confirmado desde la revisión guiada del período"
                : "Confirmed from the guided period review",
          }),
        },
      );
      if (!response.ok) {
        const failure = (await response.json().catch(() => null)) as null | {
          error?: string;
          periodEnd?: string;
          balances?: {
            policyName: string;
            balanceHours: number;
            shortageHours: number;
          }[];
        };
        setApprovalMessage(
          failure?.error === "pay_rate_required"
            ? locale === "es"
              ? `No se puede aprobar para compensación. Esta persona no tiene una tarifa vigente para ${failure.periodEnd ?? approvalRun.periodEnd}. Registre la tarifa en su perfil antes de aprobar la hoja.`
              : `Cannot approve for compensation. This person has no effective pay rate for ${failure.periodEnd ?? approvalRun.periodEnd}. Record the rate in the worker profile before approving the timesheet.`
            : failure?.error === "leave_balance_insufficient"
              ? locale === "es"
                ? `No se puede aprobar para compensación. ${failure.balances?.map((item) => `${item.policyName}: balance ${item.balanceHours.toFixed(2)} h; faltan ${item.shortageHours.toFixed(2)} h`).join(" · ") ?? "El balance de licencia es insuficiente."} al ${failure.periodEnd ?? approvalRun.periodEnd}. Corrija la solicitud o el libro de licencias de esta persona.`
                : `Cannot approve for compensation. ${failure.balances?.map((item) => `${item.policyName}: ${item.balanceHours.toFixed(2)} h balance; ${item.shortageHours.toFixed(2)} h short`).join(" · ") ?? "The leave balance is insufficient."} as of ${failure.periodEnd ?? approvalRun.periodEnd}. Correct this person's request or leave ledger.`
              : locale === "es"
                ? "No se pudo completar la transición. Revise las alertas de la hoja."
                : "The transition could not be completed. Review the timesheet alerts.",
        );
        setBusy(false);
        return;
      }
    }
    setApprovalMessage(
      locale === "es"
        ? "La aprobación fue guardada y auditada."
        : "The approval was saved and audited.",
    );
    await load();
    const candidatesResponse = await api(
      `/v1/tenants/${tenant.id}/time-approval-candidates`,
    );
    if (candidatesResponse.ok) {
      const candidates = (
        (await candidatesResponse.json()) as {
          candidates: { workerId: string; payrollRunId: string }[];
        }
      ).candidates;
      setApprovalCandidates(candidates);
      const periodStillPending = candidates.some(
          (candidate) =>
            candidate.workerId === approvalWorkerId &&
            candidate.payrollRunId === approvalRun.id,
        );
      if (!periodStillPending) {
        setApprovalWorkerId("");
        setApprovalRunId("");
        if (approvalTarget === "locked") {
          setEventForm((current) => ({ ...current, workerId: "" }));
          setGridRows([]);
          setGridComment("");
          setError(false);
          setGridMessage(
            locale === "es"
              ? "La hoja fue aprobada y cerrada. Seleccione la próxima persona para continuar con este período."
              : "The timesheet was approved and closed. Select the next person to continue this period.",
          );
          window.setTimeout(
            () =>
              document
                .getElementById("time-entry-period")
                ?.scrollIntoView({ behavior: "smooth", block: "start" }),
            250,
          );
        }
      }
    }
    setBusy(false);
  };
  return (
    <div
      className={`content timekeeping-workspace timekeeping-workspace-${workspace}${showOtherTimeOptions ? " show-time-other-options" : ""}`}
    >
      {activeOwnNoTracking && (
        <section className="operation-panel">
          <strong>
            {locale === "es"
              ? "Política vigente sin registro de tiempo"
              : "Current no-tracking policy"}
          </strong>
          <p>
            {locale === "es"
              ? "Su clasificación aprobada no usa ponches ni hojas durante este período. Los intentos de registrar tiempo son bloqueados por el servidor."
              : "Your approved classification does not use punches or timesheets during this period. The server blocks attempts to record time."}
          </p>
          <small>
            {formatDateRange(
              activeOwnNoTracking.effectiveFrom,
              activeOwnNoTracking.effectiveTo,
              locale,
              locale === "es" ? "abierto" : "open",
            )}{" "}
            · {activeOwnNoTracking.reason}
          </small>
        </section>
      )}
      <div className="section-heading">
        <div>
          <p className="kicker">{tenant.displayName}</p>
          <h1>
            {roleKey === "user"
              ? workerType === "professional_services"
                ? locale === "es"
                  ? "Mis servicios y tiempo"
                  : "My services and time"
                : locale === "es"
                  ? "Mi tiempo"
                  : "My time"
              : locale === "es"
                ? "Tiempo y operaciones"
                : "Time & operations"}
          </h1>
          <p className="lead">
            {roleKey === "user" && workerType === "professional_services"
              ? locale === "es"
                ? "Registre sus horas de servicio, comentarios y correcciones."
                : "Record your service hours, comments, and corrections."
              : locale === "es"
                ? roleKey === "user"
                  ? "Registre sus ponches, comentarios, correcciones y hoja de tiempo."
                  : "Registre y revise las horas realmente trabajadas, sus correcciones y las hojas de tiempo."
                : roleKey === "user"
                  ? "Record your punches, comments, corrections, and timesheet."
                  : "Record and review actual hours worked, corrections, and timesheets."}
          </p>
        </div>
      </div>
      {roleKey !== "user" && (
        <section
          id="time-entry-period"
          className="settings-form compact-form time-entry-grid time-workflow-step time-workflow-step-1"
        >
          <div className="section-heading">
            <div>
              <h2>
                {locale === "es"
                  ? "1) Registrar horas del período"
                  : "1) Enter period hours"}
              </h2>
              <p>
                {locale === "es"
                  ? "Seleccione la persona y complete una fila por día. Guardar prepara la hoja, pero no la aprueba; después continuará en la sección 3."
                  : "Select the person and complete one row per day. Saving prepares the timesheet but does not approve it; you will continue in section 3."}
              </p>
            </div>
          </div>
          <div className="form-grid">
            <label>
              {locale === "es" ? "Persona" : "Person"}
              <select
                required
                value={eventForm.workerId}
                onChange={(e) => {
                  setGridRunId("");
                  setGridRows([]);
                  setGridComment("");
                  setGridMessage("");
                  setEventForm({
                    ...eventForm,
                    workerId: e.currentTarget.value,
                  });
                }}
              >
                <option value="">—</option>
                {workspaceWorkers.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.preferredName || w.firstName} {w.lastName}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {locale === "es"
                ? "Período de compensación"
                : "Compensation period"}
              <select
                required
                value={gridRunId}
                onChange={(e) => setGridRunId(e.currentTarget.value)}
              >
                <option value="">—</option>
                {entryPeriodRuns.map((run) => (
                  <option key={run.id} value={run.id}>
                    {formatDate(run.periodStart, locale)} —{" "}
                    {formatDate(run.periodEnd, locale)} ·{" "}
                    {locale === "es" ? "pago" : "pay"}{" "}
                    {formatDate(run.payDate, locale)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {locale === "es" ? "Tipo de almuerzo" : "Lunch type"}
              <select
                value={gridMealMode}
                disabled={gridUsesScheduledAttendance}
                onChange={(e) =>
                  setGridMealMode(e.currentTarget.value as "duration" | "times")
                }
              >
                <option value="duration">
                  {locale === "es" ? "Cantidad de tiempo" : "Amount of time"}
                </option>
                <option value="times">
                  {locale === "es"
                    ? "Salida y regreso"
                    : "Lunch out and return"}
                </option>
              </select>
            </label>
            <div className="time-grid-schedule-action">
              <button
                type="button"
                className="secondary"
                disabled={
                  !eventForm.workerId ||
                  !gridRunId ||
                  gridUsesScheduledAttendance
                }
                onClick={fillGridSchedule}
              >
                {gridUsesScheduledAttendance
                  ? locale === "es"
                    ? "Horario aplicado automáticamente"
                    : "Schedule applied automatically"
                  : locale === "es"
                    ? "Usar horario de la persona"
                    : "Use person's schedule"}
              </button>
            </div>
          </div>
          {eventForm.workerId && gridRunId && gridLoading && (
            <p className="info-note" role="status">
              <LoaderCircle className="spin" />
              {locale === "es"
                ? "Cargando las horas guardadas…"
                : "Loading saved hours…"}
            </p>
          )}
          {eventForm.workerId && gridRunId && !gridLoading && (
            <div className={`responsive-table-shell time-grid-${gridMealMode}`}>
              <table>
                <thead>
                  <tr>
                    <th>{locale === "es" ? "Día" : "Day"}</th>
                    <th>{locale === "es" ? "Entrada" : "In"}</th>
                    <th>{locale === "es" ? "Salida" : "Out"}</th>
                    {gridMealMode === "duration" ? (
                      <th>{locale === "es" ? "Almuerzo" : "Meal"}</th>
                    ) : (
                      <>
                        <th>
                          {locale === "es" ? "Salida a almuerzo" : "Lunch out"}
                        </th>
                        <th>
                          {locale === "es"
                            ? "Regreso al trabajo"
                            : "Return to work"}
                        </th>
                      </>
                    )}
                    <th>{locale === "es" ? "Cargar a" : "Charge to"}</th>
                    <th>{locale === "es" ? "Aplicar regla" : "Apply rule"}</th>
                  </tr>
                </thead>
                <tbody>
                  {gridRows.map((row, index) => {
                    const assignedRuleIds = new Set(
                        gridAssignments
                          .filter(
                            (item) =>
                              item.workerId === eventForm.workerId &&
                              item.effectiveFrom <= row.workDate &&
                              (!item.effectiveTo ||
                                item.effectiveTo >= row.workDate),
                          )
                          .map((item) => item.ruleId),
                      ),
                      assignedLeaveIds = new Set(
                        gridLeaveAssignments
                          .filter(
                            (item) =>
                              item.workerId === eventForm.workerId &&
                              item.effectiveFrom <= row.workDate &&
                              (!item.effectiveTo ||
                                item.effectiveTo >= row.workDate),
                          )
                          .map((item) => item.policyId),
                      ),
                      locked = Boolean(row.leavePolicyId),
                      timeLocked = Boolean(
                        row.existing || locked || gridUsesScheduledAttendance,
                      );
                    return (
                      <tr key={row.workDate}>
                        <td>
                          {formatDate(row.workDate, locale)}
                          {!row.existing &&
                            Boolean(row.clockIn || row.clockOut) && (
                              <button
                                type="button"
                                className="link-button"
                                onClick={() =>
                                  updateGridRow(index, {
                                    clockIn: "",
                                    clockOut: "",
                                    mealMinutes: 0,
                                    mealStart: "",
                                    mealEnd: "",
                                    ruleId: "",
                                    leavePolicyId: "",
                                    metadataDirty: false,
                                  })
                                }
                              >
                                {locale === "es"
                                  ? "No trabajó"
                                  : "Did not work"}
                              </button>
                            )}
                        </td>
                        <td>
                          <input
                            aria-label={`${row.workDate} entrada`}
                            type="time"
                            value={row.clockIn}
                            disabled={timeLocked}
                            onChange={(e) =>
                              updateGridRow(index, {
                                clockIn: e.currentTarget.value,
                              })
                            }
                          />
                        </td>
                        <td>
                          <input
                            aria-label={`${row.workDate} salida`}
                            type="time"
                            value={row.clockOut}
                            disabled={timeLocked}
                            onChange={(e) =>
                              updateGridRow(index, {
                                clockOut: e.currentTarget.value,
                              })
                            }
                          />
                        </td>
                        {gridMealMode === "duration" ? (
                          <td>
                            <select
                              aria-label={`${row.workDate} almuerzo`}
                              value={row.mealMinutes}
                              disabled={locked}
                              onChange={(e) =>
                                updateGridRow(index, {
                                  mealMinutes: Number(e.currentTarget.value) as
                                    | 0
                                    | 30
                                    | 60,
                                  metadataDirty: true,
                                })
                              }
                            >
                              <option value={0}>
                                {locale === "es"
                                  ? "Sin descuento"
                                  : "No deduction"}
                              </option>
                              <option value={30}>30 min</option>
                              <option value={60}>60 min</option>
                            </select>
                          </td>
                        ) : (
                          <>
                            <td>
                              <input
                                aria-label={`${row.workDate} salida a almuerzo`}
                                type="time"
                                value={row.mealStart}
                                disabled={locked}
                                onChange={(e) =>
                                  updateGridRow(index, {
                                    mealStart: e.currentTarget.value,
                                    metadataDirty: true,
                                  })
                                }
                              />
                            </td>
                            <td>
                              <input
                                aria-label={`${row.workDate} regreso al trabajo`}
                                type="time"
                                value={row.mealEnd}
                                disabled={locked}
                                onChange={(e) =>
                                  updateGridRow(index, {
                                    mealEnd: e.currentTarget.value,
                                    metadataDirty: true,
                                  })
                                }
                              />
                            </td>
                          </>
                        )}
                        <td>
                          <select
                            aria-label={`${row.workDate} cargar a`}
                            value={`${row.chargeType}:${row.chargeId}`}
                            disabled={locked}
                            onChange={(e) => {
                              const [chargeType, chargeId] =
                                e.currentTarget.value.split(":");
                              updateGridRow(index, {
                                chargeType: chargeType as GridRow["chargeType"],
                                chargeId: chargeId ?? "",
                                metadataDirty: true,
                              });
                            }}
                          >
                            <option value="home:">
                              {locale === "es"
                                ? "Asignación principal"
                                : "Home assignment"}
                            </option>
                            {allocationDepartments.map((item) => (
                              <option
                                key={`d-${item.id}`}
                                value={`department:${item.id}`}
                              >
                                {locale === "es"
                                  ? "Departamento"
                                  : "Department"}
                                : {item.name}
                              </option>
                            ))}
                            {allocationLocations.map((item) => (
                              <option
                                key={`l-${item.id}`}
                                value={`location:${item.id}`}
                              >
                                {locale === "es" ? "Ubicación" : "Location"}:{" "}
                                {item.name}
                              </option>
                            ))}
                            {allocationCostCenters.map((item) => (
                              <option
                                key={`c-${item.id}`}
                                value={`cost_center:${item.id}`}
                              >
                                {locale === "es"
                                  ? "Centro de costo"
                                  : "Cost center"}
                                : {item.code} · {item.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <select
                            aria-label={`${row.workDate} regla`}
                            value={
                              row.attendanceStatus === "contractor_absent"
                                ? "attendance:contractor_absent"
                                : row.leavePolicyId
                                  ? `leave:${row.leavePolicyId}`
                                  : row.ruleId
                                    ? `pay:${row.ruleId}`
                                    : ""
                            }
                            disabled={Boolean(row.leavePolicyId)}
                            onChange={(e) => {
                              const [kind, id] =
                                e.currentTarget.value.split(":");
                              updateGridRow(index, {
                                ruleId: kind === "pay" ? (id ?? "") : "",
                                leavePolicyId:
                                  kind === "leave" ? (id ?? "") : "",
                                attendanceStatus:
                                  kind === "attendance"
                                    ? "contractor_absent"
                                    : "scheduled",
                                metadataDirty: true,
                                clockIn: kind === "leave" ? "" : row.clockIn,
                                clockOut: kind === "leave" ? "" : row.clockOut,
                              });
                            }}
                          >
                            <option value="">
                              {locale === "es" ? "Ninguna" : "None"}
                            </option>
                            {gridUsesScheduledAttendance &&
                              gridWorker?.workerType ===
                                "professional_services" && (
                                <option value="attendance:contractor_absent">
                                  {locale === "es"
                                    ? "Contratista ausente"
                                    : "Contractor absent"}
                                </option>
                              )}
                            {!gridUsesScheduledAttendance && (
                              <optgroup
                                label={
                                  locale === "es"
                                    ? "Reglas de pago"
                                    : "Pay rules"
                                }
                              >
                                {gridRules
                                  .filter(
                                    (item) =>
                                      item.active &&
                                      assignedRuleIds.has(item.id),
                                  )
                                  .map((item) => (
                                    <option
                                      key={item.id}
                                      value={`pay:${item.id}`}
                                    >
                                      {item.name} ({item.sourceCode})
                                    </option>
                                  ))}
                              </optgroup>
                            )}
                            {!gridUsesScheduledAttendance && (
                              <optgroup
                                label={locale === "es" ? "Licencias" : "Leave"}
                              >
                                {gridLeavePolicies
                                  .filter(
                                    (item) =>
                                      assignedLeaveIds.has(item.id) &&
                                      item.effectiveFrom <= row.workDate &&
                                      (!item.effectiveTo ||
                                        item.effectiveTo >= row.workDate) &&
                                      !(
                                        ["Vacaciones", "Enfermedad"].includes(
                                          item.name,
                                        ) &&
                                        gridLeavePolicies.some(
                                          (candidate) =>
                                            candidate.id !== item.id &&
                                            candidate.leaveType ===
                                              item.leaveType &&
                                            assignedLeaveIds.has(
                                              candidate.id,
                                            ) &&
                                            candidate.effectiveFrom <=
                                              row.workDate &&
                                            (!candidate.effectiveTo ||
                                              candidate.effectiveTo >=
                                                row.workDate) &&
                                            (candidate.name.includes(
                                              "Ley 180",
                                            ) ||
                                              candidate.name.includes(
                                                "patrono de",
                                              )),
                                        )
                                      ),
                                  )
                                  .map((item) => (
                                    <option
                                      key={item.id}
                                      value={`leave:${item.id}`}
                                    >
                                      {item.name}
                                    </option>
                                  ))}
                              </optgroup>
                            )}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {eventForm.workerId && gridRunId && (
            <label className="time-grid-comments">
              {locale === "es" ? "Comentarios del período" : "Period comments"}
              <textarea
                value={gridComment}
                onChange={(e) => setGridComment(e.currentTarget.value)}
                placeholder={locale === "es" ? "Opcional" : "Optional"}
              />
            </label>
          )}
          {eventForm.workerId &&
            gridRunId &&
            gridRows.some(
              (row) =>
                row.existing ||
                row.leavePolicyId ||
                row.clockIn ||
                row.clockOut,
            ) && (
              <div className="time-grid-status-summary" role="status">
                <strong>
                  {locale === "es" ? "Resumen del período" : "Period summary"}
                </strong>
                <span>
                  {locale === "es"
                    ? `${gridRows.filter((row) => row.existing && !row.approvedLeaveName).length} días registrados · ${gridRows.filter((row) => row.approvedLeaveName).length} licencias aprobadas · ${gridRows.filter((row) => !row.existing && Boolean(row.clockIn || row.clockOut)).length} días pendientes · ${gridRows.filter((row) => !row.existing && Boolean(row.leavePolicyId)).length} licencias por aprobar`
                    : `${gridRows.filter((row) => row.existing && !row.approvedLeaveName).length} saved days · ${gridRows.filter((row) => row.approvedLeaveName).length} approved leave days · ${gridRows.filter((row) => !row.existing && Boolean(row.clockIn || row.clockOut)).length} pending days · ${gridRows.filter((row) => !row.existing && Boolean(row.leavePolicyId)).length} leave days awaiting approval`}
                </span>
              </div>
            )}
          {eventForm.workerId && gridRunId && (
            <div className="inline-actions">
              <button
                type="button"
                className="primary"
                disabled={
                  busy ||
                  !gridRows.some(
                    (row) =>
                      row.existing ||
                      (!row.existing &&
                        ((row.clockIn && row.clockOut) || row.leavePolicyId)),
                  )
                }
                onClick={() => void saveGrid()}
              >
                {busy ? <LoaderCircle className="spin" /> : <Check />}
                {locale === "es"
                  ? "Guardar y pasar a aprobación"
                  : "Save and continue to approval"}
              </button>
            </div>
          )}
          {gridMessage && (
            <p className={error ? "form-error" : "success-note"}>
              {gridMessage}
            </p>
          )}
        </section>
      )}
      {roleKey === "user" && (
        <form
          className="settings-form compact-form time-workflow-step time-workflow-step-1"
          onSubmit={submit}
        >
          <div className="form-grid">
            <>
              <label>
                {locale === "es" ? "Persona" : "Person"}
                <select
                  required
                  value={eventForm.workerId}
                  onChange={(e) =>
                    setEventForm({
                      ...eventForm,
                      workerId: e.currentTarget.value,
                    })
                  }
                >
                  <option value="">—</option>
                  {workspaceWorkers.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.preferredName || w.firstName} {w.lastName}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {locale === "es" ? "Evento" : "Event"}
                <select
                  value={eventForm.eventType}
                  onChange={(e) =>
                    setEventForm({
                      ...eventForm,
                      eventType: e.currentTarget.value,
                    })
                  }
                >
                  <option value="clock_in">
                    {optionLabel("clock_in", locale)}
                  </option>
                  <option value="clock_out">
                    {optionLabel("clock_out", locale)}
                  </option>
                  <option value="meal_start">
                    {optionLabel("meal_start", locale)}
                  </option>
                  <option value="meal_end">
                    {optionLabel("meal_end", locale)}
                  </option>
                </select>
              </label>
              <label>
                {locale === "es" ? "Fecha y hora" : "Date and time"}
                <input
                  required
                  type="datetime-local"
                  value={eventForm.eventAt}
                  onInput={(e) =>
                    setEventForm({
                      ...eventForm,
                      eventAt: (e.target as HTMLInputElement).value,
                    })
                  }
                  onChange={(e) =>
                    setEventForm({
                      ...eventForm,
                      eventAt: e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Comentario (opcional)" : "Comment (optional)"}
                <input
                  value={eventForm.comment}
                  onChange={(e) =>
                    setEventForm({
                      ...eventForm,
                      comment: e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es"
                  ? "Cargar a departamento"
                  : "Charge to department"}
                <select
                  value={eventForm.departmentId}
                  onChange={(e) =>
                    setEventForm({
                      ...eventForm,
                      departmentId: e.currentTarget.value,
                    })
                  }
                >
                  <option value="">
                    {locale === "es"
                      ? "Asignación principal"
                      : "Home assignment"}
                  </option>
                  {allocationDepartments.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {locale === "es" ? "Cargar a ubicación" : "Charge to location"}
                <select
                  value={eventForm.locationId}
                  onChange={(e) =>
                    setEventForm({
                      ...eventForm,
                      locationId: e.currentTarget.value,
                    })
                  }
                >
                  <option value="">
                    {locale === "es"
                      ? "Asignación principal"
                      : "Home assignment"}
                  </option>
                  {allocationLocations.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {locale === "es"
                  ? "Cargar a centro de costo"
                  : "Charge to cost center"}
                <select
                  value={eventForm.costCenterId}
                  onChange={(e) =>
                    setEventForm({
                      ...eventForm,
                      costCenterId: e.currentTarget.value,
                    })
                  }
                >
                  <option value="">
                    {locale === "es"
                      ? "Asignación principal"
                      : "Home assignment"}
                  </option>
                  {allocationCostCenters.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.code} · {item.name}
                    </option>
                  ))}
                </select>
              </label>
            </>
          </div>
          {error && (
            <p className="form-error">
              {locale === "es"
                ? "No se pudo guardar. Revise la información."
                : "Could not save. Review the information."}
            </p>
          )}
          {timekeepingGateIssues.length > 0 && (
            <section className="operation-panel">
              <strong>
                {locale === "es"
                  ? "Revise la autorización antes de registrar tiempo"
                  : "Review authorization before recording time"}
              </strong>
              <div className="run-list">
                {timekeepingGateIssues.map((issue) => (
                  <article key={issue}>
                    <span>
                      {(
                        {
                          classification_required:
                            locale === "es"
                              ? "Seleccione empleado o servicios profesionales en Personal."
                              : "Select employee or professional services in Personnel.",
                          position_title_required:
                            locale === "es"
                              ? "Añada el puesto o título en Personal."
                              : "Add the position or title in Personnel.",
                          start_date_required:
                            locale === "es"
                              ? "Registre la fecha inicial real en Personal."
                              : "Record the actual initial start date in Personnel.",
                          before_employment_start:
                            locale === "es"
                              ? "La fecha de tiempo no puede ser anterior al inicio del empleo."
                              : "Time cannot precede the employment start date.",
                          time_entry_method_required:
                            locale === "es"
                              ? "Seleccione un método de registro de tiempo en Personal."
                              : "Select a time-entry method in Personnel.",
                          no_tracking_policy_active:
                            locale === "es"
                              ? "La política vigente sin registro de tiempo bloquea ponches y hojas."
                              : "The current no-tracking policy blocks punches and timesheets.",
                          worker_not_active:
                            locale === "es"
                              ? "La persona no está activa."
                              : "The worker is not active.",
                        } as Record<string, string>
                      )[issue] ?? issue}
                    </span>
                  </article>
                ))}
              </div>
            </section>
          )}
          <button className="primary" disabled={busy}>
            {busy ? <LoaderCircle className="spin" /> : <Check />}
            {locale === "es" ? "Guardar" : "Save"}
          </button>
        </form>
      )}
      {canConfigure && (
        <section className="settings-form compact-form time-workflow-step time-workflow-step-1a optional-workflow-step">
          <div className="optional-workflow-question">
            <div>
              <h2>
                {locale === "es"
                  ? "1a) ¿Necesita importar un archivo de tiempo?"
                  : "1a) Do you need to import a time file?"}
              </h2>
              <p>
                {locale === "es"
                  ? "Use esta opción solamente cuando ya tenga las horas en un archivo CSV o TSV."
                  : "Use this option only when the hours are already in a CSV or TSV file."}
              </p>
            </div>
            <button
              type="button"
              aria-expanded={showTimeImport}
              onClick={() => setShowTimeImport((current) => !current)}
            >
              {showTimeImport
                ? locale === "es"
                  ? "Ocultar importación"
                  : "Hide import"
                : locale === "es"
                  ? "Sí, importar archivo"
                  : "Yes, import file"}
            </button>
          </div>
          {showTimeImport && (
            <div className="optional-workflow-content">
              <p>
                {locale === "es"
                  ? "Carga CSV o TSV para FirstName, MiddleName, LastName, InDate, InTime, OutDate, OutTime y Code. Use fechas MM/DD/YYYY y horas como 8:00a o 4:00p. Las filas sin ponches se omiten; P no crea una categoría y sus ponches se conservan."
                  : "CSV or TSV upload for FirstName, MiddleName, LastName, InDate, InTime, OutDate, OutTime, and Code. Use MM/DD/YYYY dates and times such as 8:00a or 4:00p. Rows without punches are skipped; P creates no category and its punches are retained."}
              </p>
              <button type="button" onClick={downloadTimeImportTemplate}>
                {locale === "es"
                  ? "Descargar plantilla para Excel (.csv)"
                  : "Download Excel-compatible template (.csv)"}
              </button>
              <section
                className="operation-panel"
                aria-labelledby="company-time-codes-title"
              >
                <h3 id="company-time-codes-title">
                  {locale === "es"
                    ? "Códigos disponibles para esta compañía"
                    : "Codes available for this company"}
                </h3>
                <div className="run-list">
                  <article>
                    <div>
                      <strong>
                        {locale === "es" ? "Código en blanco" : "Blank code"}
                      </strong>
                      <small>
                        {locale === "es"
                          ? "Tiempo regular; no añade un diferencial por código."
                          : "Regular time; no code-based differential is added."}
                      </small>
                    </div>
                  </article>
                  {gridRules
                    .filter((item) => item.active)
                    .map((item) => {
                      const assignedNames = gridAssignments
                        .filter((assignment) => assignment.ruleId === item.id)
                        .map((assignment) => assignment.workerName);
                      return (
                        <article key={`import-code-${item.id}`}>
                          <div>
                            <strong>
                              {item.sourceCode} · {item.name}
                            </strong>
                            <small>
                              {new Intl.NumberFormat(
                                locale === "es" ? "es-PR" : "en-US",
                                {
                                  style: "currency",
                                  currency: "USD",
                                },
                              ).format(item.centsPerHour / 100)}{" "}
                              {locale === "es"
                                ? "adicional por hora"
                                : "additional per hour"}
                              {item.includeOvertime
                                ? locale === "es"
                                  ? " · incluye horas extra"
                                  : " · includes overtime"
                                : locale === "es"
                                  ? " · solo horas regulares"
                                  : " · regular hours only"}
                            </small>
                            <small>
                              {locale === "es"
                                ? "Asignado a: "
                                : "Assigned to: "}
                              {assignedNames.length
                                ? assignedNames.join(", ")
                                : locale === "es"
                                  ? "ninguna persona"
                                  : "no one"}
                            </small>
                          </div>
                          <span>
                            {formatDateRange(
                              item.effectiveFrom,
                              item.effectiveTo,
                              locale,
                              locale === "es" ? "vigente" : "current",
                            )}
                          </span>
                        </article>
                      );
                    })}
                </div>
                {!gridRules.some((item) => item.active) && (
                  <p className="field-note">
                    {locale === "es"
                      ? "Esta compañía no tiene códigos de diferencial configurados. Use la columna Code en blanco."
                      : "This company has no configured differential codes. Leave the Code column blank."}
                  </p>
                )}
                <p className="field-note">
                  {locale === "es"
                    ? "Un código solamente activa el pago adicional cuando su regla está asignada a esa persona y está vigente en la fecha trabajada."
                    : "A code only activates additional pay when its rule is assigned to that person and is effective on the work date."}
                </p>
              </section>
              <label>
                {locale === "es" ? "Archivo CSV o TSV" : "CSV or TSV file"}
                <input
                  type="file"
                  accept=".csv,.txt,.tsv,text/csv,text/plain,text/tab-separated-values"
                  onChange={(event) => (
                    setTimeImportFile(event.target.files?.[0] ?? null),
                    setTimeImportMappingIssues([]),
                    setTimeImportMappings({}),
                    setTimeImportError("")
                  )}
                />
              </label>
              {timeImportMappingIssues.length > 0 && (
                <section
                  className="operation-panel"
                  aria-labelledby="time-import-mapping-title"
                >
                  <h3 id="time-import-mapping-title">
                    {locale === "es"
                      ? "Relacionar nombres del archivo"
                      : "Map file names"}
                  </h3>
                  <p>
                    {locale === "es"
                      ? "Seleccione la persona correcta para cada nombre. Las sugerencias son solo una ayuda; las horas no se importarán hasta que usted confirme el mapeo."
                      : "Select the correct person for each name. Suggestions are only an aid; time will not be imported until you confirm the mapping."}
                  </p>
                  <div className="form-grid">
                    {timeImportMappingIssues.map((issue) => (
                      <label key={issue.sourceKey}>
                        <strong>{issue.workerName}</strong>
                        <select
                          value={timeImportMappings[issue.sourceKey] ?? ""}
                          onChange={(event) =>
                            setTimeImportMappings((current) => ({
                              ...current,
                              [issue.sourceKey]: event.currentTarget.value,
                            }))
                          }
                        >
                          <option value="">
                            {locale === "es"
                              ? "Seleccione una persona"
                              : "Select a person"}
                          </option>
                          {issue.candidates.length > 0 && (
                            <optgroup
                              label={
                                locale === "es" ? "Sugerencias" : "Suggestions"
                              }
                            >
                              {issue.candidates.map((candidate) => (
                                <option
                                  key={`suggested-${issue.sourceKey}-${candidate.workerId}`}
                                  value={candidate.workerId}
                                >
                                  {candidate.workerName} · {candidate.score}%
                                </option>
                              ))}
                            </optgroup>
                          )}
                          <optgroup
                            label={
                              locale === "es"
                                ? "Todas las personas"
                                : "All people"
                            }
                          >
                            {workers
                              .filter((worker) =>
                                ["employee", "professional_services"].includes(
                                  worker.workerType,
                                ),
                              )
                              .filter(
                                (worker) =>
                                  !issue.candidates.some(
                                    (candidate) =>
                                      candidate.workerId === worker.id,
                                  ),
                              )
                              .map((worker) => (
                                <option
                                  key={`all-${issue.sourceKey}-${worker.id}`}
                                  value={worker.id}
                                >
                                  {worker.firstName} {worker.lastName ?? ""}
                                </option>
                              ))}
                          </optgroup>
                        </select>
                      </label>
                    ))}
                  </div>
                  <p className="field-note">
                    {locale === "es"
                      ? "Después de completar todos los mapeos, vuelva a oprimir Validar e importar."
                      : "After completing every mapping, click Validate and import again."}
                  </p>
                </section>
              )}
              <button
                type="button"
                className="primary"
                disabled={
                  busy ||
                  !timeImportFile ||
                  (timeImportMappingIssues.length > 0 &&
                    timeImportMappingIssues.some(
                      (issue) => !timeImportMappings[issue.sourceKey],
                    ))
                }
                onClick={() => void importTime()}
              >
                {busy ? <LoaderCircle className="spin" /> : <Check />}
                {locale === "es" ? "Validar e importar" : "Validate and import"}
              </button>
              <div id="time-import-result" aria-live="assertive">
                {timeImportResult && (
                  <OperationalFeedback
                    locale={locale}
                    isError={false}
                    message={
                      timeImportAlreadyProcessed
                        ? locale === "es"
                          ? `Este archivo ya había sido procesado: ${timeImportResult.fileName}. No se añadieron ponches duplicados.`
                          : `This file had already been processed: ${timeImportResult.fileName}. No duplicate punches were added.`
                        : locale === "es"
                          ? `Importación completada correctamente: ${timeImportResult.fileName} · ${timeImportResult.sourceRowCount} filas procesadas · ${timeImportResult.acceptedPairCount} turnos nuevos y ${timeImportResult.acceptedEventCount} ponches nuevos · ${timeImportResult.duplicatePairCount ?? 0} turnos existentes omitidos · ${timeImportResult.skippedRowCount} filas omitidas en total · 0 rechazadas.`
                          : `Import completed successfully: ${timeImportResult.fileName} · ${timeImportResult.sourceRowCount} rows processed · ${timeImportResult.acceptedPairCount} new shifts and ${timeImportResult.acceptedEventCount} new punches · ${timeImportResult.duplicatePairCount ?? 0} existing shifts skipped · ${timeImportResult.skippedRowCount} total rows skipped · 0 rejected.`
                    }
                  />
                )}
                <OperationalError value={timeImportError} locale={locale} />
              </div>
            </div>
          )}
        </section>
      )}
      {false && canConfigure && (
        <section className="settings-form">
          <div className="section-heading">
            <div>
              <h2>
                {locale === "es"
                  ? "Asignaciones de campo"
                  : "Field assignments"}
              </h2>
              <p>
                {locale === "es"
                  ? "Asigne proyectos y vehículos por fechas efectivas. El historial no se elimina y los ponches respetan las asignaciones vigentes."
                  : "Assign projects and vehicles by effective dates. History is retained and punches honor current assignments."}
              </p>
            </div>
          </div>
          <form className="compact-form" onSubmit={createAssignment}>
            <div className="form-grid">
              <label>
                {locale === "es" ? "Persona" : "Person"}
                <select
                  required
                  value={assignmentForm.workerId}
                  onChange={(e) =>
                    setAssignmentForm({
                      ...assignmentForm,
                      workerId: e.currentTarget.value,
                    })
                  }
                >
                  <option value="">—</option>
                  {workspaceWorkers.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.preferredName || item.firstName} {item.lastName}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {locale === "es" ? "Tipo" : "Type"}
                <select
                  value={assignmentForm.resourceType}
                  onChange={(e) =>
                    setAssignmentForm({
                      ...assignmentForm,
                      resourceType: e.currentTarget.value,
                      resourceId: "",
                    })
                  }
                >
                  <option value="project">
                    {locale === "es" ? "Proyecto" : "Project"}
                  </option>
                  <option value="vehicle">
                    {locale === "es" ? "Vehículo" : "Vehicle"}
                  </option>
                </select>
              </label>
              <label>
                {locale === "es" ? "Recurso" : "Resource"}
                <select
                  required
                  value={assignmentForm.resourceId}
                  onChange={(e) =>
                    setAssignmentForm({
                      ...assignmentForm,
                      resourceId: e.currentTarget.value,
                    })
                  }
                >
                  <option value="">—</option>
                  {assignmentForm.resourceType === "project"
                    ? projects
                        .filter((x) => x.status === "active")
                        .map((x) => (
                          <option key={x.id} value={x.id}>
                            {x.name}
                          </option>
                        ))
                    : vehicles
                        .filter((x) => x.status === "active")
                        .map((x) => (
                          <option key={x.id} value={x.id}>
                            {x.unitNumber}
                          </option>
                        ))}
                </select>
              </label>
              <label>
                {locale === "es" ? "Vigente desde" : "Effective from"}
                <input
                  required
                  type="date"
                  value={assignmentForm.effectiveFrom}
                  onInput={(e) =>
                    setAssignmentForm({
                      ...assignmentForm,
                      effectiveFrom: e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Vigente hasta (opcional)" : "Effective to (optional)"}
                <input
                  type="date"
                  value={assignmentForm.effectiveTo}
                  onInput={(e) =>
                    setAssignmentForm({
                      ...assignmentForm,
                      effectiveTo: e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Razón" : "Reason"}
                <input
                  required
                  minLength={3}
                  value={assignmentForm.reason}
                  onChange={(e) =>
                    setAssignmentForm({
                      ...assignmentForm,
                      reason: e.currentTarget.value,
                    })
                  }
                />
              </label>
            </div>
            <button className="primary" disabled={busy}>
              {locale === "es" ? "Asignar recurso" : "Assign resource"}
            </button>
          </form>
          <h3>{locale === "es" ? "Proyectos" : "Projects"}</h3>
          <div className="run-list">
            {projects.map((item) => (
              <article key={item.id}>
                <div>
                  <strong>{item.name}</strong>
                  <p>{item.code || "—"}</p>
                </div>
                <span className={`run-status ${item.status}`}>
                  {optionLabel(item.status, locale)}
                </span>
                <button type="button" onClick={() => void editProject(item)}>
                  {locale === "es"
                    ? "Editar / desactivar"
                    : "Edit / deactivate"}
                </button>
              </article>
            ))}
          </div>
          <h3>{locale === "es" ? "Vehículos" : "Vehicles"}</h3>
          <div className="run-list">
            {vehicles.map((item) => (
              <article key={item.id}>
                <div>
                  <strong>{item.unitNumber}</strong>
                </div>
                <span className={`run-status ${item.status}`}>
                  {optionLabel(item.status, locale)}
                </span>
                <button type="button" onClick={() => void editVehicle(item)}>
                  {locale === "es"
                    ? "Editar / desactivar"
                    : "Edit / deactivate"}
                </button>
              </article>
            ))}
          </div>
          <h3>
            {locale === "es"
              ? "Historial de asignaciones"
              : "Assignment history"}
          </h3>
          <div className="run-list">
            {fieldAssignments.map((item) => (
              <article key={item.id}>
                <div>
                  <strong>
                    {item.workerName} · {item.projectName || item.vehicleUnit}
                  </strong>
                  <p>
                    {formatDateRange(
                      item.effectiveFrom,
                      item.effectiveTo,
                      locale,
                      locale === "es" ? "vigente" : "current",
                    )}
                  </p>
                  <small>{item.reason}</small>
                </div>
                <span className={`run-status ${item.status}`}>
                  {optionLabel(item.status, locale)}
                </span>
                {item.status === "active" && (
                  <button
                    type="button"
                    onClick={() => void closeAssignment(item)}
                  >
                    {locale === "es" ? "Cerrar asignación" : "Close assignment"}
                  </button>
                )}
              </article>
            ))}
          </div>
        </section>
      )}
      <section className="settings-form time-person-setup time-company-setup time-schedule-setup">
        <div className="section-heading">
          <div>
            <h2>
              {workspace === "operations"
                ? locale === "es"
                  ? "Excepciones de ponche"
                  : "Punch exceptions"
                : locale === "es"
                  ? "Horarios y ventanas de ponche"
                  : "Schedules and punch windows"}
            </h2>
            <p>
              {workspace === "operations"
                ? locale === "es"
                  ? "Revise los ponches que quedaron fuera de la ventana autorizada."
                  : "Review punches recorded outside the authorized window."
                : locale === "es"
                  ? "Los horarios son efectivos por fecha. Las ventanas pueden bloquear, advertir, requerir aprobación o exigir explicación."
                  : "Schedules are effective-dated. Windows may block, warn, require approval, or require an explanation."}
            </p>
          </div>
        </div>
        {canConfigure && (
          <>
            <form
              className="compact-form time-company-setup-only"
              onSubmit={createPolicy}
            >
              <h3>
                {locale === "es"
                  ? "Nueva política de ponche"
                  : "New punch policy"}
              </h3>
              <div className="form-grid">
                <label>
                  {locale === "es" ? "Nombre" : "Name"}
                  <input
                    required
                    value={policyForm.name}
                    onChange={(e) =>
                      setPolicyForm({
                        ...policyForm,
                        name: e.currentTarget.value,
                      })
                    }
                  />
                </label>
                <label>
                  {locale === "es"
                    ? "Resultado fuera de ventana"
                    : "Out-of-window outcome"}
                  <select
                    value={policyForm.outOfWindowAction}
                    onChange={(e) =>
                      setPolicyForm({
                        ...policyForm,
                        outOfWindowAction: e.currentTarget.value,
                      })
                    }
                  >
                    <option value="block">
                      {optionLabel("block", locale)}
                    </option>
                    <option value="warn">{optionLabel("warn", locale)}</option>
                    <option value="approval">
                      {optionLabel("approval", locale)}
                    </option>
                    <option value="explanation">
                      {optionLabel("explanation", locale)}
                    </option>
                  </select>
                </label>
                <label>
                  {locale === "es"
                    ? "Minutos antes de entrada"
                    : "Minutes before start"}
                  <input
                    type="number"
                    min="0"
                    max="240"
                    value={policyForm.beforeStartMinutes}
                    onInput={(e) =>
                      setPolicyForm({
                        ...policyForm,
                        beforeStartMinutes: Number(e.currentTarget.value),
                      })
                    }
                  />
                </label>
                <label>
                  {locale === "es"
                    ? "Minutos después de entrada"
                    : "Minutes after start"}
                  <input
                    type="number"
                    min="0"
                    max="240"
                    value={policyForm.afterStartMinutes}
                    onInput={(e) =>
                      setPolicyForm({
                        ...policyForm,
                        afterStartMinutes: Number(e.currentTarget.value),
                      })
                    }
                  />
                </label>
                <label>
                  {locale === "es"
                    ? "Minutos antes de salida"
                    : "Minutes before end"}
                  <input
                    type="number"
                    min="0"
                    max="240"
                    value={policyForm.beforeEndMinutes}
                    onInput={(e) =>
                      setPolicyForm({
                        ...policyForm,
                        beforeEndMinutes: Number(e.currentTarget.value),
                      })
                    }
                  />
                </label>
                <label>
                  {locale === "es"
                    ? "Minutos después de salida"
                    : "Minutes after end"}
                  <input
                    type="number"
                    min="0"
                    max="240"
                    value={policyForm.afterEndMinutes}
                    onInput={(e) =>
                      setPolicyForm({
                        ...policyForm,
                        afterEndMinutes: Number(e.currentTarget.value),
                      })
                    }
                  />
                </label>
                <label className="check-label">
                  <input
                    type="checkbox"
                    checked={policyForm.requireMeal}
                    onChange={(e) =>
                      setPolicyForm({
                        ...policyForm,
                        requireMeal: e.currentTarget.checked,
                      })
                    }
                  />
                  {locale === "es" ? "Requiere comida" : "Meal required"}
                </label>
              </div>
              <button className="primary" disabled={busy}>
                {locale === "es" ? "Guardar política" : "Save policy"}
              </button>
            </form>
            <form
              className="compact-form time-person-setup-only"
              onSubmit={createWorkSchedule}
            >
              <h3>
                {locale === "es"
                  ? "Nuevo horario efectivo"
                  : "New effective schedule"}
              </h3>
              <div className="form-grid">
                <label>
                  {locale === "es" ? "Persona" : "Person"}
                  <select
                    required
                    value={scheduleForm.workerId}
                    onChange={(e) =>
                      setScheduleForm({
                        ...scheduleForm,
                        workerId: e.currentTarget.value,
                      })
                    }
                  >
                    <option value="">—</option>
                    {workspaceWorkers.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.preferredName || w.firstName} {w.lastName}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  {locale === "es" ? "Nombre" : "Name"}
                  <input
                    required
                    value={scheduleForm.name}
                    onChange={(e) =>
                      setScheduleForm({
                        ...scheduleForm,
                        name: e.currentTarget.value,
                      })
                    }
                  />
                </label>
                <label>
                  {locale === "es" ? "Modo" : "Mode"}
                  <select
                    value={scheduleForm.scheduleMode}
                    onChange={(e) =>
                      setScheduleForm({
                        ...scheduleForm,
                        scheduleMode: e.currentTarget.value,
                      })
                    }
                  >
                    <option value="fixed">
                      {optionLabel("fixed", locale)}
                    </option>
                    <option value="flexible">
                      {optionLabel("flexible", locale)}
                    </option>
                    <option value="as_needed">
                      {optionLabel("as_needed", locale)}
                    </option>
                    <option value="field_mobile">
                      {optionLabel("field_mobile", locale)}
                    </option>
                    <option value="project_service">
                      {optionLabel("project_service", locale)}
                    </option>
                  </select>
                </label>
                <label>
                  {locale === "es" ? "Política de ponche" : "Punch policy"}
                  <select
                    value={scheduleForm.punchPolicyId}
                    onChange={(e) =>
                      setScheduleForm({
                        ...scheduleForm,
                        punchPolicyId: e.currentTarget.value,
                      })
                    }
                  >
                    <option value="">—</option>
                    {punchPolicies
                      .filter((x) => x.active)
                      .map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.name} · {x.outOfWindowAction}
                        </option>
                      ))}
                  </select>
                </label>
                <label>
                  {locale === "es" ? "Vigente desde" : "Effective from"}
                  <input
                    required
                    type="date"
                    value={scheduleForm.effectiveFrom}
                    onInput={(e) =>
                      setScheduleForm({
                        ...scheduleForm,
                        effectiveFrom: e.currentTarget.value,
                      })
                    }
                  />
                </label>
                <label>
                  {locale === "es" ? "Vigente hasta (opcional)" : "Effective to (optional)"}
                  <input
                    type="date"
                    value={scheduleForm.effectiveTo}
                    onInput={(e) =>
                      setScheduleForm({
                        ...scheduleForm,
                        effectiveTo: e.currentTarget.value,
                      })
                    }
                  />
                </label>
              </div>
              {scheduleForm.scheduleMode === "field_mobile" && (
                <p className="field-note">
                  {locale === "es"
                    ? "Cada entrada, salida, transferencia o bloque semanal exige un proyecto o vehículo asignado para conservar el costo de campo."
                    : "Every clock event, transfer, or weekly block requires an assigned project or vehicle so field cost remains attributable."}
                </p>
              )}
              {scheduleForm.scheduleMode === "project_service" && (
                <p className="field-note">
                  {locale === "es"
                    ? "Cada entrada, salida, transferencia o bloque semanal exige un proyecto asignado; el servidor también aplica esta regla a ponches móviles y fuera de línea."
                    : "Every clock event, transfer, or weekly block requires an assigned project; the server also enforces this rule for mobile and offline punches."}
                </p>
              )}
              {scheduleForm.scheduleMode === "as_needed" && (
                <p className="field-note">
                  {locale === "es"
                    ? "No crea horas programadas ni ventanas de ponche; solo se evalúa el tiempo que realmente se registre."
                    : "Creates no scheduled hours or punch windows; only time actually recorded is evaluated."}
                </p>
              )}
              {["fixed", "flexible"].includes(scheduleForm.scheduleMode) && (
                <div className="run-list">
                  <p className="field-note">
                    {scheduleForm.scheduleMode === "flexible"
                      ? locale === "es"
                        ? "En modalidad flexible, cada intervalo define horas núcleo durante las cuales la persona debe permanecer trabajando; la entrada y salida fuera de esas horas siguen siendo flexibles."
                        : "For flexible schedules, each interval defines core hours during which the worker must remain working; arrival and departure outside those hours remain flexible."
                      : locale === "es"
                        ? "Cada turno define la hora programada de entrada y salida."
                        : "Each shift defines the scheduled start and end time."}
                  </p>
                  {scheduleForm.shifts.map((shift, index) => (
                    <article key={index}>
                      <div className="form-grid">
                        <label>
                          {locale === "es" ? "Día" : "Day"}
                          <select
                            required
                            value={shift.dayOfWeek}
                            onChange={(e) =>
                              setScheduleForm({
                                ...scheduleForm,
                                shifts: scheduleForm.shifts.map((x, i) =>
                                  i === index
                                    ? {
                                        ...x,
                                        dayOfWeek: Number(e.currentTarget.value),
                                      }
                                    : x,
                                ),
                              })
                            }
                          >
                            <option value={0}>{locale === "es" ? "domingo" : "Sunday"}</option>
                            <option value={1}>{locale === "es" ? "lunes" : "Monday"}</option>
                            <option value={2}>{locale === "es" ? "martes" : "Tuesday"}</option>
                            <option value={3}>{locale === "es" ? "miércoles" : "Wednesday"}</option>
                            <option value={4}>{locale === "es" ? "jueves" : "Thursday"}</option>
                            <option value={5}>{locale === "es" ? "viernes" : "Friday"}</option>
                            <option value={6}>{locale === "es" ? "sábado" : "Saturday"}</option>
                          </select>
                        </label>
                        <label>
                          {locale === "es" ? "Entrada" : "Start"}
                          <input
                            required
                            type="time"
                            value={shift.startTime}
                            onChange={(e) =>
                              setScheduleForm({
                                ...scheduleForm,
                                shifts: scheduleForm.shifts.map((x, i) =>
                                  i === index
                                    ? { ...x, startTime: e.currentTarget.value }
                                    : x,
                                ),
                              })
                            }
                          />
                        </label>
                        <label>
                          {locale === "es" ? "Salida" : "End"}
                          <input
                            required
                            type="time"
                            value={shift.endTime}
                            onChange={(e) =>
                              setScheduleForm({
                                ...scheduleForm,
                                shifts: scheduleForm.shifts.map((x, i) =>
                                  i === index
                                    ? { ...x, endTime: e.currentTarget.value }
                                    : x,
                                ),
                              })
                            }
                          />
                        </label>
                        <label>
                          {locale === "es" ? "Comida (min)" : "Meal (min)"}
                          <input
                            required
                            type="number"
                            min="0"
                            value={shift.mealMinutes}
                            onInput={(e) =>
                              setScheduleForm({
                                ...scheduleForm,
                                shifts: scheduleForm.shifts.map((x, i) =>
                                  i === index
                                    ? {
                                        ...x,
                                        mealMinutes: Number(
                                          e.currentTarget.value,
                                        ),
                                      }
                                    : x,
                                ),
                              })
                            }
                          />
                        </label>
                        <p className="field-note">
                          {locale === "es"
                            ? "Las horas pagadas se calculan automáticamente usando entrada, salida y comida."
                            : "Paid hours are calculated automatically from start, end, and meal."}
                        </p>
                      </div>
                      {scheduleForm.shifts.length > 1 && (
                        <button
                          type="button"
                          onClick={() =>
                            setScheduleForm({
                              ...scheduleForm,
                              shifts: scheduleForm.shifts.filter(
                                (_, i) => i !== index,
                              ),
                            })
                          }
                        >
                          {locale === "es" ? "Quitar turno" : "Remove shift"}
                        </button>
                      )}
                    </article>
                  ))}
                </div>
              )}
              <div className="run-actions">
                {["fixed", "flexible"].includes(scheduleForm.scheduleMode) && (
                  <button
                    type="button"
                    onClick={() =>
                      setScheduleForm({
                        ...scheduleForm,
                        shifts: [
                          ...scheduleForm.shifts,
                          {
                            dayOfWeek: 1,
                            sequence: scheduleForm.shifts.length + 1,
                            startTime: "08:00",
                            endTime: "17:00",
                            mealMinutes: 60,
                            expectedPaidMinutes: 480,
                          },
                        ],
                      })
                    }
                  >
                    {locale === "es" ? "Añadir turno" : "Add shift"}
                  </button>
                )}
                <button
                  className="primary"
                  disabled={busy || !scheduleForm.workerId}
                >
                  {locale === "es" ? "Guardar horario" : "Save schedule"}
                </button>
              </div>
            </form>
          </>
        )}
        <div className="time-person-setup-only">
          <h3>
            {locale === "es" ? "Horarios efectivos" : "Effective schedules"}
          </h3>
          <ResponsiveDataTable
            label={
              locale === "es"
                ? "Tabla de horarios efectivos"
                : "Effective schedules table"
            }
            caption={
              locale === "es"
                ? "Horarios por persona, modalidad, vigencia, política, turnos y licencia"
                : "Schedules by worker, mode, effective dates, policy, shifts, and leave"
            }
            columns={
              locale === "es"
                ? [
                    "Persona y horario",
                    "Modalidad",
                    "Vigencia",
                    "Política de ponche",
                    "Turnos",
                    "Licencia aprobada",
                    "Acciones",
                  ]
                : [
                    "Worker and schedule",
                    "Mode",
                    "Effective dates",
                    "Punch policy",
                    "Shifts",
                    "Approved leave",
                    "Actions",
                  ]
            }
            empty={
              locale === "es"
                ? "No hay horarios efectivos configurados."
                : "There are no effective schedules configured."
            }
            rows={schedules.map((item) => {
              const leaveText = item.leaveSegments.length
                  ? item.leaveSegments
                      .map(
                        (segment) =>
                          `${formatDate(segment.workDate, locale)} · ${(segment.hoursHundredths / 100).toFixed(2)} h · ${segment.policyName}`,
                      )
                      .join("; ")
                  : "—",
                actions =
                  canConfigure &&
                  (!item.effectiveTo || item.effectiveTo >= today) ? (
                    <div className="run-actions">
                      <button
                        className="danger-action"
                        onClick={() => endWorkSchedule(item)}
                      >
                        {locale === "es" ? "Terminar horario" : "End schedule"}
                      </button>
                    </div>
                  ) : null;
              return {
                key: item.id,
                cells: [
                  <>
                    <strong>{item.workerName}</strong>
                    <small>{item.name}</small>
                  </>,
                  optionLabel(item.scheduleMode, locale),
                  formatDateRange(
                    item.effectiveFrom,
                    item.effectiveTo,
                    locale,
                    locale === "es" ? "vigente" : "current",
                  ),
                  item.punchPolicyName ??
                    (locale === "es" ? "Sin política" : "No policy"),
                  item.shifts.length,
                  leaveText,
                  actions,
                ],
                card: (
                  <article key={item.id}>
                    <div>
                      <strong>
                        {item.workerName} · {item.name}
                      </strong>
                      <p>
                        {optionLabel(item.scheduleMode, locale)} ·{" "}
                        {formatDateRange(
                          item.effectiveFrom,
                          item.effectiveTo,
                          locale,
                          locale === "es" ? "vigente" : "current",
                        )}
                      </p>
                      <small>
                        {item.punchPolicyName ??
                          (locale === "es" ? "Sin política" : "No policy")}{" "}
                        · {item.shifts.length}{" "}
                        {locale === "es" ? "turnos" : "shifts"}
                      </small>
                      {item.leaveSegments.length > 0 && (
                        <p>
                          <strong>
                            {locale === "es"
                              ? "Licencia aprobada en el calendario"
                              : "Approved leave on schedule"}
                            :
                          </strong>{" "}
                          {leaveText}
                        </p>
                      )}
                    </div>
                    {actions}
                  </article>
                ),
              };
            })}
          />
        </div>
        <div className="time-operations-only">
          <h3>
            {locale === "es" ? "Excepciones de ponche" : "Punch exceptions"}
          </h3>
          <ResponsiveDataTable
            label={
              locale === "es"
                ? "Tabla de excepciones de ponche"
                : "Punch exceptions table"
            }
            caption={
              locale === "es"
                ? "Excepciones por persona, evento, fecha, desviación, política, estado y acciones"
                : "Exceptions by worker, event, date, deviation, policy, status, and actions"
            }
            columns={
              locale === "es"
                ? [
                    "Persona y evento",
                    "Fecha y hora",
                    "Desviación",
                    "Política y explicación",
                    "Estado",
                    "Acciones",
                  ]
                : [
                    "Worker and event",
                    "Date and time",
                    "Deviation",
                    "Policy and explanation",
                    "Status",
                    "Actions",
                  ]
            }
            empty={
              locale === "es"
                ? "No hay excepciones de ponche."
                : "There are no punch exceptions."
            }
            rows={punchExceptions.map((item) => {
              const detail =
                [item.policyAction, item.explanation, item.reviewComment]
                  .filter(Boolean)
                  .join(" · ") || "—";
              return {
                key: item.id,
                cells: [
                  <>
                    <strong>{item.workerName}</strong>
                    <small>{optionLabel(item.eventType, locale)}</small>
                  </>,
                  formatDateTime(item.actualAt, locale),
                  `${item.deviationMinutes > 0 ? "+" : ""}${item.deviationMinutes} min`,
                  <>
                    <strong>{item.policyName}</strong>
                    <small>{detail}</small>
                  </>,
                  <span className={`run-status ${item.status}`}>
                    {optionLabel(item.status, locale)}
                  </span>,
                  punchExceptionActions(item),
                ],
                card: (
                  <article key={item.id}>
                    <div>
                      <strong>
                        {item.workerName} ·{" "}
                        {optionLabel(item.eventType, locale)}
                      </strong>
                      <p>
                        {formatDateTime(item.actualAt, locale)} ·{" "}
                        {item.deviationMinutes > 0 ? "+" : ""}
                        {item.deviationMinutes} min · {item.policyName}
                      </p>
                      <small>{detail}</small>
                    </div>
                    <span className={`run-status ${item.status}`}>
                      {optionLabel(item.status, locale)}
                    </span>
                    {punchExceptionActions(item)}
                  </article>
                ),
              };
            })}
          />
        </div>
      </section>
      <section className="settings-form time-workflow-step time-workflow-step-2 optional-workflow-step">
        <div className="section-heading">
          <div>
            <h2>
              {locale === "es"
                ? "2) ¿Necesita añadir comentarios diarios?"
                : "2) Do you need to add daily comments?"}
            </h2>
            <p>
              {locale === "es"
                ? "Documente condiciones del día, explicaciones o contexto del proyecto. El comentario queda inmutable y otra persona autorizada lo revisa."
                : "Document daily conditions, explanations, or project context. The comment remains immutable and a different authorized person reviews it."}
            </p>
          </div>
          <button
            type="button"
            aria-expanded={showDailyComments}
            onClick={() => setShowDailyComments((current) => !current)}
          >
            {showDailyComments
              ? locale === "es"
                ? "Ocultar comentarios"
                : "Hide comments"
              : locale === "es"
                ? "Sí, añadir comentario"
                : "Yes, add comment"}
          </button>
        </div>
        {showDailyComments && (
          <div className="optional-workflow-content">
            <form className="compact-form" onSubmit={submitDailyComment}>
              <div className="form-grid">
                <label>
                  {locale === "es" ? "Persona" : "Person"}
                  <select
                    required
                    value={dailyCommentForm.workerId}
                    onChange={(e) =>
                      setDailyCommentForm({
                        ...dailyCommentForm,
                        workerId: e.currentTarget.value,
                      })
                    }
                  >
                    <option value="">—</option>
                    {workspaceWorkers.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.preferredName || w.firstName} {w.lastName}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  {locale === "es" ? "Fecha de trabajo" : "Work date"}
                  <input
                    required
                    type="date"
                    value={dailyCommentForm.workDate}
                    onInput={(e) =>
                      setDailyCommentForm({
                        ...dailyCommentForm,
                        workDate: e.currentTarget.value,
                      })
                    }
                  />
                </label>
                <label>
                  {locale === "es" ? "Proyecto opcional" : "Optional project"}
                  <select
                    value={dailyCommentForm.projectId}
                    onChange={(e) =>
                      setDailyCommentForm({
                        ...dailyCommentForm,
                        projectId: e.currentTarget.value,
                      })
                    }
                  >
                    <option value="">—</option>
                    {projects
                      .filter((project) => project.status === "active")
                      .map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                  </select>
                </label>
                <label>
                  {locale === "es" ? "Comentario" : "Comment"}
                  <input
                    required
                    minLength={3}
                    maxLength={2000}
                    value={dailyCommentForm.comment}
                    onChange={(e) =>
                      setDailyCommentForm({
                        ...dailyCommentForm,
                        comment: e.currentTarget.value,
                      })
                    }
                  />
                </label>
              </div>
              <button
                className="primary"
                disabled={busy || !dailyCommentForm.workerId}
              >
                {locale === "es" ? "Guardar comentario" : "Save comment"}
              </button>
            </form>
            <ResponsiveDataTable
              label={
                locale === "es"
                  ? "Tabla de comentarios diarios"
                  : "Daily comments table"
              }
              caption={
                locale === "es"
                  ? "Comentarios por persona, fecha, proyecto, estado, revisión y acciones"
                  : "Comments by worker, date, project, status, review, and actions"
              }
              columns={
                locale === "es"
                  ? [
                      "Persona y fecha",
                      "Comentario",
                      "Proyecto",
                      "Estado",
                      "Revisión",
                      "Acciones",
                    ]
                  : [
                      "Worker and date",
                      "Comment",
                      "Project",
                      "Status",
                      "Review",
                      "Actions",
                    ]
              }
              empty={
                locale === "es"
                  ? "No hay comentarios diarios."
                  : "There are no daily comments."
              }
              rows={dailyComments.map((item) => ({
                key: item.id,
                cells: [
                  <>
                    <strong>{item.workerName}</strong>
                    <small>{formatDate(item.workDate, locale)}</small>
                  </>,
                  item.comment,
                  item.projectName ??
                    (locale === "es" ? "Sin proyecto" : "No project"),
                  <span className={`run-status ${item.status}`}>
                    {optionLabel(item.status, locale)}
                  </span>,
                  item.reviewComment ?? "—",
                  dailyCommentActions(item),
                ],
                card: (
                  <article key={item.id}>
                    <div>
                      <strong>
                        {item.workerName} · {formatDate(item.workDate, locale)}
                      </strong>
                      <p>{item.comment}</p>
                      <small>
                        {item.projectName ??
                          (locale === "es" ? "Sin proyecto" : "No project")}
                        {item.reviewComment ? ` · ${item.reviewComment}` : ""}
                      </small>
                    </div>
                    <span className={`run-status ${item.status}`}>
                      {optionLabel(item.status, locale)}
                    </span>
                    {dailyCommentActions(item)}
                  </article>
                ),
              }))}
            />
          </div>
        )}
      </section>
      <section className="settings-form" id="time-corrections">
        <div className="section-heading">
          <div>
            <h2>
              {locale === "es" ? "Correcciones de tiempo" : "Time corrections"}
            </h2>
            <p>
              {locale === "es"
                ? "Solicite un ponche faltante o una corrección. Otra persona autorizada debe aprobarla y aplicarla; el evento original nunca se reescribe."
                : "Request a missing punch or correction. A different authorized person must approve and apply it; the original event is never rewritten."}
            </p>
          </div>
        </div>
        <form className="compact-form" onSubmit={requestCorrection}>
          <div className="form-grid">
            <label>
              {locale === "es" ? "Persona" : "Person"}
              <select
                required
                value={correctionForm.workerId}
                onChange={(e) =>
                  setCorrectionForm({
                    ...correctionForm,
                    workerId: e.currentTarget.value,
                    originalEventId: "",
                  })
                }
              >
                <option value="">—</option>
                {workspaceWorkers.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.preferredName || w.firstName} {w.lastName}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {locale === "es"
                ? "Evento original (opcional si falta el ponche)"
                : "Original event (optional for missing punch)"}
              <select
                value={correctionForm.originalEventId}
                onChange={(e) => {
                  const original = events.find(
                    (item) => item.id === e.currentTarget.value,
                  );
                  setCorrectionForm({
                    ...correctionForm,
                    originalEventId: e.currentTarget.value,
                    requestedEventType:
                      original?.eventType ?? correctionForm.requestedEventType,
                    requestedEventAt: original
                      ? new Date(original.eventAt).toISOString().slice(0, 16)
                      : correctionForm.requestedEventAt,
                  });
                }}
              >
                <option value="">—</option>
                {events
                  .filter(
                    (item) =>
                      item.workerId === correctionForm.workerId &&
                      !["superseded", "rejected"].includes(item.status),
                  )
                  .map((item) => (
                    <option key={item.id} value={item.id}>
                      {optionLabel(item.eventType, locale)} ·{" "}
                      {formatDateTime(item.eventAt, locale)}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              {locale === "es" ? "Evento solicitado" : "Requested event"}
              <select
                value={correctionForm.requestedEventType}
                onChange={(e) =>
                  setCorrectionForm({
                    ...correctionForm,
                    requestedEventType: e.currentTarget.value,
                  })
                }
              >
                <option value="clock_in">
                  {optionLabel("clock_in", locale)}
                </option>
                <option value="clock_out">
                  {optionLabel("clock_out", locale)}
                </option>
                <option value="meal_start">
                  {optionLabel("meal_start", locale)}
                </option>
                <option value="meal_end">
                  {optionLabel("meal_end", locale)}
                </option>
                <option value="project_transfer">
                  {optionLabel("project_transfer", locale)}
                </option>
              </select>
            </label>
            <label>
              {locale === "es"
                ? "Fecha y hora correctas"
                : "Correct date and time"}
              <input
                required
                type="datetime-local"
                value={correctionForm.requestedEventAt}
                onInput={(e) =>
                  setCorrectionForm({
                    ...correctionForm,
                    requestedEventAt: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Razón detallada" : "Detailed reason"}
              <input
                required
                minLength={3}
                value={correctionForm.reason}
                onChange={(e) =>
                  setCorrectionForm({
                    ...correctionForm,
                    reason: e.currentTarget.value,
                  })
                }
              />
            </label>
          </div>
          <button
            className="primary"
            disabled={busy || !correctionForm.workerId}
          >
            {locale === "es" ? "Solicitar corrección" : "Request correction"}
          </button>
        </form>
        <ResponsiveDataTable
          label={
            locale === "es"
              ? "Tabla de correcciones de tiempo"
              : "Time corrections table"
          }
          caption={
            locale === "es"
              ? "Correcciones por persona, cambio solicitado, fecha, razón, estado y acciones"
              : "Corrections by worker, requested change, date, reason, status, and actions"
          }
          columns={
            locale === "es"
              ? [
                  "Persona",
                  "Cambio solicitado",
                  "Fecha y hora",
                  "Razón y revisión",
                  "Estado",
                  "Acciones",
                ]
              : [
                  "Worker",
                  "Requested change",
                  "Date and time",
                  "Reason and review",
                  "Status",
                  "Actions",
                ]
          }
          empty={
            locale === "es"
              ? "No hay correcciones de tiempo."
              : "There are no time corrections."
          }
          rows={corrections.map((item) => {
            const change = `${item.originalEventType ? `${optionLabel(item.originalEventType, locale)} → ` : "+ "}${optionLabel(item.requestedEventType, locale)}`;
            const reason = `${item.reason}${item.reviewReason ? ` · ${item.reviewReason}` : ""}`;
            return {
              key: item.id,
              cells: [
                <strong>{item.workerName}</strong>,
                change,
                formatDateTime(item.requestedEventAt, locale),
                reason,
                <span className={`run-status ${item.status}`}>
                  {optionLabel(item.status, locale)}
                </span>,
                correctionActions(item),
              ],
              card: (
                <article key={item.id}>
                  <div>
                    <strong>{item.workerName}</strong>
                    <p>
                      {change} · {formatDateTime(item.requestedEventAt, locale)}
                    </p>
                    <small>{reason}</small>
                  </div>
                  <span className={`run-status ${item.status}`}>
                    {optionLabel(item.status, locale)}
                  </span>
                  {correctionActions(item)}
                </article>
              ),
            };
          })}
        />
      </section>
      {!professionalServicesView && (
        <section className="settings-form">
          <div className="section-heading">
            <div>
              <h2>
                {locale === "es"
                  ? "Evaluación laboral de Puerto Rico"
                  : "Puerto Rico labor evaluation"}
              </h2>
              <p>
                {locale === "es"
                  ? "Evalúa eventos registrados contra horas extra diarias/semanales, pago estatutario del séptimo día y períodos de alimentos."
                  : "Evaluates recorded events for daily/weekly overtime, statutory seventh-day pay, and meal periods."}
              </p>
            </div>
            <div className="form-grid">
              <label>
                {locale === "es" ? "Desde" : "From"}
                <input
                  type="date"
                  value={laborFrom}
                  onInput={(e) => setLaborFrom(e.currentTarget.value)}
                />
              </label>
              <label>
                {locale === "es"
                  ? "Hasta (máximo 7 días)"
                  : "To (7 days maximum)"}
                <input
                  type="date"
                  value={laborTo}
                  onInput={(e) => setLaborTo(e.currentTarget.value)}
                />
              </label>
            </div>
          </div>
          {laborError ? (
            <div className="notice error" role="alert">
              <strong>
                {locale === "es"
                  ? "No se pudo evaluar este período."
                  : "This period could not be evaluated."}
              </strong>
              <p>
                {locale === "es"
                  ? "Revise las fechas y la configuración de jornada; luego vuelva a intentar."
                  : "Review the dates and workweek configuration, then try again."}
              </p>
              <small>{laborError}</small>
              <button type="button" onClick={() => void load()}>
                {locale === "es" ? "Volver a intentar" : "Try again"}
              </button>
            </div>
          ) : labor.length === 0 ? (
            <p>
              {locale === "es"
                ? "No hay pares de entrada/salida en este período."
                : "No clock-in/out pairs in this period."}
            </p>
          ) : (
            <div className="run-list">
              {labor.map((item) => (
                <article key={item.workerId}>
                  <div>
                    <strong>{item.workerName}</strong>
                    <p>
                      {Math.round(item.workedMinutes / 6) / 10} h ·{" "}
                      {locale === "es" ? "extra diaria" : "daily OT"}{" "}
                      {Math.round(item.dailyOvertimeMinutes / 6) / 10} h ·{" "}
                      {locale === "es" ? "extra semanal" : "weekly OT"}{" "}
                      {Math.round(item.weeklyOvertimeMinutes / 6) / 10} h
                      {item.seventhDayMinutes > 0
                        ? ` · ${locale === "es" ? "séptimo día" : "seventh day"} ${Math.round(item.seventhDayMinutes / 6) / 10} h (${formatDate(item.seventhDayWorkDate, locale)})`
                        : ""}
                    </p>
                    <small>
                      {item.ruleVersion} ·{" "}
                      {item.overtimeEligible
                        ? locale === "es"
                          ? `multiplicador ${item.overtimeMultiplier}×`
                          : `${item.overtimeMultiplier}× multiplier`
                        : locale === "es"
                          ? "clasificado no elegible"
                          : "classified ineligible"}
                      {item.seventhDayMinutes > 0
                        ? locale === "es"
                          ? " · se añadirá automáticamente a la nómina"
                          : " · automatically added to payroll"
                        : ""}
                      {item.alternateWorkdayReview
                        ? locale === "es"
                          ? " · horario alterno requiere resolución"
                          : " · alternate workday requires resolution"
                        : ""}
                    </small>
                    {item.eventIssues.length > 0 && (
                      <ul className="issue-list">
                        {item.eventIssues.map((issue) => (
                          <li key={issue}>
                            {issue.startsWith("flexible_core_hours_missing:")
                              ? locale === "es"
                                ? `No cubrió horas núcleo: ${issue.split(":").slice(1).join(" · ")}`
                                : `Core hours not covered: ${issue.split(":").slice(1).join(" · ")}`
                              : issue}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <span
                    className={
                      item.mealIssueCount ||
                      item.secondMealReviewCount ||
                      item.alternateWorkdayReview ||
                      item.eventIssues.length
                        ? "run-status diagnostic"
                        : "run-status calculated"
                    }
                  >
                    {item.mealIssueCount
                      ? `${item.mealIssueCount} ${locale === "es" ? "alimentos" : "meal"}`
                      : item.secondMealReviewCount
                        ? `${item.secondMealReviewCount} ${locale === "es" ? "segunda comida" : "second meal"}`
                        : item.alternateWorkdayReview
                          ? locale === "es"
                            ? "horario alterno"
                            : "alternate workday"
                          : item.seventhDayReview
                            ? locale === "es"
                              ? "7º día calculado"
                              : "7th day calculated"
                            : item.eventIssues.length
                              ? locale === "es"
                                ? "eventos incompletos"
                                : "event issues"
                              : locale === "es"
                                ? "sin señales"
                                : "no flags"}
                  </span>
                </article>
              ))}
            </div>
          )}
        </section>
      )}
      {canConfigure && (
        <section className="settings-form time-person-setup time-company-setup time-meal-setup">
          <div className="section-heading">
            <div>
              <h2>
                {workspace === "operations"
                  ? locale === "es"
                    ? "Alertas de períodos de comida"
                    : "Meal-period alerts"
                  : locale === "es"
                    ? "Reglas y acuerdos de períodos de comida"
                    : "Meal-period rules and agreements"}
              </h2>
              <p>
                {workspace === "operations"
                  ? locale === "es"
                    ? "Corrija la señal o registre la resolución aplicable."
                    : "Correct the signal or record the applicable resolution."
                  : locale === "es"
                    ? "Configure la regla general o documente un acuerdo individual con su evidencia."
                    : "Configure the company rule or document an individual agreement and its evidence."}
              </p>
            </div>
          </div>
          <form
            className="compact-form time-company-setup-only"
            onSubmit={saveMealAutoPolicy}
          >
            <h3>
              {locale === "es"
                ? "Regla general de descuento automático"
                : "Company automatic-deduction rule"}
            </h3>
            <p>
              {locale === "es"
                ? "Aplica a todos los empleados. Si no existen ponches de comida y la jornada supera el umbral, descuenta los minutos indicados. Guardar crea una revisión vigente sin borrar el historial."
                : "Applies to all employees. When no meal punches exist and the shift exceeds the threshold, the configured minutes are deducted. Saving creates an effective revision without deleting history."}
            </p>
            {mealAutoPolicies[0] && (
              <p>
                <strong>
                  {locale === "es" ? "Regla vigente" : "Current rule"}:
                </strong>{" "}
                {mealAutoPolicies[0].deductionMinutes} min ·{" "}
                {locale === "es" ? "jornadas mayores de" : "shifts longer than"}{" "}
                {mealAutoPolicies[0].thresholdMinutes / 60} h ·{" "}
                {locale === "es" ? "desde" : "from"}{" "}
                {formatDate(mealAutoPolicies[0].effectiveFrom, locale)}
              </p>
            )}
            <div className="form-grid">
              <label>
                {locale === "es" ? "Vigente desde" : "Effective from"}
                <input
                  required
                  type="date"
                  value={mealAutoPolicyForm.effectiveFrom}
                  onInput={(event) =>
                    setMealAutoPolicyForm({
                      ...mealAutoPolicyForm,
                      effectiveFrom: event.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es"
                  ? "Umbral de jornada (minutos)"
                  : "Shift threshold (minutes)"}
                <input
                  required
                  type="number"
                  min="1"
                  max="1440"
                  value={mealAutoPolicyForm.thresholdMinutes}
                  onInput={(event) =>
                    setMealAutoPolicyForm({
                      ...mealAutoPolicyForm,
                      thresholdMinutes: event.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es"
                  ? "Descuento predeterminado (minutos)"
                  : "Default deduction (minutes)"}
                <input
                  required
                  type="number"
                  min="1"
                  max="240"
                  value={mealAutoPolicyForm.deductionMinutes}
                  onInput={(event) =>
                    setMealAutoPolicyForm({
                      ...mealAutoPolicyForm,
                      deductionMinutes: event.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Razón de la revisión" : "Revision reason"}
                <input
                  required
                  minLength={3}
                  value={mealAutoPolicyForm.reason}
                  onChange={(event) =>
                    setMealAutoPolicyForm({
                      ...mealAutoPolicyForm,
                      reason: event.currentTarget.value,
                    })
                  }
                />
              </label>
            </div>
            <button className="primary" disabled={busy}>
              {locale === "es" ? "Guardar nueva revisión" : "Save new revision"}
            </button>
          </form>
          <div className="run-list time-operations-only">
            {labor.flatMap((item) =>
              item.days
                .filter(
                  (day) =>
                    day.mealIssueUnresolved || day.secondMealReviewUnresolved,
                )
                .map((day) => (
                  <article key={`${item.workerId}-${day.workDate}`}>
                    <div>
                      <strong>
                        {item.workerName} · {formatDate(day.workDate, locale)}
                      </strong>
                      <p>
                        {day.workedMinutes} min{" "}
                        {locale === "es" ? "trabajados" : "worked"} ·{" "}
                        {day.mealMinutes} min{" "}
                        {locale === "es" ? "alimentos" : "meal"} ·{" "}
                        {locale === "es" ? "mínimo" : "minimum"}{" "}
                        {day.minimumMealMinutes}
                      </p>
                    </div>
                    <div className="run-actions">
                      {day.mealIssueUnresolved && (
                        <button
                          type="button"
                          onClick={() =>
                            void resolveMeal(
                              item.workerId,
                              day.workDate,
                              "first_meal",
                              false,
                            )
                          }
                        >
                          {locale === "es"
                            ? "Resolver primera comida"
                            : "Resolve first meal"}
                        </button>
                      )}
                      {day.secondMealReviewUnresolved && (
                        <button
                          type="button"
                          onClick={() =>
                            void resolveMeal(
                              item.workerId,
                              day.workDate,
                              "second_meal",
                              day.workedMinutes <= 720 &&
                                day.mealMinutes >= day.minimumMealMinutes,
                            )
                          }
                        >
                          {locale === "es"
                            ? "Resolver segunda comida"
                            : "Resolve second meal"}
                        </button>
                      )}
                    </div>
                  </article>
                )),
            )}
            {labor.every((item) =>
              item.days.every(
                (day) =>
                  !day.mealIssueUnresolved && !day.secondMealReviewUnresolved,
              ),
            ) && (
              <p>
                {locale === "es"
                  ? "No hay señales de alimentos sin resolver."
                  : "There are no unresolved meal-period signals."}
              </p>
            )}
          </div>
          <form
            className="compact-form time-person-setup-only"
            onSubmit={createMealAgreement}
          >
            <h3>
              {locale === "es"
                ? "Acuerdo escrito de período reducido"
                : "Written reduced-period agreement"}
            </h3>
            <div className="form-grid">
              <label>
                {locale === "es" ? "Persona" : "Person"}
                <select
                  required
                  value={mealAgreementForm.workerId}
                  onChange={(e) =>
                    setMealAgreementForm({
                      ...mealAgreementForm,
                      workerId: e.currentTarget.value,
                    })
                  }
                >
                  <option value="">—</option>
                  {workspaceWorkers.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.preferredName || w.firstName} {w.lastName}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {locale === "es" ? "Minutos" : "Minutes"}
                <select
                  value={mealAgreementForm.minimumMealMinutes}
                  onChange={(e) =>
                    setMealAgreementForm({
                      ...mealAgreementForm,
                      minimumMealMinutes: e.currentTarget.value,
                    })
                  }
                >
                  <option value="30">30</option>
                  <option value="20">20</option>
                </select>
              </label>
              <label>
                {locale === "es" ? "Base ocupacional" : "Occupation basis"}
                <select
                  value={mealAgreementForm.occupationBasis}
                  onChange={(e) =>
                    setMealAgreementForm({
                      ...mealAgreementForm,
                      occupationBasis: e.currentTarget.value,
                    })
                  }
                >
                  <option value="general">
                    {locale === "es"
                      ? "General (mínimo 30)"
                      : "General (30 minimum)"}
                  </option>
                  <option value="dealer">
                    {optionLabel("dealer", locale)}
                  </option>
                  <option value="nurse">
                    {locale === "es" ? "Enfermería" : "Nursing"}
                  </option>
                  <option value="security_guard">
                    {locale === "es"
                      ? "Guardia de seguridad"
                      : "Security guard"}
                  </option>
                </select>
              </label>
              <label>
                {locale === "es" ? "Vigente desde" : "Effective from"}
                <input
                  required
                  type="date"
                  value={mealAgreementForm.effectiveFrom}
                  onInput={(e) =>
                    setMealAgreementForm({
                      ...mealAgreementForm,
                      effectiveFrom: e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Vigente hasta (opcional)" : "Effective to (optional)"}
                <input
                  type="date"
                  value={mealAgreementForm.effectiveTo}
                  onInput={(e) =>
                    setMealAgreementForm({
                      ...mealAgreementForm,
                      effectiveTo: e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Firma empleado" : "Employee signed"}
                <input
                  required
                  type="date"
                  value={mealAgreementForm.employeeSignedOn}
                  onInput={(e) =>
                    setMealAgreementForm({
                      ...mealAgreementForm,
                      employeeSignedOn: e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Firma patrono" : "Employer signed"}
                <input
                  required
                  type="date"
                  value={mealAgreementForm.employerSignedOn}
                  onInput={(e) =>
                    setMealAgreementForm({
                      ...mealAgreementForm,
                      employerSignedOn: e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Razón" : "Reason"}
                <input
                  required
                  minLength={3}
                  value={mealAgreementForm.reason}
                  onChange={(e) =>
                    setMealAgreementForm({
                      ...mealAgreementForm,
                      reason: e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es"
                  ? "Acuerdo firmado (PDF/imagen)"
                  : "Signed agreement (PDF/image)"}
                <input
                  required
                  type="file"
                  accept="application/pdf,image/jpeg,image/png,image/webp"
                  onChange={(e) =>
                    setMealAgreementForm({
                      ...mealAgreementForm,
                      evidence: e.currentTarget.files?.[0] ?? null,
                    })
                  }
                />
              </label>
            </div>
            <button
              className="primary"
              disabled={
                busy ||
                !mealAgreementForm.workerId ||
                !mealAgreementForm.evidence
              }
            >
              {locale === "es" ? "Retener acuerdo" : "Retain agreement"}
            </button>
          </form>
          <h3>
            {locale === "es" ? "Historial inmutable" : "Immutable history"}
          </h3>
          <div className="run-list">
            {mealResolutions.map((item) => (
              <article key={item.id}>
                <div>
                  <strong>
                    {item.workerName} · {formatDate(item.workDate, locale)}
                  </strong>
                  <p>
                    {optionLabel(item.issueKind, locale)} ·{" "}
                    {optionLabel(item.resolutionType, locale)} ·{" "}
                    {item.penaltyMinutes} min @{" "}
                    {(item.penaltyMultiplierBps / 10000).toFixed(2)}×
                  </p>
                  <small>{item.reason}</small>
                </div>
              </article>
            ))}
            {mealAgreements.map((item) => (
              <article key={item.id}>
                <div>
                  <strong>
                    {item.workerName} · {item.minimumMealMinutes} min
                  </strong>
                  <p>
                    {formatDateRange(
                      item.effectiveFrom,
                      item.effectiveTo,
                      locale,
                      locale === "es" ? "vigente" : "current",
                    )}{" "}
                    · {optionLabel(item.occupationBasis, locale)}
                  </p>
                  <button
                    type="button"
                    onClick={() =>
                      window.open(
                        `${API}/v1/tenants/${tenant.id}/meal-period-agreements/${item.id}/evidence`,
                        "_blank",
                        "noopener,noreferrer",
                      )
                    }
                  >
                    {locale === "es" ? "Abrir evidencia" : "Open evidence"}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}
      <section
        className="settings-form time-workflow-step time-workflow-step-3"
        id="timesheet-approval"
      >
        <div className="section-heading">
          <div>
            <h2>
              {locale === "es"
                ? "3) Aprobación de hojas de tiempo"
                : "3) Timesheet approval"}
            </h2>
            <p>
              {locale === "es"
                ? "Esta es la única sección donde se aprueba la hoja. Revise las horas y complete las etapas hasta cerrar el período; cada transición queda auditada."
                : "This is the only section where the timesheet is approved. Review the hours and complete every stage through final closure; each transition is audited."}
            </p>
            {timeFocus &&
              (() => {
                const worker = workers.find(
                  (item) => item.id === timeFocus.workerId,
                );
                return (
                  <p>
                    <strong>
                      {locale === "es" ? "Desde Nómina" : "From Payroll"}:
                    </strong>{" "}
                    {worker
                      ? `${worker.firstName} ${worker.lastName ?? ""}`.trim()
                      : ""}{" "}
                    ·{" "}
                    {formatDateRange(
                      timeFocus.periodStart,
                      timeFocus.periodEnd,
                      locale,
                      "—",
                    )}
                  </p>
                );
              })()}
          </div>
        </div>
        {timeFocus &&
          (() => {
            const focusedEvents = events.filter(
              (event) =>
                event.workerId === timeFocus.workerId &&
                event.eventAt.slice(0, 10) >= timeFocus.periodStart &&
                event.eventAt.slice(0, 10) <= timeFocus.periodEnd,
            );
            return (
              <div className="operation-panel">
                <h3>
                  {locale === "es"
                    ? "Ponches importados del período"
                    : "Imported punches for the period"}
                </h3>
                <p>
                  {locale === "es"
                    ? `${focusedEvents.length} eventos encontrados en Cloudflare.`
                    : `${focusedEvents.length} events found in Cloudflare.`}
                </p>
                {focusedEvents.length > 0 && (
                  <div className="run-list">
                    {focusedEvents.map((event) => (
                      <article key={event.id}>
                        <div>
                          <strong>
                            {formatDateTime(event.eventAt, locale)}
                          </strong>
                          <small>
                            {optionLabel(event.eventType, locale)} ·{" "}
                            {event.comment ?? optionLabel(event.source, locale)}
                          </small>
                        </div>
                        <span className={`run-status ${event.status}`}>
                          {optionLabel(event.status, locale)}
                        </span>
                      </article>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        <div className="timesheet-approval-guide">
          <div className="form-grid timesheet-approval-selectors">
            <label>
              {locale === "es"
                ? "Persona con hoja pendiente"
                : "Person with pending timesheet"}
              <select
                value={approvalWorkerId}
                onChange={(event) => {
                  setApprovalWorkerId(event.currentTarget.value);
                  setApprovalRunId("");
                  setApprovalRows([]);
                  setApprovalMessage("");
                }}
              >
                <option value="">—</option>
                {approvalWorkers.map((worker) => (
                  <option key={worker.id} value={worker.id}>
                    {worker.preferredName || worker.firstName}{" "}
                    {worker.lastName ?? ""}
                  </option>
                ))}
              </select>
            </label>
            {approvalWorkerId && (
              <label>
                {locale === "es"
                  ? "Período pendiente de aprobación"
                  : "Period pending approval"}
                <select
                  value={approvalRunId}
                  onChange={(event) => {
                    setApprovalRunId(event.currentTarget.value);
                    setApprovalMessage("");
                  }}
                >
                  <option value="">—</option>
                  {approvalPeriodOptions.map((run) => (
                    <option key={run.id} value={run.id}>
                      {formatDate(run.periodStart, locale)} —{" "}
                      {formatDate(run.periodEnd, locale)}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          {approvalRun && (
            <section className="operation-panel timesheet-guided-review">
              <div className="section-heading">
                <div>
                  <h3>
                    {locale === "es" ? "Revisión del período" : "Period review"}
                  </h3>
                  <p>
                    {formatDateRange(
                      approvalRun.periodStart,
                      approvalRun.periodEnd,
                      locale,
                      "—",
                    )}
                  </p>
                </div>
                <button type="button" onClick={editApprovalTimesheet}>
                  {locale === "es" ? "Editar hoja de tiempo" : "Edit timesheet"}
                </button>
              </div>
              <div className="summary-strip">
                <span>
                  {locale === "es" ? "Tiempo trabajado" : "Worked time"}
                  <strong>
                    {approvalAttendanceMinutes > 0
                      ? `${(approvalAttendanceMinutes / 60).toFixed(2)} h`
                      : approvalLaborSummary
                        ? `${(approvalLaborSummary.workedMinutes / 60).toFixed(2)} h`
                        : "—"}
                  </strong>
                  {approvalAttendanceDays > 0 && (
                    <small>
                      {approvalAttendanceDays}{" "}
                      {locale === "es" ? "días" : "days"}
                      {" × "}
                      {(
                        approvalAttendanceMinutes /
                        approvalAttendanceDays /
                        60
                      ).toFixed(2)}{" "}
                      h
                    </small>
                  )}
                </span>
                <span>
                  {locale === "es" ? "Horas extra" : "Overtime"}
                  <strong>
                    {approvalIsContractor
                      ? locale === "es"
                        ? "No aplica"
                        : "Not applicable"
                      : approvalLaborSummary
                        ? `${(approvalLaborSummary.totalOvertimeMinutes / 60).toFixed(2)} h`
                        : "—"}
                  </strong>
                </span>
                <span>
                  {locale === "es" ? "Licencia aprobada" : "Approved leave"}
                  <strong>{approvalLeaveHours.toFixed(2)} h</strong>
                </span>
                <span>
                  {locale === "es" ? "Feriado pagado" : "Paid holiday"}
                  <strong>
                    {approvalIsContractor
                      ? locale === "es"
                        ? "No aplica"
                        : "Not applicable"
                      : `${approvalHolidayHours.toFixed(2)} h`}
                  </strong>
                </span>
                <span>
                  {locale === "es" ? "Total pagadero" : "Total payable"}
                  <strong>
                    {(
                      (approvalAttendanceMinutes > 0
                        ? approvalAttendanceMinutes
                        : (approvalLaborSummary?.workedMinutes ?? 0)) /
                        60 +
                      approvalLeaveHours +
                      approvalHolidayHours
                    ).toFixed(2)}{" "}
                    h
                  </strong>
                </span>
                <span>
                  {locale === "es" ? "Alertas" : "Alerts"}
                  <strong>{approvalAlertCount}</strong>
                </span>
              </div>
              {approvalAlerts.length ? (
                <section className="timesheet-review-alerts">
                  <div className="section-heading">
                    <div>
                      <h4>
                        {locale === "es"
                          ? "Alertas que requieren atención"
                          : "Alerts requiring attention"}
                      </h4>
                      <p>
                        {approvalBlockingAlertCount
                          ? locale === "es"
                            ? `${approvalBlockingAlertCount} deben resolverse antes de aprobar. Cada alerta indica qué hacer.`
                            : `${approvalBlockingAlertCount} must be resolved before approval. Each alert shows what to do.`
                          : locale === "es"
                            ? "Estas alertas son informativas y no bloquean la aprobación."
                            : "These alerts are advisory and do not block approval."}
                      </p>
                    </div>
                  </div>
                  <div className="run-list">
                    {approvalAlerts.map((alert) => (
                      <article key={alert.key}>
                        <div>
                          <strong>{alert.title}</strong>
                          <small>{alert.detail}</small>
                        </div>
                        <div className="run-actions">
                          {alert.kind === "first_meal" && alert.workDate ? (
                            <button
                              type="button"
                              onClick={() =>
                                resolveMeal(
                                  approvalWorkerId,
                                  alert.workDate!,
                                  "first_meal",
                                  false,
                                )
                              }
                            >
                              {locale === "es"
                                ? "Resolver comida"
                                : "Resolve meal"}
                            </button>
                          ) : alert.kind === "second_meal" && alert.workDate ? (
                            <button
                              type="button"
                              onClick={() =>
                                resolveMeal(
                                  approvalWorkerId,
                                  alert.workDate!,
                                  "second_meal",
                                  false,
                                )
                              }
                            >
                              {locale === "es"
                                ? "Resolver segunda comida"
                                : "Resolve second meal"}
                            </button>
                          ) : alert.kind === "classification" ? (
                            <button
                              type="button"
                              onClick={() =>
                                openApprovalPersonSetup("overtime")
                              }
                            >
                              {locale === "es"
                                ? "Revisar clasificación"
                                : "Review classification"}
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={editApprovalTimesheet}
                            >
                              {locale === "es"
                                ? "Corregir hoja de tiempo"
                                : "Correct timesheet"}
                            </button>
                          )}
                          {alert.kind === "schedule" && (
                            <>
                              <button
                                type="button"
                                onClick={() => openApprovalPersonSetup("pay")}
                              >
                                {locale === "es"
                                  ? "Revisar horario personal"
                                  : "Review personal schedule"}
                              </button>
                              <button
                                type="button"
                                className="primary"
                                disabled={busy}
                                onClick={() =>
                                  void acceptApprovalScheduleAlert(alert)
                                }
                              >
                                <Check />
                                {locale === "es"
                                  ? "Aceptar como está"
                                  : "Accept as recorded"}
                              </button>
                            </>
                          )}
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              ) : approvalLaborSummary ? (
                <p className="success-note" role="status">
                  {locale === "es"
                    ? "No hay alertas pendientes para este período."
                    : "There are no pending alerts for this period."}
                </p>
              ) : null}
              <ResponsiveDataTable
                label={
                  locale === "es"
                    ? "Datos de la hoja que se aprobará"
                    : "Timesheet data to approve"
                }
                caption={
                  locale === "es"
                    ? "Horas, comida, asignación y regla por día"
                    : "Hours, meal, assignment, and rule by day"
                }
                columns={
                  locale === "es"
                    ? [
                        "Día",
                        "Entrada",
                        "Salida",
                        "Almuerzo",
                        "Cargar a",
                        "Regla",
                      ]
                    : ["Day", "In", "Out", "Meal", "Charge to", "Rule"]
                }
                empty={
                  locale === "es"
                    ? "No hay datos registrados para este período."
                    : "No recorded data exists for this period."
                }
                rows={approvalRows.map((row) => {
                  const allocation = row.departmentId
                      ? allocationDepartments.find(
                          (item) => item.id === row.departmentId,
                        )?.name
                      : row.locationId
                        ? allocationLocations.find(
                            (item) => item.id === row.locationId,
                          )?.name
                        : row.costCenterId
                          ? allocationCostCenters.find(
                              (item) => item.id === row.costCenterId,
                            )?.name
                          : locale === "es"
                            ? "Asignación principal"
                            : "Home assignment",
                    rule = row.holidayName
                      ? row.holidayName
                      : row.leaveName
                        ? row.leaveName
                        : row.attendanceStatus === "contractor_absent"
                          ? locale === "es"
                            ? "Contratista ausente"
                            : "Contractor absent"
                          : row.sourceCode ||
                            (locale === "es" ? "Ninguna" : "None"),
                    cells: ReactNode[] = [
                      formatDate(row.workDate, locale),
                      row.clockIn ?? "—",
                      row.clockOut ?? "—",
                      row.holidayName ? "—" : `${row.mealMinutes} min`,
                      allocation ?? "—",
                      rule,
                    ];
                  return {
                    key: row.workDate,
                    cells,
                    card: (
                      <article key={row.workDate}>
                        <div>
                          <strong>{formatDate(row.workDate, locale)}</strong>
                          <p>
                            {row.clockIn ?? "—"} — {row.clockOut ?? "—"} ·{" "}
                            {row.holidayName
                              ? `${(row.holidayHoursHundredths / 100).toFixed(2)} h`
                              : `${row.mealMinutes} min`}
                          </p>
                          <small>
                            {allocation ?? "—"} · {rule}
                          </small>
                        </div>
                      </article>
                    ),
                  };
                })}
              />
              <div className="timesheet-review-comments">
                <h4>
                  {locale === "es" ? "Comentarios diarios" : "Daily comments"}
                </h4>
                {approvalComments.length ? (
                  <div className="run-list">
                    {approvalComments.map((comment) => (
                      <article key={comment.id}>
                        <div>
                          <strong>
                            {formatDate(comment.workDate, locale)}
                          </strong>
                          <p>{comment.comment}</p>
                          {comment.reviewComment && (
                            <small>{comment.reviewComment}</small>
                          )}
                        </div>
                        <span className={`run-status ${comment.status}`}>
                          {optionLabel(comment.status, locale)}
                        </span>
                      </article>
                    ))}
                  </div>
                ) : (
                  <p className="empty-state">
                    {locale === "es"
                      ? "No hay comentarios diarios para este período."
                      : "There are no daily comments for this period."}
                  </p>
                )}
              </div>
              <div className="timesheet-review-statuses">
                {selectedApprovalSheets.length ? (
                  selectedApprovalSheets.map((sheet) => (
                    <span
                      key={sheet.id}
                      className={`run-status ${sheet.status}`}
                    >
                      {formatDateRange(
                        sheet.periodStart,
                        sheet.periodEnd,
                        locale,
                        "—",
                      )}
                      {" · "}
                      {optionLabel(sheet.status, locale)}
                    </span>
                  ))
                ) : (
                  <span className="run-status open">
                    {locale === "es"
                      ? "Pendiente de someter"
                      : "Awaiting submission"}
                  </span>
                )}
              </div>
              {approvalMessage && (
                <p className="success-note" role="status">
                  {approvalMessage}
                </p>
              )}
              {approvalTarget && (
                <div className="inline-actions">
                  <button
                    type="button"
                    className="primary"
                    disabled={isTimesheetApprovalDisabled({
                      busy,
                      contractor: approvalIsContractor,
                      blockingAlertCount: approvalBlockingAlertCount,
                      approvalRowCount: approvalRows.length,
                    })}
                    onClick={() => void saveApprovalSelection()}
                  >
                    <Check />
                    {approvalTarget === "submitted"
                      ? locale === "es"
                        ? `Guardar y someter semana ${approvalActionRange}`
                        : `Save and submit week ${approvalActionRange}`
                      : approvalTarget === "supervisor_approved"
                        ? locale === "es"
                          ? `Aprobar semana ${approvalActionRange} como supervisor`
                          : `Supervisor approve week ${approvalActionRange}`
                        : approvalTarget === "payroll_approved"
                          ? locale === "es"
                            ? `Aprobar semana ${approvalActionRange} para compensación`
                            : `Approve week ${approvalActionRange} for compensation`
                          : locale === "es"
                            ? `Cerrar semana ${approvalActionRange}`
                            : `Close week ${approvalActionRange}`}
                  </button>
                </div>
              )}
            </section>
          )}
        </div>
        <div className="timesheet-approval-legacy" aria-hidden="true">
          <ResponsiveDataTable
            label={
              locale === "es"
                ? "Tabla de aprobación de hojas de tiempo"
                : "Timesheet approval table"
            }
            caption={
              locale === "es"
                ? "Hojas de tiempo por persona, período, licencia, estado, historial y acciones"
                : "Timesheets by worker, period, leave, status, history, and actions"
            }
            columns={
              locale === "es"
                ? [
                    "Persona",
                    "Período",
                    "Licencia aprobada",
                    "Estado",
                    "Historial",
                    "Acciones",
                  ]
                : [
                    "Worker",
                    "Period",
                    "Approved leave",
                    "Status",
                    "History",
                    "Actions",
                  ]
            }
            empty={
              locale === "es"
                ? "No hay hojas de tiempo para revisar."
                : "There are no timesheets to review."
            }
            rows={timesheets
              .filter(
                (sheet) =>
                  !timeFocus ||
                  (sheet.workerId === timeFocus.workerId &&
                    sheet.periodEnd >= timeFocus.periodStart &&
                    sheet.periodStart <= timeFocus.periodEnd),
              )
              .map((sheet) => {
                const leaveText = sheet.leaveSegments.length
                  ? sheet.leaveSegments
                      .map(
                        (segment) =>
                          `${formatDate(segment.workDate, locale)} · ${(segment.hoursHundredths / 100).toFixed(2)} h · ${segment.policyName}`,
                      )
                      .join("; ")
                  : "—";
                const historyText = sheet.reopenCount
                  ? `${locale === "es" ? "Reaperturas" : "Reopens"}: ${sheet.reopenCount} · ${sheet.latestTransitionReason ?? "—"}`
                  : (sheet.latestTransitionReason ?? "—");
                return {
                  key: sheet.id,
                  cells: [
                    <strong>{sheet.workerName}</strong>,
                    formatDateRange(
                      sheet.periodStart,
                      sheet.periodEnd,
                      locale,
                      "—",
                    ),
                    leaveText,
                    <span className={`run-status ${sheet.status}`}>
                      {optionLabel(sheet.status, locale)}
                    </span>,
                    historyText,
                    timesheetActions(sheet),
                  ],
                  card: (
                    <article key={sheet.id}>
                      <div>
                        <strong>{sheet.workerName}</strong>
                        <p>
                          {formatDateRange(
                            sheet.periodStart,
                            sheet.periodEnd,
                            locale,
                            "—",
                          )}
                        </p>
                        <small>{historyText}</small>
                        {sheet.leaveSegments.length > 0 && (
                          <p>
                            <strong>
                              {locale === "es"
                                ? "Horas de licencia aprobadas"
                                : "Approved leave hours"}
                              :
                            </strong>{" "}
                            {leaveText}
                          </p>
                        )}
                      </div>
                      <span className={`run-status ${sheet.status}`}>
                        {optionLabel(sheet.status, locale)}
                      </span>
                      {timesheetActions(sheet)}
                    </article>
                  ),
                };
              })}
          />
          {viewingTimesheetId &&
            (() => {
              const sheet = timesheets.find(
                (item) => item.id === viewingTimesheetId,
              );
              if (!sheet) return null;
              const sheetEvents = events
                  .filter(
                    (event) =>
                      event.workerId === sheet.workerId &&
                      event.eventAt.slice(0, 10) >= sheet.periodStart &&
                      event.eventAt.slice(0, 10) <= sheet.periodEnd,
                  )
                  .sort((a, b) => a.eventAt.localeCompare(b.eventAt)),
                evaluation = labor.find(
                  (item) => item.workerId === sheet.workerId,
                ),
                leaveHours =
                  sheet.leaveSegments.reduce(
                    (total, segment) => total + segment.hoursHundredths,
                    0,
                  ) / 100;
              return (
                <section
                  className="operation-panel"
                  id="timesheet-detail"
                  aria-live="polite"
                >
                  <div className="section-heading">
                    <div>
                      <h3>
                        {locale === "es"
                          ? "Detalle de la hoja"
                          : "Timesheet detail"}
                      </h3>
                      <p>
                        <strong>{sheet.workerName}</strong> ·{" "}
                        {formatDateRange(
                          sheet.periodStart,
                          sheet.periodEnd,
                          locale,
                          "—",
                        )}
                      </p>
                    </div>
                    <span className={`run-status ${sheet.status}`}>
                      {optionLabel(sheet.status, locale)}
                    </span>
                  </div>
                  <div className="summary-strip">
                    <span>
                      {locale === "es" ? "Tiempo trabajado" : "Worked time"}
                      <strong>
                        {evaluation
                          ? `${(evaluation.workedMinutes / 60).toFixed(2)} h`
                          : "—"}
                      </strong>
                    </span>
                    <span>
                      {locale === "es" ? "Horas extra" : "Overtime"}
                      <strong>
                        {evaluation
                          ? `${(evaluation.totalOvertimeMinutes / 60).toFixed(2)} h`
                          : "—"}
                      </strong>
                    </span>
                    <span>
                      {locale === "es" ? "Licencia aprobada" : "Approved leave"}
                      <strong>{leaveHours.toFixed(2)} h</strong>
                    </span>
                    <span>
                      {locale === "es" ? "Alertas" : "Alerts"}
                      <strong>
                        {evaluation
                          ? evaluation.eventIssues.length +
                            evaluation.mealIssueCount +
                            evaluation.secondMealReviewCount
                          : "—"}
                      </strong>
                    </span>
                  </div>
                  {evaluation?.days.length ? (
                    <>
                      <h4>
                        {locale === "es" ? "Resumen diario" : "Daily summary"}
                      </h4>
                      <div className="run-list">
                        {evaluation.days.map((day) => (
                          <article key={day.workDate}>
                            <div>
                              <strong>
                                {formatDate(day.workDate, locale)} ·{" "}
                                {(day.workedMinutes / 60).toFixed(2)} h
                              </strong>
                              <small>
                                {day.autoDeductedMealMinutes
                                  ? locale === "es"
                                    ? `Comida predeterminada descontada: ${day.autoDeductedMealMinutes} min`
                                    : `Default meal deducted: ${day.autoDeductedMealMinutes} min`
                                  : locale === "es"
                                    ? `Comida registrada: ${day.mealMinutes} min`
                                    : `Recorded meal: ${day.mealMinutes} min`}
                              </small>
                            </div>
                          </article>
                        ))}
                      </div>
                    </>
                  ) : null}
                  {sheet.leaveSegments.length > 0 && (
                    <div className="run-list">
                      {sheet.leaveSegments.map((segment) => (
                        <article
                          key={`${segment.leaveRequestId}-${segment.workDate}`}
                        >
                          <div>
                            <strong>
                              {formatDate(segment.workDate, locale)} ·{" "}
                              {segment.policyName}
                            </strong>
                            <p>
                              {(segment.hoursHundredths / 100).toFixed(2)} h ·{" "}
                              {optionLabel(segment.leaveType, locale)}
                            </p>
                          </div>
                          <span className="run-status approved">
                            {locale === "es" ? "Aprobada" : "Approved"}
                          </span>
                        </article>
                      ))}
                    </div>
                  )}
                  <h4>
                    {locale === "es" ? "Entradas y salidas" : "Clock events"}
                  </h4>
                  {sheetEvents.length ? (
                    <div className="run-list">
                      {sheetEvents.map((event) => (
                        <article key={event.id}>
                          <div>
                            <strong>
                              {formatDateTime(event.eventAt, locale)}
                            </strong>
                            <small>
                              {optionLabel(event.eventType, locale)} ·{" "}
                              {event.comment ??
                                optionLabel(event.source, locale)}
                            </small>
                          </div>
                          {["open", "rejected"].includes(sheet.status) && (
                            <button
                              type="button"
                              onClick={() => correctTimeEvent(event)}
                            >
                              {locale === "es"
                                ? "Solicitar corrección"
                                : "Request correction"}
                            </button>
                          )}
                        </article>
                      ))}
                    </div>
                  ) : (
                    <p className="empty-state">
                      {locale === "es"
                        ? "No hay ponches dentro de esta hoja."
                        : "There are no punches in this timesheet."}
                    </p>
                  )}
                  {evaluation?.eventIssues.length ? (
                    <>
                      <h4>{locale === "es" ? "Alertas" : "Alerts"}</h4>
                      <ul className="issue-list">
                        {evaluation.eventIssues.map((issue) => (
                          <li key={issue}>{issue.replaceAll("_", " ")}</li>
                        ))}
                      </ul>
                    </>
                  ) : null}
                </section>
              );
            })()}
        </div>
      </section>
      <section className="settings-form time-workflow-step time-workflow-step-4 optional-workflow-step">
        <div className="optional-workflow-question">
          <div>
            <h2>
              {locale === "es" ? "4) Otras opciones" : "4) Other options"}
            </h2>
            <p>
              {locale === "es"
                ? "Estas herramientas permanecen disponibles para revisarlas más adelante."
                : "These tools remain available for review later."}
            </p>
          </div>
          <button
            type="button"
            aria-expanded={showOtherTimeOptions}
            onClick={() => setShowOtherTimeOptions((current) => !current)}
          >
            {showOtherTimeOptions
              ? locale === "es"
                ? "Ocultar opciones"
                : "Hide options"
              : locale === "es"
                ? "Ver otras opciones"
                : "View other options"}
          </button>
        </div>
        <ul className="other-time-options-list">
          <li>
            {locale === "es" ? "Excepciones de ponche" : "Punch exceptions"}
          </li>
          <li>
            {locale === "es" ? "Correcciones de tiempo" : "Time corrections"}
          </li>
          <li>
            {locale === "es"
              ? "Evaluación laboral de Puerto Rico"
              : "Puerto Rico labor evaluation"}
          </li>
          <li>
            {locale === "es"
              ? "Alertas de períodos de comida"
              : "Meal-period alerts"}
          </li>
          <li>
            {locale === "es"
              ? "Política sin registro de tiempo"
              : "No-tracking policy"}
          </li>
          <li>
            {locale === "es" ? "Horas por categoría" : "Categorized time"}
          </li>
          <li>
            {locale === "es"
              ? "Acuerdos de jornada semanal alterna"
              : "Alternate weekly schedules"}
          </li>
          <li>
            {locale === "es"
              ? "Solicitudes de cambio de horario"
              : "Schedule change requests"}
          </li>
        </ul>
      </section>
      {canConfigure && (
        <section className="settings-form time-person-setup">
          <div className="section-heading">
            <div>
              <h2>
                {locale === "es"
                  ? "Política gobernada sin registro de tiempo"
                  : "Governed no-tracking policy"}
              </h2>
              <p>
                {locale === "es"
                  ? "Solo aplica a servicios profesionales o a un empleado asalariado con decisión exenta vigente. Bloquea ponches y hojas; no permite ocultar horas de un empleado por hora o no exento."
                  : "Only professional services or a salaried employee with a current exempt decision qualifies. It blocks punches and timesheets and cannot hide hours for an hourly or nonexempt employee."}
              </p>
            </div>
          </div>
          <form className="compact-form" onSubmit={createNoTracking}>
            <div className="form-grid">
              <label>
                {locale === "es"
                  ? "Persona configurada con método Ninguno"
                  : "Person configured with None method"}
                <select
                  required
                  value={noTrackingForm.workerId}
                  onChange={(e) => {
                    const worker = workers.find(
                      (item) => item.id === e.currentTarget.value,
                    );
                    setNoTrackingForm({
                      ...noTrackingForm,
                      workerId: e.currentTarget.value,
                      basis:
                        worker?.workerType === "professional_services"
                          ? "professional_services"
                          : "exempt_salary",
                    });
                  }}
                >
                  <option value="">—</option>
                  {workspaceWorkers
                    .filter((worker) => worker.timeEntryMethod === "none")
                    .map((worker) => (
                      <option key={worker.id} value={worker.id}>
                        {worker.preferredName || worker.firstName}{" "}
                        {worker.lastName}
                      </option>
                    ))}
                </select>
              </label>
              <label>
                {locale === "es" ? "Base" : "Basis"}
                <select
                  value={noTrackingForm.basis}
                  onChange={(e) =>
                    setNoTrackingForm({
                      ...noTrackingForm,
                      basis: e.currentTarget.value,
                    })
                  }
                >
                  <option value="professional_services">
                    {locale === "es"
                      ? "Servicios profesionales"
                      : "Professional services"}
                  </option>
                  <option value="exempt_salary">
                    {locale === "es"
                      ? "Salario exento gobernado"
                      : "Governed exempt salary"}
                  </option>
                </select>
              </label>
              <label>
                {locale === "es" ? "Vigente desde" : "Effective from"}
                <input
                  required
                  type="date"
                  value={noTrackingForm.effectiveFrom}
                  onInput={(e) =>
                    setNoTrackingForm({
                      ...noTrackingForm,
                      effectiveFrom: e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Vigente hasta (opcional)" : "Effective to (optional)"}
                <input
                  type="date"
                  value={noTrackingForm.effectiveTo}
                  onInput={(e) =>
                    setNoTrackingForm({
                      ...noTrackingForm,
                      effectiveTo: e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Fuente legal HTTPS" : "HTTPS legal source"}
                <input
                  required
                  type="url"
                  value={noTrackingForm.legalSourceUrl}
                  onChange={(e) =>
                    setNoTrackingForm({
                      ...noTrackingForm,
                      legalSourceUrl: e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Razón documentada" : "Documented reason"}
                <input
                  required
                  minLength={10}
                  maxLength={1000}
                  value={noTrackingForm.reason}
                  onChange={(e) =>
                    setNoTrackingForm({
                      ...noTrackingForm,
                      reason: e.currentTarget.value,
                    })
                  }
                />
              </label>
            </div>
            <button
              className="primary"
              disabled={busy || !noTrackingForm.workerId}
            >
              {locale === "es" ? "Crear política" : "Create policy"}
            </button>
          </form>
          <div className="run-list">
            {noTrackingPolicies.map((item) => (
              <article key={item.id}>
                <div>
                  <strong>
                    {item.workerName} · {optionLabel(item.basis, locale)}
                  </strong>
                  <p>
                    {formatDateRange(
                      item.effectiveFrom,
                      item.effectiveTo,
                      locale,
                      locale === "es" ? "abierto" : "open",
                    )}{" "}
                    · {item.reason}
                  </p>
                  <a
                    className="official-source-link"
                    href={item.legalSourceUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {locale === "es" ? "Fuente legal" : "Legal source"}
                  </a>
                  {item.terminatedEffectiveOn && (
                    <small>
                      {locale === "es" ? "Terminada" : "Ended"}:{" "}
                      {formatDate(item.terminatedEffectiveOn, locale)} ·{" "}
                      {item.terminationReason}
                    </small>
                  )}
                </div>
                {!item.terminationId && (
                  <button
                    type="button"
                    onClick={() => void endNoTracking(item)}
                  >
                    {locale === "es" ? "Terminar" : "End"}
                  </button>
                )}
              </article>
            ))}
          </div>
        </section>
      )}
      <section className="settings-form">
        <div className="section-heading">
          <div>
            <h2>
              {locale === "es" ? "Horas por categoría" : "Categorized time"}
            </h2>
            <p>
              {locale === "es"
                ? "Registre trabajo, adiestramiento, descansos, viaje y otro tiempo pagado o no pagado. El sistema deriva las horas extra; nunca se seleccionan manualmente."
                : "Record work, training, breaks, travel, and other paid or unpaid time. The system derives overtime; it is never selected manually."}
            </p>
          </div>
        </div>
        <form className="compact-form" onSubmit={createCategorizedEntry}>
          <div className="form-grid">
            <label>
              {locale === "es" ? "Hoja abierta" : "Open timesheet"}
              <select
                required
                value={categorizedForm.timesheetId}
                onChange={(e) => {
                  const sheet = timesheets.find(
                    (item) => item.id === e.currentTarget.value,
                  );
                  setCategorizedForm({
                    ...categorizedForm,
                    timesheetId: e.currentTarget.value,
                    workDate: sheet?.periodStart ?? categorizedForm.workDate,
                  });
                }}
              >
                <option value="">—</option>
                {timesheets
                  .filter(
                    (sheet) =>
                      ["open", "rejected"].includes(sheet.status) &&
                      ["weekly_timesheet", "administrator_entry"].includes(
                        workers.find((worker) => worker.id === sheet.workerId)
                          ?.timeEntryMethod ?? "",
                      ),
                  )
                  .map((sheet) => (
                    <option key={sheet.id} value={sheet.id}>
                      {sheet.workerName} ·{" "}
                      {formatDateRange(
                        sheet.periodStart,
                        sheet.periodEnd,
                        locale,
                        "—",
                      )}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              {locale === "es" ? "Fecha" : "Date"}
              <input
                required
                type="date"
                value={categorizedForm.workDate}
                min={
                  timesheets.find(
                    (item) => item.id === categorizedForm.timesheetId,
                  )?.periodStart
                }
                max={
                  timesheets.find(
                    (item) => item.id === categorizedForm.timesheetId,
                  )?.periodEnd
                }
                onChange={(e) =>
                  setCategorizedForm({
                    ...categorizedForm,
                    workDate: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Categoría" : "Category"}
              <select
                value={categorizedForm.category}
                onChange={(e) =>
                  setCategorizedForm({
                    ...categorizedForm,
                    category: e.currentTarget.value,
                  })
                }
              >
                {[
                  ["regular_work", "Trabajo regular", "Regular work"],
                  ["training", "Adiestramiento", "Training"],
                  ["break_paid", "Descanso pagado", "Paid break"],
                  ["break_unpaid", "Descanso no pagado", "Unpaid break"],
                  ["travel", "Viaje", "Travel"],
                  ["other_paid", "Otro pagado", "Other paid"],
                  ["other_unpaid", "Otro no pagado", "Other unpaid"],
                ].map((option) => (
                  <option key={option[0]} value={option[0]}>
                    {option[locale === "es" ? 1 : 2]}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {locale === "es" ? "Minutos" : "Minutes"}
              <input
                required
                type="number"
                min="1"
                max="1440"
                step="1"
                value={categorizedForm.minutes}
                onInput={(e) =>
                  setCategorizedForm({
                    ...categorizedForm,
                    minutes: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label className="full-width">
              {locale === "es"
                ? "Descripción (obligatoria para ‘otro’)"
                : "Description (required for ‘other’)"}
              <input
                maxLength={500}
                value={categorizedForm.description}
                onChange={(e) =>
                  setCategorizedForm({
                    ...categorizedForm,
                    description: e.currentTarget.value,
                  })
                }
              />
            </label>
          </div>
          <button type="submit" disabled={busy || !categorizedForm.timesheetId}>
            {locale === "es" ? "Guardar tiempo" : "Save time"}
          </button>
        </form>
        <div className="run-list">
          {timesheets
            .flatMap((sheet) => sheet.categorizedEntries ?? [])
            .map((item) => (
              <article key={item.id}>
                <div>
                  <strong>
                    {formatDate(item.workDate, locale)} ·{" "}
                    {categorizedLabels[item.category][locale === "es" ? 0 : 1]}
                  </strong>
                  <p>
                    {item.minutes} {locale === "es" ? "minutos" : "minutes"}
                    {item.description ? ` · ${item.description}` : ""}
                  </p>
                  <small>
                    {[
                      item.projectName,
                      item.vehicleUnit,
                      item.departmentName,
                      item.locationName,
                      item.costCenterCode,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </small>
                </div>
                {["open", "rejected"].includes(
                  timesheets.find((sheet) => sheet.id === item.timesheetId)
                    ?.status ?? "",
                ) && (
                  <button
                    type="button"
                    onClick={() => void voidCategorizedEntry(item)}
                  >
                    {locale === "es" ? "Anular" : "Void"}
                  </button>
                )}
              </article>
            ))}
        </div>
      </section>
      {canConfigure && (
        <section className="settings-form time-person-setup time-alternate-setup">
          <div className="section-heading legal-heading">
            <div>
              <h2>
                {locale === "es"
                  ? "Acuerdos de jornada semanal alterna"
                  : "Alternate weekly work schedule agreements"}
              </h2>
              <p>
                {locale === "es"
                  ? "Acuerdo escrito y voluntario bajo la Ley 379: ningún día acordado puede exceder 10 horas ni la semana 40. No es lo mismo que cambiar el comienzo del día de 24 horas. Las represalias están prohibidas."
                  : "Written, voluntary agreement under Law 379: no agreed day may exceed 10 hours and the week may not exceed 40. This is different from changing the 24-hour workday boundary. Retaliation is prohibited."}
              </p>
            </div>
            <a
              className="official-source-link"
              href={officialAlternateWorkweekSource}
              target="_blank"
              rel="noreferrer"
            >
              <ExternalLink />
              {locale === "es" ? "Fuente oficial" : "Official source"}
            </a>
          </div>
          <form className="compact-form" onSubmit={createAlternateAgreement}>
            <h3>
              {locale === "es"
                ? "Retener acuerdo firmado"
                : "Retain signed agreement"}
            </h3>
            <div className="form-grid">
              <label>
                {locale === "es" ? "Empleado" : "Employee"}
                <select
                  required
                  value={alternateForm.workerId}
                  onChange={(e) =>
                    setAlternateForm({
                      ...alternateForm,
                      workerId: e.currentTarget.value,
                    })
                  }
                >
                  <option value="">—</option>
                  {workspaceWorkers
                    .filter((worker) => worker.workerType === "employee")
                    .map((worker) => (
                      <option key={worker.id} value={worker.id}>
                        {worker.preferredName || worker.firstName}{" "}
                        {worker.lastName}
                      </option>
                    ))}
                </select>
              </label>
              <label>
                {locale === "es" ? "Vigente desde" : "Effective from"}
                <input
                  required
                  type="date"
                  value={alternateForm.effectiveFrom}
                  onInput={(e) =>
                    setAlternateForm({
                      ...alternateForm,
                      effectiveFrom: e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Vigente hasta (opcional)" : "Effective to (optional)"}
                <input
                  type="date"
                  value={alternateForm.effectiveTo}
                  onInput={(e) =>
                    setAlternateForm({
                      ...alternateForm,
                      effectiveTo: e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Firma empleado" : "Employee signed"}
                <input
                  required
                  type="date"
                  value={alternateForm.employeeSignedOn}
                  onInput={(e) =>
                    setAlternateForm({
                      ...alternateForm,
                      employeeSignedOn: e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Firma patrono" : "Employer signed"}
                <input
                  required
                  type="date"
                  value={alternateForm.employerSignedOn}
                  onInput={(e) =>
                    setAlternateForm({
                      ...alternateForm,
                      employerSignedOn: e.currentTarget.value,
                    })
                  }
                />
              </label>
              {[
                locale === "es" ? "Dom" : "Sun",
                locale === "es" ? "Lun" : "Mon",
                locale === "es" ? "Mar" : "Tue",
                locale === "es" ? "Mié" : "Wed",
                locale === "es" ? "Jue" : "Thu",
                locale === "es" ? "Vie" : "Fri",
                locale === "es" ? "Sáb" : "Sat",
              ].map((label, index) => (
                <label key={label}>
                  {label} ·{" "}
                  {locale === "es" ? "horas acordadas" : "agreed hours"}
                  <input
                    type="number"
                    min="0.01"
                    max="10"
                    step="0.01"
                    value={alternateForm.dayHours[index]}
                    onInput={(e) => {
                      const dayHours = [...alternateForm.dayHours];
                      dayHours[index] = e.currentTarget.value;
                      setAlternateForm({ ...alternateForm, dayHours });
                    }}
                  />
                </label>
              ))}
              <label>
                {locale === "es" ? "Razón" : "Reason"}
                <input
                  required
                  minLength={3}
                  maxLength={500}
                  value={alternateForm.reason}
                  onChange={(e) =>
                    setAlternateForm({
                      ...alternateForm,
                      reason: e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es"
                  ? "Acuerdo firmado (PDF/imagen)"
                  : "Signed agreement (PDF/image)"}
                <input
                  required
                  type="file"
                  accept="application/pdf,image/jpeg,image/png,image/webp"
                  onChange={(e) =>
                    setAlternateForm({
                      ...alternateForm,
                      evidence: e.currentTarget.files?.[0] ?? null,
                    })
                  }
                />
              </label>
            </div>
            <p>
              {locale === "es"
                ? "Ingrese solamente los días acordados. El total no puede exceder 40 horas; cada día no puede exceder 10."
                : "Enter only agreed days. The total cannot exceed 40 hours; each day cannot exceed 10."}
            </p>
            <button
              className="primary"
              disabled={
                busy || !alternateForm.workerId || !alternateForm.evidence
              }
            >
              {locale === "es" ? "Crear acuerdo" : "Create agreement"}
            </button>
          </form>
          <div className="run-list">
            {alternateAgreements.map((item) => (
              <article key={item.id}>
                <div>
                  <strong>{item.workerName}</strong>
                  <p>
                    {formatDateRange(
                      item.effectiveFrom,
                      item.effectiveTo,
                      locale,
                      locale === "es" ? "abierto" : "open",
                    )}{" "}
                    ·{" "}
                    {item.schedule
                      .map(
                        (day) =>
                          `${optionLabel(String(day.dayOfWeek), locale)}:${day.minutes / 60}h`,
                      )
                      .join(" · ")}
                  </p>
                  <small>
                    {item.reason}
                    {item.terminatedEffectiveOn
                      ? ` · ${locale === "es" ? "termina" : "terminates"} ${formatDate(item.terminatedEffectiveOn, locale)} (${item.terminationMode})`
                      : ""}
                  </small>
                </div>
                <div className="run-actions">
                  <button
                    type="button"
                    onClick={() =>
                      window.open(
                        `${API}/v1/tenants/${tenant.id}/alternate-workweek-agreements/${item.id}/evidence`,
                        "_blank",
                        "noopener,noreferrer",
                      )
                    }
                  >
                    {locale === "es" ? "Abrir acuerdo" : "Open agreement"}
                  </button>
                  {item.terminationId && (
                    <button
                      type="button"
                      onClick={() =>
                        window.open(
                          `${API}/v1/tenants/${tenant.id}/alternate-workweek-terminations/${item.terminationId}/evidence`,
                          "_blank",
                          "noopener,noreferrer",
                        )
                      }
                    >
                      {locale === "es"
                        ? "Abrir terminación"
                        : "Open termination"}
                    </button>
                  )}
                  {!item.terminationId && (
                    <button
                      type="button"
                      onClick={() =>
                        setAlternateTerminationForm({
                          ...alternateTerminationForm,
                          agreementId: item.id,
                        })
                      }
                    >
                      {locale === "es" ? "Terminar" : "Terminate"}
                    </button>
                  )}
                </div>
              </article>
            ))}
            {!alternateAgreements.length && (
              <p>
                {locale === "es"
                  ? "No hay acuerdos retenidos."
                  : "No retained agreements."}
              </p>
            )}
          </div>
          {alternateTerminationForm.agreementId && (
            <form
              className="compact-form"
              onSubmit={terminateAlternateAgreement}
            >
              <h3>
                {locale === "es"
                  ? "Terminación documentada"
                  : "Documented termination"}
              </h3>
              <div className="form-grid">
                <label>
                  {locale === "es" ? "Vigente el" : "Effective on"}
                  <input
                    required
                    type="date"
                    value={alternateTerminationForm.effectiveOn}
                    onInput={(e) =>
                      setAlternateTerminationForm({
                        ...alternateTerminationForm,
                        effectiveOn: e.currentTarget.value,
                      })
                    }
                  />
                </label>
                <label>
                  {locale === "es" ? "Modo" : "Mode"}
                  <select
                    value={alternateTerminationForm.terminationMode}
                    onChange={(e) =>
                      setAlternateTerminationForm({
                        ...alternateTerminationForm,
                        terminationMode: e.currentTarget.value,
                      })
                    }
                  >
                    <option value="mutual">
                      {locale === "es"
                        ? "Mutua (en cualquier momento)"
                        : "Mutual (any time)"}
                    </option>
                    <option value="unilateral_employee">
                      {locale === "es"
                        ? "Unilateral empleado (después de 1 año)"
                        : "Employee unilateral (after 1 year)"}
                    </option>
                    <option value="unilateral_employer">
                      {locale === "es"
                        ? "Unilateral patrono (después de 1 año)"
                        : "Employer unilateral (after 1 year)"}
                    </option>
                  </select>
                </label>
                {alternateTerminationForm.terminationMode === "mutual" && (
                  <>
                    <label>
                      {locale === "es" ? "Firma empleado" : "Employee signed"}
                      <input
                        required
                        type="date"
                        value={alternateTerminationForm.employeeSignedOn}
                        onInput={(e) =>
                          setAlternateTerminationForm({
                            ...alternateTerminationForm,
                            employeeSignedOn: e.currentTarget.value,
                          })
                        }
                      />
                    </label>
                    <label>
                      {locale === "es" ? "Firma patrono" : "Employer signed"}
                      <input
                        required
                        type="date"
                        value={alternateTerminationForm.employerSignedOn}
                        onInput={(e) =>
                          setAlternateTerminationForm({
                            ...alternateTerminationForm,
                            employerSignedOn: e.currentTarget.value,
                          })
                        }
                      />
                    </label>
                  </>
                )}
                <label>
                  {locale === "es" ? "Razón" : "Reason"}
                  <input
                    required
                    minLength={3}
                    value={alternateTerminationForm.reason}
                    onChange={(e) =>
                      setAlternateTerminationForm({
                        ...alternateTerminationForm,
                        reason: e.currentTarget.value,
                      })
                    }
                  />
                </label>
                <label>
                  {locale === "es" ? "Evidencia firmada" : "Signed evidence"}
                  <input
                    required
                    type="file"
                    accept="application/pdf,image/jpeg,image/png,image/webp"
                    onChange={(e) =>
                      setAlternateTerminationForm({
                        ...alternateTerminationForm,
                        evidence: e.currentTarget.files?.[0] ?? null,
                      })
                    }
                  />
                </label>
              </div>
              <button
                className="primary"
                disabled={busy || !alternateTerminationForm.evidence}
              >
                {locale === "es" ? "Retener terminación" : "Retain termination"}
              </button>
            </form>
          )}
          <form
            className="compact-form time-company-organization"
            onSubmit={createEmployerAcquisition}
          >
            <h3>
              {locale === "es"
                ? "Continuidad por venta o adquisición"
                : "Continuity after sale or acquisition"}
            </h3>
            <p>
              {locale === "es"
                ? "La Ley 379 mantiene automáticamente vigentes estos acuerdos cuando cambia el patrono. Este registro no pide una firma nueva ni altera tiempo histórico; vincula la adquisición con cada acuerdo activo."
                : "Law 379 automatically keeps these agreements in effect when the employer changes. This record does not request a new signature or alter historical time; it links the acquisition to every active agreement."}
            </p>
            <div className="form-grid">
              <label>
                {locale === "es" ? "Fecha efectiva" : "Effective date"}
                <input
                  required
                  type="date"
                  value={acquisitionForm.effectiveOn}
                  onInput={(e) =>
                    setAcquisitionForm({
                      ...acquisitionForm,
                      effectiveOn: e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Transacción" : "Transaction"}
                <select
                  value={acquisitionForm.transactionType}
                  onChange={(e) =>
                    setAcquisitionForm({
                      ...acquisitionForm,
                      transactionType: e.currentTarget.value,
                    })
                  }
                >
                  <option value="sale">
                    {locale === "es" ? "Venta" : "Sale"}
                  </option>
                  <option value="acquisition">
                    {locale === "es" ? "Adquisición" : "Acquisition"}
                  </option>
                  <option value="merger">
                    {locale === "es" ? "Fusión" : "Merger"}
                  </option>
                  <option value="other">
                    {locale === "es" ? "Otra" : "Other"}
                  </option>
                </select>
              </label>
              <label>
                {locale === "es" ? "Patrono anterior" : "Predecessor employer"}
                <input
                  required
                  minLength={2}
                  value={acquisitionForm.predecessorLegalName}
                  onChange={(e) =>
                    setAcquisitionForm({
                      ...acquisitionForm,
                      predecessorLegalName: e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Patrono sucesor" : "Successor employer"}
                <input
                  required
                  minLength={2}
                  value={acquisitionForm.successorLegalName}
                  onChange={(e) =>
                    setAcquisitionForm({
                      ...acquisitionForm,
                      successorLegalName: e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es"
                  ? "Razón y descripción"
                  : "Reason and description"}
                <textarea
                  required
                  minLength={3}
                  value={acquisitionForm.reason}
                  onChange={(e) =>
                    setAcquisitionForm({
                      ...acquisitionForm,
                      reason: e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es"
                  ? "Evidencia de la transacción"
                  : "Transaction evidence"}
                <input
                  required
                  type="file"
                  accept="application/pdf,image/jpeg,image/png,image/webp"
                  onChange={(e) =>
                    setAcquisitionForm({
                      ...acquisitionForm,
                      evidence: e.currentTarget.files?.[0] ?? null,
                    })
                  }
                />
              </label>
            </div>
            <button
              className="primary"
              disabled={busy || !acquisitionForm.evidence}
            >
              {locale === "es"
                ? "Registrar continuidad automática"
                : "Record automatic continuity"}
            </button>
          </form>
          <div className="run-list time-company-organization">
            {employerAcquisitions.map((item) => (
              <article key={item.id}>
                <div>
                  <strong>
                    {item.predecessorLegalName} → {item.successorLegalName}
                  </strong>
                  <p>
                    {formatDate(item.effectiveOn, locale)} ·{" "}
                    {item.transactionType} · {item.continuations.length}{" "}
                    {locale === "es"
                      ? "acuerdo(s) continuado(s) automáticamente"
                      : "agreement(s) continued automatically"}
                  </p>
                  <small>{item.reason}</small>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    window.open(
                      API +
                        "/v1/tenants/" +
                        tenant.id +
                        "/employer-acquisitions/" +
                        item.id +
                        "/evidence",
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                >
                  {locale === "es" ? "Abrir evidencia" : "Open evidence"}
                </button>
              </article>
            ))}
          </div>
        </section>
      )}
      {!professionalServicesView && (
        <section className="settings-form time-person-setup">
          <div className="section-heading">
            <div>
              <h2>
                {locale === "es"
                  ? "Solicitud de cambio de horario"
                  : "Schedule change request"}
              </h2>
              <p>
                {locale === "es"
                  ? "La persona solicita por escrito un cambio de horario, horas o lugar. El patrono debe responder en 20 días calendario; una reunión oportuna mueve el plazo a 14 días después de la reunión."
                  : "The employee submits a written schedule, hours, or location change. The employer must respond within 20 calendar days; a timely meeting moves the deadline to 14 days after the meeting."}
              </p>
            </div>
          </div>
          {roleKey === "user" && (
            <form className="compact-form" onSubmit={submitScheduleChange}>
              <div className="form-grid">
                <label>
                  {locale === "es" ? "Tipo de cambio" : "Change type"}
                  <select
                    value={scheduleChangeForm.changeType}
                    onChange={(e) =>
                      setScheduleChangeForm({
                        ...scheduleChangeForm,
                        changeType: e.currentTarget.value,
                      })
                    }
                  >
                    <option value="schedule">
                      {locale === "es" ? "Horario" : "Schedule"}
                    </option>
                    <option value="hours">
                      {locale === "es"
                        ? "Cantidad de horas"
                        : "Amount of hours"}
                    </option>
                    <option value="location">
                      {locale === "es" ? "Lugar de trabajo" : "Work location"}
                    </option>
                    <option value="multiple">
                      {locale === "es" ? "Varios" : "Multiple"}
                    </option>
                  </select>
                </label>
                <label>
                  {locale === "es"
                    ? "Fecha solicitada"
                    : "Requested effective date"}
                  <input
                    required
                    type="date"
                    value={scheduleChangeForm.requestedEffectiveOn}
                    onInput={(e) =>
                      setScheduleChangeForm({
                        ...scheduleChangeForm,
                        requestedEffectiveOn: e.currentTarget.value,
                      })
                    }
                  />
                </label>
                <label>
                  {locale === "es" ? "Hasta (opcional)" : "Through (optional)"}
                  <input
                    type="date"
                    value={scheduleChangeForm.requestedThrough}
                    onInput={(e) =>
                      setScheduleChangeForm({
                        ...scheduleChangeForm,
                        requestedThrough: e.currentTarget.value,
                      })
                    }
                  />
                </label>
                <label>
                  {locale === "es" ? "Prioridad legal" : "Statutory priority"}
                  <select
                    value={scheduleChangeForm.priorityBasis}
                    onChange={(e) =>
                      setScheduleChangeForm({
                        ...scheduleChangeForm,
                        priorityBasis: e.currentTarget.value,
                      })
                    }
                  >
                    <option value="standard">
                      {locale === "es" ? "Estándar" : "Standard"}
                    </option>
                    <option value="single_custodial_parent">
                      {locale === "es"
                        ? "Jefe de familia con custodia única"
                        : "Head of family with sole custody"}
                    </option>
                    <option value="certified_informal_caregiver">
                      {locale === "es"
                        ? "Cuidador informal certificado"
                        : "Certified informal caregiver"}
                    </option>
                  </select>
                </label>
                <label>
                  {locale === "es" ? "Cambio solicitado" : "Requested change"}
                  <textarea
                    required
                    minLength={3}
                    value={scheduleChangeForm.requestedDetails}
                    onChange={(e) =>
                      setScheduleChangeForm({
                        ...scheduleChangeForm,
                        requestedDetails: e.currentTarget.value,
                      })
                    }
                  />
                </label>
                <label>
                  {locale === "es" ? "Razón" : "Reason"}
                  <textarea
                    required
                    minLength={3}
                    value={scheduleChangeForm.reason}
                    onChange={(e) =>
                      setScheduleChangeForm({
                        ...scheduleChangeForm,
                        reason: e.currentTarget.value,
                      })
                    }
                  />
                </label>
                {scheduleChangeForm.priorityBasis ===
                  "certified_informal_caregiver" && (
                  <label>
                    {locale === "es"
                      ? "Certificación del Departamento de la Familia"
                      : "Department of the Family certification"}
                    <input
                      required
                      type="file"
                      accept="application/pdf,image/jpeg,image/png,image/webp"
                      onChange={(e) =>
                        setScheduleChangeForm({
                          ...scheduleChangeForm,
                          caregiverEvidence: e.currentTarget.files?.[0] ?? null,
                        })
                      }
                    />
                  </label>
                )}
              </div>
              {["schedule", "hours", "multiple"].includes(
                scheduleChangeForm.changeType,
              ) && (
                <>
                  <h3>
                    {locale === "es"
                      ? "Horario propuesto"
                      : "Proposed schedule"}
                  </h3>
                  <div className="form-grid">
                    <label>
                      {locale === "es" ? "Nombre del horario" : "Schedule name"}
                      <input
                        required
                        value={scheduleChangeForm.proposalName}
                        onChange={(e) =>
                          setScheduleChangeForm({
                            ...scheduleChangeForm,
                            proposalName: e.currentTarget.value,
                          })
                        }
                      />
                    </label>
                    <label>
                      {locale === "es" ? "Política de ponche" : "Punch policy"}
                      <select
                        value={scheduleChangeForm.punchPolicyId}
                        onChange={(e) =>
                          setScheduleChangeForm({
                            ...scheduleChangeForm,
                            punchPolicyId: e.currentTarget.value,
                          })
                        }
                      >
                        <option value="">—</option>
                        {punchPolicies
                          .filter((item) => item.active)
                          .map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.name}
                            </option>
                          ))}
                      </select>
                    </label>
                  </div>
                  {scheduleChangeForm.shifts.map((item, index) => (
                    <div
                      className="form-grid"
                      key={`${index}-${item.dayOfWeek}-${item.sequence}`}
                    >
                      <label>
                        {locale === "es"
                          ? "Día (0 domingo–6 sábado)"
                          : "Day (0 Sunday–6 Saturday)"}
                        <input
                          required
                          type="number"
                          min="0"
                          max="6"
                          value={item.dayOfWeek}
                          onInput={(e) => {
                            const shifts = [...scheduleChangeForm.shifts];
                            shifts[index] = {
                              ...item,
                              dayOfWeek: Number(e.currentTarget.value),
                            };
                            setScheduleChangeForm({
                              ...scheduleChangeForm,
                              shifts,
                            });
                          }}
                        />
                      </label>
                      <label>
                        {locale === "es" ? "Entrada" : "Start"}
                        <input
                          required
                          type="time"
                          value={item.startTime}
                          onChange={(e) => {
                            const shifts = [...scheduleChangeForm.shifts];
                            shifts[index] = {
                              ...item,
                              startTime: e.currentTarget.value,
                            };
                            setScheduleChangeForm({
                              ...scheduleChangeForm,
                              shifts,
                            });
                          }}
                        />
                      </label>
                      <label>
                        {locale === "es" ? "Salida" : "End"}
                        <input
                          required
                          type="time"
                          value={item.endTime}
                          onChange={(e) => {
                            const shifts = [...scheduleChangeForm.shifts];
                            shifts[index] = {
                              ...item,
                              endTime: e.currentTarget.value,
                            };
                            setScheduleChangeForm({
                              ...scheduleChangeForm,
                              shifts,
                            });
                          }}
                        />
                      </label>
                      <label>
                        {locale === "es" ? "Comida (min)" : "Meal (min)"}
                        <input
                          required
                          type="number"
                          min="0"
                          max="600"
                          value={item.mealMinutes}
                          onInput={(e) => {
                            const shifts = [...scheduleChangeForm.shifts];
                            shifts[index] = {
                              ...item,
                              mealMinutes: Number(e.currentTarget.value),
                            };
                            setScheduleChangeForm({
                              ...scheduleChangeForm,
                              shifts,
                            });
                          }}
                        />
                      </label>
                      <label>
                        {locale === "es" ? "Minutos pagados" : "Paid minutes"}
                        <input
                          required
                          type="number"
                          min="1"
                          max="1440"
                          value={item.expectedPaidMinutes}
                          onInput={(e) => {
                            const shifts = [...scheduleChangeForm.shifts];
                            shifts[index] = {
                              ...item,
                              expectedPaidMinutes: Number(
                                e.currentTarget.value,
                              ),
                            };
                            setScheduleChangeForm({
                              ...scheduleChangeForm,
                              shifts,
                            });
                          }}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() =>
                          setScheduleChangeForm({
                            ...scheduleChangeForm,
                            shifts: scheduleChangeForm.shifts.filter(
                              (_, position) => position !== index,
                            ),
                          })
                        }
                      >
                        {locale === "es" ? "Quitar" : "Remove"}
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      setScheduleChangeForm({
                        ...scheduleChangeForm,
                        shifts: [
                          ...scheduleChangeForm.shifts,
                          {
                            dayOfWeek: 1,
                            sequence: 1,
                            startTime: "",
                            endTime: "",
                            mealMinutes: 0,
                            expectedPaidMinutes: 0,
                          },
                        ],
                      })
                    }
                  >
                    {locale === "es" ? "Añadir día/turno" : "Add day/shift"}
                  </button>
                </>
              )}
              <p>
                {locale === "es"
                  ? "La solicitud se permite normalmente después de un año de empleo y con 30 horas regulares semanales. Un cuidador informal certificado puede usar la excepción legal. No se permite otra solicitud durante el período estatutario de seis meses."
                  : "The request is normally available after one year of employment and 30 regular weekly hours. A certified informal caregiver may use the statutory exception. Another request is not allowed during the statutory six-month period."}
              </p>
              <button className="primary" disabled={busy}>
                {locale === "es"
                  ? "Someter solicitud escrita"
                  : "Submit written request"}
              </button>
            </form>
          )}
          <div className="run-list">
            {scheduleChangeRequests.map((item) => (
              <article key={item.id}>
                <div>
                  <strong>
                    {item.workerName} · {optionLabel(item.changeType, locale)}
                  </strong>
                  <p>{item.requestedDetails}</p>
                  <small>
                    {locale === "es" ? "Recibida" : "Received"}{" "}
                    {formatDate(item.receivedOn, locale)} ·{" "}
                    {locale === "es" ? "respuesta vence" : "response due"}{" "}
                    {formatDate(
                      item.finalResponseDueOn ?? item.responseDueOn,
                      locale,
                    )}{" "}
                    · {optionLabel(item.priorityBasis, locale)} ·{" "}
                    {(item.regularWeeklyMinutes / 60).toFixed(2)} h/
                    {locale === "es" ? "semana" : "week"}
                    {item.lateResponse
                      ? ` · ${locale === "es" ? "RESPUESTA TARDÍA" : "LATE RESPONSE"}`
                      : ""}
                  </small>
                  {item.writtenResponse && (
                    <p>
                      {item.writtenResponse}
                      {item.denialReason
                        ? ` · ${locale === "es" ? "Razón" : "Reason"}: ${item.denialReason}`
                        : ""}
                      {item.alternative
                        ? ` · ${locale === "es" ? "Alternativa" : "Alternative"}: ${item.alternative}`
                        : ""}
                    </p>
                  )}
                </div>
                <span className={`run-status ${item.status}`}>
                  {optionLabel(item.status, locale)}
                </span>
                <div className="run-actions">
                  {item.caregiverEvidenceFileName && (
                    <button
                      type="button"
                      onClick={() =>
                        window.open(
                          `${API}/v1/tenants/${tenant.id}/schedule-change-requests/${item.id}/caregiver-evidence`,
                          "_blank",
                          "noopener,noreferrer",
                        )
                      }
                    >
                      {locale === "es"
                        ? "Abrir certificación"
                        : "Open certification"}
                    </button>
                  )}
                  {item.status === "pending" && roleKey === "user" && (
                    <button
                      type="button"
                      onClick={() => void withdrawScheduleChange(item)}
                    >
                      {locale === "es" ? "Retirar" : "Withdraw"}
                    </button>
                  )}
                  {item.status === "pending" && canConfigure && (
                    <button
                      type="button"
                      onClick={() =>
                        setScheduleResponseForm({
                          ...scheduleResponseForm,
                          requestId: item.id,
                        })
                      }
                    >
                      {locale === "es" ? "Responder" : "Respond"}
                    </button>
                  )}
                </div>
              </article>
            ))}
            {!scheduleChangeRequests.length && (
              <p>{locale === "es" ? "No hay solicitudes." : "No requests."}</p>
            )}
          </div>
          {canConfigure && scheduleResponseForm.requestId && (
            <form className="compact-form" onSubmit={respondScheduleChange}>
              <h3>
                {locale === "es"
                  ? "Respuesta escrita del patrono"
                  : "Employer written response"}
              </h3>
              <div className="form-grid">
                <label>
                  {locale === "es" ? "Decisión" : "Decision"}
                  <select
                    value={scheduleResponseForm.decision}
                    onChange={(e) =>
                      setScheduleResponseForm({
                        ...scheduleResponseForm,
                        decision: e.currentTarget.value,
                      })
                    }
                  >
                    <option value="approved">
                      {locale === "es" ? "Aprobar" : "Approve"}
                    </option>
                    <option value="denied">
                      {locale === "es" ? "Denegar" : "Deny"}
                    </option>
                  </select>
                </label>
                <label>
                  {locale === "es"
                    ? "Reunión (opcional, dentro de 20 días)"
                    : "Meeting (optional, within 20 days)"}
                  <input
                    type="date"
                    value={scheduleResponseForm.meetingOn}
                    onInput={(e) =>
                      setScheduleResponseForm({
                        ...scheduleResponseForm,
                        meetingOn: e.currentTarget.value,
                      })
                    }
                  />
                </label>
                <label>
                  {locale === "es" ? "Respuesta escrita" : "Written response"}
                  <textarea
                    required
                    minLength={3}
                    value={scheduleResponseForm.writtenResponse}
                    onChange={(e) =>
                      setScheduleResponseForm({
                        ...scheduleResponseForm,
                        writtenResponse: e.currentTarget.value,
                      })
                    }
                  />
                </label>
                <label>
                  {locale === "es"
                    ? "Condiciones de concesión"
                    : "Approval conditions"}
                  <textarea
                    value={scheduleResponseForm.conditions}
                    onChange={(e) =>
                      setScheduleResponseForm({
                        ...scheduleResponseForm,
                        conditions: e.currentTarget.value,
                      })
                    }
                  />
                </label>
                {scheduleResponseForm.decision === "denied" && (
                  <>
                    <label>
                      {locale === "es"
                        ? "Razón de denegación"
                        : "Denial reason"}
                      <textarea
                        required
                        minLength={3}
                        value={scheduleResponseForm.denialReason}
                        onChange={(e) =>
                          setScheduleResponseForm({
                            ...scheduleResponseForm,
                            denialReason: e.currentTarget.value,
                          })
                        }
                      />
                    </label>
                    <label>
                      {locale === "es"
                        ? "Alternativa ofrecida"
                        : "Alternative offered"}
                      <textarea
                        required
                        minLength={3}
                        value={scheduleResponseForm.alternative}
                        onChange={(e) =>
                          setScheduleResponseForm({
                            ...scheduleResponseForm,
                            alternative: e.currentTarget.value,
                          })
                        }
                      />
                    </label>
                  </>
                )}
              </div>
              <button className="primary" disabled={busy}>
                {locale === "es"
                  ? "Retener respuesta y aplicar"
                  : "Retain response and apply"}
              </button>
            </form>
          )}
        </section>
      )}
      {timeAction && timeActionCopy && (
        <dialog
          className="workflow-dialog"
          open
          aria-labelledby="time-action-title"
        >
          <form onSubmit={submitTimeAction}>
            <header>
              <div>
                <p className="kicker">
                  {locale === "es"
                    ? "HISTORIAL DE TIEMPO AUDITADO"
                    : "AUDITED TIME HISTORY"}
                </p>
                <h2 id="time-action-title">
                  {timeActionCopy[locale === "es" ? 0 : 1]}
                </h2>
                <p>{timeActionSubject}</p>
              </div>
              <button
                type="button"
                aria-label={locale === "es" ? "Cerrar" : "Close"}
                onClick={() => setTimeAction(null)}
              >
                ×
              </button>
            </header>
            <p className="form-detail">
              {locale === "es"
                ? "La decisión se aplica mediante el flujo autorizado y queda vinculada al registro original; el historial anterior no se reescribe."
                : "The decision is applied through the authorized workflow and remains linked to the original record; prior history is not rewritten."}
            </p>
            <OperationalError value={timeActionFailure} locale={locale} />
            <div className="form-grid">
              {timeAction.kind === "end_no_tracking" && (
                <label>
                  {locale === "es"
                    ? "Fecha efectiva de terminación"
                    : "Effective end date"}
                  <input
                    required
                    type="date"
                    min={timeAction.item.effectiveFrom}
                    value={timeAction.effectiveOn}
                    onInput={(e) =>
                      setTimeAction({
                        ...timeAction,
                        effectiveOn: e.currentTarget.value,
                      })
                    }
                  />
                </label>
              )}
              {timeAction.kind === "end_schedule" && (
                <label>
                  {locale === "es"
                    ? "Último día del horario"
                    : "Last schedule date"}
                  <input
                    required
                    type="date"
                    min={
                      today < timeAction.item.effectiveFrom
                        ? timeAction.item.effectiveFrom
                        : today
                    }
                    max={timeAction.item.effectiveTo ?? undefined}
                    value={timeAction.effectiveTo}
                    onInput={(e) =>
                      setTimeAction({
                        ...timeAction,
                        effectiveTo: e.currentTarget.value,
                      })
                    }
                  />
                </label>
              )}
              {timeAction.kind === "close_assignment" && (
                <label>
                  {locale === "es"
                    ? "Último día de asignación"
                    : "Last assignment date"}
                  <input
                    required
                    type="date"
                    min={timeAction.item.effectiveFrom}
                    value={timeAction.effectiveTo}
                    onInput={(e) =>
                      setTimeAction({
                        ...timeAction,
                        effectiveTo: e.currentTarget.value,
                      })
                    }
                  />
                </label>
              )}
              <label className="full-width">
                {timeAction.kind === "daily_comment" ||
                timeAction.kind === "punch_exception"
                  ? locale === "es"
                    ? "Comentario de revisión"
                    : "Review comment"
                  : locale === "es"
                    ? "Razón documentada"
                    : "Documented reason"}
                <textarea
                  required
                  minLength={3}
                  value={timeAction.reason}
                  onChange={(e) =>
                    setTimeAction({
                      ...timeAction,
                      reason: e.currentTarget.value,
                    })
                  }
                />
              </label>
            </div>
            <footer>
              <button type="button" onClick={() => setTimeAction(null)}>
                {locale === "es" ? "Cancelar" : "Cancel"}
              </button>
              <button
                className={
                  (timeAction.kind === "correction" &&
                    timeAction.decision === "rejected") ||
                  (timeAction.kind === "punch_exception" &&
                    timeAction.decision === "rejected") ||
                  timeAction.kind === "void_entry" ||
                  timeAction.kind === "end_schedule"
                    ? "danger-action"
                    : "primary"
                }
                disabled={busy || timeAction.reason.trim().length < 3}
              >
                {busy ? (
                  <LoaderCircle className="spin" />
                ) : locale === "es" ? (
                  "Confirmar decisión"
                ) : (
                  "Confirm decision"
                )}
              </button>
            </footer>
          </form>
        </dialog>
      )}
      {timeEdit && (
        <dialog
          className="workflow-dialog"
          open
          aria-labelledby="time-edit-title"
        >
          <form onSubmit={submitTimeEdit}>
            <header>
              <div>
                <p className="kicker">
                  {locale === "es"
                    ? "CONFIGURACIÓN VALIDADA"
                    : "VALIDATED CONFIGURATION"}
                </p>
                <h2 id="time-edit-title">
                  {timeEdit.kind === "meal"
                    ? locale === "es"
                      ? "Resolver período de alimentos"
                      : "Resolve meal period"
                    : timeEdit.kind === "project"
                      ? locale === "es"
                        ? "Editar proyecto"
                        : "Edit project"
                      : locale === "es"
                        ? "Editar vehículo"
                        : "Edit vehicle"}
                </h2>
                <p>
                  {timeEdit.kind === "meal"
                    ? `${formatDate(timeEdit.workDate, locale)} · ${timeEdit.issueKind === "first_meal" ? (locale === "es" ? "primer período" : "first meal") : locale === "es" ? "segundo período" : "second meal"}`
                    : timeEdit.kind === "project"
                      ? timeEdit.item.name
                      : timeEdit.item.unitNumber}
                </p>
              </div>
              <button
                type="button"
                aria-label={locale === "es" ? "Cerrar" : "Close"}
                onClick={() => setTimeEdit(null)}
              >
                ×
              </button>
            </header>
            <p className="form-detail">
              {timeEdit.kind === "meal"
                ? locale === "es"
                  ? "Seleccione el tratamiento legal aplicable y documente la evidencia. La omisión estatutaria solo está disponible para la revisión del segundo período."
                  : "Select the applicable legal treatment and document the evidence. Statutory omission is available only for second-meal review."
                : locale === "es"
                  ? "Los cambios se conservan con razón y no eliminan el historial de uso anterior."
                  : "Changes retain a reason and do not delete prior usage history."}
            </p>
            <div className="form-grid">
              {timeEdit.kind === "meal" ? (
                <>
                  <label>
                    {locale === "es" ? "Resolución" : "Resolution"}
                    <select
                      value={timeEdit.resolutionType}
                      onChange={(e) =>
                        setTimeEdit({
                          ...timeEdit,
                          resolutionType: e.currentTarget.value as
                            | "penalty_due"
                            | "statutory_second_omission",
                        })
                      }
                    >
                      <option value="penalty_due">
                        {locale === "es" ? "Pagar penalidad" : "Pay penalty"}
                      </option>
                      {timeEdit.allowOmission && (
                        <option value="statutory_second_omission">
                          {locale === "es"
                            ? "Omisión estatutaria del segundo período"
                            : "Statutory second-meal omission"}
                        </option>
                      )}
                    </select>
                  </label>
                  {timeEdit.resolutionType === "penalty_due" && (
                    <label>
                      {locale === "es"
                        ? "Minutos de penalidad"
                        : "Penalty minutes"}
                      <input
                        required
                        type="number"
                        min="1"
                        max="60"
                        step="1"
                        value={timeEdit.penaltyMinutes}
                        onInput={(e) =>
                          setTimeEdit({
                            ...timeEdit,
                            penaltyMinutes: e.currentTarget.value,
                          })
                        }
                      />
                    </label>
                  )}
                </>
              ) : timeEdit.kind === "project" ? (
                <>
                  <label>
                    {locale === "es" ? "Nombre" : "Name"}
                    <input
                      required
                      value={timeEdit.name}
                      onChange={(e) =>
                        setTimeEdit({
                          ...timeEdit,
                          name: e.currentTarget.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    {locale === "es" ? "Código opcional" : "Optional code"}
                    <input
                      value={timeEdit.code}
                      onChange={(e) =>
                        setTimeEdit({
                          ...timeEdit,
                          code: e.currentTarget.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    {locale === "es" ? "Estado" : "Status"}
                    <select
                      value={timeEdit.status}
                      onChange={(e) =>
                        setTimeEdit({
                          ...timeEdit,
                          status: e.currentTarget.value as
                            | "active"
                            | "inactive"
                            | "closed",
                        })
                      }
                    >
                      <option value="active">
                        {locale === "es" ? "Activo" : "Active"}
                      </option>
                      <option value="inactive">
                        {locale === "es" ? "Inactivo" : "Inactive"}
                      </option>
                      <option value="closed">
                        {locale === "es" ? "Cerrado" : "Closed"}
                      </option>
                    </select>
                  </label>
                </>
              ) : (
                <>
                  <label>
                    {locale === "es" ? "Número de unidad" : "Unit number"}
                    <input
                      required
                      value={timeEdit.unitNumber}
                      onChange={(e) =>
                        setTimeEdit({
                          ...timeEdit,
                          unitNumber: e.currentTarget.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    {locale === "es" ? "Estado" : "Status"}
                    <select
                      value={timeEdit.status}
                      onChange={(e) =>
                        setTimeEdit({
                          ...timeEdit,
                          status: e.currentTarget.value as
                            | "active"
                            | "inactive"
                            | "retired",
                        })
                      }
                    >
                      <option value="active">
                        {locale === "es" ? "Activo" : "Active"}
                      </option>
                      <option value="inactive">
                        {locale === "es" ? "Inactivo" : "Inactive"}
                      </option>
                      <option value="retired">
                        {locale === "es" ? "Retirado" : "Retired"}
                      </option>
                    </select>
                  </label>
                  <label>
                    {locale === "es" ? "Notas (opcional)" : "Notes (optional)"}
                    <textarea
                      value={timeEdit.notes}
                      onChange={(e) =>
                        setTimeEdit({
                          ...timeEdit,
                          notes: e.currentTarget.value,
                        })
                      }
                    />
                  </label>
                </>
              )}
              <label className="full-width">
                {locale === "es" ? "Razón y evidencia" : "Reason and evidence"}
                <textarea
                  required
                  minLength={3}
                  value={timeEdit.reason}
                  onChange={(e) =>
                    setTimeEdit({ ...timeEdit, reason: e.currentTarget.value })
                  }
                />
              </label>
            </div>
            {error && (
              <p className="form-error">
                {locale === "es"
                  ? "Revise los campos y las reglas aplicables."
                  : "Review the fields and applicable rules."}
              </p>
            )}
            <footer>
              <button type="button" onClick={() => setTimeEdit(null)}>
                {locale === "es" ? "Cancelar" : "Cancel"}
              </button>
              <button
                className="primary"
                disabled={busy || timeEdit.reason.trim().length < 3}
              >
                {busy ? (
                  <LoaderCircle className="spin" />
                ) : locale === "es" ? (
                  "Guardar decisión"
                ) : (
                  "Save decision"
                )}
              </button>
            </footer>
          </form>
        </dialog>
      )}
    </div>
  );
}

type LeavePolicy = {
  id: string;
  name: string;
  leaveType: string;
  paid: number;
  accrualMethod: string;
  accrualValue: string;
  minimumMonthlyHours: string;
  statutoryBasis: string | null;
  statutorySourceUrl: string | null;
  assignedWorkerCount: number;
  annualGrantTiming:
    | "policy_anniversary"
    | "worker_anniversary"
    | "calendar_date";
  annualGrantMonthDay: string | null;
  carryoverResetTiming:
    | "policy_anniversary"
    | "worker_anniversary"
    | "calendar_date";
  carryoverResetMonthDay: string;
  effectiveFrom: string;
  effectiveTo: string | null;
};
type LeaveRequest = {
  id: string;
  workerId: string;
  workerName: string;
  policyName: string;
  startsOn: string;
  endsOn: string;
  hours: string;
  status: string;
};
type LeaveBalance = {
  workerId: string;
  policyId: string;
  balanceHours: string;
};
type LeavePolicyAssignment = {
  workerId: string;
  policyId: string;
  effectiveFrom: string;
  effectiveTo: string | null;
};
type Holiday = {
  id: string;
  name: string;
  holidayDate: string;
  paid: number;
  paidHours: string | null;
  eligibilityMode: string;
  active: number;
  revisionNumber: number;
  specialRule: string | null;
  eligibilityTargets: {
    targetType: "worker" | "department" | "location" | "cost_center";
    targetId: string;
  }[];
};
type LeaveScheduleRun = {
  id: string;
  runDate: string;
  triggerSource: string;
  status: string;
  grantCount: number;
  expirationCount: number;
  startedAt: string;
  completedAt: string | null;
};
type LeaveActionDraft =
  | {
      kind: "modify";
      item: LeaveRequest;
      startsOn: string;
      endsOn: string;
      hours: string;
      reason: string;
    }
  | { kind: "cancel"; item: LeaveRequest; reason: string }
  | { kind: "schedule"; asOf: string }
  | {
      kind: "holiday";
      item: Holiday;
      name: string;
      holidayDate: string;
      paid: boolean;
      paidHours: string;
      eligibilityMode: "all_active" | "scheduled_to_work";
      active: boolean;
      replaceTargets: boolean;
      reason: string;
    };
function Leave({
  locale,
  tenant,
  canManage,
  roleKey,
  actorWorkerId,
}: {
  locale: Locale;
  tenant: Tenant;
  canManage: boolean;
  roleKey: string;
  actorWorkerId: string | null;
}) {
  const [workers, setWorkers] = useState<Worker[]>([]),
    [policies, setPolicies] = useState<LeavePolicy[]>([]),
    [policyAssignments, setPolicyAssignments] = useState<
      LeavePolicyAssignment[]
    >([]),
    [requests, setRequests] = useState<LeaveRequest[]>([]),
    [balances, setBalances] = useState<LeaveBalance[]>([]),
    [holidays, setHolidays] = useState<Holiday[]>([]),
    [holidayDepartments, setHolidayDepartments] = useState<OrgItem[]>([]),
    [holidayLocations, setHolidayLocations] = useState<OrgItem[]>([]),
    [holidayCostCenters, setHolidayCostCenters] = useState<OrgItem[]>([]),
    [scheduleRuns, setScheduleRuns] = useState<LeaveScheduleRun[]>([]),
    [busy, setBusy] = useState(true),
    [error, setError] = useState("");
  const leaveDiagnosticWorkerId =
    sessionStorage.getItem("leave-diagnostic-worker") ?? "";
  const [balanceWorkerId, setBalanceWorkerId] = useState(
    leaveDiagnosticWorkerId,
  );
  const today = new Date().toISOString().slice(0, 10);
  const [policy, setPolicy] = useState({
    name: "",
    leaveType: "vacation",
    paid: true,
    accrualMethod: "per_payroll",
    accrualValue: "0",
    annualGrantTiming: "policy_anniversary" as
      | "policy_anniversary"
      | "worker_anniversary"
      | "calendar_date",
    annualGrantMonthDay: null as string | null,
    allowNegative: false,
    carryoverCapHours: null as string | null,
    carryoverResetTiming: "calendar_date" as
      | "policy_anniversary"
      | "worker_anniversary"
      | "calendar_date",
    carryoverResetMonthDay: "01-01",
    effectiveFrom: today,
    effectiveTo: null as string | null,
  });
  const [assignment, setAssignment] = useState({
    workerId: leaveDiagnosticWorkerId,
    policyId: "",
    effectiveFrom: today,
    effectiveTo: null as string | null,
  });
  const [ledger, setLedger] = useState({
    workerId: leaveDiagnosticWorkerId,
    policyId: "",
    transactionType: "opening",
    hours: "0",
    effectiveOn: today,
    reason: "",
  });
  const [leaveRequest, setLeaveRequest] = useState({
    workerId: leaveDiagnosticWorkerId,
    policyId: "",
    startsOn: today,
    endsOn: today,
    hours: "8",
    reason: "",
  });
  const [holiday, setHoliday] = useState({
    name: "",
    holidayDate: today,
    paid: true,
    paidHours: "8",
    eligibilityMode: "all_active",
    eligibilityTargets: [] as Holiday["eligibilityTargets"],
    specialRule: null as string | null,
  });
  const [leaveAction, setLeaveAction] = useState<LeaveActionDraft | null>(null);
  const load = async () => {
    const [w, p, r, l, h, o] = await Promise.all([
      api(`/v1/tenants/${tenant.id}/workers`),
      api(`/v1/tenants/${tenant.id}/leave-policies`),
      api(`/v1/tenants/${tenant.id}/leave-requests`),
      api(`/v1/tenants/${tenant.id}/leave-ledger`),
      api(`/v1/tenants/${tenant.id}/holidays`),
      api(`/v1/tenants/${tenant.id}/organization`),
    ]);
    if (w.ok) setWorkers(((await w.json()) as { workers: Worker[] }).workers);
    if (p.ok) {
      const body = (await p.json()) as {
        policies: LeavePolicy[];
        assignments: LeavePolicyAssignment[];
      };
      setPolicies(body.policies);
      setPolicyAssignments(body.assignments ?? []);
    }
    if (r.ok)
      setRequests(((await r.json()) as { requests: LeaveRequest[] }).requests);
    if (l.ok)
      setBalances(((await l.json()) as { balances: LeaveBalance[] }).balances);
    if (h.ok)
      setHolidays(((await h.json()) as { holidays: Holiday[] }).holidays);
    if (o.ok) {
      const organization = (await o.json()) as {
        departments: OrgItem[];
        locations: OrgItem[];
        costCenters: OrgItem[];
      };
      setHolidayDepartments(organization.departments);
      setHolidayLocations(organization.locations);
      setHolidayCostCenters(organization.costCenters);
    }
    if (canManage) {
      const s = await api(`/v1/tenants/${tenant.id}/leave-schedule-runs`);
      if (s.ok)
        setScheduleRuns(
          ((await s.json()) as { runs: LeaveScheduleRun[] }).runs,
        );
    }
    setBusy(false);
  };
  useEffect(() => {
    if (!balanceWorkerId) {
      const first =
        roleKey === "user"
          ? workers.find((item) => item.id === actorWorkerId)
          : workers.find(
              (item) =>
                item.workerType === "employee" &&
                !["inactive", "terminated"].includes(item.status),
            );
      if (first) setBalanceWorkerId(first.id);
    }
  }, [workers, balanceWorkerId, roleKey, actorWorkerId]);
  useEffect(() => {
    void load();
  }, [tenant.id]);
  const post = async (path: string, body: unknown) => {
    setBusy(true);
    setError("");
    const response = await api(`/v1/tenants/${tenant.id}/${path}`, {
      method: "POST",
      body: JSON.stringify(body),
    });
    if (!response.ok) setError(`${path}:${response.status}`);
    await load();
    return response.ok;
  };
  const review = async (id: string, decision: "approved" | "rejected") =>
    post(`leave-requests/${id}/review`, { decision, comment: null });
  const modifyAndApprove = (item: LeaveRequest) =>
    setLeaveAction({
      kind: "modify",
      item,
      startsOn: item.startsOn,
      endsOn: item.endsOn,
      hours: item.hours,
      reason: "",
    });
  const cancel = (item: LeaveRequest) =>
    setLeaveAction({ kind: "cancel", item, reason: "" });
  const runSchedule = () => setLeaveAction({ kind: "schedule", asOf: today });
  const editLeavePolicy = async (item: LeavePolicy) => {
    const values = await requestWorkflowDialog({
      title: locale === "es" ? "Modificar política" : "Edit policy",
      detail: item.name,
      confirmLabel: locale === "es" ? "Guardar cambios" : "Save changes",
      cancelLabel: locale === "es" ? "Cancelar" : "Cancel",
      fields: [
        {
          key: "name",
          label: locale === "es" ? "Nombre" : "Name",
          value: item.name,
          required: true,
        },
        {
          key: "accrualValue",
          label:
            locale === "es"
              ? "Horas por mes elegible"
              : "Hours per eligible month",
          type: "number",
          value: item.accrualValue,
          min: "0",
          step: "0.0001",
          required: true,
        },
        {
          key: "minimumMonthlyHours",
          label:
            locale === "es"
              ? "Horas mínimas requeridas"
              : "Minimum required hours",
          type: "number",
          value: item.minimumMonthlyHours,
          min: "0",
          step: "0.0001",
          required: true,
        },
        {
          key: "reason",
          label: locale === "es" ? "Razón del cambio" : "Reason for change",
          type: "textarea",
          required: true,
          minLength: 3,
          fullWidth: true,
        },
      ],
    });
    if (!values) return;
    setBusy(true);
    const response = await api(
      `/v1/tenants/${tenant.id}/leave-policies/${item.id}`,
      { method: "PATCH", body: JSON.stringify(values) },
    );
    if (!response.ok) setError(`leave-policy:${response.status}`);
    await load();
  };
  const removeLeavePolicy = async (item: LeavePolicy) => {
    const values = await requestWorkflowDialog({
      title: locale === "es" ? "Remover política" : "Remove policy",
      detail: item.assignedWorkerCount
        ? locale === "es"
          ? "La política tiene historial o asignaciones y será desactivada, no borrada."
          : "The policy has history or assignments and will be deactivated, not erased."
        : locale === "es"
          ? "La política sin uso puede borrarse permanentemente."
          : "The unused policy may be permanently deleted.",
      confirmLabel: locale === "es" ? "Confirmar remoción" : "Confirm removal",
      cancelLabel: locale === "es" ? "Cancelar" : "Cancel",
      danger: true,
      fields: [
        {
          key: "reason",
          label: locale === "es" ? "Razón" : "Reason",
          type: "textarea",
          required: true,
          minLength: 3,
          fullWidth: true,
        },
      ],
    });
    if (!values) return;
    setBusy(true);
    const response = await api(
      `/v1/tenants/${tenant.id}/leave-policies/${item.id}`,
      { method: "DELETE", body: JSON.stringify(values) },
    );
    if (!response.ok) setError(`leave-policy:${response.status}`);
    await load();
  };
  const reviseHoliday = (item: Holiday) =>
    setLeaveAction({
      kind: "holiday",
      item,
      name: item.name,
      holidayDate: item.holidayDate,
      paid: Boolean(item.paid),
      paidHours: item.paidHours ?? "",
      eligibilityMode: item.eligibilityMode as
        | "all_active"
        | "scheduled_to_work",
      active: Boolean(item.active),
      replaceTargets: false,
      reason: "",
    });
  const submitLeaveAction = async (ev: FormEvent) => {
    ev.preventDefault();
    const action = leaveAction;
    if (!action) return;
    let ok = false;
    if (action.kind === "modify")
      ok = await post(`leave-requests/${action.item.id}/review`, {
        decision: "modified_approved",
        comment: action.reason,
        modification: {
          startsOn: action.startsOn,
          endsOn: action.endsOn,
          hours: action.hours,
        },
      });
    else if (action.kind === "cancel")
      ok = await post(`leave-requests/${action.item.id}/cancel`, {
        reason: action.reason,
      });
    else if (action.kind === "schedule")
      ok = await post("leave-schedule-runs", { asOf: action.asOf });
    else
      ok = await post(`holidays/${action.item.id}/revisions`, {
        name: action.name,
        holidayDate: action.holidayDate,
        paid: action.paid,
        paidHours: action.paid ? action.paidHours : null,
        eligibilityMode: action.eligibilityMode,
        eligibilityTargets: action.replaceTargets
          ? holiday.eligibilityTargets
          : action.item.eligibilityTargets,
        specialRule: action.item.specialRule,
        active: action.active,
        reason: action.reason,
      });
    if (ok) setLeaveAction(null);
  };
  const toggleHolidayTarget = (
    targetType: Holiday["eligibilityTargets"][number]["targetType"],
    targetId: string,
  ) =>
    setHoliday((current) => {
      const exists = current.eligibilityTargets.some(
        (target) =>
          target.targetType === targetType && target.targetId === targetId,
      );
      return {
        ...current,
        eligibilityTargets: exists
          ? current.eligibilityTargets.filter(
              (target) =>
                target.targetType !== targetType ||
                target.targetId !== targetId,
            )
          : [...current.eligibilityTargets, { targetType, targetId }],
      };
    });
  const leaveRequestActions = (item: LeaveRequest) => (
    <div className="run-actions">
      {item.status === "requested" &&
        roleKey !== "user" &&
        item.workerId !== actorWorkerId && (
          <>
            <button onClick={() => void review(item.id, "approved")}>
              {locale === "es" ? "Aprobar" : "Approve"}
            </button>
            <button onClick={() => void modifyAndApprove(item)}>
              {locale === "es" ? "Modificar y aprobar" : "Modify and approve"}
            </button>
            <button
              className="danger-action"
              onClick={() => void review(item.id, "rejected")}
            >
              {locale === "es" ? "Rechazar" : "Reject"}
            </button>
          </>
        )}
      {item.status === "requested" &&
        (item.workerId === actorWorkerId || canManage) && (
          <button
            type="button"
            className="danger-action"
            onClick={() => void cancel(item)}
          >
            {locale === "es" ? "Cancelar" : "Cancel"}
          </button>
        )}
      {item.status === "approved" &&
        canManage &&
        item.workerId !== actorWorkerId && (
          <button
            type="button"
            className="danger-action"
            onClick={() => void cancel(item)}
          >
            {locale === "es" ? "Cancelar y revertir" : "Cancel and reverse"}
          </button>
        )}
    </div>
  );
  const holidayActions = (item: Holiday) =>
    canManage ? (
      <div className="run-actions">
        <button type="button" onClick={() => void reviseHoliday(item)}>
          {locale === "es" ? "Corregir / desactivar" : "Correct / deactivate"}
        </button>
      </div>
    ) : null;
  return (
    <div className="content">
      <p className="kicker">{tenant.displayName}</p>
      <h1>{locale === "es" ? "Licencias y feriados" : "Leave & holidays"}</h1>
      <p className="lead">
        {locale === "es"
          ? "Las acumulaciones, usos y ajustes se conservan como transacciones. Aprobar una solicitud registra el uso automáticamente."
          : "Accruals, usage, and adjustments are retained as transactions. Approving a request records usage automatically."}
      </p>
      <div className="leave-forms">
        {canManage && (
          <details className="operation-panel">
            <summary>
              {locale === "es" ? "Crear política" : "Create policy"}
            </summary>
            <div className="form-grid">
              <label>
                {locale === "es" ? "Nombre" : "Name"}
                <input
                  value={policy.name}
                  onChange={(e) =>
                    setPolicy({ ...policy, name: e.currentTarget.value })
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Tipo" : "Type"}
                <input
                  value={policy.leaveType}
                  onChange={(e) =>
                    setPolicy({ ...policy, leaveType: e.currentTarget.value })
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Método de acumulación" : "Accrual method"}
                <select
                  value={policy.accrualMethod}
                  onChange={(e) =>
                    setPolicy({
                      ...policy,
                      accrualMethod: e.currentTarget.value,
                    })
                  }
                >
                  <option value="per_payroll">
                    {optionLabel("per_payroll", locale)}
                  </option>
                  <option value="per_month">
                    {optionLabel("per_month", locale)}
                  </option>
                  <option value="per_hours_worked">
                    {optionLabel("per_hours_worked", locale)}
                  </option>
                  <option value="annual_grant">
                    {optionLabel("annual_grant", locale)}
                  </option>
                  <option value="manual">
                    {optionLabel("manual", locale)}
                  </option>
                </select>
              </label>
              <label>
                {locale === "es" ? "Valor de acumulación" : "Accrual value"}
                <input
                  value={policy.accrualValue}
                  onChange={(e) =>
                    setPolicy({
                      ...policy,
                      accrualValue: e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Vigente desde" : "Effective from"}
                <input
                  type="date"
                  value={policy.effectiveFrom}
                  onInput={(e) =>
                    setPolicy({
                      ...policy,
                      effectiveFrom: (e.target as HTMLInputElement).value,
                    })
                  }
                  onChange={(e) =>
                    setPolicy({
                      ...policy,
                      effectiveFrom: e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label className="check-label">
                <input
                  type="checkbox"
                  checked={policy.paid}
                  onChange={(e) =>
                    setPolicy({ ...policy, paid: e.currentTarget.checked })
                  }
                />
                {locale === "es" ? "Licencia pagada" : "Paid leave"}
              </label>
              <label className="check-label">
                <input
                  type="checkbox"
                  checked={policy.allowNegative}
                  onChange={(e) =>
                    setPolicy({
                      ...policy,
                      allowNegative: e.currentTarget.checked,
                    })
                  }
                />
                {locale === "es"
                  ? "Permitir balance negativo"
                  : "Allow negative balance"}
              </label>
              {policy.accrualMethod === "annual_grant" && (
                <>
                  <label>
                    {locale === "es"
                      ? "Momento de concesión anual"
                      : "Annual grant timing"}
                    <select
                      value={policy.annualGrantTiming}
                      onChange={(e) =>
                        setPolicy({
                          ...policy,
                          annualGrantTiming: e.currentTarget
                            .value as typeof policy.annualGrantTiming,
                          annualGrantMonthDay:
                            e.currentTarget.value === "calendar_date"
                              ? (policy.annualGrantMonthDay ?? "01-01")
                              : null,
                        })
                      }
                    >
                      <option value="policy_anniversary">
                        {locale === "es"
                          ? "Aniversario de la política"
                          : "Policy anniversary"}
                      </option>
                      <option value="worker_anniversary">
                        {locale === "es"
                          ? "Aniversario laboral"
                          : "Worker anniversary"}
                      </option>
                      <option value="calendar_date">
                        {locale === "es"
                          ? "Fecha anual configurada"
                          : "Configured annual date"}
                      </option>
                    </select>
                  </label>
                  {policy.annualGrantTiming === "calendar_date" && (
                    <label>
                      {locale === "es"
                        ? "Mes y día de concesión (MM-DD)"
                        : "Grant month and day (MM-DD)"}
                      <input
                        value={policy.annualGrantMonthDay ?? ""}
                        pattern="(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])"
                        onChange={(e) =>
                          setPolicy({
                            ...policy,
                            annualGrantMonthDay: e.currentTarget.value || null,
                          })
                        }
                      />
                    </label>
                  )}
                </>
              )}
              <label>
                {locale === "es"
                  ? "Máximo de arrastre anual (horas, opcional)"
                  : "Annual carryover cap (hours, optional)"}
                <input
                  type="number"
                  min="0"
                  step="0.0001"
                  value={policy.carryoverCapHours ?? ""}
                  onInput={(e) =>
                    setPolicy({
                      ...policy,
                      carryoverCapHours: e.currentTarget.value || null,
                    })
                  }
                />
              </label>
              {policy.carryoverCapHours !== null && (
                <>
                  <label>
                    {locale === "es"
                      ? "Momento de reinicio/arrastre"
                      : "Reset/carryover timing"}
                    <select
                      value={policy.carryoverResetTiming}
                      onChange={(e) =>
                        setPolicy({
                          ...policy,
                          carryoverResetTiming: e.currentTarget
                            .value as typeof policy.carryoverResetTiming,
                        })
                      }
                    >
                      <option value="calendar_date">
                        {locale === "es"
                          ? "Fecha anual configurada"
                          : "Configured annual date"}
                      </option>
                      <option value="policy_anniversary">
                        {locale === "es"
                          ? "Aniversario de la política"
                          : "Policy anniversary"}
                      </option>
                      <option value="worker_anniversary">
                        {locale === "es"
                          ? "Aniversario laboral"
                          : "Worker anniversary"}
                      </option>
                    </select>
                  </label>
                  {policy.carryoverResetTiming === "calendar_date" && (
                    <label>
                      {locale === "es"
                        ? "Mes y día del reinicio (MM-DD)"
                        : "Reset month and day (MM-DD)"}
                      <input
                        value={policy.carryoverResetMonthDay}
                        pattern="(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])"
                        onChange={(e) =>
                          setPolicy({
                            ...policy,
                            carryoverResetMonthDay: e.currentTarget.value,
                          })
                        }
                      />
                    </label>
                  )}
                </>
              )}
            </div>
            <button
              className="primary"
              onClick={() => void post("leave-policies", policy)}
            >
              {locale === "es" ? "Guardar política" : "Save policy"}
            </button>
          </details>
        )}
        {canManage && (
          <details className="operation-panel">
            <summary>
              {locale === "es"
                ? "Acumulaciones programadas"
                : "Scheduled accruals"}
            </summary>
            <p>
              {locale === "es"
                ? "Cloudflare ejecuta diariamente acumulaciones mensuales, concesiones anuales y límites de arrastre. Cada período se registra una sola vez."
                : "Cloudflare runs monthly accruals, annual grants, and carryover limits daily. Every period is posted only once."}
            </p>
            <button type="button" onClick={() => void runSchedule()}>
              {locale === "es"
                ? "Ejecutar o recuperar período"
                : "Run or recover period"}
            </button>
            <div className="run-list">
              {scheduleRuns.map((run) => (
                <article key={run.id}>
                  <div>
                    <strong>
                      {formatDateTime(run.runDate, locale)} ·{" "}
                      {run.triggerSource}
                    </strong>
                    <small>
                      {locale === "es" ? "Acumulaciones" : "Grants"}:{" "}
                      {run.grantCount} ·{" "}
                      {locale === "es" ? "Expiraciones" : "Expirations"}:{" "}
                      {run.expirationCount}
                    </small>
                  </div>
                  <span className={`run-status ${run.status}`}>
                    {optionLabel(run.status, locale)}
                  </span>
                </article>
              ))}
            </div>
          </details>
        )}
        {canManage && (
          <details className="operation-panel">
            <summary>
              {locale === "es" ? "Asignar política" : "Assign policy"}
            </summary>
            <div className="form-grid">
              <label>
                {locale === "es" ? "Persona" : "Person"}
                <select
                  value={assignment.workerId}
                  onChange={(e) =>
                    setAssignment({
                      ...assignment,
                      workerId: e.currentTarget.value,
                    })
                  }
                >
                  <option value="">—</option>
                  {workers.map((w) => (
                    <option value={w.id} key={w.id}>
                      {w.firstName} {w.lastName}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {locale === "es" ? "Política" : "Policy"}
                <select
                  value={assignment.policyId}
                  onChange={(e) =>
                    setAssignment({
                      ...assignment,
                      policyId: e.currentTarget.value,
                    })
                  }
                >
                  <option value="">—</option>
                  {policies.map((p) => (
                    <option value={p.id} key={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {locale === "es" ? "Vigente desde" : "Effective from"}
                <input
                  type="date"
                  value={assignment.effectiveFrom}
                  onInput={(e) =>
                    setAssignment({
                      ...assignment,
                      effectiveFrom: (e.target as HTMLInputElement).value,
                    })
                  }
                  onChange={(e) =>
                    setAssignment({
                      ...assignment,
                      effectiveFrom: e.currentTarget.value,
                    })
                  }
                />
              </label>
            </div>
            <button
              className="primary"
              disabled={!assignment.workerId || !assignment.policyId}
              onClick={() => void post("leave-policy-assignments", assignment)}
            >
              {locale === "es" ? "Asignar" : "Assign"}
            </button>
          </details>
        )}
        {canManage && (
          <details className="operation-panel">
            <summary>
              {locale === "es"
                ? "Registrar transacción de balance"
                : "Record balance transaction"}
            </summary>
            <div className="form-grid">
              <label>
                {locale === "es" ? "Persona" : "Person"}
                <select
                  value={ledger.workerId}
                  onChange={(e) =>
                    setLedger({ ...ledger, workerId: e.currentTarget.value })
                  }
                >
                  <option value="">—</option>
                  {workers.map((w) => (
                    <option value={w.id} key={w.id}>
                      {w.firstName} {w.lastName}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {locale === "es" ? "Política" : "Policy"}
                <select
                  value={ledger.policyId}
                  onChange={(e) =>
                    setLedger({ ...ledger, policyId: e.currentTarget.value })
                  }
                >
                  <option value="">—</option>
                  {policies.map((p) => (
                    <option value={p.id} key={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {locale === "es" ? "Transacción" : "Transaction"}
                <select
                  value={ledger.transactionType}
                  onChange={(e) =>
                    setLedger({
                      ...ledger,
                      transactionType: e.currentTarget.value,
                    })
                  }
                >
                  <option value="opening">
                    {optionLabel("opening", locale)}
                  </option>
                  <option value="accrual">
                    {optionLabel("accrual", locale)}
                  </option>
                  <option value="adjustment">
                    {optionLabel("adjustment", locale)}
                  </option>
                  <option value="expiration">
                    {optionLabel("expiration", locale)}
                  </option>
                  <option value="reversal">
                    {optionLabel("reversal", locale)}
                  </option>
                </select>
              </label>
              <label>
                {locale === "es" ? "Horas" : "Hours"}
                <input
                  value={ledger.hours}
                  onChange={(e) =>
                    setLedger({ ...ledger, hours: e.currentTarget.value })
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Fecha efectiva" : "Effective date"}
                <input
                  type="date"
                  value={ledger.effectiveOn}
                  onInput={(e) =>
                    setLedger({
                      ...ledger,
                      effectiveOn: (e.target as HTMLInputElement).value,
                    })
                  }
                  onChange={(e) =>
                    setLedger({ ...ledger, effectiveOn: e.currentTarget.value })
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Razón" : "Reason"}
                <input
                  value={ledger.reason}
                  onChange={(e) =>
                    setLedger({ ...ledger, reason: e.currentTarget.value })
                  }
                />
              </label>
            </div>
            <button
              className="primary"
              onClick={() => void post("leave-ledger", ledger)}
            >
              {locale === "es" ? "Registrar" : "Record"}
            </button>
          </details>
        )}
        <details className="operation-panel">
          <summary>
            {locale === "es" ? "Solicitar licencia" : "Request leave"}
          </summary>
          <div className="form-grid">
            <label>
              {locale === "es" ? "Persona" : "Person"}
              <select
                value={leaveRequest.workerId}
                onChange={(e) =>
                  setLeaveRequest({
                    ...leaveRequest,
                    workerId: e.currentTarget.value,
                  })
                }
              >
                <option value="">—</option>
                {workers.map((w) => (
                  <option value={w.id} key={w.id}>
                    {w.firstName} {w.lastName}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {locale === "es" ? "Política" : "Policy"}
              <select
                value={leaveRequest.policyId}
                onChange={(e) =>
                  setLeaveRequest({
                    ...leaveRequest,
                    policyId: e.currentTarget.value,
                  })
                }
              >
                <option value="">—</option>
                {policies.map((p) => (
                  <option value={p.id} key={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {locale === "es" ? "Inicio" : "Start"}
              <input
                type="date"
                value={leaveRequest.startsOn}
                onInput={(e) =>
                  setLeaveRequest({
                    ...leaveRequest,
                    startsOn: (e.target as HTMLInputElement).value,
                  })
                }
                onChange={(e) =>
                  setLeaveRequest({
                    ...leaveRequest,
                    startsOn: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Fin" : "End"}
              <input
                type="date"
                value={leaveRequest.endsOn}
                onInput={(e) =>
                  setLeaveRequest({
                    ...leaveRequest,
                    endsOn: (e.target as HTMLInputElement).value,
                  })
                }
                onChange={(e) =>
                  setLeaveRequest({
                    ...leaveRequest,
                    endsOn: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Horas" : "Hours"}
              <input
                value={leaveRequest.hours}
                onChange={(e) =>
                  setLeaveRequest({
                    ...leaveRequest,
                    hours: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Razón" : "Reason"}
              <input
                value={leaveRequest.reason}
                onChange={(e) =>
                  setLeaveRequest({
                    ...leaveRequest,
                    reason: e.currentTarget.value,
                  })
                }
              />
            </label>
          </div>
          <button
            className="primary"
            onClick={() =>
              void post("leave-requests", {
                ...leaveRequest,
                reason: leaveRequest.reason || null,
              })
            }
          >
            {locale === "es" ? "Someter" : "Submit"}
          </button>
        </details>
        {canManage && (
          <details className="operation-panel">
            <summary>
              {locale === "es" ? "Añadir feriado" : "Add holiday"}
            </summary>
            <div className="form-grid">
              <label>
                {locale === "es" ? "Nombre" : "Name"}
                <input
                  value={holiday.name}
                  onChange={(e) =>
                    setHoliday({ ...holiday, name: e.currentTarget.value })
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Fecha" : "Date"}
                <input
                  type="date"
                  value={holiday.holidayDate}
                  onInput={(e) =>
                    setHoliday({
                      ...holiday,
                      holidayDate: (e.target as HTMLInputElement).value,
                    })
                  }
                  onChange={(e) =>
                    setHoliday({
                      ...holiday,
                      holidayDate: e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Horas pagadas" : "Paid hours"}
                <input
                  value={holiday.paidHours}
                  onChange={(e) =>
                    setHoliday({ ...holiday, paidHours: e.currentTarget.value })
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Elegibilidad" : "Eligibility"}
                <select
                  value={holiday.eligibilityMode}
                  onChange={(e) =>
                    setHoliday({
                      ...holiday,
                      eligibilityMode: e.currentTarget.value,
                    })
                  }
                >
                  <option value="all_active">
                    {locale === "es"
                      ? "Todo empleado activo por hora"
                      : "All active hourly employees"}
                  </option>
                  <option value="scheduled_to_work">
                    {locale === "es"
                      ? "Solo programado para trabajar"
                      : "Scheduled to work only"}
                  </option>
                </select>
              </label>
            </div>
            <h3>
              {locale === "es"
                ? "Personas y grupos elegibles (opcional)"
                : "Eligible workers and groups (optional)"}
            </h3>
            <p>
              {locale === "es"
                ? "Sin selección, aplica a todas las personas que cumplan el modo. Con selección, aplica a cualquier persona seleccionada o asignada a uno de los grupos elegidos en la fecha del feriado."
                : "With no selection, all workers matching the mode qualify. With targets, a selected worker or anyone assigned to a selected group on the holiday date qualifies."}
            </p>
            <div className="setup-grid">
              <div>
                <strong>{locale === "es" ? "Personas" : "Workers"}</strong>
                {workers
                  .filter(
                    (worker) =>
                      worker.workerType === "employee" &&
                      !["inactive", "terminated"].includes(worker.status),
                  )
                  .map((worker) => (
                    <label className="check-label" key={worker.id}>
                      <input
                        type="checkbox"
                        checked={holiday.eligibilityTargets.some(
                          (target) =>
                            target.targetType === "worker" &&
                            target.targetId === worker.id,
                        )}
                        onChange={() =>
                          toggleHolidayTarget("worker", worker.id)
                        }
                      />
                      {worker.firstName} {worker.lastName}
                    </label>
                  ))}
              </div>
              <div>
                <strong>
                  {locale === "es" ? "Departamentos" : "Departments"}
                </strong>
                {holidayDepartments
                  .filter((item) => item.active)
                  .map((item) => (
                    <label className="check-label" key={item.id}>
                      <input
                        type="checkbox"
                        checked={holiday.eligibilityTargets.some(
                          (target) =>
                            target.targetType === "department" &&
                            target.targetId === item.id,
                        )}
                        onChange={() =>
                          toggleHolidayTarget("department", item.id)
                        }
                      />
                      {item.name}
                    </label>
                  ))}
              </div>
              <div>
                <strong>{locale === "es" ? "Ubicaciones" : "Locations"}</strong>
                {holidayLocations
                  .filter((item) => item.active)
                  .map((item) => (
                    <label className="check-label" key={item.id}>
                      <input
                        type="checkbox"
                        checked={holiday.eligibilityTargets.some(
                          (target) =>
                            target.targetType === "location" &&
                            target.targetId === item.id,
                        )}
                        onChange={() =>
                          toggleHolidayTarget("location", item.id)
                        }
                      />
                      {item.name}
                    </label>
                  ))}
              </div>
              <div>
                <strong>
                  {locale === "es" ? "Centros de costo" : "Cost centers"}
                </strong>
                {holidayCostCenters
                  .filter((item) => item.active)
                  .map((item) => (
                    <label className="check-label" key={item.id}>
                      <input
                        type="checkbox"
                        checked={holiday.eligibilityTargets.some(
                          (target) =>
                            target.targetType === "cost_center" &&
                            target.targetId === item.id,
                        )}
                        onChange={() =>
                          toggleHolidayTarget("cost_center", item.id)
                        }
                      />
                      {item.code} · {item.name}
                    </label>
                  ))}
              </div>
            </div>
            <button
              className="primary"
              onClick={() => void post("holidays", holiday)}
            >
              {locale === "es" ? "Guardar feriado" : "Save holiday"}
            </button>
          </details>
        )}
      </div>
      <OperationalError value={error} locale={locale} />
      {busy ? (
        <LoaderCircle className="spin" />
      ) : (
        <>
          {canManage && (
            <section className="settings-form">
              <div className="section-heading">
                <div>
                  <h2>
                    {locale === "es"
                      ? "Políticas estatutarias aplicables"
                      : "Applicable statutory policies"}
                  </h2>
                  <p>
                    {locale === "es"
                      ? "Ley 180: la acumulación mensual requiere por lo menos 130 horas calificadas. El sistema asigna vacaciones según tamaño patronal, antigüedad y fecha de contratación; enfermedad acumula 8 horas."
                      : "Act 180: monthly accrual requires at least 130 qualifying hours. The system assigns vacation by employer size, service, and hire date; sick leave accrues 8 hours."}
                  </p>
                </div>
                <button
                  type="button"
                  className="primary"
                  disabled={busy}
                  onClick={() =>
                    void post("leave-policies/configure-act-180", {})
                  }
                >
                  {locale === "es"
                    ? "Aplicar o verificar Ley 180"
                    : "Apply or verify Act 180"}
                </button>
              </div>
              <div className="run-list">
                {policies
                  .filter((item) => item.statutoryBasis)
                  .map((item) => (
                    <article key={item.id}>
                      <div>
                        <strong>{item.name}</strong>
                        <p>
                          {item.accrualValue} h/
                          {locale === "es"
                            ? "mes elegible"
                            : "eligible month"}{" "}
                          · {locale === "es" ? "mínimo" : "minimum"}{" "}
                          {item.minimumMonthlyHours} h
                        </p>
                        <small>{item.statutoryBasis}</small>
                      </div>
                      <span
                        className={`run-status ${item.assignedWorkerCount > 0 ? "active" : "inactive"}`}
                      >
                        {item.assignedWorkerCount > 0
                          ? locale === "es"
                            ? `Aplica a ${item.assignedWorkerCount}`
                            : `Applies to ${item.assignedWorkerCount}`
                          : locale === "es"
                            ? "No aplica actualmente"
                            : "Does not currently apply"}
                      </span>
                      <div className="run-actions">
                        {item.statutorySourceUrl && (
                          <a
                            className="official-source-link"
                            href={item.statutorySourceUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {locale === "es"
                              ? "Fuente oficial"
                              : "Official source"}
                          </a>
                        )}
                        <button
                          type="button"
                          onClick={() => void editLeavePolicy(item)}
                        >
                          {locale === "es" ? "Modificar" : "Edit"}
                        </button>
                        <button
                          type="button"
                          className="danger-action"
                          onClick={() => void removeLeavePolicy(item)}
                        >
                          {locale === "es" ? "Remover" : "Remove"}
                        </button>
                      </div>
                    </article>
                  ))}
              </div>
              {!busy && !policies.some((item) => item.statutoryBasis) && (
                <p className="form-detail">
                  {locale === "es"
                    ? "Las reglas todavía no se han aplicado a esta compañía."
                    : "The rules have not yet been applied to this company."}
                </p>
              )}
            </section>
          )}
          <section className="settings-form">
            <h2>
              {locale === "es" ? "Balances por persona" : "Balances by person"}
            </h2>
            <label>
              {locale === "es" ? "Persona" : "Person"}
              <select
                value={balanceWorkerId}
                onChange={(event) =>
                  setBalanceWorkerId(event.currentTarget.value)
                }
              >
                <option value="">—</option>
                {workers
                  .filter(
                    (item) =>
                      item.workerType === "employee" &&
                      !["inactive", "terminated"].includes(item.status),
                  )
                  .map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.firstName} {item.lastName}
                    </option>
                  ))}
              </select>
            </label>
            <div className="balance-grid">
              {policyAssignments
                .filter((item) => item.workerId === balanceWorkerId)
                .map((assignment) => {
                  const selectedPolicy = policies.find(
                      (item) => item.id === assignment.policyId,
                    ),
                    balance = balances.find(
                      (item) =>
                        item.workerId === balanceWorkerId &&
                        item.policyId === assignment.policyId,
                    );
                  return selectedPolicy ? (
                    <article key={assignment.policyId}>
                      <strong>{selectedPolicy.name}</strong>
                      <span>
                        {selectedPolicy.accrualValue} h/
                        {locale === "es" ? "mes elegible" : "eligible month"}
                      </span>
                      <em>{Number(balance?.balanceHours ?? 0).toFixed(2)} h</em>
                    </article>
                  ) : null;
                })}
            </div>
            {balanceWorkerId &&
              !policyAssignments.some(
                (item) => item.workerId === balanceWorkerId,
              ) && (
                <p className="empty-state">
                  {locale === "es"
                    ? "Esta persona no tiene políticas de licencia asignadas."
                    : "This person has no assigned leave policies."}
                </p>
              )}
          </section>
          <h2 className="subheading">
            {locale === "es" ? "Solicitudes" : "Requests"}
          </h2>
          <ResponsiveDataTable
            label={
              locale === "es"
                ? "Tabla de solicitudes de licencia"
                : "Leave requests table"
            }
            caption={
              locale === "es"
                ? "Solicitudes por persona, política, fechas, horas, estado y acciones"
                : "Requests by worker, policy, dates, hours, status, and actions"
            }
            columns={
              locale === "es"
                ? [
                    "Persona",
                    "Política",
                    "Fechas",
                    "Horas",
                    "Estado",
                    "Acciones",
                  ]
                : ["Worker", "Policy", "Dates", "Hours", "Status", "Actions"]
            }
            empty={
              locale === "es"
                ? "No hay solicitudes de licencia."
                : "There are no leave requests."
            }
            rows={requests.map((item) => ({
              key: item.id,
              cells: [
                <strong>{item.workerName}</strong>,
                item.policyName,
                formatDateRange(item.startsOn, item.endsOn, locale, "—"),
                `${item.hours} h`,
                <span className={`run-status ${item.status}`}>
                  {optionLabel(item.status, locale)}
                </span>,
                leaveRequestActions(item),
              ],
              card: (
                <article key={item.id}>
                  <div>
                    <strong>
                      {item.workerName} · {item.policyName}
                    </strong>
                    <p>
                      {formatDateRange(item.startsOn, item.endsOn, locale, "—")}{" "}
                      · {item.hours} h
                    </p>
                  </div>
                  <span className={`run-status ${item.status}`}>
                    {optionLabel(item.status, locale)}
                  </span>
                  {leaveRequestActions(item)}
                </article>
              ),
            }))}
          />
          <h2 className="subheading">
            {locale === "es" ? "Días feriados" : "Holidays"}
          </h2>
          <ResponsiveDataTable
            label={
              locale === "es" ? "Tabla de días feriados" : "Holidays table"
            }
            caption={
              locale === "es"
                ? "Feriados por nombre, fecha, pago, elegibilidad, estado, revisión y acciones"
                : "Holidays by name, date, pay, eligibility, status, revision, and actions"
            }
            columns={
              locale === "es"
                ? [
                    "Feriado",
                    "Fecha",
                    "Pago",
                    "Elegibilidad",
                    "Estado y revisión",
                    "Acciones",
                  ]
                : [
                    "Holiday",
                    "Date",
                    "Pay",
                    "Eligibility",
                    "Status and revision",
                    "Actions",
                  ]
            }
            empty={
              locale === "es"
                ? "No hay días feriados configurados."
                : "There are no configured holidays."
            }
            rows={holidays.map((item) => {
              const payText = item.paid
                ? `${item.paidHours ?? 0} h`
                : locale === "es"
                  ? "No pagado"
                  : "Unpaid";
              const eligibilityText = item.eligibilityTargets.length
                ? `${locale === "es" ? "Objetivos" : "Targets"}: ${item.eligibilityTargets.length}`
                : locale === "es"
                  ? "Toda persona que cumpla el modo"
                  : "All workers matching the mode";
              const status = item.active ? "active" : "inactive";
              return {
                key: item.id,
                cells: [
                  <strong>{item.name}</strong>,
                  formatDate(item.holidayDate, locale),
                  payText,
                  eligibilityText,
                  <>
                    <span className={`run-status ${status}`}>
                      {optionLabel(status, locale)}
                    </span>
                    <small>
                      {locale === "es" ? "Revisión" : "Revision"}{" "}
                      {item.revisionNumber}
                    </small>
                  </>,
                  holidayActions(item),
                ],
                card: (
                  <article key={item.id}>
                    <div>
                      <strong>{item.name}</strong>
                      <p>
                        {formatDate(item.holidayDate, locale)} · {payText}
                      </p>
                      <small>
                        {eligibilityText} · {optionLabel(status, locale)} ·{" "}
                        {locale === "es" ? "revisión" : "revision"}{" "}
                        {item.revisionNumber}
                      </small>
                    </div>
                    {holidayActions(item)}
                  </article>
                ),
              };
            })}
          />
        </>
      )}
      {leaveAction && (
        <dialog
          className="workflow-dialog"
          open
          aria-labelledby="leave-action-title"
        >
          <form onSubmit={submitLeaveAction}>
            <header>
              <div>
                <p className="kicker">
                  {locale === "es"
                    ? "FLUJO DE LICENCIA AUDITADO"
                    : "AUDITED LEAVE WORKFLOW"}
                </p>
                <h2 id="leave-action-title">
                  {leaveAction.kind === "modify"
                    ? locale === "es"
                      ? "Modificar y aprobar solicitud"
                      : "Modify and approve request"
                    : leaveAction.kind === "cancel"
                      ? locale === "es"
                        ? "Cancelar solicitud o uso"
                        : "Cancel request or usage"
                      : leaveAction.kind === "schedule"
                        ? locale === "es"
                          ? "Ejecutar reglas programadas"
                          : "Run scheduled rules"
                        : locale === "es"
                          ? "Crear revisión de feriado"
                          : "Create holiday revision"}
                </h2>
                <p>
                  {leaveAction.kind === "modify" ||
                  leaveAction.kind === "cancel"
                    ? `${leaveAction.item.workerName} · ${leaveAction.item.policyName}`
                    : leaveAction.kind === "holiday"
                      ? leaveAction.item.name
                      : tenant.displayName}
                </p>
              </div>
              <button
                type="button"
                aria-label={locale === "es" ? "Cerrar" : "Close"}
                onClick={() => setLeaveAction(null)}
              >
                ×
              </button>
            </header>
            <p className="form-detail">
              {leaveAction.kind === "modify"
                ? locale === "es"
                  ? "La solicitud original se conserva y la modificación aprobada registra exactamente las fechas, horas y explicación siguientes."
                  : "The original request is retained and the approved modification records the exact dates, hours, and explanation below."
                : leaveAction.kind === "cancel"
                  ? locale === "es"
                    ? "Si ya estaba aprobada, el sistema registra la reversión correspondiente sin borrar el uso original."
                    : "If already approved, the system records the corresponding reversal without deleting the original usage."
                  : leaveAction.kind === "schedule"
                    ? locale === "es"
                      ? "Se procesarán acumulaciones, concesiones y reinicios vencidos hasta esta fecha de control."
                      : "Accruals, grants, and due resets will be processed through this control date."
                    : locale === "es"
                      ? "La revisión sustituye la versión vigente sin reescribir el feriado ni su historial de elegibilidad."
                      : "The revision supersedes the current version without rewriting the holiday or its eligibility history."}
            </p>
            <div className="form-grid">
              {leaveAction.kind === "modify" ? (
                <>
                  <label>
                    {locale === "es" ? "Inicio aprobado" : "Approved start"}
                    <input
                      required
                      type="date"
                      value={leaveAction.startsOn}
                      onInput={(e) =>
                        setLeaveAction({
                          ...leaveAction,
                          startsOn: e.currentTarget.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    {locale === "es" ? "Final aprobado" : "Approved end"}
                    <input
                      required
                      type="date"
                      min={leaveAction.startsOn}
                      value={leaveAction.endsOn}
                      onInput={(e) =>
                        setLeaveAction({
                          ...leaveAction,
                          endsOn: e.currentTarget.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    {locale === "es" ? "Horas aprobadas" : "Approved hours"}
                    <input
                      required
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={leaveAction.hours}
                      onInput={(e) =>
                        setLeaveAction({
                          ...leaveAction,
                          hours: e.currentTarget.value,
                        })
                      }
                    />
                  </label>
                  <label className="full-width">
                    {locale === "es"
                      ? "Explicación de la modificación"
                      : "Modification explanation"}
                    <textarea
                      required
                      minLength={3}
                      value={leaveAction.reason}
                      onChange={(e) =>
                        setLeaveAction({
                          ...leaveAction,
                          reason: e.currentTarget.value,
                        })
                      }
                    />
                  </label>
                </>
              ) : leaveAction.kind === "cancel" ? (
                <label className="full-width">
                  {locale === "es"
                    ? "Razón de cancelación"
                    : "Cancellation reason"}
                  <textarea
                    required
                    minLength={3}
                    value={leaveAction.reason}
                    onChange={(e) =>
                      setLeaveAction({
                        ...leaveAction,
                        reason: e.currentTarget.value,
                      })
                    }
                  />
                </label>
              ) : leaveAction.kind === "schedule" ? (
                <label>
                  {locale === "es" ? "Procesar hasta" : "Process through"}
                  <input
                    required
                    type="date"
                    value={leaveAction.asOf}
                    onInput={(e) =>
                      setLeaveAction({
                        ...leaveAction,
                        asOf: e.currentTarget.value,
                      })
                    }
                  />
                </label>
              ) : (
                <>
                  <label>
                    {locale === "es" ? "Nombre" : "Name"}
                    <input
                      required
                      value={leaveAction.name}
                      onChange={(e) =>
                        setLeaveAction({
                          ...leaveAction,
                          name: e.currentTarget.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    {locale === "es" ? "Fecha" : "Date"}
                    <input
                      required
                      type="date"
                      value={leaveAction.holidayDate}
                      onInput={(e) =>
                        setLeaveAction({
                          ...leaveAction,
                          holidayDate: e.currentTarget.value,
                        })
                      }
                    />
                  </label>
                  <label className="check-label">
                    <input
                      type="checkbox"
                      checked={leaveAction.paid}
                      onChange={(e) =>
                        setLeaveAction({
                          ...leaveAction,
                          paid: e.currentTarget.checked,
                        })
                      }
                    />
                    {locale === "es" ? "Feriado pagado" : "Paid holiday"}
                  </label>
                  {leaveAction.paid && (
                    <label>
                      {locale === "es" ? "Horas pagadas" : "Paid hours"}
                      <input
                        required
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={leaveAction.paidHours}
                        onInput={(e) =>
                          setLeaveAction({
                            ...leaveAction,
                            paidHours: e.currentTarget.value,
                          })
                        }
                      />
                    </label>
                  )}
                  <label>
                    {locale === "es" ? "Elegibilidad" : "Eligibility"}
                    <select
                      value={leaveAction.eligibilityMode}
                      onChange={(e) =>
                        setLeaveAction({
                          ...leaveAction,
                          eligibilityMode: e.currentTarget.value as
                            | "all_active"
                            | "scheduled_to_work",
                        })
                      }
                    >
                      <option value="all_active">
                        {locale === "es"
                          ? "Toda persona activa que cumpla"
                          : "All qualifying active workers"}
                      </option>
                      <option value="scheduled_to_work">
                        {locale === "es"
                          ? "Solo quienes estaban programados"
                          : "Only workers scheduled to work"}
                      </option>
                    </select>
                  </label>
                  <label className="check-label">
                    <input
                      type="checkbox"
                      checked={leaveAction.active}
                      onChange={(e) =>
                        setLeaveAction({
                          ...leaveAction,
                          active: e.currentTarget.checked,
                        })
                      }
                    />
                    {locale === "es"
                      ? "Mantener feriado activo"
                      : "Keep holiday active"}
                  </label>
                  <label className="check-label full-width">
                    <input
                      type="checkbox"
                      aria-label={locale === "es" ? "Reemplazar la selección de personas elegibles para este feriado" : "Replace the eligible-worker selection for this holiday"}
                      checked={leaveAction.replaceTargets}
                      onChange={(e) =>
                        setLeaveAction({
                          ...leaveAction,
                          replaceTargets: e.currentTarget.checked,
                        })
                      }
                    />
                    {locale === "es"
                      ? `Usar la selección actual del formulario (${holiday.eligibilityTargets.length} objetivos); sin marcar conserva los ${leaveAction.item.eligibilityTargets.length} vigentes.`
                      : `Use the current form selection (${holiday.eligibilityTargets.length} targets); unchecked preserves the ${leaveAction.item.eligibilityTargets.length} current targets.`}
                  </label>
                  <label className="full-width">
                    {locale === "es"
                      ? "Razón de la revisión"
                      : "Revision reason"}
                    <textarea
                      required
                      minLength={3}
                      value={leaveAction.reason}
                      onChange={(e) =>
                        setLeaveAction({
                          ...leaveAction,
                          reason: e.currentTarget.value,
                        })
                      }
                    />
                  </label>
                </>
              )}
            </div>
            <footer>
              <button type="button" onClick={() => setLeaveAction(null)}>
                {locale === "es" ? "Cancelar" : "Cancel"}
              </button>
              <button
                className={
                  leaveAction.kind === "cancel" ? "danger-action" : "primary"
                }
                disabled={
                  busy ||
                  (leaveAction.kind !== "schedule" &&
                    leaveAction.reason.trim().length < 3)
                }
              >
                {busy ? (
                  <LoaderCircle className="spin" />
                ) : locale === "es" ? (
                  "Confirmar"
                ) : (
                  "Confirm"
                )}
              </button>
            </footer>
          </form>
        </dialog>
      )}
    </div>
  );
}

type PayrollRun = {
  id: string;
  runType: string;
  periodStart: string;
  periodEnd: string;
  payDate: string;
  payrollFrequency: string;
  status: string;
  sourceRunId: string | null;
  correctionReason: string | null;
  companionReversalRunId: string | null;
  voidedByRunId: string | null;
  christmasBonusCycleId: string | null;
  approvalReason: string | null;
  approvedAt: string | null;
  lastReviewDecision: string | null;
  lastReviewReason: string | null;
};
type ContractorPayment = {
  id: string;
  workerId: string;
  workerName: string;
  paymentDate: string;
  description: string;
  grossCents: number;
  exemptionAppliedCents: number;
  subjectCents: number;
  withholdingRateBps: number;
  withholdingCents: number;
  netCents: number;
  compensationType: "hourly" | "fixed_fee" | "per_job" | "daily" | "weekly" | null;
  hoursHundredths: number | null;
  rateCents: number | null;
  status: string;
};
type WorkerDeduction = {
  id: string;
  workerId: string;
  workerName: string;
  deductionType: string;
  description: string;
  calculationType: string;
  amountCents: number | null;
  rateBps: number | null;
  currentBalanceCents: number | null;
  priority: number;
  maximumPerPayrollCents: number | null;
  disposableIncomeLimitBps: number;
  aggregateLimitBps: number | null;
  prorationWeightCents: number | null;
  status: string;
};
type DeductionDocument = {
  id: string;
  documentType: string;
  fileName: string;
  byteSize: number;
  contentSha256: string;
  createdAt: string;
};
type DeductionBalanceEvent = {
  id: string;
  eventType: string;
  amountCents: number;
  balanceAfterCents: number | null;
  reason: string | null;
  createdAt: string;
};
type TaxElection = {
  id: string;
  personalExemption: string;
  veteran: number;
  dependents: number;
  halfDependents: number;
  deductionAllowances: number;
  additionalWithholdingCents: number;
  additionalWithholdingBps: number;
  militarySpouseExempt: number;
  certifiedAt: string | null;
  certificationMethod: string | null;
  formVersion: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  certificateDocumentId?: string | null;
  haciendaSubmissionStatus?: string | null;
};
type HaciendaSubmission = {
  case: {
    id: string;
    status: string;
    submissionMethod: string | null;
    submittedOn: string | null;
    submissionReference: string | null;
    notes: string | null;
  };
  documents: {
    id: string;
    documentType: string;
    fileName: string;
    byteSize: number;
    contentSha256: string;
    createdAt: string;
  }[];
  events: {
    id: string;
    fromStatus: string | null;
    toStatus: string;
    reason: string;
    createdAt: string;
  }[];
};
type TaxSubmissionDraft = {
  item: TaxElection;
  status: "ready" | "submitted" | "confirmed" | "returned";
  reason: string;
  submissionMethod: "electronic" | "mail" | "in_person" | "other";
  submittedOn: string;
  submissionReference: string;
};
type BonusCycle = {
  id: string;
  coverageYear: number;
  coverageStart: string;
  coverageEnd: string;
  paymentDeadline: string;
  employeeCount: number;
  exemptionStatus: string;
  exemptionReference: string | null;
  hasExemptionDocument: number;
  status: string;
  ruleVersion: string;
  withholdingMethod:
    | "annual_aggregate"
    | "prior_effective_rate"
    | "accumulated_wage_bracket"
    | null;
  payrollRunId: string | null;
  paymentDate: string | null;
};
type BonusItem = {
  id: string;
  workerId: string;
  workerName: string;
  qualifyingHoursHundredths: number;
  qualifyingEarningsCents: number;
  eligible: number;
  requiredHours: number;
  rateBps: number;
  maximumBonusCents: number;
  bonusCents: number;
};
type EmployerCostRule = {
  id: string;
  workerId: string | null;
  workerName: string | null;
  costType: "benefit" | "other_burden";
  costKey: string;
  description: string;
  calculationType: "fixed" | "percentage";
  basis: "gross_pay" | "taxable_wages";
  amountCents: number | null;
  rateBps: number | null;
  createsLiability: number;
  liabilityPayee: string | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  status: string;
};
type SupplementalWithholdingPolicy = {
  id: string;
  taxYear: number;
  method: "annualized_fluctuating";
  adoptedOn: string;
  reason: string;
  sourceUrl: string;
};
type RecurringBonusInstruction = {
  id: string;
  workerId: string;
  workerName: string;
  amountCents: number;
  description: string;
  regularRateTreatment: "included_nondiscretionary" | "excluded_discretionary";
  effectiveFrom: string;
  effectiveTo: string | null;
  status: "active" | "ended";
  createdAt: string;
};
type ManualEarningCode =
  | "regular"
  | "overtime"
  | "vacation"
  | "sick"
  | "holiday"
  | "bonus"
  | "recurring_bonus"
  | "commission"
  | "car_allowance"
  | "mileage"
  | "expense_reimbursement"
  | "per_diem"
  | "other_earnings"
  | "other_nonwage_payment";
type ManualEarningLine = {
  id: string;
  earningCode: ManualEarningCode;
  amount: string;
  taxTreatment: "taxable_wages" | "accountable_plan_nontaxable";
  description: string;
  expenseClaimId?: string;
  projectId?: string | undefined;
  regularRateTreatment?:
    | "included_nondiscretionary"
    | "excluded_discretionary"
    | "excluded_statutory"
    | undefined;
  regularRateAllocationStart?: string | undefined;
  regularRateAllocationEnd?: string | undefined;
  regularRateAllocationMethod?: "equal_workweeks" | undefined;
  regularRateAllocationStrategy?:
    | "equal_workweeks"
    | "proportional_weekly_earnings"
    | undefined;
  regularRateAllocationWeightsJson?: string | undefined;
};
type OvertimeClassification = {
  id: string;
  workerId: string;
  workerName: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  classification:
    | "nonexempt"
    | "exempt_executive"
    | "exempt_administrative"
    | "exempt_professional"
    | "exempt_outside_sales"
    | "exempt_other_statutory";
  dutiesSummary: string;
  salaryBasisReviewed: number;
  legalSourceUrl: string;
  reason: string;
  evidenceFileName: string | null;
  createdAt: string;
};
type FinalPayPlan = {
  id: string;
  workerId: string;
  workerName: string;
  endDate: string;
  dueOn: string;
  leaveDisposition: string;
  deductionDisposition: string;
  late: boolean;
  balances: {
    policyId: string;
    policyName: string;
    leaveType: string;
    hours: number;
  }[];
  rateCents: number | null;
  issues: string[];
  earnings: Record<string, unknown>[];
};
type PayrollActionDraft =
  | {
      kind: "run_decision";
      item: PayrollRun & { description: string };
      verb: "approve" | "reject";
      reason: string;
    }
  | {
      kind: "expense";
      item: ExpenseClaim;
      decision: "approved" | "rejected";
      reason: string;
    }
  | {
      kind: "close_cost";
      item: EmployerCostRule;
      effectiveTo: string;
      reason: string;
    }
  | { kind: "adopt_supplemental"; taxYear: number; reason: string }
  | {
      kind: "end_bonus";
      item: RecurringBonusInstruction;
      effectiveTo: string;
      reason: string;
    }
  | {
      kind: "diagnostic";
      issueId: string;
      action: "explain" | "acknowledge" | "approve_exception";
      reason: string;
    }
  | {
      kind: "deduction_status";
      item: WorkerDeduction;
      status: "active" | "suspended" | "completed";
      reason: string;
    }
  | {
      kind: "deduction_balance";
      item: WorkerDeduction;
      amount: string;
      reason: string;
    };
type BonusActionDraft =
  | {
      kind: "status";
      cycle: BonusCycle;
      status: "approved" | "finalized" | "voided";
      reason: string;
    }
  | {
      kind: "payroll";
      cycle: BonusCycle;
      payDate: string;
      withholdingMethod:
        | "annual_aggregate"
        | "prior_effective_rate"
        | "accumulated_wage_bracket";
    };
function Payroll({
  locale,
  tenant,
  navigate,
  setupMode = null,
  setupWorkerId = "",
}: {
  locale: Locale;
  tenant: Tenant;
  navigate: (section: Section) => void;
  setupMode?: "overtime" | "pay" | "tax" | null;
  setupWorkerId?: string;
}) {
  const [workers, setWorkers] = useState<Worker[]>([]),
    [runs, setRuns] = useState<PayrollRun[]>([]),
    [payments, setPayments] = useState<ContractorPayment[]>([]),
    [deductions, setDeductions] = useState<WorkerDeduction[]>([]),
    [deductionEvidence, setDeductionEvidence] = useState<
      Record<
        string,
        {
          documents: DeductionDocument[];
          balanceEvents: DeductionBalanceEvent[];
        }
      >
    >({}),
    [bonusCycles, setBonusCycles] = useState<BonusCycle[]>([]),
    [bonusItems, setBonusItems] = useState<Record<string, BonusItem[]>>({}),
    [expenseClaims, setExpenseClaims] = useState<ExpenseClaim[]>([]),
    [employerCostRules, setEmployerCostRules] = useState<EmployerCostRule[]>(
      [],
    ),
    [supplementalPolicies, setSupplementalPolicies] = useState<
      SupplementalWithholdingPolicy[]
    >([]),
    [recurringBonuses, setRecurringBonuses] = useState<
      RecurringBonusInstruction[]
    >([]),
    [overtimeClassifications, setOvertimeClassifications] = useState<
      OvertimeClassification[]
    >([]),
    [payrollProjects, setPayrollProjects] = useState<Project[]>([]),
    [diagnosticIssues, setDiagnosticIssues] = useState<
      {
        id: string;
        workerId: string | null;
        workerName?: string | null;
        code: string;
        severity: string;
        status?: string;
        messageEs?: string;
        messageEn?: string;
        events?: {
          id: string;
          action: string;
          reason: string;
          occurredAt: string;
        }[];
      }[]
    >([]),
    [diagnosticRunId, setDiagnosticRunId] = useState(""),
    [preparedTime, setPreparedTime] = useState<{
      runId: string;
      items: {
        workerId: string;
        earnings: {
          earningType: "regular" | "overtime" | "vacation" | "sick" | "holiday";
          amountCents: number;
          hoursHundredths: number | null;
          rateCents: number | null;
          sourceTimesheetId?: string;
          sourceLeaveRequestId?: string;
          sourceHolidayId?: string;
          sourceRuleVersion: string;
        }[];
      }[];
    } | null>(null),
    [preparedFinalPay, setPreparedFinalPay] = useState<{
      runId: string;
      plans: FinalPayPlan[];
      items: {
        workerId: string;
        finalPayPeriodId: string;
        earnings: Record<string, unknown>[];
      }[];
    } | null>(null),
    [timeImportIssues, setTimeImportIssues] = useState<
      { workerId: string | null; timesheetId: string | null; code: string }[]
    >([]),
    [timeImportExcludedWorkerIds, setTimeImportExcludedWorkerIds] = useState<
      string[]
    >([]),
    [timeImportRunId, setTimeImportRunId] = useState(""),
    [timeImportError, setTimeImportError] = useState(""),
    [payrollReview, setPayrollReview] = useState<null | {
      run: {
        id: string;
        periodStart: string;
        periodEnd: string;
        payDate: string;
        status: string;
      };
      totals: {
        grossCents: number;
        employeeTaxesCents: number;
        contractorWithholdingCents: number;
        deductionsCents: number;
        netCents: number;
        employerTaxesCents: number;
        totalEmployerCostCents: number;
      };
      items: {
        workerId: string;
        workerName: string;
        grossCents: number;
        employeeTaxesCents: number;
        deductionsCents: number;
        netCents: number;
        employerTaxesCents: number;
        totalEmployerCostCents: number;
        earnings: {
          earningType: string;
          description: string | null;
          hoursHundredths: number | null;
          rateCents: number | null;
          amountCents: number;
        }[];
        taxes: {
          taxKey: string;
          employeeAmountCents: number;
          employerAmountCents: number;
          taxableWagesCents: number;
        }[];
        deductions: {
          deductionType: string;
          description: string;
          amountCents: number;
        }[];
      }[];
      contractors: {
        id: string;
        workerId: string;
        workerName: string;
        description: string;
        hoursHundredths: number | null;
        rateCents: number | null;
        grossCents: number;
        exemptionAppliedCents: number;
        subjectCents: number;
        withholdingRateBps: number;
        withholdingCents: number;
        netCents: number;
        status: string;
      }[];
    }>(null),
    [contractorPreparation, setContractorPreparation] = useState<null | {
      runId: string;
      prepared: {
        workerId: string;
        workerName: string;
        paymentId: string;
        hoursHundredths: number;
        grossCents: number;
        existing: boolean;
      }[];
      issues: { workerId: string; workerName: string; code: string }[];
    }>(null),
    [busy, setBusy] = useState(true),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  type CheckCandidate = {
    workerId: string;
    payee: string;
    amountCents: number;
    workerType: "employee" | "contractor";
  };
  const [checkSelection, setCheckSelection] = useState<{
    run: PayrollRun;
    candidates: CheckCandidate[];
    selectedWorkerIds: string[];
  } | null>(null);
  const setAllCheckCandidates = (selected: boolean) =>
    setCheckSelection((current) =>
      current
        ? {
            ...current,
            selectedWorkerIds: selected
              ? current.candidates.map((candidate) => candidate.workerId)
              : [],
          }
        : null,
    );
  const toggleCheckCandidate = (workerId: string, selected: boolean) =>
    setCheckSelection((current) => {
      if (!current) return null;
      const ids = new Set(current.selectedWorkerIds);
      if (selected) ids.add(workerId);
      else ids.delete(workerId);
      return { ...current, selectedWorkerIds: [...ids] };
    });
  const [payrollAction, setPayrollAction] = useState<PayrollActionDraft | null>(
    null,
  );
  const [diagnosticActionBusyId, setDiagnosticActionBusyId] = useState<
    string | null
  >(null);
  const [bonusActionDraft, setBonusActionDraft] =
    useState<BonusActionDraft | null>(null);
  const [runStatusFilter, setRunStatusFilter] = useState("all");
  const [runSort, setRunSort] = useState<"newest" | "oldest">("newest");
  const [runPage, setRunPage] = useState(0);
  const [runDraftTouched, setRunDraftTouched] = useState(false);
  const [showCompensationReimbursements, setShowCompensationReimbursements] =
    useState(false);
  const [
    compensationReimbursementWorkerId,
    setCompensationReimbursementWorkerId,
  ] = useState("");
  const [showCompensationOtherOptions, setShowCompensationOtherOptions] =
    useState(false);
  const revealCompensationResult = () =>
    window.setTimeout(
      () =>
        document
          .getElementById("compensation-action-result")
          ?.scrollIntoView({ behavior: "smooth", block: "start" }),
      0,
    );
  const [createdPeriod, setCreatedPeriod] = useState<{
    periodStart: string;
    periodEnd: string;
    payDate: string;
  } | null>(null);
  const today = new Date().toISOString().slice(0, 10),
    [run, setRun] = useState({
      runType: "regular",
      periodStart: today,
      periodEnd: today,
      payDate: today,
      payrollFrequency: "biweekly",
      sourceRunId: null as string | null,
      correctionReason: null as string | null,
    });
  const updateRun = (patch: Partial<typeof run>) => {
    setRun((current) => ({ ...current, ...patch }));
    setRunDraftTouched(true);
  };
  const [manualEarnings, setManualEarnings] = useState<
      Record<string, ManualEarningLine[]>
    >({}),
    [rates, setRates] = useState({
      effectiveFrom: today,
      prUnemploymentRateBps: "",
      futaRateBps: "60",
      sinotEmployerPaysEmployeeShare: false,
    });
  const [employerCostForm, setEmployerCostForm] = useState({
    workerId: "",
    costType: "benefit" as "benefit" | "other_burden",
    costKey: "",
    description: "",
    calculationType: "fixed" as "fixed" | "percentage",
    basis: "gross_pay" as "gross_pay" | "taxable_wages",
    amount: "",
    ratePercent: "",
    createsLiability: false,
    liabilityPayee: "",
    effectiveFrom: today,
    effectiveTo: "",
  });
  const [supplementalPolicyForm, setSupplementalPolicyForm] = useState({
    taxYear: today.slice(0, 4),
    reason: "",
  });
  const [recurringBonusForm, setRecurringBonusForm] = useState({
    workerId: "",
    amount: "",
    description: "",
    regularRateTreatment:
      "included_nondiscretionary" as RecurringBonusInstruction["regularRateTreatment"],
    effectiveFrom: today,
    reason: "",
  });
  const [overtimeClassificationForm, setOvertimeClassificationForm] = useState({
      workerId: "",
      effectiveFrom: today,
      effectiveTo: "",
      classification: "nonexempt" as OvertimeClassification["classification"],
      dutiesSummary: "",
      salaryBasisReviewed: false,
      legalSourceUrl:
        "https://www.trabajo.pr.gov/docs/Boletines/Guias_Legislacion_Laboral.pdf",
      reason: "",
    }),
    [overtimeClassificationFile, setOvertimeClassificationFile] =
      useState<File | null>(null);
  const [profileWorker, setProfileWorker] = useState(""),
    [profileSaveFeedback, setProfileSaveFeedback] = useState<null | {
      kind: "success" | "error";
      message: string;
    }>(null),
    [profile, setProfile] = useState({
      effectiveFrom: today,
      rateType: "hourly",
      rate: "",
      salaryFrequency: "biweekly",
      salaryRegularHours: "",
      salaryOvertimeMethod: "fixed_regular_hours",
      overtimeEligible: true,
      personalExemption: "individual",
      veteran: false,
      dependents: "0",
      halfDependents: "0",
      deductionAllowances: "0",
      additionalWithholding: "0",
      militarySpouseExempt: false,
      certifiedByWorker: false,
      formVersion: "499 R-4.1",
      relevoType: "none",
      relevoNumber: "",
      relevoExpiresOn: "",
      statutoryExempt: false,
      payeeType: "individual",
    });
  const [payment, setPayment] = useState({
    workerId: "",
    paymentDate: today,
    periodStart: "",
    periodEnd: "",
    description: "",
    hoursHundredths: "",
    serviceUnits: "",
    rate: "",
    gross: "",
    compensationType: "" as "" | "hourly" | "fixed_fee" | "per_job" | "daily" | "weekly",
  });
  const [deduction, setDeduction] = useState({
    workerId: "",
    deductionType: "other",
    description: "",
    calculationType: "fixed",
    amount: "",
    ratePercent: "",
    originalBalance: "",
    priority: "100",
    maximumPerPayroll: "",
    disposableIncomeLimitPercent: "100",
    protectedAmount: "0",
    aggregateLimitPercent: "50",
    prorationWeight: "",
    referenceNumber: "",
    issuingAuthority: "",
    receivedOn: "",
    caseNumber: "",
    effectiveFrom: today,
    effectiveTo: "",
    status: "active",
  });
  const [deductionFiles, setDeductionFiles] = useState<
      Record<string, File | null>
    >({}),
    [deductionDocumentTypes, setDeductionDocumentTypes] = useState<
      Record<string, string>
    >({});
  const [taxWorker, setTaxWorker] = useState(
      () => sessionStorage.getItem("payroll-tax-worker") ?? "",
    ),
    [taxElections, setTaxElections] = useState<TaxElection[]>([]),
    [taxSubmissions, setTaxSubmissions] = useState<
      Record<string, HaciendaSubmission>
    >({}),
    [taxSubmissionFiles, setTaxSubmissionFiles] = useState<
      Record<string, File | null>
    >({}),
    [taxSubmissionDraft, setTaxSubmissionDraft] =
      useState<TaxSubmissionDraft | null>(null),
    [taxPaperFile, setTaxPaperFile] = useState<File | null>(null),
    [taxForm, setTaxForm] = useState({
      effectiveFrom: today,
      formVersion: "499 R-4.1",
      personalExemption: "individual",
      veteran: false,
      dependents: "0",
      halfDependents: "0",
      deductionAllowances: "0",
      additionalWithholding: "0",
      additionalWithholdingPercent: "0",
      militarySpouseExempt: false,
      spouseName: "",
      spouseTaxpayerId: "",
      postalLine1: "",
      postalCity: "",
      postalCode: "",
      dateOfBirth: "",
      youngWorkerElection: "not_applicable",
      governmentRetirementParticipant: false,
      retirementSupplementPlan: false,
      retirementSavingsProgram: false,
      retirementSavingsPercent: "0",
      certifiedName: "",
      receivedOn: today,
      workerSigned: false,
      developerAttestation: false,
    });
  const [bonusForm, setBonusForm] = useState({
      coverageYear: today.slice(0, 4),
      employeeCount: "1",
      exemptionStatus: "none",
      exemptionReference: "",
    }),
    [bonusAdjustment, setBonusAdjustment] = useState({
      cycleId: "",
      workerId: "",
      hours: "0",
      earnings: "0",
      reason: "",
      dockWorker: false,
    }),
    [bonusFile, setBonusFile] = useState<File | null>(null);
  const earningOptions: {
    code: ManualEarningCode;
    es: string;
    en: string;
    accountable: boolean;
  }[] = [
    {
      code: "regular",
      es: "Salario regular",
      en: "Regular wages",
      accountable: false,
    },
    { code: "overtime", es: "Horas extra", en: "Overtime", accountable: false },
    {
      code: "vacation",
      es: "Vacaciones pagadas",
      en: "Paid vacation",
      accountable: false,
    },
    {
      code: "sick",
      es: "Enfermedad pagada",
      en: "Paid sick leave",
      accountable: false,
    },
    {
      code: "holiday",
      es: "Día feriado",
      en: "Holiday pay",
      accountable: false,
    },
    { code: "bonus", es: "Bono", en: "Bonus", accountable: false },
    {
      code: "recurring_bonus",
      es: "Bono recurrente",
      en: "Recurring bonus",
      accountable: false,
    },
    {
      code: "commission",
      es: "Comisión",
      en: "Commission",
      accountable: false,
    },
    {
      code: "car_allowance",
      es: "Asignación de automóvil",
      en: "Car allowance",
      accountable: true,
    },
    { code: "mileage", es: "Millaje", en: "Mileage", accountable: true },
    {
      code: "expense_reimbursement",
      es: "Reembolso de gastos",
      en: "Expense reimbursement",
      accountable: true,
    },
    {
      code: "per_diem",
      es: "Dieta / per diem",
      en: "Per diem",
      accountable: true,
    },
    {
      code: "other_earnings",
      es: "Otro ingreso salarial",
      en: "Other wage earnings",
      accountable: false,
    },
    {
      code: "other_nonwage_payment",
      es: "Otro pago no salarial",
      en: "Other non-wage payment",
      accountable: true,
    },
  ];
  const addManualEarning = (workerId: string) =>
    setManualEarnings((current) => ({
      ...current,
      [workerId]: [
        ...(current[workerId] ?? []),
        {
          id: crypto.randomUUID(),
          earningCode: "regular",
          amount: "",
          taxTreatment: "taxable_wages",
          description: "",
          regularRateTreatment: "included_nondiscretionary",
          regularRateAllocationStart: run.periodStart,
          regularRateAllocationEnd: run.periodEnd,
          regularRateAllocationMethod: "equal_workweeks",
          regularRateAllocationStrategy: "equal_workweeks",
          regularRateAllocationWeightsJson: "",
        },
      ],
    }));
  const updateManualEarning = (
    workerId: string,
    id: string,
    change: Partial<ManualEarningLine>,
  ) =>
    setManualEarnings((current) => ({
      ...current,
      [workerId]: (current[workerId] ?? []).map((line) => {
        if (line.id !== id) return line;
        const normalized =
          "regularRateAllocationMethod" in change
            ? {
                ...change,
                regularRateAllocationStrategy:
                  change.regularRateAllocationMethod
                    ? ("equal_workweeks" as const)
                    : undefined,
                regularRateAllocationWeightsJson: undefined,
              }
            : change;
        return { ...line, ...normalized };
      }),
    }));
  const removeManualEarning = (workerId: string, id: string) =>
    setManualEarnings((current) => ({
      ...current,
      [workerId]: (current[workerId] ?? []).filter((line) => line.id !== id),
    }));
  const addApprovedClaim = (claim: ExpenseClaim) =>
    setManualEarnings((current) => ({
      ...current,
      [claim.workerId]: [
        ...(current[claim.workerId] ?? []),
        {
          id: crypto.randomUUID(),
          earningCode: claim.category,
          amount: (claim.amountCents / 100).toFixed(2),
          taxTreatment: "accountable_plan_nontaxable",
          description: claim.businessPurpose,
          expenseClaimId: claim.id,
        },
      ],
    }));
  const reviewExpense = (
    item: ExpenseClaim,
    decision: "approved" | "rejected",
  ) => setPayrollAction({ kind: "expense", item, decision, reason: "" });
  const viewExpenseReceipt = async (claim: ExpenseClaim) => {
    const response = await api(
      `/v1/tenants/${tenant.id}/expense-claims/${claim.id}/receipt`,
    );
    if (!response.ok) {
      setError(`expense-receipt:${response.status}`);
      return;
    }
    const url = URL.createObjectURL(await response.blob());
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };
  const load = async () => {
    const [
      w,
      r,
      p,
      d,
      b,
      e,
      c,
      s,
      projectResponse,
      overtimeResponse,
      recurringResponse,
      rateResponse,
    ] = await Promise.all([
      api(`/v1/tenants/${tenant.id}/workers`),
      api(`/v1/tenants/${tenant.id}/payroll-runs`),
      api(`/v1/tenants/${tenant.id}/contractor-payments`),
      api(`/v1/tenants/${tenant.id}/deductions`),
      api(`/v1/tenants/${tenant.id}/christmas-bonus-cycles`),
      api(`/v1/tenants/${tenant.id}/expense-claims`),
      api(`/v1/tenants/${tenant.id}/employer-cost-rules`),
      api(`/v1/tenants/${tenant.id}/supplemental-withholding-policies`),
      api(`/v1/tenants/${tenant.id}/projects`),
      api(`/v1/tenants/${tenant.id}/overtime-classifications`),
      api(`/v1/tenants/${tenant.id}/recurring-bonuses`),
      api(`/v1/tenants/${tenant.id}/employer-rates`),
    ]);
    if (w.ok) setWorkers(((await w.json()) as { workers: Worker[] }).workers);
    if (r.ok) setRuns(((await r.json()) as { runs: PayrollRun[] }).runs);
    if (p.ok)
      setPayments(
        ((await p.json()) as { payments: ContractorPayment[] }).payments,
      );
    if (d.ok)
      setDeductions(
        ((await d.json()) as { deductions: WorkerDeduction[] }).deductions,
      );
    if (b.ok)
      setBonusCycles(((await b.json()) as { cycles: BonusCycle[] }).cycles);
    if (e.ok)
      setExpenseClaims(((await e.json()) as { claims: ExpenseClaim[] }).claims);
    if (c.ok)
      setEmployerCostRules(
        ((await c.json()) as { rules: EmployerCostRule[] }).rules,
      );
    if (s.ok)
      setSupplementalPolicies(
        ((await s.json()) as { policies: SupplementalWithholdingPolicy[] })
          .policies,
      );
    if (projectResponse.ok)
      setPayrollProjects(
        ((await projectResponse.json()) as { projects: Project[] }).projects,
      );
    if (overtimeResponse.ok)
      setOvertimeClassifications(
        (
          (await overtimeResponse.json()) as {
            classifications: OvertimeClassification[];
          }
        ).classifications,
      );
    if (recurringResponse.ok)
      setRecurringBonuses(
        (
          (await recurringResponse.json()) as {
            instructions: RecurringBonusInstruction[];
          }
        ).instructions,
      );
    if (rateResponse.ok) {
      const retained = (
          (await rateResponse.json()) as {
            rates: {
              rateKey: string;
              rateValue: string;
              effectiveFrom: string;
            }[];
          }
        ).rates,
        map = new Map(retained.map((item) => [item.rateKey, item]));
      setRates((current) => ({
        ...current,
        effectiveFrom: retained[0]?.effectiveFrom ?? current.effectiveFrom,
        prUnemploymentRateBps:
          map.get("pr_unemployment_rate_bps")?.rateValue ??
          current.prUnemploymentRateBps,
        futaRateBps: map.get("futa_rate_bps")?.rateValue ?? current.futaRateBps,
        sinotEmployerPaysEmployeeShare:
          map.get("sinot_employer_pays_employee_share")?.rateValue === "1",
      }));
    }
    setBusy(false);
  };
  useEffect(() => {
    void load();
  }, [tenant.id]);
  const setupInitialized = useRef(false);
  useEffect(() => {
    if (
      !setupMode ||
      !setupWorkerId ||
      !workers.length ||
      setupInitialized.current
    )
      return;
    setupInitialized.current = true;
    if (setupMode === "overtime")
      setOvertimeClassificationForm((current) => ({
        ...current,
        workerId: setupWorkerId,
      }));
    else if (setupMode === "pay") void loadPayrollProfile(setupWorkerId);
    else setTaxWorker(setupWorkerId);
  }, [setupMode, setupWorkerId, workers.length]);
  useEffect(() => {
    if (runDraftTouched) return;
    setRun((current) => ({
      ...current,
      ...suggestNextPayrollPeriod(
        runs,
        current.payrollFrequency as PayrollFrequency,
        today,
      ),
    }));
  }, [runs, runDraftTouched, today]);
  useEffect(() => {
    if (!taxWorker) {
      setTaxElections([]);
      return;
    }
    void api(
      `/v1/tenants/${tenant.id}/workers/${taxWorker}/tax-elections`,
    ).then(async (response) => {
      if (response.ok)
        setTaxElections(
          ((await response.json()) as { elections: TaxElection[] }).elections,
        );
    });
  }, [tenant.id, taxWorker]);
  const create = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setCreatedPeriod(null);
    const response = await api(`/v1/tenants/${tenant.id}/payroll-runs`, {
      method: "POST",
      body: JSON.stringify(run),
    });
    if (!response.ok) setError("run");
    else {
      const body = (await response.json()) as { run: PayrollRun };
      setCreatedPeriod(body.run);
      setRunDraftTouched(false);
    }
    await load();
  };
  const saveRates = async () => {
    setBusy(true);
    const response = await api(`/v1/tenants/${tenant.id}/employer-rates`, {
      method: "PUT",
      body: JSON.stringify({
        ...rates,
        prUnemploymentRateBps: Number(rates.prUnemploymentRateBps),
        futaRateBps: Number(rates.futaRateBps),
      }),
    });
    if (!response.ok) setError("rates");
    setBusy(false);
  };
  const createEmployerCostRule = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const response = await api(`/v1/tenants/${tenant.id}/employer-cost-rules`, {
      method: "POST",
      body: JSON.stringify({
        workerId: employerCostForm.workerId || null,
        costType: employerCostForm.costType,
        costKey: employerCostForm.costKey,
        description: employerCostForm.description,
        calculationType: employerCostForm.calculationType,
        basis: employerCostForm.basis,
        amountCents:
          employerCostForm.calculationType === "fixed"
            ? Math.round(Number(employerCostForm.amount) * 100)
            : null,
        rateBps:
          employerCostForm.calculationType === "percentage"
            ? Math.round(Number(employerCostForm.ratePercent) * 100)
            : null,
        createsLiability: employerCostForm.createsLiability,
        liabilityPayee: employerCostForm.createsLiability
          ? employerCostForm.liabilityPayee || null
          : null,
        effectiveFrom: employerCostForm.effectiveFrom,
        effectiveTo: employerCostForm.effectiveTo || null,
      }),
    });
    if (!response.ok) setError(`employer-cost:${response.status}`);
    else
      setEmployerCostForm((current) => ({
        ...current,
        costKey: "",
        description: "",
        amount: "",
        ratePercent: "",
        liabilityPayee: "",
      }));
    await load();
  };
  const closeEmployerCostRule = (item: EmployerCostRule) =>
    setPayrollAction({
      kind: "close_cost",
      item,
      effectiveTo: today,
      reason: "",
    });
  const adoptAnnualizedSupplemental = () => {
    if (supplementalPolicyForm.reason.trim())
      setPayrollAction({
        kind: "adopt_supplemental",
        taxYear: Number(supplementalPolicyForm.taxYear),
        reason: supplementalPolicyForm.reason,
      });
  };
  const createRecurringBonus = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const response = await api(`/v1/tenants/${tenant.id}/recurring-bonuses`, {
      method: "POST",
      body: JSON.stringify({
        ...recurringBonusForm,
        amountCents: Math.round(Number(recurringBonusForm.amount) * 100),
      }),
    });
    if (!response.ok) setError(`recurring-bonus:${response.status}`);
    else
      setRecurringBonusForm((current) => ({
        ...current,
        amount: "",
        description: "",
        reason: "",
      }));
    await load();
  };
  const endRecurringBonus = (item: RecurringBonusInstruction) =>
    setPayrollAction({
      kind: "end_bonus",
      item,
      effectiveTo: today,
      reason: "",
    });
  const createOvertimeClassification = async (event: FormEvent) => {
    event.preventDefault();
    const exempt = overtimeClassificationForm.classification !== "nonexempt";
    if (exempt && !overtimeClassificationFile) return;
    setBusy(true);
    setError("");
    const form = new FormData();
    for (const [key, value] of Object.entries(overtimeClassificationForm))
      form.set(key, String(value));
    if (overtimeClassificationFile)
      form.set("evidence", overtimeClassificationFile);
    const response = await api(
      `/v1/tenants/${tenant.id}/overtime-classifications`,
      { method: "POST", body: form },
    );
    if (!response.ok) setError(`overtime-classification:${response.status}`);
    else {
      setOvertimeClassificationFile(null);
      setOvertimeClassificationForm((current) => ({
        ...current,
        dutiesSummary: "",
        reason: "",
        salaryBasisReviewed: false,
      }));
    }
    await load();
  };
  const loadPayrollProfile = async (workerId: string) => {
    setProfileWorker(workerId);
    setProfileSaveFeedback(null);
    const worker = workers.find((item) => item.id === workerId);
    if (!workerId) {
      setProfile((current) => ({
        ...current,
        rateType: "hourly",
        rate: "",
        salaryRegularHours: "",
      }));
      return;
    }
    setBusy(true);
    setError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/workers/${workerId}/payroll-profile`,
    );
    if (!response.ok) {
      setError(`payroll-profile:${response.status}`);
      setBusy(false);
      return;
    }
    const body = (await response.json()) as {
      rate: {
        effectiveFrom: string;
        rateType: string;
        rateCents: number;
        salaryFrequency: string | null;
        salaryRegularHoursHundredths: number | null;
        salaryOvertimeMethod: string | null;
        overtimeEligible: number;
      } | null;
      election: {
        personalExemption: string;
        veteran: number;
        dependents: number;
        halfDependents: number;
        deductionAllowances: number;
        additionalWithholdingCents: number;
        militarySpouseExempt: number;
        certifiedByWorker: number;
        formVersion: string;
      } | null;
      contractor: {
        relevoType: string;
        relevoNumber: string | null;
        relevoExpiresOn: string | null;
        statutoryExempt: number;
        payeeType: string;
      } | null;
    };
    setProfile((current) => ({
      ...current,
      effectiveFrom: body.rate?.effectiveFrom ?? today,
      rateType:
        body.rate?.rateType ??
        (worker?.workerType === "employee"
          ? "hourly"
          : worker?.workerType === "professional_services"
            ? "fixed_fee"
            : ""),
      rate: body.rate ? (body.rate.rateCents / 100).toFixed(2) : "",
      salaryFrequency: body.rate?.salaryFrequency ?? "biweekly",
      salaryRegularHours: body.rate?.salaryRegularHoursHundredths
        ? (body.rate.salaryRegularHoursHundredths / 100).toFixed(2)
        : "",
      salaryOvertimeMethod:
        body.rate?.salaryOvertimeMethod ?? "fixed_regular_hours",
      overtimeEligible: Boolean(body.rate?.overtimeEligible),
      personalExemption: body.election?.personalExemption ?? "individual",
      veteran: Boolean(body.election?.veteran),
      dependents: String(body.election?.dependents ?? 0),
      halfDependents: String(body.election?.halfDependents ?? 0),
      deductionAllowances: String(body.election?.deductionAllowances ?? 0),
      additionalWithholding: (
        (body.election?.additionalWithholdingCents ?? 0) / 100
      ).toFixed(2),
      militarySpouseExempt: Boolean(body.election?.militarySpouseExempt),
      certifiedByWorker: Boolean(body.election?.certifiedByWorker),
      formVersion: body.election?.formVersion ?? "499 R-4.1",
      relevoType: body.contractor?.relevoType ?? "none",
      relevoNumber: body.contractor?.relevoNumber ?? "",
      relevoExpiresOn: body.contractor?.relevoExpiresOn ?? "",
      statutoryExempt: Boolean(body.contractor?.statutoryExempt),
      payeeType: body.contractor?.payeeType ?? "individual",
    }));
    setBusy(false);
  };
  const downloadOvertimeEvidence = async (item: OvertimeClassification) => {
    const response = await api(
      `/v1/tenants/${tenant.id}/overtime-classifications/${item.id}/evidence`,
    );
    if (!response.ok) {
      setError(`overtime-evidence:${response.status}`);
      return;
    }
    const url = URL.createObjectURL(await response.blob()),
      anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = item.evidenceFileName ?? "classification-evidence";
    anchor.click();
    URL.revokeObjectURL(url);
  };
  const saveProfile = async () => {
    if (!profileWorker) return;
    setBusy(true);
    setError("");
    setProfileSaveFeedback(null);
    const selected = workers.find((w) => w.id === profileWorker)!;
    const response = await api(
      `/v1/tenants/${tenant.id}/workers/${profileWorker}/payroll-profile`,
      {
        method: "PUT",
        body: JSON.stringify({
          effectiveFrom: profile.effectiveFrom,
          rateType: profile.rateType,
          rateCents: Math.round(Number(profile.rate) * 100),
          salaryFrequency:
            profile.rateType === "salary" ? profile.salaryFrequency : null,
          salaryRegularHoursHundredths:
            profile.rateType === "salary"
              ? Math.round(Number(profile.salaryRegularHours) * 100)
              : null,
          salaryOvertimeMethod:
            profile.rateType === "salary" ? profile.salaryOvertimeMethod : null,
          overtimeEligible: profile.overtimeEligible,
          personalExemption:
            selected.workerType === "employee"
              ? profile.personalExemption
              : null,
          veteran: profile.veteran,
          dependents: Number(profile.dependents),
          halfDependents: Number(profile.halfDependents),
          deductionAllowances: Number(profile.deductionAllowances),
          additionalWithholdingCents: Math.round(
            Number(profile.additionalWithholding) * 100,
          ),
          militarySpouseExempt: profile.militarySpouseExempt,
          certifiedByWorker: profile.certifiedByWorker,
          formVersion: profile.formVersion,
          relevoType: profile.relevoType,
          relevoNumber: profile.relevoNumber || null,
          relevoExpiresOn: profile.relevoExpiresOn || null,
          statutoryExempt: profile.statutoryExempt,
          payeeType: profile.payeeType,
        }),
      },
    );
    if (!response.ok) {
      setError(`profile:${response.status}`);
      setProfileSaveFeedback({
        kind: "error",
        message:
          locale === "es"
            ? `No se pudo guardar el perfil (error ${response.status}). Revise los datos e intente nuevamente.`
            : `The profile could not be saved (error ${response.status}). Review the information and try again.`,
      });
      setBusy(false);
      return;
    }
    await loadPayrollProfile(profileWorker);
    setProfileSaveFeedback({
      kind: "success",
      message:
        locale === "es"
          ? `Perfil de pago de ${selected.firstName} ${selected.lastName ?? ""} guardado correctamente.`
          : `${selected.firstName} ${selected.lastName ?? ""}'s pay profile was saved successfully.`,
    });
    setBusy(false);
  };
  const action = async (
    item: PayrollRun,
    verb: "diagnose" | "calculate" | "approve" | "reject" | "finalize",
  ) => {
    if (verb === "approve" || verb === "reject") {
      setPayrollAction({
        kind: "run_decision",
        item: {
          ...item,
          description: `${formatDateRange(item.periodStart, item.periodEnd, locale, "—")} · ${formatDate(item.payDate, locale)}`,
        },
        verb,
        reason: "",
      });
      return;
    }
    setBusy(true);
    setError("");
    const init: RequestInit = { method: "POST" };
    if (verb === "calculate") {
      const combined = new Map<
        string,
        {
          workerId: string;
          finalPayPeriodId?: string;
          earnings: Record<string, unknown>[];
        }
      >();
      if (preparedTime?.runId === item.id)
        for (const prepared of preparedTime.items)
          combined.set(prepared.workerId, {
            workerId: prepared.workerId,
            earnings: [...prepared.earnings],
          });
      if (preparedFinalPay?.runId === item.id)
        for (const prepared of preparedFinalPay.items) {
          const existing = combined.get(prepared.workerId) ?? {
            workerId: prepared.workerId,
            earnings: [],
          };
          existing.finalPayPeriodId = prepared.finalPayPeriodId;
          existing.earnings.push(...prepared.earnings);
          combined.set(prepared.workerId, existing);
        }
      for (const worker of workers.filter(
        (worker) => worker.workerType === "employee",
      ))
        for (const line of manualEarnings[worker.id] ?? []) {
          const amountCents = Math.round(Number(line.amount) * 100);
          if (!amountCents) continue;
          let regularRateAllocationWeights:
            | { from: string; to: string; earningsCents: number }[]
            | undefined;
          if (
            line.regularRateAllocationStrategy ===
            "proportional_weekly_earnings"
          )
            try {
              const parsed = JSON.parse(
                line.regularRateAllocationWeightsJson ?? "",
              ) as { from?: unknown; to?: unknown; earnings?: unknown }[];
              if (!Array.isArray(parsed) || !parsed.length) throw new Error();
              regularRateAllocationWeights = parsed.map((item) => {
                const earningsCents = Math.round(Number(item.earnings) * 100);
                if (
                  typeof item.from !== "string" ||
                  typeof item.to !== "string" ||
                  !/^\d{4}-\d{2}-\d{2}$/.test(item.from) ||
                  !/^\d{4}-\d{2}-\d{2}$/.test(item.to) ||
                  item.to < item.from ||
                  !Number.isInteger(earningsCents) ||
                  earningsCents < 0
                )
                  throw new Error();
                return { from: item.from, to: item.to, earningsCents };
              });
            } catch {
              setError(
                locale === "es"
                  ? "Revise el JSON de ingresos semanales proporcionales."
                  : "Review the proportional weekly-earnings JSON.",
              );
              setBusy(false);
              return;
            }
          const existing = combined.get(worker.id) ?? {
              workerId: worker.id,
              earnings: [],
            },
            accountable = line.taxTreatment === "accountable_plan_nontaxable",
            earningType =
              line.earningCode === "regular" ||
              line.earningCode === "overtime" ||
              line.earningCode === "vacation" ||
              line.earningCode === "sick" ||
              line.earningCode === "holiday" ||
              line.earningCode === "bonus" ||
              line.earningCode === "commission"
                ? line.earningCode
                : line.earningCode === "recurring_bonus"
                  ? "bonus"
                  : accountable
                    ? line.earningCode === "other_nonwage_payment"
                      ? "nontaxable_other"
                      : "reimbursement"
                    : "taxable_other";
          existing.earnings.push({
            earningType,
            earningCode: line.earningCode,
            taxTreatment: line.taxTreatment,
            description: line.description || undefined,
            expenseClaimId: line.expenseClaimId,
            projectId: line.projectId || undefined,
            amountCents,
            hoursHundredths: null,
            rateCents: null,
            sourceRuleVersion: accountable
              ? "IRS-PUB-15-2026-ACCOUNTABLE-PLAN"
              : "MANUAL-TAXABLE-WAGES",
            regularRateTreatment: line.regularRateTreatment,
            regularRateAllocationStart:
              line.regularRateTreatment === "included_nondiscretionary"
                ? line.regularRateAllocationStart
                : undefined,
            regularRateAllocationEnd:
              line.regularRateTreatment === "included_nondiscretionary"
                ? line.regularRateAllocationEnd
                : undefined,
            regularRateAllocationMethod:
              line.regularRateTreatment === "included_nondiscretionary"
                ? line.regularRateAllocationMethod
                : undefined,
            regularRateAllocationStrategy:
              line.regularRateTreatment === "included_nondiscretionary"
                ? line.regularRateAllocationStrategy
                : undefined,
            regularRateAllocationWeights,
          });
          combined.set(worker.id, existing);
        }
      init.body = JSON.stringify({ items: [...combined.values()] });
    }
    const response = await api(
      `/v1/tenants/${tenant.id}/payroll-runs/${item.id}/${verb}`,
      init,
    );
    if (!response.ok) {
      const code = await apiErrorCode(response, verb);
      const messages: Record<string, [string, string]> = {
        regular_rate_treatment_required: [
          "Clasifique si el pago adicional se incluye en la tarifa regular.",
          "Classify whether the additional payment is included in the regular rate.",
        ],
        manual_statutory_exclusion_forbidden: [
          "La exclusión estatutaria seleccionada no corresponde a este concepto.",
          "The selected statutory exclusion is not valid for this earning.",
        ],
        invalid_regular_rate_coverage: [
          "Revise las fechas del período en que se ganó el pago; no pueden ser futuras ni exceder el límite permitido.",
          "Review the earned-period dates; they cannot be future dates or exceed the supported limit.",
        ],
        regular_rate_labor_evaluation_failed: [
          "No se pudo evaluar la jornada del período ganado. Corrija la configuración de tiempo y vuelva a intentar.",
          "The earned-period labor evaluation failed. Correct the time configuration and retry.",
        ],
        regular_rate_time_incomplete: [
          "El período ganado contiene eventos de tiempo incompletos o una clasificación de horas extra pendiente.",
          "The earned period contains incomplete time events or a pending overtime classification.",
        ],
        regular_rate_timesheet_not_locked: [
          "Bloquee la hoja de tiempo del período ganado antes de calcular el ajuste de tarifa regular.",
          "Lock the earned-period timesheet before calculating the regular-rate true-up.",
        ],
      };
      const message = messages[code];
      setError(
        message
          ? locale === "es"
            ? message[0]
            : message[1]
          : `${code}:${response.status}`,
      );
    } else if (verb === "diagnose") {
      const body = (await response.json()) as {
        issues: {
          id: string;
          workerId: string | null;
          code: string;
          severity: string;
          status: string;
          messageEs: string;
          messageEn: string;
        }[];
      };
      setDiagnosticRunId(item.id);
      setDiagnosticIssues(body.issues);
      setNotice(
        locale === "es"
          ? `Diagnóstico completado: ${body.issues.length} asuntos para revisar.`
          : `Diagnostics completed: ${body.issues.length} items to review.`,
      );
    } else {
      setNotice(
        verb === "calculate"
          ? locale === "es"
            ? "El período fue calculado. Use Revisar cálculo para confirmar cada persona."
            : "The period was calculated. Use Review calculation to confirm each person."
          : locale === "es"
            ? "La acción del período fue completada."
            : "The period action was completed.",
      );
    }
    await load();
    revealCompensationResult();
  };
  const actOnDiagnostic = (
    issue: { id: string },
    action: "explain" | "acknowledge" | "approve_exception",
  ) => {
    if (diagnosticRunId)
      setPayrollAction({
        kind: "diagnostic",
        issueId: issue.id,
        action,
        reason: "",
      });
  };
  const acceptDiagnosticAsIs = async (issue: {
    id: string;
    severity: string;
  }) => {
    if (!diagnosticRunId) return;
    setDiagnosticActionBusyId(issue.id);
    setError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/payroll-runs/${diagnosticRunId}/diagnostics/${issue.id}/action`,
      {
        method: "POST",
        body: JSON.stringify({
          action:
            issue.severity === "error" ? "approve_exception" : "acknowledge",
          reason: "Aceptado como está desde la revisión del diagnóstico",
        }),
      },
    );
    if (!response.ok) {
      setError(`diagnostic:${response.status}`);
      setDiagnosticActionBusyId(null);
      return;
    }
    const list = await api(
      `/v1/tenants/${tenant.id}/payroll-runs/${diagnosticRunId}/diagnostics`,
    );
    if (list.ok)
      setDiagnosticIssues(
        ((await list.json()) as { diagnostics: typeof diagnosticIssues })
          .diagnostics,
      );
    setNotice(
      locale === "es"
        ? "El aviso fue aceptado como está y quedó registrado en la auditoría."
        : "The notice was accepted as is and recorded in the audit trail.",
    );
    setDiagnosticActionBusyId(null);
  };
  const fixDiagnostic = (issue: { code: string; workerId: string | null }) => {
    const base = issue.code.split(":")[0]!;
    if (base === "labor_overtime_detected") {
      const target = document.getElementById("prepared-earnings-result");
      if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
      else
        setNotice(
          locale === "es"
            ? "Use Cargar tiempo aprobado en este período para abrir las líneas de ingreso y revisar las horas extra detalladas."
            : "Use Load approved time for this period to open the earning lines and review the overtime detail.",
        );
      return;
    }
    if (base === "professional_services_threshold_issue") {
      const target = document.getElementById("professional-services-result");
      if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
      else
        setNotice(
          locale === "es"
            ? "Use Preparar servicios profesionales en este período para abrir el pago, su base sujeta y la retención."
            : "Use Prepare professional services for this period to open the payment, subject amount, and withholding.",
        );
      return;
    }
    const time = new Set([
        "missing_clock_in",
        "missing_clock_out",
        "duplicate_punch",
        "unclassified_overtime",
        "excessive_shift",
        "zero_hours",
        "missing_meal",
        "invalid_meal",
        "early_punch",
        "late_punch",
        "early_departure",
        "out_of_window_punch",
        "scheduled_day_no_time",
        "overlapping_shift",
        "missing_supervisor_approval",
        "offline_punch_anomaly",
        "unsynchronized_punches",
        "locked_period_modification",
        "incomplete_time_events",
        "second_meal_review",
        "alternate_workday_review",
      ]),
      leave = new Set([
        "vacation_insufficient",
        "sick_insufficient",
        "holiday_conflict",
      ]),
      personnel = new Set([
        "missing_tax_setup",
        "missing_withholding_certificate",
        "missing_taxpayer_identifier",
      ]);
    if (leave.has(base) && issue.workerId)
      sessionStorage.setItem("leave-diagnostic-worker", issue.workerId);
    navigate(
      time.has(base)
        ? "time"
        : leave.has(base)
          ? "leave"
          : personnel.has(base)
            ? "personnel"
            : "payroll",
    );
  };
  const loadApprovedTime = async (item: PayrollRun) => {
    setBusy(true);
    setError("");
    setTimeImportIssues([]);
    setTimeImportExcludedWorkerIds([]);
    setTimeImportRunId(item.id);
    setTimeImportError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/payroll-runs/${item.id}/time-earnings`,
    );
    if (!response.ok) {
      setTimeImportError(
        locale === "es"
          ? `No se pudo revisar el tiempo aprobado (referencia ${response.status}).`
          : `Approved time could not be reviewed (reference ${response.status}).`,
      );
      setPreparedTime(null);
    } else {
      const body = (await response.json()) as {
        ready: boolean;
        items: {
          workerId: string;
          earnings: {
            earningType:
              | "regular"
              | "overtime"
              | "vacation"
              | "sick"
              | "holiday";
            amountCents: number;
            hoursHundredths: number | null;
            rateCents: number | null;
            sourceTimesheetId?: string;
            sourceLeaveRequestId?: string;
            sourceHolidayId?: string;
            sourceRuleVersion: string;
          }[];
        }[];
        issues: {
          workerId: string | null;
          timesheetId: string | null;
          code: string;
        }[];
        excludedWorkerIds: string[];
      };
      setTimeImportIssues(body.issues);
      setTimeImportExcludedWorkerIds(body.excludedWorkerIds ?? []);
      setPreparedTime(
        body.ready ? { runId: item.id, items: body.items } : null,
      );
    }
    setBusy(false);
    revealCompensationResult();
  };
  const ignoreWorkerForCompensation = async (
    item: PayrollRun,
    workerId: string,
  ) => {
    setBusy(true);
    setTimeImportError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/payroll-runs/${item.id}/excluded-workers`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          workerId,
          reason:
            "No compensation is due to this person for the selected period.",
        }),
      },
    );
    if (!response.ok) {
      setTimeImportError(
        locale === "es"
          ? `No se pudo excluir a la persona de esta compensación (referencia ${response.status}).`
          : `The person could not be excluded from this compensation (reference ${response.status}).`,
      );
      setBusy(false);
      return;
    }
    await loadApprovedTime(item);
  };
  const loadFinalPay = async (item: PayrollRun) => {
    setBusy(true);
    setError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/payroll-runs/${item.id}/final-pay`,
    );
    if (!response.ok) {
      setError(`final-pay:${response.status}`);
      setPreparedFinalPay(null);
    } else {
      const body = (await response.json()) as NonNullable<
        typeof preparedFinalPay
      >;
      setPreparedFinalPay(body);
      if (!body.plans.length)
        setError(
          locale === "es"
            ? "No hay pagos finales pendientes elegibles para esta fecha."
            : "No pending final pay is eligible on this date.",
        );
    }
    setBusy(false);
  };
  const loadContractorCompensation = async (
    workerId: string,
    paymentDate: string,
  ) => {
    setError("");
    setPayment((current) => ({
      ...current,
      workerId,
      paymentDate,
      compensationType: "",
      hoursHundredths: "",
      serviceUnits: "",
      rate: "",
      gross: "",
    }));
    if (!workerId) return;
    const response = await api(
      `/v1/tenants/${tenant.id}/workers/${workerId}/payroll-profile`,
    );
    if (!response.ok) {
      setError(`contractor-rate:${response.status}`);
      return;
    }
    const { rate } = (await response.json()) as {
      rate: {
        rateType: "hourly" | "fixed_fee" | "per_job" | "daily" | "weekly";
        rateCents: number;
        effectiveFrom: string;
      } | null;
    };
    if (!rate || rate.effectiveFrom > paymentDate) {
      setError(
        locale === "es"
          ? "Falta una compensación vigente para la fecha de pago."
          : "An effective compensation rate is missing for the payment date.",
      );
      return;
    }
    setPayment((current) =>
      current.workerId === workerId && current.paymentDate === paymentDate
        ? {
            ...current,
            compensationType: rate.rateType,
            rate: (rate.rateCents / 100).toFixed(2),
            gross:
              rate.rateType === "hourly" || ["daily", "weekly"].includes(rate.rateType)
                ? ""
                : (rate.rateCents / 100).toFixed(2),
          }
        : current,
    );
  };
  const createPayment = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const body = {
      workerId: payment.workerId,
      paymentDate: payment.paymentDate,
      periodStart: payment.periodStart || null,
      periodEnd: payment.periodEnd || null,
      description: payment.description,
      hoursHundredths: payment.hoursHundredths
        ? Math.round(Number(payment.hoursHundredths) * 100)
        : null,
      serviceUnits: payment.serviceUnits ? Number(payment.serviceUnits) : null,
      rateCents: payment.rate ? Math.round(Number(payment.rate) * 100) : null,
      grossCents: Math.round(Number(payment.gross) * 100),
    };
    const response = await api(`/v1/tenants/${tenant.id}/contractor-payments`, {
      method: "POST",
      body: JSON.stringify(body),
    });
    if (!response.ok) setError(`contractor:${response.status}`);
    else
      setPayment({
        ...payment,
        description: "",
        hoursHundredths: "",
        serviceUnits: "",
        gross:
          payment.compensationType === "hourly" ||
          ["daily", "weekly"].includes(payment.compensationType)
            ? ""
            : payment.gross,
      });
    await load();
  };
  const paymentStatus = async (
    item: ContractorPayment,
    status: "approved" | "paid" | "voided",
  ) => {
    setBusy(true);
    setError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/contractor-payments/${item.id}/status`,
      { method: "POST", body: JSON.stringify({ status }) },
    );
    if (!response.ok) setError(`contractor-${status}:${response.status}`);
    await load();
  };
  const downloadDocument = async (documentId: string, fileName: string) => {
    const response = await api(
      `/v1/tenants/${tenant.id}/documents/${documentId}`,
    );
    if (!response.ok) {
      setError(`document:${response.status}`);
      return;
    }
    const url = URL.createObjectURL(await response.blob()),
      anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);
  };
  const generatePayStubs = async (item: PayrollRun) => {
    setBusy(true);
    setError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/payroll-runs/${item.id}/pay-stubs`,
      {
        method: "POST",
        body: JSON.stringify({ locale: locale === "es" ? "es-PR" : "en-PR" }),
      },
    );
    if (!response.ok) setError(`pay-stubs:${response.status}`);
    else {
      const body = (await response.json()) as { documentId: string };
      await downloadDocument(
        body.documentId,
        `comprobantes-${item.periodStart}-${item.periodEnd}.pdf`,
      );
      setNotice(
        locale === "es"
          ? "El PDF conjunto de comprobantes fue regenerado con los datos vigentes. La nómina finalizada no fue alterada."
          : "The combined pay-statement PDF was regenerated with current data. The finalized payroll was not changed.",
      );
    }
    setBusy(false);
    revealCompensationResult();
  };
  const emailPayStubs = async (item: PayrollRun) => {
    setBusy(true);
    setError("");
    setNotice("");
    const response = await api(
      `/v1/tenants/${tenant.id}/payroll-runs/${item.id}/email-pay-stubs`,
      {
        method: "POST",
        body: JSON.stringify({ locale: locale === "es" ? "es-PR" : "en-PR" }),
      },
    );
    if (!response.ok) setError(`email-pay-stubs:${response.status}`);
    else {
      const result = (await response.json()) as {
        sentCount: number;
        failedCount: number;
        skippedCount: number;
      };
      setNotice(
        locale === "es"
          ? `Comprobantes enviados: ${result.sentCount}. Fallidos: ${result.failedCount}. Sin correo: ${result.skippedCount}.`
          : `Pay stubs sent: ${result.sentCount}. Failed: ${result.failedCount}. Missing email: ${result.skippedCount}.`,
      );
    }
    setBusy(false);
    revealCompensationResult();
  };
  const createChecks = async (item: PayrollRun) => {
    setBusy(true);
    setError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/payroll-runs/${item.id}/check-candidates`,
    );
    if (!response.ok) setError(`check-candidates:${response.status}`);
    else {
      const body = (await response.json()) as { candidates: CheckCandidate[] };
      setCheckSelection({
        run: item,
        candidates: body.candidates,
        selectedWorkerIds: body.candidates.map(
          (candidate) => candidate.workerId,
        ),
      });
    }
    setBusy(false);
    revealCompensationResult();
  };
  const confirmChecks = async () => {
    if (!checkSelection || !checkSelection.selectedWorkerIds.length) return;
    const item = checkSelection.run;
    setBusy(true);
    setError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/payroll-runs/${item.id}/checks`,
      {
        method: "POST",
        body: JSON.stringify({
          selectedWorkerIds: checkSelection.selectedWorkerIds,
        }),
      },
    );
    if (!response.ok) setError(`checks:${response.status}`);
    else {
      const body = (await response.json()) as { batchId: string };
      const pdf = await api(
        `/v1/tenants/${tenant.id}/check-batches/${body.batchId}/pdf`,
      );
      if (pdf.ok) {
        const url = URL.createObjectURL(await pdf.blob()),
          anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = `cheques-${item.periodStart}-${item.periodEnd}.pdf`;
        anchor.click();
        URL.revokeObjectURL(url);
        setNotice(
          locale === "es"
            ? "Los cheques fueron creados y el PDF conjunto fue generado."
            : "Checks were created and the combined PDF was generated.",
        );
      }
      setCheckSelection(null);
    }
    setBusy(false);
    revealCompensationResult();
  };
  const generateContractorStatement = async (item: ContractorPayment) => {
    setBusy(true);
    setError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/contractor-payments/${item.id}/statement`,
      {
        method: "POST",
        body: JSON.stringify({ locale: locale === "es" ? "es-PR" : "en-PR" }),
      },
    );
    if (!response.ok) setError(`contractor-statement:${response.status}`);
    else {
      const body = (await response.json()) as { documentId: string };
      await downloadDocument(
        body.documentId,
        `contractor-statement-${item.paymentDate}.pdf`,
      );
    }
    setBusy(false);
  };
  const contractorPaymentActions = (item: ContractorPayment) => (
    <div className="run-actions">
      {item.status === "draft" && (
        <button
          type="button"
          onClick={() => void paymentStatus(item, "approved")}
        >
          {locale === "es" ? "Aprobar" : "Approve"}
        </button>
      )}
      {item.status === "approved" && (
        <button type="button" onClick={() => void paymentStatus(item, "paid")}>
          {locale === "es" ? "Marcar pagado" : "Mark paid"}
        </button>
      )}
      {item.status === "paid" && (
        <button
          type="button"
          onClick={() => void generateContractorStatement(item)}
        >
          {locale === "es" ? "Estado PDF" : "PDF statement"}
        </button>
      )}
      {["draft", "approved"].includes(item.status) && (
        <button
          type="button"
          className="danger-action"
          onClick={() => void paymentStatus(item, "voided")}
        >
          {locale === "es" ? "Anular" : "Void"}
        </button>
      )}
    </div>
  );
  const createDeduction = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const cents = (v: string) =>
      v === "" ? null : Math.round(Number(v) * 100);
    const response = await api(`/v1/tenants/${tenant.id}/deductions`, {
      method: "POST",
      body: JSON.stringify({
        workerId: deduction.workerId,
        deductionType: deduction.deductionType,
        description: deduction.description,
        calculationType: deduction.calculationType,
        amountCents: cents(deduction.amount),
        rateBps:
          deduction.ratePercent === ""
            ? null
            : Math.round(Number(deduction.ratePercent) * 100),
        originalBalanceCents: cents(deduction.originalBalance),
        priority: Number(deduction.priority),
        maximumPerPayrollCents: cents(deduction.maximumPerPayroll),
        disposableIncomeLimitBps: Math.round(
          Number(deduction.disposableIncomeLimitPercent) * 100,
        ),
        protectedAmountCents: cents(deduction.protectedAmount) ?? 0,
        aggregateLimitBps:
          deduction.deductionType === "asume"
            ? Math.round(Number(deduction.aggregateLimitPercent) * 100)
            : null,
        prorationWeightCents:
          deduction.deductionType === "asume"
            ? cents(deduction.prorationWeight)
            : null,
        referenceNumber: deduction.referenceNumber || null,
        issuingAuthority: deduction.issuingAuthority || null,
        receivedOn: deduction.receivedOn || null,
        caseNumber: deduction.caseNumber || null,
        effectiveFrom: deduction.effectiveFrom,
        effectiveTo: deduction.effectiveTo || null,
        status: deduction.status,
      }),
    });
    if (!response.ok) setError(`deduction:${response.status}`);
    else
      setDeduction({
        ...deduction,
        description: "",
        amount: "",
        ratePercent: "",
        originalBalance: "",
        maximumPerPayroll: "",
        prorationWeight: "",
        referenceNumber: "",
        issuingAuthority: "",
        receivedOn: "",
        caseNumber: "",
      });
    await load();
  };
  const deductionStatus = (
    item: WorkerDeduction,
    status: "active" | "suspended" | "completed",
  ) => setPayrollAction({ kind: "deduction_status", item, status, reason: "" });
  const loadDeductionEvidence = async (item: WorkerDeduction) => {
    setBusy(true);
    setError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/deductions/${item.id}/documents`,
    );
    if (response.ok)
      setDeductionEvidence({
        ...deductionEvidence,
        [item.id]: (await response.json()) as {
          documents: DeductionDocument[];
          balanceEvents: DeductionBalanceEvent[];
        },
      });
    else setError(`deduction-evidence:${response.status}`);
    setBusy(false);
  };
  const uploadDeductionEvidence = async (item: WorkerDeduction) => {
    const file = deductionFiles[item.id];
    if (!file) return;
    setBusy(true);
    setError("");
    const form = new FormData();
    form.set("file", file);
    form.set("documentType", deductionDocumentTypes[item.id] ?? "order");
    const response = await api(
      `/v1/tenants/${tenant.id}/deductions/${item.id}/documents`,
      { method: "POST", body: form },
    );
    if (!response.ok) setError(`deduction-document:${response.status}`);
    else {
      setDeductionFiles({ ...deductionFiles, [item.id]: null });
      await loadDeductionEvidence(item);
    }
    setBusy(false);
  };
  const downloadDeductionEvidence = async (
    item: WorkerDeduction,
    doc: DeductionDocument,
  ) => {
    const response = await api(
      `/v1/tenants/${tenant.id}/deductions/${item.id}/documents/${doc.id}`,
    );
    if (!response.ok) {
      setError(`deduction-document:${response.status}`);
      return;
    }
    const url = URL.createObjectURL(await response.blob()),
      anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = doc.fileName;
    anchor.click();
    URL.revokeObjectURL(url);
  };
  const adjustDeduction = (item: WorkerDeduction) =>
    setPayrollAction({
      kind: "deduction_balance",
      item,
      amount: "",
      reason: "",
    });
  const submitPayrollAction = async (ev: FormEvent) => {
    ev.preventDefault();
    const action = payrollAction;
    if (!action) return;
    setBusy(true);
    setError("");
    let path: string, body: unknown;
    if (action.kind === "run_decision") {
      path = `payroll-runs/${action.item.id}/${action.verb}`;
      body = { reason: action.reason };
    } else if (action.kind === "expense") {
      path = `expense-claims/${action.item.id}/review`;
      body = { decision: action.decision, comment: action.reason };
    } else if (action.kind === "close_cost") {
      path = `employer-cost-rules/${action.item.id}/close`;
      body = { effectiveTo: action.effectiveTo, reason: action.reason };
    } else if (action.kind === "adopt_supplemental") {
      path = "supplemental-withholding-policies";
      body = { taxYear: action.taxYear, reason: action.reason };
    } else if (action.kind === "end_bonus") {
      path = `recurring-bonuses/${action.item.id}/end`;
      body = { effectiveTo: action.effectiveTo, reason: action.reason };
    } else if (action.kind === "diagnostic") {
      if (!diagnosticRunId) {
        setBusy(false);
        return;
      }
      path = `payroll-runs/${diagnosticRunId}/diagnostics/${action.issueId}/action`;
      body = { action: action.action, reason: action.reason };
    } else if (action.kind === "deduction_status") {
      path = `deductions/${action.item.id}/status`;
      body = { status: action.status, reason: action.reason };
    } else {
      const amountCents = Math.round(Number(action.amount) * 100);
      if (!Number.isFinite(amountCents) || amountCents === 0) {
        setError(
          locale === "es"
            ? "El ajuste debe ser distinto de cero."
            : "The adjustment must be non-zero.",
        );
        setBusy(false);
        return;
      }
      path = `deductions/${action.item.id}/balance`;
      body = { amountCents, reason: action.reason };
    }
    const response = await api(`/v1/tenants/${tenant.id}/${path}`, {
      method: "POST",
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const code = await apiErrorCode(response, action.kind);
      setError(
        code === "independent_payroll_approval_required" ||
          code === "independent_payroll_review_required"
          ? locale === "es"
            ? "Esta nómina requiere revisión por una cuenta distinta de quien la calculó. En desarrollo, ingrese con el acceso de desarrollador autorizado."
            : "This payroll requires review by an account other than the calculator. In development, sign in with the authorized developer access."
          : `${action.kind}:${response.status}`,
      );
      setBusy(false);
      return;
    }
    setPayrollAction(null);
    if (action.kind === "adopt_supplemental")
      setSupplementalPolicyForm((current) => ({ ...current, reason: "" }));
    if (action.kind === "diagnostic") {
      const list = await api(
        `/v1/tenants/${tenant.id}/payroll-runs/${diagnosticRunId}/diagnostics`,
      );
      if (list.ok)
        setDiagnosticIssues(
          ((await list.json()) as { diagnostics: typeof diagnosticIssues })
            .diagnostics,
        );
    }
    if (action.kind === "deduction_balance")
      await loadDeductionEvidence(action.item);
    await load();
    if (action.kind === "run_decision") {
      setNotice(
        locale === "es"
          ? `La nómina fue ${action.verb === "approve" ? "aprobada" : "devuelta para corrección"}.`
          : `Payroll was ${action.verb === "approve" ? "approved" : "returned for correction"}.`,
      );
      revealCompensationResult();
    }
  };
  const saveTaxElection = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/workers/${taxWorker}/tax-elections/signed-paper`,
      {
        method: "POST",
        body: JSON.stringify({
          effectiveFrom: taxForm.effectiveFrom,
          formVersion: taxForm.formVersion,
          personalExemption: taxForm.personalExemption,
          veteran: taxForm.veteran,
          dependents: Number(taxForm.dependents),
          halfDependents: Number(taxForm.halfDependents),
          deductionAllowances: Number(taxForm.deductionAllowances),
          additionalWithholdingCents: Math.round(
            Number(taxForm.additionalWithholding) * 100,
          ),
          additionalWithholdingBps: Math.round(
            Number(taxForm.additionalWithholdingPercent) * 100,
          ),
          militarySpouseExempt: taxForm.militarySpouseExempt,
          spouseName: taxForm.spouseName || null,
          spouseTaxpayerId: taxForm.spouseTaxpayerId || null,
          postalAddress: taxForm.postalLine1
            ? {
                line1: taxForm.postalLine1,
                line2: null,
                city: taxForm.postalCity,
                region: "PR",
                postalCode: taxForm.postalCode,
                country: "PR",
              }
            : null,
          dateOfBirth: taxForm.dateOfBirth || null,
          youngWorkerElection: taxForm.youngWorkerElection,
          governmentRetirementParticipant:
            taxForm.governmentRetirementParticipant,
          retirementSupplementPlan: taxForm.retirementSupplementPlan,
          retirementSavingsProgram: taxForm.retirementSavingsProgram,
          retirementSavingsBps: Math.round(
            Number(taxForm.retirementSavingsPercent) * 100,
          ),
          certifiedName: taxForm.certifiedName,
          receivedOn: taxForm.receivedOn,
          workerSigned: taxForm.workerSigned,
          developerAttestation: taxForm.developerAttestation,
        }),
      },
    );
    if (!response.ok) setError(`tax-election:${response.status}`);
    else {
      if (taxPaperFile) {
        const documentBody = new FormData();
        documentBody.set("file", taxPaperFile);
        documentBody.set("category", "tax");
        documentBody.set("title", `499 R-4 firmado · ${taxForm.effectiveFrom}`);
        documentBody.set("visibility", "employee_visible");
        documentBody.set("effectiveOn", taxForm.effectiveFrom);
        documentBody.set("expiresOn", "");
        const upload = await api(
          `/v1/tenants/${tenant.id}/workers/${taxWorker}/personnel-documents`,
          { method: "POST", body: documentBody },
        );
        if (!upload.ok)
          setError(
            locale === "es"
              ? "La elección se guardó, pero no se pudo cargar la copia opcional."
              : "The election was saved, but the optional copy could not be uploaded.",
          );
        else setTaxPaperFile(null);
      }
      const history = await api(
        `/v1/tenants/${tenant.id}/workers/${taxWorker}/tax-elections`,
      );
      if (history.ok)
        setTaxElections(
          ((await history.json()) as { elections: TaxElection[] }).elections,
        );
      setTaxForm({
        ...taxForm,
        spouseTaxpayerId: "",
        workerSigned: false,
        developerAttestation: false,
      });
    }
    setBusy(false);
  };
  const generateTaxCertificate = async (item: TaxElection) => {
    setBusy(true);
    setError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/workers/${taxWorker}/tax-elections/${item.id}/certificate`,
      { method: "POST" },
    );
    if (response.ok) {
      const body = (await response.json()) as { documentId: string };
      setTaxElections((current) =>
        current.map((row) =>
          row.id === item.id
            ? { ...row, certificateDocumentId: body.documentId }
            : row,
        ),
      );
    } else setError(`tax-certificate:${response.status}`);
    setBusy(false);
  };
  const loadTaxSubmission = async (item: TaxElection) => {
    setBusy(true);
    setError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/workers/${taxWorker}/tax-elections/${item.id}/hacienda-submission`,
    );
    if (response.ok) {
      const parsed = (await response.json()) as HaciendaSubmission;
      setTaxSubmissions((current) => ({ ...current, [item.id]: parsed }));
    } else setError(`hacienda-submission:${response.status}`);
    setBusy(false);
  };
  const uploadTaxSubmission = async (item: TaxElection) => {
    const file = taxSubmissionFiles[item.id];
    if (!file) return;
    setBusy(true);
    const form = new FormData();
    form.set("file", file);
    form.set("documentType", "form_copy");
    const response = await api(
      `/v1/tenants/${tenant.id}/workers/${taxWorker}/tax-elections/${item.id}/hacienda-submission/documents`,
      { method: "POST", body: form },
    );
    if (!response.ok) setError(`hacienda-document:${response.status}`);
    else {
      setTaxSubmissionFiles((current) => ({ ...current, [item.id]: null }));
      await loadTaxSubmission(item);
    }
    setBusy(false);
  };
  const downloadTaxSubmissionDocument = async (
    electionId: string,
    evidence: { id: string; fileName: string },
  ) => {
    const response = await api(
      `/v1/tenants/${tenant.id}/workers/${taxWorker}/tax-elections/${electionId}/hacienda-submission/documents/${evidence.id}`,
    );
    if (!response.ok) {
      setError(`hacienda-download:${response.status}`);
      return;
    }
    const url = URL.createObjectURL(await response.blob()),
      anchor = window.document.createElement("a");
    anchor.href = url;
    anchor.download = evidence.fileName;
    anchor.click();
    URL.revokeObjectURL(url);
  };
  const transitionTaxSubmission = (
    item: TaxElection,
    status: "ready" | "submitted" | "confirmed" | "returned",
  ) => {
    const current = taxSubmissions[item.id]?.case;
    setTaxSubmissionDraft({
      item,
      status,
      reason: "",
      submissionMethod:
        (current?.submissionMethod as TaxSubmissionDraft["submissionMethod"]) ??
        "electronic",
      submittedOn: current?.submittedOn ?? today,
      submissionReference: current?.submissionReference ?? "",
    });
  };
  const submitTaxSubmission = async (e: FormEvent) => {
    e.preventDefault();
    const draft = taxSubmissionDraft;
    if (!draft) return;
    setBusy(true);
    setError("");
    const requiresSubmission =
        draft.status === "submitted" || draft.status === "confirmed",
      response = await api(
        `/v1/tenants/${tenant.id}/workers/${taxWorker}/tax-elections/${draft.item.id}/hacienda-submission/status`,
        {
          method: "POST",
          body: JSON.stringify({
            status: draft.status,
            submissionMethod: requiresSubmission
              ? draft.submissionMethod
              : null,
            submittedOn: requiresSubmission ? draft.submittedOn : null,
            submissionReference: requiresSubmission
              ? draft.submissionReference.trim()
              : null,
            notes: null,
            reason: draft.reason.trim(),
          }),
        },
      );
    if (!response.ok) {
      setError(`hacienda-status:${response.status}`);
      setBusy(false);
      return;
    }
    setTaxSubmissionDraft(null);
    await loadTaxSubmission(draft.item);
    const history = await api(
      `/v1/tenants/${tenant.id}/workers/${taxWorker}/tax-elections`,
    );
    if (history.ok)
      setTaxElections(
        ((await history.json()) as { elections: TaxElection[] }).elections,
      );
    setBusy(false);
  };
  const createBonusCycle = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/christmas-bonus-cycles`,
      {
        method: "POST",
        body: JSON.stringify({
          coverageYear: Number(bonusForm.coverageYear),
          employeeCount: Number(bonusForm.employeeCount),
          exemptionStatus: bonusForm.exemptionStatus,
          exemptionReference: bonusForm.exemptionReference || null,
        }),
      },
    );
    if (!response.ok) setError(`bonus-cycle:${response.status}`);
    await load();
  };
  const addBonusAdjustment = async () => {
    setBusy(true);
    setError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/christmas-bonus-cycles/${bonusAdjustment.cycleId}/adjustments`,
      {
        method: "POST",
        body: JSON.stringify({
          workerId: bonusAdjustment.workerId,
          hours: Number(bonusAdjustment.hours),
          earningsCents: Math.round(Number(bonusAdjustment.earnings) * 100),
          reason: bonusAdjustment.reason,
          dockWorker: bonusAdjustment.dockWorker,
        }),
      },
    );
    if (!response.ok) setError(`bonus-adjustment:${response.status}`);
    else
      setBonusAdjustment({
        ...bonusAdjustment,
        hours: "0",
        earnings: "0",
        reason: "",
      });
    setBusy(false);
  };
  const bonusAction = async (
    cycle: BonusCycle,
    action: "calculate" | "approved" | "finalized" | "voided",
  ) => {
    if (action !== "calculate") {
      setBonusActionDraft({
        kind: "status",
        cycle,
        status: action,
        reason: "",
      });
      return;
    }
    setBusy(true);
    setError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/christmas-bonus-cycles/${cycle.id}/calculate`,
      { method: "POST" },
    );
    if (!response.ok) setError(`bonus-${action}:${response.status}`);
    else {
      const items = await api(
        `/v1/tenants/${tenant.id}/christmas-bonus-cycles/${cycle.id}/items`,
      );
      if (items.ok) {
        const parsed = (await items.json()) as { items: BonusItem[] };
        setBonusItems((current) => ({ ...current, [cycle.id]: parsed.items }));
      }
    }
    await load();
  };
  const uploadBonusEvidence = async (cycle: BonusCycle) => {
    if (!bonusFile) return;
    const form = new FormData();
    form.set("file", bonusFile);
    setBusy(true);
    const response = await api(
      `/v1/tenants/${tenant.id}/christmas-bonus-cycles/${cycle.id}/exemption`,
      { method: "POST", body: form },
    );
    if (!response.ok) setError(`bonus-evidence:${response.status}`);
    else setBonusFile(null);
    await load();
  };
  const createBonusPayroll = (cycle: BonusCycle) =>
    setBonusActionDraft({
      kind: "payroll",
      cycle,
      payDate: cycle.paymentDeadline,
      withholdingMethod: "annual_aggregate",
    });
  const submitBonusAction = async (e: FormEvent) => {
    e.preventDefault();
    const draft = bonusActionDraft;
    if (!draft) return;
    setBusy(true);
    setError("");
    const path =
        draft.kind === "status"
          ? `christmas-bonus-cycles/${draft.cycle.id}/status`
          : `christmas-bonus-cycles/${draft.cycle.id}/payroll`,
      body =
        draft.kind === "status"
          ? { status: draft.status, reason: draft.reason.trim() }
          : {
              payDate: draft.payDate,
              withholdingMethod: draft.withholdingMethod,
            },
      response = await api(`/v1/tenants/${tenant.id}/${path}`, {
        method: "POST",
        body: JSON.stringify(body),
      });
    if (!response.ok) {
      setError(`bonus-${draft.kind}:${response.status}`);
      setBusy(false);
      return;
    }
    setBonusActionDraft(null);
    await load();
  };
  const money = (cents: number) =>
    new Intl.NumberFormat(locale === "es" ? "es-PR" : "en-US", {
      style: "currency",
      currency: "USD",
    }).format(cents / 100);
  const openWorkerTimesheets = (workerId: string, payrollRun: PayrollRun) => {
    navigate("time");
    const query = new URLSearchParams({
      workerId,
      periodStart: payrollRun.periodStart,
      periodEnd: payrollRun.periodEnd,
      returnTo: "payroll",
    });
    window.history.replaceState({}, "", `/time?${query.toString()}`);
  };
  const visibleRuns = useMemo(
    () =>
      [...runs]
        .filter(
          (item) =>
            runStatusFilter === "all" || item.status === runStatusFilter,
        )
        .sort((a, b) =>
          runSort === "newest"
            ? b.payDate.localeCompare(a.payDate)
            : a.payDate.localeCompare(b.payDate),
        ),
    [runSort, runStatusFilter, runs],
  );
  const runPageCount = Math.max(1, Math.ceil(visibleRuns.length / 4));
  const pagedRuns = visibleRuns.slice(runPage * 4, runPage * 4 + 4);
  const timeImportRun = runs.find((item) => item.id === timeImportRunId);
  useEffect(() => setRunPage(0), [runSort, runStatusFilter]);
  useEffect(
    () => setRunPage((current) => Math.min(current, runPageCount - 1)),
    [runPageCount],
  );
  const reviewCalculation = async (item: PayrollRun) => {
    setBusy(true);
    setError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/payroll-runs/${item.id}/review`,
    );
    if (response.ok) setPayrollReview(await response.json());
    else setError(`review:${response.status}`);
    setBusy(false);
    revealCompensationResult();
  };
  const prepareProfessionalServices = async (item: PayrollRun) => {
    setBusy(true);
    setError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/payroll-runs/${item.id}/contractor-payments/prepare`,
      { method: "POST" },
    );
    if (response.ok) {
      setContractorPreparation(await response.json());
      await load();
    } else setError(`contractor-prepare:${response.status}`);
    setBusy(false);
    revealCompensationResult();
  };
  const payrollRunActions = (item: PayrollRun) => (
    <div className="run-actions">
      {item.runType !== "reversal" &&
        ["draft", "diagnostic", "calculated"].includes(item.status) && (
          <button onClick={() => void loadApprovedTime(item)}>
            {locale === "es" ? "Cargar tiempo aprobado" : "Load approved time"}
          </button>
        )}
      {item.runType !== "reversal" &&
        ["draft", "diagnostic", "calculated"].includes(item.status) && (
          <button onClick={() => void prepareProfessionalServices(item)}>
            {locale === "es"
              ? "Preparar servicios profesionales"
              : "Prepare professional services"}
          </button>
        )}
      {item.runType === "supplemental" &&
        ["draft", "diagnostic", "calculated"].includes(item.status) && (
          <button onClick={() => void loadFinalPay(item)}>
            {locale === "es" ? "Preparar pago final" : "Prepare final pay"}
          </button>
        )}
      {item.runType !== "reversal" &&
        !["finalized", "voided"].includes(item.status) && (
          <button onClick={() => void action(item, "diagnose")}>
            {locale === "es" ? "Diagnosticar" : "Diagnose"}
          </button>
        )}
      {item.runType !== "reversal" &&
        ["draft", "diagnostic", "calculated"].includes(item.status) && (
          <button onClick={() => void action(item, "calculate")}>
            {locale === "es" ? "Calcular" : "Calculate"}
          </button>
        )}
      {item.status === "calculated" && (
        <>
          <button onClick={() => void reviewCalculation(item)}>
            {locale === "es" ? "Revisar cálculo" : "Review calculation"}
          </button>
          <button onClick={() => void action(item, "approve")}>
            {locale === "es" ? "Aprobar" : "Approve"}
          </button>
          <button
            className="danger-action"
            onClick={() => void action(item, "reject")}
          >
            {locale === "es" ? "Rechazar" : "Reject"}
          </button>
        </>
      )}
      {item.status === "approved" && (
        <button
          className="danger-action"
          onClick={() => void action(item, "finalize")}
        >
          {locale === "es" ? "Finalizar" : "Finalize"}
        </button>
      )}
      {item.status === "finalized" && (
        <>
          <button onClick={() => void generatePayStubs(item)}>
            {locale === "es"
              ? "Regenerar comprobantes PDF"
              : "Regenerate PDF pay stubs"}
          </button>
          <button onClick={() => void emailPayStubs(item)}>
            {locale === "es" ? "Enviar por Email" : "Send by email"}
          </button>
          <button onClick={() => void createChecks(item)}>
            {locale === "es" ? "Crear cheques" : "Create checks"}
          </button>
        </>
      )}
    </div>
  );
  const payrollRunsPanel = busy ? (
    <LoaderCircle className="spin" />
  ) : (
    <section
      className="data-section payroll-runs-primary compensation-workflow-step compensation-workflow-step-3"
      aria-labelledby="payroll-runs-title"
    >
      <div className="data-section-heading">
        <div>
          <h2 id="payroll-runs-title">
            {locale === "es"
              ? "3) Períodos de compensación"
              : "3) Compensation periods"}
          </h2>
          <p>
            {locale === "es"
              ? `${visibleRuns.length} de ${runs.length} períodos`
              : `${visibleRuns.length} of ${runs.length} runs`}
          </p>
        </div>
        <div className="table-controls">
          <label>
            {locale === "es" ? "Estado" : "Status"}
            <select
              value={runStatusFilter}
              onChange={(event) =>
                setRunStatusFilter(event.currentTarget.value)
              }
            >
              <option value="all">{locale === "es" ? "Todos" : "All"}</option>
              {[...new Set(runs.map((item) => item.status))]
                .sort()
                .map((status) => (
                  <option key={status} value={status}>
                    {optionLabel(status, locale)}
                  </option>
                ))}
            </select>
          </label>
          <label>
            {locale === "es" ? "Fecha de pago" : "Pay date"}
            <select
              value={runSort}
              onChange={(event) =>
                setRunSort(event.currentTarget.value as "newest" | "oldest")
              }
            >
              <option value="newest">
                {locale === "es" ? "Más reciente" : "Newest"}
              </option>
              <option value="oldest">
                {locale === "es" ? "Más antigua" : "Oldest"}
              </option>
            </select>
          </label>
        </div>
      </div>
      {visibleRuns.length === 0 ? (
        <p className="empty-state">
          {locale === "es"
            ? "No hay períodos con este estado."
            : "There are no runs with this status."}
        </p>
      ) : (
        <>
          <div
            className="responsive-table-shell"
            role="region"
            tabIndex={0}
            aria-label={
              locale === "es"
                ? "Tabla de períodos de nómina"
                : "Payroll runs table"
            }
          >
            <table>
              <caption>
                {locale === "es"
                  ? "Períodos, fechas, estado y acciones de nómina"
                  : "Payroll periods, dates, status, and actions"}
              </caption>
              <thead>
                <tr>
                  <th scope="col">{locale === "es" ? "Período" : "Period"}</th>
                  <th scope="col">
                    {locale === "es" ? "Fecha de pago" : "Pay date"}
                  </th>
                  <th scope="col">
                    {locale === "es"
                      ? "Frecuencia y tipo"
                      : "Frequency and type"}
                  </th>
                  <th scope="col">{locale === "es" ? "Estado" : "Status"}</th>
                </tr>
              </thead>
              <tbody>
                {pagedRuns.map((item) => (
                  <Fragment key={item.id}>
                    <tr className="compensation-period-details">
                      <th scope="row">
                        <strong>
                          {formatDateRange(
                            item.periodStart,
                            item.periodEnd,
                            locale,
                            "—",
                          )}
                        </strong>
                        {item.approvalReason && (
                          <small>
                            {locale === "es"
                              ? "Aprobación independiente"
                              : "Independent approval"}
                            : {item.approvalReason} ·{" "}
                            {formatDateTime(item.approvedAt, locale)}
                          </small>
                        )}
                      </th>
                      <td>{formatDate(item.payDate, locale)}</td>
                      <td>
                        {optionLabel(item.payrollFrequency, locale)}
                        <small>{optionLabel(item.runType, locale)}</small>
                      </td>
                      <td>
                        <span className={`run-status ${item.status}`}>
                          {optionLabel(item.status, locale)}
                        </span>
                      </td>
                    </tr>
                    <tr className="compensation-period-actions">
                      <td colSpan={4}>
                        <strong className="compensation-actions-label">
                          {locale === "es" ? "Acciones" : "Actions"}
                        </strong>
                        {payrollRunActions(item)}
                      </td>
                    </tr>
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <div className="run-list responsive-card-list">
            {pagedRuns.map((item) => (
              <article key={item.id}>
                <div>
                  <strong>
                    {formatDateRange(
                      item.periodStart,
                      item.periodEnd,
                      locale,
                      "—",
                    )}
                  </strong>
                  <p>
                    {formatDate(item.payDate, locale)} ·{" "}
                    {optionLabel(item.payrollFrequency, locale)} ·{" "}
                    {optionLabel(item.runType, locale)}
                  </p>
                  {item.sourceRunId && (
                    <small>
                      {locale === "es" ? "Corrige" : "Corrects"}:{" "}
                      {item.sourceRunId.slice(0, 8)} · {item.correctionReason}
                      {item.companionReversalRunId
                        ? ` · ${locale === "es" ? "reversa" : "reversal"} ${item.companionReversalRunId.slice(0, 8)}`
                        : ""}
                    </small>
                  )}
                  {item.approvalReason && (
                    <small>
                      {locale === "es"
                        ? "Aprobación independiente"
                        : "Independent approval"}
                      : {item.approvalReason} ·{" "}
                      {formatDateTime(item.approvedAt, locale)}
                    </small>
                  )}
                  {item.lastReviewDecision === "rejected" && (
                    <small>
                      {locale === "es"
                        ? "Devuelta para corrección"
                        : "Returned for correction"}
                      : {item.lastReviewReason}
                    </small>
                  )}
                </div>
                <span className={`run-status ${item.status}`}>
                  {optionLabel(item.status, locale)}
                </span>
                {payrollRunActions(item)}
              </article>
            ))}
          </div>
          {runPageCount > 1 && (
            <nav
              className="pagination-controls"
              aria-label={
                locale === "es"
                  ? "Páginas de períodos de nómina"
                  : "Payroll-period pages"
              }
            >
              <button
                type="button"
                disabled={runPage === 0}
                onClick={() => setRunPage((current) => current - 1)}
              >
                {locale === "es" ? "Anterior" : "Previous"}
              </button>
              <span>
                {locale === "es"
                  ? `Página ${runPage + 1} de ${runPageCount}`
                  : `Page ${runPage + 1} of ${runPageCount}`}
              </span>
              <button
                type="button"
                disabled={runPage + 1 >= runPageCount}
                onClick={() => setRunPage((current) => current + 1)}
              >
                {locale === "es" ? "Siguiente" : "Next"}
              </button>
            </nav>
          )}
        </>
      )}
    </section>
  );
  return (
    <div
      className={`content ${setupMode ? `personnel-setup-view setup-${setupMode}` : `payroll-workflow${showCompensationOtherOptions ? " show-compensation-other-options" : ""}`}`}
    >
      {setupMode && (
        <div className="personnel-setup-header">
          <button
            type="button"
            onClick={() => {
              sessionStorage.removeItem("personnel-setup-kind");
              sessionStorage.removeItem("personnel-setup-worker");
              navigate("personnel");
            }}
          >
            {locale === "es" ? "Volver a la persona" : "Back to worker"}
          </button>
          <p className="kicker">{locale === "es" ? "PERSONAL" : "PERSONNEL"}</p>
          <h1>
            {setupMode === "overtime"
              ? locale === "es"
                ? "Clasificación de horas extra"
                : "Overtime classification"
              : setupMode === "pay"
                ? locale === "es"
                  ? "Pago y contribuciones"
                  : "Pay and taxes"
                : locale === "es"
                  ? "Elección contributiva 499 R-4"
                  : "499 R-4 withholding election"}
          </h1>
        </div>
      )}
      <p className="kicker">{tenant.displayName}</p>
      <h1>{locale === "es" ? "Compensación" : "Compensation"}</h1>
      <p className="lead">
        {locale === "es"
          ? "Prepare en un mismo período la nómina de empleados y los pagos de servicios profesionales; revise ambos antes de aprobarlos."
          : "Prepare employee payroll and professional-service payments in the same period; review both before approval."}
      </p>
      {checkSelection && (
        <dialog
          className="workflow-dialog"
          open
          aria-labelledby="check-selection-title"
        >
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void confirmChecks();
            }}
          >
            <header>
              <div>
                <p className="kicker">
                  {locale === "es" ? "SELECCIÓN DE CHEQUES" : "CHECK SELECTION"}
                </p>
                <h2 id="check-selection-title">
                  {locale === "es"
                    ? "¿A quién se le creará cheque?"
                    : "Who should receive a check?"}
                </h2>
                <p>
                  {formatDate(checkSelection.run.periodStart, locale)} —{" "}
                  {formatDate(checkSelection.run.periodEnd, locale)}
                </p>
              </div>
              <button type="button" onClick={() => setCheckSelection(null)}>
                {locale === "es" ? "Cerrar" : "Close"}
              </button>
            </header>
            <p>
              {locale === "es"
                ? "Todas las personas elegibles aparecen seleccionadas. Desmarque quienes cobran electrónicamente."
                : "All eligible people are selected. Uncheck anyone paid electronically."}
            </p>
            <div className="responsive-table">
              <table>
                <thead>
                  <tr>
                    <th>{locale === "es" ? "Crear" : "Create"}</th>
                    <th>{locale === "es" ? "Persona" : "Person"}</th>
                    <th>{locale === "es" ? "Tipo" : "Type"}</th>
                    <th>{locale === "es" ? "Neto" : "Net"}</th>
                  </tr>
                </thead>
                <tbody>
                  {checkSelection.candidates.map((candidate) => (
                    <tr key={candidate.workerId}>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`${locale === "es" ? "Crear cheque para" : "Create check for"} ${candidate.payee}`}
                          checked={checkSelection.selectedWorkerIds.includes(
                            candidate.workerId,
                          )}
                          onChange={(event) =>
                            toggleCheckCandidate(
                              candidate.workerId,
                              event.currentTarget.checked,
                            )
                          }
                        />
                      </td>
                      <td>
                        <strong>{candidate.payee}</strong>
                      </td>
                      <td>
                        {candidate.workerType === "contractor"
                          ? locale === "es"
                            ? "Servicios profesionales"
                            : "Professional services"
                          : locale === "es"
                            ? "Empleado"
                            : "Employee"}
                      </td>
                      <td>
                        {new Intl.NumberFormat(
                          locale === "es" ? "es-PR" : "en-US",
                          { style: "currency", currency: "USD" },
                        ).format(candidate.amountCents / 100)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setAllCheckCandidates(true)}>
                {locale === "es" ? "Seleccionar todos" : "Select all"}
              </button>
              <button
                type="button"
                onClick={() => setAllCheckCandidates(false)}
              >
                {locale === "es" ? "Quitar todos" : "Clear all"}
              </button>
              <button
                className="primary"
                disabled={busy || !checkSelection.selectedWorkerIds.length}
              >
                {locale === "es"
                  ? `Generar ${checkSelection.selectedWorkerIds.length} cheque(s)`
                  : `Generate ${checkSelection.selectedWorkerIds.length} check(s)`}
              </button>
            </div>
          </form>
        </dialog>
      )}
      <form
        className="settings-form compact-form payroll-period-form compensation-workflow-step compensation-workflow-step-1"
        onSubmit={create}
      >
        <h2>
          {locale === "es"
            ? "1) Nuevo período de nómina"
            : "1) New payroll period"}
        </h2>
        <p className="field-note">
          {locale === "es"
            ? "Propuesta basada en el final de la última nómina regular. Revise las fechas antes de crearla."
            : "Suggested from the end of the latest regular payroll. Review the dates before creating it."}
        </p>
        <div className="form-grid">
          <label>
            {locale === "es" ? "Inicio" : "Start"}
            <input
              required
              type="date"
              value={run.periodStart}
              onInput={(e) => updateRun({ periodStart: e.currentTarget.value })}
            />
          </label>
          <label>
            {locale === "es" ? "Fin" : "End"}
            <input
              required
              type="date"
              value={run.periodEnd}
              onInput={(e) => updateRun({ periodEnd: e.currentTarget.value })}
            />
          </label>
          <label>
            {locale === "es" ? "Fecha de pago" : "Pay date"}
            <input
              required
              type="date"
              value={run.payDate}
              onInput={(e) => updateRun({ payDate: e.currentTarget.value })}
            />
          </label>
          <label>
            {locale === "es" ? "Frecuencia" : "Frequency"}
            <select
              value={run.payrollFrequency}
              onChange={(e) => {
                const payrollFrequency = e.currentTarget
                  .value as PayrollFrequency;
                setRun((current) => ({
                  ...current,
                  payrollFrequency,
                  ...suggestNextPayrollPeriod(runs, payrollFrequency, today),
                }));
                setRunDraftTouched(false);
              }}
            >
              <option value="weekly">{optionLabel("weekly", locale)}</option>
              <option value="biweekly">
                {optionLabel("biweekly", locale)}
              </option>
              <option value="semimonthly">
                {optionLabel("semimonthly", locale)}
              </option>
              <option value="monthly">{optionLabel("monthly", locale)}</option>
            </select>
          </label>
        </div>
        <button className="primary" disabled={busy}>
          <CircleDollarSign />
          {locale === "es" ? "Crear nómina" : "Create payroll"}
        </button>
        {createdPeriod && (
          <OperationalFeedback
            locale={locale}
            isError={false}
            message={
              locale === "es"
                ? `Período creado: ${formatDateRange(createdPeriod.periodStart, createdPeriod.periodEnd, locale, "—")} · pago ${formatDate(createdPeriod.payDate, locale)}.`
                : `Period created: ${formatDateRange(createdPeriod.periodStart, createdPeriod.periodEnd, locale, "—")} · paid ${formatDate(createdPeriod.payDate, locale)}.`
            }
          />
        )}
      </form>
      <section className="settings-form compensation-workflow-step compensation-workflow-step-2 optional-workflow-step">
        <div className="optional-workflow-question">
          <div>
            <h2>
              {locale === "es"
                ? "2) ¿Necesita incluir un gasto o reembolso?"
                : "2) Do you need to include an expense or reimbursement?"}
            </h2>
            <p>
              {locale === "es"
                ? "Seleccione una persona solamente cuando deba revisar o añadir un reembolso antes de aprobar su compensación."
                : "Select a person only when a reimbursement must be reviewed or added before approving compensation."}
            </p>
          </div>
          <button
            type="button"
            aria-expanded={showCompensationReimbursements}
            onClick={() =>
              setShowCompensationReimbursements((current) => !current)
            }
          >
            {showCompensationReimbursements
              ? locale === "es"
                ? "Ocultar reembolsos"
                : "Hide reimbursements"
              : locale === "es"
                ? "Sí, indicar persona"
                : "Yes, select person"}
          </button>
        </div>
        {showCompensationReimbursements && (
          <label>
            {locale === "es"
              ? "Empleado o contratista"
              : "Employee or contractor"}
            <select
              value={compensationReimbursementWorkerId}
              onChange={(event) =>
                setCompensationReimbursementWorkerId(event.currentTarget.value)
              }
            >
              <option value="">—</option>
              {workers
                .filter((worker) => worker.status === "active")
                .map((worker) => (
                  <option key={worker.id} value={worker.id}>
                    {worker.firstName} {worker.lastName} ·{" "}
                    {optionLabel(worker.workerType, locale)}
                  </option>
                ))}
            </select>
          </label>
        )}
      </section>
      {payrollRunsPanel}
      <div
        id="compensation-action-result"
        className="compensation-action-result"
        aria-live="polite"
      >
        <OperationalError value={error} locale={locale} />
        {notice && (
          <p className="success-note" role="status">
            {notice}
          </p>
        )}
        {diagnosticIssues.some(
          (issue) => (issue.status ?? "open") === "open",
        ) && (
          <section className="operation-panel">
            <h2>
              {locale === "es"
                ? "Resultado del diagnóstico"
                : "Diagnostic result"}
            </h2>
            <p>
              {locale === "es"
                ? "Revise estos asuntos aquí antes de continuar con el período."
                : "Review these items here before continuing with the period."}
            </p>
            <div className="run-list">
              {diagnosticIssues
                .filter((issue) => (issue.status ?? "open") === "open")
                .map((issue) => (
                  <article key={`period-result-${issue.id}`}>
                    <div>
                      <strong>
                        {issue.workerName ? `${issue.workerName}: ` : ""}
                        {locale === "es" ? issue.messageEs : issue.messageEn}
                      </strong>
                      <small>
                        {issue.code} · {issue.severity} ·{" "}
                        {optionLabel(issue.status ?? "open", locale)}
                      </small>
                    </div>
                    {(issue.status ?? "open") === "open" && (
                      <div className="run-actions">
                        <button
                          type="button"
                          onClick={() => fixDiagnostic(issue)}
                        >
                          {locale === "es"
                            ? "Corregir o revisar"
                            : "Fix or review"}
                        </button>
                        <button
                          type="button"
                          disabled={diagnosticActionBusyId === issue.id}
                          onClick={() => void actOnDiagnostic(issue, "explain")}
                        >
                          {locale === "es" ? "Explicar" : "Explain"}
                        </button>
                        {(issue.status ?? "open") === "open" && (
                          <button
                            type="button"
                            disabled={diagnosticActionBusyId !== null}
                            onClick={() => void acceptDiagnosticAsIs(issue)}
                          >
                            {diagnosticActionBusyId === issue.id ? (
                              <LoaderCircle className="spin" />
                            ) : locale === "es" ? (
                              "Aceptar como está"
                            ) : (
                              "Accept as is"
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </article>
                ))}
            </div>
          </section>
        )}
        {contractorPreparation && (
          <section id="professional-services-result" className="data-section">
            <div className="data-section-heading">
              <div>
                <h2>
                  {locale === "es"
                    ? "Servicios profesionales preparados"
                    : "Prepared professional services"}
                </h2>
                <p>
                  {locale === "es"
                    ? `${contractorPreparation.prepared.length} pagos creados para revisión`
                    : `${contractorPreparation.prepared.length} payments created for review`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setContractorPreparation(null)}
              >
                {locale === "es" ? "Cerrar" : "Close"}
              </button>
            </div>
            <div className="run-list">
              {contractorPreparation.prepared.map((item) => (
                <article key={item.paymentId}>
                  <div>
                    <strong>{item.workerName}</strong>
                    <p>
                      {(item.hoursHundredths / 100).toFixed(2)} h ·{" "}
                      {money(item.grossCents)}
                    </p>
                  </div>
                  <span className="run-status draft">
                    {locale === "es" ? "Borrador" : "Draft"}
                  </span>
                </article>
              ))}
              {contractorPreparation.issues.map((item) => (
                <article key={`${item.workerId}-${item.code}`}>
                  <div>
                    <strong>{item.workerName}</strong>
                    <p>{optionLabel(item.code, locale)}</p>
                  </div>
                  <span className="run-status warning">
                    {locale === "es" ? "Revisar" : "Review"}
                  </span>
                </article>
              ))}
            </div>
          </section>
        )}
        {payrollReview && (
          <section
            className="operation-panel payroll-review-panel"
            aria-labelledby="payroll-review-title"
          >
            <div className="data-section-heading">
              <div>
                <p className="kicker">
                  {locale === "es" ? "ANTES DE APROBAR" : "BEFORE APPROVAL"}
                </p>
                <h2 id="payroll-review-title">
                  {locale === "es"
                    ? "Revisión del cálculo"
                    : "Calculation review"}
                </h2>
                <p>
                  {formatDateRange(
                    payrollReview.run.periodStart,
                    payrollReview.run.periodEnd,
                    locale,
                    "—",
                  )}{" "}
                  · {locale === "es" ? "pago" : "pay"}{" "}
                  {formatDate(payrollReview.run.payDate, locale)}
                </p>
              </div>
              <button type="button" onClick={() => setPayrollReview(null)}>
                {locale === "es" ? "Cerrar" : "Close"}
              </button>
            </div>
            <div className="balance-grid">
              <article>
                <strong>
                  {locale === "es"
                    ? "Compensación bruta total"
                    : "Total gross compensation"}
                </strong>
                <em>{money(payrollReview.totals.grossCents)}</em>
              </article>
              <article>
                <strong>
                  {locale === "es"
                    ? "Retención de servicios"
                    : "Service withholding"}
                </strong>
                <em>
                  {money(payrollReview.totals.contractorWithholdingCents)}
                </em>
              </article>
              <article>
                <strong>
                  {locale === "es"
                    ? "Contribuciones del personal"
                    : "Employee taxes"}
                </strong>
                <em>{money(payrollReview.totals.employeeTaxesCents)}</em>
              </article>
              <article>
                <strong>
                  {locale === "es" ? "Otras deducciones" : "Other deductions"}
                </strong>
                <em>{money(payrollReview.totals.deductionsCents)}</em>
              </article>
              <article>
                <strong>
                  {locale === "es" ? "Neto total" : "Total net pay"}
                </strong>
                <em>{money(payrollReview.totals.netCents)}</em>
              </article>
            </div>
            <h3>{locale === "es" ? "Empleados" : "Employees"}</h3>
            <div className="run-list">
              {payrollReview.items.map((item) => (
                <details key={item.workerId} className="review-worker">
                  <summary>
                    <span>
                      <strong>{item.workerName}</strong>
                      <small>
                        {locale === "es" ? "Bruto" : "Gross"}:{" "}
                        {money(item.grossCents)} ·{" "}
                        {locale === "es" ? "Contribuciones" : "Taxes"}:{" "}
                        {money(item.employeeTaxesCents)} ·{" "}
                        {locale === "es" ? "Deducciones" : "Deductions"}:{" "}
                        {money(item.deductionsCents)}
                      </small>
                    </span>
                    <strong>
                      {locale === "es" ? "Neto" : "Net"}: {money(item.netCents)}
                    </strong>
                  </summary>
                  <div className="responsive-table-shell">
                    <table>
                      <caption>
                        {locale === "es"
                          ? `Ingresos de ${item.workerName}`
                          : `${item.workerName} earnings`}
                      </caption>
                      <thead>
                        <tr>
                          <th>{locale === "es" ? "Concepto" : "Earning"}</th>
                          <th>{locale === "es" ? "Horas" : "Hours"}</th>
                          <th>{locale === "es" ? "Tarifa" : "Rate"}</th>
                          <th>{locale === "es" ? "Importe" : "Amount"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {item.earnings.map((line, index) => (
                          <tr key={index}>
                            <td>
                              {line.description ||
                                optionLabel(line.earningType, locale)}
                            </td>
                            <td>
                              {line.hoursHundredths == null
                                ? "—"
                                : (line.hoursHundredths / 100).toFixed(2)}
                            </td>
                            <td>
                              {line.rateCents == null
                                ? "—"
                                : money(line.rateCents)}
                            </td>
                            <td>{money(line.amountCents)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="responsive-table-shell">
                    <table>
                      <caption>
                        {locale === "es"
                          ? "Contribuciones y deducciones"
                          : "Taxes and deductions"}
                      </caption>
                      <thead>
                        <tr>
                          <th>{locale === "es" ? "Concepto" : "Item"}</th>
                          <th>
                            {locale === "es" ? "Del empleado" : "Employee"}
                          </th>
                          <th>{locale === "es" ? "Patronal" : "Employer"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {item.taxes.map((line) => (
                          <tr key={line.taxKey}>
                            <td>{optionLabel(line.taxKey, locale)}</td>
                            <td>{money(line.employeeAmountCents)}</td>
                            <td>{money(line.employerAmountCents)}</td>
                          </tr>
                        ))}
                        {item.deductions.map((line, index) => (
                          <tr key={`deduction-${index}`}>
                            <td>
                              {line.description ||
                                optionLabel(line.deductionType, locale)}
                            </td>
                            <td>{money(line.amountCents)}</td>
                            <td>—</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              ))}
            </div>
            {payrollReview.contractors.length > 0 && (
              <>
                <h3>
                  {locale === "es"
                    ? "Servicios profesionales"
                    : "Professional services"}
                </h3>
                <div className="run-list">
                  {payrollReview.contractors.map((item) => (
                    <details key={item.id} className="review-worker">
                      <summary>
                        <span>
                          <strong>{item.workerName}</strong>
                          <small>
                            {locale === "es" ? "Bruto" : "Gross"}:{" "}
                            {money(item.grossCents)} ·{" "}
                            {locale === "es" ? "Retención" : "Withholding"}:{" "}
                            {money(item.withholdingCents)} (
                            {(item.withholdingRateBps / 100).toFixed(2)}%)
                          </small>
                        </span>
                        <strong>
                          {locale === "es" ? "Neto" : "Net"}:{" "}
                          {money(item.netCents)}
                        </strong>
                      </summary>
                      <div className="responsive-table-shell">
                        <table>
                          <caption>
                            {locale === "es"
                              ? `Compensación de ${item.workerName}`
                              : `${item.workerName} compensation`}
                          </caption>
                          <thead>
                            <tr>
                              <th>{locale === "es" ? "Concepto" : "Item"}</th>
                              <th>
                                {locale === "es"
                                  ? "Horas/unidades"
                                  : "Hours/units"}
                              </th>
                              <th>{locale === "es" ? "Tarifa" : "Rate"}</th>
                              <th>{locale === "es" ? "Importe" : "Amount"}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>{item.description}</td>
                              <td>
                                {item.hoursHundredths === null
                                  ? "—"
                                  : (item.hoursHundredths / 100).toFixed(2)}
                              </td>
                              <td>
                                {item.rateCents === null
                                  ? "—"
                                  : money(item.rateCents)}
                              </td>
                              <td>{money(item.grossCents)}</td>
                            </tr>
                            <tr>
                              <td>
                                {locale === "es"
                                  ? "Retención de servicios profesionales"
                                  : "Professional-services withholding"}
                              </td>
                              <td>—</td>
                              <td>
                                {(item.withholdingRateBps / 100).toFixed(2)}%
                              </td>
                              <td>-{money(item.withholdingCents)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </details>
                  ))}
                </div>
              </>
            )}
          </section>
        )}
        {timeImportRun && (
          <section className="operation-panel" aria-live="polite">
            <h2>
              {locale === "es"
                ? "Resultado del tiempo aprobado"
                : "Approved-time result"}
            </h2>
            <p>
              <strong>
                {formatDateRange(
                  timeImportRun.periodStart,
                  timeImportRun.periodEnd,
                  locale,
                  "—",
                )}
              </strong>
            </p>
            {timeImportExcludedWorkerIds.length > 0 && (
              <p className="muted compact-copy">
                {locale === "es"
                  ? "Sin compensación en este período: "
                  : "No compensation in this period: "}
                {timeImportExcludedWorkerIds
                  .map((workerId) => {
                    const worker = workers.find((item) => item.id === workerId);
                    return worker
                      ? `${worker.firstName} ${worker.lastName ?? ""}`.trim()
                      : workerId;
                  })
                  .join(", ")}
                .
              </p>
            )}
            {timeImportError ? (
              <OperationalFeedback
                locale={locale}
                isError
                message={timeImportError}
              />
            ) : timeImportIssues.length > 0 ? (
              <>
                <p>
                  {locale === "es"
                    ? "El período todavía no está listo. Complete lo siguiente:"
                    : "The period is not ready yet. Complete the following:"}
                </p>
                <ul className="issue-list">
                  {timeImportIssues.map((issue, index) => {
                    const worker = workers.find(
                      (item) => item.id === issue.workerId,
                    );
                    const messages: Record<string, [string, string]> = {
                      missing_locked_timesheet: [
                        "Falta aprobar y cerrar la hoja de tiempo que cubre este período.",
                        "The timesheet covering this period must be approved and locked.",
                      ],
                      timesheet_coverage_gap: [
                        "Hay días del período que no están cubiertos por hojas de tiempo cerradas.",
                        "Some period days are not covered by locked timesheets.",
                      ],
                      missing_period_pay_rate: [
                        "Falta una tarifa de pago vigente para todo el período.",
                        "An effective pay rate covering the period is missing.",
                      ],
                      missing_categorized_time_entries: [
                        "La hoja aprobada no tiene horas clasificadas para nómina.",
                        "The approved timesheet has no payroll-classified hours.",
                      ],
                      no_payroll_earnings: [
                        "No hay tiempo aprobado ni salario configurado para cargar.",
                        "There is no approved time or configured salary to load.",
                      ],
                    };
                    const name = worker
                      ? `${worker.firstName} ${worker.lastName ?? ""}`.trim()
                      : locale === "es"
                        ? "Nómina"
                        : "Payroll";
                    return (
                      <li
                        key={`${issue.workerId}-${issue.code}-${index}`}
                        className="compensation-issue-row"
                      >
                        <span>
                          {issue.workerId ? (
                            <button
                              type="button"
                              className="inline-link"
                              onClick={() =>
                                openWorkerTimesheets(
                                  issue.workerId!,
                                  timeImportRun,
                                )
                              }
                            >
                              <strong>{name}</strong>
                            </button>
                          ) : (
                            <strong>{name}</strong>
                          )}
                          :{" "}
                          {messages[issue.code]?.[locale === "es" ? 0 : 1] ??
                            issue.code.replaceAll("_", " ")}
                        </span>
                        {issue.workerId && (
                          <button
                            type="button"
                            className="secondary compact-action"
                            disabled={busy}
                            onClick={() =>
                              void ignoreWorkerForCompensation(
                                timeImportRun,
                                issue.workerId!,
                              )
                            }
                          >
                            {locale === "es"
                              ? "Ignorar en esta compensación"
                              : "Ignore for this compensation"}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </>
            ) : preparedTime?.runId === timeImportRun.id ? (
              <OperationalFeedback
                locale={locale}
                isError={false}
                message={
                  locale === "es"
                    ? `Tiempo aprobado cargado: ${preparedTime.items.length} empleados y ${preparedTime.items.reduce((total, item) => total + item.earnings.reduce((hours, earning) => hours + (earning.hoursHundredths ?? 0), 0), 0) / 100} horas.`
                    : `Approved time loaded: ${preparedTime.items.length} employees and ${preparedTime.items.reduce((total, item) => total + item.earnings.reduce((hours, earning) => hours + (earning.hoursHundredths ?? 0), 0), 0) / 100} hours.`
                }
              />
            ) : (
              <LoaderCircle className="spin" />
            )}
          </section>
        )}
      </div>
      <section className="settings-form compensation-workflow-step compensation-workflow-step-4 optional-workflow-step">
        <div className="optional-workflow-question">
          <div>
            <h2>
              {locale === "es" ? "4) Otras opciones" : "4) Other options"}
            </h2>
            <p>
              {locale === "es"
                ? "Herramientas avanzadas que puede revisar más adelante."
                : "Advanced tools that can be reviewed later."}
            </p>
          </div>
          <button
            type="button"
            aria-expanded={showCompensationOtherOptions}
            onClick={() =>
              setShowCompensationOtherOptions((current) => !current)
            }
          >
            {showCompensationOtherOptions
              ? locale === "es"
                ? "Ocultar opciones"
                : "Hide options"
              : locale === "es"
                ? "Ver otras opciones"
                : "View other options"}
          </button>
        </div>
        <ul className="other-time-options-list">
          <li>
            {locale === "es"
              ? "Diagnóstico y cálculo"
              : "Diagnostics and calculation"}
          </li>
          <li>
            {locale === "es"
              ? "Ingresos adicionales y bonos"
              : "Additional earnings and bonuses"}
          </li>
          <li>
            {locale === "es"
              ? "Deducciones y órdenes"
              : "Deductions and orders"}
          </li>
          <li>
            {locale === "es" ? "Contribuciones patronales" : "Employer taxes"}
          </li>
          <li>
            {locale === "es"
              ? "Correcciones y nómina final"
              : "Corrections and final pay"}
          </li>
        </ul>
      </section>
      <details
        className="operation-panel personnel-owned-setup overtime-setup-panel"
        open={setupMode === "overtime" || undefined}
      >
        <summary>
          {locale === "es"
            ? "Clasificación de horas extra"
            : "Overtime classification"}
        </summary>
        <p>
          {locale === "es"
            ? "La tarifa no decide la exención. Registre una revisión de funciones vigente. Una clasificación exenta requiere revisión de base salarial y evidencia privada; no exento es la opción protectora."
            : "The pay rate does not decide exemption. Record an effective duties review. An exempt decision requires salary-basis review and private evidence; nonexempt is the protective choice."}
        </p>
        <form onSubmit={createOvertimeClassification}>
          <div className="form-grid">
            <label>
              {locale === "es" ? "Empleado" : "Employee"}
              <select
                required
                value={overtimeClassificationForm.workerId}
                onChange={(e) =>
                  setOvertimeClassificationForm({
                    ...overtimeClassificationForm,
                    workerId: e.currentTarget.value,
                  })
                }
              >
                <option value="">—</option>
                {workers
                  .filter((worker) => worker.workerType === "employee")
                  .map((worker) => (
                    <option key={worker.id} value={worker.id}>
                      {worker.firstName} {worker.lastName}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              {locale === "es" ? "Vigente desde" : "Effective from"}
              <input
                required
                type="date"
                value={overtimeClassificationForm.effectiveFrom}
                onInput={(e) =>
                  setOvertimeClassificationForm({
                    ...overtimeClassificationForm,
                    effectiveFrom: (e.target as HTMLInputElement).value,
                  })
                }
                onChange={(e) =>
                  setOvertimeClassificationForm({
                    ...overtimeClassificationForm,
                    effectiveFrom: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es"
                ? "Vigente hasta (opcional)"
                : "Effective through (optional)"}
              <input
                type="date"
                value={overtimeClassificationForm.effectiveTo}
                onInput={(e) =>
                  setOvertimeClassificationForm({
                    ...overtimeClassificationForm,
                    effectiveTo: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Decisión" : "Decision"}
              <select
                value={overtimeClassificationForm.classification}
                onChange={(e) =>
                  setOvertimeClassificationForm({
                    ...overtimeClassificationForm,
                    classification: e.currentTarget
                      .value as OvertimeClassification["classification"],
                  })
                }
              >
                <option value="nonexempt">
                  {locale === "es"
                    ? "No exento — paga horas extra"
                    : "Nonexempt — overtime applies"}
                </option>
                <option value="exempt_executive">
                  {optionLabel("exempt_executive", locale)}
                </option>
                <option value="exempt_administrative">
                  {optionLabel("exempt_administrative", locale)}
                </option>
                <option value="exempt_professional">
                  {optionLabel("exempt_professional", locale)}
                </option>
                <option value="exempt_outside_sales">
                  {optionLabel("exempt_outside_sales", locale)}
                </option>
                <option value="exempt_other_statutory">
                  {optionLabel("exempt_other_statutory", locale)}
                </option>
              </select>
            </label>
            <label>
              {locale === "es"
                ? "Resumen de funciones reales"
                : "Actual duties summary"}
              <textarea
                required
                minLength={10}
                value={overtimeClassificationForm.dutiesSummary}
                onChange={(e) =>
                  setOvertimeClassificationForm({
                    ...overtimeClassificationForm,
                    dutiesSummary: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Fuente jurídica HTTPS" : "HTTPS legal source"}
              <input
                required
                type="url"
                value={overtimeClassificationForm.legalSourceUrl}
                onChange={(e) =>
                  setOvertimeClassificationForm({
                    ...overtimeClassificationForm,
                    legalSourceUrl: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Razón documentada" : "Documented reason"}
              <input
                required
                minLength={3}
                value={overtimeClassificationForm.reason}
                onChange={(e) =>
                  setOvertimeClassificationForm({
                    ...overtimeClassificationForm,
                    reason: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es"
                ? "Memorando/evidencia PDF o imagen"
                : "Memo/evidence PDF or image"}
              <input
                required={
                  overtimeClassificationForm.classification !== "nonexempt"
                }
                type="file"
                accept="application/pdf,image/png,image/jpeg,image/webp"
                onChange={(e) =>
                  setOvertimeClassificationFile(
                    e.currentTarget.files?.[0] ?? null,
                  )
                }
              />
            </label>
            <label className="check-label">
              <input
                type="checkbox"
                required={
                  overtimeClassificationForm.classification !== "nonexempt"
                }
                checked={overtimeClassificationForm.salaryBasisReviewed}
                onChange={(e) =>
                  setOvertimeClassificationForm({
                    ...overtimeClassificationForm,
                    salaryBasisReviewed: e.currentTarget.checked,
                  })
                }
              />
              {locale === "es"
                ? "Revisé la base salarial y los deberes; el título por sí solo no decide"
                : "I reviewed salary basis and duties; job title alone does not decide"}
            </label>
          </div>
          <button className="primary" disabled={busy}>
            {locale === "es"
              ? "Registrar decisión inmutable"
              : "Record immutable decision"}
          </button>
        </form>
        <div className="run-list">
          {overtimeClassifications.map((item) => (
            <article key={item.id}>
              <div>
                <strong>
                  {item.workerName} · {optionLabel(item.classification, locale)}
                </strong>
                <p>
                  {formatDateRange(
                    item.effectiveFrom,
                    item.effectiveTo,
                    locale,
                    "∞",
                  )}{" "}
                  · {item.dutiesSummary}
                </p>
                <small>
                  {item.reason} ·{" "}
                  <a
                    className="official-source-link"
                    href={item.legalSourceUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {locale === "es" ? "fuente" : "source"}
                  </a>
                </small>
              </div>
              {item.evidenceFileName && (
                <button
                  type="button"
                  onClick={() => void downloadOvertimeEvidence(item)}
                >
                  {locale === "es" ? "Evidencia" : "Evidence"}
                </button>
              )}
            </article>
          ))}
        </div>
      </details>
      <details
        className="operation-panel personnel-owned-setup worker-pay-panel"
        open={setupMode === "pay" || undefined}
      >
        <summary>
          {locale === "es"
            ? "Configuración de pago y contribuciones por persona"
            : "Worker pay and tax setup"}
        </summary>
        <div className="form-grid">
          <label>
            {locale === "es" ? "Tipo de nómina" : "Payroll type"}
            <select
              value={run.runType}
              onChange={(e) =>
                setRun({
                  ...run,
                  runType: e.currentTarget.value,
                  sourceRunId:
                    e.currentTarget.value === "regular"
                      ? null
                      : run.sourceRunId,
                  correctionReason:
                    e.currentTarget.value === "regular"
                      ? null
                      : run.correctionReason,
                })
              }
            >
              <option value="regular">{optionLabel("regular", locale)}</option>
              <option value="supplemental">
                {optionLabel("supplemental", locale)}
              </option>
              <option value="adjustment">
                {optionLabel("adjustment", locale)}
              </option>
              <option value="reversal">
                {optionLabel("reversal", locale)}
              </option>
              <option value="void_reissue">
                {optionLabel("void_reissue", locale)}
              </option>
            </select>
          </label>
          {run.runType !== "regular" && (
            <>
              <label>
                {locale === "es"
                  ? "Nómina finalizada de origen"
                  : "Finalized source payroll"}
                <select
                  required={["adjustment", "reversal", "void_reissue"].includes(
                    run.runType,
                  )}
                  value={run.sourceRunId ?? ""}
                  onChange={(e) =>
                    setRun({
                      ...run,
                      sourceRunId: e.currentTarget.value || null,
                    })
                  }
                >
                  <option value="">
                    {locale === "es"
                      ? "Sin vínculo (solo suplementaria)"
                      : "No source (supplemental only)"}
                  </option>
                  {runs
                    .filter((item) => item.status === "finalized")
                    .map((item) => (
                      <option value={item.id} key={item.id}>
                        {formatDate(item.payDate, locale)} ·{" "}
                        {optionLabel(item.runType, locale)} ·{" "}
                        {item.id.slice(0, 8)}
                      </option>
                    ))}
                </select>
              </label>
              <label>
                {locale === "es" ? "Razón documentada" : "Documented reason"}
                <input
                  required
                  minLength={3}
                  value={run.correctionReason ?? ""}
                  onChange={(e) =>
                    setRun({
                      ...run,
                      correctionReason: e.currentTarget.value || null,
                    })
                  }
                />
              </label>
            </>
          )}
          {run.runType === "supplemental" && (
            <p className="field-note">
              {locale === "es"
                ? "Para bono, comisión u horas extra pagadas aparte, seleccione la nómina regular correspondiente. Si cubre varios períodos, deje el origen vacío y use Inicio/Fin para abarcar exactamente las nóminas regulares finalizadas. La retención se agregará o promediará según el Manual de Hacienda y restará lo ya retenido."
                : "For a bonus, commission, or overtime paid separately, select the corresponding regular payroll. If it covers multiple periods, leave the source blank and set Start/End to exactly cover the finalized regular payrolls. Withholding will be aggregated or averaged under Hacienda's guide and prior withholding will be subtracted."}
            </p>
          )}
          <label>
            {locale === "es" ? "Persona" : "Person"}
            <select
              value={profileWorker}
              onChange={(e) => void loadPayrollProfile(e.currentTarget.value)}
            >
              <option value="">—</option>
              {workers.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.firstName} {w.lastName} · {w.workerType}
                </option>
              ))}
            </select>
          </label>
          <label>
            {locale === "es" ? "Tipo de tarifa" : "Rate type"}
            <select
              value={profile.rateType}
              onChange={(e) =>
                setProfile({ ...profile, rateType: e.currentTarget.value })
              }
            >
              {workers.find((w) => w.id === profileWorker)?.workerType ===
              "employee" ? (
                <>
                  <option value="hourly">
                    {optionLabel("hourly", locale)}
                  </option>
                  <option value="salary">
                    {optionLabel("salary", locale)}
                  </option>
                </>
              ) : workers.find((w) => w.id === profileWorker)?.workerType ===
                "professional_services" ? (
                <>
                  <option value="hourly">
                    {locale === "es" ? "Servicio por hora" : "Hourly service"}
                  </option>
                  <option value="fixed_fee">
                    {optionLabel("fixed_fee", locale)}
                  </option>
                  <option value="daily">{optionLabel("daily", locale)}</option>
                  <option value="weekly">{optionLabel("weekly", locale)}</option>
                  <option value="per_job">
                    {optionLabel("per_job", locale)}
                  </option>
                </>
              ) : (
                <option value="">
                  {locale === "es"
                    ? "Seleccione una clasificación"
                    : "Select a classification"}
                </option>
              )}
            </select>
          </label>
          <label>
            {profile.rateType === "salary"
              ? locale === "es"
                ? "Salario por nómina"
                : "Salary per payroll"
              : profile.rateType === "daily"
                ? locale === "es"
                  ? "Importe por día trabajado"
                  : "Amount per worked day"
                : profile.rateType === "weekly"
                  ? locale === "es"
                    ? "Importe por semana"
                    : "Amount per week"
                : locale === "es"
                  ? "Tarifa"
                  : "Rate"}
            <input
              type="number"
              min="0"
              step="0.01"
              value={profile.rate}
              onInput={(e) =>
                setProfile({
                  ...profile,
                  rate: (e.target as HTMLInputElement).value,
                })
              }
              onChange={(e) =>
                setProfile({ ...profile, rate: e.currentTarget.value })
              }
            />
          </label>
          {profile.rateType === "salary" && (
            <label>
              {locale === "es" ? "Frecuencia del salario" : "Salary frequency"}
              <select
                value={profile.salaryFrequency}
                onChange={(e) =>
                  setProfile({
                    ...profile,
                    salaryFrequency: e.currentTarget.value,
                  })
                }
              >
                <option value="weekly">
                  {locale === "es" ? "Semanal" : "Weekly"}
                </option>
                <option value="biweekly">
                  {locale === "es" ? "Cada dos semanas" : "Biweekly"}
                </option>
                <option value="semimonthly">
                  {locale === "es" ? "Dos veces al mes" : "Semimonthly"}
                </option>
                <option value="monthly">
                  {locale === "es" ? "Mensual" : "Monthly"}
                </option>
              </select>
              <small>
                {locale === "es"
                  ? "Debe coincidir con la frecuencia de la nómina; el importe es exacto para una nómina completa."
                  : "Must match the payroll run frequency; the amount is exact for one complete payroll."}
              </small>
            </label>
          )}
          {profile.rateType === "salary" && (
            <label>
              {locale === "es"
                ? "Horas regulares representadas por ese salario"
                : "Regular hours represented by that salary"}
              <input
                type="number"
                min="0.01"
                step="0.01"
                required
                value={profile.salaryRegularHours}
                onInput={(e) =>
                  setProfile({
                    ...profile,
                    salaryRegularHours: e.currentTarget.value,
                  })
                }
              />
              <small>
                {locale === "es"
                  ? "Base contractual retenida para equivalentes por hora y liquidación de vacaciones; no se inventan horas trabajadas."
                  : "Retained contractual basis for hourly equivalents and vacation settlement; it does not manufacture worked hours."}
              </small>
            </label>
          )}
          {profile.rateType === "salary" && (
            <label>
              {locale === "es"
                ? "Método de horas extra del salario"
                : "Salary overtime method"}
              <select
                required
                value={profile.salaryOvertimeMethod}
                onChange={(e) =>
                  setProfile({
                    ...profile,
                    salaryOvertimeMethod: e.currentTarget.value,
                  })
                }
              >
                <option value="fixed_regular_hours">
                  {locale === "es"
                    ? "Salario cubre horas regulares fijas"
                    : "Salary covers fixed regular hours"}
                </option>
              </select>
              <small>
                {locale === "es"
                  ? "Para una persona no exenta, las horas bloqueadas que sean extra se pagan adicionalmente al multiplicador legal completo sobre la tarifa derivada. No es el método de semana fluctuante."
                  : "For a nonexempt person, locked overtime hours are paid in addition at the full legal multiplier over the derived rate. This is not the fluctuating-workweek method."}
              </small>
            </label>
          )}
          <label>
            {locale === "es" ? "Fecha de vigencia" : "Effective date"}
            <input
              type="date"
              value={profile.effectiveFrom}
              onInput={(e) =>
                setProfile({
                  ...profile,
                  effectiveFrom: (e.target as HTMLInputElement).value,
                })
              }
              onChange={(e) =>
                setProfile({ ...profile, effectiveFrom: e.currentTarget.value })
              }
            />
          </label>
        </div>
        {workers.find((w) => w.id === profileWorker)?.workerType ===
        "employee" ? (
          <p className="success-note">
            {locale === "es"
              ? "La tarifa se guarda aquí. La elección contributiva certificada se registra por separado abajo."
              : "Save the pay rate here. Record the certified tax election separately below."}
          </p>
        ) : (
          profileWorker && (
            <div className="form-grid">
              <label>
                {locale === "es" ? "Relevo" : "Waiver"}
                <select
                  value={profile.relevoType}
                  onChange={(e) =>
                    setProfile({
                      ...profile,
                      relevoType: e.currentTarget.value,
                    })
                  }
                >
                  <option value="none">
                    {locale === "es" ? "Ninguno" : "None"}
                  </option>
                  <option value="partial">
                    {locale === "es" ? "Parcial 6%" : "Partial 6%"}
                  </option>
                  <option value="full">
                    {locale === "es" ? "Completo" : "Full"}
                  </option>
                </select>
              </label>
              <label>
                {locale === "es" ? "Número de relevo" : "Waiver number"}
                <input
                  value={profile.relevoNumber}
                  onChange={(e) =>
                    setProfile({
                      ...profile,
                      relevoNumber: e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Fecha de expiración" : "Expiration date"}
                <input
                  type="date"
                  value={profile.relevoExpiresOn}
                  onInput={(e) =>
                    setProfile({
                      ...profile,
                      relevoExpiresOn: e.currentTarget.value,
                    })
                  }
                />
              </label>
            </div>
          )
        )}
        <button
          type="button"
          className="primary"
          disabled={busy || !profileWorker || !profile.rate}
          onClick={() => void saveProfile()}
        >
          {busy ? <LoaderCircle className="spin" /> : <Check />}
          {locale === "es" ? "Guardar perfil" : "Save profile"}
        </button>
        <div aria-live="polite">
          {profileSaveFeedback && (
            <p
              className={
                profileSaveFeedback.kind === "success"
                  ? "success-note"
                  : "form-error"
              }
              role={profileSaveFeedback.kind === "error" ? "alert" : "status"}
            >
              {profileSaveFeedback.message}
            </p>
          )}
        </div>
      </details>
      {Object.keys(taxSubmissions).length > 0 && (
        <section className="operation-panel">
          <h2>
            {locale === "es"
              ? "Evidencia retenida de envíos a Hacienda"
              : "Retained Hacienda submission evidence"}
          </h2>
          <div className="run-list">
            {Object.entries(taxSubmissions).flatMap(
              ([electionId, submission]) =>
                submission.documents.map((evidence) => (
                  <article key={evidence.id}>
                    <div>
                      <strong>
                        {optionLabel(evidence.documentType, locale)}
                      </strong>
                      <p>
                        {evidence.fileName} ·{" "}
                        {(evidence.byteSize / 1024).toFixed(1)} KB
                      </p>
                      <small>SHA-256 {evidence.contentSha256}</small>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        void downloadTaxSubmissionDocument(electionId, evidence)
                      }
                    >
                      {locale === "es" ? "Descargar PDF" : "Download PDF"}
                    </button>
                  </article>
                )),
            )}
          </div>
        </section>
      )}
      <details
        className="operation-panel personnel-owned-setup tax-election-panel"
        open={setupMode === "tax" || undefined}
      >
        <summary>
          {locale === "es"
            ? "Elección contributiva 499 R-4 certificada"
            : "Certified 499 R-4 withholding election"}
        </summary>
        <p>
          {locale === "es"
            ? "Durante la etapa de acceso exclusivo del desarrollador, use esta sección únicamente para transcribir un formulario firmado por el empleado. El portal del empleado permitirá certificación digital cuando se habilite su acceso."
            : "While access is developer-only, use this section only to transcribe a form signed by the employee. The employee portal will support digital certification when employee access is enabled."}
        </p>
        <form onSubmit={saveTaxElection}>
          <div className="form-grid">
            <label>
              {locale === "es" ? "Empleado" : "Employee"}
              <select
                required
                value={taxWorker}
                onChange={(e) => {
                  setTaxWorker(e.currentTarget.value);
                  const w = workers.find(
                    (item) => item.id === e.currentTarget.value,
                  );
                  setTaxForm((current) => ({
                    ...current,
                    certifiedName: w
                      ? `${w.firstName} ${w.lastName ?? ""}`.trim()
                      : "",
                  }));
                }}
              >
                <option value="">—</option>
                {workers
                  .filter((w) => w.workerType === "employee")
                  .map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.firstName} {w.lastName}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              {locale === "es" ? "Vigente desde" : "Effective from"}
              <input
                required
                type="date"
                value={taxForm.effectiveFrom}
                onInput={(e) =>
                  setTaxForm({
                    ...taxForm,
                    effectiveFrom: (e.target as HTMLInputElement).value,
                  })
                }
                onChange={(e) =>
                  setTaxForm({
                    ...taxForm,
                    effectiveFrom: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Versión del formulario" : "Form version"}
              <input
                required
                value={taxForm.formVersion}
                onChange={(e) =>
                  setTaxForm({ ...taxForm, formVersion: e.currentTarget.value })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Exención personal" : "Personal exemption"}
              <select
                value={taxForm.personalExemption}
                onChange={(e) =>
                  setTaxForm({
                    ...taxForm,
                    personalExemption: e.currentTarget.value,
                  })
                }
              >
                <option value="none">{optionLabel("none", locale)}</option>
                <option value="individual">
                  {optionLabel("individual", locale)}
                </option>
                <option value="married_joint_full">
                  {optionLabel("married_joint_full", locale)}
                </option>
                <option value="married_joint_half_or_optional">
                  {optionLabel("married_joint_half_or_optional", locale)}
                </option>
              </select>
            </label>
            <label>
              {locale === "es" ? "Dependientes" : "Dependents"}
              <input
                required
                type="number"
                min="0"
                value={taxForm.dependents}
                onInput={(e) =>
                  setTaxForm({
                    ...taxForm,
                    dependents: (e.target as HTMLInputElement).value,
                  })
                }
                onChange={(e) =>
                  setTaxForm({ ...taxForm, dependents: e.currentTarget.value })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Medios dependientes" : "Half dependents"}
              <input
                required
                type="number"
                min="0"
                value={taxForm.halfDependents}
                onInput={(e) =>
                  setTaxForm({
                    ...taxForm,
                    halfDependents: (e.target as HTMLInputElement).value,
                  })
                }
                onChange={(e) =>
                  setTaxForm({
                    ...taxForm,
                    halfDependents: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es"
                ? "Concesiones por deducciones"
                : "Deduction allowances"}
              <input
                required
                type="number"
                min="0"
                value={taxForm.deductionAllowances}
                onInput={(e) =>
                  setTaxForm({
                    ...taxForm,
                    deductionAllowances: (e.target as HTMLInputElement).value,
                  })
                }
                onChange={(e) =>
                  setTaxForm({
                    ...taxForm,
                    deductionAllowances: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es"
                ? "Retención adicional"
                : "Additional withholding"}
              <input
                required
                type="number"
                min="0"
                step="0.01"
                value={taxForm.additionalWithholding}
                onInput={(e) =>
                  setTaxForm({
                    ...taxForm,
                    additionalWithholding: (e.target as HTMLInputElement).value,
                  })
                }
                onChange={(e) =>
                  setTaxForm({
                    ...taxForm,
                    additionalWithholding: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es"
                ? "Porcentaje adicional"
                : "Additional percentage"}
              <input
                required
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={taxForm.additionalWithholdingPercent}
                onInput={(e) =>
                  setTaxForm({
                    ...taxForm,
                    additionalWithholdingPercent: (e.target as HTMLInputElement)
                      .value,
                  })
                }
                onChange={(e) =>
                  setTaxForm({
                    ...taxForm,
                    additionalWithholdingPercent: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Nombre del cónyuge (solo si aplica)" : "Spouse name (only if applicable)"}
              <input
                value={taxForm.spouseName}
                onChange={(e) =>
                  setTaxForm({ ...taxForm, spouseName: e.currentTarget.value })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Seguro Social del cónyuge (solo si aplica)" : "Spouse SSN (only if applicable)"}
              <input
                autoComplete="off"
                value={taxForm.spouseTaxpayerId}
                onChange={(e) =>
                  setTaxForm({
                    ...taxForm,
                    spouseTaxpayerId: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Dirección postal" : "Postal address"}
              <input
                disabled
                value={taxForm.postalLine1}
                onChange={(e) =>
                  setTaxForm({ ...taxForm, postalLine1: e.currentTarget.value })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Ciudad postal" : "Postal city"}
              <input
                disabled
                value={taxForm.postalCity}
                onChange={(e) =>
                  setTaxForm({ ...taxForm, postalCity: e.currentTarget.value })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Código postal" : "Postal code"}
              <input
                disabled
                value={taxForm.postalCode}
                onChange={(e) =>
                  setTaxForm({ ...taxForm, postalCode: e.currentTarget.value })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Fecha de nacimiento (solo para elección de joven trabajador)" : "Date of birth (only for young-worker election)"}
              <input
                type="date"
                value={taxForm.dateOfBirth}
                onInput={(e) =>
                  setTaxForm({ ...taxForm, dateOfBirth: e.currentTarget.value })
                }
              />
            </label>
            <label>
              {locale === "es"
                ? "Elección de joven trabajador"
                : "Young-worker election"}
              <select
                value={taxForm.youngWorkerElection}
                onChange={(e) =>
                  setTaxForm({
                    ...taxForm,
                    youngWorkerElection: e.currentTarget.value,
                  })
                }
              >
                <option value="not_applicable">
                  {locale === "es" ? "No aplica" : "Not applicable"}
                </option>
                <option value="claim">
                  {locale === "es" ? "Reclamar $40,000" : "Claim $40,000"}
                </option>
                <option value="decline">
                  {locale === "es" ? "No aplicar" : "Decline"}
                </option>
              </select>
            </label>
            <label>
              {locale === "es"
                ? "Porcentaje de ahorro para retiro (opcional)"
                : "Retirement savings percentage (optional)"}
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={taxForm.retirementSavingsPercent}
                onInput={(e) =>
                  setTaxForm({
                    ...taxForm,
                    retirementSavingsPercent: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label className="check-label">
              <input
                type="checkbox"
                checked={taxForm.veteran}
                onChange={(e) =>
                  setTaxForm({ ...taxForm, veteran: e.currentTarget.checked })
                }
              />
              {locale === "es" ? "Veterano elegible" : "Eligible veteran"}
            </label>
            <label className="check-label">
              <input
                type="checkbox"
                checked={taxForm.militarySpouseExempt}
                onChange={(e) =>
                  setTaxForm({
                    ...taxForm,
                    militarySpouseExempt: e.currentTarget.checked,
                  })
                }
              />
              {locale === "es"
                ? "Exención de cónyuge militar"
                : "Military spouse exemption"}
            </label>
            <label className="check-label">
              <input
                type="checkbox"
                checked={taxForm.governmentRetirementParticipant}
                onChange={(e) =>
                  setTaxForm({
                    ...taxForm,
                    governmentRetirementParticipant: e.currentTarget.checked,
                  })
                }
              />
              {locale === "es"
                ? "Sistema gubernamental de retiro"
                : "Government retirement system"}
            </label>
            <label className="check-label">
              <input
                type="checkbox"
                checked={taxForm.retirementSupplementPlan}
                onChange={(e) =>
                  setTaxForm({
                    ...taxForm,
                    retirementSupplementPlan: e.currentTarget.checked,
                  })
                }
              />
              {locale === "es"
                ? "Plan suplementario de retiro"
                : "Retirement supplementary plan"}
            </label>
            <label className="check-label">
              <input
                type="checkbox"
                checked={taxForm.retirementSavingsProgram}
                onChange={(e) =>
                  setTaxForm({
                    ...taxForm,
                    retirementSavingsProgram: e.currentTarget.checked,
                  })
                }
              />
              {locale === "es"
                ? "Programa de ahorro para retiro"
                : "Retirement savings program"}
            </label>
            <label>
              {locale === "es" ? "Nombre firmado" : "Signed name"}
              <input
                required
                value={taxForm.certifiedName}
                onChange={(e) =>
                  setTaxForm({
                    ...taxForm,
                    certifiedName: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Formulario recibido" : "Form received"}
              <input
                required
                type="date"
                value={taxForm.receivedOn}
                onInput={(e) =>
                  setTaxForm({
                    ...taxForm,
                    receivedOn: (e.target as HTMLInputElement).value,
                  })
                }
                onChange={(e) =>
                  setTaxForm({ ...taxForm, receivedOn: e.currentTarget.value })
                }
              />
            </label>
          </div>
          <label className="check-label">
            <input
              required
              type="checkbox"
              checked={taxForm.workerSigned}
              onChange={(e) =>
                setTaxForm({
                  ...taxForm,
                  workerSigned: e.currentTarget.checked,
                })
              }
            />
            {locale === "es"
              ? "Confirmo que el empleado firmó el formulario"
              : "I confirm the worker signed the form"}
          </label>
          <label className="check-label">
            <input
              required
              type="checkbox"
              checked={taxForm.developerAttestation}
              onChange={(e) =>
                setTaxForm({
                  ...taxForm,
                  developerAttestation: e.currentTarget.checked,
                })
              }
            />
            {locale === "es"
              ? "Atestiguo que transcribí fielmente el formulario recibido"
              : "I attest that I accurately transcribed the received form"}
          </label>
          <label>
            {locale === "es"
              ? "Copia del formulario firmado (opcional)"
              : "Signed form copy (optional)"}
            <input
              type="file"
              accept="application/pdf,image/png,image/jpeg"
              onChange={(e) =>
                setTaxPaperFile(e.currentTarget.files?.[0] ?? null)
              }
            />
            <small>
              {locale === "es"
                ? "Se guarda de forma privada en Cloudflare. No es requisito para procesar nómina."
                : "Stored privately in Cloudflare. It is not required to process payroll."}
            </small>
          </label>
          <button
            className="primary"
            disabled={
              busy ||
              !taxWorker ||
              !taxForm.workerSigned ||
              !taxForm.developerAttestation
            }
          >
            {locale === "es"
              ? "Registrar elección certificada"
              : "Record certified election"}
          </button>
        </form>
        {taxElections.length > 0 && (
          <div className="run-list">
            {taxElections.map((item) => (
              <article key={item.id}>
                <div>
                  <strong>
                    {item.formVersion} ·{" "}
                    {optionLabel(item.personalExemption, locale)}
                  </strong>
                  <p>
                    {formatDateRange(
                      item.effectiveFrom,
                      item.effectiveTo,
                      locale,
                      locale === "es" ? "vigente" : "current",
                    )}
                  </p>
                  <small>
                    {item.dependents + item.halfDependents} dependents ·{" "}
                    {item.deductionAllowances} allowances ·{" "}
                    {money(item.additionalWithholdingCents)} additional ·{" "}
                    {item.certificationMethod ?? "legacy"}
                  </small>
                </div>
                <span
                  className={`run-status ${item.certifiedAt ? "certified" : "draft"}`}
                >
                  {optionLabel(
                    item.certifiedAt ? "certified" : "draft",
                    locale,
                  )}
                </span>
                {item.certificateDocumentId ? (
                  <button
                    type="button"
                    onClick={() =>
                      void downloadDocument(
                        item.certificateDocumentId!,
                        `499-R-4-${item.effectiveFrom}.pdf`,
                      )
                    }
                  >
                    {locale === "es"
                      ? "Descargar certificado"
                      : "Download certificate"}
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void generateTaxCertificate(item)}
                  >
                    {locale === "es"
                      ? "Generar certificado"
                      : "Generate certificate"}
                  </button>
                )}
                {item.dependents + item.halfDependents > 8 && (
                  <section className="operation-panel">
                    <strong>
                      {locale === "es"
                        ? "Copia requerida por Hacienda · más de 8 dependientes"
                        : "Hacienda copy required · more than 8 dependents"}
                    </strong>
                    <p>
                      {locale === "es"
                        ? "Conserve la declaración de sustento, la copia del 499 R-4 y la evidencia de envío."
                        : "Retain the supporting statement, Form 499 R-4 copy, and submission evidence."}
                    </p>
                    <span
                      className={`run-status ${item.haciendaSubmissionStatus ?? "required"}`}
                    >
                      {optionLabel(
                        item.haciendaSubmissionStatus ?? "required",
                        locale,
                      )}
                    </span>
                    <button
                      type="button"
                      onClick={() => void loadTaxSubmission(item)}
                    >
                      {locale === "es"
                        ? "Gestionar envío"
                        : "Manage submission"}
                    </button>
                    {taxSubmissions[item.id] && (
                      <>
                        <label>
                          {locale === "es"
                            ? "Copia PDF para Hacienda"
                            : "PDF copy for Hacienda"}
                          <input
                            type="file"
                            accept="application/pdf"
                            onChange={(e) =>
                              setTaxSubmissionFiles((current) => ({
                                ...current,
                                [item.id]: e.currentTarget.files?.[0] ?? null,
                              }))
                            }
                          />
                        </label>
                        <button
                          type="button"
                          disabled={!taxSubmissionFiles[item.id]}
                          onClick={() => void uploadTaxSubmission(item)}
                        >
                          {locale === "es"
                            ? "Subir copia PDF"
                            : "Upload PDF copy"}
                        </button>
                        <div className="run-actions">
                          {["required", "returned"].includes(
                            taxSubmissions[item.id]?.case.status ?? "",
                          ) && (
                            <button
                              type="button"
                              onClick={() =>
                                void transitionTaxSubmission(item, "ready")
                              }
                            >
                              {locale === "es" ? "Marcar listo" : "Mark ready"}
                            </button>
                          )}
                          {taxSubmissions[item.id]?.case.status === "ready" && (
                            <button
                              type="button"
                              onClick={() =>
                                void transitionTaxSubmission(item, "submitted")
                              }
                            >
                              {locale === "es"
                                ? "Registrar envío"
                                : "Record submission"}
                            </button>
                          )}
                          {taxSubmissions[item.id]?.case.status ===
                            "submitted" && (
                            <button
                              type="button"
                              onClick={() =>
                                void transitionTaxSubmission(item, "confirmed")
                              }
                            >
                              {locale === "es" ? "Confirmar" : "Confirm"}
                            </button>
                          )}
                        </div>
                        {taxSubmissions[item.id]?.documents.map((document) => (
                          <small key={document.id}>
                            {document.documentType} · {document.fileName} ·{" "}
                            {document.contentSha256.slice(0, 12)}
                          </small>
                        ))}
                      </>
                    )}
                  </section>
                )}
              </article>
            ))}
          </div>
        )}
      </details>
      <details className="operation-panel">
        <summary>
          {locale === "es"
            ? "Tasas específicas del patrono"
            : "Employer-specific rates"}
        </summary>
        <p>
          {locale === "es"
            ? "SINOT, Seguro Social y Medicare provienen automáticamente de la biblioteca estatutaria. Registre aquí solamente las tasas que dependen de la determinación o circunstancias de este patrono."
            : "SINOT, Social Security, and Medicare come automatically from the statutory library. Enter only rates that depend on this employer's determination or circumstances."}
        </p>
        <div className="form-grid">
          <label>
            {locale === "es"
              ? "Desempleo de PR según notificación (puntos base)"
              : "PR unemployment per notice (basis points)"}
            <input
              value={rates.prUnemploymentRateBps}
              onChange={(e) =>
                setRates({
                  ...rates,
                  prUnemploymentRateBps: e.currentTarget.value,
                })
              }
            />
          </label>
          <label>
            {locale === "es"
              ? "FUTA después del crédito aplicable (puntos base)"
              : "FUTA after applicable credit (basis points)"}
            <input
              value={rates.futaRateBps}
              onChange={(e) =>
                setRates({ ...rates, futaRateBps: e.currentTarget.value })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Vigente desde" : "Effective date"}
            <input
              type="date"
              value={rates.effectiveFrom}
              onInput={(e) =>
                setRates({ ...rates, effectiveFrom: e.currentTarget.value })
              }
            />
          </label>
          <label className="check-label">
            <input
              type="checkbox"
              checked={rates.sinotEmployerPaysEmployeeShare}
              onChange={(e) =>
                setRates({
                  ...rates,
                  sinotEmployerPaysEmployeeShare: e.currentTarget.checked,
                })
              }
            />
            {locale === "es"
              ? "El patrono paga también la aportación del empleado a SINOT"
              : "Employer also pays the employee SINOT contribution"}
          </label>
        </div>
        <button
          type="button"
          className="primary"
          onClick={() => void saveRates()}
        >
          {locale === "es" ? "Guardar tasas" : "Save rates"}
        </button>
        <h3>
          {locale === "es"
            ? "Método anualizado para pagos suplementarios fluctuantes"
            : "Annualized method for fluctuating supplemental payments"}
        </h3>
        <p>
          {locale === "es"
            ? "Hacienda permite esta elección para comisiones, bonos gerenciales y pagos semejantes que fluctúan considerablemente. Una vez adoptada, debe usarse durante todo el año natural y no puede editarse."
            : "Hacienda permits this election for commissions, managerial bonuses, and similar payments that fluctuate considerably. Once adopted, it must be used for the entire calendar year and cannot be edited."}
        </p>
        <div className="form-grid">
          <label>
            {locale === "es" ? "Año contributivo" : "Tax year"}
            <input
              type="number"
              min="2017"
              max="2100"
              value={supplementalPolicyForm.taxYear}
              onInput={(e) =>
                setSupplementalPolicyForm({
                  ...supplementalPolicyForm,
                  taxYear: e.currentTarget.value,
                })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Razón documentada" : "Documented reason"}
            <input
              minLength={3}
              value={supplementalPolicyForm.reason}
              onChange={(e) =>
                setSupplementalPolicyForm({
                  ...supplementalPolicyForm,
                  reason: e.currentTarget.value,
                })
              }
            />
          </label>
        </div>
        <button
          type="button"
          disabled={
            busy ||
            !supplementalPolicyForm.reason.trim() ||
            supplementalPolicies.some(
              (item) => item.taxYear === Number(supplementalPolicyForm.taxYear),
            )
          }
          onClick={() => void adoptAnnualizedSupplemental()}
        >
          {locale === "es"
            ? "Adoptar método anualizado"
            : "Adopt annualized method"}
        </button>
        <div className="run-list">
          {supplementalPolicies.map((policy) => (
            <article key={policy.id}>
              <div>
                <strong>
                  {policy.taxYear} · {optionLabel(policy.method, locale)}
                </strong>
                <p>{policy.reason}</p>
                <small>
                  {locale === "es" ? "Adoptado" : "Adopted"}{" "}
                  {formatDate(policy.adoptedOn, locale)} ·{" "}
                  <a
                    className="official-source-link"
                    href={policy.sourceUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Hacienda
                  </a>
                </small>
              </div>
              <span className="run-status finalized">
                {locale === "es" ? "inmutable" : "immutable"}
              </span>
            </article>
          ))}
        </div>
      </details>
      <details className="operation-panel">
        <summary>
          {locale === "es"
            ? "Beneficios y otros costos patronales"
            : "Benefits and other employer costs"}
        </summary>
        <p>
          {locale === "es"
            ? "Defina costos vigentes para toda la compañía o para un empleado. Los importes calculados forman parte del costo real de cada nómina; los pagaderos también crean obligaciones."
            : "Define effective-dated costs for the whole company or one employee. Calculated amounts become part of each payroll's true employer cost; payable amounts also create liabilities."}
        </p>
        <form onSubmit={createEmployerCostRule}>
          <div className="form-grid">
            <label>
              {locale === "es" ? "Alcance" : "Scope"}
              <select
                value={employerCostForm.workerId}
                onChange={(e) =>
                  setEmployerCostForm({
                    ...employerCostForm,
                    workerId: e.currentTarget.value,
                  })
                }
              >
                <option value="">
                  {locale === "es" ? "Toda la compañía" : "Whole company"}
                </option>
                {workers
                  .filter((w) => w.workerType === "employee")
                  .map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.firstName} {w.lastName}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              {locale === "es" ? "Tipo" : "Type"}
              <select
                value={employerCostForm.costType}
                onChange={(e) =>
                  setEmployerCostForm({
                    ...employerCostForm,
                    costType: e.currentTarget.value as
                      | "benefit"
                      | "other_burden",
                  })
                }
              >
                <option value="benefit">
                  {locale === "es" ? "Beneficio" : "Benefit"}
                </option>
                <option value="other_burden">
                  {locale === "es" ? "Otra carga" : "Other burden"}
                </option>
              </select>
            </label>
            <label>
              {locale === "es" ? "Clave única" : "Unique key"}
              <input
                required
                pattern="[a-z0-9_]+"
                placeholder="medical_plan"
                value={employerCostForm.costKey}
                onChange={(e) =>
                  setEmployerCostForm({
                    ...employerCostForm,
                    costKey: e.currentTarget.value
                      .toLowerCase()
                      .replace(/[^a-z0-9_]/g, "_"),
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Descripción" : "Description"}
              <input
                required
                value={employerCostForm.description}
                onChange={(e) =>
                  setEmployerCostForm({
                    ...employerCostForm,
                    description: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Cálculo" : "Calculation"}
              <select
                value={employerCostForm.calculationType}
                onChange={(e) =>
                  setEmployerCostForm({
                    ...employerCostForm,
                    calculationType: e.currentTarget.value as
                      | "fixed"
                      | "percentage",
                  })
                }
              >
                <option value="fixed">
                  {locale === "es" ? "Fijo por nómina" : "Fixed per payroll"}
                </option>
                <option value="percentage">
                  {locale === "es" ? "Porcentaje" : "Percentage"}
                </option>
              </select>
            </label>
            {employerCostForm.calculationType === "fixed" ? (
              <label>
                {locale === "es" ? "Importe" : "Amount"}
                <input
                  required
                  type="number"
                  min="0"
                  step="0.01"
                  value={employerCostForm.amount}
                  onInput={(e) =>
                    setEmployerCostForm({
                      ...employerCostForm,
                      amount: e.currentTarget.value,
                    })
                  }
                />
              </label>
            ) : (
              <>
                <label>
                  {locale === "es" ? "Porcentaje" : "Percent"}
                  <input
                    required
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={employerCostForm.ratePercent}
                    onInput={(e) =>
                      setEmployerCostForm({
                        ...employerCostForm,
                        ratePercent: e.currentTarget.value,
                      })
                    }
                  />
                </label>
                <label>
                  {locale === "es" ? "Base" : "Basis"}
                  <select
                    value={employerCostForm.basis}
                    onChange={(e) =>
                      setEmployerCostForm({
                        ...employerCostForm,
                        basis: e.currentTarget.value as
                          | "gross_pay"
                          | "taxable_wages",
                      })
                    }
                  >
                    <option value="gross_pay">
                      {locale === "es" ? "Pago bruto" : "Gross pay"}
                    </option>
                    <option value="taxable_wages">
                      {locale === "es" ? "Salarios sujetos" : "Taxable wages"}
                    </option>
                  </select>
                </label>
              </>
            )}
            <label>
              {locale === "es" ? "Vigente desde" : "Effective from"}
              <input
                required
                type="date"
                value={employerCostForm.effectiveFrom}
                onInput={(e) =>
                  setEmployerCostForm({
                    ...employerCostForm,
                    effectiveFrom: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es"
                ? "Vigente hasta (opcional)"
                : "Effective to (optional)"}
              <input
                type="date"
                value={employerCostForm.effectiveTo}
                onInput={(e) =>
                  setEmployerCostForm({
                    ...employerCostForm,
                    effectiveTo: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label className="check-label">
              <input
                type="checkbox"
                checked={employerCostForm.createsLiability}
                onChange={(e) =>
                  setEmployerCostForm({
                    ...employerCostForm,
                    createsLiability: e.currentTarget.checked,
                  })
                }
              />
              {locale === "es"
                ? "Crear obligación pagadera"
                : "Create payable liability"}
            </label>
            {employerCostForm.createsLiability && (
              <label>
                {locale === "es" ? "Beneficiario" : "Payee"}
                <input
                  required
                  value={employerCostForm.liabilityPayee}
                  onChange={(e) =>
                    setEmployerCostForm({
                      ...employerCostForm,
                      liabilityPayee: e.currentTarget.value,
                    })
                  }
                />
              </label>
            )}
          </div>
          <button className="primary" disabled={busy}>
            {locale === "es" ? "Guardar costo patronal" : "Save employer cost"}
          </button>
        </form>
        <div className="run-list">
          {employerCostRules.map((rule) => (
            <article key={rule.id}>
              <div>
                <strong>{rule.description}</strong>
                <p>
                  {rule.workerName ??
                    (locale === "es"
                      ? "Toda la compañía"
                      : "Whole company")}{" "}
                  ·{" "}
                  {formatDateRange(
                    rule.effectiveFrom,
                    rule.effectiveTo,
                    locale,
                    locale === "es" ? "vigente" : "current",
                  )}
                </p>
                <small>
                  {rule.calculationType === "fixed"
                    ? money(rule.amountCents ?? 0)
                    : `${((rule.rateBps ?? 0) / 100).toFixed(2)}% ${optionLabel(rule.basis, locale)}`}{" "}
                  · {optionLabel(rule.costType, locale)}
                  {rule.createsLiability ? ` · ${rule.liabilityPayee}` : ""}
                </small>
              </div>
              <span className={`run-status ${rule.status}`}>
                {optionLabel(rule.status, locale)}
              </span>
              {!rule.effectiveTo && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void closeEmployerCostRule(rule)}
                >
                  {locale === "es" ? "Cerrar" : "Close"}
                </button>
              )}
            </article>
          ))}
        </div>
      </details>
      <details className="operation-panel">
        <summary>
          {locale === "es" ? "Bonos recurrentes" : "Recurring bonuses"}
        </summary>
        <p>
          {locale === "es"
            ? "Configure una sola vez el bono que debe entrar automáticamente en cada nómina regular vigente. Los términos originales se conservan; terminarlo requiere fecha y razón. El tratamiento de tarifa regular se aplica y recalcula en el servidor."
            : "Configure once the bonus that must automatically enter every effective regular payroll. Original terms are retained; ending it requires a date and reason. Regular-rate treatment is enforced and recalculated by the server."}
        </p>
        <form onSubmit={createRecurringBonus}>
          <div className="form-grid">
            <label>
              {locale === "es" ? "Empleado" : "Employee"}
              <select
                required
                value={recurringBonusForm.workerId}
                onChange={(e) =>
                  setRecurringBonusForm({
                    ...recurringBonusForm,
                    workerId: e.currentTarget.value,
                  })
                }
              >
                <option value="">—</option>
                {workers
                  .filter(
                    (worker) =>
                      worker.workerType === "employee" &&
                      ["active_incomplete", "active_complete"].includes(
                        worker.status,
                      ),
                  )
                  .map((worker) => (
                    <option key={worker.id} value={worker.id}>
                      {worker.firstName} {worker.lastName}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              {locale === "es"
                ? "Importe por nómina regular"
                : "Amount per regular payroll"}
              <input
                required
                type="number"
                min="0.01"
                step="0.01"
                value={recurringBonusForm.amount}
                onInput={(e) =>
                  setRecurringBonusForm({
                    ...recurringBonusForm,
                    amount: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Descripción" : "Description"}
              <input
                required
                minLength={3}
                value={recurringBonusForm.description}
                onChange={(e) =>
                  setRecurringBonusForm({
                    ...recurringBonusForm,
                    description: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es"
                ? "Tratamiento de tarifa regular"
                : "Regular-rate treatment"}
              <select
                value={recurringBonusForm.regularRateTreatment}
                onChange={(e) =>
                  setRecurringBonusForm({
                    ...recurringBonusForm,
                    regularRateTreatment: e.currentTarget
                      .value as RecurringBonusInstruction["regularRateTreatment"],
                  })
                }
              >
                <option value="included_nondiscretionary">
                  {locale === "es"
                    ? "Incluir · no discrecional"
                    : "Include · nondiscretionary"}
                </option>
                <option value="excluded_discretionary">
                  {locale === "es"
                    ? "Excluir · realmente discrecional"
                    : "Exclude · genuinely discretionary"}
                </option>
              </select>
            </label>
            <label>
              {locale === "es" ? "Vigente desde" : "Effective from"}
              <input
                required
                type="date"
                value={recurringBonusForm.effectiveFrom}
                onInput={(e) =>
                  setRecurringBonusForm({
                    ...recurringBonusForm,
                    effectiveFrom: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Razón documentada" : "Documented reason"}
              <input
                required
                minLength={3}
                value={recurringBonusForm.reason}
                onChange={(e) =>
                  setRecurringBonusForm({
                    ...recurringBonusForm,
                    reason: e.currentTarget.value,
                  })
                }
              />
            </label>
          </div>
          <button className="primary" disabled={busy}>
            {locale === "es"
              ? "Activar bono recurrente"
              : "Activate recurring bonus"}
          </button>
        </form>
        <div className="run-list">
          {recurringBonuses.map((item) => (
            <article key={item.id}>
              <div>
                <strong>
                  {item.workerName} · {money(item.amountCents)}
                </strong>
                <p>
                  {item.description} ·{" "}
                  {formatDateRange(
                    item.effectiveFrom,
                    item.effectiveTo,
                    locale,
                    locale === "es" ? "vigente" : "current",
                  )}
                </p>
                <small>
                  {item.regularRateTreatment === "included_nondiscretionary"
                    ? locale === "es"
                      ? "Incluido en tarifa regular"
                      : "Included in regular rate"
                    : locale === "es"
                      ? "Exclusión discrecional documentada"
                      : "Documented discretionary exclusion"}
                </small>
              </div>
              <span className={`run-status ${item.status}`}>
                {item.status === "active"
                  ? locale === "es"
                    ? "activo"
                    : "active"
                  : locale === "es"
                    ? "terminado"
                    : "ended"}
              </span>
              {item.status === "active" && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void endRecurringBonus(item)}
                >
                  {locale === "es" ? "Terminar" : "End"}
                </button>
              )}
            </article>
          ))}
        </div>
      </details>
      {showCompensationReimbursements && compensationReimbursementWorkerId && (
        <section className="operation-panel compensation-workflow-step compensation-workflow-step-2-detail">
          <h2>
            {locale === "es"
              ? "Gastos sometidos y reembolsos aprobados"
              : "Submitted expenses and approved reimbursements"}
          </h2>
          <p>
            {locale === "es"
              ? "Revise la evidencia de otra persona. Solamente una reclamación aprobada puede añadirse como pago no salarial."
              : "Review another person's evidence. Only an approved claim can be added as a non-wage payment."}
          </p>
          <div className="run-list">
            {expenseClaims
              .filter(
                (claim) => claim.workerId === compensationReimbursementWorkerId,
              )
              .map((claim) => {
                const selected = (manualEarnings[claim.workerId] ?? []).some(
                  (line) => line.expenseClaimId === claim.id,
                );
                return (
                  <article key={claim.id}>
                    <div>
                      <strong>
                        {claim.workerName} ·{" "}
                        {optionLabel(claim.category, locale)} ·{" "}
                        {money(claim.amountCents)}
                      </strong>
                      <p>
                        {claim.incurredOn} · {claim.businessPurpose}
                      </p>
                      <small>
                        {claim.reviewComment ?? claim.receiptFileName}
                      </small>
                    </div>
                    <span className={`run-status ${claim.status}`}>
                      {optionLabel(claim.status, locale)}
                    </span>
                    <div className="run-actions">
                      <button
                        type="button"
                        onClick={() => void viewExpenseReceipt(claim)}
                      >
                        {locale === "es" ? "Ver evidencia" : "View evidence"}
                      </button>
                      {claim.status === "submitted" && (
                        <>
                          <button
                            type="button"
                            onClick={() =>
                              void reviewExpense(claim, "approved")
                            }
                          >
                            {locale === "es" ? "Aprobar" : "Approve"}
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              void reviewExpense(claim, "rejected")
                            }
                          >
                            {locale === "es" ? "Rechazar" : "Reject"}
                          </button>
                        </>
                      )}
                      {claim.status === "approved" && (
                        <button
                          type="button"
                          disabled={selected}
                          onClick={() => addApprovedClaim(claim)}
                        >
                          {selected
                            ? locale === "es"
                              ? "Añadido"
                              : "Added"
                            : locale === "es"
                              ? "Añadir a nómina"
                              : "Add to payroll"}
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
          </div>
        </section>
      )}
      {showCompensationReimbursements &&
        compensationReimbursementWorkerId &&
        workers.some(
          (w) =>
            w.id === compensationReimbursementWorkerId &&
            w.workerType === "employee",
        ) && (
          <section className="earning-entry compensation-workflow-step compensation-workflow-step-2-detail">
            <h2>
              {locale === "es"
                ? "Ingresos y reembolsos por persona"
                : "Earnings and reimbursements by person"}
            </h2>
            <p>
              {locale === "es"
                ? "Añada cada concepto por separado. Bonos, comisiones y asignaciones no documentadas son salarios tributables. Use el tratamiento no salarial solamente para gastos de negocio sustentados bajo un plan con rendición de cuentas."
                : "Add every payment separately. Bonuses, commissions, and undocumented allowances are taxable wages. Use non-wage treatment only for substantiated business expenses under an accountable plan."}
            </p>
            {workers
              .flatMap((worker) =>
                (manualEarnings[worker.id] ?? [])
                  .filter(
                    (line) =>
                      line.regularRateTreatment === "included_nondiscretionary",
                  )
                  .map((line) => ({ worker, line })),
              )
              .map(({ worker, line }) => (
                <section
                  className="operation-panel"
                  key={"regular-rate-allocation-" + line.id}
                >
                  <h3>
                    {worker.firstName} {worker.lastName} ·{" "}
                    {locale === "es"
                      ? "Método de asignación de tarifa regular"
                      : "Regular-rate allocation method"}
                  </h3>
                  <label>
                    {locale === "es"
                      ? "Método respaldado por el plan"
                      : "Plan-supported method"}
                    <select
                      value={
                        line.regularRateAllocationStrategy ?? "equal_workweeks"
                      }
                      onChange={(e) =>
                        updateManualEarning(worker.id, line.id, {
                          regularRateAllocationStrategy: e.currentTarget
                            .value as ManualEarningLine["regularRateAllocationStrategy"],
                          regularRateAllocationWeightsJson:
                            e.currentTarget.value === "equal_workweeks"
                              ? ""
                              : line.regularRateAllocationWeightsJson,
                        })
                      }
                    >
                      <option value="equal_workweeks">
                        {locale === "es"
                          ? "Igual por semana de trabajo"
                          : "Equal by workweek"}
                      </option>
                      <option value="proportional_weekly_earnings">
                        {locale === "es"
                          ? "Proporcional a ingresos semanales documentados"
                          : "Proportional to documented weekly earnings"}
                      </option>
                    </select>
                  </label>
                  {line.regularRateAllocationStrategy ===
                    "proportional_weekly_earnings" && (
                    <label>
                      {locale === "es"
                        ? "Ingresos por semana (JSON)"
                        : "Weekly earnings (JSON)"}
                      <textarea
                        required
                        value={line.regularRateAllocationWeightsJson ?? ""}
                        onChange={(e) =>
                          updateManualEarning(worker.id, line.id, {
                            regularRateAllocationWeightsJson:
                              e.currentTarget.value,
                          })
                        }
                        placeholder={
                          '[{"from":"2026-01-04","to":"2026-01-10","earnings":"800.00"}]'
                        }
                      />
                      <small>
                        {locale === "es"
                          ? "Incluya exactamente cada semana generada entre las fechas ganadas, en orden, con sus ingresos documentados. El total del pago adicional se conserva al centavo y se distribuye proporcionalmente."
                          : "Include every generated workweek between the earned dates, in order, with documented earnings. The complete additional payment is conserved to the cent and allocated proportionally."}
                      </small>
                    </label>
                  )}
                </section>
              ))}
            {workers
              .filter(
                (w) =>
                  w.workerType === "employee" &&
                  w.id === compensationReimbursementWorkerId,
              )
              .map((w) => (
                <article key={w.id} className="operation-panel">
                  <div className="section-heading">
                    <strong>
                      {w.firstName} {w.lastName}
                    </strong>
                    <button
                      type="button"
                      onClick={() => addManualEarning(w.id)}
                    >
                      {locale === "es" ? "Añadir concepto" : "Add earning"}
                    </button>
                  </div>
                  {(manualEarnings[w.id] ?? []).length === 0 && (
                    <small>
                      {locale === "es"
                        ? "Sin conceptos manuales. Puede cargar tiempo aprobado o añadir pagos aquí."
                        : "No manual entries. Load approved time or add payments here."}
                    </small>
                  )}
                  {(manualEarnings[w.id] ?? []).map((line) => {
                    const option = earningOptions.find(
                      (item) => item.code === line.earningCode,
                    )!;
                    return (
                      <div className="form-grid" key={line.id}>
                        <label>
                          {locale === "es" ? "Concepto" : "Category"}
                          <select
                            value={line.earningCode}
                            disabled={Boolean(line.expenseClaimId)}
                            onChange={(e) => {
                              const selected = earningOptions.find(
                                  (item) =>
                                    item.code ===
                                    (e.currentTarget
                                      .value as ManualEarningCode),
                                )!,
                                excluded = [
                                  "overtime",
                                  "vacation",
                                  "sick",
                                  "holiday",
                                ].includes(selected.code);
                              updateManualEarning(w.id, line.id, {
                                earningCode: selected.code,
                                taxTreatment: selected.accountable
                                  ? line.taxTreatment
                                  : "taxable_wages",
                                regularRateTreatment: selected.accountable
                                  ? undefined
                                  : excluded
                                    ? "excluded_statutory"
                                    : "included_nondiscretionary",
                                regularRateAllocationStart:
                                  selected.accountable || excluded
                                    ? undefined
                                    : (line.regularRateAllocationStart ??
                                      run.periodStart),
                                regularRateAllocationEnd:
                                  selected.accountable || excluded
                                    ? undefined
                                    : (line.regularRateAllocationEnd ??
                                      run.periodEnd),
                                regularRateAllocationMethod:
                                  selected.accountable || excluded
                                    ? undefined
                                    : "equal_workweeks",
                              });
                            }}
                          >
                            {earningOptions.map((item) => (
                              <option key={item.code} value={item.code}>
                                {locale === "es" ? item.es : item.en}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          {locale === "es" ? "Cantidad" : "Amount"}
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            disabled={Boolean(line.expenseClaimId)}
                            value={line.amount}
                            onInput={(e) =>
                              updateManualEarning(w.id, line.id, {
                                amount: e.currentTarget.value,
                              })
                            }
                            placeholder="0.00"
                          />
                        </label>
                        <label>
                          {locale === "es"
                            ? "Trabajo/proyecto CFSE"
                            : "CFSE job/project"}
                          <select
                            value={line.projectId ?? ""}
                            onChange={(e) =>
                              updateManualEarning(w.id, line.id, {
                                projectId: e.currentTarget.value || undefined,
                              })
                            }
                          >
                            <option value="">
                              {locale === "es"
                                ? "Clasificación del trabajador"
                                : "Worker classification"}
                            </option>
                            {payrollProjects
                              .filter((project) => project.status === "active")
                              .map((project) => (
                                <option key={project.id} value={project.id}>
                                  {project.name}
                                </option>
                              ))}
                          </select>
                        </label>
                        <label>
                          {locale === "es"
                            ? "Tratamiento contributivo"
                            : "Tax treatment"}
                          <select
                            value={line.taxTreatment}
                            disabled={!line.expenseClaimId}
                            onChange={(e) =>
                              updateManualEarning(w.id, line.id, {
                                taxTreatment: e.currentTarget
                                  .value as ManualEarningLine["taxTreatment"],
                              })
                            }
                          >
                            <option value="taxable_wages">
                              {locale === "es"
                                ? "Salario tributable PR/FICA"
                                : "Taxable PR/FICA wages"}
                            </option>
                            {line.expenseClaimId && (
                              <option value="accountable_plan_nontaxable">
                                {locale === "es"
                                  ? "Plan con rendición de cuentas · no salarial"
                                  : "Accountable plan · non-wage"}
                              </option>
                            )}
                          </select>
                        </label>
                        {line.taxTreatment === "taxable_wages" && (
                          <label>
                            {locale === "es"
                              ? "Tratamiento para tarifa regular"
                              : "Regular-rate treatment"}
                            <select
                              value={line.regularRateTreatment ?? ""}
                              onChange={(e) =>
                                updateManualEarning(w.id, line.id, {
                                  regularRateTreatment: e.currentTarget
                                    .value as ManualEarningLine["regularRateTreatment"],
                                })
                              }
                            >
                              <option value="">—</option>
                              <option value="included_nondiscretionary">
                                {locale === "es"
                                  ? "Incluir · no discrecional"
                                  : "Include · nondiscretionary"}
                              </option>
                              {line.earningCode === "bonus" && (
                                <option value="excluded_discretionary">
                                  {locale === "es"
                                    ? "Excluir · bono realmente discrecional"
                                    : "Exclude · genuinely discretionary bonus"}
                                </option>
                              )}
                              {[
                                "overtime",
                                "vacation",
                                "sick",
                                "holiday",
                              ].includes(line.earningCode) && (
                                <option value="excluded_statutory">
                                  {locale === "es"
                                    ? "Excluir · prima/pago estatutario"
                                    : "Exclude · statutory premium/payment"}
                                </option>
                              )}
                            </select>
                            <small>
                              {locale === "es"
                                ? "La etiqueta 'bono' no basta para excluirlo; la discreción debe existir hasta cerca del pago."
                                : "A 'bonus' label alone does not exclude it; discretion must remain until near payment."}
                            </small>
                          </label>
                        )}
                        {line.regularRateTreatment ===
                          "included_nondiscretionary" && (
                          <>
                            <label>
                              {locale === "es"
                                ? "Inicio del período ganado"
                                : "Earned-period start"}
                              <input
                                required
                                type="date"
                                value={line.regularRateAllocationStart ?? ""}
                                onInput={(e) =>
                                  updateManualEarning(w.id, line.id, {
                                    regularRateAllocationStart:
                                      e.currentTarget.value,
                                  })
                                }
                              />
                            </label>
                            <label>
                              {locale === "es"
                                ? "Fin del período ganado"
                                : "Earned-period end"}
                              <input
                                required
                                type="date"
                                value={line.regularRateAllocationEnd ?? ""}
                                onInput={(e) =>
                                  updateManualEarning(w.id, line.id, {
                                    regularRateAllocationEnd:
                                      e.currentTarget.value,
                                  })
                                }
                              />
                            </label>
                            <label>
                              {locale === "es" ? "Asignación" : "Allocation"}
                              <select
                                value={
                                  line.regularRateAllocationMethod ??
                                  "equal_workweeks"
                                }
                                onChange={(e) =>
                                  updateManualEarning(w.id, line.id, {
                                    regularRateAllocationMethod: e.currentTarget
                                      .value as "equal_workweeks",
                                  })
                                }
                              >
                                <option value="equal_workweeks">
                                  {locale === "es"
                                    ? "Igual por semana de trabajo"
                                    : "Equal by workweek"}
                                </option>
                              </select>
                              <small>
                                {locale === "es"
                                  ? "El sistema recalcula automáticamente la prima adicional en cada semana con horas extra."
                                  : "The system automatically recalculates the additional premium for each overtime workweek."}
                              </small>
                            </label>
                          </>
                        )}
                        <label>
                          {locale === "es"
                            ? "Descripción / propósito"
                            : "Description / business purpose"}
                          <input
                            disabled={Boolean(line.expenseClaimId)}
                            required={
                              line.taxTreatment ===
                                "accountable_plan_nontaxable" ||
                              line.regularRateTreatment ===
                                "excluded_discretionary"
                            }
                            value={line.description}
                            onChange={(e) =>
                              updateManualEarning(w.id, line.id, {
                                description: e.currentTarget.value,
                              })
                            }
                          />
                        </label>
                        <button
                          type="button"
                          onClick={() => removeManualEarning(w.id, line.id)}
                        >
                          {locale === "es" ? "Eliminar" : "Remove"}
                        </button>
                      </div>
                    );
                  })}
                </article>
              ))}
          </section>
        )}
      {diagnosticIssues.length > 0 && (
        <section className="operation-panel compensation-related-result">
          {diagnosticIssues.some(
            (issue) => (issue.status ?? "open") === "open",
          ) && (
            <>
              <h2>
                {locale === "es"
                  ? "Diagnósticos pendientes"
                  : "Pending diagnostics"}
              </h2>
              <p>
                {locale === "es"
                  ? "Revise solamente los asuntos abiertos antes de calcular."
                  : "Review only open items before calculation."}
              </p>
              <div className="run-list">
                {diagnosticIssues
                  .filter((issue) => (issue.status ?? "open") === "open")
                  .map((issue) => (
                    <article key={`action-${issue.id}`}>
                      <div>
                        <strong>
                          {(issue.workerName ??
                          workers.find((worker) => worker.id === issue.workerId)
                            ?.firstName)
                            ? `${issue.workerName ?? `${workers.find((worker) => worker.id === issue.workerId)?.firstName ?? ""} ${workers.find((worker) => worker.id === issue.workerId)?.lastName ?? ""}`.trim()}: `
                            : ""}
                          {locale === "es" ? issue.messageEs : issue.messageEn}
                        </strong>
                        <small>
                          {issue.code} · {optionLabel(issue.severity, locale)} ·{" "}
                          {locale === "es" ? "Pendiente" : "Open"}
                        </small>
                        {issue.events?.map((event) => (
                          <small key={event.id}>
                            {optionLabel(event.action, locale)} · {event.reason}{" "}
                            · {formatDateTime(event.occurredAt, locale)}
                          </small>
                        ))}
                      </div>
                      <div className="run-actions">
                        <button
                          type="button"
                          onClick={() => fixDiagnostic(issue)}
                        >
                          {locale === "es" ? "Corregir" : "Fix"}
                        </button>
                        <button
                          type="button"
                          disabled={diagnosticActionBusyId === issue.id}
                          onClick={() => void actOnDiagnostic(issue, "explain")}
                        >
                          {locale === "es" ? "Explicar" : "Explain"}
                        </button>
                        {(issue.status ?? "open") === "open" && (
                          <button
                            type="button"
                            disabled={diagnosticActionBusyId !== null}
                            onClick={() => void acceptDiagnosticAsIs(issue)}
                          >
                            {diagnosticActionBusyId === issue.id ? (
                              <LoaderCircle className="spin" />
                            ) : locale === "es" ? (
                              "Aceptar como está"
                            ) : (
                              "Accept as is"
                            )}
                          </button>
                        )}
                        {issue.severity === "warning" &&
                          (issue.status ?? "open") === "open" && (
                            <button
                              type="button"
                              disabled={diagnosticActionBusyId === issue.id}
                              onClick={() =>
                                void actOnDiagnostic(issue, "approve_exception")
                              }
                            >
                              {locale === "es"
                                ? "Aprobar excepción"
                                : "Approve exception"}
                            </button>
                          )}
                      </div>
                    </article>
                  ))}
              </div>
            </>
          )}
          {diagnosticIssues.some(
            (issue) => (issue.status ?? "open") !== "open",
          ) && (
            <details>
              <summary>
                {locale === "es"
                  ? `Historial de diagnósticos resueltos (${diagnosticIssues.filter((issue) => (issue.status ?? "open") !== "open").length})`
                  : `Resolved diagnostic history (${diagnosticIssues.filter((issue) => (issue.status ?? "open") !== "open").length})`}
              </summary>
              <p>
                {locale === "es"
                  ? "Estos asuntos ya fueron decididos. Se conservan únicamente como evidencia y no requieren otra acción."
                  : "These items were already decided. They remain only as evidence and require no further action."}
              </p>
              <div className="run-list">
                {diagnosticIssues
                  .filter((issue) => (issue.status ?? "open") !== "open")
                  .map((issue) => (
                    <article key={`history-${issue.id}`}>
                      <div>
                        <strong>
                          {locale === "es" ? issue.messageEs : issue.messageEn}
                        </strong>
                        <small>
                          {optionLabel(issue.severity, locale)} ·{" "}
                          {locale === "es" ? "Reconocido" : "Acknowledged"}
                        </small>
                        {issue.events?.map((event) => (
                          <small key={event.id}>
                            {optionLabel(event.action, locale)} ·{" "}
                            {event.action === "acknowledged"
                              ? locale === "es"
                                ? "Aceptado como está"
                                : "Accepted as is"
                              : event.reason}{" "}
                            · {formatDateTime(event.occurredAt, locale)}
                          </small>
                        ))}
                      </div>
                    </article>
                  ))}
              </div>
            </details>
          )}
        </section>
      )}
      {timeImportIssues.length > 0 && (
        <section className="operation-panel compensation-related-result">
          <h2>
            {locale === "es"
              ? "Acciones requeridas para preparar ingresos"
              : "Actions required to prepare earnings"}
          </h2>
          <div className="run-list">
            {timeImportIssues.map((issue, index) => {
              const messages: Record<string, [string, string]> = {
                  salary_rate_change_within_period: [
                    "Divida la nómina o registre el importe salarial revisado para el período parcial.",
                    "Split the payroll or record the reviewed salary amount for the partial period.",
                  ],
                  partial_period_salary_requires_review: [
                    "Revise manualmente el salario de inicio o terminación antes de calcular.",
                    "Review starting or terminating salary manually before calculation.",
                  ],
                  nonexempt_salary_requires_hourly_equivalent: [
                    "Configure el equivalente por hora y el tratamiento de horas extra para este empleado no exento.",
                    "Configure the hourly equivalent and overtime treatment for this nonexempt employee.",
                  ],
                  salary_overtime_classification_missing_or_changed: [
                    "Registre una clasificación de horas extra que cubra todo el período.",
                    "Record an overtime classification covering the entire period.",
                  ],
                  salary_frequency_mismatch: [
                    "Alinee la frecuencia del salario con la frecuencia de esta nómina.",
                    "Match the salary frequency to this payroll run frequency.",
                  ],
                  no_payroll_earnings: [
                    "Configure salario o cargue tiempo aprobado antes de calcular.",
                    "Configure salary or load approved time before calculation.",
                  ],
                  missing_locked_timesheet: [
                    "Apruebe y cierre la hoja de tiempo que cubre el período.",
                    "Approve and lock the timesheet covering the period.",
                  ],
                  timesheet_coverage_gap: [
                    "Corrija el espacio entre hojas de tiempo cerradas.",
                    "Correct the gap between locked timesheets.",
                  ],
                },
                worker = workers.find((item) => item.id === issue.workerId),
                message =
                  messages[issue.code]?.[locale === "es" ? 0 : 1] ??
                  issue.code.replaceAll("_", " ");
              return (
                <article key={`${issue.workerId}-${issue.code}-${index}`}>
                  <div>
                    <strong>
                      {worker
                        ? `${worker.firstName} ${worker.lastName ?? ""}`
                        : locale === "es"
                          ? "Nómina"
                          : "Payroll"}
                    </strong>
                    <p>{message}</p>
                  </div>
                  <span className="run-status draft">
                    {issue.code.replaceAll("_", " ")}
                  </span>
                </article>
              );
            })}
          </div>
        </section>
      )}
      {preparedTime && (
        <section
          id="prepared-earnings-result"
          className="operation-panel compensation-related-result"
        >
          <h2>
            {locale === "es"
              ? "Ingresos preparados con procedencia"
              : "Prepared earnings with provenance"}
          </h2>
          <div className="run-list">
            {preparedTime.items.flatMap((item) =>
              item.earnings.map((earning, index) => (
                <article
                  key={`${item.workerId}-${earning.earningType}-${index}`}
                >
                  <div>
                    <strong>
                      {workers.find((worker) => worker.id === item.workerId)
                        ?.firstName ?? item.workerId}{" "}
                      · {earning.earningType}
                    </strong>
                    <p>
                      {earning.hoursHundredths === null
                        ? locale === "es"
                          ? "Salario del período"
                          : "Period salary"
                        : `${(earning.hoursHundredths / 100).toFixed(2)} h · ${money(earning.rateCents ?? 0)}`}{" "}
                      · {money(earning.amountCents)}
                    </p>
                    <small>
                      {earning.sourceTimesheetId
                        ? `timesheet ${earning.sourceTimesheetId}`
                        : earning.sourceLeaveRequestId
                          ? `leave ${earning.sourceLeaveRequestId}`
                          : earning.sourceHolidayId
                            ? `holiday ${earning.sourceHolidayId}`
                            : earning.sourceRuleVersion.startsWith("SALARY:")
                              ? "salary compensation"
                              : "manual"}{" "}
                      · {earning.sourceRuleVersion}
                    </small>
                  </div>
                </article>
              )),
            )}
          </div>
        </section>
      )}
      {preparedFinalPay && (
        <section className="operation-panel compensation-related-result">
          <h2>
            {locale === "es"
              ? "Liquidación final vinculada"
              : "Linked final-pay settlement"}
          </h2>
          <p>
            {locale === "es"
              ? "La nómina suplementaria queda vinculada al período de empleo exacto. Los balances se comprueban nuevamente al finalizar y solo entonces se saldan en el mayor de licencias."
              : "The supplemental payroll is linked to the exact employment period. Balances are checked again at finalization and only then settled in the leave ledger."}
          </p>
          <div className="run-list">
            {preparedFinalPay.plans.map((plan) => (
              <article key={plan.id}>
                <div>
                  <strong>
                    {plan.workerName} · {plan.endDate}
                  </strong>
                  <p>
                    {locale === "es" ? "Vence" : "Due"} {plan.dueOn}
                    {plan.late
                      ? ` · ${locale === "es" ? "fecha de pago tardía" : "late pay date"}`
                      : ""}{" "}
                    · {plan.leaveDisposition} · {plan.deductionDisposition}
                  </p>
                  {plan.balances.map((balance) => (
                    <small key={balance.policyId}>
                      {balance.policyName} · {balance.leaveType} ·{" "}
                      {balance.hours.toFixed(4)} h
                      {plan.rateCents ? ` × ${money(plan.rateCents)}` : ""}
                    </small>
                  ))}
                  {plan.issues.map((issue) => (
                    <small className="form-error" key={issue}>
                      {issue.replaceAll("_", " ")}
                    </small>
                  ))}
                </div>
                <span
                  className={`run-status ${plan.issues.length ? "pending" : "calculated"}`}
                >
                  {plan.issues.length
                    ? locale === "es"
                      ? "Requiere corrección"
                      : "Needs correction"
                    : locale === "es"
                      ? "Preparado"
                      : "Prepared"}
                </span>
              </article>
            ))}
          </div>
        </section>
      )}
      <details className="operation-panel">
        <summary>
          {locale === "es"
            ? "Bono de Navidad de Puerto Rico"
            : "Puerto Rico Christmas Bonus"}
        </summary>
        <p>
          {locale === "es"
            ? "Período oficial: 1 de octubre al 30 de septiembre. El cálculo distingue fecha de contratación, horas, tamaño patronal, primer año y exención documentada."
            : "Official period: October 1 through September 30. Calculation distinguishes hire date, hours, employer size, first year, and documented exemption."}
        </p>
        <form onSubmit={createBonusCycle}>
          <div className="form-grid">
            <label>
              {locale === "es" ? "Año del bono" : "Bonus year"}
              <input
                required
                type="number"
                min="2017"
                value={bonusForm.coverageYear}
                onInput={(e) =>
                  setBonusForm({
                    ...bonusForm,
                    coverageYear: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Cantidad de empleados" : "Employee count"}
              <input
                required
                type="number"
                min="1"
                value={bonusForm.employeeCount}
                onInput={(e) =>
                  setBonusForm({
                    ...bonusForm,
                    employeeCount: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Estado de exención" : "Exemption status"}
              <select
                value={bonusForm.exemptionStatus}
                onChange={(e) =>
                  setBonusForm({
                    ...bonusForm,
                    exemptionStatus: e.currentTarget.value,
                  })
                }
              >
                <option value="none">{optionLabel("none", locale)}</option>
                <option value="applied">
                  {optionLabel("applied", locale)}
                </option>
                <option value="granted">
                  {optionLabel("granted", locale)}
                </option>
                <option value="denied">{optionLabel("denied", locale)}</option>
              </select>
            </label>
            <label>
              {locale === "es"
                ? "Referencia de exención"
                : "Exemption reference"}
              <input
                value={bonusForm.exemptionReference}
                onChange={(e) =>
                  setBonusForm({
                    ...bonusForm,
                    exemptionReference: e.currentTarget.value,
                  })
                }
              />
            </label>
          </div>
          <button className="primary" disabled={busy}>
            {locale === "es" ? "Crear ciclo" : "Create cycle"}
          </button>
        </form>
        <h3>
          {locale === "es"
            ? "Ajuste documentado de horas o salarios"
            : "Documented hours or earnings adjustment"}
        </h3>
        <div className="form-grid">
          <label>
            {locale === "es" ? "Ciclo" : "Cycle"}
            <select
              value={bonusAdjustment.cycleId}
              onChange={(e) =>
                setBonusAdjustment({
                  ...bonusAdjustment,
                  cycleId: e.currentTarget.value,
                })
              }
            >
              <option value="">—</option>
              {bonusCycles
                .filter((c) => ["draft", "calculated"].includes(c.status))
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.coverageYear}
                  </option>
                ))}
            </select>
          </label>
          <label>
            {locale === "es" ? "Empleado" : "Employee"}
            <select
              value={bonusAdjustment.workerId}
              onChange={(e) =>
                setBonusAdjustment({
                  ...bonusAdjustment,
                  workerId: e.currentTarget.value,
                })
              }
            >
              <option value="">—</option>
              {workers
                .filter((w) => w.workerType === "employee")
                .map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.firstName} {w.lastName}
                  </option>
                ))}
            </select>
          </label>
          <label>
            {locale === "es" ? "Horas (+/-)" : "Hours (+/-)"}
            <input
              type="number"
              step="0.01"
              value={bonusAdjustment.hours}
              onInput={(e) =>
                setBonusAdjustment({
                  ...bonusAdjustment,
                  hours: e.currentTarget.value,
                })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Salarios (+/-)" : "Earnings (+/-)"}
            <input
              type="number"
              step="0.01"
              value={bonusAdjustment.earnings}
              onInput={(e) =>
                setBonusAdjustment({
                  ...bonusAdjustment,
                  earnings: e.currentTarget.value,
                })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Razón" : "Reason"}
            <input
              value={bonusAdjustment.reason}
              onChange={(e) =>
                setBonusAdjustment({
                  ...bonusAdjustment,
                  reason: e.currentTarget.value,
                })
              }
            />
          </label>
          <label className="check-label">
            <input
              type="checkbox"
              checked={bonusAdjustment.dockWorker}
              onChange={(e) =>
                setBonusAdjustment({
                  ...bonusAdjustment,
                  dockWorker: e.currentTarget.checked,
                })
              }
            />
            {locale === "es" ? "Trabajador de muelles" : "Dock worker"}
          </label>
        </div>
        <button
          type="button"
          className="primary"
          disabled={
            !bonusAdjustment.cycleId ||
            !bonusAdjustment.workerId ||
            !bonusAdjustment.reason
          }
          onClick={() => void addBonusAdjustment()}
        >
          {locale === "es" ? "Guardar ajuste" : "Save adjustment"}
        </button>
        <label>
          {locale === "es"
            ? "Evidencia PDF de exención (máx. 10 MB)"
            : "Exemption evidence PDF (max 10 MB)"}
          <input
            type="file"
            accept="application/pdf"
            onChange={(e) => setBonusFile(e.currentTarget.files?.[0] ?? null)}
          />
        </label>
        <div className="run-list">
          {bonusCycles.map((cycle) => (
            <article key={cycle.id}>
              <div>
                <strong>
                  {cycle.coverageYear} · {cycle.coverageStart} —{" "}
                  {cycle.coverageEnd}
                </strong>
                <p>
                  {locale === "es" ? "Pagar no más tarde de" : "Pay by"}{" "}
                  {cycle.paymentDeadline} · {cycle.employeeCount}{" "}
                  {locale === "es" ? "empleados" : "employees"} ·{" "}
                  {cycle.ruleVersion}
                </p>
                <small>
                  {locale === "es" ? "Exención" : "Exemption"}:{" "}
                  {cycle.exemptionStatus} {cycle.exemptionReference ?? ""}{" "}
                  {cycle.hasExemptionDocument ? "· PDF" : ""}
                  {cycle.payrollRunId
                    ? ` · ${locale === "es" ? "nómina" : "payroll"} ${cycle.payrollRunId.slice(0, 8)} · ${cycle.withholdingMethod}`
                    : ""}
                  {cycle.paymentDate
                    ? ` · ${locale === "es" ? "pagado" : "paid"} ${cycle.paymentDate}`
                    : ""}
                </small>
              </div>
              <span className={`run-status ${cycle.status}`}>
                {optionLabel(cycle.status, locale)}
              </span>
              <div className="run-actions">
                {["draft", "calculated"].includes(cycle.status) && (
                  <button onClick={() => void bonusAction(cycle, "calculate")}>
                    {locale === "es" ? "Calcular" : "Calculate"}
                  </button>
                )}
                {cycle.status === "calculated" && (
                  <button onClick={() => void bonusAction(cycle, "approved")}>
                    {locale === "es" ? "Aprobar" : "Approve"}
                  </button>
                )}
                {cycle.status === "approved" && !cycle.payrollRunId && (
                  <button
                    className="primary"
                    onClick={() => void createBonusPayroll(cycle)}
                  >
                    {locale === "es"
                      ? "Crear nómina del bono"
                      : "Create bonus payroll"}
                  </button>
                )}
                {["draft", "calculated"].includes(cycle.status) &&
                  bonusFile && (
                    <button onClick={() => void uploadBonusEvidence(cycle)}>
                      {locale === "es" ? "Subir evidencia" : "Upload evidence"}
                    </button>
                  )}
              </div>
              {bonusItems[cycle.id]?.map((item) => (
                <div key={item.id}>
                  <strong>
                    {item.workerName}: {money(item.bonusCents)}
                  </strong>
                  <small>
                    {(item.qualifyingHoursHundredths / 100).toFixed(2)} h /{" "}
                    {item.requiredHours} h ·{" "}
                    {money(item.qualifyingEarningsCents)} · {item.rateBps / 100}
                    % · {item.eligible ? "eligible" : "not eligible"}
                  </small>
                </div>
              ))}
            </article>
          ))}
        </div>
      </details>
      {false && (
        <>
          <details className="operation-panel">
            <summary>
              {locale === "es"
                ? "Pagos por servicios profesionales"
                : "Professional-services payments"}
            </summary>
            <form onSubmit={createPayment}>
              <div className="form-grid">
                <label>
                  {locale === "es" ? "Contratista" : "Contractor"}
                  <select
                    required
                    value={payment.workerId}
                    onChange={(e) =>
                      void loadContractorCompensation(
                        e.currentTarget.value,
                        payment.paymentDate,
                      )
                    }
                  >
                    <option value="">—</option>
                    {workers
                      .filter((w) => w.workerType === "professional_services")
                      .map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.firstName} {w.lastName}
                        </option>
                      ))}
                  </select>
                </label>
                <label>
                  {locale === "es" ? "Fecha de pago" : "Payment date"}
                  <input
                    required
                    type="date"
                    value={payment.paymentDate}
                    onInput={(e) =>
                      void loadContractorCompensation(
                        payment.workerId,
                        e.currentTarget.value,
                      )
                    }
                  />
                </label>
                <label>
                  {locale === "es" ? "Descripción" : "Description"}
                  <input
                    required
                    value={payment.description}
                    onChange={(e) =>
                      setPayment({
                        ...payment,
                        description: e.currentTarget.value,
                      })
                    }
                  />
                </label>
                <label>
                  {locale === "es" ? "Importe bruto" : "Gross amount"}
                  <input
                    required
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={payment.gross}
                    readOnly
                  />
                </label>
                <label>
                  {locale === "es"
                    ? "Inicio del período (opcional)"
                    : "Period start (optional)"}
                  <input
                    type="date"
                    value={payment.periodStart}
                    onInput={(e) =>
                      setPayment({
                        ...payment,
                        periodStart: e.currentTarget.value,
                      })
                    }
                  />
                </label>
                <label>
                  {locale === "es"
                    ? "Fin del período (opcional)"
                    : "Period end (optional)"}
                  <input
                    type="date"
                    value={payment.periodEnd}
                    onInput={(e) =>
                      setPayment({
                        ...payment,
                        periodEnd: e.currentTarget.value,
                      })
                    }
                  />
                </label>
                <label>
                  {locale === "es" ? "Horas" : "Hours"}
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    required={payment.compensationType === "hourly"}
                    disabled={payment.compensationType !== "hourly"}
                    value={payment.hoursHundredths}
                    onInput={(e) => {
                      const hours = e.currentTarget.value;
                      setPayment({
                        ...payment,
                        hoursHundredths: hours,
                        gross:
                          hours && payment.rate
                            ? (Number(hours) * Number(payment.rate)).toFixed(2)
                            : "",
                      });
                    }}
                  />
                </label>
                {["daily", "weekly"].includes(payment.compensationType) && (
                  <label>
                    {payment.compensationType === "daily"
                      ? locale === "es" ? "Días trabajados" : "Worked days"
                      : locale === "es" ? "Semanas" : "Weeks"}
                    <input
                      type="number"
                      min="1"
                      step="1"
                      required
                      value={payment.serviceUnits}
                      onInput={(e) => {
                        const serviceUnits = e.currentTarget.value;
                        setPayment({
                          ...payment,
                          serviceUnits,
                          gross:
                            serviceUnits && payment.rate
                              ? (
                                  Number(serviceUnits) * Number(payment.rate)
                                ).toFixed(2)
                              : "",
                        });
                      }}
                    />
                  </label>
                )}
                <label>
                  {locale === "es" ? "Tarifa vigente" : "Effective rate"}
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={payment.rate}
                    readOnly
                  />
                </label>
              </div>
              <button
                className="primary"
                disabled={
                  busy ||
                  !payment.workerId ||
                  !payment.compensationType ||
                  !payment.gross ||
                  (payment.compensationType === "hourly" &&
                    !payment.hoursHundredths) ||
                  (["daily", "weekly"].includes(payment.compensationType) &&
                    !payment.serviceUnits)
                }
              >
                <CircleDollarSign />
                {locale === "es"
                  ? "Calcular y crear borrador"
                  : "Calculate and create draft"}
              </button>
            </form>
          </details>
          <section className="data-section compensation-workflow-step compensation-workflow-step-3-contractor">
            <h2 className="subheading">
              {locale === "es"
                ? "Servicios profesionales incluidos en los períodos"
                : "Professional services included in periods"}
            </h2>
            <p className="field-note">
              {locale === "es"
                ? "Estos importes pagan el servicio del contratista; no son reembolsos de gastos."
                : "These amounts pay for contractor services; they are not expense reimbursements."}
            </p>
            <ResponsiveDataTable
              label={
                locale === "es"
                  ? "Tabla de administración de pagos de contratistas"
                  : "Contractor payment administration table"
              }
              caption={
                locale === "es"
                  ? "Contratista, pago, base de cálculo, retención, neto, estado y acciones"
                  : "Contractor, payment, calculation basis, withholding, net, status, and actions"
              }
              columns={
                locale === "es"
                  ? [
                      "Contratista y servicio",
                      "Fecha y cálculo",
                      "Bruto y exención",
                      "Retención y neto",
                      "Estado",
                      "Acciones",
                    ]
                  : [
                      "Contractor and service",
                      "Date and calculation",
                      "Gross and exemption",
                      "Withholding and net",
                      "Status",
                      "Actions",
                    ]
              }
              rows={payments.map((item) => {
                const calculation = item.compensationType
                  ? optionLabel(item.compensationType, locale)
                  : locale === "es"
                    ? "Histórico"
                    : "Legacy";
                return {
                  key: item.id,
                  cells: [
                    <>
                      <strong>{item.workerName}</strong>
                      <small>{item.description}</small>
                    </>,
                    <>
                      <span>{formatDate(item.paymentDate, locale)}</span>
                      <small>
                        {calculation}
                        {item.hoursHundredths
                          ? ` · ${(item.hoursHundredths / 100).toFixed(2)} h × ${money(item.rateCents ?? 0)}`
                          : ` · ${money(item.rateCents ?? item.grossCents)}`}
                      </small>
                    </>,
                    <>
                      <span>{money(item.grossCents)}</span>
                      <small>
                        {locale === "es" ? "Exención" : "Exemption"}:{" "}
                        {money(item.exemptionAppliedCents)} ·{" "}
                        {locale === "es" ? "Sujeto" : "Subject"}:{" "}
                        {money(item.subjectCents)}
                      </small>
                    </>,
                    <>
                      <span>
                        {money(item.withholdingCents)} ·{" "}
                        {(item.withholdingRateBps / 100).toFixed(2)}%
                      </span>
                      <small>
                        {locale === "es" ? "Neto" : "Net"}:{" "}
                        {money(item.netCents)}
                      </small>
                    </>,
                    <span className={`run-status ${item.status}`}>
                      {optionLabel(item.status, locale)}
                    </span>,
                    contractorPaymentActions(item),
                  ],
                  card: (
                    <article>
                      <div>
                        <strong>{item.workerName}</strong>
                        <small>
                          {formatDate(item.paymentDate, locale)} ·{" "}
                          {item.description}
                        </small>
                      </div>
                      <p>
                        {calculation}
                        {item.hoursHundredths
                          ? ` · ${(item.hoursHundredths / 100).toFixed(2)} h × ${money(item.rateCents ?? 0)}`
                          : ""}
                      </p>
                      <p>
                        {locale === "es" ? "Bruto" : "Gross"}{" "}
                        {money(item.grossCents)} ·{" "}
                        {locale === "es" ? "Retención" : "Withholding"}{" "}
                        {money(item.withholdingCents)} ·{" "}
                        {locale === "es" ? "Neto" : "Net"}{" "}
                        {money(item.netCents)}
                      </p>
                      <span className={`run-status ${item.status}`}>
                        {optionLabel(item.status, locale)}
                      </span>
                      {contractorPaymentActions(item)}
                    </article>
                  ),
                };
              })}
              empty={
                locale === "es"
                  ? "No hay pagos de contratistas."
                  : "No contractor payments."
              }
            />
          </section>
        </>
      )}
      <details className="operation-panel">
        <summary>
          {locale === "es"
            ? "Deducciones, ASUME, embargos y préstamos"
            : "Deductions, ASUME, garnishments, and loans"}
        </summary>
        <form onSubmit={createDeduction}>
          <div className="form-grid">
            <label>
              {locale === "es" ? "Empleado" : "Employee"}
              <select
                required
                value={deduction.workerId}
                onChange={(e) =>
                  setDeduction({
                    ...deduction,
                    workerId: e.currentTarget.value,
                  })
                }
              >
                <option value="">—</option>
                {workers
                  .filter((w) => w.workerType === "employee")
                  .map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.firstName} {w.lastName}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              {locale === "es" ? "Tipo" : "Type"}
              <select
                value={deduction.deductionType}
                onChange={(e) =>
                  setDeduction({
                    ...deduction,
                    deductionType: e.currentTarget.value,
                  })
                }
              >
                {[
                  "insurance",
                  "retirement",
                  "asume",
                  "garnishment",
                  "loan",
                  "advance",
                  "benefit",
                  "other",
                ].map((v) => (
                  <option value={v} key={v}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {locale === "es" ? "Descripción" : "Description"}
              <input
                required
                value={deduction.description}
                onChange={(e) =>
                  setDeduction({
                    ...deduction,
                    description: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Cálculo" : "Calculation"}
              <select
                value={deduction.calculationType}
                onChange={(e) =>
                  setDeduction({
                    ...deduction,
                    calculationType: e.currentTarget.value,
                  })
                }
              >
                <option value="fixed">
                  {locale === "es" ? "Cantidad fija" : "Fixed amount"}
                </option>
                <option value="percentage">
                  {optionLabel("percentage", locale)}
                </option>
              </select>
            </label>
            {deduction.calculationType === "fixed" ? (
              <label>
                {locale === "es" ? "Importe" : "Amount"}
                <input
                  required
                  type="number"
                  min="0"
                  step="0.01"
                  value={deduction.amount}
                  onInput={(e) =>
                    setDeduction({
                      ...deduction,
                      amount: e.currentTarget.value,
                    })
                  }
                />
              </label>
            ) : (
              <label>
                {locale === "es" ? "Por ciento" : "Percent"}
                <input
                  required
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={deduction.ratePercent}
                  onInput={(e) =>
                    setDeduction({
                      ...deduction,
                      ratePercent: e.currentTarget.value,
                    })
                  }
                />
              </label>
            )}
            <label>
              {locale === "es"
                ? "Balance original (opcional)"
                : "Original balance (optional)"}
              <input
                type="number"
                min="0"
                step="0.01"
                value={deduction.originalBalance}
                onInput={(e) =>
                  setDeduction({
                    ...deduction,
                    originalBalance: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es"
                ? "Prioridad (menor primero)"
                : "Priority (lower first)"}
              <input
                required
                type="number"
                min="1"
                value={deduction.priority}
                onInput={(e) =>
                  setDeduction({
                    ...deduction,
                    priority: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Máximo por nómina" : "Maximum per payroll"}
              <input
                type="number"
                min="0"
                step="0.01"
                value={deduction.maximumPerPayroll}
                onInput={(e) =>
                  setDeduction({
                    ...deduction,
                    maximumPerPayroll: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es"
                ? "Límite de ingreso disponible %"
                : "Disposable-income limit %"}
              <input
                required
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={deduction.disposableIncomeLimitPercent}
                onInput={(e) =>
                  setDeduction({
                    ...deduction,
                    disposableIncomeLimitPercent: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Cantidad protegida" : "Protected amount"}
              <input
                required
                type="number"
                min="0"
                step="0.01"
                value={deduction.protectedAmount}
                onInput={(e) =>
                  setDeduction({
                    ...deduction,
                    protectedAmount: e.currentTarget.value,
                  })
                }
              />
            </label>
            {deduction.deductionType === "asume" && (
              <>
                <label>
                  {locale === "es"
                    ? "Límite agregado CCPA %"
                    : "Aggregate CCPA limit %"}
                  <select
                    required
                    value={deduction.aggregateLimitPercent}
                    onChange={(e) =>
                      setDeduction({
                        ...deduction,
                        aggregateLimitPercent: e.currentTarget.value,
                      })
                    }
                  >
                    <option value="50">50% · other family / no arrears</option>
                    <option value="55">55% · other family / arrears</option>
                    <option value="60">
                      60% · no other family / no arrears
                    </option>
                    <option value="65">65% · no other family / arrears</option>
                  </select>
                  <small>
                    {locale === "es"
                      ? "Seleccione según la orden y la situación documentada."
                      : "Select from the order and documented circumstances."}
                  </small>
                </label>
                <label>
                  {locale === "es"
                    ? "Pensión ordenada para prorrateo"
                    : "Ordered pension for proration"}
                  <input
                    required
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={deduction.prorationWeight}
                    onInput={(e) =>
                      setDeduction({
                        ...deduction,
                        prorationWeight: e.currentTarget.value,
                      })
                    }
                  />
                  <small>
                    {locale === "es"
                      ? "Se usa para distribuir proporcionalmente cuando existen múltiples casos."
                      : "Used to allocate proportionally when multiple cases exist."}
                  </small>
                </label>
              </>
            )}
            <label>
              {locale === "es" ? "Referencia/orden" : "Reference/order"}
              <input
                value={deduction.referenceNumber}
                onChange={(e) =>
                  setDeduction({
                    ...deduction,
                    referenceNumber: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Autoridad emisora" : "Issuing authority"}
              <input
                value={deduction.issuingAuthority}
                onChange={(e) =>
                  setDeduction({
                    ...deduction,
                    issuingAuthority: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Número de caso" : "Case number"}
              <input
                value={deduction.caseNumber}
                onChange={(e) =>
                  setDeduction({
                    ...deduction,
                    caseNumber: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Fecha recibida" : "Received date"}
              <input
                type="date"
                value={deduction.receivedOn}
                onInput={(e) =>
                  setDeduction({
                    ...deduction,
                    receivedOn: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Vigente desde" : "Effective from"}
              <input
                required
                type="date"
                value={deduction.effectiveFrom}
                onInput={(e) =>
                  setDeduction({
                    ...deduction,
                    effectiveFrom: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Vigente hasta (opcional)" : "Effective to (optional)"}
              <input
                type="date"
                value={deduction.effectiveTo}
                onInput={(e) =>
                  setDeduction({
                    ...deduction,
                    effectiveTo: e.currentTarget.value,
                  })
                }
              />
            </label>
          </div>
          <button className="primary" disabled={busy}>
            {locale === "es" ? "Guardar deducción" : "Save deduction"}
          </button>
        </form>
      </details>
      <h2 className="subheading">
        {locale === "es"
          ? "Deducciones activas e históricas"
          : "Active and historical deductions"}
      </h2>
      <div className="run-list">
        {deductions.map((item) => (
          <article key={item.id}>
            <div>
              <strong>
                {item.workerName} · {item.deductionType}
              </strong>
              <p>
                {item.description} ·{" "}
                {item.calculationType === "fixed"
                  ? money(item.amountCents ?? 0)
                  : `${(item.rateBps ?? 0) / 100}%`}{" "}
                · {locale === "es" ? "prioridad" : "priority"} {item.priority}
              </p>
              <small>
                {locale === "es" ? "Balance" : "Balance"}:{" "}
                {item.currentBalanceCents === null
                  ? "—"
                  : money(item.currentBalanceCents)}{" "}
                · {locale === "es" ? "límite" : "limit"}{" "}
                {item.disposableIncomeLimitBps / 100}%
                {item.deductionType === "asume" && (
                  <>
                    {" "}
                    · CCPA aggregate {(item.aggregateLimitBps ?? 0) / 100}% ·{" "}
                    {locale === "es" ? "peso de prorrateo" : "proration weight"}{" "}
                    {money(item.prorationWeightCents ?? 0)}
                  </>
                )}
              </small>
            </div>
            <span className={`run-status ${item.status}`}>
              {optionLabel(item.status, locale)}
            </span>
            <div className="run-actions">
              <button
                type="button"
                onClick={() => void loadDeductionEvidence(item)}
              >
                {locale === "es"
                  ? "Historial y documentos"
                  : "History & documents"}
              </button>
              {item.currentBalanceCents !== null && (
                <button
                  type="button"
                  onClick={() => void adjustDeduction(item)}
                >
                  {locale === "es" ? "Ajustar balance" : "Adjust balance"}
                </button>
              )}
              {item.status === "active" && (
                <button onClick={() => void deductionStatus(item, "suspended")}>
                  {locale === "es" ? "Suspender" : "Suspend"}
                </button>
              )}
              {item.status === "suspended" && (
                <button onClick={() => void deductionStatus(item, "active")}>
                  {locale === "es" ? "Reactivar" : "Reactivate"}
                </button>
              )}
              {item.status !== "completed" && (
                <button onClick={() => void deductionStatus(item, "completed")}>
                  {locale === "es" ? "Completar" : "Complete"}
                </button>
              )}
            </div>
            {deductionEvidence[item.id] && (
              <div className="operation-panel">
                <div className="form-grid">
                  <label>
                    {locale === "es" ? "Tipo de documento" : "Document type"}
                    <select
                      value={deductionDocumentTypes[item.id] ?? "order"}
                      onChange={(e) =>
                        setDeductionDocumentTypes({
                          ...deductionDocumentTypes,
                          [item.id]: e.currentTarget.value,
                        })
                      }
                    >
                      <option value="order">
                        {locale === "es" ? "Orden" : "Order"}
                      </option>
                      <option value="amendment">
                        {locale === "es" ? "Enmienda" : "Amendment"}
                      </option>
                      <option value="release">
                        {locale === "es" ? "Liberación" : "Release"}
                      </option>
                      <option value="supporting">
                        {locale === "es" ? "Apoyo" : "Supporting"}
                      </option>
                    </select>
                  </label>
                  <label>
                    {locale === "es"
                      ? "PDF o imagen (máx. 10 MB)"
                      : "PDF or image (10 MB max)"}
                    <input
                      type="file"
                      accept="application/pdf,image/png,image/jpeg"
                      onChange={(e) =>
                        setDeductionFiles({
                          ...deductionFiles,
                          [item.id]: e.currentTarget.files?.[0] ?? null,
                        })
                      }
                    />
                  </label>
                </div>
                <button
                  type="button"
                  disabled={!deductionFiles[item.id] || busy}
                  onClick={() => void uploadDeductionEvidence(item)}
                >
                  {locale === "es" ? "Subir documento" : "Upload document"}
                </button>
                <div className="run-list">
                  {deductionEvidence[item.id]?.documents.map((doc) => (
                    <article key={doc.id}>
                      <div>
                        <strong>
                          {doc.documentType} · {doc.fileName}
                        </strong>
                        <small>
                          {Math.ceil(doc.byteSize / 1024)} KB · SHA-256{" "}
                          {doc.contentSha256.slice(0, 12)}… ·{" "}
                          {formatDateTime(doc.createdAt, locale)}
                        </small>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          void downloadDeductionEvidence(item, doc)
                        }
                      >
                        {locale === "es" ? "Descargar" : "Download"}
                      </button>
                    </article>
                  ))}
                  {deductionEvidence[item.id]?.balanceEvents.map((event) => (
                    <article key={event.id}>
                      <div>
                        <strong>
                          {optionLabel(event.eventType, locale)} ·{" "}
                          {money(event.amountCents)}
                        </strong>
                        <small>
                          {locale === "es" ? "Balance luego" : "Balance after"}:{" "}
                          {event.balanceAfterCents === null
                            ? "—"
                            : money(event.balanceAfterCents)}{" "}
                          · {event.reason ?? "—"} ·{" "}
                          {formatDateTime(event.createdAt, locale)}
                        </small>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            )}
          </article>
        ))}
      </div>
      {taxSubmissionDraft && (
        <dialog
          className="workflow-dialog"
          open
          aria-labelledby="tax-submission-title"
        >
          <form onSubmit={submitTaxSubmission}>
            <header>
              <div>
                <p className="kicker">
                  {locale === "es"
                    ? "ENVÍO AUDITADO A HACIENDA"
                    : "AUDITED HACIENDA SUBMISSION"}
                </p>
                <h2 id="tax-submission-title">
                  {taxSubmissionDraft.status === "ready"
                    ? locale === "es"
                      ? "Marcar expediente listo"
                      : "Mark case ready"
                    : taxSubmissionDraft.status === "submitted"
                      ? locale === "es"
                        ? "Registrar envío"
                        : "Record submission"
                      : taxSubmissionDraft.status === "confirmed"
                        ? locale === "es"
                          ? "Confirmar recepción"
                          : "Confirm receipt"
                        : locale === "es"
                          ? "Registrar devolución"
                          : "Record return"}
                </h2>
                <p>
                  {taxSubmissionDraft.item.formVersion} ·{" "}
                  {formatDate(taxSubmissionDraft.item.effectiveFrom, locale)}
                </p>
              </div>
              <button
                type="button"
                aria-label={locale === "es" ? "Cerrar" : "Close"}
                onClick={() => setTaxSubmissionDraft(null)}
              >
                ×
              </button>
            </header>
            <p className="form-detail">
              {locale === "es"
                ? "Revise todos los datos antes de guardar. El cambio y la razón se incorporan al historial del expediente."
                : "Review every value before saving. The transition and reason are retained in the case history."}
            </p>
            <div className="form-grid">
              {(taxSubmissionDraft.status === "submitted" ||
                taxSubmissionDraft.status === "confirmed") && (
                <>
                  <label>
                    {locale === "es" ? "Método de envío" : "Submission method"}
                    <select
                      value={taxSubmissionDraft.submissionMethod}
                      onChange={(e) =>
                        setTaxSubmissionDraft({
                          ...taxSubmissionDraft,
                          submissionMethod: e.currentTarget
                            .value as TaxSubmissionDraft["submissionMethod"],
                        })
                      }
                    >
                      <option value="electronic">
                        {locale === "es" ? "Electrónico" : "Electronic"}
                      </option>
                      <option value="mail">
                        {locale === "es" ? "Correo" : "Mail"}
                      </option>
                      <option value="in_person">
                        {locale === "es" ? "En persona" : "In person"}
                      </option>
                      <option value="other">
                        {locale === "es" ? "Otro" : "Other"}
                      </option>
                    </select>
                  </label>
                  <label>
                    {locale === "es" ? "Fecha de envío" : "Submission date"}
                    <input
                      required
                      type="date"
                      value={taxSubmissionDraft.submittedOn}
                      onInput={(e) =>
                        setTaxSubmissionDraft({
                          ...taxSubmissionDraft,
                          submittedOn: e.currentTarget.value,
                        })
                      }
                    />
                  </label>
                  <label className="full-width">
                    {locale === "es"
                      ? "Referencia o confirmación"
                      : "Reference or confirmation"}
                    <input
                      required
                      minLength={2}
                      maxLength={160}
                      value={taxSubmissionDraft.submissionReference}
                      onChange={(e) =>
                        setTaxSubmissionDraft({
                          ...taxSubmissionDraft,
                          submissionReference: e.currentTarget.value,
                        })
                      }
                    />
                  </label>
                </>
              )}
              <label className="full-width">
                {locale === "es" ? "Razón documentada" : "Documented reason"}
                <textarea
                  required
                  minLength={3}
                  maxLength={1000}
                  value={taxSubmissionDraft.reason}
                  onChange={(e) =>
                    setTaxSubmissionDraft({
                      ...taxSubmissionDraft,
                      reason: e.currentTarget.value,
                    })
                  }
                />
              </label>
            </div>
            <OperationalError value={error} locale={locale} />
            <footer>
              <button type="button" onClick={() => setTaxSubmissionDraft(null)}>
                {locale === "es" ? "Cancelar" : "Cancel"}
              </button>
              <button
                className={
                  taxSubmissionDraft.status === "returned"
                    ? "danger-action"
                    : "primary"
                }
                disabled={
                  busy ||
                  taxSubmissionDraft.reason.trim().length < 3 ||
                  ((taxSubmissionDraft.status === "submitted" ||
                    taxSubmissionDraft.status === "confirmed") &&
                    taxSubmissionDraft.submissionReference.trim().length < 2)
                }
              >
                {busy ? (
                  <LoaderCircle className="spin" />
                ) : locale === "es" ? (
                  "Guardar transición"
                ) : (
                  "Save transition"
                )}
              </button>
            </footer>
          </form>
        </dialog>
      )}
      {bonusActionDraft && (
        <dialog
          className="workflow-dialog"
          open
          aria-labelledby="bonus-action-title"
        >
          <form onSubmit={submitBonusAction}>
            <header>
              <div>
                <p className="kicker">
                  {locale === "es"
                    ? "BONO DE NAVIDAD AUDITADO"
                    : "AUDITED CHRISTMAS BONUS"}
                </p>
                <h2 id="bonus-action-title">
                  {bonusActionDraft.kind === "payroll"
                    ? locale === "es"
                      ? "Crear nómina del bono"
                      : "Create bonus payroll"
                    : bonusActionDraft.status === "approved"
                      ? locale === "es"
                        ? "Aprobar cálculo del bono"
                        : "Approve bonus calculation"
                      : bonusActionDraft.status === "finalized"
                        ? locale === "es"
                          ? "Finalizar ciclo del bono"
                          : "Finalize bonus cycle"
                        : locale === "es"
                          ? "Anular ciclo del bono"
                          : "Void bonus cycle"}
                </h2>
                <p>
                  {bonusActionDraft.cycle.coverageYear} ·{" "}
                  {bonusActionDraft.cycle.coverageStart} —{" "}
                  {bonusActionDraft.cycle.coverageEnd}
                </p>
              </div>
              <button
                type="button"
                aria-label={locale === "es" ? "Cerrar" : "Close"}
                onClick={() => setBonusActionDraft(null)}
              >
                ×
              </button>
            </header>
            <p className="form-detail">
              {bonusActionDraft.kind === "payroll"
                ? locale === "es"
                  ? "Seleccione la fecha real de pago y el método de retención aplicable antes de crear la nómina."
                  : "Select the actual payment date and applicable withholding method before creating payroll."
                : locale === "es"
                  ? "El cambio de estado y su razón permanecen en el historial del ciclo."
                  : "The status change and its reason remain in the cycle history."}
            </p>
            <div className="form-grid">
              {bonusActionDraft.kind === "payroll" ? (
                <>
                  <label>
                    {locale === "es" ? "Fecha de pago" : "Payment date"}
                    <input
                      required
                      type="date"
                      value={bonusActionDraft.payDate}
                      onInput={(e) =>
                        setBonusActionDraft({
                          ...bonusActionDraft,
                          payDate: e.currentTarget.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    {locale === "es"
                      ? "Método de retención"
                      : "Withholding method"}
                    <select
                      value={bonusActionDraft.withholdingMethod}
                      onChange={(e) =>
                        setBonusActionDraft({
                          ...bonusActionDraft,
                          withholdingMethod: e.currentTarget.value as
                            | "annual_aggregate"
                            | "prior_effective_rate"
                            | "accumulated_wage_bracket",
                        })
                      }
                    >
                      <option value="annual_aggregate">
                        {locale === "es"
                          ? "Agregado anual"
                          : "Annual aggregate"}
                      </option>
                      <option value="prior_effective_rate">
                        {locale === "es"
                          ? "Tasa efectiva anterior"
                          : "Prior effective rate"}
                      </option>
                      <option value="accumulated_wage_bracket">
                        {locale === "es"
                          ? "Tabla de salario acumulado"
                          : "Accumulated wage bracket"}
                      </option>
                    </select>
                  </label>
                </>
              ) : (
                <label className="full-width">
                  {locale === "es" ? "Razón documentada" : "Documented reason"}
                  <textarea
                    required
                    minLength={3}
                    maxLength={1000}
                    value={bonusActionDraft.reason}
                    onChange={(e) =>
                      setBonusActionDraft({
                        ...bonusActionDraft,
                        reason: e.currentTarget.value,
                      })
                    }
                  />
                </label>
              )}
            </div>
            <OperationalError value={error} locale={locale} />
            <footer>
              <button type="button" onClick={() => setBonusActionDraft(null)}>
                {locale === "es" ? "Cancelar" : "Cancel"}
              </button>
              <button
                className={
                  bonusActionDraft.kind === "status" &&
                  bonusActionDraft.status === "voided"
                    ? "danger-action"
                    : "primary"
                }
                disabled={
                  busy ||
                  (bonusActionDraft.kind === "status" &&
                    bonusActionDraft.reason.trim().length < 3)
                }
              >
                {busy ? (
                  <LoaderCircle className="spin" />
                ) : locale === "es" ? (
                  "Confirmar"
                ) : (
                  "Confirm"
                )}
              </button>
            </footer>
          </form>
        </dialog>
      )}
      {payrollAction && (
        <dialog
          className="workflow-dialog"
          open
          aria-labelledby="payroll-action-title"
        >
          <form onSubmit={submitPayrollAction}>
            <header>
              <div>
                <p className="kicker">
                  {locale === "es"
                    ? "DECISIÓN DE NÓMINA AUDITADA"
                    : "AUDITED PAYROLL DECISION"}
                </p>
                <h2 id="payroll-action-title">
                  {payrollAction.kind === "run_decision"
                    ? payrollAction.verb === "approve"
                      ? locale === "es"
                        ? "Aprobar nómina"
                        : "Approve payroll"
                      : locale === "es"
                        ? "Rechazar nómina"
                        : "Reject payroll"
                    : payrollAction.kind === "expense"
                      ? payrollAction.decision === "approved"
                        ? locale === "es"
                          ? "Aprobar gasto"
                          : "Approve expense"
                        : locale === "es"
                          ? "Rechazar gasto"
                          : "Reject expense"
                      : payrollAction.kind === "close_cost"
                        ? locale === "es"
                          ? "Cerrar regla de costo patronal"
                          : "Close employer-cost rule"
                        : payrollAction.kind === "adopt_supplemental"
                          ? locale === "es"
                            ? "Adoptar método anualizado"
                            : "Adopt annualized method"
                          : payrollAction.kind === "end_bonus"
                            ? locale === "es"
                              ? "Terminar bono recurrente"
                              : "End recurring bonus"
                            : payrollAction.kind === "diagnostic"
                              ? locale === "es"
                                ? "Decidir diagnóstico"
                                : "Decide diagnostic"
                              : payrollAction.kind === "deduction_status"
                                ? locale === "es"
                                  ? "Cambiar estado de deducción"
                                  : "Change deduction status"
                                : locale === "es"
                                  ? "Ajustar balance de deducción"
                                  : "Adjust deduction balance"}
                </h2>
                <p>
                  {payrollAction.kind === "expense"
                    ? payrollAction.item.workerName
                    : payrollAction.kind === "close_cost"
                      ? payrollAction.item.description
                      : payrollAction.kind === "adopt_supplemental"
                        ? String(payrollAction.taxYear)
                        : payrollAction.kind === "end_bonus"
                          ? `${payrollAction.item.workerName} · ${payrollAction.item.description}`
                          : payrollAction.kind === "diagnostic"
                            ? payrollAction.action
                            : payrollAction.item.description}
                </p>
              </div>
              <button
                type="button"
                aria-label={locale === "es" ? "Cerrar" : "Close"}
                onClick={() => setPayrollAction(null)}
              >
                ×
              </button>
            </header>
            <p className="form-detail">
              {payrollAction.kind === "run_decision"
                ? locale === "es"
                  ? "La aprobación o rechazo cambia el estado de la nómina y queda vinculado a esta razón."
                  : "Approval or rejection changes the payroll state and is retained with this reason."
                : payrollAction.kind === "adopt_supplemental"
                  ? locale === "es"
                    ? "Esta elección es inmutable y obliga a usar el método durante todo el año natural seleccionado."
                    : "This election is immutable and requires the method throughout the selected calendar year."
                  : payrollAction.kind === "deduction_balance"
                    ? locale === "es"
                      ? "Un valor positivo aumenta el balance; uno negativo lo reduce. El evento y balance resultante quedan en historial."
                      : "A positive value increases the balance; a negative value reduces it. The event and resulting balance remain in history."
                    : locale === "es"
                      ? "La acción se conserva con su razón y el contexto original."
                      : "The action is retained with its reason and original context."}
            </p>
            <div className="form-grid">
              {payrollAction.kind === "close_cost" ||
              payrollAction.kind === "end_bonus" ? (
                <label>
                  {locale === "es"
                    ? "Último día vigente"
                    : "Last effective date"}
                  <input
                    required
                    type="date"
                    min={payrollAction.item.effectiveFrom}
                    value={payrollAction.effectiveTo}
                    onInput={(e) =>
                      setPayrollAction({
                        ...payrollAction,
                        effectiveTo: e.currentTarget.value,
                      })
                    }
                  />
                </label>
              ) : null}
              {payrollAction.kind === "deduction_balance" && (
                <label>
                  {locale === "es" ? "Ajuste al balance" : "Balance adjustment"}
                  <input
                    required
                    type="number"
                    step="0.01"
                    value={payrollAction.amount}
                    onInput={(e) =>
                      setPayrollAction({
                        ...payrollAction,
                        amount: e.currentTarget.value,
                      })
                    }
                  />
                </label>
              )}
              {payrollAction.kind === "run_decision" && payrollAction.verb === "approve" ? (
                <AuditControl locale={locale} value={payrollAction.reason} onChange={value=>setPayrollAction({...payrollAction,reason:value})}/>
              ) : <label className="full-width">
                {payrollAction.kind === "expense"
                  ? locale === "es"
                    ? "Comentario de revisión"
                    : "Review comment"
                  : payrollAction.kind === "diagnostic" &&
                      payrollAction.action === "explain"
                    ? locale === "es"
                      ? "Explicación"
                      : "Explanation"
                    : locale === "es"
                      ? "Razón documentada"
                      : "Documented reason"}
                <textarea
                  required
                  minLength={3}
                  value={payrollAction.reason}
                  onChange={(e) =>
                    setPayrollAction({
                      ...payrollAction,
                      reason: e.currentTarget.value,
                    })
                  }
                />
              </label>}
            </div>
            <OperationalError value={error} locale={locale} />
            <footer>
              <button type="button" onClick={() => setPayrollAction(null)}>
                {locale === "es" ? "Cancelar" : "Cancel"}
              </button>
              <button
                className={
                  (payrollAction.kind === "run_decision" &&
                    payrollAction.verb === "reject") ||
                  (payrollAction.kind === "expense" &&
                    payrollAction.decision === "rejected") ||
                  (payrollAction.kind === "deduction_status" &&
                    payrollAction.status === "completed")
                    ? "danger-action"
                    : "primary"
                }
                disabled={
                  busy ||
                  (!(payrollAction.kind === "run_decision" && payrollAction.verb === "approve") && payrollAction.reason.trim().length < 3) ||
                  (payrollAction.kind === "deduction_balance" &&
                    (!payrollAction.amount ||
                      Number(payrollAction.amount) === 0))
                }
              >
                {busy ? (
                  <LoaderCircle className="spin" />
                ) : locale === "es" ? (
                  "Confirmar decisión"
                ) : (
                  "Confirm decision"
                )}
              </button>
            </footer>
          </form>
        </dialog>
      )}
    </div>
  );
}

type SelfDocument = {
  id: string;
  documentType:
    | "pay_stub"
    | "contractor_statement"
    | "tax_withholding_certificate";
  referenceId: string;
  locale: "es-PR" | "en-PR";
  status: string;
  createdAt: string;
  paymentDate: string | null;
  periodStart: string | null;
  periodEnd: string | null;
};
type CompanyDocument = SelfDocument & { workerId: string; workerName: string };
type SelfPersonnelDocument = Pick<
  PersonnelDocument,
  | "id"
  | "category"
  | "title"
  | "effectiveOn"
  | "expiresOn"
  | "fileName"
  | "contentType"
  | "byteSize"
  | "createdAt"
>;
type ExpenseClaim = {
  id: string;
  workerId: string;
  workerName: string;
  category: ManualEarningCode;
  amountCents: number;
  incurredOn: string;
  businessPurpose: string;
  receiptFileName: string;
  receiptMimeType: string;
  status: "submitted" | "approved" | "rejected" | "paid";
  submittedAt: string;
  reviewedAt: string | null;
  reviewComment: string | null;
  paidAt: string | null;
  targetPayrollRunId: string | null;
  targetPeriodStart: string | null;
  targetPeriodEnd: string | null;
  targetPayDate: string | null;
  canReview: boolean;
};
function ExpenseManagement({
  locale,
  tenant,
  actorWorkerId,
}: {
  locale: Locale;
  tenant: Tenant;
  actorWorkerId: string | null;
}) {
  const [claims, setClaims] = useState<ExpenseClaim[]>([]),
    [workers, setWorkers] = useState<Worker[]>([]),
    [runs, setRuns] = useState<PayrollRun[]>([]),
    [busy, setBusy] = useState(true),
    [error, setError] = useState(""),
    [receiptFile, setReceiptFile] = useState<File | null>(null),
    [expenseForm, setExpenseForm] = useState({
      workerId: "",
      targetPayrollRunId: "",
      category: "expense_reimbursement",
      amount: "",
      incurredOn: new Date().toISOString().slice(0, 10),
      businessPurpose: "",
    }),
    [reviewDraft, setReviewDraft] = useState<{
      item: ExpenseClaim;
      decision: "approved" | "rejected";
      comment: string;
      targetPayrollRunId: string;
    } | null>(null);
  const load = async () => {
    setBusy(true);
    setError("");
    const [response, workerResponse, runResponse] = await Promise.all([
      api(`/v1/tenants/${tenant.id}/expense-claims`),
      api(`/v1/tenants/${tenant.id}/workers`),
      api(`/v1/tenants/${tenant.id}/payroll-runs`),
    ]);
    if (response.ok)
      setClaims(((await response.json()) as { claims: ExpenseClaim[] }).claims);
    else setError(`expense-claims:${response.status}`);
    if (workerResponse.ok)
      setWorkers(
        ((await workerResponse.json()) as { workers: Worker[] }).workers.filter(
          (item) =>
            item.workerType === "employee" &&
            !["inactive", "terminated"].includes(item.status),
        ),
      );
    if (runResponse.ok)
      setRuns(
        ((await runResponse.json()) as { runs: PayrollRun[] }).runs.filter(
          (item) =>
            item.runType === "regular" &&
            ["draft", "diagnostic", "calculated"].includes(item.status),
        ),
      );
    setBusy(false);
  };
  useEffect(() => {
    void load();
  }, [tenant.id]);
  const review = (item: ExpenseClaim, decision: "approved" | "rejected") =>
    setReviewDraft({
      item,
      decision,
      comment: "",
      targetPayrollRunId: item.targetPayrollRunId ?? "",
    });
  const submitManaged = async (event: FormEvent) => {
    event.preventDefault();
    if (!receiptFile) return;
    setBusy(true);
    setError("");
    const body = new FormData();
    Object.entries(expenseForm).forEach(([key, value]) =>
      body.append(key, value),
    );
    body.append("receipt", receiptFile);
    const response = await api(`/v1/tenants/${tenant.id}/expense-claims`, {
      method: "POST",
      body,
    });
    if (response.ok) {
      setExpenseForm({ ...expenseForm, amount: "", businessPurpose: "" });
      setReceiptFile(null);
      await load();
    } else {
      setError(
        response.status === 409
          ? locale === "es"
            ? "No se pudo registrar: verifique la persona, el período y la cuenta del supervisor."
            : "Could not record it: verify the person, payroll period, and supervisor account."
          : `create-expense:${response.status}`,
      );
      setBusy(false);
    }
  };
  const submitReview = async (e: FormEvent) => {
    e.preventDefault();
    const draft = reviewDraft;
    if (!draft) return;
    setBusy(true);
    setError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/expense-claims/${draft.item.id}/review`,
      {
        method: "POST",
        body: JSON.stringify({
          decision: draft.decision,
          comment: draft.comment.trim(),
          targetPayrollRunId: draft.targetPayrollRunId || null,
        }),
      },
    );
    if (response.ok) {
      setReviewDraft(null);
      await load();
    } else {
      setError(
        response.status === 409
          ? locale === "es"
            ? "No puede revisar su propio gasto o ya fue decidido."
            : "You cannot review your own expense, or it was already decided."
          : `review:${response.status}`,
      );
      setBusy(false);
    }
  };
  const receipt = async (item: ExpenseClaim) => {
    const response = await api(
      `/v1/tenants/${tenant.id}/expense-claims/${item.id}/receipt`,
    );
    if (!response.ok) {
      setError(`receipt:${response.status}`);
      return;
    }
    const url = URL.createObjectURL(await response.blob());
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };
  const money = (value: number) =>
    new Intl.NumberFormat(locale === "es" ? "es-PR" : "en-US", {
      style: "currency",
      currency: "USD",
    }).format(value / 100);
  return (
    <div className="content">
      <p className="kicker">{tenant.displayName}</p>
      <h1>
        {locale === "es"
          ? "Revisión de gastos y reembolsos"
          : "Expense and reimbursement review"}
      </h1>
      <p className="lead">
        {locale === "es"
          ? "Registre recibos entregados por empleados o revise los gastos sometidos por ellos. Quien registra un gasto no puede aprobarlo."
          : "Record receipts delivered by employees or review their submitted expenses. The person who records a claim cannot approve it."}
      </p>
      <section className="settings-form">
        <h2>
          {locale === "es"
            ? "Crear reembolso para una persona"
            : "Create reimbursement for a person"}
        </h2>
        <form onSubmit={submitManaged}>
          <div className="form-grid">
            <label>
              {locale === "es" ? "Empleado" : "Employee"}
              <select
                required
                value={expenseForm.workerId}
                onChange={(event) =>
                  setExpenseForm({
                    ...expenseForm,
                    workerId: event.currentTarget.value,
                  })
                }
              >
                <option value="">—</option>
                {workers.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.firstName} {item.lastName}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {locale === "es"
                ? "Período en que se reembolsará"
                : "Payroll period for reimbursement"}
              <select
                required
                value={expenseForm.targetPayrollRunId}
                onChange={(event) =>
                  setExpenseForm({
                    ...expenseForm,
                    targetPayrollRunId: event.currentTarget.value,
                  })
                }
              >
                <option value="">—</option>
                {runs.map((item) => (
                  <option key={item.id} value={item.id}>
                    {formatDate(item.periodStart, locale)} —{" "}
                    {formatDate(item.periodEnd, locale)} ·{" "}
                    {locale === "es" ? "pago" : "pay"}{" "}
                    {formatDate(item.payDate, locale)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {locale === "es" ? "Tipo de gasto" : "Expense type"}
              <select
                value={expenseForm.category}
                onChange={(event) =>
                  setExpenseForm({
                    ...expenseForm,
                    category: event.currentTarget.value,
                  })
                }
              >
                {[
                  "expense_reimbursement",
                  "mileage",
                  "per_diem",
                  "car_allowance",
                  "other_nonwage_payment",
                ].map((value) => (
                  <option key={value} value={value}>
                    {optionLabel(value, locale)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {locale === "es" ? "Cantidad" : "Amount"}
              <input
                required
                type="number"
                min="0.01"
                step="0.01"
                value={expenseForm.amount}
                onChange={(event) =>
                  setExpenseForm({
                    ...expenseForm,
                    amount: event.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Fecha del gasto" : "Expense date"}
              <input
                required
                type="date"
                max={new Date().toISOString().slice(0, 10)}
                value={expenseForm.incurredOn}
                onChange={(event) =>
                  setExpenseForm({
                    ...expenseForm,
                    incurredOn: event.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Recibo o evidencia" : "Receipt or evidence"}
              <input
                required
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp"
                onChange={(event) =>
                  setReceiptFile(event.currentTarget.files?.[0] ?? null)
                }
              />
            </label>
            <label className="full-width">
              {locale === "es" ? "Propósito del gasto" : "Business purpose"}
              <textarea
                required
                minLength={3}
                maxLength={500}
                value={expenseForm.businessPurpose}
                onChange={(event) =>
                  setExpenseForm({
                    ...expenseForm,
                    businessPurpose: event.currentTarget.value,
                  })
                }
              />
            </label>
          </div>
          <button className="primary" disabled={busy}>
            {locale === "es" ? "Registrar reembolso" : "Record reimbursement"}
          </button>
        </form>
      </section>
      {busy && !reviewDraft ? (
        <LoaderCircle className="spin" />
      ) : (
        <div className="run-list">
          {claims.map((item) => (
            <article key={item.id}>
              <div>
                <strong>
                  {item.workerName} · {money(item.amountCents)}
                </strong>
                <p>
                  {optionLabel(item.category, locale)} ·{" "}
                  {formatDate(item.incurredOn, locale)} · {item.businessPurpose}
                </p>
                <small>
                  {item.receiptFileName}
                  {item.targetPeriodStart && item.targetPeriodEnd
                    ? ` · ${locale === "es" ? "Nómina" : "Payroll"}: ${formatDate(item.targetPeriodStart, locale)} — ${formatDate(item.targetPeriodEnd, locale)}`
                    : ""}
                  {item.reviewComment ? ` · ${item.reviewComment}` : ""}
                </small>
              </div>
              <span className={`run-status ${item.status}`}>
                {optionLabel(item.status, locale)}
              </span>
              <div className="run-actions">
                <button type="button" onClick={() => void receipt(item)}>
                  {locale === "es" ? "Ver evidencia" : "View evidence"}
                </button>
                {item.status === "submitted" &&
                  item.workerId !== actorWorkerId &&
                  item.canReview && (
                    <>
                      <button
                        type="button"
                        onClick={() => review(item, "approved")}
                      >
                        {locale === "es" ? "Aprobar" : "Approve"}
                      </button>
                      <button
                        type="button"
                        onClick={() => review(item, "rejected")}
                      >
                        {locale === "es" ? "Rechazar" : "Reject"}
                      </button>
                    </>
                  )}
              </div>
            </article>
          ))}
        </div>
      )}
      {!busy && !claims.length && (
        <p>
          {locale === "es"
            ? "No hay gastos visibles para revisar."
            : "There are no visible expenses to review."}
        </p>
      )}
      <OperationalError value={error} locale={locale} />
      {reviewDraft && (
        <dialog
          className="workflow-dialog"
          open
          aria-labelledby="expense-review-title"
        >
          <form onSubmit={submitReview}>
            <header>
              <div>
                <p className="kicker">
                  {locale === "es"
                    ? "REVISIÓN FINANCIERA AUDITADA"
                    : "AUDITED FINANCIAL REVIEW"}
                </p>
                <h2 id="expense-review-title">
                  {reviewDraft.decision === "approved"
                    ? locale === "es"
                      ? "Aprobar gasto"
                      : "Approve expense"
                    : locale === "es"
                      ? "Rechazar gasto"
                      : "Reject expense"}
                </h2>
                <p>
                  {reviewDraft.item.workerName} ·{" "}
                  {money(reviewDraft.item.amountCents)} ·{" "}
                  {reviewDraft.item.incurredOn}
                </p>
              </div>
              <button
                type="button"
                aria-label={locale === "es" ? "Cerrar" : "Close"}
                onClick={() => setReviewDraft(null)}
              >
                ×
              </button>
            </header>
            <p className="form-detail">{reviewDraft.item.businessPurpose}</p>
            <div className="form-grid">
              {reviewDraft.decision === "approved" && (
                <label>
                  {locale === "es"
                    ? "Período en que se reembolsará"
                    : "Payroll period for reimbursement"}
                  <select
                    required
                    value={reviewDraft.targetPayrollRunId}
                    onChange={(event) =>
                      setReviewDraft({
                        ...reviewDraft,
                        targetPayrollRunId: event.currentTarget.value,
                      })
                    }
                  >
                    <option value="">—</option>
                    {runs.map((item) => (
                      <option key={item.id} value={item.id}>
                        {formatDate(item.periodStart, locale)} —{" "}
                        {formatDate(item.periodEnd, locale)} ·{" "}
                        {formatDate(item.payDate, locale)}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label className="full-width">
                {reviewDraft.decision === "approved"
                  ? locale === "es"
                    ? "Justificación de aprobación"
                    : "Approval justification"
                  : locale === "es"
                    ? "Razón del rechazo"
                    : "Rejection reason"}
                <textarea
                  required
                  minLength={3}
                  maxLength={1000}
                  value={reviewDraft.comment}
                  onChange={(e) =>
                    setReviewDraft({
                      ...reviewDraft,
                      comment: e.currentTarget.value,
                    })
                  }
                />
              </label>
            </div>
            <OperationalError value={error} locale={locale} />
            <footer>
              <button type="button" onClick={() => setReviewDraft(null)}>
                {locale === "es" ? "Cancelar" : "Cancel"}
              </button>
              <button
                className={
                  reviewDraft.decision === "rejected"
                    ? "danger-action"
                    : "primary"
                }
                disabled={
                  busy ||
                  reviewDraft.comment.trim().length < 3 ||
                  (reviewDraft.decision === "approved" &&
                    !reviewDraft.targetPayrollRunId)
                }
              >
                {busy ? (
                  <LoaderCircle className="spin" />
                ) : locale === "es" ? (
                  "Guardar revisión"
                ) : (
                  "Save review"
                )}
              </button>
            </footer>
          </form>
        </dialog>
      )}
    </div>
  );
}
function EmployeeExpenseClaims({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const today = new Date().toISOString().slice(0, 10),
    [claims, setClaims] = useState<ExpenseClaim[]>([]),
    [submissionAvailable, setSubmissionAvailable] = useState(false),
    [busy, setBusy] = useState(true),
    [message, setMessage] = useState(""),
    [file, setFile] = useState<File | null>(null),
    [form, setForm] = useState({
      category: "expense_reimbursement",
      amount: "",
      incurredOn: today,
      businessPurpose: "",
    });
  const load = async () => {
    setBusy(true);
    const response = await api(`/v1/tenants/${tenant.id}/self/expense-claims`);
    if (response.ok) {
      const body = (await response.json()) as {
        claims: ExpenseClaim[];
        submissionAvailable: boolean;
      };
      setClaims(body.claims);
      setSubmissionAvailable(body.submissionAvailable);
    }
    setBusy(false);
  };
  useEffect(() => {
    void load();
  }, [tenant.id]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    setMessage("");
    const body = new FormData();
    Object.entries(form).forEach(([key, value]) => body.append(key, value));
    body.append("receipt", file);
    const response = await api(`/v1/tenants/${tenant.id}/self/expense-claims`, {
      method: "POST",
      body,
    });
    setMessage(
      response.ok
        ? locale === "es"
          ? "Gasto sometido para revisión."
          : "Expense submitted for review."
        : locale === "es"
          ? "No se pudo someter. Use PDF, JPG, PNG o WebP de hasta 10 MB."
          : "Submission failed. Use a PDF, JPG, PNG, or WebP up to 10 MB.",
    );
    if (response.ok) {
      setForm({ ...form, amount: "", businessPurpose: "" });
      setFile(null);
      await load();
    } else setBusy(false);
  };
  const receipt = async (claim: ExpenseClaim) => {
    const response = await api(
      `/v1/tenants/${tenant.id}/expense-claims/${claim.id}/receipt`,
    );
    if (!response.ok) return;
    const url = URL.createObjectURL(await response.blob());
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };
  return (
    <div className="content">
      <p className="kicker">{tenant.displayName}</p>
      <h1>
        {locale === "es"
          ? "Mis gastos y reembolsos"
          : "My expenses and reimbursements"}
      </h1>
      <p className="lead">
        {locale === "es"
          ? "Someta el gasto con su evidencia. Otra persona autorizada debe revisarlo antes de que pueda pagarse como reembolso no salarial."
          : "Submit the expense with evidence. Another authorized person must review it before it can be paid as a non-wage reimbursement."}
      </p>
      {submissionAvailable ? (
        <form className="settings-form" onSubmit={submit}>
          <div className="form-grid">
            <label>
              {locale === "es" ? "Categoría" : "Category"}
              <select
                value={form.category}
                onChange={(e) =>
                  setForm({ ...form, category: e.currentTarget.value })
                }
              >
                <option value="expense_reimbursement">
                  {locale === "es"
                    ? "Reembolso de gastos"
                    : "Expense reimbursement"}
                </option>
                <option value="mileage">
                  {locale === "es" ? "Millaje" : "Mileage"}
                </option>
                <option value="per_diem">
                  {optionLabel("per_diem", locale)}
                </option>
                <option value="car_allowance">
                  {locale === "es"
                    ? "Asignación de automóvil"
                    : "Car allowance"}
                </option>
                <option value="other_nonwage_payment">
                  {locale === "es"
                    ? "Otro pago no salarial"
                    : "Other non-wage payment"}
                </option>
              </select>
            </label>
            <label>
              {locale === "es" ? "Cantidad" : "Amount"}
              <input
                type="number"
                min="0.01"
                step="0.01"
                required
                value={form.amount}
                onInput={(e) =>
                  setForm({ ...form, amount: e.currentTarget.value })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Fecha incurrida" : "Date incurred"}
              <input
                type="date"
                max={today}
                required
                value={form.incurredOn}
                onInput={(e) =>
                  setForm({ ...form, incurredOn: e.currentTarget.value })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Propósito de negocio" : "Business purpose"}
              <input
                minLength={3}
                maxLength={500}
                required
                value={form.businessPurpose}
                onChange={(e) =>
                  setForm({ ...form, businessPurpose: e.currentTarget.value })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Recibo o evidencia" : "Receipt or evidence"}
              <input
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp"
                required
                onChange={(e) => setFile(e.currentTarget.files?.[0] ?? null)}
              />
            </label>
          </div>
          <button className="primary" disabled={busy || !file}>
            {locale === "es" ? "Someter para revisión" : "Submit for review"}
          </button>
          {message && <p className="form-detail action-result">{message}</p>}
        </form>
      ) : (
        <p className="success-note">
          {locale === "es"
            ? "Vista de revisión: puede inspeccionar el historial, pero solamente la cuenta autenticada de la persona puede someter evidencia."
            : "Review view: you may inspect history, but only the worker's authenticated account can submit evidence."}
        </p>
      )}
      <section className="settings-form">
        <h2>{locale === "es" ? "Historial" : "History"}</h2>
        {busy ? (
          <LoaderCircle className="spin" />
        ) : (
          <div className="run-list">
            {claims.map((claim) => (
              <article key={claim.id}>
                <div>
                  <strong>
                    {optionLabel(claim.category, locale)} ·{" "}
                    {new Intl.NumberFormat(localeTag(locale), {
                      style: "currency",
                      currency: "USD",
                    }).format(claim.amountCents / 100)}
                  </strong>
                  <p>
                    {claim.incurredOn} · {claim.businessPurpose}
                  </p>
                  <small>{claim.reviewComment ?? claim.receiptFileName}</small>
                </div>
                <span className={`run-status ${claim.status}`}>
                  {optionLabel(claim.status, locale)}
                </span>
                <button type="button" onClick={() => void receipt(claim)}>
                  {locale === "es" ? "Ver evidencia" : "View evidence"}
                </button>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
function CompanyDocumentLibrary({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const [workers, setWorkers] = useState<Worker[]>([]),
    [documents, setDocuments] = useState<CompanyDocument[]>([]),
    [personnel, setPersonnel] = useState<PersonnelDocument[]>([]),
    [workerId, setWorkerId] = useState(""),
    [query, setQuery] = useState(""),
    [busy, setBusy] = useState(true),
    [error, setError] = useState(""),
    [archiveDraft, setArchiveDraft] = useState<{
      item: PersonnelDocument;
      reason: string;
    } | null>(null);
  const load = async () => {
    setBusy(true);
    setError("");
    const [peopleResponse, documentResponse] = await Promise.all([
      api(`/v1/tenants/${tenant.id}/workers`),
      api(`/v1/tenants/${tenant.id}/documents`),
    ]);
    if (peopleResponse.ok) {
      const people = ((await peopleResponse.json()) as { workers: Worker[] })
        .workers;
      setWorkers(people);
      setWorkerId((current) =>
        people.some((item) => item.id === current)
          ? current
          : (people[0]?.id ?? ""),
      );
    } else setError(`workers:${peopleResponse.status}`);
    if (documentResponse.ok)
      setDocuments(
        ((await documentResponse.json()) as { documents: CompanyDocument[] })
          .documents,
      );
    else setError(`documents:${documentResponse.status}`);
    setBusy(false);
  };
  useEffect(() => {
    void load();
  }, [tenant.id]);
  useEffect(() => {
    if (!workerId) {
      setPersonnel([]);
      return;
    }
    void api(
      `/v1/tenants/${tenant.id}/workers/${workerId}/personnel-documents`,
    ).then(async (response) => {
      if (response.ok)
        setPersonnel(
          ((await response.json()) as { documents: PersonnelDocument[] })
            .documents,
        );
      else setError(`personnel-documents:${response.status}`);
    });
  }, [tenant.id, workerId]);
  const saveBlob = async (path: string, fileName: string) => {
    setBusy(true);
    const response = await api(path);
    if (response.ok) {
      const url = URL.createObjectURL(await response.blob()),
        anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fileName;
      anchor.click();
      URL.revokeObjectURL(url);
    } else setError(`download:${response.status}`);
    setBusy(false);
  };
  const archive = (item: PersonnelDocument) =>
    setArchiveDraft({ item, reason: "" });
  const submitArchive = async (e: FormEvent) => {
    e.preventDefault();
    const draft = archiveDraft;
    if (!draft) return;
    setBusy(true);
    setError("");
    const reason = draft.reason.trim(),
      response = await api(
        `/v1/tenants/${tenant.id}/workers/${workerId}/personnel-documents/${draft.item.id}/archive`,
        { method: "POST", body: JSON.stringify({ reason }) },
      );
    if (response.ok) {
      setPersonnel((items) =>
        items.map((value) =>
          value.id === draft.item.id
            ? { ...value, status: "archived", archiveReason: reason }
            : value,
        ),
      );
      setArchiveDraft(null);
    } else setError(`archive:${response.status}`);
    setBusy(false);
  };
  const needle = query.trim().toLowerCase(),
    visibleGenerated = documents.filter(
      (item) =>
        !needle ||
        `${item.workerName} ${item.documentType} ${item.paymentDate ?? item.createdAt}`
          .toLowerCase()
          .includes(needle),
    ),
    visiblePersonnel = personnel.filter(
      (item) =>
        !needle ||
        `${item.title} ${item.category} ${item.fileName}`
          .toLowerCase()
          .includes(needle),
    ),
    selected = workers.find((item) => item.id === workerId);
  return (
    <div className="content">
      <p className="kicker">{tenant.displayName}</p>
      <h1>{locale === "es" ? "Centro de documentos" : "Document center"}</h1>
      <p className="lead">
        {locale === "es"
          ? "Documentos privados almacenados en Cloudflare R2, vinculados a sus registros en D1 y protegidos por permisos."
          : "Private documents stored in Cloudflare R2, linked to their D1 records and protected by permissions."}
      </p>
      <section className="settings-form">
        <div className="form-grid">
          <label>
            {locale === "es" ? "Buscar" : "Search"}
            <input
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
              placeholder={
                locale === "es"
                  ? "Persona, tipo o archivo"
                  : "Person, type, or file"
              }
            />
          </label>
          <label>
            {locale === "es" ? "Expediente de personal" : "Personnel file"}
            <select
              value={workerId}
              onChange={(e) => setWorkerId(e.currentTarget.value)}
            >
              {workers.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.firstName} {item.lastName}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>
      {busy && !archiveDraft && <LoaderCircle className="spin" />}
      <section className="settings-form">
        <h2>
          {locale === "es"
            ? "Documentos generados de nómina y contribuciones"
            : "Generated payroll and tax documents"}
        </h2>
        <div className="run-list">
          {visibleGenerated.map((item) => (
            <article key={item.id}>
              <div>
                <strong>
                  {item.workerName} · {optionLabel(item.documentType, locale)}
                </strong>
                <p>{formatDate(item.paymentDate ?? item.createdAt, locale)}</p>
                <small>
                  {item.locale} · {optionLabel(item.status, locale)}
                </small>
              </div>
              <button
                type="button"
                onClick={() =>
                  void saveBlob(
                    `/v1/tenants/${tenant.id}/documents/${item.id}`,
                    `${item.documentType}-${item.workerName}-${item.paymentDate ?? item.createdAt.slice(0, 10)}.pdf`,
                  )
                }
              >
                {locale === "es" ? "Descargar" : "Download"}
              </button>
            </article>
          ))}
        </div>
        {!busy && !visibleGenerated.length && (
          <p>
            {locale === "es"
              ? "No hay documentos generados que coincidan."
              : "No matching generated documents."}
          </p>
        )}
      </section>
      <section className="settings-form">
        <h2>
          {locale === "es"
            ? `Expediente de ${selected ? `${selected.firstName} ${selected.lastName}` : "personal"}`
            : `${selected ? `${selected.firstName} ${selected.lastName}'s` : "Personnel"} file`}
        </h2>
        <div className="run-list">
          {visiblePersonnel.map((item) => (
            <article key={item.id}>
              <div>
                <strong>
                  {item.title} · {optionLabel(item.category, locale)}
                </strong>
                <p>
                  {item.fileName} · {Math.ceil(item.byteSize / 1024)} KB
                </p>
                <small>
                  {item.visibility} · {optionLabel(item.status, locale)}
                  {item.archiveReason ? ` · ${item.archiveReason}` : ""}
                </small>
              </div>
              <div className="run-actions">
                <button
                  type="button"
                  onClick={() =>
                    void saveBlob(
                      `/v1/tenants/${tenant.id}/workers/${workerId}/personnel-documents/${item.id}`,
                      item.fileName,
                    )
                  }
                >
                  {locale === "es" ? "Descargar" : "Download"}
                </button>
                {item.status === "active" && (
                  <button type="button" onClick={() => archive(item)}>
                    {locale === "es" ? "Archivar" : "Archive"}
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
        {!busy && !visiblePersonnel.length && (
          <p>
            {locale === "es"
              ? "Este expediente todavía no contiene documentos."
              : "This personnel file does not contain documents yet."}
          </p>
        )}
      </section>
      <OperationalError value={error} locale={locale} />
      {archiveDraft && (
        <dialog
          className="workflow-dialog"
          open
          aria-labelledby="document-archive-title"
        >
          <form onSubmit={submitArchive}>
            <header>
              <div>
                <p className="kicker">
                  {locale === "es"
                    ? "CAMBIO DE EXPEDIENTE AUDITADO"
                    : "AUDITED FILE CHANGE"}
                </p>
                <h2 id="document-archive-title">
                  {locale === "es"
                    ? "Archivar documento de personal"
                    : "Archive personnel document"}
                </h2>
                <p>
                  {selected
                    ? `${selected.firstName} ${selected.lastName} · `
                    : ""}
                  {archiveDraft.item.title}
                </p>
              </div>
              <button
                type="button"
                aria-label={locale === "es" ? "Cerrar" : "Close"}
                onClick={() => setArchiveDraft(null)}
              >
                ×
              </button>
            </header>
            <p className="form-detail">
              {archiveDraft.item.category} · {archiveDraft.item.fileName}.{" "}
              {locale === "es"
                ? "El archivo se conserva de forma privada y deja de aparecer como documento activo."
                : "The file remains privately retained and no longer appears as an active document."}
            </p>
            <div className="form-grid">
              <label className="full-width">
                {locale === "es"
                  ? "Razón para archivar"
                  : "Reason for archiving"}
                <textarea
                  required
                  minLength={3}
                  maxLength={1000}
                  value={archiveDraft.reason}
                  onChange={(e) =>
                    setArchiveDraft({
                      ...archiveDraft,
                      reason: e.currentTarget.value,
                    })
                  }
                />
              </label>
            </div>
            <OperationalError value={error} locale={locale} />
            <footer>
              <button type="button" onClick={() => setArchiveDraft(null)}>
                {locale === "es" ? "Cancelar" : "Cancel"}
              </button>
              <button
                className="danger-action"
                disabled={busy || archiveDraft.reason.trim().length < 3}
              >
                {busy ? (
                  <LoaderCircle className="spin" />
                ) : locale === "es" ? (
                  "Archivar documento"
                ) : (
                  "Archive document"
                )}
              </button>
            </footer>
          </form>
        </dialog>
      )}
    </div>
  );
}
function EmployeeDocumentLibrary({
  locale,
  tenant,
  contractor = false,
}: {
  locale: Locale;
  tenant: Tenant;
  contractor?: boolean;
}) {
  const [documents, setDocuments] = useState<SelfDocument[]>([]),
    [personnelDocuments, setPersonnelDocuments] = useState<
      SelfPersonnelDocument[]
    >([]),
    [busy, setBusy] = useState(true),
    [error, setError] = useState("");
  const load = async () => {
    setBusy(true);
    setError("");
    const [response, personnelResponse] = await Promise.all([
      api(`/v1/tenants/${tenant.id}/self/documents`),
      api(`/v1/tenants/${tenant.id}/self/personnel-documents`),
    ]);
    if (response.ok)
      setDocuments(
        ((await response.json()) as { documents: SelfDocument[] }).documents,
      );
    else setError(`documents:${response.status}`);
    if (personnelResponse.ok)
      setPersonnelDocuments(
        (
          (await personnelResponse.json()) as {
            documents: SelfPersonnelDocument[];
          }
        ).documents,
      );
    else setError(`personnel-documents:${personnelResponse.status}`);
    setBusy(false);
  };
  useEffect(() => {
    void load();
  }, [tenant.id]);
  const download = async (item: SelfDocument) => {
    setBusy(true);
    setError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/self/documents/${item.id}`,
    );
    if (response.ok) {
      const url = URL.createObjectURL(await response.blob()),
        anchor = document.createElement("a"),
        kind =
          item.documentType === "pay_stub"
            ? "pay-stub"
            : item.documentType === "tax_withholding_certificate"
              ? "499-R-4"
              : "contractor-statement";
      anchor.href = url;
      anchor.download = `${kind}-${item.paymentDate ?? item.createdAt.slice(0, 10)}-${item.locale}.pdf`;
      anchor.click();
      URL.revokeObjectURL(url);
    } else setError(`document:${response.status}`);
    setBusy(false);
  };
  const downloadPersonnel = async (item: SelfPersonnelDocument) => {
    const response = await api(
      `/v1/tenants/${tenant.id}/self/personnel-documents/${item.id}`,
    );
    if (!response.ok) {
      setError(`personnel-document:${response.status}`);
      return;
    }
    const url = URL.createObjectURL(await response.blob()),
      anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = item.fileName;
    anchor.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="content">
      <p className="kicker">{tenant.displayName}</p>
      <h1>{locale === "es" ? "Mis documentos" : "My documents"}</h1>
      <p className="lead">
        {contractor
          ? locale === "es"
            ? "Documentos de personal autorizados y estados de servicios disponibles solamente para su cuenta."
            : "Authorized personnel documents and professional-service statements available only to your account."
          : locale === "es"
            ? "Documentos de personal autorizados, comprobantes y certificados disponibles solamente para su cuenta."
            : "Authorized personnel documents, pay stubs, and certificates available only to your account."}
      </p>
      {busy ? (
        <LoaderCircle className="spin" />
      ) : documents.length === 0 && personnelDocuments.length === 0 ? (
        <section className="empty-module">
          <span>
            <FileText />
          </span>
          <h2>
            {locale === "es"
              ? "No hay documentos disponibles"
              : "No documents available"}
          </h2>
          <p>
            {locale === "es"
              ? "Los documentos aparecerán cuando gerencia comparta uno o después de finalizar un pago o certificación."
              : "Documents appear when management shares one or after payment or certification."}
          </p>
        </section>
      ) : (
        <>
          <div className="run-list">
            {personnelDocuments.map((item) => (
              <article key={item.id}>
                <div>
                  <strong>{item.title}</strong>
                  <p>
                    {optionLabel(item.category, locale)} ·{" "}
                    {formatDate(item.effectiveOn ?? item.createdAt, locale)}
                    {item.expiresOn
                      ? ` — ${formatDate(item.expiresOn, locale)}`
                      : ""}
                  </p>
                  <small>
                    {item.fileName} · {Math.ceil(item.byteSize / 1024)} KB
                  </small>
                </div>
                <button
                  type="button"
                  onClick={() => void downloadPersonnel(item)}
                >
                  {locale === "es" ? "Descargar" : "Download"}
                </button>
              </article>
            ))}
          </div>
          <div className="run-list">
            {documents.map((item) => (
              <article key={item.id}>
                <div>
                  <strong>
                    {item.documentType === "pay_stub"
                      ? locale === "es"
                        ? "Comprobante de nómina"
                        : "Pay stub"
                      : item.documentType === "tax_withholding_certificate"
                        ? locale === "es"
                          ? "Certificado de retención 499 R-4"
                          : "499 R-4 withholding certificate"
                        : locale === "es"
                          ? "Estado de servicios profesionales"
                          : "Professional-services statement"}
                  </strong>
                  <p>
                    {formatDate(item.paymentDate ?? item.createdAt, locale)}
                    {item.periodStart
                      ? ` · ${formatDateRange(item.periodStart, item.periodEnd, locale, "—")}`
                      : ""}
                  </p>
                  <small>
                    {item.locale} · {optionLabel(item.status, locale)}
                  </small>
                </div>
                <button type="button" onClick={() => void download(item)}>
                  {locale === "es" ? "Descargar PDF" : "Download PDF"}
                </button>
              </article>
            ))}
          </div>
        </>
      )}
      <OperationalError value={error} locale={locale} />
    </div>
  );
}

type SelfAddress = {
  line1: string;
  line2: string | null;
  city: string;
  state: string;
  postalCode: string;
  country: string;
};
type SelfProfile = {
  id: string;
  firstName: string;
  middleName: string | null;
  lastName: string | null;
  preferredName: string | null;
  workerNumber: string | null;
  workerType: string;
  positionTitle: string | null;
  phoneE164: string | null;
  secondaryPhoneE164: string | null;
  email: string | null;
  startDate: string | null;
  address: SelfAddress | null;
  taxpayerIdentifierPresent: boolean;
  maskedTaxpayerId: string | null;
};
type ReadinessItem = { code: string; stage: string; complete: boolean };
const readinessLabels: Record<string, { es: string; en: string }> = {
  legal_name: { es: "Nombre legal completo", en: "Complete legal name" },
  worker_classification: {
    es: "Clasificación del trabajador",
    en: "Worker classification",
  },
  position_title: { es: "Puesto o título", en: "Position or title" },
  start_date: { es: "Fecha de inicio", en: "Start date" },
  time_entry_method: {
    es: "Método de registro de tiempo",
    en: "Time-entry method",
  },
  configured_schedule: {
    es: "Horario configurado",
    en: "Configured schedule",
  },
  worker_number: { es: "Número de trabajador", en: "Worker number" },
  effective_pay_rate: {
    es: "Tarifa de pago vigente",
    en: "Effective pay rate",
  },
  certified_499_r4: { es: "499 R-4 certificada", en: "Certified 499 R-4" },
  residential_address: {
    es: "Dirección residencial",
    en: "Residential address",
  },
  phone: { es: "Número de teléfono", en: "Phone number" },
  taxpayer_identifier: {
    es: "Seguro Social / identificación contributiva",
    en: "Social Security / taxpayer identifier",
  },
  email: { es: "Correo electrónico", en: "Email address" },
};
function EmployeeSelfProfile({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const emptyAddress: SelfAddress = {
    line1: "",
    line2: null,
    city: "",
    state: "PR",
    postalCode: "",
    country: "PR",
  };
  const [profile, setProfile] = useState<SelfProfile | null>(null),
    [readiness, setReadiness] = useState<{
      percent: number;
      complete: boolean;
      items: ReadinessItem[];
      missing: ReadinessItem[];
    } | null>(null),
    [taxpayerId, setTaxpayerId] = useState(""),
    [busy, setBusy] = useState(true),
    [saved, setSaved] = useState(false),
    [error, setError] = useState("");
  const load = async () => {
    setBusy(true);
    const response = await api(`/v1/tenants/${tenant.id}/self/profile`);
    if (response.ok) {
      const body = (await response.json()) as {
        profile: SelfProfile;
        readiness: typeof readiness;
      };
      setProfile(body.profile);
      setReadiness(body.readiness);
    } else setError(`profile:${response.status}`);
    setBusy(false);
  };
  useEffect(() => {
    void load();
  }, [tenant.id]);
  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!profile) return;
    setBusy(true);
    setSaved(false);
    setError("");
    const response = await api(`/v1/tenants/${tenant.id}/self/profile`, {
      method: "PUT",
      body: JSON.stringify({
        preferredName: profile.preferredName || null,
        secondaryPhoneE164: profile.secondaryPhoneE164 || null,
        email: profile.email || null,
        address: profile.address?.line1 ? profile.address : null,
      }),
    });
    if (response.ok) {
      const body = (await response.json()) as {
        profile: SelfProfile;
        readiness: typeof readiness;
      };
      setProfile(body.profile);
      setReadiness(body.readiness);
      setSaved(true);
    } else setError(`profile:${response.status}`);
    setBusy(false);
  };
  const saveTaxpayerId = async () => {
    if (!taxpayerId) return;
    setBusy(true);
    setSaved(false);
    setError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/self/taxpayer-identifier`,
      { method: "PUT", body: JSON.stringify({ taxpayerId }) },
    );
    setTaxpayerId("");
    if (response.ok) {
      await load();
      setSaved(true);
    } else {
      setError(
        response.status === 409
          ? locale === "es"
            ? "Ese identificador ya pertenece a otra persona."
            : "That identifier already belongs to another person."
          : `taxpayer-id:${response.status}`,
      );
      setBusy(false);
    }
  };
  if (busy && !profile)
    return (
      <div className="content">
        <LoaderCircle className="spin" />
      </div>
    );
  if (!profile)
    return (
      <div className="content">
        <OperationalError value={error} locale={locale} />
      </div>
    );
  const address = profile.address ?? emptyAddress;
  const contractor = profile.workerType === "professional_services";
  const readinessLabel = (item: ReadinessItem) => {
    if (!contractor)
      return (
        readinessLabels[item.code]?.[locale === "es" ? "es" : "en"] ?? item.code
      );
    const contractorLabels: Record<string, { es: string; en: string }> = {
      worker_number: { es: "Número de proveedor", en: "Provider number" },
      effective_pay_rate: {
        es: "Compensación vigente",
        en: "Current compensation",
      },
    };
    return (
      contractorLabels[item.code]?.[locale === "es" ? "es" : "en"] ??
      readinessLabels[item.code]?.[locale === "es" ? "es" : "en"] ??
      item.code
    );
  };
  const readinessStage = (stage: string) =>
    contractor && stage === "payroll"
      ? locale === "es"
        ? "Pago de servicios"
        : "Service payment"
      : optionLabel(stage, locale);
  const setAddress = (key: keyof SelfAddress, value: string | null) =>
    setProfile({ ...profile, address: { ...address, [key]: value } });
  return (
    <div className="content">
      <p className="kicker">{tenant.displayName}</p>
      <h1>
        {locale === "es"
          ? "Mi perfil y preparación"
          : "My profile and readiness"}
      </h1>
      <p className="lead">
        {contractor
          ? locale === "es"
            ? "Complete la información que usted controla. Los requisitos administrativos indican qué falta antes de registrar servicios, recibir pagos y preparar documentos contributivos."
            : "Complete the information you control. Administrative requirements show what remains before recording services, receiving payments, and preparing tax documents."
          : locale === "es"
            ? "Complete la información que usted controla. Los requisitos administrativos indican exactamente qué falta antes de tiempo, nómina, pago y fin de año."
            : "Complete the information you control. Administrative requirements show exactly what remains before timekeeping, payroll, payment, and year end."}
      </p>
      {readiness && (
        <section
          className={readiness.complete ? "success-note" : "operation-panel"}
        >
          <h2>
            {readiness.percent}% {locale === "es" ? "completo" : "complete"}
          </h2>
          <div className="run-list">
            {readiness.items.map((item) => (
              <article key={item.code}>
                <div>
                  <strong>{readinessLabel(item)}</strong>
                  <small>{readinessStage(item.stage)}</small>
                </div>
                <span
                  className={`run-status ${item.complete ? "approved" : "draft"}`}
                >
                  {item.complete
                    ? locale === "es"
                      ? "Listo"
                      : "Ready"
                    : locale === "es"
                      ? "Falta"
                      : "Missing"}
                </span>
              </article>
            ))}
          </div>
        </section>
      )}
      <form className="settings-form" onSubmit={save}>
        <h2>
          {locale === "es" ? "Información personal" : "Personal information"}
        </h2>
        <div className="form-grid">
          <label>
            {locale === "es" ? "Nombre legal" : "Legal name"}
            <input
              disabled
              value={`${profile.firstName} ${profile.middleName ?? ""} ${profile.lastName ?? ""}`
                .replace(/\s+/g, " ")
                .trim()}
            />
          </label>
          <label>
            {locale === "es" ? "Nombre preferido" : "Preferred name"}
            <input
              value={profile.preferredName ?? ""}
              onChange={(e) =>
                setProfile({ ...profile, preferredName: e.currentTarget.value })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Teléfono de acceso" : "Login phone"}
            <input disabled value={profile.phoneE164 ?? ""} />
            <small>
              {locale === "es"
                ? "Use el flujo seguro de cambio de teléfono."
                : "Use the secure phone-change workflow."}
            </small>
          </label>
          <label>
            {locale === "es" ? "Teléfono secundario" : "Secondary phone"}
            <input
              placeholder="+17875551234"
              value={profile.secondaryPhoneE164 ?? ""}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  secondaryPhoneE164: e.currentTarget.value,
                })
              }
            />
          </label>
          <label>
            Email
            <input
              type="email"
              value={profile.email ?? ""}
              onChange={(e) =>
                setProfile({ ...profile, email: e.currentTarget.value })
              }
            />
          </label>
          <label>
            {locale === "es"
              ? "Seguro Social / identificación contributiva"
              : "Social Security / taxpayer identifier"}
            <input
              type="password"
              inputMode="numeric"
              autoComplete="off"
              placeholder={profile.maskedTaxpayerId ?? "123-45-6789"}
              value={taxpayerId}
              onChange={(e) => setTaxpayerId(e.currentTarget.value)}
            />
            <small>
              {profile.taxpayerIdentifierPresent
                ? locale === "es"
                  ? `Guardado cifrado: ${profile.maskedTaxpayerId}`
                  : `Encrypted and stored: ${profile.maskedTaxpayerId}`
                : locale === "es"
                  ? "Requerido antes de radicación contributiva."
                  : "Required before tax filing."}
            </small>
            <button
              type="button"
              disabled={busy || !taxpayerId}
              onClick={() => void saveTaxpayerId()}
            >
              {profile.taxpayerIdentifierPresent
                ? locale === "es"
                  ? "Reemplazar"
                  : "Replace"
                : locale === "es"
                  ? "Guardar cifrado"
                  : "Encrypt and save"}
            </button>
          </label>
        </div>
        <h2>
          {locale === "es" ? "Dirección residencial" : "Residential address"}
        </h2>
        <div className="form-grid">
          <label>
            {locale === "es" ? "Dirección" : "Address line 1"}
            <input
              value={address.line1}
              onChange={(e) => setAddress("line1", e.currentTarget.value)}
            />
          </label>
          <label>
            {locale === "es" ? "Dirección 2" : "Address line 2"}
            <input
              value={address.line2 ?? ""}
              onChange={(e) =>
                setAddress("line2", e.currentTarget.value || null)
              }
            />
          </label>
          <label>
            {locale === "es" ? "Ciudad" : "City"}
            <input
              value={address.city}
              onChange={(e) => setAddress("city", e.currentTarget.value)}
            />
          </label>
          <label>
            {locale === "es" ? "Estado/territorio" : "State/territory"}
            <input
              value={address.state}
              onChange={(e) => setAddress("state", e.currentTarget.value)}
            />
          </label>
          <label>
            {locale === "es" ? "Código postal" : "Postal code"}
            <input
              value={address.postalCode}
              onChange={(e) => setAddress("postalCode", e.currentTarget.value)}
            />
          </label>
          <label>
            {locale === "es" ? "País" : "Country"}
            <input
              maxLength={2}
              value={address.country}
              onChange={(e) =>
                setAddress("country", e.currentTarget.value.toUpperCase())
              }
            />
          </label>
        </div>
        {saved && (
          <p className="success-note">
            {locale === "es"
              ? "Perfil guardado en Cloudflare."
              : "Profile saved in Cloudflare."}
          </p>
        )}
        <OperationalError value={error} locale={locale} />
        <button className="primary" disabled={busy}>
          {locale === "es" ? "Guardar perfil" : "Save profile"}
        </button>
      </form>
    </div>
  );
}

type PostalAddress = {
  line1: string;
  line2: string | null;
  city: string;
  region: string;
  postalCode: string;
  country: string;
};
type DeductionWorksheet = {
  mortgageInterestCents: number;
  donationsCents: number;
  medicalExpensesCents: number;
  studentLoanInterestCents: number;
  governmentPensionContributionsCents: number;
  iraContributionsCents: number;
  educationAccountContributionsCents: number;
  healthSavingsContributionsCents: number;
  homeCasualtyLossCents: number;
  personalPropertyCasualtyLossCents: number;
};
const blankDeductionWorksheet: DeductionWorksheet = {
  mortgageInterestCents: 0,
  donationsCents: 0,
  medicalExpensesCents: 0,
  studentLoanInterestCents: 0,
  governmentPensionContributionsCents: 0,
  iraContributionsCents: 0,
  educationAccountContributionsCents: 0,
  healthSavingsContributionsCents: 0,
  homeCasualtyLossCents: 0,
  personalPropertyCasualtyLossCents: 0,
};
type EmployeeTaxDraft = {
  personalExemption: string;
  veteran: number | boolean;
  dependents: number;
  halfDependents: number;
  deductionAllowances: number;
  deductionWorksheet: DeductionWorksheet;
  deductionWorksheetJson?: string;
  additionalWithholdingCents: number;
  additionalWithholdingBps: number;
  militarySpouseExempt: number | boolean;
  spouseName: string | null;
  spouseTaxpayerId: string | null;
  spouseTaxpayerIdLastFour?: string | null;
  postalAddress: PostalAddress | null;
  postalAddressJson?: string | null;
  dateOfBirth: string | null;
  youngWorkerElection: "not_applicable" | "claim" | "decline";
  governmentRetirementParticipant: number | boolean;
  retirementSupplementPlan: number | boolean;
  retirementSavingsProgram: number | boolean;
  retirementSavingsBps: number;
  updatedAt?: string;
};
const blankEmployeeTaxDraft: EmployeeTaxDraft = {
  personalExemption: "individual",
  veteran: false,
  dependents: 0,
  halfDependents: 0,
  deductionAllowances: 0,
  deductionWorksheet: blankDeductionWorksheet,
  additionalWithholdingCents: 0,
  additionalWithholdingBps: 0,
  militarySpouseExempt: false,
  spouseName: null,
  spouseTaxpayerId: null,
  postalAddress: null,
  dateOfBirth: null,
  youngWorkerElection: "not_applicable",
  governmentRetirementParticipant: false,
  retirementSupplementPlan: false,
  retirementSavingsProgram: false,
  retirementSavingsBps: 0,
};
function normalizeEmployeeTaxDraft(value: EmployeeTaxDraft) {
  let postal = value.postalAddress ?? null;
  if (!postal && value.postalAddressJson)
    try {
      postal = JSON.parse(value.postalAddressJson) as PostalAddress;
    } catch {
      postal = null;
    }
  let worksheet = value.deductionWorksheet ?? blankDeductionWorksheet;
  if (value.deductionWorksheetJson)
    try {
      worksheet = {
        ...blankDeductionWorksheet,
        ...(JSON.parse(value.deductionWorksheetJson) as DeductionWorksheet),
      };
    } catch {
      worksheet = blankDeductionWorksheet;
    }
  return {
    ...blankEmployeeTaxDraft,
    ...value,
    deductionWorksheet: worksheet,
    spouseTaxpayerId: null,
    postalAddress: postal,
  };
}
function EmployeeTaxQuestionnaire({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const [form, setForm] = useState<EmployeeTaxDraft>(blankEmployeeTaxDraft),
    [current, setCurrent] = useState<TaxElection | null>(null),
    [busy, setBusy] = useState(true),
    [saved, setSaved] = useState(false),
    [error, setError] = useState("");
  const [certificationAvailable, setCertificationAvailable] = useState(false),
    [cert, setCert] = useState({
      effectiveFrom: new Date().toISOString().slice(0, 10),
      formVersion: locale === "es" ? "499 R-4" : "499 R-4.1",
      certifiedName: "",
      sectionConfirmations: {
        filingStatus: false,
        dependents: false,
        deductions: false,
        specialConditions: false,
      },
      attestation: false,
    });
  useEffect(() => {
    setBusy(true);
    setError("");
    void api(`/v1/tenants/${tenant.id}/self/tax-questionnaire`).then(
      async (response) => {
        if (response.ok) {
          const body = (await response.json()) as {
            draft: EmployeeTaxDraft | null;
            current: TaxElection | null;
            certificationAvailable: boolean;
          };
          if (body.draft) setForm(normalizeEmployeeTaxDraft(body.draft));
          setCurrent(body.current);
          setCertificationAvailable(body.certificationAvailable);
        } else setError(`questionnaire:${response.status}`);
        setBusy(false);
      },
    );
  }, [tenant.id]);
  const payload = () => ({
    ...form,
    veteran: Boolean(form.veteran),
    militarySpouseExempt: Boolean(form.militarySpouseExempt),
    governmentRetirementParticipant: Boolean(
      form.governmentRetirementParticipant,
    ),
    retirementSupplementPlan: Boolean(form.retirementSupplementPlan),
    retirementSavingsProgram: Boolean(form.retirementSavingsProgram),
    dependents: Number(form.dependents),
    halfDependents: Number(form.halfDependents),
    deductionAllowances: Number(form.deductionAllowances),
    additionalWithholdingCents: Number(form.additionalWithholdingCents),
    retirementSavingsBps: Number(form.retirementSavingsBps),
  });
  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setSaved(false);
    setError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/self/tax-questionnaire`,
      { method: "PUT", body: JSON.stringify(payload()) },
    );
    if (response.ok) {
      const body = (await response.json()) as { draft: EmployeeTaxDraft };
      setForm(normalizeEmployeeTaxDraft(body.draft));
      setSaved(true);
    } else setError(`questionnaire:${response.status}`);
    setBusy(false);
  };
  const allSectionsConfirmed = Object.values(cert.sectionConfirmations).every(
    Boolean,
  );
  const certify = async () => {
    if (!cert.attestation || !allSectionsConfirmed) return;
    setBusy(true);
    setError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/self/tax-election/certify`,
      { method: "POST", body: JSON.stringify({ ...payload(), ...cert }) },
    );
    if (response.ok) {
      const body = (await response.json()) as { election: TaxElection };
      setCurrent(body.election);
      setSaved(false);
      setCert({
        ...cert,
        sectionConfirmations: {
          filingStatus: false,
          dependents: false,
          deductions: false,
          specialConditions: false,
        },
        attestation: false,
      });
    } else setError(`certification:${response.status}`);
    setBusy(false);
  };
  const money = (cents: number) =>
    new Intl.NumberFormat(locale === "es" ? "es-PR" : "en-US", {
      style: "currency",
      currency: "USD",
    }).format(cents / 100);
  const worksheetFields: {
      key: keyof DeductionWorksheet;
      es: string;
      en: string;
    }[] = [
      {
        key: "mortgageInterestCents",
        es: "Intereses hipotecarios",
        en: "Mortgage interest",
      },
      { key: "donationsCents", es: "Donativos", en: "Donations" },
      {
        key: "medicalExpensesCents",
        es: "Gastos médicos",
        en: "Medical expenses",
      },
      {
        key: "studentLoanInterestCents",
        es: "Intereses de préstamos estudiantiles",
        en: "Student-loan interest",
      },
      {
        key: "governmentPensionContributionsCents",
        es: "Aportaciones a pensión gubernamental",
        en: "Government pension contributions",
      },
      {
        key: "iraContributionsCents",
        es: "Aportaciones a IRA",
        en: "IRA contributions",
      },
      {
        key: "educationAccountContributionsCents",
        es: "Cuenta de aportación educativa",
        en: "Education account contributions",
      },
      {
        key: "healthSavingsContributionsCents",
        es: "Cuenta de ahorro de salud",
        en: "Health savings contributions",
      },
      {
        key: "homeCasualtyLossCents",
        es: "Pérdida fortuita de residencia",
        en: "Home casualty loss",
      },
      {
        key: "personalPropertyCasualtyLossCents",
        es: "Pérdida fortuita de bienes muebles",
        en: "Personal-property casualty loss",
      },
    ],
    worksheetTotal = Object.values(form.deductionWorksheet).reduce(
      (sum, value) => sum + value,
      0,
    ),
    allowanceLimit =
      Math.floor(worksheetTotal / 50000) +
      (worksheetTotal % 50000 > 25000 ? 1 : 0);
  return (
    <div className="content">
      <p className="kicker">{tenant.displayName}</p>
      <h1>
        {locale === "es"
          ? "Mi información de retención"
          : "My withholding information"}
      </h1>
      <p className="lead">
        {locale === "es"
          ? "Complete la información que Hacienda utiliza para calcular la retención de contribución sobre ingresos de cada nómina."
          : "Complete the information Hacienda uses to calculate income-tax withholding on each payroll."}
      </p>
      {current && (
        <section className="success-note">
          <strong>
            {locale === "es"
              ? "Elección certificada vigente"
              : "Current certified election"}
          </strong>
          <br />
          {current.formVersion} · {formatDate(current.effectiveFrom, locale)} ·{" "}
          {optionLabel(current.personalExemption, locale)} ·{" "}
          {current.dependents} {locale === "es" ? "dependientes" : "dependents"}{" "}
          · {money(current.additionalWithholdingCents)}{" "}
          {locale === "es" ? "adicional" : "additional"}
        </section>
      )}
      {current &&
        current.dependents + current.halfDependents > 8 &&
        !["submitted", "confirmed"].includes(
          current.haciendaSubmissionStatus ?? "",
        ) && (
          <section className="operation-panel">
            <strong>
              {locale === "es"
                ? "Acción requerida: copia para Hacienda"
                : "Action required: Hacienda copy"}
            </strong>
            <p>
              {locale === "es"
                ? "Su elección reclama más de ocho dependientes. Entregue al patrono la declaración de sustento y la copia requerida para documentar su envío al Negociado de Auditoría Fiscal."
                : "Your election claims more than eight dependents. Give the employer the supporting statement and required copy so submission to Hacienda's Fiscal Audit Bureau can be documented."}
            </p>
          </section>
        )}
      {busy ? (
        <LoaderCircle className="spin" />
      ) : error ? (
        <section className="form-error">
          <strong>
            {locale === "es"
              ? "Cuestionario no disponible"
              : "Questionnaire unavailable"}
          </strong>
          <p>
            {locale === "es"
              ? "La persona seleccionada no es un empleado activo autorizado para completar el 499 R-4, o esta vista no tiene acceso a su registro. No se muestran valores predeterminados como si fueran datos guardados."
              : "The selected person is not an active employee authorized to complete Form 499 R-4, or this view cannot access the record. Default values are not shown as if they were saved data."}
          </p>
        </section>
      ) : (
        <form className="settings-form" onSubmit={save}>
          <h2>499 R-4 / 499 R-4.1</h2>
          <div className="form-grid">
            <label>
              {locale === "es"
                ? "Exención personal y cómputo matrimonial"
                : "Personal exemption and married computation"}
              <select
                value={form.personalExemption}
                onChange={(e) =>
                  setForm({ ...form, personalExemption: e.currentTarget.value })
                }
              >
                <option value="none">
                  {locale === "es"
                    ? "No reclamar exención"
                    : "Claim no exemption"}
                </option>
                <option value="individual">
                  {locale === "es"
                    ? "Contribuyente individual"
                    : "Individual taxpayer"}
                </option>
                <option value="married_joint_full">
                  {locale === "es"
                    ? "Casados: exención completa"
                    : "Married: full exemption"}
                </option>
                <option value="married_joint_half_or_optional">
                  {locale === "es"
                    ? "Casados: mitad/cómputo opcional"
                    : "Married: half/optional computation"}
                </option>
              </select>
              <small>
                {locale === "es"
                  ? "Seleccione lo indicado en su formulario de Hacienda."
                  : "Select the option shown on your Hacienda form."}
              </small>
            </label>
            <label>
              {locale === "es" ? "Dependientes completos" : "Full dependents"}
              <input
                type="number"
                min="0"
                max="99"
                required
                value={form.dependents}
                onInput={(e) =>
                  setForm({
                    ...form,
                    dependents: Number(e.currentTarget.value),
                  })
                }
              />
              <small>
                {locale === "es"
                  ? "Dependientes reclamados completamente por usted."
                  : "Dependents claimed fully by you."}
              </small>
            </label>
            <label>
              {locale === "es"
                ? "Medios dependientes / custodia compartida"
                : "Half dependents / joint custody"}
              <input
                type="number"
                min="0"
                max="99"
                required
                value={form.halfDependents}
                onInput={(e) =>
                  setForm({
                    ...form,
                    halfDependents: Number(e.currentTarget.value),
                  })
                }
              />
              <small>
                {locale === "es"
                  ? "Incluya solamente los reclamados a mitad."
                  : "Include only dependents claimed at one-half."}
              </small>
            </label>
            <label>
              {locale === "es"
                ? "Concesiones por deducciones"
                : "Deduction allowances"}
              <input
                type="number"
                min="0"
                max="99"
                required
                value={form.deductionAllowances}
                onInput={(e) =>
                  setForm({
                    ...form,
                    deductionAllowances: Number(e.currentTarget.value),
                  })
                }
              />
              <small>
                {locale === "es"
                  ? "Cantidad calculada en la hoja de concesiones del formulario."
                  : "Amount calculated on the form's deduction-allowance worksheet."}
              </small>
            </label>
            {worksheetFields.map((item) => (
              <label key={item.key}>
                {locale === "es" ? item.es : item.en}
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.deductionWorksheet[item.key] / 100}
                  onInput={(e) =>
                    setForm({
                      ...form,
                      deductionWorksheet: {
                        ...form.deductionWorksheet,
                        [item.key]: Math.round(
                          Number(e.currentTarget.value) * 100,
                        ),
                      },
                    })
                  }
                />
              </label>
            ))}
            <section className="success-note">
              <strong>
                {locale === "es" ? "Total de deducciones" : "Total deductions"}:{" "}
                {money(worksheetTotal)}
              </strong>
              <br />
              {locale === "es"
                ? `Máximo según la regla de $500: ${allowanceLimit} concesiones`
                : `Maximum under the $500 rule: ${allowanceLimit} allowances`}
            </section>
            <label>
              {locale === "es"
                ? "Retención adicional por nómina"
                : "Additional withholding per payroll"}
              <input
                type="number"
                min="0"
                step="0.01"
                required
                value={form.additionalWithholdingCents / 100}
                onInput={(e) =>
                  setForm({
                    ...form,
                    additionalWithholdingCents: Math.round(
                      Number(e.currentTarget.value) * 100,
                    ),
                  })
                }
              />
              <small>
                {locale === "es"
                  ? "Cantidad adicional en dólares; puede ser cero."
                  : "Additional dollar amount; it may be zero."}
              </small>
            </label>
            <label>
              {locale === "es"
                ? "Porcentaje adicional por nómina"
                : "Additional percentage per payroll"}
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                required
                value={form.additionalWithholdingBps / 100}
                onInput={(e) =>
                  setForm({
                    ...form,
                    additionalWithholdingBps: Math.round(
                      Number(e.currentTarget.value) * 100,
                    ),
                  })
                }
              />
              <small>
                {locale === "es"
                  ? "Porcentaje adicional de los salarios, según la Parte D."
                  : "Additional percentage of wages, as elected in Part D."}
              </small>
            </label>
            <label>
              {locale === "es"
                ? "Nombre legal del cónyuge (solo si aplica)"
                : "Spouse legal name (only if applicable)"}
              <input
                value={form.spouseName ?? ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    spouseName: e.currentTarget.value || null,
                  })
                }
              />
            </label>
            <label>
              {locale === "es"
                ? "Seguro Social del cónyuge (solo si aplica)"
                : "Spouse Social Security number (only if applicable)"}
              <input
                inputMode="numeric"
                autoComplete="off"
                placeholder={
                  form.spouseTaxpayerIdLastFour
                    ? `***-**-${form.spouseTaxpayerIdLastFour}`
                    : "000-00-0000"
                }
                value={form.spouseTaxpayerId ?? ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    spouseTaxpayerId: e.currentTarget.value || null,
                  })
                }
              />
              <small>
                {locale === "es"
                  ? "Se cifra antes de guardarse; nunca se devuelve completo."
                  : "Encrypted before storage; never returned in full."}
              </small>
            </label>
            <label>
              {locale === "es" ? "Fecha de nacimiento (solo para elección de joven trabajador)" : "Date of birth (only for young-worker election)"}
              <input
                type="date"
                value={form.dateOfBirth ?? ""}
                onInput={(e) =>
                  setForm({
                    ...form,
                    dateOfBirth: e.currentTarget.value || null,
                  })
                }
              />
            </label>
            <label>
              {locale === "es"
                ? "Elección de joven trabajador"
                : "Young-worker election"}
              <select
                value={form.youngWorkerElection}
                onChange={(e) =>
                  setForm({
                    ...form,
                    youngWorkerElection: e.currentTarget
                      .value as EmployeeTaxDraft["youngWorkerElection"],
                  })
                }
              >
                <option value="not_applicable">
                  {locale === "es" ? "No aplica" : "Not applicable"}
                </option>
                <option value="claim">
                  {locale === "es"
                    ? "Reclamar exención anual de $40,000"
                    : "Claim annual $40,000 exemption"}
                </option>
                <option value="decline">
                  {locale === "es"
                    ? "No aplicar la exención en este patrono"
                    : "Do not apply at this employer"}
                </option>
              </select>
            </label>
            <label>
              {locale === "es" ? "Dirección postal" : "Postal address"}
              <input
                placeholder={locale === "es" ? "Línea 1" : "Line 1"}
                value={form.postalAddress?.line1 ?? ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    postalAddress: {
                      ...(form.postalAddress ?? {
                        line1: "",
                        line2: null,
                        city: "",
                        region: "PR",
                        postalCode: "",
                        country: "PR",
                      }),
                      line1: e.currentTarget.value,
                    },
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Ciudad postal" : "Postal city"}
              <input
                value={form.postalAddress?.city ?? ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    postalAddress: {
                      ...(form.postalAddress ?? {
                        line1: "",
                        line2: null,
                        city: "",
                        region: "PR",
                        postalCode: "",
                        country: "PR",
                      }),
                      city: e.currentTarget.value,
                    },
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Código postal" : "Postal code"}
              <input
                value={form.postalAddress?.postalCode ?? ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    postalAddress: {
                      ...(form.postalAddress ?? {
                        line1: "",
                        line2: null,
                        city: "",
                        region: "PR",
                        postalCode: "",
                        country: "PR",
                      }),
                      postalCode: e.currentTarget.value,
                    },
                  })
                }
              />
            </label>
            <label>
              {locale === "es"
                ? "Aportación al programa de ahorro para retiro (opcional)"
                : "Retirement savings contribution (optional)"}
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={form.retirementSavingsBps / 100}
                onInput={(e) =>
                  setForm({
                    ...form,
                    retirementSavingsBps: Math.round(
                      Number(e.currentTarget.value) * 100,
                    ),
                  })
                }
              />
            </label>
          </div>
          <label className="check-label">
            <input
              type="checkbox"
              checked={Boolean(form.veteran)}
              onChange={(e) =>
                setForm({ ...form, veteran: e.currentTarget.checked })
              }
            />
            {locale === "es"
              ? "Soy veterano(a) elegible según el formulario"
              : "I am an eligible veteran under the form"}
          </label>
          <label className="check-label">
            <input
              type="checkbox"
              checked={Boolean(form.militarySpouseExempt)}
              onChange={(e) =>
                setForm({
                  ...form,
                  militarySpouseExempt: e.currentTarget.checked,
                })
              }
            />
            {locale === "es"
              ? "Reclamo la exención aplicable de cónyuge militar"
              : "I claim the applicable military-spouse exemption"}
          </label>
          <label className="check-label">
            <input
              type="checkbox"
              checked={Boolean(form.governmentRetirementParticipant)}
              onChange={(e) =>
                setForm({
                  ...form,
                  governmentRetirementParticipant: e.currentTarget.checked,
                  retirementSupplementPlan: e.currentTarget.checked
                    ? form.retirementSupplementPlan
                    : false,
                  retirementSavingsProgram: e.currentTarget.checked
                    ? form.retirementSavingsProgram
                    : false,
                  retirementSavingsBps: e.currentTarget.checked
                    ? form.retirementSavingsBps
                    : 0,
                })
              }
            />
            {locale === "es"
              ? "Participo en un sistema gubernamental de pensión o retiro"
              : "I participate in a government pension or retirement system"}
          </label>
          {Boolean(form.governmentRetirementParticipant) && (
            <>
              <label className="check-label">
                <input
                  type="checkbox"
                  checked={Boolean(form.retirementSupplementPlan)}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      retirementSupplementPlan: e.currentTarget.checked,
                    })
                  }
                />
                {locale === "es"
                  ? "Plan Suplementario de Retención para el Retiro"
                  : "Retirement Withholding Supplementary Plan"}
              </label>
              <label className="check-label">
                <input
                  type="checkbox"
                  checked={Boolean(form.retirementSavingsProgram)}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      retirementSavingsProgram: e.currentTarget.checked,
                      retirementSavingsBps: e.currentTarget.checked
                        ? form.retirementSavingsBps
                        : 0,
                    })
                  }
                />
                {locale === "es"
                  ? "Programa de Cuentas de Ahorro para el Retiro"
                  : "Retirement Savings Accounts Program"}
              </label>
            </>
          )}
          <p>
            {certificationAvailable
              ? locale === "es"
                ? "Puede guardar un borrador o certificarlo. La certificación reemplaza la elección vigente desde la fecha indicada y se usará en nómina."
                : "You may save a draft or certify it. Certification replaces the current election from the stated date and will be used by payroll."
              : locale === "es"
                ? "Guardar conserva sus respuestas como borrador y no cambia la retención certificada. La vista de revisión no puede fabricar la firma del empleado."
                : "Save keeps your answers as a draft and does not change certified withholding. Review mode cannot manufacture an employee signature."}
          </p>
          <OperationalError value={error} locale={locale} />
          {saved && (
            <p className="success-note">
              {locale === "es"
                ? "Borrador guardado en Cloudflare. No se certificó ninguna elección."
                : "Draft saved in Cloudflare. No election was certified."}
            </p>
          )}
          <button className="primary" disabled={busy}>
            {locale === "es" ? "Guardar borrador" : "Save draft"}
          </button>
          {certificationAvailable && (
            <section className="operation-panel">
              <h2>
                {locale === "es"
                  ? "Revisión y certificación digital"
                  : "Review and digital certification"}
              </h2>
              <p>
                {locale === "es"
                  ? "Confirme cada sección. Ninguna respuesta predeterminada se considerará una elección sin estas confirmaciones."
                  : "Confirm every section. No displayed default will be treated as your election without these confirmations."}
              </p>
              <label className="check-label">
                <input
                  type="checkbox"
                  checked={cert.sectionConfirmations.filingStatus}
                  onChange={(e) =>
                    setCert({
                      ...cert,
                      sectionConfirmations: {
                        ...cert.sectionConfirmations,
                        filingStatus: e.currentTarget.checked,
                      },
                    })
                  }
                />
                {locale === "es"
                  ? "Revisé mi estado y exención personal, incluyendo veterano si aplica."
                  : "I reviewed my filing status and personal exemption, including veteran status if applicable."}
              </label>
              <label className="check-label">
                <input
                  type="checkbox"
                  checked={cert.sectionConfirmations.dependents}
                  onChange={(e) =>
                    setCert({
                      ...cert,
                      sectionConfirmations: {
                        ...cert.sectionConfirmations,
                        dependents: e.currentTarget.checked,
                      },
                    })
                  }
                />
                {locale === "es"
                  ? "Revisé los dependientes completos y de custodia compartida."
                  : "I reviewed full and joint-custody dependents."}
              </label>
              <label className="check-label">
                <input
                  type="checkbox"
                  checked={cert.sectionConfirmations.deductions}
                  onChange={(e) =>
                    setCert({
                      ...cert,
                      sectionConfirmations: {
                        ...cert.sectionConfirmations,
                        deductions: e.currentTarget.checked,
                      },
                    })
                  }
                />
                {locale === "es"
                  ? "Revisé cada partida de la hoja de deducciones y las concesiones reclamadas."
                  : "I reviewed every deduction-workpaper amount and the allowances claimed."}
              </label>
              <label className="check-label">
                <input
                  type="checkbox"
                  checked={cert.sectionConfirmations.specialConditions}
                  onChange={(e) =>
                    setCert({
                      ...cert,
                      sectionConfirmations: {
                        ...cert.sectionConfirmations,
                        specialConditions: e.currentTarget.checked,
                      },
                    })
                  }
                />
                {locale === "es"
                  ? "Revisé la retención adicional, cónyuge militar, joven trabajador y retiro gubernamental."
                  : "I reviewed additional withholding, military spouse, young-worker, and government-retirement selections."}
              </label>
              <div className="form-grid">
                <label>
                  {locale === "es" ? "Vigente desde" : "Effective from"}
                  <input
                    type="date"
                    value={cert.effectiveFrom}
                    onInput={(e) =>
                      setCert({ ...cert, effectiveFrom: e.currentTarget.value })
                    }
                  />
                </label>
                <label>
                  {locale === "es" ? "Versión del formulario" : "Form version"}
                  <input
                    value={cert.formVersion}
                    onChange={(e) =>
                      setCert({ ...cert, formVersion: e.currentTarget.value })
                    }
                  />
                </label>
                <label>
                  {locale === "es"
                    ? "Nombre legal completo"
                    : "Full legal name"}
                  <input
                    value={cert.certifiedName}
                    onChange={(e) =>
                      setCert({ ...cert, certifiedName: e.currentTarget.value })
                    }
                  />
                </label>
              </div>
              <label className="check-label">
                <input
                  type="checkbox"
                  checked={cert.attestation}
                  onChange={(e) =>
                    setCert({ ...cert, attestation: e.currentTarget.checked })
                  }
                />
                {locale === "es"
                  ? "Bajo mi cuenta autenticada, certifico que estas respuestas son correctas y constituyen mi elección de retención."
                  : "Under my authenticated account, I certify that these answers are correct and constitute my withholding election."}
              </label>
              <button
                type="button"
                className="primary"
                disabled={
                  busy ||
                  !allSectionsConfirmed ||
                  !cert.attestation ||
                  !cert.certifiedName
                }
                onClick={() => void certify()}
              >
                {locale === "es" ? "Certificar elección" : "Certify election"}
              </button>
            </section>
          )}
        </form>
      )}
    </div>
  );
}

type ComplianceExport = {
  id: string;
  reportType: string;
  periodKey: string;
  specVersion: string;
  status: "blocked" | "generated";
  diagnosticsJson: string;
  contentSha256: string | null;
  rowCount: number;
  createdAt: string;
};
function LegacyComplianceExports({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const currentYear = new Date().getFullYear(),
    [reportType, setReportType] = useState("w2pr_workpaper"),
    [year, setYear] = useState(currentYear),
    [quarter, setQuarter] = useState(1),
    [items, setItems] = useState<ComplianceExport[]>([]),
    [busy, setBusy] = useState(true),
    [message, setMessage] = useState("");
  const quarterly =
    reportType === "quarterly_941_workpaper" ||
    reportType === "dtrh_wages_workpaper";
  const load = async () => {
    setBusy(true);
    const response = await api(`/v1/tenants/${tenant.id}/compliance/exports`);
    if (response.ok)
      setItems(
        ((await response.json()) as { exports: ComplianceExport[] }).exports,
      );
    setBusy(false);
  };
  useEffect(() => {
    void load();
  }, [tenant.id]);
  const generate = async () => {
    setBusy(true);
    setMessage("");
    const response = await api(`/v1/tenants/${tenant.id}/compliance/exports`, {
      method: "POST",
      body: JSON.stringify({
        reportType,
        year,
        quarter: quarterly ? quarter : null,
      }),
    });
    if (response.status === 409) {
      const body = (await response.json()) as {
        export: { diagnostics: string[] };
      };
      setMessage(
        (locale === "es" ? "Bloqueado: " : "Blocked: ") +
          body.export.diagnostics.join(" · "),
      );
    } else if (!response.ok)
      setMessage(`${locale === "es" ? "Error" : "Error"}: ${response.status}`);
    else
      setMessage(
        locale === "es"
          ? "Hoja de trabajo generada y guardada de forma privada."
          : "Workpaper generated and stored privately.",
      );
    await load();
  };
  const download = async (item: ComplianceExport) => {
    const response = await api(
      `/v1/tenants/${tenant.id}/compliance/exports/${item.id}`,
    );
    if (!response.ok) {
      setMessage(`download:${response.status}`);
      return;
    }
    const url = URL.createObjectURL(await response.blob()),
      anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${item.reportType}-${item.periodKey}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };
  return (
    <section className="settings-form">
      <h2>
        {locale === "es" ? "Paquetes de cumplimiento" : "Compliance packages"}
      </h2>
      <p>
        {locale === "es"
          ? "Genere hojas de trabajo validadas desde nóminas finalizadas. Se bloquean si faltan cuentas patronales, identificación cifrada, direcciones o datos del período. Estas hojas no se presentan automáticamente."
          : "Generate validated workpapers from finalized payroll. They are blocked when employer accounts, encrypted identity, addresses, or period data are missing. These workpapers are not filed automatically."}
      </p>
      <div className="form-grid">
        <label>
          {locale === "es" ? "Informe" : "Report"}
          <select
            value={reportType}
            onChange={(e) => setReportType(e.currentTarget.value)}
          >
            <option value="w2pr_workpaper">
              499R-2/W-2PR annual workpaper
            </option>
            <option value="quarterly_941_workpaper">
              IRS 941 quarterly workpaper
            </option>
            <option value="dtrh_wages_workpaper">
              {locale === "es"
                ? "Hoja de trabajo trimestral de salarios DTRH"
                : "DTRH quarterly wages workpaper"}
            </option>
            <option value="4806sp_workpaper">480.6SP annual workpaper</option>
          </select>
        </label>
        <label>
          {locale === "es" ? "Año" : "Year"}
          <input
            type="number"
            min="2020"
            max="2100"
            value={year}
            onInput={(e) => setYear(Number(e.currentTarget.value))}
          />
        </label>
        {quarterly && (
          <label>
            {locale === "es" ? "Trimestre" : "Quarter"}
            <select
              value={quarter}
              onChange={(e) => setQuarter(Number(e.currentTarget.value))}
            >
              {[1, 2, 3, 4].map((value) => (
                <option key={value} value={value}>
                  Q{value}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <button
        className="primary"
        disabled={busy}
        onClick={() => void generate()}
      >
        {locale === "es" ? "Validar y generar" : "Validate and generate"}
      </button>
      <OperationalFeedback
        message={message}
        isError={
          message.startsWith("Bloqueado") ||
          message.startsWith("Blocked") ||
          message.startsWith("Error")
        }
        locale={locale}
      />
      <div className="run-list">
        {items.map((item) => {
          let diagnostics: string[] = [];
          try {
            diagnostics = JSON.parse(item.diagnosticsJson) as string[];
          } catch {}
          return (
            <article key={item.id}>
              <div>
                <strong>
                  {optionLabel(item.reportType, locale)} · {item.periodKey}
                </strong>
                <p>
                  {item.specVersion} · {item.rowCount}{" "}
                  {locale === "es" ? "filas" : "rows"}
                </p>
                <small>
                  {diagnostics.join(" · ") ||
                    item.contentSha256?.slice(0, 16) ||
                    "—"}
                </small>
              </div>
              <span className={`run-status ${item.status}`}>
                {optionLabel(item.status, locale)}
              </span>
              {item.status === "generated" && (
                <button onClick={() => void download(item)}>
                  {locale === "es" ? "Descargar" : "Download"}
                </button>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}

type ReportSummary = {
  range: { from: string; to: string };
  payroll: {
    runCount: number;
    workerPaymentCount: number;
    grossCents: number;
    employeeTaxesCents: number;
    deductionsCents: number;
    netCents: number;
    employerTaxesCents: number;
    employerBenefitsCents: number;
    otherEmployerBurdenCents: number;
    totalEmployerCostCents: number;
  };
  taxes: {
    taxKey: string;
    taxableWagesCents: number;
    employeeAmountCents: number;
    employerAmountCents: number;
  }[];
  employerCosts: {
    costType: string;
    costKey: string;
    description: string;
    amountCents: number;
  }[];
  laborAllocations: {
    departmentName: string | null;
    locationName: string | null;
    costCenterName: string | null;
    hoursHundredths: number;
    grossCents: number;
    employerTaxesCents: number;
    employerBenefitsCents: number;
    otherBurdenCents: number;
    totalEmployerCostCents: number;
  }[];
  professionalServices: {
    paymentCount: number;
    serviceCompensationCents: number;
    tipCents: number;
    grossCents: number;
    subjectCents: number;
    withholdingCents: number;
    netCents: number;
  };
  leave: { balanceHours: number };
  liabilities: { status: string; itemCount: number; amountCents: number }[];
};
type Reconciliation = {
  runCount: number;
  status: string;
  checks: {
    scopeType: "payroll_run" | "contractor_payment" | "leave_accrual";
    scopeId: string;
    effectiveDate: string;
    code: string;
    expected: number;
    actual: number;
    difference: number;
    unit: "cents" | "hours";
  }[];
  totals: {
    payroll: Record<string, number>;
    taxes: {
      taxKey: string;
      taxableWagesCents: number;
      employeeAmountCents: number;
      employerAmountCents: number;
    }[];
    professionalServices: Record<string, number>;
    leave: Record<string, number>;
    liabilities: Record<string, number>;
  };
};
type Liability = {
  id: string;
  liabilityKey: string;
  periodStart: string;
  periodEnd: string;
  amountCents: number;
  dueDate: string | null;
  status: string;
  paymentDate: string | null;
  confirmation: string | null;
  filingStatus: string | null;
};
type LiabilityCredit = {
  id: string;
  reversalPayrollRunId: string;
  sourceLiabilityId: string;
  liabilityKey: string;
  amountCents: number;
  status: "open" | "applied" | "refunded";
  resolutionReference: string | null;
  resolutionReason: string | null;
  resolvedAt: string | null;
  createdAt: string;
};
type DeductionRemittanceEligible = {
  liabilityId: string;
  amountCents: number;
  periodStart: string;
  periodEnd: string;
  deductionId: string;
  deductionType: string;
  description: string;
  payee: string | null;
  referenceNumber: string | null;
  caseNumber: string | null;
  workerNumber: string;
  workerName: string;
};
type DeductionRemittanceBatch = {
  id: string;
  payee: string;
  paymentDate: string;
  paymentMethod: string;
  confirmation: string;
  totalCents: number;
  status: string;
  evidenceFileName: string;
  evidenceSha256: string;
  reason: string;
  createdAt: string;
  allocationCount: number;
};
type Efw2Profile = {
  taxYear: 2025;
  ssaUserId: string;
  softwareVendorCode: string | null;
  softwareCode: "98" | "99";
  submitterName: string;
  address: {
    line1: string;
    line2?: string | null;
    city: string;
    state: string;
    postalCode: string;
  };
  contactName: string;
  contactPhone: string;
  contactExtension: string | null;
  contactEmail: string;
  contactFax: string | null;
  preparesCode: "A" | "L" | "S" | "P" | "O";
  terminatingBusiness: boolean;
  cessationDate: string | null;
};
type Efw2Worker = {
  workerId: string;
  workerNumber: string | null;
  workerName: string;
  taxYear: number | null;
  suffix: string | null;
  commissionsCents: number | null;
  allowancesCents: number | null;
  tipsCents: number | null;
  governmentalRetirementCents: number | null;
  charitableContributionsCents: number | null;
  saveAndDoubleCents: number | null;
  codaContributionsCents: number | null;
  reimbursedExpensesFringeCents: number | null;
  employerHealthCoverageCents: number | null;
  qsehraBenefitsCents: number | null;
  exemptSalary16Cents: number | null;
  exemptCode16: string | null;
  exemptSalary17Cents: number | null;
  exemptCode17: string | null;
  exemptSalary18Cents: number | null;
  exemptCode18: string | null;
  qualifiedPhysician: number | null;
  domesticServices: number | null;
  agriculturalLabor: number | null;
  religiousServices: number | null;
  healthProfessional: number | null;
};
type Efw2Export = {
  id: string;
  submissionKind: string;
  wfid: string | null;
  resubmissionOfExportId: string | null;
  status: string;
  diagnosticsJson: string;
  recordCount: number;
  employeeCount: number;
  contentSha256: string | null;
  createdAt: string;
};
type Efw2Decision = {
  id: string;
  exportId: string;
  outcome: "accepted" | "rejected";
  agencyReference: string;
  decisionOn: string;
  reason: string;
  wfid: string | null;
  evidenceFileName: string | null;
  createdAt: string;
};
function Efw2prDecisions({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const [exports, setExports] = useState<Efw2Export[]>([]),
    [decisions, setDecisions] = useState<Efw2Decision[]>([]),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  const load = async () => {
    const response = await api(`/v1/tenants/${tenant.id}/filings/efw2pr`);
    if (response.ok) {
      const body = (await response.json()) as {
        exports: Efw2Export[];
        decisions: Efw2Decision[];
      };
      setExports(body.exports);
      setDecisions(body.decisions ?? []);
    }
  };
  useEffect(() => {
    void load();
  }, [tenant.id]);
  const accept = async (item: Efw2Export) => {
    const values = await requestWorkflowDialog({
      title:
        locale === "es"
          ? "Registrar aceptación de SURI"
          : "Record SURI acceptance",
      detail: `EFW2PR · ${item.submissionKind} · SHA-256 ${item.contentSha256?.slice(0, 16) ?? "—"}…`,
      confirmLabel: locale === "es" ? "Guardar aceptación" : "Save acceptance",
      cancelLabel: locale === "es" ? "Cancelar" : "Cancel",
      fields: [
        {
          key: "agencyReference",
          label:
            locale === "es"
              ? "Referencia de aceptación"
              : "Acceptance reference",
          required: true,
          minLength: 2,
        },
        {
          key: "decisionOn",
          label: locale === "es" ? "Fecha procesada" : "Processed date",
          type: "date",
          value: new Date().toISOString().slice(0, 10),
          required: true,
        },
        {
          key: "reason",
          label:
            locale === "es"
              ? "Razón y evidencia de la decisión"
              : "Decision reason and evidence",
          type: "textarea",
          required: true,
          minLength: 3,
          fullWidth: true,
        },
      ],
    });
    if (!values) return;
    setBusy(true);
    const response = await api(
      `/v1/tenants/${tenant.id}/filings/efw2pr/exports/${item.id}/decision`,
      {
        method: "POST",
        body: JSON.stringify({
          agencyReference: String(values.agencyReference),
          decisionOn: String(values.decisionOn),
          reason: String(values.reason),
        }),
      },
    );
    setMessage(
      response.ok
        ? locale === "es"
          ? "Aceptación inmutable registrada."
          : "Immutable acceptance recorded."
        : `decision:${response.status}`,
    );
    setBusy(false);
    await load();
  };
  const reject = async (item: Efw2Export) => {
    const values = await requestWorkflowDialog({
      title:
        locale === "es" ? "Registrar rechazo de SURI" : "Record SURI rejection",
      detail:
        locale === "es"
          ? "Conserve la referencia, el WFID y la evidencia exacta recibida de Hacienda."
          : "Retain the exact reference, WFID, and evidence received from Hacienda.",
      confirmLabel: locale === "es" ? "Guardar rechazo" : "Save rejection",
      cancelLabel: locale === "es" ? "Cancelar" : "Cancel",
      danger: true,
      fields: [
        {
          key: "agencyReference",
          label:
            locale === "es" ? "Referencia del rechazo" : "Rejection reference",
          required: true,
          minLength: 2,
        },
        {
          key: "decisionOn",
          label: locale === "es" ? "Fecha del rechazo" : "Rejection date",
          type: "date",
          value: new Date().toISOString().slice(0, 10),
          required: true,
        },
        { key: "wfid", label: "WFID", required: true, pattern: "[0-9]{6}" },
        {
          key: "reason",
          label:
            locale === "es"
              ? "Razón exacta del rechazo"
              : "Exact rejection reason",
          type: "textarea",
          required: true,
          minLength: 3,
          fullWidth: true,
        },
        {
          key: "file",
          label:
            locale === "es" ? "Evidencia de Hacienda" : "Hacienda evidence",
          type: "file",
          accept: "application/pdf,image/png,image/jpeg,image/webp",
          required: true,
          fullWidth: true,
        },
      ],
    });
    if (!values || !(values.file instanceof File)) return;
    const form = new FormData();
    form.set("agencyReference", String(values.agencyReference));
    form.set("decisionOn", String(values.decisionOn));
    form.set("reason", String(values.reason));
    form.set("wfid", String(values.wfid));
    form.set("file", values.file);
    setBusy(true);
    const response = await api(
      `/v1/tenants/${tenant.id}/filings/efw2pr/exports/${item.id}/decision/reject`,
      { method: "POST", body: form },
    );
    setMessage(
      response.ok
        ? locale === "es"
          ? "Rechazo, WFID y evidencia guardados."
          : "Rejection, WFID, and evidence retained."
        : `rejection:${response.status}`,
    );
    setBusy(false);
    await load();
  };
  const resubmit = async (item: Efw2Export, decision: Efw2Decision) => {
    setBusy(true);
    const response = await api(
        `/v1/tenants/${tenant.id}/filings/efw2pr/exports`,
        {
          method: "POST",
          body: JSON.stringify({
            taxYear: 2025,
            submissionKind: "resubmission",
            wfid: decision.wfid,
            resubmissionOfExportId: item.id,
          }),
        },
      ),
      body = (await response.json().catch(() => ({}))) as {
        export?: { diagnostics?: string[] };
        error?: string;
      };
    setMessage(
      response.ok
        ? locale === "es"
          ? "Archivo corregido reconstruido y enlazado al rechazo."
          : "Corrected file rebuilt and linked to the rejection."
        : (body.export?.diagnostics?.join(" · ") ??
            body.error ??
            `resubmission:${response.status}`),
    );
    setBusy(false);
    await load();
  };
  const downloadEvidence = async (decision: Efw2Decision) => {
    const response = await api(
      `/v1/tenants/${tenant.id}/filings/efw2pr/decisions/${decision.id}/evidence`,
    );
    if (!response.ok) return;
    const url = URL.createObjectURL(await response.blob());
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };
  const generated = exports.filter((item) => item.status === "generated");
  return (
    <section className="settings-form">
      <h2>
        {locale === "es"
          ? "Decisiones y reenvíos corregidos W-2PR"
          : "W-2PR decisions and corrected resubmissions"}
      </h2>
      <p>
        {locale === "es"
          ? "Conserve la respuesta de SURI y el WFID. Un reenvío se reconstruye con los datos corregidos, usa el WFID del rechazo y permanece enlazado al archivo rechazado."
          : "Retain the SURI response and WFID. A resubmission is rebuilt from corrected data, uses the rejection WFID, and remains linked to the rejected file."}
      </p>
      <div className="run-list">
        {generated.map((item) => {
          const decision = decisions.find(
              (value) => value.exportId === item.id,
            ),
            retried = exports.find(
              (value) => value.resubmissionOfExportId === item.id,
            );
          return (
            <article key={item.id}>
              <div>
                <strong>
                  {item.submissionKind} · {item.id.slice(0, 8)}
                </strong>
                <small>
                  {decision
                    ? `${decision.outcome} · ${decision.agencyReference}${decision.wfid ? ` · WFID ${decision.wfid}` : ""}`
                    : locale === "es"
                      ? "Pendiente de decisión SURI"
                      : "SURI decision pending"}
                </small>
              </div>
              <span className={`run-status ${decision?.outcome ?? "pending"}`}>
                {optionLabel(decision?.outcome ?? "pending", locale)}
              </span>
              {!decision && (
                <div className="run-actions">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void accept(item)}
                  >
                    {locale === "es"
                      ? "Registrar aceptación"
                      : "Record acceptance"}
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => reject(item)}
                  >
                    {locale === "es" ? "Registrar rechazo" : "Record rejection"}
                  </button>
                </div>
              )}
              {decision?.outcome === "rejected" && (
                <>
                  {decision.evidenceFileName && (
                    <button
                      type="button"
                      onClick={() => void downloadEvidence(decision)}
                    >
                      {locale === "es" ? "Ver rechazo" : "View rejection"}
                    </button>
                  )}
                  {retried ? (
                    <span className="run-status active">
                      {locale === "es"
                        ? "Corrección generada"
                        : "Correction generated"}
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="primary"
                      disabled={busy}
                      onClick={() => void resubmit(item, decision)}
                    >
                      {locale === "es"
                        ? "Reconstruir archivo corregido"
                        : "Rebuild corrected file"}
                    </button>
                  )}
                </>
              )}
            </article>
          );
        })}
      </div>
      {!generated.length && (
        <p>
          {locale === "es"
            ? "No hay archivos EFW2PR generados."
            : "There are no generated EFW2PR files."}
        </p>
      )}
      <OperationalFeedback
        message={message}
        isError={message.includes(":") || message.includes("required")}
        locale={locale}
      />
    </section>
  );
}
type EfinfProfile = {
  taxYear: 2025;
  submitterIdType: "1" | "2";
  softwareCode: "98" | "99";
  submitterName: string;
  address: {
    line1: string;
    line2?: string | null;
    city: string;
    state: string;
    postalCode: string;
  };
  contactName: string;
  contactPhone: string;
  contactExtension: string | null;
  contactEmail: string | null;
  contactFax: string | null;
  preparesCode: "A" | "L" | "S" | "P" | "O";
  industryDescription: string;
  taxpayerType: "I" | "P" | "C" | "T" | "O";
  withholdingAgentName: string;
  changeOfAddress: boolean;
  specialistPaid: boolean;
  specialistSelfEmployed: boolean;
  specialistRegistration: string;
  specialistFirmName: string | null;
  specialistFirstName: string | null;
  specialistMiddleName: string | null;
  specialistLastName: string | null;
  specialistMothersLastName: string | null;
  specialistAddress: {
    line1: string;
    line2?: string | null;
    city: string;
    state: string;
    postalCode: string;
  };
};
type EfinfWorker = {
  workerId: string;
  workerNumber: string | null;
  workerName: string;
  payeeType: "individual" | "entity";
  entityName: string | null;
  mothersMaidenLastName: string | null;
  exemptionCode: string | null;
  specialContributionCents: number | null;
  reimbursedExpensesCents: number | null;
  healthProviderPaymentsCents: number | null;
  healthServices: number | null;
  physiciansActServices: number | null;
  optionalTaxWaiver: number | null;
  outsourcedServices: number | null;
  act48Services: number | null;
  reimbursedExpensesIndicator: number | null;
  waiverType: string | null;
  waiverCertificate: string | null;
  healthProfessional: number | null;
};
type EfinfExport = {
  id: string;
  fileType: string;
  resubmission: number;
  resubmissionOfExportId: string | null;
  status: string;
  diagnosticsJson: string;
  detailCount: number;
  recordCount: number;
  contentSha256: string | null;
  createdAt: string;
};
type EfinfDecision = {
  id: string;
  exportId: string;
  outcome: string;
  agencyReference: string;
  decisionOn: string;
  reason: string;
  evidenceFileName: string | null;
  createdAt: string;
};
type EfinfAcceptedPayee = {
  workerId: string;
  sourceExportId: string;
  controlNumber: string;
};
const blankEfw2Profile: Efw2Profile = {
  taxYear: 2025,
  ssaUserId: "",
  softwareVendorCode: null,
  softwareCode: "98",
  submitterName: "",
  address: { line1: "", line2: "", city: "", state: "PR", postalCode: "" },
  contactName: "",
  contactPhone: "",
  contactExtension: null,
  contactEmail: "",
  contactFax: null,
  preparesCode: "L",
  terminatingBusiness: false,
  cessationDate: null,
};
const infoAddress = {
  line1: "",
  line2: "",
  city: "",
  state: "PR",
  postalCode: "",
};
const blankEfinfProfile: EfinfProfile = {
  taxYear: 2025,
  submitterIdType: "1",
  softwareCode: "98",
  submitterName: "",
  address: { ...infoAddress },
  contactName: "",
  contactPhone: "",
  contactExtension: null,
  contactEmail: null,
  contactFax: null,
  preparesCode: "L",
  industryDescription: "",
  taxpayerType: "C",
  withholdingAgentName: "",
  changeOfAddress: false,
  specialistPaid: false,
  specialistSelfEmployed: true,
  specialistRegistration: "",
  specialistFirmName: null,
  specialistFirstName: null,
  specialistMiddleName: null,
  specialistLastName: null,
  specialistMothersLastName: null,
  specialistAddress: { ...infoAddress },
};
const adjustmentKeys = [
  "commissionsCents",
  "allowancesCents",
  "tipsCents",
  "governmentalRetirementCents",
  "charitableContributionsCents",
  "saveAndDoubleCents",
  "codaContributionsCents",
  "reimbursedExpensesFringeCents",
  "employerHealthCoverageCents",
  "qsehraBenefitsCents",
  "exemptSalary16Cents",
  "exemptSalary17Cents",
  "exemptSalary18Cents",
] as const;
function Efw2prFiling({ locale, tenant }: { locale: Locale; tenant: Tenant }) {
  const [profile, setProfile] = useState<Efw2Profile>(blankEfw2Profile),
    [workers, setWorkers] = useState<Efw2Worker[]>([]),
    [exports, setExports] = useState<Efw2Export[]>([]),
    [selected, setSelected] = useState(""),
    [kind, setKind] = useState<"original" | "resubmission" | "addition">(
      "original",
    ),
    [wfid, setWfid] = useState(""),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  const load = async () => {
    const response = await api(`/v1/tenants/${tenant.id}/filings/efw2pr`);
    if (!response.ok) {
      setMessage(`EFW2PR: ${response.status}`);
      return;
    }
    const body = (await response.json()) as {
      profile: Efw2Profile | null;
      companyIdentity: {legalName:string;contactName:string;phone:string;email:string;address:Efw2Profile["address"]}|null;
      workers: Efw2Worker[];
      exports: Efw2Export[];
    };
    setProfile({...blankEfw2Profile,...(body.profile??{}),submitterName:body.companyIdentity?.legalName??body.profile?.submitterName??"",contactName:body.companyIdentity?.contactName??body.profile?.contactName??"",contactPhone:body.companyIdentity?.phone??body.profile?.contactPhone??"",contactEmail:body.companyIdentity?.email??body.profile?.contactEmail??"",address:body.companyIdentity?.address??body.profile?.address??blankEfw2Profile.address});
    setWorkers(body.workers);
    setExports(body.exports);
    setSelected((current) => current || body.workers[0]?.workerId || "");
  };
  useEffect(() => {
    void load();
  }, [tenant.id]);
  const saveProfile = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const response = await api(
      `/v1/tenants/${tenant.id}/filings/efw2pr/submitter-profile`,
      { method: "PUT", body: JSON.stringify(profile) },
    );
    setMessage(
      response.ok
        ? locale === "es"
          ? "Perfil del remitente guardado."
          : "Submitter profile saved."
        : `profile:${response.status}`,
    );
    setBusy(false);
    if (response.ok) await load();
  };
  const worker = workers.find((item) => item.workerId === selected);
  const saveWorker = async (e: FormEvent) => {
    e.preventDefault();
    if (!worker) return;
    setBusy(true);
    const payload = {
      taxYear: 2025,
      suffix: worker.suffix ?? null,
      ...Object.fromEntries(
        adjustmentKeys.map((key) => [key, worker[key] ?? 0]),
      ),
      exemptCode16: worker.exemptCode16 ?? null,
      exemptCode17: worker.exemptCode17 ?? null,
      exemptCode18: worker.exemptCode18 ?? null,
      qualifiedPhysician: Boolean(worker.qualifiedPhysician),
      domesticServices: Boolean(worker.domesticServices),
      otherServicesCode: null,
      otherServicesDescription: null,
      teleworkerCode: null,
      agriculturalLabor: Boolean(worker.agriculturalLabor),
      religiousServices: Boolean(worker.religiousServices),
      healthProfessional: Boolean(worker.healthProfessional),
      directEmployment: false,
      directEmploymentEin: null,
      directHoursHundredths: 0,
      directEmploymentLeased: false,
      specialistRegistration: null,
    };
    const response = await api(
      `/v1/tenants/${tenant.id}/filings/efw2pr/workers/${worker.workerId}`,
      { method: "PUT", body: JSON.stringify(payload) },
    );
    setMessage(
      response.ok
        ? locale === "es"
          ? "Casillas anuales guardadas."
          : "Annual boxes saved."
        : `worker:${response.status}`,
    );
    setBusy(false);
    if (response.ok) await load();
  };
  const generate = async () => {
    setBusy(true);
    const response = await api(
        `/v1/tenants/${tenant.id}/filings/efw2pr/exports`,
        {
          method: "POST",
          body: JSON.stringify({
            taxYear: 2025,
            submissionKind: kind,
            wfid: kind === "resubmission" ? wfid : null,
          }),
        },
      ),
      body = (await response.json().catch(() => ({}))) as {
        export?: { diagnostics?: string[] };
      };
    setMessage(
      response.ok
        ? locale === "es"
          ? "Archivo EFW2PR exacto generado y guardado privadamente."
          : "Exact EFW2PR file generated and stored privately."
        : (body.export?.diagnostics?.join(" · ") ??
            `export:${response.status}`),
    );
    setBusy(false);
    await load();
  };
  const download = async (item: Efw2Export) => {
    const response = await api(
      `/v1/tenants/${tenant.id}/filings/efw2pr/exports/${item.id}`,
    );
    if (!response.ok) {
      setMessage(`download:${response.status}`);
      return;
    }
    const url = URL.createObjectURL(await response.blob()),
      a = document.createElement("a");
    a.href = url;
    a.download = `EFW2PR-2025-${item.id}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const moneyLabel = (key: string) =>
    key
      .replace(/Cents$/, "")
      .replace(/([A-Z])/g, " $1")
      .trim();
  return (
    <section className="settings-form">
      <h2>
        {locale === "es"
          ? "Radicación exacta W-2PR / EFW2PR 2025"
          : "Exact W-2PR / EFW2PR 2025 filing"}
      </h2>
      <p>
        {locale === "es"
          ? "Genera los nueve registros obligatorios de 512 bytes de la Publicación 25-01. Los números de control son permanentes por empleado y año; el archivo se conserva en almacenamiento privado."
          : "Generates all nine mandatory 512-byte Publication 25-01 records. Control numbers are permanent per employee and year; the file is retained in private storage."}{" "}
        <a
          className="official-source-link"
          href="https://hacienda.pr.gov/sites/default/files/publicaciones/2025/09/publication_25-01.pdf"
          target="_blank"
          rel="noreferrer"
        >
          Publication 25-01
        </a>
      </p>
      <form onSubmit={saveProfile}>
        <h3>
          {locale === "es" ? "Identidad del remitente" : "Submitter identity"}
        </h3>
        <p className="info-note">
          {locale === "es" ? "Los datos de compañía se muestran aquí solamente para revisión. Para corregir el nombre, contacto, teléfono, correo o dirección, use " : "Company details are shown here for review only. To correct the name, contact, phone, email, or address, use "}
          <a href={sectionPaths.settings}>{locale === "es" ? "Perfil de compañía" : "Company profile"}</a>.
        </p>
        <div className="form-grid">
          <label>
            SSA User ID
            <input
              required
              inputMode="numeric"
              pattern="[0-9]{8}"
              value={profile.ssaUserId}
              onChange={(e) =>
                setProfile({ ...profile, ssaUserId: e.currentTarget.value })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Nombre del remitente" : "Submitter name"}
            <input
              disabled
              required
              value={profile.submitterName}
              onChange={(e) =>
                setProfile({ ...profile, submitterName: e.currentTarget.value })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Nombre contacto" : "Contact name"}
            <input
              disabled
              required
              value={profile.contactName}
              onChange={(e) =>
                setProfile({ ...profile, contactName: e.currentTarget.value })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Teléfono" : "Phone"}
            <input
              disabled
              required
              value={profile.contactPhone}
              onChange={(e) =>
                setProfile({ ...profile, contactPhone: e.currentTarget.value })
              }
            />
          </label>
          <label>
            Email
            <input
              disabled
              required
              type="email"
              value={profile.contactEmail}
              onChange={(e) =>
                setProfile({ ...profile, contactEmail: e.currentTarget.value })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Dirección" : "Address"}
            <input
              disabled
              required
              value={profile.address.line1}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  address: { ...profile.address, line1: e.currentTarget.value },
                })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Ciudad" : "City"}
            <input
              disabled
              required
              value={profile.address.city}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  address: { ...profile.address, city: e.currentTarget.value },
                })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Estado" : "State"}
            <input
              disabled
              required
              maxLength={2}
              value={profile.address.state}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  address: { ...profile.address, state: e.currentTarget.value },
                })
              }
            />
          </label>
          <label>
            ZIP
            <input
              disabled
              required
              value={profile.address.postalCode}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  address: {
                    ...profile.address,
                    postalCode: e.currentTarget.value,
                  },
                })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Software" : "Software"}
            <select
              value={profile.softwareCode}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  softwareCode: e.currentTarget.value as "98" | "99",
                })
              }
            >
              <option value="98">98 · In-house</option>
              <option value="99">99 · Off-the-shelf</option>
            </select>
          </label>
          <label>
            {locale === "es" ? "Preparador" : "Preparer"}
            <select
              value={profile.preparesCode}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  preparesCode: e.currentTarget
                    .value as Efw2Profile["preparesCode"],
                })
              }
            >
              <option value="L">L · Self-prepared</option>
              <option value="A">A · Accounting firm</option>
              <option value="S">S · Service bureau</option>
              <option value="P">P · Parent company</option>
              <option value="O">O · Other</option>
            </select>
          </label>
        </div>
        <button className="primary" disabled={busy}>
          {locale === "es" ? "Guardar remitente" : "Save submitter"}
        </button>
      </form>
      {worker && (
        <form onSubmit={saveWorker}>
          <h3>
            {locale === "es"
              ? "Casillas anuales por empleado"
              : "Annual employee boxes"}
          </h3>
          <label>
            {locale === "es" ? "Empleado" : "Employee"}
            <select
              value={selected}
              onChange={(e) => setSelected(e.currentTarget.value)}
            >
              {workers.map((item) => (
                <option key={item.workerId} value={item.workerId}>
                  {item.workerNumber ?? "—"} · {item.workerName}
                </option>
              ))}
            </select>
          </label>
          <div className="form-grid">
            <label>
              Suffix
              <input
                maxLength={4}
                value={worker.suffix ?? ""}
                onChange={(e) =>
                  setWorkers(
                    workers.map((item) =>
                      item.workerId === worker.workerId
                        ? { ...item, suffix: e.currentTarget.value }
                        : item,
                    ),
                  )
                }
              />
            </label>
            {adjustmentKeys.map((key) => (
              <label key={key}>
                {moneyLabel(key)}
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={(worker[key] ?? 0) / 100}
                  onInput={(e) =>
                    setWorkers(
                      workers.map((item) =>
                        item.workerId === worker.workerId
                          ? {
                              ...item,
                              [key]: Math.round(
                                Number(e.currentTarget.value) * 100,
                              ),
                            }
                          : item,
                      ),
                    )
                  }
                />
              </label>
            ))}
            {([16, 17, 18] as const).map((box) => (
              <label key={box}>
                {`Exempt code ${box}`}
                <input
                  maxLength={box === 18 ? 2 : 1}
                  value={worker[`exemptCode${box}`] ?? ""}
                  onChange={(e) =>
                    setWorkers(
                      workers.map((item) =>
                        item.workerId === worker.workerId
                          ? {
                              ...item,
                              [`exemptCode${box}`]: e.currentTarget.value,
                            }
                          : item,
                      ),
                    )
                  }
                />
              </label>
            ))}
          </div>
          <div className="check-grid">
            {(
              [
                "qualifiedPhysician",
                "domesticServices",
                "agriculturalLabor",
                "religiousServices",
                "healthProfessional",
              ] as const
            ).map((key) => (
              <label className="check-label" key={key}>
                <input
                  type="checkbox"
                  aria-label={`${locale === "es" ? "Casilla anual W-2PR" : "Annual W-2PR box"}: ${moneyLabel(key)}`}
                  checked={Boolean(worker[key])}
                  onChange={(e) =>
                    setWorkers(
                      workers.map((item) =>
                        item.workerId === worker.workerId
                          ? { ...item, [key]: e.currentTarget.checked ? 1 : 0 }
                          : item,
                      ),
                    )
                  }
                />
                {moneyLabel(key)}
              </label>
            ))}
          </div>
          <button className="primary" disabled={busy}>
            {locale === "es" ? "Guardar casillas" : "Save boxes"}
          </button>
        </form>
      )}
      <div className="operation-panel">
        <h3>
          {locale === "es"
            ? "Validar y generar archivo"
            : "Validate and generate file"}
        </h3>
        <div className="form-grid">
          <label>
            {locale === "es" ? "Tipo de envío" : "Submission type"}
            <select
              value={kind}
              onChange={(e) => setKind(e.currentTarget.value as typeof kind)}
            >
              <option value="original">
                {optionLabel("original", locale)}
              </option>
              <option value="resubmission">
                {optionLabel("resubmission", locale)}
              </option>
              <option value="addition">Additional W-2s</option>
            </select>
          </label>
          {kind === "resubmission" && (
            <label>
              WFID
              <input
                required
                pattern="[0-9]{6}"
                value={wfid}
                onChange={(e) => setWfid(e.currentTarget.value)}
              />
            </label>
          )}
        </div>
        <button
          className="primary"
          type="button"
          disabled={busy}
          onClick={() => void generate()}
        >
          {locale === "es"
            ? "Validar y generar EFW2PR"
            : "Validate and generate EFW2PR"}
        </button>
      </div>
      <OperationalFeedback
        message={message}
        isError={
          message.includes(":") ||
          message.includes("missing") ||
          message.includes("invalid")
        }
        locale={locale}
      />
      <div className="run-list">
        {exports.map((item) => {
          let diagnostics: string[] = [];
          try {
            diagnostics = JSON.parse(item.diagnosticsJson) as string[];
          } catch {}
          return (
            <article key={item.id}>
              <div>
                <strong>EFW2PR 2025 · {item.submissionKind}</strong>
                <p>
                  {item.employeeCount}{" "}
                  {locale === "es" ? "empleados" : "employees"} ·{" "}
                  {item.recordCount} {locale === "es" ? "registros" : "records"}
                </p>
                <small>
                  {diagnostics.join(" · ") ||
                    `SHA-256 ${item.contentSha256?.slice(0, 16)}…`}{" "}
                  · {formatDateTime(item.createdAt, locale)}
                </small>
              </div>
              <span className={`run-status ${item.status}`}>
                {optionLabel(item.status, locale)}
              </span>
              {item.status === "generated" && (
                <button type="button" onClick={() => void download(item)}>
                  {locale === "es" ? "Descargar archivo" : "Download file"}
                </button>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}

function Efinfpr4806spFiling({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const [profile, setProfile] = useState<EfinfProfile>(blankEfinfProfile),
    [workers, setWorkers] = useState<EfinfWorker[]>([]),
    [exports, setExports] = useState<EfinfExport[]>([]),
    [selected, setSelected] = useState(""),
    [fileType, setFileType] = useState<"O" | "A">("O"),
    [resubmission, setResubmission] = useState(false),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  const load = async () => {
    const r = await api(`/v1/tenants/${tenant.id}/filings/efinfpr/4806sp`);
    if (!r.ok) {
      setMessage(`EFINFPR:${r.status}`);
      return;
    }
    const b = (await r.json()) as {
      profile: EfinfProfile | null;
      companyIdentity: {legalName:string;contactName:string;phone:string;email:string;address:EfinfProfile["address"]}|null;
      workers: EfinfWorker[];
      exports: EfinfExport[];
    };
    setProfile({...blankEfinfProfile,...(b.profile??{}),submitterName:b.companyIdentity?.legalName??b.profile?.submitterName??"",contactName:b.companyIdentity?.contactName??b.profile?.contactName??"",contactPhone:b.companyIdentity?.phone??b.profile?.contactPhone??"",contactEmail:b.companyIdentity?.email||null,address:b.companyIdentity?.address??b.profile?.address??blankEfinfProfile.address,withholdingAgentName:b.companyIdentity?.legalName??b.profile?.withholdingAgentName??""});
    setWorkers(b.workers);
    setExports(b.exports);
    setSelected((v) => v || b.workers[0]?.workerId || "");
  };
  useEffect(() => {
    void load();
  }, [tenant.id]);
  const saveProfile = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const r = await api(
      `/v1/tenants/${tenant.id}/filings/efinfpr/4806sp/submitter-profile`,
      { method: "PUT", body: JSON.stringify(profile) },
    );
    setMessage(
      r.ok
        ? locale === "es"
          ? "Perfil EFINFPR guardado."
          : "EFINFPR profile saved."
        : `profile:${r.status}`,
    );
    setBusy(false);
    if (r.ok) await load();
  };
  const worker = workers.find((x) => x.workerId === selected),
    change = (v: Partial<EfinfWorker>) =>
      worker &&
      setWorkers(
        workers.map((x) =>
          x.workerId === worker.workerId ? { ...x, ...v } : x,
        ),
      );
  const saveWorker = async (e: FormEvent) => {
    e.preventDefault();
    if (!worker) return;
    const body = {
      taxYear: 2025,
      entityName: worker.entityName,
      mothersMaidenLastName: worker.mothersMaidenLastName,
      exemptionCode: worker.exemptionCode,
      specialContributionCents: worker.specialContributionCents ?? 0,
      reimbursedExpensesCents: worker.reimbursedExpensesCents ?? 0,
      healthProviderPaymentsCents: worker.healthProviderPaymentsCents ?? 0,
      healthServices: Boolean(worker.healthServices),
      physiciansActServices: Boolean(worker.physiciansActServices),
      optionalTaxWaiver: Boolean(worker.optionalTaxWaiver),
      outsourcedServices: Boolean(worker.outsourcedServices),
      act48Services: Boolean(worker.act48Services),
      reimbursedExpensesIndicator: Boolean(worker.reimbursedExpensesIndicator),
      waiverType: worker.waiverType || null,
      waiverCertificate: worker.waiverCertificate || null,
      healthProfessional: Boolean(worker.healthProfessional),
    };
    setBusy(true);
    const r = await api(
      `/v1/tenants/${tenant.id}/filings/efinfpr/4806sp/workers/${worker.workerId}`,
      { method: "PUT", body: JSON.stringify(body) },
    );
    setMessage(
      r.ok
        ? locale === "es"
          ? "Casillas 480.6SP guardadas."
          : "480.6SP boxes saved."
        : `worker:${r.status}`,
    );
    setBusy(false);
    if (r.ok) await load();
  };
  const generate = async () => {
    setBusy(true);
    const r = await api(
        `/v1/tenants/${tenant.id}/filings/efinfpr/4806sp/exports`,
        {
          method: "POST",
          body: JSON.stringify({ taxYear: 2025, fileType, resubmission }),
        },
      ),
      b = (await r.json().catch(() => ({}))) as {
        export?: { diagnostics?: string[] };
      };
    setMessage(
      r.ok
        ? locale === "es"
          ? "Paquete exacto generado."
          : "Exact package generated."
        : (b.export?.diagnostics?.join(" · ") ?? `export:${r.status}`),
    );
    setBusy(false);
    await load();
  };
  const download = async (x: EfinfExport) => {
    const r = await api(
      `/v1/tenants/${tenant.id}/filings/efinfpr/4806sp/exports/${x.id}`,
    );
    if (!r.ok) return setMessage(`download:${r.status}`);
    const u = URL.createObjectURL(await r.blob()),
      a = document.createElement("a");
    a.href = u;
    a.download = `EFINFPR-4806SP-2025-${x.id}.zip`;
    a.click();
    URL.revokeObjectURL(u);
  };
  const dollar = (
    key:
      | "specialContributionCents"
      | "reimbursedExpensesCents"
      | "healthProviderPaymentsCents",
    label: string,
  ) => (
    <label>
      {label}
      <input
        type="number"
        min="0"
        step="0.01"
        value={(worker?.[key] ?? 0) / 100}
        onInput={(e) =>
          change({ [key]: Math.round(Number(e.currentTarget.value) * 100) })
        }
      />
    </label>
  );
  return (
    <section className="settings-form">
      <h2>
        {locale === "es"
          ? "Radicación exacta 480.6SP / EFINFPR 2025"
          : "Exact 480.6SP / EFINFPR 2025 filing"}
      </h2>
      <p>
        {locale === "es"
          ? "ZIP con los cinco archivos obligatorios de 2,500 bytes: SU, PA, 480.6SP, 480.6SP.2 y 480.5."
          : "ZIP with all five mandatory 2,500-byte files: SU, PA, 480.6SP, 480.6SP.2, and 480.5."}{" "}
        <a
          className="official-source-link"
          href="https://hacienda.pr.gov/sites/default/files/publicaciones/2026/05/publication_25-03_vers._2_0.pdf"
          target="_blank"
          rel="noreferrer"
        >
          Publication 25-03 v2.0
        </a>
      </p>
      <form onSubmit={saveProfile}>
        <h3>
          {locale === "es"
            ? "Remitente y especialista"
            : "Submitter and specialist"}
        </h3>
        <p className="info-note">
          {locale === "es" ? "Los datos del remitente y agente retenedor se muestran aquí solamente para revisión y provienen de " : "Submitter and withholding-agent details are read-only here and come from "}
          <a href={sectionPaths.settings}>{locale === "es" ? "Perfil de compañía" : "Company profile"}</a>.
        </p>
        <div className="form-grid">
          <label>
            {locale === "es" ? "Remitente" : "Submitter"}
            <input
              disabled
              required
              value={profile.submitterName}
              onChange={(e) =>
                setProfile({ ...profile, submitterName: e.currentTarget.value })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Contacto" : "Contact"}
            <input
              disabled
              required
              value={profile.contactName}
              onChange={(e) =>
                setProfile({ ...profile, contactName: e.currentTarget.value })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Teléfono" : "Phone"}
            <input
              disabled
              required
              value={profile.contactPhone}
              onChange={(e) =>
                setProfile({ ...profile, contactPhone: e.currentTarget.value })
              }
            />
          </label>
          <label>
            Email
            <input
              disabled
              type="email"
              value={profile.contactEmail ?? ""}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  contactEmail: e.currentTarget.value || null,
                })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Dirección" : "Address"}
            <input
              disabled
              required
              value={profile.address.line1}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  address: { ...profile.address, line1: e.currentTarget.value },
                })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Ciudad" : "City"}
            <input
              disabled
              required
              value={profile.address.city}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  address: { ...profile.address, city: e.currentTarget.value },
                })
              }
            />
          </label>
          <label>
            State
            <input
              disabled
              required
              maxLength={2}
              value={profile.address.state}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  address: { ...profile.address, state: e.currentTarget.value },
                })
              }
            />
          </label>
          <label>
            ZIP
            <input
              disabled
              required
              value={profile.address.postalCode}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  address: {
                    ...profile.address,
                    postalCode: e.currentTarget.value,
                  },
                })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Industria" : "Industry"}
            <input
              required
              maxLength={20}
              value={profile.industryDescription}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  industryDescription: e.currentTarget.value,
                })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Agente retenedor" : "Withholding agent"}
            <input
              disabled
              required
              maxLength={30}
              value={profile.withholdingAgentName}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  withholdingAgentName: e.currentTarget.value,
                })
              }
            />
          </label>
          <label>
            {locale === "es"
              ? "Registro especialista"
              : "Specialist registration"}
            <input
              required
              pattern="[0-9]{7}"
              value={profile.specialistRegistration}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  specialistRegistration: e.currentTarget.value,
                })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Nombre especialista" : "Specialist first name"}
            <input
              value={profile.specialistFirstName ?? ""}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  specialistFirstName: e.currentTarget.value || null,
                })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Apellido especialista" : "Specialist last name"}
            <input
              value={profile.specialistLastName ?? ""}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  specialistLastName: e.currentTarget.value || null,
                })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Dirección especialista" : "Specialist address"}
            <input
              required
              value={profile.specialistAddress.line1}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  specialistAddress: {
                    ...profile.specialistAddress,
                    line1: e.currentTarget.value,
                  },
                })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Ciudad especialista" : "Specialist city"}
            <input
              required
              value={profile.specialistAddress.city}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  specialistAddress: {
                    ...profile.specialistAddress,
                    city: e.currentTarget.value,
                  },
                })
              }
            />
          </label>
          <label>
            Specialist state
            <input
              required
              maxLength={2}
              value={profile.specialistAddress.state}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  specialistAddress: {
                    ...profile.specialistAddress,
                    state: e.currentTarget.value,
                  },
                })
              }
            />
          </label>
          <label>
            Specialist ZIP
            <input
              required
              value={profile.specialistAddress.postalCode}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  specialistAddress: {
                    ...profile.specialistAddress,
                    postalCode: e.currentTarget.value,
                  },
                })
              }
            />
          </label>
        </div>
        <button className="primary" disabled={busy}>
          {locale === "es" ? "Guardar perfil" : "Save profile"}
        </button>
      </form>
      {worker && (
        <form onSubmit={saveWorker}>
          <h3>{locale === "es" ? "Casillas por proveedor" : "Payee boxes"}</h3>
          <label>
            {locale === "es" ? "Proveedor" : "Payee"}
            <select
              value={selected}
              onChange={(e) => setSelected(e.currentTarget.value)}
            >
              {workers.map((x) => (
                <option key={x.workerId} value={x.workerId}>
                  {x.workerNumber ?? "—"} · {x.workerName} · {x.payeeType}
                </option>
              ))}
            </select>
          </label>
          <div className="form-grid">
            {worker.payeeType === "entity" && (
              <label>
                {locale === "es" ? "Nombre entidad" : "Entity name"}
                <input
                  required
                  value={worker.entityName ?? ""}
                  onChange={(e) =>
                    change({ entityName: e.currentTarget.value })
                  }
                />
              </label>
            )}
            <label>
              {locale === "es" ? "Apellido materno" : "Mother's maiden name"}
              <input
                value={worker.mothersMaidenLastName ?? ""}
                onChange={(e) =>
                  change({
                    mothersMaidenLastName: e.currentTarget.value || null,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Código exención" : "Exemption code"}
              <select
                value={worker.exemptionCode ?? ""}
                onChange={(e) =>
                  change({ exemptionCode: e.currentTarget.value || null })
                }
              >
                <option value="">—</option>
                {"ABCDEFGHIJKL".split("").map((v) => (
                  <option key={v}>{v}</option>
                ))}
              </select>
            </label>
            {dollar("specialContributionCents", "Act 48-2013")}
            {dollar(
              "reimbursedExpensesCents",
              locale === "es" ? "Gastos reembolsados" : "Reimbursed expenses",
            )}
            {dollar(
              "healthProviderPaymentsCents",
              locale === "es" ? "Pagos salud" : "Health payments",
            )}
            <label>
              {locale === "es" ? "Tipo relevo" : "Waiver type"}
              <select
                value={worker.waiverType ?? ""}
                onChange={(e) =>
                  change({ waiverType: e.currentTarget.value || null })
                }
              >
                <option value="">—</option>
                <option value="P">
                  {locale === "es" ? "Parcial" : "Partial"}
                </option>
                <option value="T">{locale === "es" ? "Total" : "Total"}</option>
              </select>
            </label>
            <label>
              {locale === "es" ? "Certificado relevo" : "Waiver certificate"}
              <input
                value={worker.waiverCertificate ?? ""}
                onChange={(e) =>
                  change({ waiverCertificate: e.currentTarget.value || null })
                }
              />
            </label>
          </div>
          <div className="check-grid">
            {(
              [
                "healthServices",
                "physiciansActServices",
                "optionalTaxWaiver",
                "outsourcedServices",
                "act48Services",
                "reimbursedExpensesIndicator",
                "healthProfessional",
              ] as const
            ).map((k) => (
              <label className="check-label" key={k}>
                <input
                  type="checkbox"
                  aria-label={`${locale === "es" ? "Casilla anual 480.6SP" : "Annual 480.6SP box"}: ${k.replace(/([A-Z])/g, " $1")}`}
                  checked={Boolean(worker[k])}
                  onChange={(e) =>
                    change({ [k]: e.currentTarget.checked ? 1 : 0 })
                  }
                />
                {k.replace(/([A-Z])/g, " $1")}
              </label>
            ))}
          </div>
          <button className="primary" disabled={busy}>
            {locale === "es" ? "Guardar casillas" : "Save boxes"}
          </button>
        </form>
      )}
      <div className="operation-panel">
        <label>
          {locale === "es" ? "Tipo archivo" : "File type"}
          <select
            value={fileType}
            onChange={(e) => setFileType(e.currentTarget.value as "O" | "A")}
          >
            <option value="O">{optionLabel("original", locale)}</option>
            <option value="A">{locale === "es" ? "Adición" : "Add"}</option>
          </select>
        </label>
        <label className="check-label">
          <input
            type="checkbox"
            checked={resubmission}
            onChange={(e) => setResubmission(e.currentTarget.checked)}
          />
          {locale === "es" ? "Reenvío rechazado" : "Rejected-file resubmission"}
        </label>
        <button
          type="button"
          className="primary"
          disabled={busy}
          onClick={() => void generate()}
        >
          {locale === "es" ? "Generar ZIP EFINFPR" : "Generate EFINFPR ZIP"}
        </button>
      </div>
      <OperationalFeedback
        message={message}
        isError={
          message.includes(":") ||
          message.includes("missing") ||
          message.includes("invalid")
        }
        locale={locale}
      />
      <div className="run-list">
        {exports.map((x) => (
          <article key={x.id}>
            <div>
              <strong>480.6SP 2025 · {x.fileType}</strong>
              <p>
                {x.detailCount} details · {x.recordCount} records
              </p>
              <small>
                {x.contentSha256
                  ? `SHA-256 ${x.contentSha256.slice(0, 16)}…`
                  : x.diagnosticsJson}{" "}
                · {formatDateTime(x.createdAt, locale)}
              </small>
            </div>
            <span className={`run-status ${x.status}`}>
              {optionLabel(x.status, locale)}
            </span>
            {x.status === "generated" && (
              <button type="button" onClick={() => void download(x)}>
                {locale === "es" ? "Descargar ZIP" : "Download ZIP"}
              </button>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}

function EfinfprAmendments({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const [workers, setWorkers] = useState<EfinfWorker[]>([]),
    [exports, setExports] = useState<EfinfExport[]>([]),
    [decisions, setDecisions] = useState<EfinfDecision[]>([]),
    [accepted, setAccepted] = useState<EfinfAcceptedPayee[]>([]),
    [workerId, setWorkerId] = useState(""),
    [documentType, setDocumentType] = useState<"A" | "X">("A"),
    [reason, setReason] = useState(""),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState(""),
    [acceptDraft, setAcceptDraft] = useState<{
      item: EfinfExport;
      agencyReference: string;
      decisionOn: string;
      reason: string;
    } | null>(null);
  const load = async () => {
    const response = await api(
      `/v1/tenants/${tenant.id}/filings/efinfpr/4806sp`,
    );
    if (!response.ok) return;
    const body = (await response.json()) as {
      workers: EfinfWorker[];
      exports: EfinfExport[];
      decisions: EfinfDecision[];
      acceptedPayees: EfinfAcceptedPayee[];
    };
    setWorkers(body.workers);
    setExports(body.exports);
    setDecisions(body.decisions ?? []);
    setAccepted(body.acceptedPayees ?? []);
    setWorkerId((value) =>
      body.acceptedPayees?.some((item) => item.workerId === value)
        ? value
        : (body.acceptedPayees?.[0]?.workerId ?? ""),
    );
  };
  useEffect(() => {
    void load();
  }, [tenant.id]);
  const generate = async () => {
    const source = accepted.find((item) => item.workerId === workerId);
    if (!source) return;
    setBusy(true);
    const response = await api(
        `/v1/tenants/${tenant.id}/filings/efinfpr/4806sp/exports`,
        {
          method: "POST",
          body: JSON.stringify({
            taxYear: 2025,
            fileType: "E",
            resubmission: false,
            corrections: [
              {
                sourceExportId: source.sourceExportId,
                workerId,
                documentType,
                reason,
              },
            ],
          }),
        },
      ),
      body = (await response.json().catch(() => ({}))) as {
        export?: { diagnostics?: string[] };
      };
    setMessage(
      response.ok
        ? locale === "es"
          ? "Paquete enmendado exacto generado."
          : "Exact amended package generated."
        : (body.export?.diagnostics?.join(" · ") ??
            `amendment:${response.status}`),
    );
    setBusy(false);
    if (response.ok) setReason("");
    await load();
  };
  const accept = (item: EfinfExport) =>
    setAcceptDraft({
      item,
      agencyReference: "",
      decisionOn: new Date().toISOString().slice(0, 10),
      reason: "",
    });
  const submitAcceptance = async (e: FormEvent) => {
    e.preventDefault();
    const draft = acceptDraft;
    if (!draft) return;
    setBusy(true);
    setMessage("");
    const response = await api(
      `/v1/tenants/${tenant.id}/filings/efinfpr/4806sp/exports/${draft.item.id}/decision`,
      {
        method: "POST",
        body: JSON.stringify({
          outcome: "accepted",
          agencyReference: draft.agencyReference.trim(),
          decisionOn: draft.decisionOn,
          reason: draft.reason.trim(),
        }),
      },
    );
    setMessage(
      response.ok
        ? locale === "es"
          ? "Aceptación inmutable registrada."
          : "Immutable acceptance recorded."
        : `decision:${response.status}`,
    );
    if (response.ok) setAcceptDraft(null);
    setBusy(false);
    await load();
  };
  const decisionFor = (id: string) =>
      decisions.find((item) => item.exportId === id),
    name = (id: string) =>
      workers.find((item) => item.workerId === id)?.workerName ?? id;
  return (
    <section className="settings-form">
      <h2>
        {locale === "es"
          ? "Aceptación, correcciones y eliminaciones 480.6SP"
          : "480.6SP acceptance, corrections, and deletions"}
      </h2>
      <p>
        {locale === "es"
          ? "Registre la aceptación procesada por SURI antes de enmendar. Una corrección recibe un control nuevo; una eliminación reproduce exactamente el registro aceptado. El original permanece cifrado e inmutable."
          : "Record SURI processing acceptance before amending. A correction receives a new control; a deletion exactly reproduces the accepted record. The original remains encrypted and immutable."}
      </p>
      {accepted.length ? (
        <div className="operation-panel">
          <div className="form-grid">
            <label>
              {locale === "es" ? "Documento aceptado" : "Accepted document"}
              <select
                value={workerId}
                onChange={(event) => setWorkerId(event.target.value)}
              >
                {accepted.map((item) => (
                  <option key={item.workerId} value={item.workerId}>
                    {name(item.workerId)} · {item.controlNumber}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {locale === "es" ? "Acción" : "Action"}
              <select
                value={documentType}
                onChange={(event) =>
                  setDocumentType(event.target.value as "A" | "X")
                }
              >
                <option value="A">
                  {locale === "es" ? "Corregir" : "Correct"}
                </option>
                <option value="X">
                  {locale === "es" ? "Eliminar" : "Delete"}
                </option>
              </select>
            </label>
            <label>
              {locale === "es" ? "Razón del cambio" : "Reason for change"}
              <input
                required
                minLength={3}
                maxLength={40}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </label>
          </div>
          <button
            type="button"
            className="primary"
            disabled={busy || !workerId || reason.trim().length < 3}
            onClick={() => void generate()}
          >
            {documentType === "A"
              ? locale === "es"
                ? "Generar corrección"
                : "Generate correction"
              : locale === "es"
                ? "Generar eliminación"
                : "Generate deletion"}
          </button>
        </div>
      ) : (
        <p>
          {locale === "es"
            ? "Todavía no hay documentos aceptados por SURI registrados."
            : "No SURI-accepted documents have been recorded yet."}
        </p>
      )}
      <div className="run-list">
        {exports
          .filter((item) => item.status === "generated")
          .map((item) => {
            const result = decisionFor(item.id);
            return (
              <article key={item.id}>
                <div>
                  <strong>
                    480.6SP · {item.fileType} · {item.detailCount}
                  </strong>
                  <small>
                    {formatDateTime(item.createdAt, locale)} ·{" "}
                    {item.contentSha256?.slice(0, 16)}…
                  </small>
                </div>
                <span className={`run-status ${result?.outcome ?? "pending"}`}>
                  {result?.outcome ??
                    (locale === "es" ? "pendiente SURI" : "SURI pending")}
                </span>
                {!result && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => accept(item)}
                  >
                    {locale === "es"
                      ? "Registrar aceptación"
                      : "Record acceptance"}
                  </button>
                )}
              </article>
            );
          })}
      </div>
      <OperationalFeedback
        message={message}
        isError={
          message.includes(":") ||
          message.includes("invalid") ||
          message.includes("mismatch")
        }
        locale={locale}
      />
      {acceptDraft && (
        <dialog
          className="workflow-dialog"
          open
          aria-labelledby="suri-accept-title"
        >
          <form onSubmit={submitAcceptance}>
            <header>
              <div>
                <p className="kicker">
                  {locale === "es"
                    ? "DECISIÓN DE SURI AUDITADA"
                    : "AUDITED SURI DECISION"}
                </p>
                <h2 id="suri-accept-title">
                  {locale === "es"
                    ? "Registrar aceptación de SURI"
                    : "Record SURI acceptance"}
                </h2>
                <p>
                  480.6SP · {acceptDraft.item.fileType} ·{" "}
                  {acceptDraft.item.id.slice(0, 8)}
                </p>
              </div>
              <button
                type="button"
                aria-label={locale === "es" ? "Cerrar" : "Close"}
                onClick={() => setAcceptDraft(null)}
              >
                ×
              </button>
            </header>
            <p className="form-detail">
              SHA-256 {acceptDraft.item.contentSha256?.slice(0, 16)}… ·{" "}
              {locale === "es"
                ? "La aceptación queda inmutable y habilita correcciones o eliminaciones posteriores."
                : "Acceptance becomes immutable and enables later corrections or deletions."}
            </p>
            <div className="form-grid">
              <label>
                {locale === "es"
                  ? "Referencia de aceptación"
                  : "Acceptance reference"}
                <input
                  required
                  minLength={2}
                  maxLength={160}
                  value={acceptDraft.agencyReference}
                  onChange={(e) =>
                    setAcceptDraft({
                      ...acceptDraft,
                      agencyReference: e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Fecha procesada" : "Processed date"}
                <input
                  required
                  type="date"
                  value={acceptDraft.decisionOn}
                  onInput={(e) =>
                    setAcceptDraft({
                      ...acceptDraft,
                      decisionOn: e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label className="full-width">
                {locale === "es"
                  ? "Razón y evidencia de la decisión"
                  : "Decision reason and evidence"}
                <textarea
                  required
                  minLength={3}
                  maxLength={1000}
                  value={acceptDraft.reason}
                  onChange={(e) =>
                    setAcceptDraft({
                      ...acceptDraft,
                      reason: e.currentTarget.value,
                    })
                  }
                />
              </label>
            </div>
            <footer>
              <button type="button" onClick={() => setAcceptDraft(null)}>
                {locale === "es" ? "Cancelar" : "Cancel"}
              </button>
              <button
                className="primary"
                disabled={
                  busy ||
                  acceptDraft.agencyReference.trim().length < 2 ||
                  acceptDraft.reason.trim().length < 3
                }
              >
                {busy ? (
                  <LoaderCircle className="spin" />
                ) : locale === "es" ? (
                  "Guardar aceptación"
                ) : (
                  "Save acceptance"
                )}
              </button>
            </footer>
          </form>
        </dialog>
      )}
    </section>
  );
}

function EfinfprRejectedResubmissions({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const [exports, setExports] = useState<EfinfExport[]>([]),
    [decisions, setDecisions] = useState<EfinfDecision[]>([]),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  const load = async () => {
    const response = await api(
      `/v1/tenants/${tenant.id}/filings/efinfpr/4806sp`,
    );
    if (response.ok) {
      const body = (await response.json()) as {
        exports: EfinfExport[];
        decisions: EfinfDecision[];
      };
      setExports(body.exports);
      setDecisions(body.decisions ?? []);
    }
  };
  useEffect(() => {
    void load();
  }, [tenant.id]);
  const reject = async (item: EfinfExport) => {
    const values = await requestWorkflowDialog({
      title:
        locale === "es" ? "Registrar rechazo de SURI" : "Record SURI rejection",
      detail: `480.6SP · ${item.fileType} · ${item.id.slice(0, 8)}`,
      confirmLabel: locale === "es" ? "Guardar rechazo" : "Save rejection",
      cancelLabel: locale === "es" ? "Cancelar" : "Cancel",
      danger: true,
      fields: [
        {
          key: "agencyReference",
          label:
            locale === "es" ? "Referencia del rechazo" : "Rejection reference",
          required: true,
          minLength: 2,
        },
        {
          key: "decisionOn",
          label: locale === "es" ? "Fecha del rechazo" : "Rejection date",
          type: "date",
          value: new Date().toISOString().slice(0, 10),
          required: true,
        },
        {
          key: "reason",
          label:
            locale === "es"
              ? "Razón exacta del rechazo"
              : "Exact rejection reason",
          type: "textarea",
          required: true,
          minLength: 3,
          fullWidth: true,
        },
        {
          key: "file",
          label: locale === "es" ? "Evidencia de SURI" : "SURI evidence",
          type: "file",
          accept: "application/pdf,image/png,image/jpeg,image/webp",
          required: true,
          fullWidth: true,
        },
      ],
    });
    if (!values || !(values.file instanceof File)) return;
    const form = new FormData();
    form.set("agencyReference", String(values.agencyReference));
    form.set("decisionOn", String(values.decisionOn));
    form.set("reason", String(values.reason));
    form.set("file", values.file);
    setBusy(true);
    const response = await api(
      `/v1/tenants/${tenant.id}/filings/efinfpr/4806sp/exports/${item.id}/decision/reject`,
      { method: "POST", body: form },
    );
    setMessage(
      response.ok
        ? locale === "es"
          ? "Rechazo y evidencia guardados."
          : "Rejection and evidence retained."
        : `rejection:${response.status}`,
    );
    setBusy(false);
    await load();
  };
  const resubmit = async (item: EfinfExport) => {
    setBusy(true);
    const response = await api(
        `/v1/tenants/${tenant.id}/filings/efinfpr/4806sp/exports`,
        {
          method: "POST",
          body: JSON.stringify({
            taxYear: 2025,
            fileType: item.fileType,
            resubmission: true,
            resubmissionOfExportId: item.id,
          }),
        },
      ),
      body = (await response.json().catch(() => ({}))) as {
        export?: { diagnostics?: string[] };
      };
    setMessage(
      response.ok
        ? locale === "es"
          ? "Reenvío exacto generado desde el paquete rechazado."
          : "Exact resubmission generated from the rejected package."
        : (body.export?.diagnostics?.join(" · ") ??
            `resubmission:${response.status}`),
    );
    setBusy(false);
    await load();
  };
  const pending = exports.filter(
      (item) =>
        item.status === "generated" &&
        !decisions.some((decision) => decision.exportId === item.id),
    ),
    rejected = exports.filter((item) =>
      decisions.some(
        (decision) =>
          decision.exportId === item.id && decision.outcome === "rejected",
      ),
    );
  return (
    <section className="settings-form">
      <h2>
        {locale === "es"
          ? "Rechazos y reenvíos exactos de SURI"
          : "SURI rejections and exact resubmissions"}
      </h2>
      <p>
        {locale === "es"
          ? "Conserve la respuesta de rechazo. El reenvío copia exactamente los cinco archivos rechazados y solo cambia a 1 el indicador SU de reenvío; no vuelve a calcular datos editables."
          : "Retain the rejection response. A resubmission copies all five rejected files exactly and changes only the SU resubmission indicator to 1; it does not recalculate editable data."}
      </p>
      <div className="run-list">
        {pending.map((item) => (
          <article key={item.id}>
            <div>
              <strong>
                {item.fileType} · {item.id.slice(0, 8)}
              </strong>
              <small>
                {formatDateTime(item.createdAt, locale)} · SHA-256{" "}
                {item.contentSha256?.slice(0, 16)}…
              </small>
            </div>
            <button
              type="button"
              disabled={busy}
              onClick={() => void reject(item)}
            >
              {locale === "es"
                ? "Registrar rechazo con evidencia"
                : "Record rejection with evidence"}
            </button>
          </article>
        ))}
        {rejected.map((item) => {
          const retried = exports.find(
              (value) => value.resubmissionOfExportId === item.id,
            ),
            decision = decisions.find((value) => value.exportId === item.id);
          return (
            <article key={item.id}>
              <div>
                <strong>
                  {locale === "es" ? "Rechazado" : "Rejected"} ·{" "}
                  {decision?.agencyReference}
                </strong>
                <small>
                  {decision?.decisionOn} · {decision?.reason}
                </small>
              </div>
              {retried ? (
                <span className="run-status active">
                  {locale === "es"
                    ? `Reenviado ${retried.id.slice(0, 8)}`
                    : `Resubmitted ${retried.id.slice(0, 8)}`}
                </span>
              ) : (
                <button
                  type="button"
                  className="primary"
                  disabled={busy}
                  onClick={() => void resubmit(item)}
                >
                  {locale === "es"
                    ? "Generar reenvío exacto"
                    : "Generate exact resubmission"}
                </button>
              )}
            </article>
          );
        })}
      </div>
      {!pending.length && !rejected.length && (
        <p>
          {locale === "es"
            ? "No hay paquetes pendientes o rechazados."
            : "There are no pending or rejected packages."}
        </p>
      )}
      <OperationalFeedback
        message={message}
        isError={message.includes(":")}
        locale={locale}
      />
    </section>
  );
}

type FilingSpecification = {
  filingType: string;
  taxYear: number;
  specVersion: string | null;
  formatStatus:
    | "published_not_implemented"
    | "pending_publication"
    | "implemented"
    | "retired";
  officialUrl: string;
  lastVerifiedOn: string;
  notes: string;
};
function ComplianceExports({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const [specifications, setSpecifications] = useState<FilingSpecification[]>(
    [],
  );
  useEffect(() => {
    void api(`/v1/tenants/${tenant.id}/compliance/exports`).then(
      async (response) => {
        if (response.ok)
          setSpecifications(
            (
              (await response.json()) as {
                specifications: FilingSpecification[];
              }
            ).specifications ?? [],
          );
      },
    );
  }, [tenant.id]);
  return (
    <>
      <section className="settings-form">
        <h2>
          {locale === "es"
            ? "Disponibilidad de formatos oficiales"
            : "Official filing-format availability"}
        </h2>
        <p>
          {locale === "es"
            ? "Una hoja de trabajo no es un archivo de radicación. El sistema solo marcará un formato como implementado cuando la publicación del año exacto esté mapeada, validada y certificada."
            : "A workpaper is not a filing file. The system marks a format implemented only after the exact tax-year publication is mapped, validated, and certified."}
        </p>
        <div className="run-list">
          {specifications.map((item) => (
            <article key={`${item.filingType}-${item.taxYear}`}>
              <div>
                <strong>
                  {optionLabel(item.filingType, locale)} · {item.taxYear}
                </strong>
                <p>
                  {item.specVersion ??
                    (locale === "es"
                      ? "Publicación aún no disponible"
                      : "Publication not yet available")}
                </p>
                <small>
                  {item.notes} · {locale === "es" ? "Verificado" : "Verified"}{" "}
                  {formatDate(item.lastVerifiedOn, locale)}
                </small>
              </div>
              <span
                className={`run-status ${item.formatStatus === "implemented" ? "active" : "pending"}`}
              >
                {optionLabel(item.formatStatus, locale)}
              </span>
              <a
                className="official-source-link"
                href={item.officialUrl}
                target="_blank"
                rel="noreferrer"
              >
                <ExternalLink />
                {locale === "es" ? "Fuente oficial" : "Official source"}
              </a>
            </article>
          ))}
        </div>
      </section>
      <Efw2prFiling locale={locale} tenant={tenant} />
      <Efw2prDecisions locale={locale} tenant={tenant} />
      <Efinfpr4806spFiling locale={locale} tenant={tenant} />
      <EfinfprAmendments locale={locale} tenant={tenant} />
      <EfinfprRejectedResubmissions locale={locale} tenant={tenant} />
      <LegacyComplianceExports locale={locale} tenant={tenant} />
    </>
  );
}

function Reports({ locale, tenant }: { locale: Locale; tenant: Tenant }) {
  const today = new Date().toISOString().slice(0, 10),
    year = `${today.slice(0, 4)}-01-01`,
    [from, setFrom] = useState(year),
    [to, setTo] = useState(today),
    [summary, setSummary] = useState<ReportSummary | null>(null),
    [recon, setRecon] = useState<Reconciliation | null>(null),
    [liabilities, setLiabilities] = useState<Liability[]>([]),
    [liabilityCredits, setLiabilityCredits] = useState<LiabilityCredit[]>([]),
    [remittanceEligible, setRemittanceEligible] = useState<
      DeductionRemittanceEligible[]
    >([]),
    [remittanceBatches, setRemittanceBatches] = useState<
      DeductionRemittanceBatch[]
    >([]),
    [selectedRemittanceIds, setSelectedRemittanceIds] = useState<string[]>([]),
    [remittanceFile, setRemittanceFile] = useState<File | null>(null),
    [remittanceForm, setRemittanceForm] = useState({
      payee: "",
      paymentDate: today,
      paymentMethod: "portal",
      confirmation: "",
      reason: "",
    }),
    [busy, setBusy] = useState(true),
    [error, setError] = useState("");
  const money = (cents: number | undefined) =>
    new Intl.NumberFormat(locale === "es" ? "es-PR" : "en-US", {
      style: "currency",
      currency: "USD",
    }).format((cents ?? 0) / 100);
  const load = async () => {
    setBusy(true);
    setError("");
    const query = new URLSearchParams({ from, to }),
      [s, r, l, m] = await Promise.all([
        api(`/v1/tenants/${tenant.id}/reports/summary?${query}`),
        api(`/v1/tenants/${tenant.id}/reports/reconciliation?${query}`),
        api(`/v1/tenants/${tenant.id}/liabilities`),
        api(`/v1/tenants/${tenant.id}/deduction-remittances`),
      ]);
    if (s.ok) setSummary((await s.json()) as ReportSummary);
    else setError(`summary:${s.status}`);
    if (r.ok) setRecon((await r.json()) as Reconciliation);
    else setError(`reconciliation:${r.status}`);
    if (l.ok) {
      const data = (await l.json()) as {
        liabilities: Liability[];
        credits: LiabilityCredit[];
      };
      setLiabilities(data.liabilities);
      setLiabilityCredits(data.credits ?? []);
    } else setError(`liabilities:${l.status}`);
    if (m.ok) {
      const data = (await m.json()) as {
        eligible: DeductionRemittanceEligible[];
        batches: DeductionRemittanceBatch[];
      };
      setRemittanceEligible(data.eligible);
      setRemittanceBatches(data.batches);
      setSelectedRemittanceIds((current) =>
        current.filter((id) =>
          data.eligible.some((item) => item.liabilityId === id),
        ),
      );
    } else setError(`remittances:${m.status}`);
    setBusy(false);
  };
  useEffect(() => {
    void load();
  }, [tenant.id]);
  const exportCsv = async () => {
    const query = new URLSearchParams({ from, to }),
      response = await api(
        `/v1/tenants/${tenant.id}/reports/payroll.csv?${query}`,
      );
    if (!response.ok) {
      setError(`export:${response.status}`);
      return;
    }
    const url = URL.createObjectURL(await response.blob()),
      anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `payroll-${from}-${to}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };
  const update = async (
    item: Liability,
    requestedStatus: "unpaid" | "scheduled" | "paid" | "filed",
  ) => {
    const filingStatus =
        requestedStatus === "filed" ? "filed" : item.filingStatus,
      status =
        requestedStatus === "filed"
          ? item.status === "filed"
            ? "paid"
            : (item.status as "unpaid" | "scheduled" | "paid")
          : requestedStatus,
      values = await requestWorkflowDialog({
        title: locale === "es" ? "Actualizar obligación" : "Update liability",
        detail: `${optionLabel(item.liabilityKey, locale)} · ${money(item.amountCents)}`,
        confirmLabel: locale === "es" ? "Guardar actualización" : "Save update",
        cancelLabel: locale === "es" ? "Cancelar" : "Cancel",
        fields: [
          {
            key: "dueDate",
            label:
              locale === "es"
                ? "Fecha de vencimiento opcional"
                : "Optional due date",
            type: "date",
            value: item.dueDate ?? "",
          },
          ...(status === "paid"
            ? [
                {
                  key: "paymentDate",
                  label: locale === "es" ? "Fecha de pago" : "Payment date",
                  type: "date" as const,
                  value: item.paymentDate ?? today,
                  required: true,
                },
                {
                  key: "confirmation",
                  label:
                    locale === "es"
                      ? "Confirmación de pago"
                      : "Payment confirmation",
                  value: item.confirmation ?? "",
                  required: true,
                  minLength: 2,
                },
              ]
            : []),
          {
            key: "reason",
            label: locale === "es" ? "Razón documentada" : "Documented reason",
            type: "textarea",
            required: true,
            minLength: 3,
            fullWidth: true,
          },
        ],
      });
    if (!values) return;
    const response = await api(
      `/v1/tenants/${tenant.id}/liabilities/${item.id}`,
      {
        method: "PUT",
        body: JSON.stringify({
          status,
          dueDate: String(values.dueDate || "") || null,
          paymentDate: status === "paid" ? String(values.paymentDate) : null,
          confirmation: status === "paid" ? String(values.confirmation) : null,
          filingStatus,
          reason: String(values.reason),
        }),
      },
    );
    if (!response.ok) setError(`liability:${response.status}`);
    await load();
  };
  const resolveCredit = async (
    item: LiabilityCredit,
    status: "applied" | "refunded",
  ) => {
    const values = await requestWorkflowDialog({
      title:
        status === "applied"
          ? locale === "es"
            ? "Registrar aplicación del crédito"
            : "Record credit application"
          : locale === "es"
            ? "Registrar reembolso del crédito"
            : "Record credit refund",
      detail: `${optionLabel(item.liabilityKey, locale)} · ${money(item.amountCents)}`,
      confirmLabel: locale === "es" ? "Guardar resolución" : "Save resolution",
      cancelLabel: locale === "es" ? "Cancelar" : "Cancel",
      fields: [
        {
          key: "resolutionReference",
          label:
            locale === "es"
              ? "Referencia de resolución"
              : "Resolution reference",
          required: true,
          minLength: 2,
        },
        {
          key: "reason",
          label: locale === "es" ? "Razón y evidencia" : "Reason and evidence",
          type: "textarea",
          required: true,
          minLength: 3,
          fullWidth: true,
        },
      ],
    });
    if (!values) return;
    const response = await api(
      `/v1/tenants/${tenant.id}/liability-credits/${item.id}/resolve`,
      {
        method: "POST",
        body: JSON.stringify({
          status,
          resolutionReference: String(values.resolutionReference),
          reason: String(values.reason),
        }),
      },
    );
    if (!response.ok) setError(`liability-credit:${response.status}`);
    await load();
  };
  const toggleRemittance = (item: DeductionRemittanceEligible) => {
    const payee = item.payee ?? "Employer";
    if (selectedRemittanceIds.includes(item.liabilityId))
      setSelectedRemittanceIds(
        selectedRemittanceIds.filter((id) => id !== item.liabilityId),
      );
    else if (
      !selectedRemittanceIds.length ||
      remittanceForm.payee.toLocaleLowerCase() === payee.toLocaleLowerCase()
    ) {
      setSelectedRemittanceIds([...selectedRemittanceIds, item.liabilityId]);
      setRemittanceForm({ ...remittanceForm, payee });
    } else
      setError(
        locale === "es"
          ? "Cada remesa debe contener un solo beneficiario."
          : "Each remittance must contain one payee.",
      );
  };
  const createRemittance = async (e: FormEvent) => {
    e.preventDefault();
    if (!remittanceFile || !selectedRemittanceIds.length) return;
    setBusy(true);
    setError("");
    const form = new FormData();
    form.set("file", remittanceFile);
    form.set("liabilityIds", JSON.stringify(selectedRemittanceIds));
    Object.entries(remittanceForm).forEach(([key, value]) =>
      form.set(key, value),
    );
    const response = await api(
      `/v1/tenants/${tenant.id}/deduction-remittances`,
      { method: "POST", body: form },
    );
    if (!response.ok) setError(`deduction-remittance:${response.status}`);
    else {
      setSelectedRemittanceIds([]);
      setRemittanceFile(null);
      setRemittanceForm({ ...remittanceForm, confirmation: "", reason: "" });
    }
    await load();
  };
  const downloadRemittance = async (item: DeductionRemittanceBatch) => {
    const response = await api(
      `/v1/tenants/${tenant.id}/deduction-remittances/${item.id}/evidence`,
    );
    if (!response.ok) {
      setError(`deduction-remittance-evidence:${response.status}`);
      return;
    }
    const url = URL.createObjectURL(await response.blob()),
      anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = item.evidenceFileName;
    anchor.click();
    URL.revokeObjectURL(url);
  };
  const liabilityActions = (item: Liability) => (
    <div className="run-actions">
      {item.status === "unpaid" && (
        <button onClick={() => void update(item, "scheduled")}>
          {locale === "es" ? "Programar" : "Schedule"}
        </button>
      )}
      {item.status !== "paid" && (
        <button onClick={() => void update(item, "paid")}>
          {locale === "es" ? "Marcar pagado" : "Mark paid"}
        </button>
      )}
      {item.status !== "filed" && (
        <button onClick={() => void update(item, "filed")}>
          {locale === "es" ? "Marcar radicado" : "Mark filed"}
        </button>
      )}
    </div>
  );
  const remittanceSelector = (item: DeductionRemittanceEligible) => (
    <label className="check-label">
      <input
        type="checkbox"
        aria-label={locale === "es" ? `Seleccionar remesa para ${item.workerName}` : `Select remittance for ${item.workerName}`}
        checked={selectedRemittanceIds.includes(item.liabilityId)}
        onChange={() => toggleRemittance(item)}
      />
      <span className="visually-hidden">
        {locale === "es"
          ? `Seleccionar remesa para ${item.workerName}`
          : `Select remittance for ${item.workerName}`}
      </span>
    </label>
  );
  return (
    <div className="content">
      <p className="kicker">{tenant.displayName}</p>
      <h1>
        {locale === "es"
          ? "Informes y reconciliación"
          : "Reports and reconciliation"}
      </h1>
      <p className="lead">
        {locale === "es"
          ? "Totales derivados únicamente de nóminas finalizadas y pagos de contratistas marcados como pagados."
          : "Totals derived only from finalized payrolls and contractor payments marked paid."}
      </p>
      <div className="report-toolbar">
        <label>
          {locale === "es" ? "Desde" : "From"}
          <input
            type="date"
            value={from}
            onInput={(e) => setFrom(e.currentTarget.value)}
          />
        </label>
        <label>
          {locale === "es" ? "Hasta" : "To"}
          <input
            type="date"
            value={to}
            onInput={(e) => setTo(e.currentTarget.value)}
          />
        </label>
        <button className="primary" onClick={() => void load()}>
          {locale === "es" ? "Actualizar" : "Refresh"}
        </button>
        <button onClick={() => void exportCsv()}>
          {locale === "es" ? "Exportar CSV de nómina" : "Export payroll CSV"}
        </button>
      </div>
      <OperationalError value={error} locale={locale} />
      {busy ? (
        <LoaderCircle className="spin" />
      ) : (
        summary && (
          <>
            <div className="balance-grid">
              <article>
                <strong>
                  {locale === "es" ? "Nómina bruta" : "Gross payroll"}
                </strong>
                <em>{money(summary.payroll.grossCents)}</em>
                <span>
                  {summary.payroll.runCount}{" "}
                  {locale === "es" ? "nóminas finalizadas" : "finalized runs"}
                </span>
              </article>
              <article>
                <strong>{locale === "es" ? "Neto" : "Net pay"}</strong>
                <em>{money(summary.payroll.netCents)}</em>
                <span>
                  {summary.payroll.workerPaymentCount}{" "}
                  {locale === "es" ? "pagos" : "payments"}
                </span>
              </article>
              <article>
                <strong>
                  {locale === "es"
                    ? "Contribuciones patronales"
                    : "Employer taxes"}
                </strong>
                <em>{money(summary.payroll.employerTaxesCents)}</em>
                <span>
                  {locale === "es" ? "Costo total" : "Total cost"}:{" "}
                  {money(summary.payroll.totalEmployerCostCents)}
                </span>
              </article>
              <article>
                <strong>
                  {locale === "es"
                    ? "Servicios profesionales"
                    : "Professional services"}
                </strong>
                <em>{money(summary.professionalServices.grossCents)}</em>
                <span>
                  {locale === "es" ? "Servicios" : "Services"}: {money(summary.professionalServices.serviceCompensationCents)} · {locale === "es" ? "Propinas" : "Tips"}: {money(summary.professionalServices.tipCents)} ·{" "}
                  {locale === "es" ? "Retenido" : "Withheld"}:{" "}
                  {money(summary.professionalServices.withholdingCents)}
                </span>
              </article>
            </div>
            <h2 className="subheading">
              {locale === "es" ? "Contribuciones por tipo" : "Taxes by type"}
            </h2>
            <ResponsiveDataTable
              label={
                locale === "es"
                  ? "Tabla de contribuciones por tipo"
                  : "Taxes by type table"
              }
              caption={
                locale === "es"
                  ? "Salarios sujetos y contribuciones de empleado y patrono"
                  : "Taxable wages and employee and employer taxes"
              }
              columns={
                locale === "es"
                  ? ["Contribución", "Salarios sujetos", "Empleado", "Patrono"]
                  : ["Tax", "Taxable wages", "Employee", "Employer"]
              }
              empty={
                locale === "es"
                  ? "No hay contribuciones para este período."
                  : "There are no taxes for this period."
              }
              rows={summary.taxes.map((item) => ({
                key: item.taxKey,
                cells: [
                  <strong>{optionLabel(item.taxKey, locale)}</strong>,
                  money(item.taxableWagesCents),
                  money(item.employeeAmountCents),
                  money(item.employerAmountCents),
                ],
                card: (
                  <article key={item.taxKey}>
                    <div>
                      <strong>{optionLabel(item.taxKey, locale)}</strong>
                      <p>
                        {locale === "es" ? "Salarios sujetos" : "Taxable wages"}
                        : {money(item.taxableWagesCents)}
                      </p>
                    </div>
                    <span>
                      {locale === "es" ? "Empleado" : "Employee"}:{" "}
                      {money(item.employeeAmountCents)} ·{" "}
                      {locale === "es" ? "Patrono" : "Employer"}:{" "}
                      {money(item.employerAmountCents)}
                    </span>
                  </article>
                ),
              }))}
            />
            <h2 className="subheading">
              {locale === "es" ? "Reconciliación" : "Reconciliation"}
            </h2>
            <p
              className={
                recon?.status === "reconciled" ? "success-note" : "form-error"
              }
            >
              {recon?.status === "reconciled"
                ? locale === "es"
                  ? `${recon.runCount} nóminas reconciliadas sin diferencias.`
                  : `${recon.runCount} payroll runs reconciled without differences.`
                : locale === "es"
                  ? `${recon?.checks.length ?? 0} diferencias detectadas.`
                  : `${recon?.checks.length ?? 0} discrepancies found.`}
            </p>
            <ResponsiveDataTable
              label={
                locale === "es"
                  ? "Tabla de diferencias de reconciliación"
                  : "Reconciliation discrepancies table"
              }
              caption={
                locale === "es"
                  ? "Diferencias esperadas y actuales por alcance y fecha"
                  : "Expected and actual differences by scope and date"
              }
              columns={
                locale === "es"
                  ? [
                      "Comprobación",
                      "Alcance y fecha",
                      "Esperado",
                      "Actual",
                      "Diferencia",
                    ]
                  : [
                      "Check",
                      "Scope and date",
                      "Expected",
                      "Actual",
                      "Difference",
                    ]
              }
              empty={
                locale === "es"
                  ? "No se detectaron diferencias."
                  : "No discrepancies were detected."
              }
              rows={(recon?.checks ?? []).map((item, index) => {
                const renderValue = (value: number) =>
                  item.unit === "cents"
                    ? money(value)
                    : `${value.toFixed(4)} h`;
                return {
                  key: `${item.scopeId}-${item.code}-${index}`,
                  cells: [
                    <strong>{optionLabel(item.code, locale)}</strong>,
                    <>
                      {optionLabel(item.scopeType, locale)}
                      <small>{formatDate(item.effectiveDate, locale)}</small>
                    </>,
                    renderValue(item.expected),
                    renderValue(item.actual),
                    renderValue(item.difference),
                  ],
                  card: (
                    <article key={`${item.scopeId}-${item.code}-${index}`}>
                      <div>
                        <strong>{optionLabel(item.code, locale)}</strong>
                        <p>
                          {optionLabel(item.scopeType, locale)} ·{" "}
                          {formatDate(item.effectiveDate, locale)}
                        </p>
                      </div>
                      <span>
                        {renderValue(item.expected)} vs{" "}
                        {renderValue(item.actual)} ·{" "}
                        {renderValue(item.difference)}
                      </span>
                    </article>
                  ),
                };
              })}
            />
            <h2 className="subheading">
              {locale === "es" ? "Obligaciones" : "Liabilities"}
            </h2>
            <ResponsiveDataTable
              label={
                locale === "es" ? "Tabla de obligaciones" : "Liabilities table"
              }
              caption={
                locale === "es"
                  ? "Obligaciones por tipo, período, vencimiento, importe, estado y acciones"
                  : "Liabilities by type, period, due date, amount, status, and actions"
              }
              columns={
                locale === "es"
                  ? [
                      "Obligación",
                      "Período y vencimiento",
                      "Importe",
                      "Estado",
                      "Pago",
                      "Acciones",
                    ]
                  : [
                      "Liability",
                      "Period and due date",
                      "Amount",
                      "Status",
                      "Payment",
                      "Actions",
                    ]
              }
              empty={
                locale === "es"
                  ? "No hay obligaciones registradas."
                  : "There are no recorded liabilities."
              }
              rows={liabilities.map((item) => ({
                key: item.id,
                cells: [
                  <strong>{optionLabel(item.liabilityKey, locale)}</strong>,
                  <>
                    {formatDateRange(
                      item.periodStart,
                      item.periodEnd,
                      locale,
                      "—",
                    )}
                    <small>
                      {locale === "es" ? "Vence" : "Due"}:{" "}
                      {formatDate(item.dueDate, locale)}
                    </small>
                  </>,
                  money(item.amountCents),
                  <span className={`run-status ${item.status}`}>
                    {optionLabel(item.status, locale)}
                  </span>,
                  <>
                    {formatDate(item.paymentDate, locale)}
                    <small>{item.confirmation ?? "—"}</small>
                  </>,
                  liabilityActions(item),
                ],
                card: (
                  <article key={item.id}>
                    <div>
                      <strong>
                        {optionLabel(item.liabilityKey, locale)} ·{" "}
                        {money(item.amountCents)}
                      </strong>
                      <p>
                        {formatDateRange(
                          item.periodStart,
                          item.periodEnd,
                          locale,
                          "—",
                        )}{" "}
                        · {locale === "es" ? "vence" : "due"}{" "}
                        {formatDate(item.dueDate, locale)}
                      </p>
                      {item.paymentDate && (
                        <small>
                          {formatDate(item.paymentDate, locale)} ·{" "}
                          {item.confirmation ?? "—"}
                        </small>
                      )}
                    </div>
                    <span className={`run-status ${item.status}`}>
                      {optionLabel(item.status, locale)}
                    </span>
                    {liabilityActions(item)}
                  </article>
                ),
              }))}
            />
            <h2 className="subheading">
              {locale === "es"
                ? "Remesas de deducciones"
                : "Deduction remittances"}
            </h2>
            <p>
              {locale === "es"
                ? "Seleccione obligaciones del mismo beneficiario. El pago requiere comprobante y se concilia exactamente con las retenciones finalizadas."
                : "Select liabilities for one payee. Payment requires retained evidence and reconciles exactly to finalized withholdings."}
            </p>
            <ResponsiveDataTable
              label={
                locale === "es"
                  ? "Tabla de obligaciones elegibles para remesa"
                  : "Remittance-eligible liabilities table"
              }
              caption={
                locale === "es"
                  ? "Selección de deducciones por persona, beneficiario, período e importe"
                  : "Deduction selection by worker, payee, period, and amount"
              }
              columns={
                locale === "es"
                  ? [
                      "Seleccionar",
                      "Persona y deducción",
                      "Beneficiario",
                      "Período",
                      "Referencia",
                      "Importe",
                    ]
                  : [
                      "Select",
                      "Worker and deduction",
                      "Payee",
                      "Period",
                      "Reference",
                      "Amount",
                    ]
              }
              empty={
                locale === "es"
                  ? "No hay deducciones pendientes elegibles para remesa."
                  : "There are no pending deductions eligible for remittance."
              }
              rows={remittanceEligible.map((item) => ({
                key: item.liabilityId,
                cells: [
                  remittanceSelector(item),
                  <>
                    <strong>{item.workerName}</strong>
                    <small>{optionLabel(item.deductionType, locale)}</small>
                  </>,
                  item.payee ?? (locale === "es" ? "Patrono" : "Employer"),
                  formatDateRange(
                    item.periodStart,
                    item.periodEnd,
                    locale,
                    "—",
                  ),
                  item.referenceNumber ?? item.caseNumber ?? "—",
                  money(item.amountCents),
                ],
                card: (
                  <article key={item.liabilityId}>
                    <label className="check-label">
                      <input
                        type="checkbox"
                        checked={selectedRemittanceIds.includes(
                          item.liabilityId,
                        )}
                        onChange={() => toggleRemittance(item)}
                      />
                      <span>
                        <strong>
                          {item.workerName} ·{" "}
                          {optionLabel(item.deductionType, locale)} ·{" "}
                          {money(item.amountCents)}
                        </strong>
                        <small>
                          {item.payee ??
                            (locale === "es" ? "Patrono" : "Employer")}{" "}
                          ·{" "}
                          {formatDateRange(
                            item.periodStart,
                            item.periodEnd,
                            locale,
                            "—",
                          )}{" "}
                          · {item.referenceNumber ?? item.caseNumber ?? "—"}
                        </small>
                      </span>
                    </label>
                  </article>
                ),
              }))}
            />
            {selectedRemittanceIds.length > 0 && (
              <form className="operation-panel" onSubmit={createRemittance}>
                <div className="form-grid">
                  <label>
                    {locale === "es" ? "Beneficiario" : "Payee"}
                    <input required readOnly value={remittanceForm.payee} />
                  </label>
                  <label>
                    {locale === "es" ? "Fecha de pago" : "Payment date"}
                    <input
                      required
                      type="date"
                      value={remittanceForm.paymentDate}
                      onInput={(e) =>
                        setRemittanceForm({
                          ...remittanceForm,
                          paymentDate: e.currentTarget.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    {locale === "es" ? "Método" : "Method"}
                    <select
                      value={remittanceForm.paymentMethod}
                      onChange={(e) =>
                        setRemittanceForm({
                          ...remittanceForm,
                          paymentMethod: e.currentTarget.value,
                        })
                      }
                    >
                      <option value="portal">
                        {optionLabel("portal", locale)}
                      </option>
                      <option value="ach">{optionLabel("ach", locale)}</option>
                      <option value="check">
                        {optionLabel("check", locale)}
                      </option>
                      <option value="wire">
                        {optionLabel("wire", locale)}
                      </option>
                      <option value="other">
                        {optionLabel("other", locale)}
                      </option>
                    </select>
                  </label>
                  <label>
                    {locale === "es" ? "Confirmación" : "Confirmation"}
                    <input
                      required
                      value={remittanceForm.confirmation}
                      onChange={(e) =>
                        setRemittanceForm({
                          ...remittanceForm,
                          confirmation: e.currentTarget.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    {locale === "es" ? "Razón / nota" : "Reason / note"}
                    <input
                      required
                      value={remittanceForm.reason}
                      onChange={(e) =>
                        setRemittanceForm({
                          ...remittanceForm,
                          reason: e.currentTarget.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    {locale === "es"
                      ? "Comprobante PDF o imagen"
                      : "PDF or image evidence"}
                    <input
                      required
                      type="file"
                      accept="application/pdf,image/png,image/jpeg"
                      onChange={(e) =>
                        setRemittanceFile(e.currentTarget.files?.[0] ?? null)
                      }
                    />
                  </label>
                </div>
                <button className="primary" disabled={busy || !remittanceFile}>
                  {locale === "es"
                    ? `Registrar remesa (${money(remittanceEligible.filter((item) => selectedRemittanceIds.includes(item.liabilityId)).reduce((sum, item) => sum + item.amountCents, 0))})`
                    : `Record remittance (${money(remittanceEligible.filter((item) => selectedRemittanceIds.includes(item.liabilityId)).reduce((sum, item) => sum + item.amountCents, 0))})`}
                </button>
              </form>
            )}
            <h3>
              {locale === "es" ? "Historial de remesas" : "Remittance history"}
            </h3>
            <ResponsiveDataTable
              label={
                locale === "es"
                  ? "Tabla de historial de remesas"
                  : "Remittance history table"
              }
              caption={
                locale === "es"
                  ? "Remesas registradas por beneficiario, pago, importe, estado y evidencia"
                  : "Recorded remittances by payee, payment, amount, status, and evidence"
              }
              columns={
                locale === "es"
                  ? [
                      "Beneficiario",
                      "Pago",
                      "Obligaciones",
                      "Importe",
                      "Estado",
                      "Evidencia",
                    ]
                  : [
                      "Payee",
                      "Payment",
                      "Liabilities",
                      "Amount",
                      "Status",
                      "Evidence",
                    ]
              }
              empty={
                locale === "es"
                  ? "No hay remesas registradas."
                  : "There are no recorded remittances."
              }
              rows={remittanceBatches.map((item) => ({
                key: item.id,
                cells: [
                  <>
                    <strong>{item.payee}</strong>
                    <small>{item.reason}</small>
                  </>,
                  <>
                    {formatDate(item.paymentDate, locale)}
                    <small>
                      {optionLabel(item.paymentMethod, locale)} ·{" "}
                      {item.confirmation}
                    </small>
                  </>,
                  item.allocationCount,
                  money(item.totalCents),
                  <span className={`run-status ${item.status}`}>
                    {optionLabel(item.status, locale)}
                  </span>,
                  <button onClick={() => void downloadRemittance(item)}>
                    {locale === "es" ? "Comprobante" : "Evidence"}
                  </button>,
                ],
                card: (
                  <article key={item.id}>
                    <div>
                      <strong>
                        {item.payee} · {money(item.totalCents)}
                      </strong>
                      <p>
                        {formatDate(item.paymentDate, locale)} ·{" "}
                        {optionLabel(item.paymentMethod, locale)} ·{" "}
                        {item.confirmation} · {item.allocationCount}{" "}
                        {locale === "es" ? "obligaciones" : "liabilities"}
                      </p>
                      <small>
                        SHA-256 {item.evidenceSha256.slice(0, 16)}… ·{" "}
                        {item.reason}
                      </small>
                    </div>
                    <span className={`run-status ${item.status}`}>
                      {optionLabel(item.status, locale)}
                    </span>
                    <button onClick={() => void downloadRemittance(item)}>
                      {locale === "es" ? "Comprobante" : "Evidence"}
                    </button>
                  </article>
                ),
              }))}
            />
          </>
        )
      )}
      <ComplianceExports locale={locale} tenant={tenant} />
    </div>
  );
}

function ReviewSwitcher({
  locale,
  tenant,
  onChange,
  viewport,
  onViewport,
  mode,
  onMode,
}: {
  locale: Locale;
  tenant: Tenant;
  onChange: (context: ReviewContext) => void;
  viewport: "desktop" | "tablet" | "iphone" | "android";
  onViewport: (value: "desktop" | "tablet" | "iphone" | "android") => void;
  mode: "read_only" | "interactive";
  onMode: (value: "read_only" | "interactive") => void;
}) {
  const [data, setData] = useState<ReviewData | null>(null);
  const [role, setRole] = useState("platform_administrator");
  const [worker, setWorker] = useState("");
  const reviewSaveQueue = useRef<Promise<void>>(Promise.resolve());
  const reviewSaveVersion = useRef(0);
  const load = async () => {
    const response = await api("/auth/developer/review-options");
    if (response.ok) {
      const body = (await response.json()) as ReviewData;
      setData(body);
      setRole(body.current.roleKey);
      setWorker(body.current.workerId ?? "");
      const currentPerson = body.people.find(
        (person) => person.workerId === body.current.workerId,
      );
      onChange({
        ...body.current,
        workerType: currentPerson?.workerType ?? null,
        workerName: currentPerson?.displayName ?? null,
      });
    }
  };
  useEffect(() => {
    void load();
    const listener = () => void load();
    window.addEventListener("workers-updated", listener);
    return () => window.removeEventListener("workers-updated", listener);
  }, []);
  const people =
      data?.people.filter((person) => person.tenantId === tenant.id) ?? [],
    roleName = (value: string) =>
      ({
        platform_administrator: {
          es: "Administrador de plataforma",
          en: "Platform administrator",
        },
        owner: { es: "Dueño", en: "Owner" },
        administrator: { es: "Administrador", en: "Administrator" },
        supervisor: { es: "Supervisor", en: "Supervisor" },
        user: { es: "Usuario", en: "User" },
      })[value]?.[locale] ?? value.replaceAll("_", " ");
  const save = async (nextRole: string, nextWorker: string) => {
    const version = ++reviewSaveVersion.current;
    setRole(nextRole);
    setWorker(nextWorker);
    const persistedContext = {
      tenantId: nextRole === "platform_administrator" ? null : tenant.id,
      roleKey: nextRole,
      workerId: nextWorker || null,
    };
    const selectedPerson = people.find(
      (person) => person.workerId === nextWorker,
    );
    const context: ReviewContext = {
      ...persistedContext,
      workerType: selectedPerson?.workerType ?? null,
      workerName: selectedPerson?.displayName ?? null,
    };
    reviewSaveQueue.current = reviewSaveQueue.current.then(async () => {
      const response = await api("/auth/developer/review-context", {
        method: "PUT",
        body: JSON.stringify(persistedContext),
      });
      if (response.ok && version === reviewSaveVersion.current)
        onChange(context);
    });
    await reviewSaveQueue.current;
  };
  return (
    <div className="review-switcher">
      <span>{locale === "es" ? "REVISAR COMO" : "REVIEW AS"}</span>
      <select
        aria-label={locale === "es" ? "Vista por rol" : "Role view"}
        value={role}
        onChange={(e) => void save(e.currentTarget.value, worker)}
      >
        {data?.roles.map((item) => (
          <option key={item} value={item}>
            {roleName(item)}
          </option>
        ))}
      </select>
      {role !== "platform_administrator" && (
        <select
          aria-label={locale === "es" ? "Vista por persona" : "Person view"}
          value={worker}
          onChange={(e) => void save(role, e.currentTarget.value)}
        >
          <option value="">
            {locale === "es" ? "Vista general del rol" : "General role view"}
          </option>
          {people.map((person) => (
            <option value={person.workerId} key={person.workerId}>
              {person.displayName} ·{" "}
              {person.workerType === "employee"
                ? locale === "es"
                  ? "empleado"
                  : "employee"
                : person.workerType === "professional_services"
                  ? locale === "es"
                    ? "contratista"
                    : "contractor"
                  : locale === "es"
                    ? "clasificación pendiente"
                    : "classification pending"}{" "}
              · {locale === "es" ? "rol real" : "actual role"}:{" "}
              {roleName(person.assignedRoleKey ?? "user")}
            </option>
          ))}
        </select>
      )}
      <select
        aria-label={
          locale === "es" ? "Vista de dispositivo" : "Device viewport"
        }
        value={viewport}
        onChange={(event) => onViewport(event.target.value as typeof viewport)}
      >
        <option value="desktop">{optionLabel("desktop", locale)}</option>
        <option value="tablet">{optionLabel("tablet", locale)}</option>
        <option value="iphone">{optionLabel("iphone", locale)}</option>
        <option value="android">{optionLabel("android", locale)}</option>
      </select>
      <select
        aria-label={locale === "es" ? "Modo de prueba" : "Testing mode"}
        value={mode}
        onChange={(event) => onMode(event.target.value as typeof mode)}
      >
        <option value="read_only">
          {locale === "es" ? "Solo lectura" : "Read only"}
        </option>
        <option value="interactive">
          {locale === "es" ? "Prueba interactiva" : "Interactive testing"}
        </option>
      </select>
    </div>
  );
}

type CompanyAddress = {
  line1: string;
  line2: string | null;
  city: string;
  state: string;
  postalCode: string;
  country: string;
};
type DepositScheduleEvent = {
  id: string;
  jurisdiction: "PR" | "US";
  oldSchedule: string;
  newSchedule: "semiweekly";
  effectiveOn: string;
  throughYear: number;
  reason: string;
  payrollRunId: string;
  createdAt: string;
};
type WorkweekDefinition = {
  id: string;
  effectiveFrom: string;
  startsOn: number;
  startsAt: string;
  reason: string;
  changedBy: string;
  createdAt: string;
};
type CompanyProfile = {
  legalName: string;
  defaultLocale: "es-PR" | "en-PR";
  prWithholdingDepositSchedule:
    | "quarterly_under_2500"
    | "monthly"
    | "semiweekly"
    | null;
  federalEmploymentDepositSchedule: "monthly" | "semiweekly" | null;
  dbaName: string | null;
  maskedEin: string | null;
  hasEin: boolean;
  prEmployerAccount: string | null;
  suriAccountReference: string | null;
  dtrhEmployerAccount: string | null;
  cfsePolicyNumber: string | null;
  physicalAddress: CompanyAddress | null;
  mailingAddress: CompanyAddress | null;
  phoneE164: string | null;
  email: string | null;
  contactName: string | null;
  timezone: "America/Puerto_Rico";
  payrollFrequency:
    | "weekly"
    | "biweekly"
    | "semimonthly"
    | "monthly"
    | "custom";
  workweekStartsOn: number;
  workweekStartsAt: string;
  workweekEffectiveFrom: string;
};

const blankProfile: CompanyProfile = {
  legalName: "",
  defaultLocale: "es-PR",
  prWithholdingDepositSchedule: null,
  federalEmploymentDepositSchedule: null,
  dbaName: null,
  maskedEin: null,
  hasEin: false,
  prEmployerAccount: null,
  suriAccountReference: null,
  dtrhEmployerAccount: null,
  cfsePolicyNumber: null,
  physicalAddress: null,
  mailingAddress: null,
  phoneE164: null,
  email: null,
  contactName: null,
  timezone: "America/Puerto_Rico",
  payrollFrequency: "biweekly",
  workweekStartsOn: 0,
  workweekStartsAt: "00:00",
  workweekEffectiveFrom: new Date().toISOString().slice(0, 10),
};

type PayDifferentialRule = {
  id: string;
  name: string;
  sourceCode: string;
  centsPerHour: number;
  includeOvertime: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  active: number;
};
type PayDifferentialAssignment = {
  id: string;
  ruleId: string;
  ruleName: string;
  workerId: string;
  workerName: string;
  effectiveFrom: string;
  effectiveTo: string | null;
};
function PayDifferentialPanel({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const [rules, setRules] = useState<PayDifferentialRule[]>([]),
    [assignments, setAssignments] = useState<PayDifferentialAssignment[]>([]),
    [workers, setWorkers] = useState<
      {
        id: string;
        firstName: string;
        lastName: string | null;
        status: string;
      }[]
    >([]),
    [busy, setBusy] = useState(true),
    [message, setMessage] = useState("");
  const [rule, setRule] = useState({
      name: "Diferencial PRIDCO",
      sourceCode: "P",
      centsPerHour: "200",
      effectiveFrom: "2026-01-01",
    }),
    [assignment, setAssignment] = useState({
      ruleId: "",
      workerId: "",
      effectiveFrom: "2026-01-01",
    });
  const load = async () => {
    setBusy(true);
    const [d, w] = await Promise.all([
      api(`/v1/tenants/${tenant.id}/pay-differentials`),
      api(`/v1/tenants/${tenant.id}/workers`),
    ]);
    if (d.ok) {
      const body = (await d.json()) as {
        rules: PayDifferentialRule[];
        assignments: PayDifferentialAssignment[];
      };
      setRules(body.rules);
      setAssignments(body.assignments);
      setAssignment((current) => ({
        ...current,
        ruleId:
          current.ruleId || body.rules.find((item) => item.active)?.id || "",
      }));
    }
    if (w.ok)
      setWorkers(
        (
          (await w.json()) as {
            workers: {
              id: string;
              firstName: string;
              lastName: string | null;
              status: string;
            }[];
          }
        ).workers,
      );
    setBusy(false);
  };
  useEffect(() => {
    void load();
  }, [tenant.id]);
  const createRule = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    const response = await api(`/v1/tenants/${tenant.id}/pay-differentials`, {
      method: "POST",
      body: JSON.stringify({
        name: rule.name,
        sourceCode: rule.sourceCode,
        centsPerHour: Number(rule.centsPerHour),
        includeOvertime: true,
        effectiveFrom: rule.effectiveFrom,
        effectiveTo: null,
      }),
    });
    setMessage(
      response.ok
        ? locale === "es"
          ? "Regla creada y auditada."
          : "Rule created and audited."
        : locale === "es"
          ? "No se pudo crear la regla."
          : "The rule could not be created.",
    );
    await load();
  };
  const assign = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    const response = await api(
      `/v1/tenants/${tenant.id}/pay-differential-assignments`,
      {
        method: "POST",
        body: JSON.stringify({ ...assignment, effectiveTo: null }),
      },
    );
    setMessage(
      response.ok
        ? locale === "es"
          ? "Regla asignada a la persona."
          : "Rule assigned to the person."
        : locale === "es"
          ? "No se pudo asignar; revise si ya está asignada."
          : "Could not assign; check whether it is already assigned.",
    );
    if (response.ok) setAssignment((current) => ({ ...current, workerId: "" }));
    await load();
  };
  return (
    <section className="settings-form">
      <h2>
        {locale === "es"
          ? "Diferenciales automáticos por código"
          : "Automatic code-based differentials"}
      </h2>
      <p>
        {locale === "es"
          ? "El código del archivo de tiempo activa un pago adicional solo para las personas asignadas. Las comidas no pagadas se excluyen antes del cálculo; las horas regulares y extra reciben el diferencial."
          : "The time-file code activates extra pay only for assigned people. Unpaid meals are excluded before calculation; regular and overtime hours receive the differential."}
      </p>
      {message && <p className="success-note">{message}</p>}
      {busy && <LoaderCircle className="spin" />}
      {rules.length === 0 ? (
        <details open>
          <summary>
            {locale === "es"
              ? "Crear regla reutilizable"
              : "Create reusable rule"}
          </summary>
          <form onSubmit={createRule}>
            <div className="form-grid">
              <label>
                {locale === "es" ? "Nombre" : "Name"}
                <input
                  required
                  value={rule.name}
                  onChange={(e) =>
                    setRule({ ...rule, name: e.currentTarget.value })
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Código de tiempo" : "Time code"}
                <input
                  required
                  maxLength={20}
                  value={rule.sourceCode}
                  onChange={(e) =>
                    setRule({
                      ...rule,
                      sourceCode: e.currentTarget.value.toUpperCase(),
                    })
                  }
                />
              </label>
              <label>
                {locale === "es"
                  ? "Adicional por hora (centavos)"
                  : "Additional hourly cents"}
                <input
                  required
                  type="number"
                  min="1"
                  value={rule.centsPerHour}
                  onChange={(e) =>
                    setRule({ ...rule, centsPerHour: e.currentTarget.value })
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Vigente desde" : "Effective from"}
                <input
                  required
                  type="date"
                  value={rule.effectiveFrom}
                  onInput={(e) =>
                    setRule({ ...rule, effectiveFrom: e.currentTarget.value })
                  }
                />
              </label>
            </div>
            <button className="primary" disabled={busy}>
              {locale === "es" ? "Crear regla" : "Create rule"}
            </button>
          </form>
        </details>
      ) : (
        <>
          <div className="run-list">
            {rules.map((item) => (
              <article key={item.id}>
                <div>
                  <strong>
                    {item.name} · {item.sourceCode}
                  </strong>
                  <small>
                    {new Intl.NumberFormat(
                      locale === "es" ? "es-PR" : "en-US",
                      { style: "currency", currency: "USD" },
                    ).format(item.centsPerHour / 100)}{" "}
                    {locale === "es"
                      ? "por hora pagada · incluye horas extra · comida excluida"
                      : "per paid hour · includes overtime · meal excluded"}
                  </small>
                </div>
                <span>
                  {formatDateRange(
                    item.effectiveFrom,
                    item.effectiveTo,
                    locale,
                    locale === "es" ? "sin fecha final" : "no end date",
                  )}
                </span>
              </article>
            ))}
          </div>
          <details open>
            <summary>
              {locale === "es"
                ? "Asignar regla a otra persona"
                : "Assign rule to another person"}
            </summary>
            <form onSubmit={assign}>
              <div className="form-grid">
                <label>
                  {locale === "es" ? "Regla" : "Rule"}
                  <select
                    required
                    value={assignment.ruleId}
                    onChange={(e) =>
                      setAssignment({
                        ...assignment,
                        ruleId: e.currentTarget.value,
                      })
                    }
                  >
                    <option value="">—</option>
                    {rules
                      .filter((item) => item.active)
                      .map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                  </select>
                </label>
                <label>
                  {locale === "es" ? "Persona" : "Person"}
                  <select
                    required
                    value={assignment.workerId}
                    onChange={(e) =>
                      setAssignment({
                        ...assignment,
                        workerId: e.currentTarget.value,
                      })
                    }
                  >
                    <option value="">—</option>
                    {workers
                      .filter(
                        (item) =>
                          !["inactive", "terminated"].includes(item.status),
                      )
                      .map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.firstName} {item.lastName ?? ""}
                        </option>
                      ))}
                  </select>
                </label>
                <label>
                  {locale === "es" ? "Vigente desde" : "Effective from"}
                  <input
                    required
                    type="date"
                    value={assignment.effectiveFrom}
                    onInput={(e) =>
                      setAssignment({
                        ...assignment,
                        effectiveFrom: e.currentTarget.value,
                      })
                    }
                  />
                </label>
              </div>
              <button className="primary" disabled={busy}>
                {locale === "es" ? "Asignar" : "Assign"}
              </button>
            </form>
          </details>
          <h3>{locale === "es" ? "Asignaciones" : "Assignments"}</h3>
          <div className="run-list">
            {assignments.map((item) => (
              <article key={item.id}>
                <div>
                  <strong>{item.workerName}</strong>
                  <small>{item.ruleName}</small>
                </div>
                <span>
                  {formatDateRange(
                    item.effectiveFrom,
                    item.effectiveTo,
                    locale,
                    locale === "es" ? "sin fecha final" : "no end date",
                  )}
                </span>
              </article>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

type PlatformStatutorySource = {
  id: string;
  jurisdiction: string;
  agency: string;
  topic: string;
  titleEs: string;
  titleEn: string;
  officialUrl: string;
  lastVerifiedOn: string;
  status: "current" | "superseded" | "unavailable";
};
type PlatformStatutoryRule = {
  id: string;
  jurisdiction: string;
  ruleKey: string;
  version: string;
  taxYear: number | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  valueJson: string;
  sourceId: string;
  sourceDocumentId: string;
  lastVerifiedOn: string;
  status: "draft" | "active" | "superseded";
};
type PlatformStatutoryDocument = {
  id: string;
  sourceId: string;
  documentKind: "external_reference" | "private_snapshot";
  title: string;
  version: string;
  externalUrl: string | null;
  fileName: string | null;
  sha256: string | null;
  sizeBytes: number | null;
  verifiedOn: string;
  createdAt: string;
};
type PlatformStatutoryEvent = {
  id: string;
  resourceType: "official_source" | "statutory_rule";
  resourceId: string;
  action: string;
  reason: string;
  developerEmail: string;
  occurredAt: string;
};

function PlatformStatutoryLibraryPanel({ locale }: { locale: Locale }) {
  const today = new Date().toISOString().slice(0, 10);
  const [sources, setSources] = useState<PlatformStatutorySource[]>([]);
  const [documents, setDocuments] = useState<PlatformStatutoryDocument[]>([]);
  const [rules, setRules] = useState<PlatformStatutoryRule[]>([]);
  const [events, setEvents] = useState<PlatformStatutoryEvent[]>([]);
  const [busy, setBusy] = useState(true);
  const [message, setMessage] = useState("");
  const [source, setSource] = useState({
    jurisdiction: "PR",
    agency: "",
    topic: "",
    titleEs: "",
    titleEn: "",
    officialUrl: "https://",
    lastVerifiedOn: today,
    reason: "",
  });
  const [document, setDocument] = useState({
    sourceId: "",
    documentKind: "external_reference" as
      | "external_reference"
      | "private_snapshot",
    title: "",
    version: "",
    externalUrl: "https://",
    verifiedOn: today,
    reason: "",
    file: null as File | null,
  });
  const [rule, setRule] = useState({
    jurisdiction: "PR",
    ruleKey: "",
    version: "",
    taxYear: String(new Date().getUTCFullYear()),
    effectiveFrom: today,
    effectiveTo: "",
    valueJson: '{\n  "value": ""\n}',
    sourceId: "",
    sourceDocumentId: "",
    lastVerifiedOn: today,
    reason: "",
  });
  const [editingSource, setEditingSource] = useState<
    (PlatformStatutorySource & { reason: string }) | null
  >(null);
  const [activation, setActivation] = useState({ ruleId: "", reason: "" });
  const load = async () => {
    setBusy(true);
    const response = await api("/v1/platform/statutory-library");
    if (response.ok) {
      const body = (await response.json()) as {
        sources: PlatformStatutorySource[];
        documents: PlatformStatutoryDocument[];
        rules: PlatformStatutoryRule[];
        events: PlatformStatutoryEvent[];
      };
      setSources(body.sources);
      setDocuments(body.documents);
      setRules(body.rules);
      setEvents(body.events);
    } else
      setMessage(
        locale === "es"
          ? "No se pudo cargar la biblioteca global."
          : "The global library could not be loaded.",
      );
    setBusy(false);
  };
  useEffect(() => {
    void load();
  }, []);
  const post = async (path: string, body: unknown, success: string) => {
    setBusy(true);
    setMessage("");
    const response = await api(path, {
      method: "POST",
      body: JSON.stringify(body),
    });
    setMessage(
      response.ok
        ? success
        : locale === "es"
          ? `No se guardó (${response.status}). Revise datos, estado y fechas.`
          : `Not saved (${response.status}). Review data, status, and dates.`,
    );
    if (response.ok) await load();
    else setBusy(false);
    return response.ok;
  };
  const createSource = async (event: FormEvent) => {
    event.preventDefault();
    if (
      await post(
        "/v1/platform/statutory-sources",
        source,
        locale === "es"
          ? "Fuente oficial retenida."
          : "Official source retained.",
      )
    )
      setSource({
        jurisdiction: "PR",
        agency: "",
        topic: "",
        titleEs: "",
        titleEn: "",
        officialUrl: "https://",
        lastVerifiedOn: today,
        reason: "",
      });
  };
  const updateSource = async (event: FormEvent) => {
    event.preventDefault();
    if (!editingSource) return;
    setBusy(true);
    setMessage("");
    const { id, status: _status, ...payload } = editingSource;
    const response = await api(`/v1/platform/statutory-sources/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
    setMessage(
      response.ok
        ? locale === "es"
          ? "Fuente oficial actualizada y auditada."
          : "Official source updated and audited."
        : locale === "es"
          ? `No se guardó (${response.status}).`
          : `Not saved (${response.status}).`,
    );
    if (response.ok) {
      setEditingSource(null);
      await load();
    } else setBusy(false);
  };
  const createRule = async (event: FormEvent) => {
    event.preventDefault();
    let value: Record<string, unknown>;
    try {
      value = JSON.parse(rule.valueJson) as Record<string, unknown>;
    } catch {
      setMessage(
        locale === "es"
          ? "El valor debe ser un objeto JSON válido."
          : "Value must be a valid JSON object.",
      );
      return;
    }
    if (!value || Array.isArray(value) || typeof value !== "object") {
      setMessage(
        locale === "es"
          ? "El valor debe ser un objeto JSON."
          : "Value must be a JSON object.",
      );
      return;
    }
    const payload = {
      ...rule,
      taxYear: rule.taxYear ? Number(rule.taxYear) : null,
      effectiveTo: rule.effectiveTo || null,
      value,
      valueJson: undefined,
    };
    if (
      await post(
        "/v1/platform/statutory-rules",
        payload,
        locale === "es"
          ? "Borrador de regla retenido."
          : "Rule draft retained.",
      )
    )
      setRule({
        jurisdiction: "PR",
        ruleKey: "",
        version: "",
        taxYear: String(new Date().getUTCFullYear()),
        effectiveFrom: today,
        effectiveTo: "",
        valueJson: '{\n  "value": ""\n}',
        sourceId: "",
        sourceDocumentId: "",
        lastVerifiedOn: today,
        reason: "",
      });
  };
  const createDocument = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    const body = new FormData();
    Object.entries(document).forEach(([key, value]) => {
      if (key !== "file" && value !== null) body.set(key, String(value));
    });
    if (document.file) body.set("file", document.file);
    const response = await api("/v1/platform/statutory-documents", {
      method: "POST",
      body,
    });
    setMessage(
      response.ok
        ? locale === "es"
          ? "Documento fuente retenido."
          : "Source document retained."
        : locale === "es"
          ? `No se guardó (${response.status}).`
          : `Not saved (${response.status}).`,
    );
    if (response.ok) {
      setDocument({
        sourceId: "",
        documentKind: "external_reference",
        title: "",
        version: "",
        externalUrl: "https://",
        verifiedOn: today,
        reason: "",
        file: null,
      });
      await load();
    } else setBusy(false);
  };
  return (
    <section
      className="panel-section"
      aria-labelledby="platform-statutory-title"
    >
      <p className="kicker">
        {locale === "es"
          ? "CONTROL GLOBAL DE PLATAFORMA"
          : "GLOBAL PLATFORM CONTROL"}
      </p>
      <h2 id="platform-statutory-title">
        {locale === "es"
          ? "Biblioteca estatutaria y fuentes oficiales"
          : "Statutory library and official sources"}
      </h2>
      <p>
        {locale === "es"
          ? "Solo el administrador de plataforma puede crear fuentes, preparar versiones y activar reglas. Cada versión y transición permanece inmutable y auditada para todos los clientes."
          : "Only the platform administrator can create sources, prepare versions, and activate rules. Every version and transition remains immutable and audited across all clients."}
      </p>
      <OperationalFeedback
        message={message}
        isError={message.includes("(")}
        locale={locale}
      />
      {busy && <LoaderCircle className="spin" />}
      <details>
        <summary>
          {locale === "es"
            ? "Añadir fuente oficial verificada"
            : "Add verified official source"}
        </summary>
        <form className="settings-form" onSubmit={createSource}>
          <div className="form-grid">
            <label>
              {locale === "es" ? "Jurisdicción" : "Jurisdiction"}
              <input
                required
                value={source.jurisdiction}
                onChange={(e) =>
                  setSource({ ...source, jurisdiction: e.currentTarget.value })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Agencia" : "Agency"}
              <input
                required
                value={source.agency}
                onChange={(e) =>
                  setSource({ ...source, agency: e.currentTarget.value })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Tema" : "Topic"}
              <input
                required
                value={source.topic}
                onChange={(e) =>
                  setSource({ ...source, topic: e.currentTarget.value })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Título en español" : "Spanish title"}
              <input
                required
                value={source.titleEs}
                onChange={(e) =>
                  setSource({ ...source, titleEs: e.currentTarget.value })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Título en inglés" : "English title"}
              <input
                required
                value={source.titleEn}
                onChange={(e) =>
                  setSource({ ...source, titleEn: e.currentTarget.value })
                }
              />
            </label>
            <label>
              {locale === "es" ? "URL oficial HTTPS" : "Official HTTPS URL"}
              <input
                required
                type="url"
                pattern="https://.*"
                value={source.officialUrl}
                onChange={(e) =>
                  setSource({ ...source, officialUrl: e.currentTarget.value })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Verificada el" : "Verified on"}
              <input
                required
                type="date"
                value={source.lastVerifiedOn}
                onInput={(e) =>
                  setSource({
                    ...source,
                    lastVerifiedOn: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Razón y evidencia" : "Reason and evidence"}
              <input
                required
                minLength={3}
                value={source.reason}
                onChange={(e) =>
                  setSource({ ...source, reason: e.currentTarget.value })
                }
              />
            </label>
          </div>
          <button className="primary" disabled={busy}>
            {locale === "es" ? "Retener fuente" : "Retain source"}
          </button>
        </form>
      </details>
      <details>
        <summary>
          {locale === "es"
            ? "Retener documento fuente inmutable"
            : "Retain immutable source document"}
        </summary>
        <form className="settings-form" onSubmit={createDocument}>
          <div className="form-grid">
            <label>
              {locale === "es" ? "Fuente oficial" : "Official source"}
              <select
                required
                value={document.sourceId}
                onChange={(e) =>
                  setDocument({ ...document, sourceId: e.currentTarget.value })
                }
              >
                <option value="">—</option>
                {sources
                  .filter((item) => item.status === "current")
                  .map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.agency} ·{" "}
                      {locale === "es" ? item.titleEs : item.titleEn}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              {locale === "es" ? "Tipo de evidencia" : "Evidence type"}
              <select
                value={document.documentKind}
                onChange={(e) =>
                  setDocument({
                    ...document,
                    documentKind: e.currentTarget
                      .value as typeof document.documentKind,
                    file: null,
                  })
                }
              >
                <option value="external_reference">
                  {locale === "es"
                    ? "Documento oficial externo"
                    : "External official document"}
                </option>
                <option value="private_snapshot">
                  {locale === "es"
                    ? "Copia privada en R2"
                    : "Private R2 snapshot"}
                </option>
              </select>
            </label>
            <label>
              {locale === "es" ? "Título del documento" : "Document title"}
              <input
                required
                value={document.title}
                onChange={(e) =>
                  setDocument({ ...document, title: e.currentTarget.value })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Versión/publicación" : "Version/publication"}
              <input
                required
                value={document.version}
                onChange={(e) =>
                  setDocument({ ...document, version: e.currentTarget.value })
                }
              />
            </label>
            {document.documentKind === "external_reference" ? (
              <label>
                {locale === "es" ? "URL HTTPS exacta" : "Exact HTTPS URL"}
                <input
                  required
                  type="url"
                  pattern="https://.*"
                  value={document.externalUrl}
                  onChange={(e) =>
                    setDocument({
                      ...document,
                      externalUrl: e.currentTarget.value,
                    })
                  }
                />
              </label>
            ) : (
              <label>
                {locale === "es"
                  ? "Archivo oficial (máx. 20 MB)"
                  : "Official file (max 20 MB)"}
                <input
                  required
                  type="file"
                  onChange={(e) =>
                    setDocument({
                      ...document,
                      file: e.currentTarget.files?.[0] ?? null,
                    })
                  }
                />
              </label>
            )}
            <label>
              {locale === "es" ? "Verificado el" : "Verified on"}
              <input
                required
                type="date"
                value={document.verifiedOn}
                onInput={(e) =>
                  setDocument({
                    ...document,
                    verifiedOn: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Razón y evidencia" : "Reason and evidence"}
              <input
                required
                minLength={3}
                value={document.reason}
                onChange={(e) =>
                  setDocument({ ...document, reason: e.currentTarget.value })
                }
              />
            </label>
          </div>
          <button className="primary" disabled={busy}>
            {locale === "es" ? "Retener documento" : "Retain document"}
          </button>
        </form>
      </details>
      <details>
        <summary>
          {locale === "es"
            ? "Preparar nueva versión de regla"
            : "Prepare a new rule version"}
        </summary>
        <form className="settings-form" onSubmit={createRule}>
          <div className="form-grid">
            <label>
              {locale === "es" ? "Jurisdicción" : "Jurisdiction"}
              <input
                required
                value={rule.jurisdiction}
                onChange={(e) =>
                  setRule({ ...rule, jurisdiction: e.currentTarget.value })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Clave de regla" : "Rule key"}
              <input
                required
                value={rule.ruleKey}
                onChange={(e) =>
                  setRule({ ...rule, ruleKey: e.currentTarget.value })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Versión" : "Version"}
              <input
                required
                value={rule.version}
                onChange={(e) =>
                  setRule({ ...rule, version: e.currentTarget.value })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Año contributivo" : "Tax year"}
              <input
                type="number"
                min="1900"
                max="2200"
                value={rule.taxYear}
                onInput={(e) =>
                  setRule({ ...rule, taxYear: e.currentTarget.value })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Vigente desde" : "Effective from"}
              <input
                required
                type="date"
                value={rule.effectiveFrom}
                onInput={(e) =>
                  setRule({ ...rule, effectiveFrom: e.currentTarget.value })
                }
              />
            </label>
            <label>
              {locale === "es"
                ? "Vigente hasta (opcional)"
                : "Effective to (optional)"}
              <input
                type="date"
                value={rule.effectiveTo}
                onInput={(e) =>
                  setRule({ ...rule, effectiveTo: e.currentTarget.value })
                }
              />
            </label>
            <label>
              {locale === "es"
                ? "Fuente oficial vigente"
                : "Current official source"}
              <select
                required
                value={rule.sourceId}
                onChange={(e) =>
                  setRule({
                    ...rule,
                    sourceId: e.currentTarget.value,
                    sourceDocumentId: "",
                  })
                }
              >
                <option value="">—</option>
                {sources
                  .filter((item) => item.status === "current")
                  .map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.agency} ·{" "}
                      {locale === "es" ? item.titleEs : item.titleEn}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              {locale === "es"
                ? "Documento fuente inmutable"
                : "Immutable source document"}
              <select
                required
                value={rule.sourceDocumentId}
                onChange={(e) =>
                  setRule({ ...rule, sourceDocumentId: e.currentTarget.value })
                }
              >
                <option value="">—</option>
                {documents
                  .filter((item) => item.sourceId === rule.sourceId)
                  .map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.title} · {item.version}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              {locale === "es" ? "Verificada el" : "Verified on"}
              <input
                required
                type="date"
                value={rule.lastVerifiedOn}
                onInput={(e) =>
                  setRule({ ...rule, lastVerifiedOn: e.currentTarget.value })
                }
              />
            </label>
            <label>
              {locale === "es" ? "Razón y evidencia" : "Reason and evidence"}
              <input
                required
                minLength={3}
                value={rule.reason}
                onChange={(e) =>
                  setRule({ ...rule, reason: e.currentTarget.value })
                }
              />
            </label>
            <label className="wide-field">
              {locale === "es" ? "Valor JSON de la regla" : "Rule value JSON"}
              <textarea
                required
                rows={8}
                value={rule.valueJson}
                onChange={(e) =>
                  setRule({ ...rule, valueJson: e.currentTarget.value })
                }
              />
            </label>
          </div>
          <button className="primary" disabled={busy}>
            {locale === "es" ? "Retener borrador" : "Retain draft"}
          </button>
        </form>
      </details>
      <h3>{locale === "es" ? "Fuentes retenidas" : "Retained sources"}</h3>
      <div className="run-list">
        {sources.map((item) => (
          <article
            key={item.id}
            className={
              editingSource?.id === item.id
                ? "source-card editing"
                : "source-card"
            }
          >
            <div>
              <strong>
                {item.agency} · {locale === "es" ? item.titleEs : item.titleEn}
              </strong>
              <small>
                {item.jurisdiction} · {item.topic} ·{" "}
                {formatDate(item.lastVerifiedOn, locale)}
              </small>
              <div className="source-actions">
                <a
                  className="official-source-link"
                  href={item.officialUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {locale === "es"
                    ? "Abrir fuente oficial"
                    : "Open official source"}
                </a>
                <button
                  type="button"
                  onClick={() => setEditingSource({ ...item, reason: "" })}
                >
                  {locale === "es"
                    ? "Actualizar fuente oficial"
                    : "Update official source"}
                </button>
              </div>
            </div>
            {editingSource?.id === item.id && (
              <form
                className="settings-form source-edit-form"
                onSubmit={updateSource}
              >
                <h3>
                  {locale === "es"
                    ? "Actualizar fuente oficial"
                    : "Update official source"}
                </h3>
                <div className="form-grid">
                  <label>
                    {locale === "es" ? "Jurisdicción" : "Jurisdiction"}
                    <input
                      required
                      value={editingSource.jurisdiction}
                      onChange={(e) =>
                        setEditingSource({
                          ...editingSource,
                          jurisdiction: e.currentTarget.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    {locale === "es" ? "Agencia" : "Agency"}
                    <input
                      required
                      value={editingSource.agency}
                      onChange={(e) =>
                        setEditingSource({
                          ...editingSource,
                          agency: e.currentTarget.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    {locale === "es" ? "Tema" : "Topic"}
                    <input
                      required
                      value={editingSource.topic}
                      onChange={(e) =>
                        setEditingSource({
                          ...editingSource,
                          topic: e.currentTarget.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    {locale === "es" ? "Título en español" : "Spanish title"}
                    <input
                      required
                      value={editingSource.titleEs}
                      onChange={(e) =>
                        setEditingSource({
                          ...editingSource,
                          titleEs: e.currentTarget.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    {locale === "es" ? "Título en inglés" : "English title"}
                    <input
                      required
                      value={editingSource.titleEn}
                      onChange={(e) =>
                        setEditingSource({
                          ...editingSource,
                          titleEn: e.currentTarget.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    {locale === "es"
                      ? "URL oficial HTTPS"
                      : "Official HTTPS URL"}
                    <input
                      required
                      type="url"
                      pattern="https://.*"
                      value={editingSource.officialUrl}
                      onChange={(e) =>
                        setEditingSource({
                          ...editingSource,
                          officialUrl: e.currentTarget.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    {locale === "es" ? "Verificada el" : "Verified on"}
                    <input
                      required
                      type="date"
                      value={editingSource.lastVerifiedOn}
                      onInput={(e) =>
                        setEditingSource({
                          ...editingSource,
                          lastVerifiedOn: e.currentTarget.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    {locale === "es"
                      ? "Razón de la modificación"
                      : "Modification reason"}
                    <input
                      required
                      minLength={3}
                      value={editingSource.reason}
                      onChange={(e) =>
                        setEditingSource({
                          ...editingSource,
                          reason: e.currentTarget.value,
                        })
                      }
                    />
                  </label>
                </div>
                <div className="form-actions">
                  <button className="primary" disabled={busy}>
                    {locale === "es" ? "Guardar actualización" : "Save update"}
                  </button>
                  <button type="button" onClick={() => setEditingSource(null)}>
                    {locale === "es" ? "Cancelar" : "Cancel"}
                  </button>
                </div>
              </form>
            )}
          </article>
        ))}
      </div>
      <h3>
        {locale === "es"
          ? "Documentos fuente retenidos"
          : "Retained source documents"}
      </h3>
      <div className="run-list">
        {documents.map((item) => (
          <article key={item.id}>
            <div>
              <strong>
                {item.title} · {item.version}
              </strong>
              <small>
                {item.documentKind === "private_snapshot"
                  ? `R2 · SHA-256 ${item.sha256}`
                  : item.externalUrl}{" "}
                · {formatDate(item.verifiedOn, locale)}
              </small>
            </div>
            <a
              className="official-source-link"
              href={`${API}/v1/platform/statutory-documents/${item.id}`}
              target="_blank"
              rel="noreferrer"
            >
              {locale === "es" ? "Abrir documento" : "Open document"}
            </a>
          </article>
        ))}
      </div>
      <h3>{locale === "es" ? "Versiones de reglas" : "Rule versions"}</h3>
      <div className="run-list">
        {rules.map((item) => (
          <article key={item.id}>
            <div>
              <strong>
                {item.ruleKey} · {item.version}
              </strong>
              <small>
                {item.jurisdiction} ·{" "}
                {formatDateRange(
                  item.effectiveFrom,
                  item.effectiveTo,
                  locale,
                  locale === "es" ? "vigente" : "current",
                )}{" "}
                · {locale === "es" ? "documento" : "document"}:{" "}
                {documents.find(
                  (documentItem) => documentItem.id === item.sourceDocumentId,
                )?.title ?? item.sourceDocumentId}
              </small>
            </div>
            <span>{optionLabel(item.status, locale)}</span>
            {item.status === "draft" && (
              <button
                type="button"
                onClick={() => setActivation({ ruleId: item.id, reason: "" })}
              >
                {locale === "es" ? "Activar" : "Activate"}
              </button>
            )}
          </article>
        ))}
      </div>
      {activation.ruleId && (
        <form
          className="inline-action-form"
          onSubmit={(event) => {
            event.preventDefault();
            void post(
              `/v1/platform/statutory-rules/${activation.ruleId}/activate`,
              { reason: activation.reason },
              locale === "es"
                ? "Versión activada y retenida."
                : "Version activated and retained.",
            ).then((ok) => {
              if (ok) setActivation({ ruleId: "", reason: "" });
            });
          }}
        >
          <label>
            {locale === "es" ? "Razón de activación" : "Activation reason"}
            <input
              required
              minLength={3}
              value={activation.reason}
              onChange={(e) =>
                setActivation({ ...activation, reason: e.currentTarget.value })
              }
            />
          </label>
          <button className="primary" disabled={busy}>
            {locale === "es" ? "Confirmar activación" : "Confirm activation"}
          </button>
          <button
            type="button"
            onClick={() => setActivation({ ruleId: "", reason: "" })}
          >
            {locale === "es" ? "Cancelar" : "Cancel"}
          </button>
        </form>
      )}
      <details>
        <summary>
          {locale === "es"
            ? `Historial inmutable (${events.length})`
            : `Immutable history (${events.length})`}
        </summary>
        <div className="run-list">
          {events.map((item) => (
            <article key={item.id}>
              <div>
                <strong>
                  {optionLabel(item.action, locale)} · {item.resourceType}
                </strong>
                <small>
                  {item.reason} · {item.developerEmail}
                </small>
              </div>
              <span>{formatDateTime(item.occurredAt, locale)}</span>
            </article>
          ))}
        </div>
      </details>
    </section>
  );
}

type BankAddressForm = {
  line1: string;
  line2: string | null;
  city: string;
  state: string;
  postalCode: string;
  country: string;
};
type BankAccountView = {
  id: string;
  accountName: string;
  bankName: string;
  bankAddress: BankAddressForm | null;
  accountHolderName: string;
  accountHolderAddress: BankAddressForm;
  routingLastFour: string;
  accountLastFour: string;
  nextCheckNumber: number;
  status: string;
  signatureFileName: string | null;
  signatureSha256: string | null;
  signatureUpdatedAt: string | null;
};
const blankBankAddress: BankAddressForm = {
  line1: "",
  line2: null,
  city: "",
  state: "PR",
  postalCode: "",
  country: "US",
};
function BankAccountConfiguration({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const [current, setCurrent] = useState<BankAccountView | null>(null),
    [form, setForm] = useState({
      accountName: "",
      bankName: "",
      bankAddress: { ...blankBankAddress } as BankAddressForm,
      accountHolderName: tenant.legalName,
      accountHolderAddress: { ...blankBankAddress } as BankAddressForm,
      routingNumber: "",
      accountNumber: "",
      nextCheckNumber: 5000,
      changeReason: "",
    }),
    [busy, setBusy] = useState(true),
    [message, setMessage] = useState(""),
    [signatureFile, setSignatureFile] = useState<File | null>(null),
    [signatureReason, setSignatureReason] = useState(""),
    [signatureBusy, setSignatureBusy] = useState(false);
  useEffect(() => {
    void api(`/v1/tenants/${tenant.id}/bank-account`).then(async (response) => {
      if (response.ok) {
        const body = (await response.json()) as {
          account: BankAccountView | null;
        };
        setCurrent(body.account);
        if (body.account)
          setForm((value) => ({
            ...value,
            accountName: body.account!.accountName,
            bankName: body.account!.bankName,
            bankAddress: body.account!.bankAddress ?? blankBankAddress,
            accountHolderName: body.account!.accountHolderName,
            accountHolderAddress: body.account!.accountHolderAddress,
            nextCheckNumber: body.account!.nextCheckNumber,
            changeReason: "",
          }));
      }
      setBusy(false);
    });
  }, [tenant.id]);
  const addressField = (
    kind: "bankAddress" | "accountHolderAddress",
    key: keyof BankAddressForm,
    value: string,
  ) =>
    setForm((old) => ({
      ...old,
      [kind]: { ...old[kind], [key]: key === "line2" && !value ? null : value },
    }));
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    const response = await api(`/v1/tenants/${tenant.id}/bank-account`, {
      method: "PUT",
      body: JSON.stringify({
        ...form,
        routingNumber: form.routingNumber || null,
        accountNumber: form.accountNumber || null,
      }),
    });
    if (response.ok) {
      const body = (await response.json()) as { account: BankAccountView };
      setCurrent({
        ...body.account,
        bankAddress: form.bankAddress,
        accountHolderAddress: form.accountHolderAddress,
        status: "active",
      });
      setForm((old) => ({
        ...old,
        routingNumber: "",
        accountNumber: "",
        changeReason: "",
      }));
      setMessage(
        locale === "es"
          ? "Cuenta bancaria guardada y auditada."
          : "Bank account saved and audited.",
      );
    } else
      setMessage(
        locale === "es"
          ? "No se pudo guardar. Complete todos los campos requeridos."
          : "Unable to save. Complete all required fields.",
      );
    setBusy(false);
  };
  const uploadSignature = async (event: FormEvent) => {
    event.preventDefault();
    if (!signatureFile || signatureReason.trim().length < 3) return;
    setSignatureBusy(true);
    setMessage("");
    const data = new FormData();
    data.set("signature", signatureFile);
    data.set("reason", signatureReason);
    const response = await api(
      `/v1/tenants/${tenant.id}/bank-account/signature`,
      { method: "POST", body: data },
    );
    if (response.ok) {
      const body = (await response.json()) as {
        signature: { fileName: string; sha256: string; updatedAt: string };
      };
      setCurrent((old) =>
        old
          ? {
              ...old,
              signatureFileName: body.signature.fileName,
              signatureSha256: body.signature.sha256,
              signatureUpdatedAt: body.signature.updatedAt,
            }
          : old,
      );
      setSignatureFile(null);
      setSignatureReason("");
      setMessage(
        locale === "es"
          ? "Firma guardada. Regenere los lotes existentes desde Chequera para aplicarla."
          : "Signature saved. Regenerate existing batches from Checkbook to apply it.",
      );
    } else
      setMessage(
        locale === "es"
          ? "No se pudo guardar la firma. Use una imagen PNG o JPEG menor de 2 MB."
          : "Unable to save the signature. Use a PNG or JPEG image smaller than 2 MB.",
      );
    setSignatureBusy(false);
  };
  const addressInputs = (kind: "bankAddress" | "accountHolderAddress") =>
    (
      [
        "line1",
        "line2",
        "city",
        "state",
        "postalCode",
        "country",
      ] as (keyof BankAddressForm)[]
    ).map((key) => (
      <label key={`${kind}-${key}`}>
        {
          {
            line1: locale === "es" ? "Dirección" : "Address",
            line2:
              locale === "es"
                ? "Dirección 2 (opcional)"
                : "Address 2 (optional)",
            city: locale === "es" ? "Municipio" : "City",
            state: locale === "es" ? "Estado/territorio" : "State/territory",
            postalCode: locale === "es" ? "Código postal" : "Postal code",
            country: locale === "es" ? "País" : "Country",
          }[key]
        }
        <input
          required={key !== "line2"}
          value={form[kind][key] ?? ""}
          onChange={(e) => addressField(kind, key, e.currentTarget.value)}
        />
      </label>
    ));
  if (busy) return <LoaderCircle className="spin" />;
  return (
    <section className="data-section">
      <h2>
        {locale === "es"
          ? "Configuración Cuenta Bancaria"
          : "Bank Account Setup"}
      </h2>
      <p>
        {locale === "es"
          ? "Configure la cuenta usada para imprimir cheques. Los números completos se cifran; después de guardar solo se muestran los últimos cuatro dígitos."
          : "Configure the account used to print checks. Full numbers are encrypted; only the last four digits are shown after saving."}
      </p>
      {current && (
        <p className="success-note">
          <Check />
          {current.bankName} · ruta ••••{current.routingLastFour} · cuenta ••••
          {current.accountLastFour} · próximo cheque {current.nextCheckNumber}
        </p>
      )}
      <form className="settings-form" onSubmit={submit}>
        <div className="form-grid">
          <label>
            {locale === "es" ? "Nombre de la cuenta" : "Account name"}
            <input
              required
              value={form.accountName}
              onChange={(e) =>
                setForm({ ...form, accountName: e.currentTarget.value })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Banco" : "Bank"}
            <input
              required
              value={form.bankName}
              onChange={(e) =>
                setForm({ ...form, bankName: e.currentTarget.value })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Titular de la cuenta" : "Account holder"}
            <input
              required
              value={form.accountHolderName}
              onChange={(e) =>
                setForm({ ...form, accountHolderName: e.currentTarget.value })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Próximo número de cheque" : "Next check number"}
            <input
              required
              type="number"
              min="1"
              value={form.nextCheckNumber}
              onChange={(e) =>
                setForm({
                  ...form,
                  nextCheckNumber: Number(e.currentTarget.value),
                })
              }
            />
          </label>
          <label>
            {locale === "es"
              ? `Número de ruta${current ? ` · actual ••••${current.routingLastFour}` : ""}`
              : "Routing number"}
            <input
              required={!current}
              inputMode="numeric"
              pattern="\d{9}"
              value={form.routingNumber}
              onChange={(e) =>
                setForm({
                  ...form,
                  routingNumber: e.currentTarget.value.replace(/\D/g, ""),
                })
              }
              placeholder={
                current
                  ? locale === "es"
                    ? "Escriba para reemplazar"
                    : "Enter to replace"
                  : "000000000"
              }
            />
          </label>
          <label>
            {locale === "es"
              ? `Número de cuenta${current ? ` · actual ••••${current.accountLastFour}` : ""}`
              : "Account number"}
            <input
              required={!current}
              inputMode="numeric"
              value={form.accountNumber}
              onChange={(e) =>
                setForm({
                  ...form,
                  accountNumber: e.currentTarget.value.replace(/\D/g, ""),
                })
              }
              placeholder={
                current
                  ? locale === "es"
                    ? "Escriba para reemplazar"
                    : "Enter to replace"
                  : "0000000000"
              }
            />
          </label>
        </div>
        <h3>{locale === "es" ? "Dirección del banco" : "Bank address"}</h3>
        <div className="form-grid">{addressInputs("bankAddress")}</div>
        <h3>
          {locale === "es"
            ? "Dirección impresa del titular"
            : "Printed account-holder address"}
        </h3>
        <div className="form-grid">{addressInputs("accountHolderAddress")}</div>
        <AuditControl locale={locale} value={form.changeReason} onChange={value=>setForm({...form,changeReason:value})}/>
        <div className="form-actions">
          <button className="primary" disabled={busy}>
            {locale === "es" ? "Guardar cuenta bancaria" : "Save bank account"}
          </button>
        </div>
      </form>
      <h3>
        {locale === "es"
          ? "Firma digital para cheques"
          : "Digital check signature"}
      </h3>
      {current?.signatureFileName && (
        <p className="success-note">
          <Check />
          {locale === "es"
            ? `Firma activa: ${current.signatureFileName}`
            : `Active signature: ${current.signatureFileName}`}
        </p>
      )}
      <form className="settings-form" onSubmit={uploadSignature}>
        <label>
          {locale === "es"
            ? "Imagen de firma (PNG o JPEG, máximo 2 MB)"
            : "Signature image (PNG or JPEG, 2 MB maximum)"}
          <input
            required
            type="file"
            accept="image/png,image/jpeg"
            onChange={(e) =>
              setSignatureFile(e.currentTarget.files?.[0] ?? null)
            }
          />
        </label>
        <AuditControl locale={locale} mode="required_reason" value={signatureReason} onChange={setSignatureReason}/>
        <div className="form-actions">
          <button
            className="primary"
            disabled={!current || signatureBusy || !signatureFile}
          >
            {signatureBusy ? (
              <LoaderCircle className="spin" />
            ) : locale === "es" ? (
              "Guardar firma"
            ) : (
              "Save signature"
            )}
          </button>
        </div>
      </form>
      {message && (
        <p
          className={
            message.includes("No se") || message.includes("Unable")
              ? "form-error"
              : "success-note"
          }
        >
          {message}
        </p>
      )}
    </section>
  );
}
type IssuedCheck = {
  id: string;
  batchId: string;
  payrollRunId: string;
  workerId: string;
  checkNumber: number;
  payeeName: string;
  amountCents: number;
  checkDate: string;
  memo: string;
  status: string;
  createdAt: string;
};
function CompanyCheckbook({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const [checks, setChecks] = useState<IssuedCheck[]>([]),
    [busy, setBusy] = useState(true),
    [regenerating, setRegenerating] = useState<string | null>(null),
    [message, setMessage] = useState(""),
    money = (cents: number) =>
      new Intl.NumberFormat(locale === "es" ? "es-PR" : "en-US", {
        style: "currency",
        currency: "USD",
      }).format(cents / 100);
  useEffect(() => {
    void api(`/v1/tenants/${tenant.id}/checks`).then(async (response) => {
      if (response.ok)
        setChecks(
          ((await response.json()) as { checks: IssuedCheck[] }).checks,
        );
      setBusy(false);
    });
  }, [tenant.id]);
  const openPdf = async (path: string) => {
    const response = await api(path);
    if (!response.ok) return;
    const url = URL.createObjectURL(await response.blob());
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };
  const batches = [
    ...new Map(checks.map((check) => [check.batchId, check])).values(),
  ];
  const regenerate = async (batchId: string) => {
    setRegenerating(batchId);
    setMessage("");
    const response = await api(
      `/v1/tenants/${tenant.id}/check-batches/${batchId}/regenerate`,
      { method: "POST" },
    );
    if (response.ok) {
      const body = (await response.json()) as {
        checkCount: number;
        signatureApplied: boolean;
      };
      setMessage(
        locale === "es"
          ? `Se regeneraron ${body.checkCount} cheques${body.signatureApplied ? " con la firma activa" : ""}.`
          : `Regenerated ${body.checkCount} checks${body.signatureApplied ? " with the active signature" : ""}.`,
      );
    } else
      setMessage(
        locale === "es"
          ? "No se pudo regenerar el lote."
          : "Unable to regenerate the batch.",
      );
    setRegenerating(null);
  };
  if (busy) return <LoaderCircle className="spin" />;
  return (
    <section className="data-section">
      <h2>{locale === "es" ? "Chequera" : "Checkbook"}</h2>
      <p>
        {locale === "es"
          ? "Registro individual de todos los cheques generados por Jornada. Puede abrir, imprimir o regenerar cada lote con la firma activa."
          : "Individual register of every check generated by Jornada. Open, print, or regenerate each batch with the active signature."}
      </p>
      {batches.length > 0 && (
        <div className="run-actions">
          {batches.map((batch) => (
            <button
              key={batch.batchId}
              disabled={regenerating !== null}
              onClick={() => void regenerate(batch.batchId)}
            >
              {regenerating === batch.batchId ? (
                <LoaderCircle className="spin" />
              ) : locale === "es" ? (
                `Regenerar lote de ${formatDate(batch.checkDate, locale)}`
              ) : (
                `Regenerate ${formatDate(batch.checkDate, locale)} batch`
              )}
            </button>
          ))}
        </div>
      )}
      {message && (
        <p
          className={
            message.includes("No se") || message.includes("Unable")
              ? "form-error"
              : "success-note"
          }
        >
          {message}
        </p>
      )}
      {!checks.length ? (
        <p>
          {locale === "es"
            ? "Todavía no se han generado cheques."
            : "No checks have been generated yet."}
        </p>
      ) : (
        <div className="responsive-table">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>{locale === "es" ? "Fecha" : "Date"}</th>
                <th>{locale === "es" ? "Beneficiario" : "Payee"}</th>
                <th>{locale === "es" ? "Importe" : "Amount"}</th>
                <th>{locale === "es" ? "Estado" : "Status"}</th>
                <th>{locale === "es" ? "Acciones" : "Actions"}</th>
              </tr>
            </thead>
            <tbody>
              {checks.map((item) => (
                <tr key={item.id}>
                  <td>{item.checkNumber}</td>
                  <td>{formatDate(item.checkDate, locale)}</td>
                  <td>
                    <strong>{item.payeeName}</strong>
                    <small>{item.memo}</small>
                  </td>
                  <td>{money(item.amountCents)}</td>
                  <td>
                    {item.status === "issued"
                      ? locale === "es"
                        ? "Emitido"
                        : "Issued"
                      : optionLabel(item.status, locale)}
                  </td>
                  <td>
                    <div className="run-actions">
                      <button
                        onClick={() =>
                          void openPdf(
                            `/v1/tenants/${tenant.id}/checks/${item.id}/pdf`,
                          )
                        }
                      >
                        {locale === "es" ? "Imprimir cheque" : "Print check"}
                      </button>
                      <button
                        onClick={() =>
                          void openPdf(
                            `/v1/tenants/${tenant.id}/check-batches/${item.batchId}/pdf`,
                          )
                        }
                      >
                        {locale === "es" ? "Imprimir lote" : "Print batch"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

type RestaurantTipMethod =
  | "equal_by_shared_shift"
  | "individual"
  | "payroll_period_hours";
function RestaurantConfiguration({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const [feePercent, setFeePercent] = useState("3.50"),
    [method, setMethod] = useState<RestaurantTipMethod>("individual"),
    [reason, setReason] = useState(""),
    [busy, setBusy] = useState(true),
    [message, setMessage] = useState("");
  useEffect(() => {
    void api(`/v1/tenants/${tenant.id}/restaurant-tip-settings`).then(
      async (response) => {
        if (response.ok) {
          const body = (await response.json()) as {
            settings: {
              creditCardFeeBps: number;
              distributionMethod: RestaurantTipMethod;
            };
          };
          setFeePercent((body.settings.creditCardFeeBps / 100).toFixed(2));
          setMethod(body.settings.distributionMethod);
        } else
          setMessage(
            locale === "es"
              ? "No se pudo cargar la configuración."
              : "Unable to load settings.",
          );
        setBusy(false);
      },
    );
  }, [tenant.id, locale]);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const fee = Number(feePercent);
    if (!Number.isFinite(fee) || fee < 0 || fee > 100) return;
    setBusy(true);
    setMessage("");
    const response = await api(
      `/v1/tenants/${tenant.id}/restaurant-tip-settings`,
      {
        method: "PUT",
        body: JSON.stringify({
          creditCardFeeBps: Math.round(fee * 100),
          distributionMethod: method,
          changeReason: reason,
        }),
      },
    );
    if (response.ok) {
      setReason("");
      setMessage(
        locale === "es"
          ? "Configuración de restaurante guardada y auditada."
          : "Restaurant settings saved and audited.",
      );
    } else
      setMessage(
        locale === "es"
          ? "No se pudo guardar la configuración."
          : "Unable to save restaurant settings.",
      );
    setBusy(false);
  };
  if (busy) return <LoaderCircle className="spin" />;
  return (
    <section className="data-section">
      <h2>
        {locale === "es"
          ? "Configuración de restaurante"
          : "Restaurant configuration"}
      </h2>
      <p>
        {locale === "es"
          ? "Defina la deducción por procesamiento de tarjeta y cómo se asignarán las propinas netas al personal elegible."
          : "Define the card-processing deduction and how net tips will be allocated to eligible personnel."}
      </p>
      <form className="settings-form" onSubmit={submit}>
        <div className="form-grid">
          <label>
            {locale === "es"
              ? "Comisión de tarjeta de crédito del restaurante (%)"
              : "Restaurant credit-card fee (%)"}
            <input
              required
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={feePercent}
              onChange={(e) => setFeePercent(e.currentTarget.value)}
            />
            <small>
              {locale === "es"
                ? "Valor predeterminado: 3.5%. Se deduce antes de asignar la propina neta."
                : "Default: 3.5%. Deducted before the net tip is assigned."}
            </small>
          </label>
          <label>
            {locale === "es"
              ? "Método de distribución de propinas"
              : "Tip distribution method"}
            <select
              value={method}
              onChange={(e) =>
                setMethod(e.currentTarget.value as RestaurantTipMethod)
              }
            >
              <option value="individual">
                {locale === "es"
                  ? "Asignar a quien recibió la propina durante su turno"
                  : "Assign to the person who received it during the shift"}
              </option>
              <option value="equal_by_shared_shift">
                {locale === "es"
                  ? "Dividir por igual entre quienes trabajaron juntos en el turno"
                  : "Split equally among those working the shared shift"}
              </option>
              <option value="payroll_period_hours">
                {locale === "es"
                  ? "Dividir el total del período según las horas trabajadas"
                  : "Split the period total according to hours worked"}
              </option>
            </select>
          </label>
        </div>
        <p className="info-note">
          {locale === "es"
            ? "En el comprobante de pago solamente aparecerá la propina neta asignada. No se mostrará la comisión de tarjeta."
            : "Only the assigned net tip will appear on the pay statement. The card fee will not be shown."}
        </p>
        <AuditControl locale={locale} value={reason} onChange={setReason}/>
        <div className="form-actions">
          <button className="primary" disabled={busy}>
            {locale === "es" ? "Guardar configuración" : "Save settings"}
          </button>
        </div>
      </form>
      {message && (
        <p
          className={
            message.includes("No se") || message.includes("Unable")
              ? "form-error"
              : "success-note"
          }
        >
          {message}
        </p>
      )}
    </section>
  );
}
function Configuration({
  locale,
  tenant,
  roleKey,
}: {
  locale: Locale;
  tenant: Tenant;
  roleKey: string;
}) {
  const t = text[locale];
  type CompanyArea =
    | "profile"
    | "banking"
    | "checkbook"
    | "restaurant"
    | "payroll_tax"
    | "time_attendance"
    | "organization"
    | "equipment"
    | "security"
    | "platform";
  const [companyArea, setCompanyArea] = useState<CompanyArea>("profile");
  const [taxArea, setTaxArea] = useState<"forecast" | "cfse" | "deadlines">(
    "forecast",
  );
  const [profile, setProfile] = useState(blankProfile);
  const [ein, setEin] = useState("");
  const [changeReason, setChangeReason] = useState("");
  const [depositScheduleEvents, setDepositScheduleEvents] = useState<
    DepositScheduleEvent[]
  >([]);
  const [workweekDefinitions, setWorkweekDefinitions] = useState<
    WorkweekDefinition[]
  >([]);
  const [busy, setBusy] = useState(true);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(false);
  useEffect(() => {
    void api(`/v1/tenants/${tenant.id}/configuration`).then(
      async (response) => {
        if (response.ok) {
          const body = (await response.json()) as {
            profile: CompanyProfile | null;
            depositScheduleEvents: DepositScheduleEvent[];
            workweekDefinitions: WorkweekDefinition[];
          };
          if (body.profile) setProfile(body.profile);
          setDepositScheduleEvents(body.depositScheduleEvents ?? []);
          setWorkweekDefinitions(body.workweekDefinitions ?? []);
        }
        setBusy(false);
      },
    );
  }, [tenant.id]);
  const field = (key: keyof CompanyProfile, value: string | number) =>
    setProfile((current) => ({
      ...current,
      [key]: value === "" ? null : value,
    }));
  const addressField = (
    kind: "physicalAddress" | "mailingAddress",
    key: keyof CompanyAddress,
    value: string,
  ) =>
    setProfile((current) => ({
      ...current,
      [kind]: {
        ...(current[kind] ?? {
          line1: "",
          line2: null,
          city: "",
          state: "PR",
          postalCode: "",
          country: "US",
        }),
        [key]: value === "" && key === "line2" ? null : value,
      },
    }));
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setSaved(false);
    setError(false);
    const response = await api(`/v1/tenants/${tenant.id}/configuration`, {
      method: "PUT",
      body: JSON.stringify({
        ...profile,
        changeReason,
        ein: ein || null,
        maskedEin: undefined,
        hasEin: undefined,
      }),
    });
    if (response.ok) {
      const body = (await response.json()) as { profile: CompanyProfile };
      setProfile(body.profile);
      setEin("");
      setChangeReason("");
      setSaved(true);
    } else setError(true);
    setBusy(false);
  };
  return (
    <div className="content">
      <p className="kicker">{tenant.displayName}</p>
      <h1>{t.settings}</h1>
      <p className="lead">
        {locale === "es"
          ? "Configure la información patronal y el calendario de nómina. Los cambios se guardan en Cloudflare y quedan auditados."
          : "Configure employer information and the payroll calendar. Changes are stored in Cloudflare and audited."}
      </p>
      <nav
        className="settings-section-nav"
        aria-label={
          locale === "es" ? "Secciones de configuración" : "Settings sections"
        }
      >
        {(
          [
            { key: "profile", es: "Perfil de compañía", en: "Company profile" },
            {
              key: "banking",
              es: "Configuración Cuenta Bancaria",
              en: "Bank Account Setup",
            },
            { key: "checkbook", es: "Chequera", en: "Checkbook" },
            {
              key: "restaurant",
              es: "Configuración de restaurante",
              en: "Restaurant configuration",
            },
            {
              key: "payroll_tax",
              es: "Contribuciones patronales",
              en: "Employer taxes",
            },
            {
              key: "time_attendance",
              es: "Tiempo y asistencia",
              en: "Time & attendance",
            },
            { key: "organization", es: "Organización", en: "Organization" },
            { key: "equipment", es: "NFC", en: "NFC" },
            ...(["owner", "platform_administrator"].includes(roleKey)
              ? [
                  {
                    key: "security",
                    es: "Acceso y seguridad",
                    en: "Access & security",
                  },
                ]
              : []),
            ...(roleKey === "platform_administrator"
              ? [
                  {
                    key: "platform",
                    es: "Controles de plataforma",
                    en: "Platform controls",
                  },
                ]
              : []),
          ] as { key: CompanyArea; es: string; en: string }[]
        ).map((item) => (
          <button
            type="button"
            key={item.key}
            className={companyArea === item.key ? "active" : ""}
            aria-current={companyArea === item.key ? "page" : undefined}
            onClick={() => setCompanyArea(item.key)}
          >
            {locale === "es" ? item.es : item.en}
          </button>
        ))}
      </nav>
      {companyArea === "banking" && (
        <BankAccountConfiguration locale={locale} tenant={tenant} />
      )}
      {companyArea === "checkbook" && (
        <CompanyCheckbook locale={locale} tenant={tenant} />
      )}
      {companyArea === "restaurant" && (
        <RestaurantConfiguration locale={locale} tenant={tenant} />
      )}
      {companyArea === "profile" &&
        (busy ? (
          <LoaderCircle className="spin" />
        ) : (
          <form className="settings-form" onSubmit={submit}>
            <h2>
              {locale === "es"
                ? "Información de la compañía"
                : "Company information"}
            </h2>
            <div className="form-grid">
              <label>
                {locale === "es" ? "Nombre legal" : "Legal name"}
                <input
                  required
                  value={profile.legalName}
                  onChange={(e) => field("legalName", e.currentTarget.value)}
                />
              </label>
              <label>
                {locale === "es" ? "Idioma predeterminado" : "Default language"}
                <select
                  value={profile.defaultLocale}
                  onChange={(e) =>
                    field("defaultLocale", e.currentTarget.value)
                  }
                >
                  <option value="es-PR">Español (Puerto Rico)</option>
                  <option value="en-PR">English (Puerto Rico)</option>
                </select>
              </label>
              <label>
                {locale === "es" ? "DBA (opcional)" : "DBA (optional)"}
                <input
                  value={profile.dbaName ?? ""}
                  onChange={(e) => field("dbaName", e.currentTarget.value)}
                />
              </label>
              <label>
                {locale === "es" ? "EIN (opcional durante configuración inicial)" : "EIN (optional during initial setup)"} {profile.maskedEin && <small>{profile.maskedEin}</small>}
                <input
                  value={ein}
                  onChange={(e) => setEin(e.currentTarget.value)}
                  placeholder={
                    profile.hasEin
                      ? locale === "es"
                        ? "Escriba solo para reemplazar"
                        : "Enter only to replace"
                      : "12-3456789"
                  }
                  pattern="\d{2}-?\d{7}"
                />
                <small>{locale === "es" ? "Se requiere antes de radicaciones y pagos contributivos federales." : "Required before federal tax filings and payments."}</small>
              </label>
              <label>
                {locale === "es" ? "Cuenta patronal PR (opcional durante configuración inicial)" : "PR employer account (optional during initial setup)"}
                <input
                  value={profile.prEmployerAccount ?? ""}
                  onChange={(e) =>
                    field("prEmployerAccount", e.currentTarget.value)
                  }
                />
                <small>{locale === "es" ? "Se requiere cuando corresponda reportar o pagar contribuciones patronales de Puerto Rico." : "Required when Puerto Rico employer taxes must be reported or paid."}</small>
              </label>
              <label>
                {locale === "es" ? "Referencia SURI (opcional durante configuración inicial)" : "SURI reference (optional during initial setup)"}
                <input
                  value={profile.suriAccountReference ?? ""}
                  onChange={(e) =>
                    field("suriAccountReference", e.currentTarget.value)
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Cuenta DTRH (opcional durante configuración inicial)" : "DTRH account (optional during initial setup)"}
                <input
                  value={profile.dtrhEmployerAccount ?? ""}
                  onChange={(e) =>
                    field("dtrhEmployerAccount", e.currentTarget.value)
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Póliza CFSE (opcional durante configuración inicial)" : "CFSE policy (optional during initial setup)"}
                <input
                  value={profile.cfsePolicyNumber ?? ""}
                  onChange={(e) =>
                    field("cfsePolicyNumber", e.currentTarget.value)
                  }
                />
              </label>
              <label>
                {locale === "es"
                  ? "Calendario de depósitos de retención PR"
                  : "PR withholding deposit schedule"}
                <select
                  value={profile.prWithholdingDepositSchedule ?? ""}
                  onChange={(e) =>
                    field("prWithholdingDepositSchedule", e.currentTarget.value)
                  }
                >
                  <option value="">—</option>
                  <option value="quarterly_under_2500">
                    {locale === "es"
                      ? "Trimestral — retención menor de $2,500"
                      : "Quarterly — withholding under $2,500"}
                  </option>
                  <option value="monthly">
                    {locale === "es" ? "Mensual" : "Monthly"}
                  </option>
                  <option value="semiweekly">
                    {locale === "es" ? "Bisemanal" : "Semiweekly"}
                  </option>
                </select>
                <small>
                  {locale === "es"
                    ? "Use la clasificación de Hacienda/SURI; no depende de la frecuencia de nómina."
                    : "Use the Hacienda/SURI classification; this is not payroll frequency."}
                </small>
              </label>
              <label>
                {locale === "es"
                  ? "Calendario de depósitos federales"
                  : "Federal employment-tax deposit schedule"}
                <select
                  value={profile.federalEmploymentDepositSchedule ?? ""}
                  onChange={(e) =>
                    field(
                      "federalEmploymentDepositSchedule",
                      e.currentTarget.value,
                    )
                  }
                >
                  <option value="">—</option>
                  <option value="monthly">
                    {locale === "es" ? "Mensual" : "Monthly"}
                  </option>
                  <option value="semiweekly">
                    {locale === "es" ? "Bisemanal" : "Semiweekly"}
                  </option>
                </select>
                <small>
                  {locale === "es"
                    ? "Use la clasificación determinada por el período retrospectivo del IRS."
                    : "Use the IRS lookback-period classification."}
                </small>
              </label>
              <label>
                {locale === "es" ? "Persona contacto (opcional)" : "Contact person (optional)"}
                <input
                  value={profile.contactName ?? ""}
                  onChange={(e) => field("contactName", e.currentTarget.value)}
                />
              </label>
              <label>
                {locale === "es" ? "Teléfono (opcional)" : "Phone (optional)"}
                <input
                  value={profile.phoneE164 ?? ""}
                  onChange={(e) => field("phoneE164", e.currentTarget.value)}
                  placeholder="+17875551234"
                />
              </label>
              <label>
                {locale === "es" ? "Email (opcional)" : "Email (optional)"}
                <input
                  type="email"
                  value={profile.email ?? ""}
                  onChange={(e) => field("email", e.currentTarget.value)}
                />
              </label>
              <label>
                {locale === "es" ? "Frecuencia de nómina" : "Payroll frequency"}
                <select
                  value={profile.payrollFrequency}
                  onChange={(e) =>
                    field("payrollFrequency", e.currentTarget.value)
                  }
                >
                  <option value="weekly">
                    {optionLabel("weekly", locale)}
                  </option>
                  <option value="biweekly">
                    {optionLabel("biweekly", locale)}
                  </option>
                  <option value="semimonthly">
                    {optionLabel("semimonthly", locale)}
                  </option>
                  <option value="monthly">
                    {optionLabel("monthly", locale)}
                  </option>
                  <option value="custom">
                    {optionLabel("custom", locale)}
                  </option>
                </select>
              </label>
              <label>
                {locale === "es"
                  ? "Día inicial de semana"
                  : "Workweek start day"}
                <select
                  value={profile.workweekStartsOn}
                  onChange={(e) =>
                    field("workweekStartsOn", Number(e.currentTarget.value))
                  }
                >
                  {(locale === "es"
                    ? [
                        "domingo",
                        "lunes",
                        "martes",
                        "miércoles",
                        "jueves",
                        "viernes",
                        "sábado",
                      ]
                    : [
                        "Sunday",
                        "Monday",
                        "Tuesday",
                        "Wednesday",
                        "Thursday",
                        "Friday",
                        "Saturday",
                      ]
                  ).map((day, index) => (
                    <option value={index} key={day}>
                      {day}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {locale === "es" ? "Hora inicial" : "Start time"}
                <input
                  type="time"
                  value={profile.workweekStartsAt}
                  onChange={(e) =>
                    field("workweekStartsAt", e.currentTarget.value)
                  }
                />
              </label>
              <label>
                {locale === "es" ? "Vigente desde" : "Effective from"}
                <input
                  required
                  type="date"
                  value={profile.workweekEffectiveFrom}
                  onInput={(e) =>
                    field("workweekEffectiveFrom", e.currentTarget.value)
                  }
                />
                <small>
                  {locale === "es"
                    ? "Se crea una revisión inmutable solo si cambia el día o la hora."
                    : "An immutable revision is created only when the day or time changes."}
                </small>
              </label>
            </div>
            <h2>{locale === "es" ? "Dirección física" : "Physical address"}</h2>
            <div className="form-grid">
              {(
                [
                  "line1",
                  "line2",
                  "city",
                  "state",
                  "postalCode",
                  "country",
                ] as (keyof CompanyAddress)[]
              ).map((key) => (
                <label key={`physical-${key}`}>
                  {
                    (
                      {
                        line1: { es: "Dirección", en: "Address line 1" },
                        line2: {
                          es: "Dirección adicional",
                          en: "Address line 2",
                        },
                        city: { es: "Municipio", en: "City" },
                        state: {
                          es: "Estado/territorio",
                          en: "State/territory",
                        },
                        postalCode: { es: "Código postal", en: "Postal code" },
                        country: { es: "País", en: "Country" },
                      } as Record<
                        keyof CompanyAddress,
                        { es: string; en: string }
                      >
                    )[key][locale]
                  }
                  <input
                    required={
                      profile.physicalAddress !== null && key !== "line2"
                    }
                    value={
                      profile.physicalAddress?.[key] ??
                      (key === "state" ? "PR" : key === "country" ? "US" : "")
                    }
                    onChange={(e) =>
                      addressField(
                        "physicalAddress",
                        key,
                        e.currentTarget.value,
                      )
                    }
                  />
                </label>
              ))}
            </div>
            <h2>{locale === "es" ? "Dirección postal" : "Mailing address"}</h2>
            <button
              type="button"
              onClick={() =>
                setProfile((current) => ({
                  ...current,
                  mailingAddress: current.physicalAddress
                    ? { ...current.physicalAddress }
                    : null,
                }))
              }
            >
              {locale === "es"
                ? "Copiar dirección física"
                : "Copy physical address"}
            </button>
            <div className="form-grid">
              {(
                [
                  "line1",
                  "line2",
                  "city",
                  "state",
                  "postalCode",
                  "country",
                ] as (keyof CompanyAddress)[]
              ).map((key) => (
                <label key={`mailing-${key}`}>
                  {
                    (
                      {
                        line1: { es: "Dirección", en: "Address line 1" },
                        line2: {
                          es: "Dirección adicional",
                          en: "Address line 2",
                        },
                        city: { es: "Municipio", en: "City" },
                        state: {
                          es: "Estado/territorio",
                          en: "State/territory",
                        },
                        postalCode: { es: "Código postal", en: "Postal code" },
                        country: { es: "País", en: "Country" },
                      } as Record<
                        keyof CompanyAddress,
                        { es: string; en: string }
                      >
                    )[key][locale]
                  }
                  <input
                    required={
                      profile.mailingAddress !== null && key !== "line2"
                    }
                    value={
                      profile.mailingAddress?.[key] ??
                      (key === "state" ? "PR" : key === "country" ? "US" : "")
                    }
                    onChange={(e) =>
                      addressField("mailingAddress", key, e.currentTarget.value)
                    }
                  />
                </label>
              ))}
            </div>
            {saved && (
              <p className="success-note">
                <Check />
                {t.saved}
              </p>
            )}
            {error && <p className="form-error">{t.required}</p>}
            <AuditControl locale={locale} mode={workweekDefinitions.length&&((workweekDefinitions[0]?.startsOn??profile.workweekStartsOn)!==profile.workweekStartsOn||(workweekDefinitions[0]?.startsAt??profile.workweekStartsAt)!==profile.workweekStartsAt)?"required_reason":"optional_comment"} value={changeReason} onChange={setChangeReason}/>
            <button className="primary" disabled={busy}>
              <Settings />
              {t.save}
            </button>
            {depositScheduleEvents.length > 0 && (
              <section>
                <h3>
                  {locale === "es"
                    ? "Cambios automáticos del calendario de depósitos"
                    : "Automatic deposit-schedule changes"}
                </h3>
                <p>
                  {locale === "es"
                    ? "La regla de $100,000 cambia el calendario a bisemanal. Este historial es inmutable y conserva la nómina que originó el cambio."
                    : "The $100,000 rule changes the schedule to semiweekly. This immutable history retains the payroll that caused the change."}
                </p>
                <div className="run-list">
                  {depositScheduleEvents.map((item) => (
                    <article key={item.id}>
                      <div>
                        <strong>
                          {item.jurisdiction} · {item.oldSchedule} →{" "}
                          {item.newSchedule}
                        </strong>
                        <small>
                          {item.reason} ·{" "}
                          {locale === "es"
                            ? "vigente hasta por lo menos"
                            : "effective through at least"}{" "}
                          {item.throughYear}
                        </small>
                      </div>
                      <span>{formatDate(item.effectiveOn, locale)}</span>
                    </article>
                  ))}
                </div>
              </section>
            )}
            {workweekDefinitions.length > 0 && (
              <section>
                <h3>
                  {locale === "es"
                    ? "Historial inmutable de semana laboral"
                    : "Immutable workweek history"}
                </h3>
                <p>
                  {locale === "es"
                    ? "Cada nómina usa el ciclo de 24 horas vigente para su período; los cambios no reinterpretan períodos anteriores."
                    : "Each payroll uses the 24-hour cycle effective for its period; changes do not reinterpret earlier periods."}
                </p>
                <div className="run-list">
                  {workweekDefinitions.map((item) => (
                    <article key={item.id}>
                      <div>
                        <strong>
                          {
                            (locale === "es"
                              ? [
                                  "domingo",
                                  "lunes",
                                  "martes",
                                  "miércoles",
                                  "jueves",
                                  "viernes",
                                  "sábado",
                                ]
                              : [
                                  "Sunday",
                                  "Monday",
                                  "Tuesday",
                                  "Wednesday",
                                  "Thursday",
                                  "Friday",
                                  "Saturday",
                                ])[item.startsOn]
                          }{" "}
                          · {item.startsAt}
                        </strong>
                        <small>
                          {item.reason} · {item.changedBy}
                        </small>
                      </div>
                      <span>{formatDate(item.effectiveFrom, locale)}</span>
                    </article>
                  ))}
                </div>
              </section>
            )}
          </form>
        ))}
      {companyArea === "payroll_tax" && (
        <>
          <nav
            className="settings-section-nav compact"
            aria-label={
              locale === "es"
                ? "Tareas contributivas patronales"
                : "Employer tax tasks"
            }
          >
            {(
              [
                { key: "forecast", es: "Pronóstico", en: "Forecast" },
                { key: "cfse", es: "CFSE", en: "CFSE" },
                {
                  key: "deadlines",
                  es: "Calendario legal",
                  en: "Legal calendar",
                },
              ] as const
            ).map((item) => (
              <button
                type="button"
                key={item.key}
                className={taxArea === item.key ? "active" : ""}
                onClick={() => setTaxArea(item.key)}
              >
                {locale === "es" ? item.es : item.en}
              </button>
            ))}
          </nav>
          {taxArea === "forecast" && (
            <PayrollObligationForecastPanel locale={locale} tenant={tenant} />
          )}
          {taxArea === "cfse" && (
            <>
              <CfseClassificationPanel locale={locale} tenant={tenant} />
              <CfseCompliancePanelV2 locale={locale} tenant={tenant} />
              <CfseAcceptancePanelV2 locale={locale} tenant={tenant} />
            </>
          )}
          {taxArea === "deadlines" && (
            <StatutoryCalendarPanel locale={locale} tenant={tenant} />
          )}
        </>
      )}
      {companyArea === "time_attendance" && (
        <>
          <PayDifferentialPanel locale={locale} tenant={tenant} />
          <Timekeeping
            locale={locale}
            tenant={tenant}
            canConfigure
            roleKey={roleKey}
            actorWorkerId={null}
            workerType={null}
            workspace="company_time"
          />
        </>
      )}
      {companyArea === "organization" && (
        <>
          <OperatingModelPanel locale={locale} tenant={tenant} />
          <OrganizationPanel locale={locale} tenant={tenant} />
          <PositionManagement locale={locale} tenant={tenant} />
          <LaborAllocationPanel locale={locale} tenant={tenant} />
          <OrganizationLaborCosting locale={locale} tenant={tenant} />
          <Timekeeping
            locale={locale}
            tenant={tenant}
            canConfigure
            roleKey={roleKey}
            actorWorkerId={null}
            workerType={null}
            workspace="company_organization"
          />
        </>
      )}
      {companyArea === "equipment" && (
        <>
          <NfcManagement locale={locale} tenant={tenant} />
        </>
      )}
      {companyArea === "security" &&
        (roleKey === "owner" || roleKey === "platform_administrator") && (
          <AuditLogPanel locale={locale} tenant={tenant} />
        )}
      {companyArea === "platform" && roleKey === "platform_administrator" && (
        <>
          <PlatformStatutoryLibraryPanel locale={locale} />
          <MobileReleasePanel locale={locale} />
          <PrivilegedRecoveryPanel locale={locale} tenant={tenant} />
          <PrivateDocumentIntegrityPanel locale={locale} tenant={tenant} />
        </>
      )}
    </div>
  );
}

type StatutoryCalendarYear = {
  jurisdiction: "US_DC" | "PR";
  calendarYear: number;
  status: string;
  sourceUrl: string;
  sourceVersion: string;
  verifiedOn: string;
};
type ObligationForecast = {
  run: {
    id: string;
    periodStart: string;
    periodEnd: string;
    payDate: string;
    status: string;
  };
  kind: "estimate" | "actual";
  totals: {
    workerCount: number;
    grossCents: number;
    netCents: number;
    employeeTaxesCents: number;
    employerTaxesCents: number;
    employerBenefitsCents: number;
    otherEmployerBurdenCents: number;
    totalEmployerCostCents: number;
  };
  taxes: {
    taxKey: string;
    taxableWagesCents: number;
    employeeAmountCents: number;
    employerAmountCents: number;
  }[];
  employerCosts: {
    costKey: string;
    description: string;
    amountCents: number;
    createsLiability: number;
  }[];
  liabilities: {
    liabilityKey: string;
    amountCents: number;
    dueDate: string | null;
    status: string;
    filingStatus: string | null;
  }[];
};
function PayrollObligationForecastPanel({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const [runs, setRuns] = useState<PayrollRun[]>([]),
    [runId, setRunId] = useState(""),
    [forecast, setForecast] = useState<ObligationForecast | null>(null),
    [busy, setBusy] = useState(true),
    [error, setError] = useState("");
  const money = (cents: number | undefined) =>
    new Intl.NumberFormat(locale === "es" ? "es-PR" : "en-US", {
      style: "currency",
      currency: "USD",
    }).format((cents ?? 0) / 100);
  useEffect(() => {
    void api(`/v1/tenants/${tenant.id}/payroll-runs`).then(async (response) => {
      if (response.ok) {
        const available = (
          (await response.json()) as { runs: PayrollRun[] }
        ).runs.filter((run) =>
          ["calculated", "approved", "finalized"].includes(run.status),
        );
        setRuns(available);
        setRunId(available[0]?.id ?? "");
      } else setError(`runs:${response.status}`);
      setBusy(false);
    });
  }, [tenant.id]);
  useEffect(() => {
    if (!runId) {
      setForecast(null);
      return;
    }
    setBusy(true);
    setError("");
    void api(
      `/v1/tenants/${tenant.id}/payroll-runs/${runId}/obligation-forecast`,
    ).then(async (response) => {
      if (response.ok)
        setForecast((await response.json()) as ObligationForecast);
      else setError(`forecast:${response.status}`);
      setBusy(false);
    });
  }, [tenant.id, runId]);
  const depositTotal =
      forecast?.taxes.reduce(
        (sum, line) =>
          sum + line.employeeAmountCents + line.employerAmountCents,
        0,
      ) ?? 0,
    cfse = forecast?.taxes.find((line) => line.taxKey === "cfse");
  return (
    <section className="settings-form">
      <h2>
        {locale === "es"
          ? "Pronóstico de obligaciones patronales"
          : "Employer obligation forecast"}
      </h2>
      <p>
        {locale === "es"
          ? "Seleccione una nómina calculada. Los importes provienen del cálculo real y sus reglas vigentes; no son tasas escritas en esta pantalla."
          : "Select a calculated payroll. Amounts come from the actual calculation and its effective rules; no rates are hard-coded on this screen."}
      </p>
      <label>
        {locale === "es" ? "Nómina" : "Payroll"}
        <select value={runId} onChange={(e) => setRunId(e.currentTarget.value)}>
          <option value="">
            {locale === "es"
              ? "No hay nóminas calculadas"
              : "No calculated payrolls"}
          </option>
          {runs.map((run) => (
            <option key={run.id} value={run.id}>
              {formatDateRange(run.periodStart, run.periodEnd, locale, "—")} ·{" "}
              {formatDate(run.payDate, locale)} ·{" "}
              {optionLabel(run.status, locale)}
            </option>
          ))}
        </select>
      </label>
      {busy && <LoaderCircle className="spin" />}
      {forecast && (
        <>
          <p
            className={
              forecast.kind === "estimate" ? "form-detail" : "success-note"
            }
          >
            <strong>
              {forecast.kind === "estimate"
                ? locale === "es"
                  ? "Estimado — la nómina todavía puede cambiar"
                  : "Estimate — payroll can still change"
                : locale === "es"
                  ? "Real — nómina finalizada"
                  : "Actual — finalized payroll"}
            </strong>
          </p>
          <div className="balance-grid">
            <article>
              <strong>
                {locale === "es" ? "Nómina bruta" : "Gross payroll"}
              </strong>
              <em>{money(forecast.totals.grossCents)}</em>
              <span>
                {forecast.totals.workerCount}{" "}
                {locale === "es" ? "personas" : "people"}
              </span>
            </article>
            <article>
              <strong>{locale === "es" ? "Neto a pagar" : "Net pay"}</strong>
              <em>{money(forecast.totals.netCents)}</em>
            </article>
            <article>
              <strong>
                {locale === "es" ? "Depósitos contributivos" : "Tax deposits"}
              </strong>
              <em>{money(depositTotal)}</em>
              <span>
                {locale === "es"
                  ? "retención más parte patronal"
                  : "withholding plus employer share"}
              </span>
            </article>
            <article>
              <strong>CFSE</strong>
              <em>{cfse ? money(cfse.employerAmountCents) : "—"}</em>
              <span>
                {cfse
                  ? locale === "es"
                    ? "prima calculada"
                    : "calculated premium"
                  : locale === "es"
                    ? "Sin importe CFSE calculado"
                    : "No calculated CFSE amount"}
              </span>
            </article>
            <article>
              <strong>
                {locale === "es"
                  ? "Costo patronal total"
                  : "Total employer cost"}
              </strong>
              <em>{money(forecast.totals.totalEmployerCostCents)}</em>
            </article>
          </div>
          <h3>{locale === "es" ? "Detalle contributivo" : "Tax detail"}</h3>
          <div className="run-list">
            {forecast.taxes.map((line) => (
              <article key={line.taxKey}>
                <div>
                  <strong>{optionLabel(line.taxKey, locale)}</strong>
                  <small>
                    {locale === "es" ? "Salarios sujetos" : "Taxable wages"}:{" "}
                    {money(line.taxableWagesCents)}
                  </small>
                </div>
                <span>
                  {money(line.employeeAmountCents + line.employerAmountCents)}
                </span>
              </article>
            ))}
          </div>
          {forecast.kind === "actual" && forecast.liabilities.length > 0 && (
            <>
              <h3>
                {locale === "es"
                  ? "Obligaciones creadas al finalizar"
                  : "Liabilities created at finalization"}
              </h3>
              <div className="run-list">
                {forecast.liabilities.map((line) => (
                  <article key={line.liabilityKey}>
                    <div>
                      <strong>{optionLabel(line.liabilityKey, locale)}</strong>
                      <small>
                        {line.dueDate
                          ? `${locale === "es" ? "Vence" : "Due"} ${formatDate(line.dueDate, locale)}`
                          : locale === "es"
                            ? "Sin vencimiento calculado"
                            : "No calculated due date"}
                      </small>
                    </div>
                    <span>{money(line.amountCents)}</span>
                  </article>
                ))}
              </div>
            </>
          )}
        </>
      )}
      {!busy && !runs.length && (
        <p className="form-detail">
          {locale === "es"
            ? "Calcule una nómina en Nómina para ver aquí el pronóstico. No necesita finalizarla."
            : "Calculate a payroll in Payroll to see its forecast here. It does not need to be finalized."}
        </p>
      )}
      <OperationalError value={error} locale={locale} />
      <p>
        <small>
          {locale === "es"
            ? "Los pagos, comprobantes de radicación y la reconciliación final se administran en Informes; no se duplican en Configuración."
            : "Payments, filing evidence, and final reconciliation are managed in Reports; they are not duplicated in Settings."}
        </small>
      </p>
    </section>
  );
}
type StatutoryCalendarDate = {
  jurisdiction: "US_DC" | "PR";
  calendarYear: number;
  nonbusinessDate: string;
  name: string;
};
function StatutoryCalendarPanel({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const [years, setYears] = useState<StatutoryCalendarYear[]>([]),
    [dates, setDates] = useState<StatutoryCalendarDate[]>([]),
    [coverage, setCoverage] = useState({
      federal2026: false,
      puertoRico2026: false,
    }),
    [busy, setBusy] = useState(true),
    [error, setError] = useState("");
  useEffect(() => {
    void api(`/v1/tenants/${tenant.id}/statutory-calendars`).then(
      async (response) => {
        if (response.ok) {
          const body = (await response.json()) as {
            years: StatutoryCalendarYear[];
            dates: StatutoryCalendarDate[];
            coverage: typeof coverage;
          };
          setYears(body.years);
          setDates(body.dates);
          setCoverage(body.coverage);
        } else setError(`calendar:${response.status}`);
        setBusy(false);
      },
    );
  }, [tenant.id]);
  return (
    <section className="settings-form">
      <h2>
        {locale === "es"
          ? "Calendarios legales para vencimientos"
          : "Legal calendars for statutory deadlines"}
      </h2>
      <p>
        {locale === "es"
          ? "Estos días no son el calendario de vacaciones de la compañía. Se usan únicamente para mover vencimientos contributivos al próximo día laborable."
          : "These dates are not the company leave calendar. They are used only to move statutory deadlines to the next business day."}
      </p>
      <div className="summary-grid">
        <article>
          <strong>IRS / District of Columbia 2026</strong>
          <span>
            {coverage.federal2026
              ? locale === "es"
                ? "Verificado"
                : "Verified"
              : locale === "es"
                ? "Falta cobertura"
                : "Coverage missing"}
          </span>
        </article>
        <article>
          <strong>Puerto Rico / Hacienda 2026</strong>
          <span>
            {coverage.puertoRico2026
              ? locale === "es"
                ? "Verificado"
                : "Verified"
              : locale === "es"
                ? "Pendiente de fuente aplicable"
                : "Applicable source pending"}
          </span>
        </article>
      </div>
      {years.map((year) => (
        <article
          className="operation-panel"
          key={`${year.jurisdiction}-${year.calendarYear}`}
        >
          <strong>
            {year.jurisdiction} · {year.calendarYear} ·{" "}
            {optionLabel(year.status, locale)}
          </strong>
          <p>
            {year.sourceVersion} · {locale === "es" ? "verificado" : "verified"}{" "}
            {formatDate(year.verifiedOn, locale)}
          </p>
          <a
            className="official-source-link"
            href={year.sourceUrl}
            target="_blank"
            rel="noreferrer"
          >
            {locale === "es" ? "Abrir fuente oficial" : "Open official source"}
          </a>
          <div className="run-list">
            {dates
              .filter(
                (date) =>
                  date.jurisdiction === year.jurisdiction &&
                  date.calendarYear === year.calendarYear,
              )
              .map((date) => (
                <article key={`${date.jurisdiction}-${date.nonbusinessDate}`}>
                  <strong>{date.name}</strong>
                  <span>{formatDate(date.nonbusinessDate, locale)}</span>
                </article>
              ))}
          </div>
        </article>
      ))}
      {busy && <LoaderCircle className="spin" />}
      <OperationalError value={error} locale={locale} />
    </section>
  );
}

function CfseCompliancePanelV2({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const today = new Date().toISOString().slice(0, 10),
    [declarations, setDeclarations] = useState<CfseDeclaration[]>([]),
    [bills, setBills] = useState<CfseBill[]>([]),
    [form, setForm] = useState({
      policyNumber: "",
      periodStart: `${today.slice(0, 4)}-01-01`,
      periodEnd: today,
      reason: "",
    }),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const load = async () => {
    const response = await api(`/v1/tenants/${tenant.id}/cfse/compliance`);
    if (response.ok) {
      const body = (await response.json()) as {
        declarations: CfseDeclaration[];
        bills: CfseBill[];
      };
      setDeclarations(body.declarations);
      setBills(body.bills);
    }
  };
  useEffect(() => {
    void load();
  }, [tenant.id]);
  const prepare = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const response = await api(`/v1/tenants/${tenant.id}/cfse/declarations`, {
      method: "POST",
      body: JSON.stringify(form),
    });
    if (!response.ok) setError(`declaration:${response.status}`);
    else {
      setForm({ ...form, reason: "" });
      await load();
    }
    setBusy(false);
  };
  const upload = async (
    path: string,
    fields: Record<string, string>,
    file: File,
  ) => {
    setBusy(true);
    const data = new FormData();
    Object.entries(fields).forEach(([key, value]) => data.set(key, value));
    data.set("file", file);
    const response = await api(`/v1/tenants/${tenant.id}/cfse/${path}`, {
      method: "POST",
      body: data,
    });
    if (!response.ok) setError(`${path}:${response.status}`);
    else await load();
    setBusy(false);
  };
  const submit = async (item: CfseDeclaration) => {
    const values = await requestWorkflowDialog({
      title:
        locale === "es"
          ? "Radicar declaración CFSE"
          : "Submit CFSE declaration",
      detail: `${item.policyNumber} · ${formatDateRange(item.periodStart, item.periodEnd, locale, "—")}`,
      confirmLabel:
        locale === "es" ? "Radicar con evidencia" : "Submit with evidence",
      cancelLabel: locale === "es" ? "Cancelar" : "Cancel",
      fields: [
        {
          key: "submissionReference",
          label:
            locale === "es"
              ? "Referencia de radicación"
              : "Submission reference",
          required: true,
          minLength: 2,
        },
        {
          key: "submittedOn",
          label: locale === "es" ? "Fecha sometida" : "Submitted date",
          type: "date",
          value: today,
          required: true,
        },
        {
          key: "reason",
          label: locale === "es" ? "Razón documentada" : "Documented reason",
          type: "textarea",
          required: true,
          minLength: 3,
          fullWidth: true,
        },
        {
          key: "file",
          label:
            locale === "es" ? "Evidencia de radicación" : "Submission evidence",
          type: "file",
          accept: "application/pdf,image/png,image/jpeg",
          required: true,
          fullWidth: true,
        },
      ],
    });
    if (values && values.file instanceof File)
      await upload(
        `declarations/${item.id}/submit`,
        {
          submissionReference: String(values.submissionReference),
          submittedOn: String(values.submittedOn),
          reason: String(values.reason),
        },
        values.file,
      );
  };
  const bill = async (item: CfseDeclaration) => {
    const values = await requestWorkflowDialog({
      title: locale === "es" ? "Registrar factura CFSE" : "Record CFSE bill",
      detail: `${item.policyNumber} · ${money(item.calculatedPremiumCents)}`,
      confirmLabel: locale === "es" ? "Guardar factura" : "Save bill",
      cancelLabel: locale === "es" ? "Cancelar" : "Cancel",
      fields: [
        {
          key: "billReference",
          label: locale === "es" ? "Referencia de factura" : "Bill reference",
          required: true,
          minLength: 2,
        },
        {
          key: "amount",
          label: locale === "es" ? "Importe facturado" : "Billed amount",
          type: "number",
          min: "0.01",
          step: "0.01",
          required: true,
        },
        {
          key: "dueDate",
          label: locale === "es" ? "Fecha de vencimiento" : "Due date",
          type: "date",
          value: today,
          required: true,
        },
        {
          key: "reason",
          label: locale === "es" ? "Razón documentada" : "Documented reason",
          type: "textarea",
          required: true,
          minLength: 3,
          fullWidth: true,
        },
        {
          key: "file",
          label: locale === "es" ? "Factura oficial" : "Official bill",
          type: "file",
          accept: "application/pdf,image/png,image/jpeg",
          required: true,
          fullWidth: true,
        },
      ],
    });
    if (values && values.file instanceof File)
      await upload(
        "bills",
        {
          declarationId: item.id,
          billReference: String(values.billReference),
          billedCents: String(Math.round(Number(values.amount) * 100)),
          dueDate: String(values.dueDate),
          reason: String(values.reason),
        },
        values.file,
      );
  };
  const pay = async (item: CfseBill) => {
    const values = await requestWorkflowDialog({
      title: locale === "es" ? "Registrar pago CFSE" : "Record CFSE payment",
      detail: `${item.billReference} · ${money(item.billedCents)}`,
      confirmLabel: locale === "es" ? "Guardar pago" : "Save payment",
      cancelLabel: locale === "es" ? "Cancelar" : "Cancel",
      fields: [
        {
          key: "paymentDate",
          label: locale === "es" ? "Fecha de pago" : "Payment date",
          type: "date",
          value: today,
          required: true,
        },
        {
          key: "paymentConfirmation",
          label: locale === "es" ? "Confirmación" : "Confirmation",
          required: true,
          minLength: 2,
        },
        {
          key: "reason",
          label: locale === "es" ? "Razón documentada" : "Documented reason",
          type: "textarea",
          required: true,
          minLength: 3,
          fullWidth: true,
        },
        {
          key: "file",
          label: locale === "es" ? "Recibo de pago" : "Payment receipt",
          type: "file",
          accept: "application/pdf,image/png,image/jpeg",
          required: true,
          fullWidth: true,
        },
      ],
    });
    if (values && values.file instanceof File)
      await upload(
        `bills/${item.id}/pay`,
        {
          paymentDate: String(values.paymentDate),
          paymentConfirmation: String(values.paymentConfirmation),
          reason: String(values.reason),
        },
        values.file,
      );
  };
  const view = async (path: string) => {
    const response = await api(
      `/v1/tenants/${tenant.id}/cfse/evidence/${path}`,
    );
    if (!response.ok) {
      setError(`evidence:${response.status}`);
      return;
    }
    const url = URL.createObjectURL(await response.blob());
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };
  return (
    <section className="settings-form">
      <h2>
        {locale === "es"
          ? "Declaraciones, facturas y pagos CFSE"
          : "CFSE declarations, bills, and payments"}
      </h2>
      <form onSubmit={prepare}>
        <div className="form-grid">
          <label>
            {locale === "es" ? "Póliza" : "Policy"}
            <input
              required
              value={form.policyNumber}
              onChange={(e) =>
                setForm({ ...form, policyNumber: e.currentTarget.value })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Desde" : "From"}
            <input
              required
              type="date"
              value={form.periodStart}
              onInput={(e) =>
                setForm({ ...form, periodStart: e.currentTarget.value })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Hasta" : "To"}
            <input
              required
              type="date"
              value={form.periodEnd}
              onInput={(e) =>
                setForm({ ...form, periodEnd: e.currentTarget.value })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Razón" : "Reason"}
            <input
              required
              minLength={3}
              value={form.reason}
              onChange={(e) =>
                setForm({ ...form, reason: e.currentTarget.value })
              }
            />
          </label>
        </div>
        <button className="primary" disabled={busy}>
          {locale === "es"
            ? "Preparar declaración desde nómina final"
            : "Prepare declaration from final payroll"}
        </button>
      </form>
      <div className="run-list">
        {declarations.map((item) => (
          <article key={item.id}>
            <div>
              <strong>
                {item.policyNumber} ·{" "}
                {formatDateRange(item.periodStart, item.periodEnd, locale, "—")}
              </strong>
              <p>
                {money(item.totalPayrollCents)} ·{" "}
                {locale === "es" ? "prima calculada" : "calculated premium"}{" "}
                {money(item.calculatedPremiumCents)}
              </p>
            </div>
            <span>{optionLabel(item.status, locale)}</span>
            {item.status === "prepared" && (
              <button type="button" onClick={() => submit(item)}>
                {locale === "es"
                  ? "Radicar con evidencia"
                  : "Submit with evidence"}
              </button>
            )}
            {item.evidenceFileName && (
              <button
                type="button"
                onClick={() => void view(`declaration/${item.id}/submission`)}
              >
                {locale === "es" ? "Ver último recibo" : "View latest receipt"}
              </button>
            )}
            {item.status === "accepted" &&
              !bills.some((b) => b.declarationId === item.id) && (
                <button type="button" onClick={() => bill(item)}>
                  {locale === "es" ? "Registrar factura" : "Record bill"}
                </button>
              )}
          </article>
        ))}
      </div>
      <div className="run-list">
        {bills.map((item) => (
          <article key={item.id}>
            <div>
              <strong>
                {item.billReference} · {money(item.billedCents)}
              </strong>
              <p>
                {locale === "es" ? "Vence" : "Due"}{" "}
                {formatDate(item.dueDate, locale)} ·{" "}
                {optionLabel(item.varianceStatus, locale)}
              </p>
            </div>
            <span>{optionLabel(item.status, locale)}</span>
            <button
              type="button"
              onClick={() => void view(`bill/${item.id}/bill`)}
            >
              {locale === "es" ? "Ver factura" : "View bill"}
            </button>
            {item.status === "unpaid" &&
              item.varianceStatus !== "review_required" && (
                <button type="button" onClick={() => pay(item)}>
                  {locale === "es" ? "Registrar pago" : "Record payment"}
                </button>
              )}
            {item.status === "unpaid" &&
              item.varianceStatus === "review_required" && (
                <small>
                  {locale === "es"
                    ? "Concilie la diferencia antes de pagar."
                    : "Reconcile the variance before payment."}
                </small>
              )}
            {item.paymentFileName && (
              <button
                type="button"
                onClick={() => void view(`bill/${item.id}/payment`)}
              >
                {locale === "es" ? "Ver recibo" : "View receipt"}
              </button>
            )}
          </article>
        ))}
      </div>
      <OperationalError value={error} locale={locale} />
    </section>
  );
}

type CfseClassification = {
  id: string;
  policyNumber: string;
  classificationCode: string;
  description: string;
  rateBps: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  sourceReference: string;
  status: string;
  reason: string;
};
type CfseAssignment = {
  id: string;
  classificationId: string;
  classificationCode: string;
  targetType: "worker" | "project";
  workerId: string | null;
  projectId: string | null;
  targetName: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  status: string;
  reason: string;
};
function CfseClassificationPanel({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const today = new Date().toISOString().slice(0, 10),
    [classifications, setClassifications] = useState<CfseClassification[]>([]),
    [assignments, setAssignments] = useState<CfseAssignment[]>([]),
    [workers, setWorkers] = useState<Worker[]>([]),
    [projects, setProjects] = useState<Project[]>([]),
    [classification, setClassification] = useState({
      policyNumber: "",
      classificationCode: "",
      description: "",
      ratePercent: "",
      effectiveFrom: today,
      effectiveTo: "",
      sourceReference: "",
      reason: "",
    }),
    [assignment, setAssignment] = useState({
      classificationId: "",
      targetType: "worker" as "worker" | "project",
      targetId: "",
      effectiveFrom: today,
      effectiveTo: "",
      reason: "",
    }),
    [busy, setBusy] = useState(true),
    [error, setError] = useState("");
  const load = async () => {
    const [cfse, workerResponse, projectResponse] = await Promise.all([
      api(`/v1/tenants/${tenant.id}/cfse`),
      api(`/v1/tenants/${tenant.id}/workers`),
      api(`/v1/tenants/${tenant.id}/projects`),
    ]);
    if (cfse.ok) {
      const body = (await cfse.json()) as {
        classifications: CfseClassification[];
        assignments: CfseAssignment[];
      };
      setClassifications(body.classifications);
      setAssignments(body.assignments);
    }
    if (workerResponse.ok)
      setWorkers(
        ((await workerResponse.json()) as { workers: Worker[] }).workers,
      );
    if (projectResponse.ok)
      setProjects(
        ((await projectResponse.json()) as { projects: Project[] }).projects,
      );
    setBusy(false);
  };
  useEffect(() => {
    void load();
  }, [tenant.id]);
  const createClassification = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/cfse/classifications`,
      {
        method: "POST",
        body: JSON.stringify({
          policyNumber: classification.policyNumber,
          classificationCode: classification.classificationCode,
          description: classification.description,
          rateBps: Math.round(Number(classification.ratePercent) * 100),
          effectiveFrom: classification.effectiveFrom,
          effectiveTo: classification.effectiveTo || null,
          sourceReference: classification.sourceReference,
          sourceDocumentId: null,
          reason: classification.reason,
        }),
      },
    );
    if (response.ok)
      setClassification({
        ...classification,
        classificationCode: "",
        description: "",
        ratePercent: "",
        sourceReference: "",
        reason: "",
      });
    else setError(`classification:${response.status}`);
    await load();
  };
  const createAssignment = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const response = await api(`/v1/tenants/${tenant.id}/cfse/assignments`, {
      method: "POST",
      body: JSON.stringify({
        classificationId: assignment.classificationId,
        targetType: assignment.targetType,
        workerId:
          assignment.targetType === "worker" ? assignment.targetId : null,
        projectId:
          assignment.targetType === "project" ? assignment.targetId : null,
        effectiveFrom: assignment.effectiveFrom,
        effectiveTo: assignment.effectiveTo || null,
        reason: assignment.reason,
      }),
    });
    if (response.ok) setAssignment({ ...assignment, targetId: "", reason: "" });
    else setError(`assignment:${response.status}`);
    await load();
  };
  const close = async (item: CfseAssignment) => {
    const values = await requestWorkflowDialog({
      title:
        locale === "es" ? "Cerrar asignación CFSE" : "Close CFSE assignment",
      detail: `${item.classificationCode} → ${item.targetName}`,
      confirmLabel: locale === "es" ? "Cerrar asignación" : "Close assignment",
      cancelLabel: locale === "es" ? "Cancelar" : "Cancel",
      danger: true,
      fields: [
        {
          key: "effectiveTo",
          label:
            locale === "es" ? "Último día de vigencia" : "Last effective date",
          type: "date",
          value: today,
          required: true,
        },
        {
          key: "reason",
          label:
            locale === "es"
              ? "Razón documentada del cierre"
              : "Documented closure reason",
          type: "textarea",
          required: true,
          minLength: 3,
          fullWidth: true,
        },
      ],
    });
    if (!values) return;
    setBusy(true);
    const response = await api(
      `/v1/tenants/${tenant.id}/cfse/assignments/${item.id}/close`,
      {
        method: "POST",
        body: JSON.stringify({
          effectiveTo: String(values.effectiveTo),
          reason: String(values.reason),
        }),
      },
    );
    if (!response.ok) setError(`close:${response.status}`);
    await load();
  };
  return (
    <section className="settings-form">
      <h2>
        {locale === "es" ? "Clasificaciones CFSE" : "CFSE classifications"}
      </h2>
      <p>
        {locale === "es"
          ? "Registre exactamente las clasificaciones y tasas de la póliza CFSE. Una asignación de proyecto tiene prioridad sobre la del trabajador; nómina se bloquea si un salario no tiene clasificación vigente."
          : "Record the exact classifications and rates on the CFSE policy. A project assignment takes priority over the worker assignment; payroll is blocked when taxable wages have no effective classification."}
      </p>
      <form onSubmit={createClassification}>
        <div className="form-grid">
          <label>
            {locale === "es" ? "Póliza" : "Policy"}
            <input
              required
              value={classification.policyNumber}
              onChange={(e) =>
                setClassification({
                  ...classification,
                  policyNumber: e.currentTarget.value,
                })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Código" : "Code"}
            <input
              required
              value={classification.classificationCode}
              onChange={(e) =>
                setClassification({
                  ...classification,
                  classificationCode: e.currentTarget.value,
                })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Descripción" : "Description"}
            <input
              required
              value={classification.description}
              onChange={(e) =>
                setClassification({
                  ...classification,
                  description: e.currentTarget.value,
                })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Tasa %" : "Rate %"}
            <input
              required
              type="number"
              min="0"
              step="0.01"
              value={classification.ratePercent}
              onInput={(e) =>
                setClassification({
                  ...classification,
                  ratePercent: e.currentTarget.value,
                })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Vigente desde" : "Effective from"}
            <input
              required
              type="date"
              value={classification.effectiveFrom}
              onInput={(e) =>
                setClassification({
                  ...classification,
                  effectiveFrom: e.currentTarget.value,
                })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Vigente hasta (opcional)" : "Effective to (optional)"}
            <input
              type="date"
              value={classification.effectiveTo}
              onInput={(e) =>
                setClassification({
                  ...classification,
                  effectiveTo: e.currentTarget.value,
                })
              }
            />
          </label>
          <label>
            {locale === "es"
              ? "Referencia de aviso/documento CFSE"
              : "CFSE notice/document reference"}
            <input
              required
              value={classification.sourceReference}
              onChange={(e) =>
                setClassification({
                  ...classification,
                  sourceReference: e.currentTarget.value,
                })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Razón auditable" : "Auditable reason"}
            <input
              required
              minLength={3}
              value={classification.reason}
              onChange={(e) =>
                setClassification({
                  ...classification,
                  reason: e.currentTarget.value,
                })
              }
            />
          </label>
        </div>
        <button className="primary" disabled={busy}>
          {locale === "es" ? "Crear clasificación" : "Create classification"}
        </button>
      </form>
      <form onSubmit={createAssignment}>
        <h3>
          {locale === "es" ? "Asignar clasificación" : "Assign classification"}
        </h3>
        <div className="form-grid">
          <label>
            {locale === "es" ? "Clasificación" : "Classification"}
            <select
              required
              value={assignment.classificationId}
              onChange={(e) =>
                setAssignment({
                  ...assignment,
                  classificationId: e.currentTarget.value,
                })
              }
            >
              <option value="">—</option>
              {classifications
                .filter((item) => item.status === "active")
                .map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.classificationCode} ·{" "}
                    {(item.rateBps / 100).toFixed(2)}%
                  </option>
                ))}
            </select>
          </label>
          <label>
            {locale === "es" ? "Aplicar a" : "Apply to"}
            <select
              value={assignment.targetType}
              onChange={(e) =>
                setAssignment({
                  ...assignment,
                  targetType: e.currentTarget.value as "worker" | "project",
                  targetId: "",
                })
              }
            >
              <option value="worker">
                {locale === "es" ? "Trabajador" : "Worker"}
              </option>
              <option value="project">
                {locale === "es" ? "Trabajo/proyecto" : "Job/project"}
              </option>
            </select>
          </label>
          <label>
            {locale === "es" ? "Destino" : "Target"}
            <select
              required
              value={assignment.targetId}
              onChange={(e) =>
                setAssignment({
                  ...assignment,
                  targetId: e.currentTarget.value,
                })
              }
            >
              <option value="">—</option>
              {assignment.targetType === "worker"
                ? workers
                    .filter((item) => item.workerType === "employee")
                    .map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.firstName} {item.lastName}
                      </option>
                    ))
                : projects
                    .filter((item) => item.status === "active")
                    .map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
            </select>
          </label>
          <label>
            {locale === "es" ? "Vigente desde" : "Effective from"}
            <input
              required
              type="date"
              value={assignment.effectiveFrom}
              onInput={(e) =>
                setAssignment({
                  ...assignment,
                  effectiveFrom: e.currentTarget.value,
                })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Vigente hasta (opcional)" : "Effective to (optional)"}
            <input
              type="date"
              value={assignment.effectiveTo}
              onInput={(e) =>
                setAssignment({
                  ...assignment,
                  effectiveTo: e.currentTarget.value,
                })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Razón" : "Reason"}
            <input
              required
              minLength={3}
              value={assignment.reason}
              onChange={(e) =>
                setAssignment({ ...assignment, reason: e.currentTarget.value })
              }
            />
          </label>
        </div>
        <button className="primary" disabled={busy}>
          {locale === "es" ? "Guardar asignación" : "Save assignment"}
        </button>
      </form>
      <div className="run-list">
        {classifications.map((item) => (
          <article key={item.id}>
            <div>
              <strong>
                {item.classificationCode} · {item.description} ·{" "}
                {(item.rateBps / 100).toFixed(2)}%
              </strong>
              <small>
                {item.policyNumber} ·{" "}
                {formatDateRange(
                  item.effectiveFrom,
                  item.effectiveTo,
                  locale,
                  locale === "es" ? "vigente" : "current",
                )}{" "}
                · {item.sourceReference}
              </small>
            </div>
            <span>{optionLabel(item.status, locale)}</span>
          </article>
        ))}
      </div>
      <div className="run-list">
        {assignments.map((item) => (
          <article key={item.id}>
            <div>
              <strong>
                {item.classificationCode} → {item.targetName}
              </strong>
              <small>
                {optionLabel(item.targetType, locale)} ·{" "}
                {formatDateRange(
                  item.effectiveFrom,
                  item.effectiveTo,
                  locale,
                  locale === "es" ? "vigente" : "current",
                )}{" "}
                · {item.reason}
              </small>
            </div>
            <span>{optionLabel(item.status, locale)}</span>
            {item.status === "active" && (
              <button type="button" onClick={() => void close(item)}>
                {locale === "es" ? "Cerrar" : "Close"}
              </button>
            )}
          </article>
        ))}
      </div>
      {busy && <LoaderCircle className="spin" />}
      <OperationalError value={error} locale={locale} />
    </section>
  );
}

type CfseDeclaration = {
  id: string;
  policyNumber: string;
  periodStart: string;
  periodEnd: string;
  totalPayrollCents: number;
  calculatedPremiumCents: number;
  status: string;
  submissionReference: string | null;
  submittedOn: string | null;
  acceptanceReference: string | null;
  acceptedOn: string | null;
  evidenceFileName: string | null;
};
type CfseBill = {
  id: string;
  declarationId: string;
  billReference: string;
  billedCents: number;
  calculatedPremiumCents: number;
  varianceCents: number;
  varianceStatus: "matched" | "review_required" | "reconciled";
  varianceResolutionMethod: string | null;
  varianceReference: string | null;
  varianceResolvedOn: string | null;
  varianceReason: string | null;
  dueDate: string;
  status: string;
  billFileName: string;
  paymentDate: string | null;
  paymentConfirmation: string | null;
  paymentFileName: string | null;
};
type CfseAttempt = {
  id: string;
  declarationId: string;
  attemptNumber: number;
  submissionReference: string;
  submittedOn: string;
  evidenceFileName: string;
  reason: string;
};
type CfseDecision = {
  id: string;
  declarationId: string;
  submissionAttemptId: string;
  outcome: "accepted" | "rejected";
  agencyReference: string;
  decisionOn: string;
  reason: string;
  evidenceFileName: string | null;
};
function CfseAcceptancePanelV2({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const today = new Date().toISOString().slice(0, 10),
    [declarations, setDeclarations] = useState<CfseDeclaration[]>([]),
    [bills, setBills] = useState<CfseBill[]>([]),
    [attempts, setAttempts] = useState<CfseAttempt[]>([]),
    [decisions, setDecisions] = useState<CfseDecision[]>([]),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const load = async () => {
    const response = await api(`/v1/tenants/${tenant.id}/cfse/compliance`);
    if (!response.ok) {
      setError(`CFSE: ${response.status}`);
      return;
    }
    const body = (await response.json()) as {
      declarations: CfseDeclaration[];
      bills: CfseBill[];
      attempts: CfseAttempt[];
      decisions: CfseDecision[];
    };
    setDeclarations(body.declarations);
    setBills(body.bills);
    setAttempts(body.attempts);
    setDecisions(body.decisions);
  };
  useEffect(() => {
    void load();
  }, [tenant.id]);
  const accept = async (item: CfseDeclaration) => {
    const values = await requestWorkflowDialog({
      title:
        locale === "es"
          ? "Registrar aceptación de CFSE"
          : "Record CFSE acceptance",
      detail: `${item.policyNumber} · ${formatDateRange(item.periodStart, item.periodEnd, locale, "—")}`,
      confirmLabel: locale === "es" ? "Guardar aceptación" : "Save acceptance",
      cancelLabel: locale === "es" ? "Cancelar" : "Cancel",
      fields: [
        {
          key: "acceptanceReference",
          label:
            locale === "es"
              ? "Referencia de aceptación"
              : "Acceptance reference",
          required: true,
          minLength: 2,
        },
        {
          key: "acceptedOn",
          label: locale === "es" ? "Fecha de aceptación" : "Acceptance date",
          type: "date",
          value: today,
          required: true,
        },
        {
          key: "reason",
          label: locale === "es" ? "Razón documentada" : "Documented reason",
          type: "textarea",
          required: true,
          minLength: 3,
          fullWidth: true,
        },
      ],
    });
    if (!values) return;
    setBusy(true);
    const response = await api(
      `/v1/tenants/${tenant.id}/cfse/declarations/${item.id}/accept`,
      {
        method: "POST",
        body: JSON.stringify({
          acceptanceReference: String(values.acceptanceReference),
          acceptedOn: String(values.acceptedOn),
          reason: String(values.reason),
        }),
      },
    );
    if (!response.ok) setError(`acceptance: ${response.status}`);
    else await load();
    setBusy(false);
  };
  const fileAction = async (
    path: string,
    fields: Record<string, string>,
    file: File,
  ) => {
    setBusy(true);
    const form = new FormData();
    Object.entries(fields).forEach(([key, value]) => form.set(key, value));
    form.set("file", file);
    const response = await api(`/v1/tenants/${tenant.id}/cfse/${path}`, {
      method: "POST",
      body: form,
    });
    if (!response.ok) setError(`${path}: ${response.status}`);
    else await load();
    setBusy(false);
  };
  const reject = async (item: CfseDeclaration) => {
    const values = await requestWorkflowDialog({
      title:
        locale === "es" ? "Registrar rechazo de CFSE" : "Record CFSE rejection",
      detail: `${item.policyNumber} · ${formatDateRange(item.periodStart, item.periodEnd, locale, "—")}`,
      confirmLabel: locale === "es" ? "Guardar rechazo" : "Save rejection",
      cancelLabel: locale === "es" ? "Cancelar" : "Cancel",
      danger: true,
      fields: [
        {
          key: "agencyReference",
          label:
            locale === "es" ? "Referencia del rechazo" : "Rejection reference",
          required: true,
          minLength: 2,
        },
        {
          key: "decisionOn",
          label: locale === "es" ? "Fecha de decisión" : "Decision date",
          type: "date",
          value: today,
          required: true,
        },
        {
          key: "reason",
          label:
            locale === "es"
              ? "Razón exacta indicada por CFSE"
              : "Exact reason provided by CFSE",
          type: "textarea",
          required: true,
          minLength: 3,
          fullWidth: true,
        },
        {
          key: "file",
          label: locale === "es" ? "Evidencia de CFSE" : "CFSE evidence",
          type: "file",
          accept: "application/pdf,image/png,image/jpeg",
          required: true,
          fullWidth: true,
        },
      ],
    });
    if (values && values.file instanceof File)
      await fileAction(
        `declarations/${item.id}/reject`,
        {
          agencyReference: String(values.agencyReference),
          decisionOn: String(values.decisionOn),
          reason: String(values.reason),
        },
        values.file,
      );
  };
  const resubmit = async (item: CfseDeclaration) => {
    const values = await requestWorkflowDialog({
      title:
        locale === "es" ? "Radicar corrección CFSE" : "Submit CFSE correction",
      detail: `${item.policyNumber} · ${formatDateRange(item.periodStart, item.periodEnd, locale, "—")}`,
      confirmLabel:
        locale === "es" ? "Radicar corrección" : "Submit correction",
      cancelLabel: locale === "es" ? "Cancelar" : "Cancel",
      fields: [
        {
          key: "submissionReference",
          label:
            locale === "es"
              ? "Nueva referencia de radicación"
              : "New submission reference",
          required: true,
          minLength: 2,
        },
        {
          key: "submittedOn",
          label:
            locale === "es"
              ? "Fecha de nueva radicación"
              : "New submission date",
          type: "date",
          value: today,
          required: true,
        },
        {
          key: "reason",
          label: locale === "es" ? "Corrección realizada" : "Correction made",
          type: "textarea",
          required: true,
          minLength: 3,
          fullWidth: true,
        },
        {
          key: "file",
          label:
            locale === "es"
              ? "Evidencia de nueva radicación"
              : "Resubmission evidence",
          type: "file",
          accept: "application/pdf,image/png,image/jpeg",
          required: true,
          fullWidth: true,
        },
      ],
    });
    if (values && values.file instanceof File)
      await fileAction(
        `declarations/${item.id}/submit`,
        {
          submissionReference: String(values.submissionReference),
          submittedOn: String(values.submittedOn),
          reason: String(values.reason),
        },
        values.file,
      );
  };
  const reconcile = async (item: CfseBill) => {
    const values = await requestWorkflowDialog({
      title:
        locale === "es"
          ? "Conciliar diferencia CFSE"
          : "Reconcile CFSE variance",
      detail: `${item.billReference} · ${locale === "es" ? "diferencia" : "variance"} ${money(item.varianceCents)}`,
      confirmLabel:
        locale === "es" ? "Guardar conciliación" : "Save reconciliation",
      cancelLabel: locale === "es" ? "Cancelar" : "Cancel",
      fields: [
        {
          key: "method",
          label:
            locale === "es"
              ? "Método de conciliación"
              : "Reconciliation method",
          type: "select",
          value: "disputed",
          required: true,
          options: [
            {
              value: "agency_correction",
              label:
                locale === "es"
                  ? "Corrección de la agencia"
                  : "Agency correction",
            },
            {
              value: "employer_accepted_adjustment",
              label:
                locale === "es"
                  ? "Ajuste aceptado por el patrono"
                  : "Employer-accepted adjustment",
            },
            {
              value: "disputed",
              label: locale === "es" ? "En disputa" : "Disputed",
            },
          ],
        },
        {
          key: "reference",
          label:
            locale === "es"
              ? "Referencia de conciliación"
              : "Reconciliation reference",
          required: true,
          minLength: 2,
        },
        {
          key: "resolvedOn",
          label:
            locale === "es" ? "Fecha de conciliación" : "Reconciliation date",
          type: "date",
          value: today,
          required: true,
        },
        {
          key: "reason",
          label:
            locale === "es"
              ? "Explicación completa de la diferencia"
              : "Complete variance explanation",
          type: "textarea",
          required: true,
          minLength: 3,
          fullWidth: true,
        },
      ],
    });
    if (!values) return;
    setBusy(true);
    const response = await api(
      `/v1/tenants/${tenant.id}/cfse/bills/${item.id}/reconcile`,
      {
        method: "POST",
        body: JSON.stringify({
          method: String(values.method),
          reference: String(values.reference),
          resolvedOn: String(values.resolvedOn),
          reason: String(values.reason),
        }),
      },
    );
    if (!response.ok) setError(`reconciliation: ${response.status}`);
    else await load();
    setBusy(false);
  };
  const latest = (id: string) =>
      attempts
        .filter((item) => item.declarationId === id)
        .sort((a, b) => b.attemptNumber - a.attemptNumber)[0],
    decisionFor = (attempt?: CfseAttempt) =>
      attempt
        ? decisions.find((item) => item.submissionAttemptId === attempt.id)
        : undefined;
  return (
    <section className="settings-form">
      <h2>
        {locale === "es"
          ? "Decisiones y conciliación CFSE"
          : "CFSE decisions and reconciliation"}
      </h2>
      <p>
        {locale === "es"
          ? "Conserve cada radicación y decisión de la agencia. Un rechazo exige evidencia y una nueva radicación; una factura diferente al cálculo debe conciliarse antes del pago."
          : "Retain every submission and agency decision. A rejection requires evidence and a new submission; a bill differing from calculation must be reconciled before payment."}
      </p>
      <div className="run-list">
        {declarations
          .filter((item) => item.status !== "prepared")
          .map((item) => {
            const attempt = latest(item.id),
              decision = decisionFor(attempt);
            return (
              <article key={item.id}>
                <div>
                  <strong>
                    {item.policyNumber} ·{" "}
                    {formatDateRange(
                      item.periodStart,
                      item.periodEnd,
                      locale,
                      "—",
                    )}
                  </strong>
                  <small>
                    {attempt
                      ? `${locale === "es" ? "Radicación" : "Submission"} #${attempt.attemptNumber} · ${attempt.submissionReference} · ${formatDate(attempt.submittedOn, locale)}`
                      : "—"}
                    {decision
                      ? ` · ${decision.outcome} · ${decision.agencyReference}`
                      : ""}
                  </small>
                </div>
                <span
                  className={`run-status ${decision?.outcome ?? item.status}`}
                >
                  {optionLabel(decision?.outcome ?? item.status, locale)}
                </span>
                {item.status === "submitted" && !decision && (
                  <>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void accept(item)}
                    >
                      {locale === "es"
                        ? "Registrar aceptación"
                        : "Record acceptance"}
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => reject(item)}
                    >
                      {locale === "es"
                        ? "Registrar rechazo"
                        : "Record rejection"}
                    </button>
                  </>
                )}
                {decision?.outcome === "rejected" && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => resubmit(item)}
                  >
                    {locale === "es"
                      ? "Radicar corrección"
                      : "Resubmit correction"}
                  </button>
                )}
                {decision?.evidenceFileName && (
                  <button
                    type="button"
                    onClick={async () => {
                      const response = await api(
                        `/v1/tenants/${tenant.id}/cfse/evidence/decision/${decision.id}/agency`,
                      );
                      if (response.ok) {
                        const url = URL.createObjectURL(await response.blob());
                        window.open(url, "_blank", "noopener,noreferrer");
                        setTimeout(() => URL.revokeObjectURL(url), 60000);
                      }
                    }}
                  >
                    {locale === "es" ? "Ver decisión" : "View decision"}
                  </button>
                )}
              </article>
            );
          })}
      </div>
      <h3>{locale === "es" ? "Variación de facturas" : "Bill variance"}</h3>
      <div className="run-list">
        {bills.map((item) => (
          <article key={item.id}>
            <div>
              <strong>{item.billReference}</strong>
              <small>
                {locale === "es" ? "Calculado" : "Calculated"}{" "}
                {money(item.calculatedPremiumCents)} ·{" "}
                {locale === "es" ? "Facturado" : "Billed"}{" "}
                {money(item.billedCents)}
                {item.varianceReason ? ` · ${item.varianceReason}` : ""}
              </small>
            </div>
            <span
              className={`run-status ${item.varianceStatus === "review_required" ? "inactive" : "active"}`}
            >
              {item.varianceStatus === "matched"
                ? locale === "es"
                  ? "Coincide"
                  : "Matched"
                : item.varianceStatus === "reconciled"
                  ? locale === "es"
                    ? "Conciliada"
                    : "Reconciled"
                  : `${locale === "es" ? "Revisar" : "Review"} ${money(item.varianceCents)}`}
            </span>
            {item.varianceStatus === "review_required" && (
              <button
                type="button"
                disabled={busy}
                onClick={() => void reconcile(item)}
              >
                {locale === "es"
                  ? "Conciliar diferencia"
                  : "Reconcile variance"}
              </button>
            )}
          </article>
        ))}
      </div>
      <OperationalError value={error} locale={locale} />
    </section>
  );
}

type MobileReleasePolicy = {
  id: string;
  platform: "ios" | "android";
  channel: "development" | "preview" | "production";
  minimumVersion: string;
  recommendedVersion: string;
  updateUrl: string | null;
  releaseNotesEs: string;
  releaseNotesEn: string;
  reason: string;
  status: string;
  createdByDeveloperEmail: string;
  createdAt: string;
};
function MobileReleasePanel({ locale }: { locale: Locale }) {
  const [policies, setPolicies] = useState<MobileReleasePolicy[]>([]),
    [form, setForm] = useState({
      platform: "ios",
      channel: "development",
      minimumVersion: "",
      recommendedVersion: "",
      updateUrl: "",
      releaseNotesEs: "",
      releaseNotesEn: "",
      reason: "",
    }),
    [busy, setBusy] = useState(true),
    [message, setMessage] = useState("");
  const load = async () => {
    setBusy(true);
    const response = await api("/v1/platform/mobile-release-policies");
    if (response.ok)
      setPolicies(
        ((await response.json()) as { policies: MobileReleasePolicy[] })
          .policies,
      );
    setBusy(false);
  };
  useEffect(() => {
    void load();
  }, []);
  const publish = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMessage("");
    const response = await api("/v1/platform/mobile-release-policies", {
      method: "POST",
      body: JSON.stringify({ ...form, updateUrl: form.updateUrl || null }),
    });
    if (response.ok) {
      setMessage(
        locale === "es"
          ? "Política publicada y auditable."
          : "Policy published and audited.",
      );
      setForm({
        ...form,
        minimumVersion: "",
        recommendedVersion: "",
        updateUrl: "",
        releaseNotesEs: "",
        releaseNotesEn: "",
        reason: "",
      });
      await load();
    } else {
      const body = (await response.json().catch(() => null)) as {
        error?: { code?: string };
      } | null;
      setMessage(
        `${locale === "es" ? "No se pudo publicar" : "Unable to publish"}: ${body?.error?.code ?? response.status}`,
      );
    }
    setBusy(false);
  };
  return (
    <section className="settings-form">
      <h2>
        {locale === "es"
          ? "Versiones de la aplicación móvil"
          : "Mobile application versions"}
      </h2>
      <p>
        {locale === "es"
          ? "Solo el desarrollador de plataforma publica una política. La versión mínima bloquea builds obsoletos; la recomendada muestra una actualización descartable. Cada publicación sustituye la anterior sin borrar historial."
          : "Only the platform developer publishes a policy. The minimum version blocks obsolete builds; the recommended version shows a dismissible update. Each publication supersedes the prior one without deleting history."}
      </p>
      <form onSubmit={publish}>
        <div className="form-grid">
          <label>
            {locale === "es" ? "Plataforma" : "Platform"}
            <select
              value={form.platform}
              onChange={(e) =>
                setForm({ ...form, platform: e.currentTarget.value })
              }
            >
              <option value="ios">{optionLabel("ios", locale)}</option>
              <option value="android">{optionLabel("android", locale)}</option>
            </select>
          </label>
          <label>
            {locale === "es" ? "Canal" : "Channel"}
            <select
              value={form.channel}
              onChange={(e) =>
                setForm({ ...form, channel: e.currentTarget.value })
              }
            >
              <option value="development">
                {optionLabel("development", locale)}
              </option>
              <option value="preview">{optionLabel("preview", locale)}</option>
              <option value="production">
                {optionLabel("production", locale)}
              </option>
            </select>
          </label>
          <label>
            {locale === "es" ? "Versión mínima" : "Minimum version"}
            <input
              required
              pattern="[0-9]+\.[0-9]+\.[0-9]+.*"
              placeholder="1.0.0"
              value={form.minimumVersion}
              onChange={(e) =>
                setForm({ ...form, minimumVersion: e.currentTarget.value })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Versión recomendada" : "Recommended version"}
            <input
              required
              pattern="[0-9]+\.[0-9]+\.[0-9]+.*"
              placeholder="1.0.0"
              value={form.recommendedVersion}
              onChange={(e) =>
                setForm({ ...form, recommendedVersion: e.currentTarget.value })
              }
            />
          </label>
          <label>
            {locale === "es"
              ? "Enlace HTTPS de instalación"
              : "HTTPS installation URL"}
            <input
              type="url"
              placeholder="https://…"
              value={form.updateUrl}
              onChange={(e) =>
                setForm({ ...form, updateUrl: e.currentTarget.value })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Razón auditable" : "Auditable reason"}
            <input
              required
              minLength={3}
              value={form.reason}
              onChange={(e) =>
                setForm({ ...form, reason: e.currentTarget.value })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Notas en español" : "Spanish notes"}
            <textarea
              required
              minLength={3}
              value={form.releaseNotesEs}
              onChange={(e) =>
                setForm({ ...form, releaseNotesEs: e.currentTarget.value })
              }
            />
          </label>
          <label>
            {locale === "es" ? "Notas en inglés" : "English notes"}
            <textarea
              required
              minLength={3}
              value={form.releaseNotesEn}
              onChange={(e) =>
                setForm({ ...form, releaseNotesEn: e.currentTarget.value })
              }
            />
          </label>
        </div>
        <button className="primary" disabled={busy}>
          {locale === "es" ? "Publicar política" : "Publish policy"}
        </button>
      </form>
      {message && (
        <p
          className={
            message.includes("public") ? "success-note" : "form-detail"
          }
        >
          {message}
        </p>
      )}
      <div className="run-list">
        {policies.map((item) => (
          <article key={item.id}>
            <div>
              <strong>
                {item.platform} · {item.channel} ·{" "}
                {optionLabel(item.status, locale)}
              </strong>
              <p>
                {locale === "es" ? "Mínima" : "Minimum"} {item.minimumVersion} ·{" "}
                {locale === "es" ? "Recomendada" : "Recommended"}{" "}
                {item.recommendedVersion}
              </p>
              <small>
                {item.reason} · {item.createdByDeveloperEmail}
              </small>
            </div>
            <span>{formatDateTime(item.createdAt, locale)}</span>
          </article>
        ))}
      </div>
    </section>
  );
}

type PrivilegedRecovery = {
  id: string;
  workerName: string;
  status: string;
  requestedAt: string;
  eligibleAt: string;
  expiresAt: string;
  decisionReason: string | null;
  decidedAt: string | null;
  decidedByDeveloperEmail: string | null;
};
function PrivilegedRecoveryPanel({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const [items, setItems] = useState<PrivilegedRecovery[]>([]),
    [busy, setBusy] = useState(true),
    [message, setMessage] = useState(""),
    [draft, setDraft] = useState<{
      item: PrivilegedRecovery;
      decision: "approved" | "rejected";
      reason: string;
    } | null>(null);
  const load = async () => {
    setBusy(true);
    const response = await api(
      `/v1/tenants/${tenant.id}/privileged-recovery-requests`,
    );
    if (response.ok)
      setItems(
        ((await response.json()) as { requests: PrivilegedRecovery[] })
          .requests,
      );
    setBusy(false);
  };
  useEffect(() => {
    void load();
  }, [tenant.id]);
  const decide = (
    item: PrivilegedRecovery,
    decision: "approved" | "rejected",
  ) => setDraft({ item, decision, reason: "" });
  const submitDecision = async (e: FormEvent) => {
    e.preventDefault();
    if (!draft) return;
    setBusy(true);
    setMessage("");
    const response = await api(
      `/v1/tenants/${tenant.id}/privileged-recovery-requests/${draft.item.id}/decision`,
      {
        method: "POST",
        body: JSON.stringify({
          decision: draft.decision,
          reason: draft.reason.trim(),
        }),
      },
    );
    setMessage(
      response.ok
        ? locale === "es"
          ? "Decisión guardada; la cuenta fue notificada."
          : "Decision saved; the account was notified."
        : locale === "es"
          ? "No se pudo decidir: verifique el período de espera, expiración y estado."
          : "Unable to decide: check waiting period, expiry, and status.",
    );
    if (response.ok) setDraft(null);
    await load();
  };
  return (
    <section className="settings-form">
      <h2>
        {locale === "es"
          ? "Recuperación de cuentas privilegiadas"
          : "Privileged account recovery"}
      </h2>
      <p>
        {locale === "es"
          ? "Propietarios y administradores no cambian su contraseña solamente con SMS. Después de verificar el código, la solicitud espera 24 horas y requiere decisión auditable del administrador de plataforma."
          : "Owners and administrators do not replace a password with SMS alone. After code verification, the request waits 24 hours and requires an auditable platform-administrator decision."}
      </p>
      {busy && !draft ? (
        <LoaderCircle className="spin" />
      ) : (
        <div className="run-list">
          {items.map((item) => (
            <article key={item.id}>
              <div>
                <strong>
                  {item.workerName} · {optionLabel(item.status, locale)}
                </strong>
                <p>
                  {locale === "es" ? "Solicitada" : "Requested"}{" "}
                  {formatDateTime(item.requestedAt, locale)} ·{" "}
                  {locale === "es" ? "Elegible" : "Eligible"}{" "}
                  {formatDateTime(item.eligibleAt, locale)}
                </p>
                <small>
                  {locale === "es" ? "Expira" : "Expires"}{" "}
                  {formatDateTime(item.expiresAt, locale)}
                  {item.decisionReason ? ` · ${item.decisionReason}` : ""}
                </small>
              </div>
              {item.status === "pending_review" && (
                <div className="run-actions">
                  <button
                    type="button"
                    disabled={new Date(item.eligibleAt) > new Date()}
                    onClick={() => decide(item, "approved")}
                  >
                    {locale === "es"
                      ? "Aprobar recuperación"
                      : "Approve recovery"}
                  </button>
                  <button
                    type="button"
                    onClick={() => decide(item, "rejected")}
                  >
                    {locale === "es" ? "Rechazar" : "Reject"}
                  </button>
                </div>
              )}
            </article>
          ))}
        </div>
      )}
      {!busy && !items.length && (
        <p>
          {locale === "es"
            ? "No hay solicitudes de recuperación privilegiada."
            : "There are no privileged recovery requests."}
        </p>
      )}
      {message && <p className="form-detail action-result">{message}</p>}
      {draft && (
        <dialog
          className="workflow-dialog"
          open
          aria-labelledby="recovery-decision-title"
        >
          <form onSubmit={submitDecision}>
            <header>
              <div>
                <p className="kicker">
                  {locale === "es"
                    ? "DECISIÓN DE SEGURIDAD AUDITADA"
                    : "AUDITED SECURITY DECISION"}
                </p>
                <h2 id="recovery-decision-title">
                  {draft.decision === "approved"
                    ? locale === "es"
                      ? "Aprobar recuperación privilegiada"
                      : "Approve privileged recovery"
                    : locale === "es"
                      ? "Rechazar recuperación privilegiada"
                      : "Reject privileged recovery"}
                </h2>
                <p>{draft.item.workerName}</p>
              </div>
              <button
                type="button"
                aria-label={locale === "es" ? "Cerrar" : "Close"}
                onClick={() => setDraft(null)}
              >
                ×
              </button>
            </header>
            <p className="form-detail">
              {locale === "es"
                ? `Elegible ${draft.item.eligibleAt} · Expira ${draft.item.expiresAt}`
                : `Eligible ${draft.item.eligibleAt} · Expires ${draft.item.expiresAt}`}
            </p>
            <div className="form-grid">
              <label className="full-width">
                {locale === "es"
                  ? "Razón documentada de la decisión"
                  : "Documented decision reason"}
                <textarea
                  required
                  minLength={3}
                  maxLength={1000}
                  value={draft.reason}
                  onChange={(e) =>
                    setDraft({ ...draft, reason: e.currentTarget.value })
                  }
                />
              </label>
            </div>
            <footer>
              <button type="button" onClick={() => setDraft(null)}>
                {locale === "es" ? "Cancelar" : "Cancel"}
              </button>
              <button
                className={
                  draft.decision === "rejected" ? "danger-action" : "primary"
                }
                disabled={busy || draft.reason.trim().length < 3}
              >
                {busy ? (
                  <LoaderCircle className="spin" />
                ) : locale === "es" ? (
                  "Guardar decisión"
                ) : (
                  "Save decision"
                )}
              </button>
            </footer>
          </form>
        </dialog>
      )}
    </section>
  );
}

type PrivateDocumentIntegrity = {
  referenced: number;
  present: number;
  verified: number;
  missing: number;
  metadataMissing: number;
  hashMismatch: number;
  complete: boolean;
  checkedAt: string;
};
type PrivateDocumentIssue = {
  registry: string;
  resourceId: string;
  status: "missing" | "metadata_missing" | "hash_mismatch";
};
function PrivateDocumentIntegrityPanel({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const [result, setResult] = useState<{
      integrity: PrivateDocumentIntegrity;
      issues: PrivateDocumentIssue[];
    } | null>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const run = async () => {
    setBusy(true);
    setError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/recovery/private-document-integrity`,
    );
    if (response.ok)
      setResult(
        (await response.json()) as {
          integrity: PrivateDocumentIntegrity;
          issues: PrivateDocumentIssue[];
        },
      );
    else
      setError(
        response.status === 409
          ? locale === "es"
            ? "El inventario excede el límite seguro de 500 objetos; ejecute la herramienta de recuperación por lotes."
            : "The inventory exceeds the safe 500-object limit; run the batched recovery tool."
          : `integrity:${response.status}`,
      );
    setBusy(false);
  };
  return (
    <section className="settings-form">
      <h2>
        {locale === "es"
          ? "Integridad de documentos privados"
          : "Private document integrity"}
      </h2>
      <p>
        {locale === "es"
          ? "Comprueba que cada documento registrado en D1 exista en el R2 aislado y que su metadato SHA-256 coincida. No descarga contenido ni muestra claves de almacenamiento o huellas completas."
          : "Checks that every document registered in D1 exists in isolated R2 and that its SHA-256 metadata matches. It downloads no content and exposes neither storage keys nor full hashes."}
      </p>
      <button
        type="button"
        className="primary"
        disabled={busy}
        onClick={() => void run()}
      >
        {busy
          ? locale === "es"
            ? "Verificando…"
            : "Checking…"
          : locale === "es"
            ? "Verificar referencias D1 → R2"
            : "Verify D1 → R2 references"}
      </button>
      <OperationalError value={error} locale={locale} />
      {result && (
        <>
          <div className="balance-grid">
            <article>
              <strong>{locale === "es" ? "Referencias" : "References"}</strong>
              <em>{result.integrity.referenced}</em>
              <span>
                {result.integrity.present}{" "}
                {locale === "es" ? "presentes" : "present"}
              </span>
            </article>
            <article>
              <strong>{locale === "es" ? "Verificadas" : "Verified"}</strong>
              <em>{result.integrity.verified}</em>
              <span>
                {result.integrity.complete
                  ? locale === "es"
                    ? "Completo"
                    : "Complete"
                  : locale === "es"
                    ? "Requiere atención"
                    : "Needs attention"}
              </span>
            </article>
            <article>
              <strong>{locale === "es" ? "Ausentes" : "Missing"}</strong>
              <em>{result.integrity.missing}</em>
              <span>
                {result.integrity.metadataMissing}{" "}
                {locale === "es" ? "sin metadato" : "without metadata"}
              </span>
            </article>
            <article>
              <strong>
                {locale === "es" ? "Huellas distintas" : "Hash mismatches"}
              </strong>
              <em>{result.integrity.hashMismatch}</em>
              <span>{result.integrity.checkedAt}</span>
            </article>
          </div>
          {result.issues.length > 0 && (
            <div className="run-list">
              {result.issues.map((item) => (
                <article key={`${item.registry}-${item.resourceId}`}>
                  <div>
                    <strong>{optionLabel(item.registry, locale)}</strong>
                    <small>
                      {locale === "es"
                        ? "Identificador opaco"
                        : "Opaque identifier"}
                      : {item.resourceId}
                    </small>
                  </div>
                  <span className="run-status draft">
                    {optionLabel(item.status, locale)}
                  </span>
                </article>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function LiabilityCreditsPanel({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const [credits, setCredits] = useState<LiabilityCredit[]>([]),
    [busy, setBusy] = useState(true),
    [error, setError] = useState(""),
    [draft, setDraft] = useState<{
      item: LiabilityCredit;
      status: "applied" | "refunded";
      resolutionReference: string;
      reason: string;
    } | null>(null);
  const formatMoney = (cents: number) =>
    new Intl.NumberFormat(locale === "es" ? "es-PR" : "en-US", {
      style: "currency",
      currency: "USD",
    }).format(cents / 100);
  const load = async () => {
    setBusy(true);
    const response = await api(`/v1/tenants/${tenant.id}/liabilities`);
    if (response.ok)
      setCredits(
        ((await response.json()) as { credits: LiabilityCredit[] }).credits ??
          [],
      );
    else setError(`liability-credits:${response.status}`);
    setBusy(false);
  };
  useEffect(() => {
    void load();
  }, [tenant.id]);
  const resolve = (item: LiabilityCredit, status: "applied" | "refunded") =>
    setDraft({ item, status, resolutionReference: "", reason: "" });
  const submitResolution = async (e: FormEvent) => {
    e.preventDefault();
    if (!draft) return;
    setBusy(true);
    setError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/liability-credits/${draft.item.id}/resolve`,
      {
        method: "POST",
        body: JSON.stringify({
          status: draft.status,
          resolutionReference: draft.resolutionReference.trim(),
          reason: draft.reason.trim(),
        }),
      },
    );
    if (response.ok) setDraft(null);
    else setError(`liability-credit:${response.status}`);
    await load();
  };
  return (
    <section className="settings-form">
      <h2>
        {locale === "es"
          ? "Créditos de obligaciones por reversión"
          : "Reversal liability credits"}
      </h2>
      <p>
        {locale === "es"
          ? "Una reversión conserva la obligación original y crea un crédito separado. Registre si la agencia o beneficiario lo aplicó a otra obligación o lo reembolsó; nunca se presenta como una obligación negativa por pagar."
          : "A reversal retains the original liability and creates a separate credit. Record whether the agency or payee applied it to another obligation or refunded it; it is never presented as a negative payable."}
      </p>
      {busy && !draft ? (
        <LoaderCircle className="spin" />
      ) : (
        <div className="run-list">
          {credits.map((item) => (
            <article key={item.id}>
              <div>
                <strong>
                  {optionLabel(item.liabilityKey, locale)} ·{" "}
                  {formatMoney(item.amountCents)}
                </strong>
                <p>
                  {locale === "es" ? "Reversión" : "Reversal"}{" "}
                  {item.reversalPayrollRunId.slice(0, 8)} ·{" "}
                  {locale === "es" ? "obligación original" : "source liability"}{" "}
                  {item.sourceLiabilityId.slice(0, 8)}
                </p>
                <small>
                  {item.resolutionReference ??
                    formatDateTime(item.createdAt, locale)}
                  {item.resolutionReason ? ` · ${item.resolutionReason}` : ""}
                </small>
              </div>
              <span className={`run-status ${item.status}`}>
                {optionLabel(item.status, locale)}
              </span>
              {item.status === "open" && (
                <div className="run-actions">
                  <button
                    type="button"
                    onClick={() => resolve(item, "applied")}
                  >
                    {locale === "es"
                      ? "Registrar aplicación"
                      : "Record application"}
                  </button>
                  <button
                    type="button"
                    onClick={() => resolve(item, "refunded")}
                  >
                    {locale === "es" ? "Registrar reembolso" : "Record refund"}
                  </button>
                </div>
              )}
            </article>
          ))}
        </div>
      )}
      {!busy && !credits.length && (
        <p>
          {locale === "es"
            ? "No hay créditos de reversión."
            : "There are no reversal credits."}
        </p>
      )}
      <OperationalError value={error} locale={locale} />
      {draft && (
        <dialog
          className="workflow-dialog"
          open
          aria-labelledby="liability-credit-title"
        >
          <form onSubmit={submitResolution}>
            <header>
              <div>
                <p className="kicker">
                  {locale === "es"
                    ? "RESOLUCIÓN FINANCIERA AUDITADA"
                    : "AUDITED FINANCIAL RESOLUTION"}
                </p>
                <h2 id="liability-credit-title">
                  {draft.status === "applied"
                    ? locale === "es"
                      ? "Registrar aplicación del crédito"
                      : "Record credit application"
                    : locale === "es"
                      ? "Registrar reembolso del crédito"
                      : "Record credit refund"}
                </h2>
                <p>
                  {optionLabel(draft.item.liabilityKey, locale)} ·{" "}
                  {formatMoney(draft.item.amountCents)}
                </p>
              </div>
              <button
                type="button"
                aria-label={locale === "es" ? "Cerrar" : "Close"}
                onClick={() => setDraft(null)}
              >
                ×
              </button>
            </header>
            <p className="form-detail">
              {locale === "es"
                ? `Obligación original ${draft.item.sourceLiabilityId.slice(0, 8)} · Reversión ${draft.item.reversalPayrollRunId.slice(0, 8)}`
                : `Source liability ${draft.item.sourceLiabilityId.slice(0, 8)} · Reversal ${draft.item.reversalPayrollRunId.slice(0, 8)}`}
            </p>
            <div className="form-grid">
              <label>
                {locale === "es"
                  ? "Referencia de resolución"
                  : "Resolution reference"}
                <input
                  required
                  minLength={2}
                  maxLength={160}
                  value={draft.resolutionReference}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      resolutionReference: e.currentTarget.value,
                    })
                  }
                />
              </label>
              <label className="full-width">
                {locale === "es" ? "Razón y evidencia" : "Reason and evidence"}
                <textarea
                  required
                  minLength={3}
                  maxLength={1000}
                  value={draft.reason}
                  onChange={(e) =>
                    setDraft({ ...draft, reason: e.currentTarget.value })
                  }
                />
              </label>
            </div>
            <footer>
              <button type="button" onClick={() => setDraft(null)}>
                {locale === "es" ? "Cancelar" : "Cancel"}
              </button>
              <button
                className="primary"
                disabled={
                  busy ||
                  draft.resolutionReference.trim().length < 2 ||
                  draft.reason.trim().length < 3
                }
              >
                {busy ? (
                  <LoaderCircle className="spin" />
                ) : locale === "es" ? (
                  "Guardar resolución"
                ) : (
                  "Save resolution"
                )}
              </button>
            </footer>
          </form>
        </dialog>
      )}
    </section>
  );
}

type TenantAuditEvent = {
  id: string;
  occurredAt: string;
  action: string;
  resourceType: string;
  resourceId: string;
  reason: string | null;
  actorLabel: string;
  source: "client" | "platform";
};
function AuditLogPanel({ locale, tenant }: { locale: Locale; tenant: Tenant }) {
  const [events, setEvents] = useState<TenantAuditEvent[]>([]),
    [nextBefore, setNextBefore] = useState<string | null>(null),
    [busy, setBusy] = useState(true),
    [error, setError] = useState("");
  const load = async (before?: string) => {
    setBusy(true);
    setError("");
    const query = new URLSearchParams({
        limit: "50",
        ...(before ? { before } : {}),
      }),
      response = await api(`/v1/tenants/${tenant.id}/audit-events?${query}`);
    if (response.ok) {
      const body = (await response.json()) as {
        events: TenantAuditEvent[];
        nextBefore: string | null;
      };
      setEvents((current) =>
        before ? [...current, ...body.events] : body.events,
      );
      setNextBefore(body.nextBefore);
    } else setError(`audit:${response.status}`);
    setBusy(false);
  };
  useEffect(() => {
    void load();
  }, [tenant.id]);
  return (
    <section className="settings-form">
      <h2>{locale === "es" ? "Registro de auditoría" : "Audit log"}</h2>
      <p>
        {locale === "es"
          ? "Vista exclusiva del propietario. Muestra quién actuó, qué recurso cambió y cuándo; los datos contributivos sensibles no se presentan aquí."
          : "Owner-only view showing who acted, which resource changed, and when; sensitive taxpayer payloads are not exposed here."}
      </p>
      {(events.length > 0 || !busy) && (
        <ResponsiveDataTable
          label={
            locale === "es"
              ? "Tabla del registro de auditoría"
              : "Audit log table"
          }
          caption={
            locale === "es"
              ? "Acción, recurso, actor, origen, razón y fecha de cada evento retenido"
              : "Action, resource, actor, source, reason, and date for each retained event"
          }
          columns={
            locale === "es"
              ? ["Acción", "Recurso", "Actor y razón", "Origen", "Fecha"]
              : ["Action", "Resource", "Actor and reason", "Source", "Date"]
          }
          rows={events.map((event) => ({
            key: `${event.source}-${event.id}`,
            cells: [
              <strong>{event.action}</strong>,
              <>
                <span>{event.resourceType}</span>
                <small>{event.resourceId}</small>
              </>,
              <>
                <span>{event.actorLabel}</span>
                {event.reason && <small>{event.reason}</small>}
              </>,
              <span className="run-status">
                {event.source === "client"
                  ? locale === "es"
                    ? "Cliente"
                    : "Client"
                  : locale === "es"
                    ? "Plataforma"
                    : "Platform"}
              </span>,
              formatDateTime(event.occurredAt, locale),
            ],
            card: (
              <article>
                <div>
                  <strong>{event.action}</strong>
                  <small>{formatDateTime(event.occurredAt, locale)}</small>
                </div>
                <p>
                  {event.resourceType} · {event.resourceId}
                </p>
                <p>
                  {event.actorLabel}
                  {event.reason ? ` · ${event.reason}` : ""}
                </p>
                <span className="run-status">
                  {event.source === "client"
                    ? locale === "es"
                      ? "Cliente"
                      : "Client"
                    : locale === "es"
                      ? "Plataforma"
                      : "Platform"}
                </span>
              </article>
            ),
          }))}
          empty={
            locale === "es"
              ? "No hay eventos registrados."
              : "No audit events recorded."
          }
        />
      )}
      {nextBefore && (
        <button
          type="button"
          disabled={busy}
          onClick={() => void load(nextBefore)}
        >
          {locale === "es" ? "Cargar anteriores" : "Load earlier"}
        </button>
      )}
      {busy && <LoaderCircle className="spin" />}
      <OperationalError value={error} locale={locale} />
    </section>
  );
}

type OperatingModel = {
  id?: string;
  effectiveFrom?: string;
  reason?: string;
  createdAt?: string;
  modelKey:
    | "restaurant"
    | "landscaping"
    | "print_shop"
    | "office"
    | "professional_services"
    | "custom";
  requireSchedule: boolean;
  requireProjectOnWorkEvents: boolean;
  requireVehicleOnWorkEvents: boolean;
  requireMealTracking: boolean;
  contractorFirst: boolean;
};
function OperatingModelPanel({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const [settings, setSettings] = useState<OperatingModel | null>(null),
    [presets, setPresets] = useState<Record<string, OperatingModel>>({}),
    [history, setHistory] = useState<OperatingModel[]>([]),
    [effectiveFrom, setEffectiveFrom] = useState(""),
    [reason, setReason] = useState(""),
    [busy, setBusy] = useState(true),
    [saved, setSaved] = useState(false),
    [error, setError] = useState("");
  useEffect(() => {
    void api(`/v1/tenants/${tenant.id}/operating-model`).then(
      async (response) => {
        if (response.ok) {
          const body = (await response.json()) as {
            settings: OperatingModel;
            presets: Record<string, OperatingModel>;
            history: OperatingModel[];
            today: string;
          };
          setSettings(body.settings);
          setPresets(body.presets);
          setHistory(body.history);
          setEffectiveFrom(body.today);
        } else setError(`operating-model:${response.status}`);
        setBusy(false);
      },
    );
  }, [tenant.id]);
  const select = (key: string) => {
    const preset = presets[key];
    if (key === "custom" && settings)
      setSettings({ ...settings, modelKey: "custom" });
    else if (preset) setSettings(preset);
  };
  const save = async () => {
    if (!settings) return;
    setBusy(true);
    setSaved(false);
    const response = await api(`/v1/tenants/${tenant.id}/operating-model`, {
      method: "PUT",
      body: JSON.stringify({
        modelKey: settings.modelKey,
        requireSchedule: settings.requireSchedule,
        requireProjectOnWorkEvents: settings.requireProjectOnWorkEvents,
        requireVehicleOnWorkEvents: settings.requireVehicleOnWorkEvents,
        requireMealTracking: settings.requireMealTracking,
        contractorFirst: settings.contractorFirst,
        effectiveFrom,
        reason,
      }),
    });
    if (response.ok) {
      const body = (await response.json()) as { version: OperatingModel };
      setHistory((current) => [body.version, ...current]);
      setReason("");
      setSaved(true);
    } else setError(`operating-model:${response.status}`);
    setBusy(false);
  };
  if (!settings)
    return (
      <section className="settings-form">
        <h2>{locale === "es" ? "Modelo operacional" : "Operating model"}</h2>
        {busy ? (
          <LoaderCircle className="spin" />
        ) : (
          <OperationalError value={error} locale={locale} />
        )}
      </section>
    );
  const toggle =
    (key: keyof OperatingModel) => (event: ChangeEvent<HTMLInputElement>) =>
      setSettings({
        ...settings,
        modelKey: "custom",
        [key]: event.target.checked,
      });
  return (
    <section className="settings-form">
      <h2>{locale === "es" ? "Modelo operacional" : "Operating model"}</h2>
      <p>
        {locale === "es"
          ? "El modelo controla requisitos reales de horario, proyecto, vehículo, alimentos y servicios profesionales al registrar trabajo."
          : "The model controls actual schedule, project, vehicle, meal, and professional-services requirements when work is recorded."}
      </p>
      <label>
        {locale === "es" ? "Escenario" : "Scenario"}
        <select
          value={settings.modelKey}
          onChange={(e) => select(e.currentTarget.value)}
        >
          <option value="restaurant">
            {optionLabel("restaurant", locale)}
          </option>
          <option value="landscaping">
            {optionLabel("landscaping", locale)}
          </option>
          <option value="print_shop">
            {optionLabel("print_shop", locale)}
          </option>
          <option value="office">{optionLabel("office", locale)}</option>
          <option value="professional_services">
            {optionLabel("professional_services", locale)}
          </option>
          <option value="custom">{optionLabel("custom", locale)}</option>
        </select>
      </label>
      <div className="form-grid">
        <label className="check-label">
          <input
            type="checkbox"
            checked={settings.requireSchedule}
            onChange={toggle("requireSchedule")}
          />
          {locale === "es"
            ? "Exigir horario vigente para ponches"
            : "Require an effective schedule for punches"}
        </label>
        <label className="check-label">
          <input
            type="checkbox"
            checked={settings.requireProjectOnWorkEvents}
            onChange={toggle("requireProjectOnWorkEvents")}
          />
          {locale === "es"
            ? "Exigir proyecto en eventos de trabajo"
            : "Require project on work events"}
        </label>
        <label className="check-label">
          <input
            type="checkbox"
            checked={settings.requireVehicleOnWorkEvents}
            onChange={toggle("requireVehicleOnWorkEvents")}
          />
          {locale === "es"
            ? "Exigir vehículo en eventos de trabajo"
            : "Require vehicle on work events"}
        </label>
        <label className="check-label">
          <input
            type="checkbox"
            checked={settings.requireMealTracking}
            onChange={toggle("requireMealTracking")}
          />
          {locale === "es"
            ? "Controlar períodos de alimentos"
            : "Track meal periods"}
        </label>
        <label className="check-label">
          <input
            type="checkbox"
            checked={settings.contractorFirst}
            onChange={toggle("contractorFirst")}
          />
          {locale === "es"
            ? "Priorizar servicios profesionales"
            : "Prioritize professional-services workflows"}
        </label>
      </div>
      <div className="form-grid">
        <label>
          {locale === "es" ? "Vigente desde" : "Effective from"}
          <input
            type="date"
            value={effectiveFrom}
            onInput={(event) => setEffectiveFrom(event.currentTarget.value)}
          />
        </label>
        <label>
          {locale === "es" ? "Razón del cambio" : "Reason for change"}
          <input
            value={reason}
            maxLength={500}
            onChange={(event) => setReason(event.currentTarget.value)}
          />
        </label>
      </div>
      <button
        type="button"
        className="primary"
        disabled={busy || !effectiveFrom || reason.trim().length < 3}
        onClick={() => void save()}
      >
        {locale === "es" ? "Guardar modelo" : "Save model"}
      </button>
      {saved && (
        <p className="success-note">
          {locale === "es"
            ? "Modelo guardado y activo."
            : "Model saved and active."}
        </p>
      )}
      <OperationalError value={error} locale={locale} />
      <h3>{locale === "es" ? "Historial inmutable" : "Immutable history"}</h3>
      <div className="run-list">
        {history.map((version) => (
          <article
            key={version.id ?? `${version.effectiveFrom}-${version.modelKey}`}
          >
            <div>
              <strong>{optionLabel(version.modelKey, locale)}</strong>
              <small>{version.reason}</small>
            </div>
            <span>
              {locale === "es" ? "Vigente desde" : "Effective from"}:{" "}
              {formatDate(version.effectiveFrom, locale)}
            </span>
          </article>
        ))}
      </div>
    </section>
  );
}

type NfcItem = {
  id: string;
  tagTechnology: string;
  status: string;
  punchPointName: string | null;
  punchPointType: string | null;
  locationId: string | null;
  locationName: string | null;
  vehicleId?: string | null;
  sessionId: string | null;
  sessionStatus: string | null;
  sessionExpiresAt: string | null;
  createdAt: string;
};
type NfcLocation = {
  id: string;
  name: string;
  description: string | null;
  status: "active" | "inactive";
  createdAt: string;
  updatedAt: string;
};
type NfcLocationHistory = {
  id: string;
  inventoryId: string;
  fromLocationName: string | null;
  toLocationName: string | null;
  effectiveAt: string;
  reason: string;
  changedBy: string | null;
};
type NfcReader = {
  write: (message: {
    records: { recordType: "text"; data: string }[];
  }) => Promise<void>;
  scan: () => Promise<void>;
  addEventListener: (
    type: "reading" | "readingerror",
    listener: (event: {
      message?: { records: { recordType: string; data: DataView }[] };
    }) => void,
    options?: { once?: boolean },
  ) => void;
};
function NfcManagement({ locale, tenant }: { locale: Locale; tenant: Tenant }) {
  const [items, setItems] = useState<NfcItem[]>([]),
    [locations, setLocations] = useState<NfcLocation[]>([]),
    [locationHistory, setLocationHistory] = useState<NfcLocationHistory[]>([]),
    [locationForm, setLocationForm] = useState({ name: "", description: "" }),
    [assignment, setAssignment] = useState({
      inventoryId: "",
      locationId: "",
      reason: "",
    }),
    [technology, setTechnology] = useState("ntag213"),
    [selected, setSelected] = useState(""),
    [session, setSession] = useState(""),
    [payload, setPayload] = useState(""),
    [readback, setReadback] = useState(""),
    [verified, setVerified] = useState(false),
    [disposition, setDisposition] = useState("active"),
    [activationLocationId, setActivationLocationId] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const load = async () => {
    const [inventoryResponse, locationResponse] = await Promise.all([
      api(`/v1/tenants/${tenant.id}/nfc/inventory`),
      api(`/v1/tenants/${tenant.id}/nfc/locations`),
    ]);
    if (inventoryResponse.ok)
      setItems(
        ((await inventoryResponse.json()) as { inventory: NfcItem[] })
          .inventory,
      );
    if (locationResponse.ok) {
      const body = (await locationResponse.json()) as {
        locations: NfcLocation[];
        history: NfcLocationHistory[];
      };
      setLocations(body.locations);
      setLocationHistory(body.history);
    }
  };
  const createLocation = async () => {
    setBusy(true);
    setError("");
    const response = await api(`/v1/tenants/${tenant.id}/nfc/locations`, {
      method: "POST",
      body: JSON.stringify({
        name: locationForm.name,
        description: locationForm.description || null,
      }),
    });
    if (response.ok) setLocationForm({ name: "", description: "" });
    else setError(`location:${response.status}`);
    await load();
    setBusy(false);
  };
  const assignLocation = async () => {
    setBusy(true);
    setError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/nfc/inventory/${assignment.inventoryId}/location`,
      {
        method: "PUT",
        body: JSON.stringify({
          locationId: assignment.locationId || null,
          reason: assignment.reason,
        }),
      },
    );
    if (response.ok)
      setAssignment({ inventoryId: "", locationId: "", reason: "" });
    else setError(`assignment:${response.status}`);
    await load();
    setBusy(false);
  };
  useEffect(() => {
    void load();
  }, [tenant.id]);
  const create = async () => {
    setBusy(true);
    setError("");
    const response = await api(`/v1/tenants/${tenant.id}/nfc/inventory`, {
      method: "POST",
      body: JSON.stringify({
        tagTechnology: technology,
        acquiredOn: null,
        sku: null,
        purchaseBatch: null,
        cost: null,
        notes: null,
        replacementOfId: null,
      }),
    });
    if (!response.ok) setError(`inventory:${response.status}`);
    await load();
    setBusy(false);
  };
  const generate = async () => {
    setBusy(true);
    setError("");
    const response = await api(`/v1/tenants/${tenant.id}/nfc/programming`, {
      method: "POST",
      body: JSON.stringify({ inventoryId: selected }),
    });
    if (response.ok) {
      const body = (await response.json()) as {
        programming: { id: string; payload: string };
      };
      setSession(body.programming.id);
      setPayload(body.programming.payload);
      setReadback("");
      setVerified(false);
      await load();
    } else setError(`generate:${response.status}`);
    setBusy(false);
  };
  const write = async () => {
    setError("");
    const Constructor = (
      window as unknown as { NDEFReader?: new () => NfcReader }
    ).NDEFReader;
    if (!Constructor) {
      setError(
        locale === "es"
          ? "Este navegador no admite escritura Web NFC. Use una aplicación NFC para escribir el texto y péguelo abajo para verificar."
          : "This browser does not support Web NFC writing. Use an NFC writer app, then paste the read-back text below.",
      );
      return;
    }
    try {
      await new Constructor().write({
        records: [{ recordType: "text", data: payload }],
      });
    } catch {
      setError(
        locale === "es"
          ? "No se pudo escribir. Acerque una etiqueta desbloqueada e inténtelo otra vez."
          : "Writing failed. Hold an unlocked tag nearby and retry.",
      );
    }
  };
  const scan = async () => {
    setError("");
    const Constructor = (
      window as unknown as { NDEFReader?: new () => NfcReader }
    ).NDEFReader;
    if (!Constructor) {
      setError(
        locale === "es"
          ? "Este navegador no admite lectura Web NFC. Lea la etiqueta con otra aplicación y pegue el texto."
          : "This browser does not support Web NFC reading. Read the tag with another app and paste the text.",
      );
      return;
    }
    try {
      const reader = new Constructor();
      reader.addEventListener(
        "reading",
        (event) => {
          const values = (event.message?.records ?? []).map((record) => {
            const bytes = new Uint8Array(
              record.data.buffer,
              record.data.byteOffset,
              record.data.byteLength,
            );
            return new TextDecoder().decode(bytes);
          });
          const match = values
            .join("\n")
            .match(/prpayroll:nfc:v1:[0-9a-f-]{36}:[A-Za-z0-9_-]{32,}/);
          if (match) setReadback(match[0]);
          else
            setError(
              locale === "es"
                ? "La etiqueta se leyó, pero no contiene un identificador válido de Jornada PR."
                : "The tag was read, but it does not contain a valid Jornada PR identifier.",
            );
        },
        { once: true },
      );
      reader.addEventListener(
        "readingerror",
        () =>
          setError(
            locale === "es"
              ? "El teléfono detectó la etiqueta, pero no pudo leer su registro NDEF."
              : "The phone detected the tag but could not read its NDEF record.",
          ),
        { once: true },
      );
      await reader.scan();
    } catch {
      setError(
        locale === "es"
          ? "No se pudo iniciar la lectura NFC."
          : "Could not start NFC scanning.",
      );
    }
  };
  const verify = async () => {
    setBusy(true);
    setError("");
    const activeSession =
      session ||
      items.find(
        (item) => item.id === selected && item.sessionStatus === "generated",
      )?.sessionId ||
      "";
    const response = await api(
      activeSession
        ? `/v1/tenants/${tenant.id}/nfc/programming/${activeSession}/verify`
        : `/v1/tenants/${tenant.id}/nfc/programming/verify`,
      { method: "POST", body: JSON.stringify({ payload: readback }) },
    );
    if (response.ok) {
      setSession(activeSession);
      setVerified(true);
      await load();
    } else setError(`verify:${response.status}`);
    setBusy(false);
  };
  const activate = async () => {
    setBusy(true);
    setError("");
    const activeSession =
      session ||
      items.find(
        (item) =>
          item.id === selected && item.sessionStatus === "readback_verified",
      )?.sessionId ||
      "";
    const response = await api(
      `/v1/tenants/${tenant.id}/nfc/programming/${activeSession}/activate`,
      {
        method: "POST",
        body: JSON.stringify({
          disposition,
          locationId: disposition === "active" ? activationLocationId : null,
        }),
      },
    );
    if (response.ok) {
      setPayload("");
      setReadback("");
      setSession("");
      setSelected("");
      setVerified(false);
      setActivationLocationId("");
      await load();
    } else setError(`activate:${response.status}`);
    setBusy(false);
  };
  const selectedNfcItem = items.find((item) => item.id === selected);
  const activationReady =
    verified || selectedNfcItem?.sessionStatus === "readback_verified";
  return (
    <section className="settings-form">
      <h2>NFC</h2>
      <p>
        {locale === "es"
          ? "Cree ubicaciones con el nombre que necesite, programe etiquetas y asígnelas a una ubicación. La etiqueta contiene solo un identificador opaco y nunca guarda datos personales."
          : "Create any named location you need, program tags, and assign them to a location. Tags contain only an opaque identifier and never store personal data."}
      </p>
      <h3>{locale === "es" ? "Ubicaciones NFC" : "NFC locations"}</h3>
      <div className="form-grid">
        <label>
          {locale === "es" ? "Nombre de la ubicación" : "Location name"}
          <input
            value={locationForm.name}
            onChange={(e) =>
              setLocationForm({ ...locationForm, name: e.currentTarget.value })
            }
            placeholder={
              locale === "es"
                ? "Ej. Entrada principal, almacén o estación 1"
                : "E.g. Main entrance, warehouse, or station 1"
            }
          />
        </label>
        <label>
          {locale === "es"
            ? "Descripción (opcional)"
            : "Description (optional)"}
          <input
            value={locationForm.description}
            onChange={(e) =>
              setLocationForm({
                ...locationForm,
                description: e.currentTarget.value,
              })
            }
          />
        </label>
      </div>
      <button
        type="button"
        disabled={busy || locationForm.name.trim().length < 2}
        onClick={() => void createLocation()}
      >
        {locale === "es" ? "Crear ubicación NFC" : "Create NFC location"}
      </button>
      <div className="run-list">
        {locations.map((location) => (
          <article key={location.id}>
            <div>
              <strong>{location.name}</strong>
              <small>
                {location.description ||
                  (locale === "es" ? "Sin descripción" : "No description")}
              </small>
            </div>
          </article>
        ))}
      </div>
      <h3>{locale === "es" ? "Programar etiqueta NFC" : "Program NFC tag"}</h3>
      <div className="form-grid">
        <label>
          {locale === "es" ? "Tecnología de etiqueta" : "Tag technology"}
          <select
            value={technology}
            onChange={(e) => setTechnology(e.currentTarget.value)}
          >
            <option value="ntag213">NTAG213</option>
            <option value="ntag215">NTAG215</option>
            <option value="ntag216">NTAG216</option>
            <option value="other">{optionLabel("other", locale)}</option>
          </select>
        </label>
        <label>
          {locale === "es" ? "Añadir al inventario" : "Add to inventory"}
          <button type="button" disabled={busy} onClick={() => void create()}>
            {locale === "es" ? "Crear registro" : "Create record"}
          </button>
        </label>
        <label>
          {locale === "es" ? "Etiqueta pendiente" : "Pending tag"}
          <select
            value={selected}
            onChange={(e) => setSelected(e.currentTarget.value)}
          >
            <option value="">—</option>
            {items
              .filter(
                (i) =>
                  i.status === "unprogrammed" ||
                  (i.status === "programmed" &&
                    i.sessionStatus === "readback_verified"),
              )
              .map((i) => (
                <option key={i.id} value={i.id}>
                  {i.tagTechnology} · {i.id.slice(0, 8)}
                </option>
              ))}
          </select>
        </label>
        <label>
          {locale === "es"
            ? "Generar identificador seguro"
            : "Generate secure identifier"}
          <button
            type="button"
            disabled={busy || selectedNfcItem?.status !== "unprogrammed"}
            onClick={() => void generate()}
          >
            {items.find(
              (item) => item.id === selected && item.sessionStatus === "generated",
            )
              ? locale === "es"
                ? "Reiniciar y generar"
                : "Restart and generate"
              : locale === "es"
                ? "Generar"
                : "Generate"}
          </button>
        </label>
      </div>
      {items.some(
        (item) => item.id === selected && item.sessionStatus === "generated",
      ) &&
        !payload && (
          <p className="form-help">
            {locale === "es"
              ? "Esta etiqueta tiene una sesión anterior sin completar. Reiniciarla cancelará de forma auditable el identificador perdido y mostrará uno nuevo."
              : "This tag has an unfinished earlier session. Restarting will cancel the lost identifier with an audit trail and display a new one."}
          </p>
        )}
      {(payload ||
        selectedNfcItem?.status === "unprogrammed" ||
        activationReady) && (
        <section className="operation-panel">
          {!activationReady && (
            <>
              <h3>
                {payload
                  ? locale === "es"
                    ? "1. Escribir y leer la etiqueta"
                    : "1. Write and read the tag"
                  : locale === "es"
                    ? "Continuar verificación de lectura"
                    : "Resume read-back verification"}
              </h3>
              {payload && (
                <>
                  <button type="button" onClick={() => void write()}>
                    {locale === "es"
                      ? "Escribir mediante Web NFC"
                      : "Write with Web NFC"}
                  </button>
                  <label>
                    {locale === "es"
                      ? "Texto para escribir con otra aplicación NFC"
                      : "Text to write with another NFC app"}
                    <textarea value={payload} readOnly />
                  </label>
                </>
              )}
              <label>
                {locale === "es"
                  ? "Texto leído de la etiqueta"
                  : "Text read from tag"}
                <textarea
                  value={readback}
                  onChange={(e) => setReadback(e.currentTarget.value)}
                />
              </label>
              <button type="button" onClick={() => void scan()}>
                {locale === "es" ? "Leer mediante Web NFC" : "Read with Web NFC"}
              </button>
              <button type="button" disabled={busy || !readback} onClick={() => void verify()}>
                {locale === "es" ? "Verificar lectura" : "Verify read-back"}
              </button>
            </>
          )}
          {activationReady && (
            <>
              <h3>
                {locale === "es"
                  ? "2. Activar o guardar"
                  : "2. Activate or store"}
              </h3>
              <div className="form-grid">
                <label>
                  {locale === "es" ? "Destino" : "Disposition"}
                  <select
                    value={disposition}
                    onChange={(e) => setDisposition(e.currentTarget.value)}
                  >
                    <option value="active">
                      {locale === "es"
                        ? "Activar punto"
                        : "Activate punch point"}
                    </option>
                    <option value="inventory">
                      {locale === "es"
                        ? "Guardar en inventario"
                        : "Store in inventory"}
                    </option>
                  </select>
                </label>
                {disposition === "active" && (
                  <>
                    <label>
                      {locale === "es" ? "Ubicación NFC" : "NFC location"}
                      <select
                        value={activationLocationId}
                        onChange={(e) =>
                          setActivationLocationId(e.currentTarget.value)
                        }
                      >
                        <option value="">—</option>
                        {locations
                          .filter((item) => item.status === "active")
                          .map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.name}
                            </option>
                          ))}
                      </select>
                    </label>
                  </>
                )}
              </div>
              <button
                type="button"
                className="primary"
                disabled={
                  busy || (disposition === "active" && !activationLocationId)
                }
                onClick={() => void activate()}
              >
                {locale === "es" ? "Confirmar" : "Confirm"}
              </button>
            </>
          )}
        </section>
      )}
      <div className="run-list">
        {items.map((item) => (
          <article key={item.id}>
            <div>
              <strong>
                {item.locationName ??
                  `${item.tagTechnology} · ${item.id.slice(0, 8)}`}
              </strong>
              <small>{item.id}</small>
            </div>
            <span className="run-status">
              {optionLabel(item.status, locale)}
            </span>
          </article>
        ))}
      </div>
      <h3>{locale === "es" ? "Ubicación de etiquetas" : "Tag locations"}</h3>
      <p>
        {locale === "es"
          ? "Use este formulario para asignar una etiqueta programada, cambiarla a otra ubicación o devolverla al inventario. Cada cambio conserva quién lo realizó, cuándo y por qué."
          : "Assign a programmed tag, move it to another location, or return it to inventory. Every change preserves who changed it, when, and why."}
      </p>
      <div className="form-grid">
        <label>
          {locale === "es" ? "Etiqueta programada" : "Programmed tag"}
          <select
            value={assignment.inventoryId}
            onChange={(e) =>
              setAssignment({
                ...assignment,
                inventoryId: e.currentTarget.value,
              })
            }
          >
            <option value="">—</option>
            {items
              .filter((item) => item.status !== "unprogrammed")
              .map((item) => (
                <option key={item.id} value={item.id}>
                  {item.locationName ?? item.tagTechnology} ·{" "}
                  {item.id.slice(0, 8)}
                </option>
              ))}
          </select>
        </label>
        <label>
          {locale === "es"
            ? "Nueva ubicación (vacío para inventario)"
            : "New location (blank for inventory)"}
          <select
            value={assignment.locationId}
            onChange={(e) =>
              setAssignment({
                ...assignment,
                locationId: e.currentTarget.value,
              })
            }
          >
            <option value="">
              {locale === "es" ? "Inventario" : "Inventory"}
            </option>
            {locations
              .filter((item) => item.status === "active")
              .map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
          </select>
        </label>
        <label>
          {locale === "es" ? "Razón del cambio" : "Change reason"}
          <input
            value={assignment.reason}
            onChange={(e) =>
              setAssignment({ ...assignment, reason: e.currentTarget.value })
            }
          />
        </label>
      </div>
      <button
        type="button"
        disabled={
          busy || !assignment.inventoryId || assignment.reason.trim().length < 3
        }
        onClick={() => void assignLocation()}
      >
        {locale === "es"
          ? "Guardar ubicación de etiqueta"
          : "Save tag location"}
      </button>
      {locationHistory.length > 0 && (
        <details>
          <summary>
            {locale === "es"
              ? `Historial de ubicaciones (${locationHistory.length})`
              : `Location history (${locationHistory.length})`}
          </summary>
          <div className="run-list">
            {locationHistory.map((item) => (
              <article key={item.id}>
                <div>
                  <strong>
                    {item.fromLocationName ??
                      (locale === "es" ? "Inventario" : "Inventory")}{" "}
                    →{" "}
                    {item.toLocationName ??
                      (locale === "es" ? "Inventario" : "Inventory")}
                  </strong>
                  <small>
                    {item.reason} · {item.changedBy ?? "—"}
                  </small>
                </div>
                <span>{formatDateTime(item.effectiveAt, locale)}</span>
              </article>
            ))}
          </div>
        </details>
      )}
      <OperationalError value={error} locale={locale} />
    </section>
  );
}

type OrgItem = { id: string; name: string; active: number; code?: string };
type OrgAssignment = {
  id: string;
  workerId: string;
  workerName: string;
  departmentId: string | null;
  departmentName: string | null;
  locationId: string | null;
  locationName: string | null;
  costCenterId: string | null;
  costCenterCode: string | null;
  positionId: string | null;
  positionName: string | null;
  supervisorWorkerId: string | null;
  supervisorName: string | null;
  effectiveFrom: string;
  effectiveTo: string | null;
};
function OrganizationPanel({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const today = new Date().toISOString().slice(0, 10),
    [workers, setWorkers] = useState<Worker[]>([]),
    [departments, setDepartments] = useState<OrgItem[]>([]),
    [locations, setLocations] = useState<OrgItem[]>([]),
    [costCenters, setCostCenters] = useState<OrgItem[]>([]),
    [assignments, setAssignments] = useState<OrgAssignment[]>([]),
    [busy, setBusy] = useState(true),
    [error, setError] = useState(""),
    [department, setDepartment] = useState(""),
    [location, setLocation] = useState(""),
    [cost, setCost] = useState({ code: "", name: "" }),
    [assignment, setAssignment] = useState({
      workerId: "",
      departmentId: "",
      locationId: "",
      costCenterId: "",
      supervisorWorkerId: "",
      effectiveFrom: today,
      effectiveTo: "",
    });
  const load = async () => {
    setBusy(true);
    const [org, people] = await Promise.all([
      api(`/v1/tenants/${tenant.id}/organization`),
      api(`/v1/tenants/${tenant.id}/workers`),
    ]);
    if (org.ok) {
      const data = (await org.json()) as {
        departments: OrgItem[];
        locations: OrgItem[];
        costCenters: OrgItem[];
        assignments: OrgAssignment[];
      };
      setDepartments(data.departments);
      setLocations(data.locations);
      setCostCenters(data.costCenters);
      setAssignments(data.assignments);
    }
    if (people.ok)
      setWorkers(((await people.json()) as { workers: Worker[] }).workers);
    setBusy(false);
  };
  useEffect(() => {
    void load();
  }, [tenant.id]);
  const post = async (path: string, body: unknown) => {
    setBusy(true);
    setError("");
    const response = await api(`/v1/tenants/${tenant.id}/${path}`, {
      method: "POST",
      body: JSON.stringify(body),
    });
    if (!response.ok) setError(`${path}:${response.status}`);
    await load();
    return response.ok;
  };
  const addDepartment = async () => {
      if (await post("departments", { name: department, active: true }))
        setDepartment("");
    },
    addLocation = async () => {
      if (
        await post("locations", { name: location, address: null, active: true })
      )
        setLocation("");
    },
    addCost = async () => {
      if (await post("cost-centers", { ...cost, active: true }))
        setCost({ code: "", name: "" });
    },
    assign = async () => {
      if (
        await post("worker-assignments", {
          workerId: assignment.workerId,
          departmentId: assignment.departmentId || null,
          locationId: assignment.locationId || null,
          costCenterId: assignment.costCenterId || null,
          supervisorWorkerId: assignment.supervisorWorkerId || null,
          effectiveFrom: assignment.effectiveFrom,
          effectiveTo: assignment.effectiveTo || null,
        })
      )
        setAssignment({
          ...assignment,
          workerId: "",
          departmentId: "",
          locationId: "",
          costCenterId: "",
          supervisorWorkerId: "",
        });
    },
    updateEntity = async (kind: string, item: OrgItem, payload: unknown) => {
      setBusy(true);
      setError("");
      const response = await api(
        `/v1/tenants/${tenant.id}/${kind}/${item.id}`,
        { method: "PUT", body: JSON.stringify(payload) },
      );
      if (!response.ok) setError(`${kind}:${response.status}`);
      await load();
    };
  return (
    <section className="settings-form">
      <h2>
        {locale === "es"
          ? "Estructura organizacional"
          : "Organization structure"}
      </h2>
      <p>
        {locale === "es"
          ? "Cree departamentos, ubicaciones y centros de costo; luego asigne la estructura de cada persona con fecha de vigencia."
          : "Create departments, locations, and cost centers, then assign each person's structure with an effective date."}
      </p>
      {busy ? (
        <LoaderCircle className="spin" />
      ) : (
        <>
          <div className="form-grid">
            <label>
              {locale === "es" ? "Nuevo departamento" : "New department"}
              <input
                value={department}
                onChange={(e) => setDepartment(e.currentTarget.value)}
              />
              <button
                type="button"
                disabled={!department}
                onClick={() => void addDepartment()}
              >
                {locale === "es" ? "Crear" : "Create"}
              </button>
            </label>
            <label>
              {locale === "es" ? "Nueva ubicación" : "New location"}
              <input
                value={location}
                onChange={(e) => setLocation(e.currentTarget.value)}
              />
              <button
                type="button"
                disabled={!location}
                onClick={() => void addLocation()}
              >
                {locale === "es" ? "Crear" : "Create"}
              </button>
            </label>
            <label>
              {locale === "es"
                ? "Código del centro de costo"
                : "Cost-center code"}
              <input
                value={cost.code}
                onChange={(e) =>
                  setCost({ ...cost, code: e.currentTarget.value })
                }
              />
            </label>
            <label>
              {locale === "es"
                ? "Nombre del centro de costo"
                : "Cost-center name"}
              <input
                value={cost.name}
                onChange={(e) =>
                  setCost({ ...cost, name: e.currentTarget.value })
                }
              />
              <button
                type="button"
                disabled={!cost.code || !cost.name}
                onClick={() => void addCost()}
              >
                {locale === "es" ? "Crear" : "Create"}
              </button>
            </label>
          </div>
          <OrgEntityLists
            locale={locale}
            departments={departments}
            locations={locations}
            costCenters={costCenters}
            update={updateEntity}
          />
          <h3>
            {locale === "es" ? "Asignación de persona" : "Worker assignment"}
          </h3>
          <div className="form-grid">
            <label>
              {locale === "es" ? "Persona" : "Worker"}
              <select
                value={assignment.workerId}
                onChange={(e) =>
                  setAssignment({
                    ...assignment,
                    workerId: e.currentTarget.value,
                  })
                }
              >
                <option value="">—</option>
                {workers
                  .filter((w) => !["inactive", "terminated"].includes(w.status))
                  .map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.firstName} {w.lastName}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              {locale === "es" ? "Departamento" : "Department"}
              <select
                value={assignment.departmentId}
                onChange={(e) =>
                  setAssignment({
                    ...assignment,
                    departmentId: e.currentTarget.value,
                  })
                }
              >
                <option value="">—</option>
                {departments
                  .filter((i) => i.active)
                  .map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              {locale === "es" ? "Ubicación principal" : "Home location"}
              <select
                value={assignment.locationId}
                onChange={(e) =>
                  setAssignment({
                    ...assignment,
                    locationId: e.currentTarget.value,
                  })
                }
              >
                <option value="">—</option>
                {locations
                  .filter((i) => i.active)
                  .map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              {locale === "es" ? "Centro de costo" : "Cost center"}
              <select
                value={assignment.costCenterId}
                onChange={(e) =>
                  setAssignment({
                    ...assignment,
                    costCenterId: e.currentTarget.value,
                  })
                }
              >
                <option value="">—</option>
                {costCenters
                  .filter((i) => i.active)
                  .map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.code} · {i.name}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              {locale === "es" ? "Supervisor" : "Supervisor"}
              <select
                value={assignment.supervisorWorkerId}
                onChange={(e) =>
                  setAssignment({
                    ...assignment,
                    supervisorWorkerId: e.currentTarget.value,
                  })
                }
              >
                <option value="">—</option>
                {workers
                  .filter(
                    (w) =>
                      w.roleKey === "supervisor" ||
                      w.roleKey === "administrator" ||
                      w.roleKey === "owner",
                  )
                  .map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.firstName} {w.lastName}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              {locale === "es" ? "Vigente desde" : "Effective from"}
              <input
                type="date"
                value={assignment.effectiveFrom}
                onInput={(e) =>
                  setAssignment({
                    ...assignment,
                    effectiveFrom: e.currentTarget.value,
                  })
                }
              />
            </label>
            <label>
              {locale === "es"
                ? "Vigente hasta (opcional)"
                : "Effective to (optional)"}
              <input
                type="date"
                value={assignment.effectiveTo}
                onInput={(e) =>
                  setAssignment({
                    ...assignment,
                    effectiveTo: e.currentTarget.value,
                  })
                }
              />
            </label>
          </div>
          <button
            type="button"
            className="primary"
            disabled={
              !assignment.workerId ||
              (!assignment.departmentId &&
                !assignment.locationId &&
                !assignment.costCenterId &&
                !assignment.supervisorWorkerId)
            }
            onClick={() => void assign()}
          >
            {locale === "es" ? "Guardar asignación" : "Save assignment"}
          </button>
          <div className="run-list">
            {assignments.map((item) => (
              <article key={item.id}>
                <div>
                  <strong>{item.workerName}</strong>
                  <p>
                    {item.departmentName ?? "—"} · {item.locationName ?? "—"} ·{" "}
                    {item.costCenterCode ?? "—"}
                  </p>
                  <small>
                    {locale === "es" ? "Supervisor" : "Supervisor"}:{" "}
                    {item.supervisorName ?? "—"}
                  </small>
                </div>
                <span>
                  {formatDateRange(
                    item.effectiveFrom,
                    item.effectiveTo,
                    locale,
                    locale === "es" ? "vigente" : "current",
                  )}
                </span>
              </article>
            ))}
          </div>
        </>
      )}
      <OperationalError value={error} locale={locale} />
    </section>
  );
}

function OrgEntityLists({
  locale,
  departments,
  locations,
  costCenters,
  update,
}: {
  locale: Locale;
  departments: OrgItem[];
  locations: OrgItem[];
  costCenters: OrgItem[];
  update: (kind: string, item: OrgItem, payload: unknown) => Promise<void>;
}) {
  const edit = async (kind: string, item: OrgItem) => {
    const values = await requestWorkflowDialog({
      title:
        locale === "es"
          ? "Editar estructura organizacional"
          : "Edit organization structure",
      detail: item.name,
      confirmLabel: locale === "es" ? "Guardar cambios" : "Save changes",
      cancelLabel: locale === "es" ? "Cancelar" : "Cancel",
      fields: [
        {
          key: "name",
          label: locale === "es" ? "Nombre" : "Name",
          value: item.name,
          required: true,
          minLength: 2,
        },
        ...(kind === "cost-centers"
          ? [
              {
                key: "code",
                label: locale === "es" ? "Código" : "Code",
                value: item.code ?? "",
                required: true,
                minLength: 1,
              },
            ]
          : []),
      ],
    });
    if (!values) return;
    const name = String(values.name).trim(),
      code = kind === "cost-centers" ? String(values.code).trim() : item.code;
    if (!name || (kind === "cost-centers" && !code)) return;
    await update(kind, item, {
      name,
      ...(code ? { code } : {}),
      ...(kind === "locations" ? { address: null } : {}),
      active: Boolean(item.active),
    });
  };
  const toggle = async (kind: string, item: OrgItem) => {
    await update(kind, item, {
      name: item.name,
      ...(item.code ? { code: item.code } : {}),
      ...(kind === "locations" ? { address: null } : {}),
      active: !item.active,
    });
  };
  const group = (title: string, kind: string, items: OrgItem[]) => (
    <div>
      <h3>{title}</h3>
      <div className="run-list">
        {items.map((item) => (
          <article key={item.id}>
            <div>
              <strong>
                {item.code ? `${item.code} · ` : ""}
                {item.name}
              </strong>
              <small>
                {item.active
                  ? locale === "es"
                    ? "Activo"
                    : "Active"
                  : locale === "es"
                    ? "Inactivo"
                    : "Inactive"}
              </small>
            </div>
            <div className="run-actions">
              <button type="button" onClick={() => void edit(kind, item)}>
                {locale === "es" ? "Editar" : "Edit"}
              </button>
              <button type="button" onClick={() => void toggle(kind, item)}>
                {item.active
                  ? locale === "es"
                    ? "Desactivar"
                    : "Deactivate"
                  : locale === "es"
                    ? "Reactivar"
                    : "Reactivate"}
              </button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
  return (
    <div className="setup-grid">
      {group(
        locale === "es" ? "Departamentos" : "Departments",
        "departments",
        departments,
      )}
      {group(
        locale === "es" ? "Ubicaciones" : "Locations",
        "locations",
        locations,
      )}
      {group(
        locale === "es" ? "Centros de costo" : "Cost centers",
        "cost-centers",
        costCenters,
      )}
    </div>
  );
}

function PositionManagement({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const today = new Date().toISOString().slice(0, 10),
    [positions, setPositions] = useState<OrgItem[]>([]),
    [assignments, setAssignments] = useState<OrgAssignment[]>([]),
    [workers, setWorkers] = useState<Worker[]>([]),
    [name, setName] = useState(""),
    [workerId, setWorkerId] = useState(""),
    [positionId, setPositionId] = useState(""),
    [effectiveFrom, setEffectiveFrom] = useState(today),
    [busy, setBusy] = useState(true),
    [error, setError] = useState("");
  const load = async () => {
    setBusy(true);
    const [orgResponse, workerResponse] = await Promise.all([
      api(`/v1/tenants/${tenant.id}/organization`),
      api(`/v1/tenants/${tenant.id}/workers`),
    ]);
    if (orgResponse.ok) {
      const body = (await orgResponse.json()) as {
        positions: OrgItem[];
        assignments: OrgAssignment[];
      };
      setPositions(body.positions);
      setAssignments(body.assignments);
    }
    if (workerResponse.ok)
      setWorkers(
        ((await workerResponse.json()) as { workers: Worker[] }).workers,
      );
    setBusy(false);
  };
  useEffect(() => {
    void load();
  }, [tenant.id]);
  const post = async (path: string, body: unknown, method = "POST") => {
    setBusy(true);
    setError("");
    const response = await api(`/v1/tenants/${tenant.id}/${path}`, {
      method,
      body: JSON.stringify(body),
    });
    if (!response.ok) setError(`${path}: ${response.status}`);
    await load();
    return response.ok;
  };
  const create = async () => {
    if (await post("positions", { name, active: true })) setName("");
  };
  const edit = async (item: OrgItem) => {
    const values = await requestWorkflowDialog({
      title: locale === "es" ? "Editar puesto" : "Edit position",
      detail: item.name,
      confirmLabel: locale === "es" ? "Guardar puesto" : "Save position",
      cancelLabel: locale === "es" ? "Cancelar" : "Cancel",
      fields: [
        {
          key: "name",
          label: locale === "es" ? "Nombre del puesto" : "Position name",
          value: item.name,
          required: true,
          minLength: 2,
        },
      ],
    });
    if (!values) return;
    await post(
      `positions/${item.id}`,
      { name: String(values.name), active: Boolean(item.active) },
      "PUT",
    );
  };
  const toggle = async (item: OrgItem) => {
    await post(
      `positions/${item.id}`,
      { name: item.name, active: !item.active },
      "PUT",
    );
  };
  const assign = async () => {
    const current = assignments.find(
      (item) =>
        item.workerId === workerId &&
        item.effectiveFrom <= effectiveFrom &&
        (!item.effectiveTo || item.effectiveTo >= effectiveFrom),
    );
    if (
      await post("worker-assignments", {
        workerId,
        departmentId: current?.departmentId ?? null,
        locationId: current?.locationId ?? null,
        costCenterId: current?.costCenterId ?? null,
        positionId,
        supervisorWorkerId: current?.supervisorWorkerId ?? null,
        effectiveFrom,
        effectiveTo: null,
      })
    ) {
      setWorkerId("");
      setPositionId("");
    }
  };
  return (
    <section className="settings-form">
      <h2>
        {locale === "es"
          ? "Puestos y clasificación organizacional"
          : "Positions and organization classification"}
      </h2>
      <p>
        {locale === "es"
          ? "Mantenga puestos efectivos sin borrar el departamento, ubicación, centro de costo o supervisor vigente de la persona."
          : "Maintain effective positions without erasing the worker's current department, location, cost center, or supervisor."}
      </p>
      {busy ? (
        <LoaderCircle className="spin" />
      ) : (
        <>
          <div className="form-grid">
            <label>
              {locale === "es" ? "Nuevo puesto" : "New position"}
              <input
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
              />
            </label>
            <button
              type="button"
              disabled={!name.trim()}
              onClick={() => void create()}
            >
              {locale === "es" ? "Crear puesto" : "Create position"}
            </button>
          </div>
          <div className="run-list">
            {positions.map((item) => (
              <article key={item.id}>
                <strong>{item.name}</strong>
                <span
                  className={`run-status ${item.active ? "active" : "inactive"}`}
                >
                  {item.active
                    ? locale === "es"
                      ? "Activo"
                      : "Active"
                    : locale === "es"
                      ? "Inactivo"
                      : "Inactive"}
                </span>
                <div className="run-actions">
                  <button type="button" onClick={() => void edit(item)}>
                    {locale === "es" ? "Editar" : "Edit"}
                  </button>
                  <button type="button" onClick={() => void toggle(item)}>
                    {item.active
                      ? locale === "es"
                        ? "Desactivar"
                        : "Deactivate"
                      : locale === "es"
                        ? "Reactivar"
                        : "Reactivate"}
                  </button>
                </div>
              </article>
            ))}
          </div>
          <h3>
            {locale === "es"
              ? "Asignar puesto efectivo"
              : "Assign effective position"}
          </h3>
          <div className="form-grid">
            <label>
              {locale === "es" ? "Persona" : "Person"}
              <select
                value={workerId}
                onChange={(e) => setWorkerId(e.currentTarget.value)}
              >
                <option value="">—</option>
                {workers
                  .filter(
                    (item) => !["inactive", "terminated"].includes(item.status),
                  )
                  .map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.firstName} {item.lastName}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              {locale === "es" ? "Puesto" : "Position"}
              <select
                value={positionId}
                onChange={(e) => setPositionId(e.currentTarget.value)}
              >
                <option value="">—</option>
                {positions
                  .filter((item) => item.active)
                  .map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              {locale === "es" ? "Vigente desde" : "Effective from"}
              <input
                type="date"
                value={effectiveFrom}
                onInput={(e) => setEffectiveFrom(e.currentTarget.value)}
              />
            </label>
          </div>
          <button
            type="button"
            className="primary"
            disabled={!workerId || !positionId}
            onClick={() => void assign()}
          >
            {locale === "es" ? "Guardar puesto" : "Save position"}
          </button>
          <div className="run-list">
            {assignments
              .filter((item) => item.positionId)
              .map((item) => (
                <article key={item.id}>
                  <div>
                    <strong>
                      {item.workerName} · {item.positionName}
                    </strong>
                    <small>
                      {item.departmentName ?? "—"} · {item.locationName ?? "—"}{" "}
                      · {item.costCenterCode ?? "—"}
                    </small>
                  </div>
                  <span>
                    {formatDateRange(
                      item.effectiveFrom,
                      item.effectiveTo,
                      locale,
                      locale === "es" ? "vigente" : "current",
                    )}
                  </span>
                </article>
              ))}
          </div>
        </>
      )}
      <OperationalError value={error} locale={locale} />
    </section>
  );
}

function LaborAllocationPanel({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const today = new Date().toISOString().slice(0, 10),
    [from, setFrom] = useState(`${today.slice(0, 4)}-01-01`),
    [to, setTo] = useState(today),
    [rows, setRows] = useState<ReportSummary["laborAllocations"]>([]),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const load = async () => {
    setBusy(true);
    setError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/reports/summary?${new URLSearchParams({ from, to })}`,
    );
    if (response.ok)
      setRows(((await response.json()) as ReportSummary).laborAllocations);
    else setError(`report: ${response.status}`);
    setBusy(false);
  };
  return (
    <section className="settings-form">
      <h2>
        {locale === "es"
          ? "Costo laboral por asignación cargada"
          : "Labor cost by charged allocation"}
      </h2>
      <p>
        {locale === "es"
          ? "Distribuye exactamente cada nómina finalizada por los minutos registrados en departamento, ubicación y centro de costo; impuestos y cargas patronales conservan el total de la nómina."
          : "Exactly distributes each finalized payroll by recorded department, location, and cost-center minutes; employer taxes and burden retain the payroll total."}
      </p>
      <div className="report-toolbar">
        <label>
          {locale === "es" ? "Desde" : "From"}
          <input
            type="date"
            value={from}
            onInput={(e) => setFrom(e.currentTarget.value)}
          />
        </label>
        <label>
          {locale === "es" ? "Hasta" : "To"}
          <input
            type="date"
            value={to}
            onInput={(e) => setTo(e.currentTarget.value)}
          />
        </label>
        <button
          type="button"
          className="primary"
          disabled={busy}
          onClick={() => void load()}
        >
          {locale === "es" ? "Generar informe" : "Generate report"}
        </button>
      </div>
      {busy ? (
        <LoaderCircle className="spin" />
      ) : (
        <div className="run-list">
          {rows.map((item, index) => (
            <article
              key={`${item.departmentName}-${item.locationName}-${item.costCenterName}-${index}`}
            >
              <div>
                <strong>
                  {item.departmentName ??
                    (locale === "es"
                      ? "Sin departamento"
                      : "No department")}{" "}
                  ·{" "}
                  {item.locationName ??
                    (locale === "es" ? "Sin ubicación" : "No location")}{" "}
                  ·{" "}
                  {item.costCenterName ??
                    (locale === "es" ? "Sin centro" : "No cost center")}
                </strong>
                <p>
                  {(item.hoursHundredths / 100).toFixed(2)} h ·{" "}
                  {locale === "es" ? "Bruto" : "Gross"} {money(item.grossCents)}{" "}
                  ·{" "}
                  {locale === "es" ? "Impuestos patronales" : "Employer taxes"}{" "}
                  {money(item.employerTaxesCents)}
                </p>
                <small>
                  {locale === "es" ? "Beneficios" : "Benefits"}{" "}
                  {money(item.employerBenefitsCents)} ·{" "}
                  {locale === "es" ? "Otras cargas" : "Other burden"}{" "}
                  {money(item.otherBurdenCents)}
                </small>
              </div>
              <span>
                {locale === "es" ? "Costo total" : "Total cost"}:{" "}
                {money(item.totalEmployerCostCents)}
              </span>
            </article>
          ))}
        </div>
      )}
      {!busy && !rows.length && !error && (
        <p>
          {locale === "es"
            ? "Genere el informe para el período seleccionado."
            : "Generate the report for the selected period."}
        </p>
      )}
      <OperationalError value={error} locale={locale} />
    </section>
  );
}

type JobCostInput = {
  id: string;
  projectId?: string;
  projectName?: string;
  vehicleId?: string;
  vehicleUnit?: string;
  effectiveOn?: string;
  incurredOn?: string;
  laborBudgetCents?: number;
  revenueCents?: number;
  category?: string;
  amountCents?: number;
  description?: string;
  reason?: string;
};
type JobCostDimension = {
  name: string;
  code: string | null;
  status: string | null;
  hoursHundredths: number;
  grossCents: number;
  employerTaxesCents: number;
  employerBenefitsCents: number;
  otherBurdenCents: number;
  totalLaborCostCents: number;
};
type JobCostReport = {
  projects: (JobCostDimension & {
    projectId: string | null;
    projectCode: string | null;
    projectName: string;
    laborBudgetCents: number | null;
    revenueCents: number | null;
    otherActualCents: number;
    totalActualCents: number;
    remainingLaborBudgetCents: number | null;
    marginCents: number | null;
  })[];
  vehicles: (JobCostDimension & {
    vehicleId: string | null;
    unitNumber: string;
    operatingCostCents: number;
    totalCostCents: number;
  })[];
  departments: (JobCostDimension & { departmentId: string | null })[];
  locations: (JobCostDimension & { locationId: string | null })[];
  costCenters: (JobCostDimension & { costCenterId: string | null })[];
  positions: (JobCostDimension & { positionId: string | null })[];
  workers: (JobCostDimension & { workerId: string | null })[];
};
function OrganizationLaborCosting({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const today = new Date().toISOString().slice(0, 10),
    [from, setFrom] = useState(`${today.slice(0, 4)}-01-01`),
    [to, setTo] = useState(today),
    [report, setReport] = useState<JobCostReport | null>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const run = async () => {
      setBusy(true);
      setError("");
      const response = await api(
        `/v1/tenants/${tenant.id}/reports/job-costing?${new URLSearchParams({ from, to })}`,
      );
      if (response.ok) setReport((await response.json()) as JobCostReport);
      else setError(`organization-costing:${response.status}`);
      setBusy(false);
    },
    money = (cents: number) =>
      new Intl.NumberFormat(locale === "es" ? "es-PR" : "en-US", {
        style: "currency",
        currency: "USD",
      }).format(cents / 100),
    dimensions = [
      { key: "departments", es: "Departamentos", en: "Departments" },
      { key: "locations", es: "Localidades", en: "Locations" },
      { key: "costCenters", es: "Centros de costo", en: "Cost centers" },
      { key: "positions", es: "Puestos", en: "Positions" },
      { key: "workers", es: "Personas", en: "Workers" },
    ] as const;
  return (
    <section className="settings-form">
      <h2>
        {locale === "es"
          ? "Costo laboral por estructura organizacional"
          : "Labor cost by organizational structure"}
      </h2>
      <p>
        {locale === "es"
          ? "Distribuye cada centavo de bruto, contribuciones patronales, beneficios, otras cargas y costo laboral total según los minutos finalizados cargados a cada dimensión."
          : "Allocates every cent of gross pay, employer taxes, benefits, other burden, and total labor cost from finalized minutes to every dimension."}
      </p>
      <div className="report-toolbar">
        <label>
          {locale === "es" ? "Desde" : "From"}
          <input
            type="date"
            value={from}
            onInput={(e) => setFrom(e.currentTarget.value)}
          />
        </label>
        <label>
          {locale === "es" ? "Hasta" : "To"}
          <input
            type="date"
            value={to}
            onInput={(e) => setTo(e.currentTarget.value)}
          />
        </label>
        <button
          type="button"
          className="primary"
          disabled={busy}
          onClick={() => void run()}
        >
          {locale === "es" ? "Calcular dimensiones" : "Calculate dimensions"}
        </button>
      </div>
      {report &&
        dimensions.map((dimension) => (
          <section key={dimension.key}>
            <h3>{locale === "es" ? dimension.es : dimension.en}</h3>
            <div className="run-list">
              {report[dimension.key].map((item, index) => (
                <article
                  key={`${dimension.key}-${item.code ?? item.name}-${index}`}
                >
                  <div>
                    <strong>
                      {item.code ? `${item.code} · ` : ""}
                      {item.name}
                    </strong>
                    <p>
                      {(item.hoursHundredths / 100).toFixed(2)} h ·{" "}
                      {locale === "es" ? "Bruto" : "Gross"}{" "}
                      {money(item.grossCents)}
                    </p>
                    <small>
                      {locale === "es"
                        ? "Contribuciones patronales"
                        : "Employer taxes"}{" "}
                      {money(item.employerTaxesCents)} ·{" "}
                      {locale === "es" ? "Beneficios" : "Benefits"}{" "}
                      {money(item.employerBenefitsCents)} ·{" "}
                      {locale === "es" ? "Otras cargas" : "Other burden"}{" "}
                      {money(item.otherBurdenCents)}
                    </small>
                  </div>
                  <span>
                    {locale === "es"
                      ? "Costo laboral total"
                      : "Total labor cost"}
                    : {money(item.totalLaborCostCents)}
                  </span>
                </article>
              ))}
            </div>
          </section>
        ))}
      <OperationalError value={error} locale={locale} />
    </section>
  );
}
function ProjectVehicleCosting({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const today = new Date().toISOString().slice(0, 10),
    [projects, setProjects] = useState<Project[]>([]),
    [vehicles, setVehicles] = useState<Vehicle[]>([]),
    [plans, setPlans] = useState<JobCostInput[]>([]),
    [projectCosts, setProjectCosts] = useState<JobCostInput[]>([]),
    [vehicleCosts, setVehicleCosts] = useState<JobCostInput[]>([]),
    [projectId, setProjectId] = useState(""),
    [vehicleId, setVehicleId] = useState(""),
    [laborBudget, setLaborBudget] = useState(""),
    [revenue, setRevenue] = useState(""),
    [amount, setAmount] = useState(""),
    [category, setCategory] = useState("materials"),
    [description, setDescription] = useState(""),
    [from, setFrom] = useState(`${today.slice(0, 4)}-01-01`),
    [to, setTo] = useState(today),
    [report, setReport] = useState<JobCostReport | null>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const load = async () => {
    const [projectResponse, vehicleResponse, inputResponse] = await Promise.all(
      [
        api(`/v1/tenants/${tenant.id}/projects`),
        api(`/v1/tenants/${tenant.id}/vehicles`),
        api(`/v1/tenants/${tenant.id}/job-costing/inputs`),
      ],
    );
    if (projectResponse.ok)
      setProjects(
        ((await projectResponse.json()) as { projects: Project[] }).projects,
      );
    if (vehicleResponse.ok)
      setVehicles(
        ((await vehicleResponse.json()) as { vehicles: Vehicle[] }).vehicles,
      );
    if (inputResponse.ok) {
      const body = (await inputResponse.json()) as {
        plans: JobCostInput[];
        projectCosts: JobCostInput[];
        vehicleCosts: JobCostInput[];
      };
      setPlans(body.plans);
      setProjectCosts(body.projectCosts);
      setVehicleCosts(body.vehicleCosts);
    }
  };
  useEffect(() => {
    void load();
  }, [tenant.id]);
  const post = async (path: string, body: unknown) => {
    setBusy(true);
    setError("");
    const response = await api(`/v1/tenants/${tenant.id}/job-costing/${path}`, {
      method: "POST",
      body: JSON.stringify(body),
    });
    if (!response.ok) setError(`${path}: ${response.status}`);
    else {
      setAmount("");
      setDescription("");
      await load();
    }
    setBusy(false);
  };
  const plan = () =>
    post("project-plans", {
      projectId,
      effectiveOn: today,
      laborBudgetCents: Math.round(Number(laborBudget) * 100),
      revenueCents: Math.round(Number(revenue) * 100),
      reason:
        locale === "es"
          ? "Plan financiero aprobado"
          : "Approved financial plan",
    });
  const cost = () =>
    post("project-costs", {
      projectId,
      incurredOn: today,
      category,
      amountCents: Math.round(Number(amount) * 100),
      description,
    });
  const vehicleCost = () =>
    post("vehicle-costs", {
      vehicleId,
      incurredOn: today,
      category: [
        "fuel",
        "maintenance",
        "repair",
        "insurance",
        "lease",
        "registration",
        "tolls",
        "other",
      ].includes(category)
        ? category
        : "other",
      amountCents: Math.round(Number(amount) * 100),
      description,
    });
  const run = async () => {
    setBusy(true);
    const response = await api(
      `/v1/tenants/${tenant.id}/reports/job-costing?${new URLSearchParams({ from, to })}`,
    );
    if (response.ok) setReport((await response.json()) as JobCostReport);
    else setError(`report: ${response.status}`);
    setBusy(false);
  };
  return (
    <section className="settings-form">
      <h2>
        {locale === "es"
          ? "Costeo de proyectos y vehículos"
          : "Project and vehicle costing"}
      </h2>
      <p>
        {locale === "es"
          ? "Planes y costos reales son históricos e inmutables. El informe distribuye exactamente la nómina finalizada por los minutos trabajados."
          : "Plans and actual costs are immutable history. The report exactly distributes finalized payroll by worked minutes."}
      </p>
      <div className="form-grid">
        <label>
          {locale === "es" ? "Proyecto" : "Project"}
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.currentTarget.value)}
          >
            <option value="">—</option>
            {projects
              .filter((x) => x.status === "active")
              .map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
          </select>
        </label>
        <label>
          {locale === "es" ? "Presupuesto laboral" : "Labor budget"}
          <input
            type="number"
            min="0"
            step="0.01"
            value={laborBudget}
            onInput={(e) => setLaborBudget(e.currentTarget.value)}
          />
        </label>
        <label>
          {locale === "es" ? "Ingreso contratado" : "Contract revenue"}
          <input
            type="number"
            min="0"
            step="0.01"
            value={revenue}
            onInput={(e) => setRevenue(e.currentTarget.value)}
          />
        </label>
        <button
          type="button"
          disabled={busy || !projectId || laborBudget === "" || revenue === ""}
          onClick={() => void plan()}
        >
          {locale === "es" ? "Guardar revisión del plan" : "Save plan revision"}
        </button>
        <label>
          {locale === "es" ? "Vehículo" : "Vehicle"}
          <select
            value={vehicleId}
            onChange={(e) => setVehicleId(e.currentTarget.value)}
          >
            <option value="">—</option>
            {vehicles
              .filter((x) => x.status === "active")
              .map((x) => (
                <option key={x.id} value={x.id}>
                  {x.unitNumber}
                </option>
              ))}
          </select>
        </label>
        <label>
          {locale === "es" ? "Categoría de costo" : "Cost category"}
          <select
            value={category}
            onChange={(e) => setCategory(e.currentTarget.value)}
          >
            <option value="materials">
              {optionLabel("materials", locale)}
            </option>
            <option value="equipment">
              {optionLabel("equipment", locale)}
            </option>
            <option value="subcontractor">
              {optionLabel("subcontractor", locale)}
            </option>
            <option value="travel">{optionLabel("travel", locale)}</option>
            <option value="permit">{optionLabel("permit", locale)}</option>
            <option value="fuel">{optionLabel("fuel", locale)}</option>
            <option value="maintenance">
              {optionLabel("maintenance", locale)}
            </option>
            <option value="repair">{optionLabel("repair", locale)}</option>
            <option value="insurance">
              {optionLabel("insurance", locale)}
            </option>
            <option value="other">{optionLabel("other", locale)}</option>
          </select>
        </label>
        <label>
          {locale === "es" ? "Costo real" : "Actual cost"}
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={amount}
            onInput={(e) => setAmount(e.currentTarget.value)}
          />
        </label>
        <label>
          {locale === "es" ? "Descripción" : "Description"}
          <input
            minLength={3}
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
          />
        </label>
        <button
          type="button"
          disabled={busy || !projectId || !amount || description.length < 3}
          onClick={() => void cost()}
        >
          {locale === "es"
            ? "Registrar costo del proyecto"
            : "Record project cost"}
        </button>
        <button
          type="button"
          disabled={busy || !vehicleId || !amount || description.length < 3}
          onClick={() => void vehicleCost()}
        >
          {locale === "es"
            ? "Registrar costo del vehículo"
            : "Record vehicle cost"}
        </button>
      </div>
      <small>
        {locale === "es" ? "Historial" : "History"}: {plans.length}{" "}
        {locale === "es" ? "planes" : "plans"} · {projectCosts.length}{" "}
        {locale === "es" ? "costos de proyecto" : "project costs"} ·{" "}
        {vehicleCosts.length}{" "}
        {locale === "es" ? "costos de vehículo" : "vehicle costs"}
      </small>
      <div className="report-toolbar">
        <label>
          {locale === "es" ? "Desde" : "From"}
          <input
            type="date"
            value={from}
            onInput={(e) => setFrom(e.currentTarget.value)}
          />
        </label>
        <label>
          {locale === "es" ? "Hasta" : "To"}
          <input
            type="date"
            value={to}
            onInput={(e) => setTo(e.currentTarget.value)}
          />
        </label>
        <button
          type="button"
          className="primary"
          disabled={busy}
          onClick={() => void run()}
        >
          {locale === "es" ? "Calcular costeo" : "Calculate costing"}
        </button>
      </div>
      {report && (
        <>
          <h3>{locale === "es" ? "Proyectos" : "Projects"}</h3>
          <div className="run-list">
            {report.projects.map((item, index) => (
              <article key={item.projectId ?? `none-${index}`}>
                <div>
                  <strong>
                    {item.projectCode ? `${item.projectCode} · ` : ""}
                    {item.projectName}
                  </strong>
                  <p>
                    {(item.hoursHundredths / 100).toFixed(2)} h ·{" "}
                    {locale === "es" ? "Labor" : "Labor"}{" "}
                    {money(item.totalLaborCostCents)} ·{" "}
                    {locale === "es" ? "Otros reales" : "Other actual"}{" "}
                    {money(item.otherActualCents)}
                  </p>
                  <small>
                    {locale === "es" ? "Presupuesto laboral" : "Labor budget"}:{" "}
                    {item.laborBudgetCents === null
                      ? "—"
                      : money(item.laborBudgetCents)}{" "}
                    · {locale === "es" ? "Ingreso" : "Revenue"}:{" "}
                    {item.revenueCents === null
                      ? "—"
                      : money(item.revenueCents)}
                  </small>
                </div>
                <span>
                  {locale === "es" ? "Margen" : "Margin"}:{" "}
                  {item.marginCents === null ? "—" : money(item.marginCents)}
                </span>
              </article>
            ))}
          </div>
          <h3>{locale === "es" ? "Vehículos" : "Vehicles"}</h3>
          <div className="run-list">
            {report.vehicles.map((item, index) => (
              <article key={item.vehicleId ?? `none-${index}`}>
                <div>
                  <strong>{item.unitNumber}</strong>
                  <p>
                    {(item.hoursHundredths / 100).toFixed(2)} h ·{" "}
                    {locale === "es" ? "Labor" : "Labor"}{" "}
                    {money(item.totalLaborCostCents)} ·{" "}
                    {locale === "es" ? "Operación" : "Operating"}{" "}
                    {money(item.operatingCostCents)}
                  </p>
                </div>
                <span>
                  {locale === "es" ? "Costo total" : "Total cost"}:{" "}
                  {money(item.totalCostCents)}
                </span>
              </article>
            ))}
          </div>
        </>
      )}
      <OperationalError value={error} locale={locale} />
    </section>
  );
}

function VehicleMasterPanel({
  locale,
  tenant,
}: {
  locale: Locale;
  tenant: Tenant;
}) {
  const empty = {
      unitNumber: "",
      plate: "",
      vin: "",
      make: "",
      model: "",
      modelYear: "",
      status: "active",
      notes: "",
    },
    [vehicles, setVehicles] = useState<Vehicle[]>([]),
    [tags, setTags] = useState<NfcItem[]>([]),
    [history, setHistory] = useState<
      {
        id: string;
        inventoryId: string;
        fromVehicleUnit: string | null;
        toVehicleUnit: string | null;
        effectiveAt: string;
        reason: string;
      }[]
    >([]),
    [selected, setSelected] = useState(""),
    [form, setForm] = useState(empty),
    [tagId, setTagId] = useState(""),
    [tagVehicleId, setTagVehicleId] = useState(""),
    [reason, setReason] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const load = async () => {
    const [vehicleResponse, tagResponse, historyResponse] = await Promise.all([
      api(`/v1/tenants/${tenant.id}/vehicles`),
      api(`/v1/tenants/${tenant.id}/nfc/inventory`),
      api(`/v1/tenants/${tenant.id}/nfc/vehicle-history`),
    ]);
    if (vehicleResponse.ok)
      setVehicles(
        ((await vehicleResponse.json()) as { vehicles: Vehicle[] }).vehicles,
      );
    if (tagResponse.ok)
      setTags(
        ((await tagResponse.json()) as { inventory: NfcItem[] }).inventory,
      );
    if (historyResponse.ok)
      setHistory(
        ((await historyResponse.json()) as { history: typeof history }).history,
      );
  };
  useEffect(() => {
    void load();
  }, [tenant.id]);
  const choose = (id: string) => {
    setSelected(id);
    const item = vehicles.find((x) => x.id === id);
    setForm(
      item
        ? {
            unitNumber: item.unitNumber,
            plate: item.plate ?? "",
            vin: item.vin ?? "",
            make: item.make ?? "",
            model: item.model ?? "",
            modelYear: item.modelYear?.toString() ?? "",
            status: item.status,
            notes: item.notes ?? "",
          }
        : empty,
    );
  };
  const save = async () => {
    setBusy(true);
    setError("");
    const body = {
        unitNumber: form.unitNumber,
        plate: form.plate || null,
        vin: form.vin || null,
        make: form.make || null,
        model: form.model || null,
        modelYear: form.modelYear ? Number(form.modelYear) : null,
        status: form.status,
        notes: form.notes || null,
      },
      response = selected
        ? await api(`/v1/tenants/${tenant.id}/vehicles/${selected}/details`, {
            method: "PUT",
            body: JSON.stringify({ ...body, reason }),
          })
        : await api(`/v1/tenants/${tenant.id}/vehicles`, {
            method: "POST",
            body: JSON.stringify(body),
          });
    if (response.ok) {
      setSelected("");
      setForm(empty);
      setReason("");
      await load();
    } else setError(`vehicle: ${response.status}`);
    setBusy(false);
  };
  const associate = async () => {
    setBusy(true);
    setError("");
    const response = await api(
      `/v1/tenants/${tenant.id}/nfc/inventory/${tagId}/vehicle`,
      {
        method: "PUT",
        body: JSON.stringify({ vehicleId: tagVehicleId || null, reason }),
      },
    );
    if (response.ok) {
      setTagId("");
      setTagVehicleId("");
      setReason("");
      await load();
    } else setError(`NFC: ${response.status}`);
    setBusy(false);
  };
  return (
    <section className="settings-form">
      <h2>
        {locale === "es"
          ? "Maestro de vehículos y asociación NFC"
          : "Vehicle master and NFC association"}
      </h2>
      <p>
        {locale === "es"
          ? "Administre todos los datos del vehículo. Placa y VIN son únicos por compañía; cada asociación o traslado NFC conserva historial inmutable."
          : "Manage complete vehicle details. Plate and VIN are company-unique; every NFC assignment or transfer retains immutable history."}
      </p>
      <label>
        {locale === "es"
          ? "Editar vehículo existente"
          : "Edit existing vehicle"}
        <select
          value={selected}
          onChange={(e) => choose(e.currentTarget.value)}
        >
          <option value="">
            {locale === "es" ? "Crear nuevo" : "Create new"}
          </option>
          {vehicles.map((item) => (
            <option key={item.id} value={item.id}>
              {item.unitNumber}
            </option>
          ))}
        </select>
      </label>
      <div className="form-grid">
        <label>
          {locale === "es" ? "Unidad" : "Unit"}
          <input
            required
            value={form.unitNumber}
            onChange={(e) =>
              setForm({ ...form, unitNumber: e.currentTarget.value })
            }
          />
        </label>
        <label>
          {locale === "es" ? "Placa" : "Plate"}
          <input
            value={form.plate}
            onChange={(e) => setForm({ ...form, plate: e.currentTarget.value })}
          />
        </label>
        <label>
          VIN
          <input
            value={form.vin}
            onChange={(e) => setForm({ ...form, vin: e.currentTarget.value })}
          />
        </label>
        <label>
          {locale === "es" ? "Marca" : "Make"}
          <input
            value={form.make}
            onChange={(e) => setForm({ ...form, make: e.currentTarget.value })}
          />
        </label>
        <label>
          {locale === "es" ? "Modelo" : "Model"}
          <input
            value={form.model}
            onChange={(e) => setForm({ ...form, model: e.currentTarget.value })}
          />
        </label>
        <label>
          {locale === "es" ? "Año" : "Year"}
          <input
            type="number"
            min="1900"
            max="2200"
            value={form.modelYear}
            onInput={(e) =>
              setForm({ ...form, modelYear: e.currentTarget.value })
            }
          />
        </label>
        {selected ? (
          <label>
            {locale === "es"
              ? "Cambiar estado del vehículo (acción auditada)"
              : "Change vehicle status (audited action)"}
            <select
              required
              value={form.status}
              onChange={(e) =>
                setForm({ ...form, status: e.currentTarget.value })
              }
            >
              <option value="active">{optionLabel("active", locale)}</option>
              <option value="inactive">{optionLabel("inactive", locale)}</option>
              <option value="retired">{optionLabel("retired", locale)}</option>
            </select>
            <small>
              {locale === "es"
                ? "Use este control solamente para activar, desactivar o retirar el vehículo."
                : "Use this control only to activate, deactivate, or retire the vehicle."}
            </small>
          </label>
        ) : (
          <p className="field-note">
            {locale === "es"
              ? "Los vehículos nuevos se crean activos automáticamente."
              : "New vehicles are created active automatically."}
          </p>
        )}
        <label>
          {locale === "es" ? "Notas (opcional)" : "Notes (optional)"}
          <input
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.currentTarget.value })}
          />
        </label>
        {selected && (
          <label>
            {locale === "es" ? "Razón del cambio" : "Change reason"}
            <input
              minLength={3}
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
            />
          </label>
        )}
      </div>
      <button
        type="button"
        className="primary"
        disabled={
          busy || !form.unitNumber || (Boolean(selected) && reason.length < 3)
        }
        onClick={() => void save()}
      >
        {selected
          ? locale === "es"
            ? "Actualizar vehículo"
            : "Update vehicle"
          : locale === "es"
            ? "Crear vehículo"
            : "Create vehicle"}
      </button>
      <h3>
        {locale === "es"
          ? "Asignar o trasladar etiqueta NFC"
          : "Assign or transfer NFC tag"}
      </h3>
      <div className="form-grid">
        <label>
          {locale === "es" ? "Etiqueta" : "Tag"}
          <select
            value={tagId}
            onChange={(e) => {
              setTagId(e.currentTarget.value);
              setTagVehicleId(
                tags.find((item) => item.id === e.currentTarget.value)
                  ?.vehicleId ?? "",
              );
            }}
          >
            <option value="">—</option>
            {tags
              .filter(
                (item) =>
                  !["lost", "replaced", "retired"].includes(item.status),
              )
              .map((item) => (
                <option key={item.id} value={item.id}>
                  {item.punchPointName ?? item.id.slice(0, 8)} ·{" "}
                  {optionLabel(item.status, locale)}
                </option>
              ))}
          </select>
        </label>
        <label>
          {locale === "es"
            ? "Vehículo destino (vacío para retirar)"
            : "Target vehicle (blank to detach)"}
          <select
            value={tagVehicleId}
            onChange={(e) => setTagVehicleId(e.currentTarget.value)}
          >
            <option value="">—</option>
            {vehicles
              .filter((item) => item.status === "active")
              .map((item) => (
                <option key={item.id} value={item.id}>
                  {item.unitNumber}
                </option>
              ))}
          </select>
        </label>
        <label>
          {locale === "es" ? "Razón" : "Reason"}
          <input
            minLength={3}
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
          />
        </label>
      </div>
      <button
        type="button"
        disabled={busy || !tagId || reason.length < 3}
        onClick={() => void associate()}
      >
        {locale === "es" ? "Guardar asociación" : "Save association"}
      </button>
      <div className="run-list">
        {history.map((item) => (
          <article key={item.id}>
            <div>
              <strong>
                {item.inventoryId.slice(0, 8)} · {item.fromVehicleUnit ?? "—"} →{" "}
                {item.toVehicleUnit ?? "—"}
              </strong>
              <small>{item.reason}</small>
            </div>
            <span>{formatDateTime(item.effectiveAt, locale)}</span>
          </article>
        ))}
      </div>
      <OperationalError value={error} locale={locale} />
    </section>
  );
}

function money(cents: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

type GuidanceSource = {
  id: string;
  agency: string;
  topic: string;
  titleEs: string;
  titleEn: string;
  officialUrl: string;
  lastVerifiedOn: string;
};
type GuidanceRule = {
  id: string;
  ruleKey: string;
  version: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  lastVerifiedOn: string;
  sourceId: string;
  value: Record<string, unknown>;
};
function Help({
  locale,
  tenant,
  role,
  navigate,
  initialArticleId,
  workerType,
}: {
  locale: Locale;
  tenant: Tenant;
  role: HelpRole;
  navigate: (section: Section) => void;
  initialArticleId: string | null;
  workerType?: "employee" | "professional_services";
}) {
  const t = text[locale];
  const [query, setQuery] = useState(""),
    [view, setView] = useState<"tasks" | "sources">("tasks"),
    [error, setError] = useState("");
  const [readiness, setReadiness] = useState<
    {
      labelEs: string;
      labelEn: string;
      detailEs?: string;
      detailEn?: string;
      complete: boolean;
      section: Section;
    }[]
  >([]);
  const [data, setData] = useState<{
    sources: GuidanceSource[];
    rules: GuidanceRule[];
  } | null>(null);
  useEffect(() => {
    void api("/v1/guidance").then(async (response) => {
      if (response.ok)
        setData(
          (await response.json()) as {
            sources: GuidanceSource[];
            rules: GuidanceRule[];
          },
        );
      else
        setError(
          locale === "es"
            ? "No se pudo cargar la biblioteca oficial."
            : "The official library could not be loaded.",
        );
    });
  }, [locale]);
  useEffect(() => {
    let active = true;
    const load = async () => {
      if (role === "user") {
        const [profileResponse, taxResponse] = await Promise.all([
            api(`/v1/tenants/${tenant.id}/self/profile`),
            workerType === "professional_services"
              ? api(`/v1/tenants/${tenant.id}/self/contractor-payment-summary`)
              : api(`/v1/tenants/${tenant.id}/self/tax-questionnaire`),
          ]),
          profile = profileResponse.ok
            ? ((await profileResponse.json()) as {
                profile: {
                  address: unknown;
                  taxpayerIdentifierPresent: boolean;
                };
              })
            : null,
          tax = taxResponse.ok
            ? ((await taxResponse.json()) as {
                current?: unknown;
                profile?: unknown;
              })
            : null;
        if (active)
          setReadiness(
            workerType === "professional_services"
              ? [
                  {
                    labelEs: "Perfil e identificación contributiva",
                    labelEn: "Profile and taxpayer identification",
                    complete: Boolean(
                      profile?.profile.address &&
                        profile.profile.taxpayerIdentifierPresent,
                    ),
                    section: "personnel",
                  },
                  {
                    labelEs: "Configuración de retención y relevo",
                    labelEn: "Withholding and waiver setup",
                    complete: Boolean(tax?.profile),
                    section: "payroll",
                  },
                ]
              : [
                  {
                    labelEs: "Perfil, dirección e identificación contributiva",
                    labelEn: "Profile, address, and taxpayer identification",
                    complete: Boolean(
                      profile?.profile.address &&
                        profile.profile.taxpayerIdentifierPresent,
                    ),
                    section: "personnel",
                  },
                  {
                    labelEs: "Elección 499 R-4 certificada",
                    labelEn: "Certified 499 R-4 election",
                    complete: Boolean(tax?.current),
                    section: "payroll",
                  },
                ],
          );
        return;
      }
      if (role === "supervisor") {
        const [workerResponse, timesheetResponse] = await Promise.all([
            api(`/v1/tenants/${tenant.id}/workers`),
            api(`/v1/tenants/${tenant.id}/timesheets`),
          ]),
          workers = workerResponse.ok
            ? ((await workerResponse.json()) as { workers: unknown[] })
            : null;
        if (active)
          setReadiness([
            {
              labelEs: "Equipo asignado y visible",
              labelEn: "Assigned team is visible",
              complete: Boolean(workers?.workers.length),
              section: "personnel",
            },
            {
              labelEs: "Cola de timesheets accesible",
              labelEn: "Timesheet queue is accessible",
              complete: timesheetResponse.ok,
              section: "time",
            },
          ]);
        return;
      }
      const response = await api(`/v1/tenants/${tenant.id}/setup-readiness`);
      if (!response.ok) {
        if (active)
          setError(
            locale === "es"
              ? "No se pudo cargar la preparación de la compañía."
              : "Company readiness could not be loaded.",
          );
        return;
      }
      const body = (await response.json()) as SetupReadiness,
        labels: Record<
          SetupStep["key"],
          { es: string; en: string; section: Section }
        > = {
          company: {
            es: "Información de la compañía",
            en: "Company information",
            section: "settings",
          },
          personnel: {
            es: "Personal creado",
            en: "Workers created",
            section: "personnel",
          },
          policies: {
            es: "Políticas y tasas",
            en: "Policies and rates",
            section: "settings",
          },
          payroll: {
            es: "Personal listo para nómina",
            en: "Workers ready for payroll",
            section: "payroll",
          },
        };
      if (active)
        setReadiness(
          body.steps.map((item) => ({
            labelEs: labels[item.key].es,
            labelEn: labels[item.key].en,
            detailEs: item.complete
              ? "Todos los requisitos están completos."
              : setupMissingText(item, "es"),
            detailEn: item.complete
              ? "All requirements are complete."
              : setupMissingText(item, "en"),
            complete: item.complete,
            section: labels[item.key].section,
          })),
        );
    };
    void load();
    return () => {
      active = false;
    };
  }, [locale, role, tenant.id, workerType]);
  const matchedArticles = visibleHelpArticles(role, query, workerType),
    articles =
      !query && initialArticleId
        ? matchedArticles.filter((article) => article.id === initialArticleId)
        : matchedArticles,
    sources =
      data?.sources.filter((source) => {
        const needle = query.trim().toLocaleLowerCase();
        return (
          !needle ||
          `${source.agency} ${source.topic} ${source.titleEs} ${source.titleEn}`
            .toLocaleLowerCase()
            .includes(needle)
        );
      }) ?? [];
  const renderValue = (value: unknown) =>
    value === null || value === undefined
      ? "—"
      : typeof value === "object"
        ? JSON.stringify(value)
        : String(value);
  return (
    <div className="content">
      <p className="kicker">JORNADA PR</p>
      <h1>
        {locale === "es"
          ? "Ayuda operacional y fuentes oficiales"
          : "Operational help and official sources"}
      </h1>
      <p className="lead">
        {locale === "es"
          ? "Busque una tarea, siga sus pasos para la vista activa y consulte las reglas que realmente utiliza el cálculo."
          : "Search for a task, follow the steps for the active view, and inspect the rules actually used by calculation."}
      </p>
      <div className="report-toolbar">
        <label>
          {locale === "es" ? "Buscar ayuda" : "Search help"}
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            placeholder={
              locale === "es"
                ? "Ej.: editar empleado, ASUME, 499 R-4"
                : "Example: edit worker, garnishment, 499 R-4"
            }
          />
        </label>
        <button
          type="button"
          className={view === "tasks" ? "primary" : ""}
          onClick={() => setView("tasks")}
        >
          {locale === "es" ? "Cómo hacer tareas" : "How to perform tasks"}
        </button>
        <button
          type="button"
          className={view === "sources" ? "primary" : ""}
          onClick={() => setView("sources")}
        >
          {t.officialGuidance}
        </button>
        {initialArticleId && !query && (
          <button type="button" onClick={() => setQuery(" ")}>
            {locale === "es" ? "Mostrar toda la ayuda" : "Show all help"}
          </button>
        )}
      </div>
      {readiness.length > 0 && (
        <section className="operation-panel">
          <h2>
            {locale === "es"
              ? "Preparación de esta vista"
              : "Readiness for this view"}
          </h2>
          <p>
            {locale === "es"
              ? "Estas comprobaciones leen el estado real de Cloudflare; no crean ni completan información."
              : "These checks read current Cloudflare state; they do not create or complete information."}
          </p>
          <div className="run-list">
            {readiness.map((item) => (
              <article key={item.labelEn}>
                <div>
                  <strong>
                    {locale === "es" ? item.labelEs : item.labelEn}
                  </strong>
                  {(locale === "es" ? item.detailEs : item.detailEn) && (
                    <p>{locale === "es" ? item.detailEs : item.detailEn}</p>
                  )}
                </div>
                <span
                  className={`run-status ${item.complete ? "completed" : "pending"}`}
                >
                  {item.complete
                    ? locale === "es"
                      ? "Completo"
                      : "Complete"
                    : locale === "es"
                      ? "Requiere atención"
                      : "Needs attention"}
                </span>
                <button type="button" onClick={() => navigate(item.section)}>
                  {locale === "es" ? "Abrir" : "Open"}
                </button>
              </article>
            ))}
          </div>
        </section>
      )}
      {view === "tasks" ? (
        <>
          {articles.length ? (
            <div className="guidance-grid">
              {articles.map((article) => (
                <article className="guidance-card" key={article.id}>
                  <span>
                    {article.roles.includes("user")
                      ? locale === "es"
                        ? "Autoservicio"
                        : "Self-service"
                      : locale === "es"
                        ? "Operación autorizada"
                        : "Authorized operation"}
                  </span>
                  <h2>{article.title[locale]}</h2>
                  <p>{article.summary[locale]}</p>
                  {helpDetailsFor(article.id).parts.length > 0 && (
                    <div className="help-parts">
                      <h3>
                        {locale === "es"
                          ? "Qué hace cada parte"
                          : "What each part does"}
                      </h3>
                      <dl>
                        {helpDetailsFor(article.id).parts.map((part) => (
                          <div key={part.title.en}>
                            <dt>{part.title[locale]}</dt>
                            <dd>{part.description[locale]}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  )}
                  <h3>
                    {locale === "es"
                      ? "Cómo usar esta sección"
                      : "How to use this section"}
                  </h3>
                  <ol>
                    {article.steps[locale].map((step, index) => (
                      <li key={`${article.id}-${index}`}>{step}</li>
                    ))}
                  </ol>
                  {helpDetailsFor(article.id).definitions.length > 0 && (
                    <div className="help-definitions">
                      <h3>
                        {locale === "es"
                          ? "Términos de esta sección"
                          : "Terms in this section"}
                      </h3>
                      <dl>
                        {helpDetailsFor(article.id).definitions.map(
                          (definition) => (
                            <div key={definition.term.en}>
                              <dt>{definition.term[locale]}</dt>
                              <dd>{definition.description[locale]}</dd>
                            </div>
                          ),
                        )}
                      </dl>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => navigate(article.section)}
                  >
                    {locale === "es" ? "Abrir módulo" : "Open module"}
                    <ChevronRight />
                  </button>
                  {article.sourceTopics.length > 0 && (
                    <div
                      className="guidance-sources"
                      aria-label={
                        locale === "es"
                          ? "Fuentes oficiales relacionadas"
                          : "Related official sources"
                      }
                    >
                      <small>
                        {locale === "es"
                          ? "Fuentes oficiales relacionadas"
                          : "Related official sources"}
                      </small>
                      <div className="guidance-source-list">
                        {article.sourceTopics.map((topic) => (
                          <button
                            className="guidance-source-chip"
                            type="button"
                            key={topic}
                            onClick={() => {
                              setQuery(topic);
                              setView("sources");
                            }}
                          >
                            {optionLabel(topic, locale)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </article>
              ))}
            </div>
          ) : (
            <p className="form-detail">
              {locale === "es"
                ? "No hay tareas que coincidan con la búsqueda en esta vista."
                : "No tasks match this search in the active view."}
            </p>
          )}
        </>
      ) : !data ? (
        error ? (
          <OperationalError value={error} locale={locale} />
        ) : (
          <LoaderCircle className="spin" />
        )
      ) : (
        <div className="guidance-grid">
          {data.rules
            .filter((rule) =>
              sources.some((source) => source.id === rule.sourceId),
            )
            .map((rule) => {
              const source = data.sources.find(
                (item) => item.id === rule.sourceId,
              );
              return (
                <article className="guidance-card" key={rule.id}>
                  <span>{source?.agency}</span>
                  <h2>{rule.version}</h2>
                  <dl>
                    {Object.entries(rule.value).map(([key, value]) => (
                      <div key={key}>
                        <dt>{key}</dt>
                        <dd>{renderValue(value)}</dd>
                      </div>
                    ))}
                  </dl>
                  <p>
                    {formatDateRange(
                      rule.effectiveFrom,
                      rule.effectiveTo,
                      locale,
                      locale === "es" ? "vigente" : "open",
                    )}
                  </p>
                  {source && (
                    <a
                      className="official-source-link"
                      href={
                        source.officialUrl.startsWith("https://")
                          ? source.officialUrl
                          : undefined
                      }
                      target="_blank"
                      rel="noreferrer"
                    >
                      {locale === "es" ? source.titleEs : source.titleEn}
                      <ChevronRight />
                    </a>
                  )}
                  <small>
                    {locale === "es" ? "Verificado" : "Verified"}:{" "}
                    {formatDate(rule.lastVerifiedOn, locale)}
                  </small>
                </article>
              );
            })}
          {sources
            .filter(
              (source) =>
                !data.rules.some((rule) => rule.sourceId === source.id),
            )
            .map((source) => (
              <article className="guidance-card" key={source.id}>
                <span>{source.agency}</span>
                <h2>{locale === "es" ? source.titleEs : source.titleEn}</h2>
                <p>{source.topic}</p>
                <a
                  className="official-source-link"
                  href={
                    source.officialUrl.startsWith("https://")
                      ? source.officialUrl
                      : undefined
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  {locale === "es"
                    ? "Abrir fuente oficial"
                    : "Open official source"}
                  <ChevronRight />
                </a>
                <small>
                  {locale === "es" ? "Verificado" : "Verified"}:{" "}
                  {formatDate(source.lastVerifiedOn, locale)}
                </small>
              </article>
            ))}
        </div>
      )}
    </div>
  );
}
